import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Users, FileText, DollarSign, Bell, LogOut, Plus, Check, X, Eye, Download, Edit2, Trash2, AlertCircle, Clock, TrendingUp, TrendingDown, Building2, Mail, Search, Filter, BarChart3, PieChart, Activity, Zap, Settings, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Link, UserCog, Shield, Key, Lock, Unlock, Menu, Tag, Save, CheckCircle, Database, RefreshCw, Target, ClipboardList, FileEdit, PlayCircle, CreditCard, ArrowRightLeft, ShoppingBag, PiggyBank, Home, Layers, Lightbulb, MoreHorizontal, Landmark, Heart, ShieldAlert, Laptop, Factory, Brain, Cpu, Dna, Leaf, Bot, Building, Rocket, GripVertical, Send, UserPlus, FilePlus, Upload, Star, MessageCircle, MessageSquare, Inbox, HelpCircle } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, PieChart as RechartsPie, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart, ScatterChart, Scatter, ZAxis } from 'recharts';
import { NauticalMark } from './NauticalMark';
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Globe from 'react-globe.gl';
import { feature } from 'topojson-client';
import * as THREE from 'three';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { CURRENCIES, FALLBACK_RATES, convertAmount, fmtCurrency, computeDealTotals, safePct } from './lib/money';
import { addDays, addMonths, formatDate, isDatePast, daysBetween } from './lib/dates';
import { parseStorageUrl, getSignedFileUrl, openSignedFile } from './lib/storage';
import { createPartner, createProspect, createPool, createTeaser, logAudit } from './lib/factories';
import { COUNTRY_FLAGS, WORLD_GEOGRAPHY } from './lib/geography';
import { DEFAULT_AGREEMENT_TYPES, DEFAULT_COMMISSION_TERMS, DEFAULT_BUSINESS_RULES, DEFAULT_COMMISSION_DEFAULTS } from './lib/defaults';
import { PERMISSIONS, hasPermission, getViewScope, getFilteredData, getPermissionMessage, getNavigationItems } from './lib/permissions';

// ── Guided walkthrough step definitions (driver.js) ──────────────────────────
// Each step spotlights a sidebar nav item (always mounted = robust) and switches
// to that tab so the section loads behind the spotlight, a real cross-tab walk.
// element:null renders a centered modal (welcome / finish hero steps).
//
// Titles are HTML (driver.js sets them via innerHTML) so each step can carry a
// gold icon badge matching its sidebar item. Icons are raw inline SVG strings
// (NOT React components), copied verbatim from the lucide-react icons the
// sidebar already renders, so the tour and the nav can never drift apart.
// `stroke="currentColor"` lets the CSS tint them gold via `.eve-tour-badge`.
const TOUR_SVG_ATTRS = 'xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"';
const TOUR_ICONS = {
  // Partner + staff sidebar items (icon per item verified against SidebarButton)
  dashboard: `<svg ${TOUR_SVG_ATTRS}><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>`,
  prospects: `<svg ${TOUR_SVG_ATTRS}><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/><line x1="16" x2="8" y1="13" y2="13"/><line x1="16" x2="8" y1="17" y2="17"/><line x1="10" x2="8" y1="9" y2="9"/></svg>`,
  teasers:   `<svg ${TOUR_SVG_ATTRS}><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>`,
  revenue:   `<svg ${TOUR_SVG_ATTRS}><line x1="12" x2="12" y1="2" y2="22"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>`,
  messages:  `<svg ${TOUR_SVG_ATTRS}><path d="m3 21 1.9-5.7a8.5 8.5 0 1 1 3.8 3.8z"/></svg>`,
  profile:   `<svg ${TOUR_SVG_ATTRS}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><circle cx="19" cy="11" r="2"/><path d="M19 8v1"/><path d="M19 13v1"/><path d="m21.6 9.5-.87.5"/><path d="m17.27 12-.87.5"/><path d="m21.6 12.5-.87-.5"/><path d="m17.27 10-.87-.5"/></svg>`,
  partners:  `<svg ${TOUR_SVG_ATTRS}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`,
  workflows: `<svg ${TOUR_SVG_ATTRS}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>`,
  analytics: `<svg ${TOUR_SVG_ATTRS}><path d="M3 3v18h18"/><path d="M18 17V9"/><path d="M13 17V5"/><path d="M8 17v-3"/></svg>`,
  // Prospect-form mini-tour
  formSteps: `<svg ${TOUR_SVG_ATTRS}><rect width="8" height="4" x="8" y="2" rx="1" ry="1"/><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><path d="M12 11h4"/><path d="M12 16h4"/><path d="M8 11h.01"/><path d="M8 16h.01"/></svg>`,
  formType:  `<svg ${TOUR_SVG_ATTRS}><rect width="16" height="20" x="4" y="2" rx="2" ry="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01"/><path d="M16 6h.01"/><path d="M12 6h.01"/><path d="M12 10h.01"/><path d="M12 14h.01"/><path d="M16 10h.01"/><path d="M16 14h.01"/><path d="M8 10h.01"/><path d="M8 14h.01"/></svg>`,
  formNext:  `<svg ${TOUR_SVG_ATTRS}><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>`,
  // Finish hero step
  finish:    `<svg ${TOUR_SVG_ATTRS}><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/><path d="M5 3v4"/><path d="M19 17v4"/><path d="M3 5h4"/><path d="M17 19h4"/></svg>`,
};
// Builds a popover title: gold icon badge + label. `icon` omitted on the welcome
// hero (it shows the EvE logo in the body instead).
const tourTitle = (icon, label) =>
  `${icon ? `<span class="eve-tour-badge">${icon}</span>` : ''}<span>${label}</span>`;
// Gold CTA rendered inside a finish step's description. `onPopoverRender` binds
// the click handler (driver.js writes descriptions via innerHTML, so no React).
const tourCta = (label) => `<button type="button" id="eve-tour-cta" class="eve-tour-cta">${label}</button>`;

// `hero: true`  → wider, centered popover (welcome + finish).
// `logo: true`  → EvE logo is prepended to the description AT RUNTIME in
//                 startTour. It cannot be interpolated here: EVE_LOGO_SRC is
//                 declared further down this file, so referencing it in these
//                 module-scope literals would hit the temporal dead zone.
const PARTNER_TOUR_STEPS = [
  { tab: 'dashboard', element: null, hero: true, logo: true, nextBtnText: 'Start the tour →',
    title: tourTitle(null, 'Welcome to the EvE Partners Portal'),
    description: 'A quick 60-second tour of everything you can do here. You can skip anytime and replay it later from the ? icon.' },
  { tab: 'dashboard', element: '[data-tour="nav-dashboard"]', title: tourTitle(TOUR_ICONS.dashboard, 'Your Dashboard'), description: "Your at-a-glance snapshot: prospects you've submitted, deals you're part of, and total commissions." },
  { tab: 'prospects', element: '[data-tour="nav-prospects"]', title: tourTitle(TOUR_ICONS.prospects, 'Prospects'),      description: 'Every lead you refer lives here with its live approval status. Submit new ones anytime with the gold button.' },
  { tab: 'teasers',   element: '[data-tour="nav-teasers"]',   title: tourTitle(TOUR_ICONS.teasers, 'Teasers'),          description: 'Confidential opportunities we share with you. Open one to view the deck. Everything here is under NDA.' },
  { tab: 'revenue',   element: '[data-tour="nav-revenue"]',   title: tourTitle(TOUR_ICONS.revenue, 'My Revenue'),       description: 'Follow your commissions and payments on closed deals. Amounts, status, and history all in one place.' },
  { tab: 'messages',  element: '[data-tour="nav-messages"]',  title: tourTitle(TOUR_ICONS.messages, 'My Messages'),     description: 'Have a question about a prospect or teaser? Message our team directly, right from the portal.' },
  { tab: 'profile',   element: '[data-tour="nav-profile"]',   title: tourTitle(TOUR_ICONS.profile, 'My Profile'),       description: 'Keep your sectors, geography, and contact details current. It helps us send you the right opportunities.' },
  { tab: 'dashboard', element: null, hero: true, cta: 'prospect',
    title: tourTitle(TOUR_ICONS.finish, "You're all set 🎉"),
    description: 'The fastest way to get value from the portal is your first referral. Ready when you are.' + tourCta('Submit your first prospect') },
];
// Opt-in mini-tour: how a partner submits a prospect. Opens the New Prospect
// wizard and spotlights what's visible on Step 1 (no wizard-step driving; the
// rest is explained in copy). The tour opens/closes the modal; it never submits.
const PROSPECT_FORM_TOUR_STEPS = [
  { element: '[data-tour="prospect-steps"]', title: tourTitle(TOUR_ICONS.formSteps, 'Submitting a prospect'), description: 'Five quick steps. Nothing is saved until you hit Submit on the last one, so feel free to explore.' },
  { element: '[data-tour="prospect-type"]',  title: tourTitle(TOUR_ICONS.formType, 'Step 1: Prospect type'),  description: 'Choose the type: Client (seeking capital), Angel investor, Investment Body (VC/fund), or a Connection.' },
  { element: '[data-tour="prospect-next"]',  title: tourTitle(TOUR_ICONS.formNext, 'Work through the steps'), description: 'Next walks you through company details, sector, geography, then financials. Hit Submit and we will review it. You can track its status right here.' },
];
const ADMIN_TOUR_STEPS = [
  { tab: 'dashboard', element: null, hero: true, logo: true, nextBtnText: 'Start the tour →',
    title: tourTitle(null, 'Welcome to the EvE Partners Portal'),
    description: 'A quick tour of the main areas you will work in. You can skip anytime and replay it later from the ? icon.' },
  { tab: 'dashboard', element: '[data-tour="nav-dashboard"]', title: tourTitle(TOUR_ICONS.dashboard, 'Dashboard'),       description: 'Your command center: KPIs, partner geography, and whatever needs your attention today.' },
  { tab: 'partners',  element: '[data-tour="nav-partners"]',  title: tourTitle(TOUR_ICONS.partners, 'Partners'),         description: 'Manage referral partners: agreements, NDA status, contract dates, and performance.' },
  { tab: 'prospects', element: '[data-tour="nav-prospects"]', title: tourTitle(TOUR_ICONS.prospects, 'Prospects'),       description: 'Review incoming referrals. Approve or reject them, and keep an eye on intro deadlines.' },
  { tab: 'teasers',   element: '[data-tour="nav-teasers"]',   title: tourTitle(TOUR_ICONS.teasers, 'Teasers'),           description: 'Upload confidential deal teasers and control exactly which partners can see each one.' },
  { tab: 'revenue',   element: '[data-tour="nav-revenue"]',   title: tourTitle(TOUR_ICONS.revenue, 'Revenue Manager'),   description: 'Build deals, split commissions across strategic partners, and record payments.' },
  { tab: 'workflows', element: '[data-tour="nav-workflows"]', title: tourTitle(TOUR_ICONS.workflows, 'Workflows'),       description: 'Automate notifications and emails, and publish Hot Topic popups to partners.' },
  { tab: 'analytics', element: '[data-tour="nav-analytics"]', title: tourTitle(TOUR_ICONS.analytics, 'Analytics'),       description: 'Funnels, partner leaderboards, and revenue trends. The numbers behind your pipeline.' },
  { tab: 'dashboard', element: null, hero: true, cta: 'dashboard',
    title: tourTitle(TOUR_ICONS.finish, "You're all set 🎉"),
    description: 'That is the whole portal. Replay this anytime from the ? icon up top.' + tourCta('Explore the Dashboard') },
];

// ============================================================================
// THEME SYSTEM - Light & Dark Mode
// ============================================================================

const BRAND_COLORS = {
  // ── Brand ────────────────────────────────────────────────────────────────
  navy:        '#0A1428',   // sidebar, header, primary text on light
  navyDeep:    '#06101F',   // sidebar mid stop
  navyDark:    '#040A14',   // sidebar bottom stop
  navyTint:    '#0F1E42',   // header right stop
  navyLegacy:  '#1A1C5C',   // historical only, do not use in new code
  gold:        '#D4AF37',   // primary CTA, focus ring, accents
  goldDeep:    '#B8941F',   // gold pressed
  cream:       '#FFF8DC',   // light text on dark, accent fills
  royalBlue:   '#4169E1',   // secondary action, partner role tone
  terracotta:  '#E07A5F',   // warning / destructive accent

  // ── Surfaces ─────────────────────────────────────────────────────────────
  bgApp:       '#F9FAFB',
  bgSurface:   '#FFFFFF',
  // Hero · Brand gradient — black → electric blue. Per May 7 product call,
  // the sidebar and header use this regardless of the May 7 Design System
  // note that deprecated --eve-electric. If/when an explicitly-marketing hero
  // token is introduced, these tokens stay (they're chrome surfaces, not
  // marketing).
  bgSidebar:   'linear-gradient(180deg, #000000 0%, #1A19A0 50%, #3533CD 100%)',
  bgHeader:    'linear-gradient(90deg, #000000, #3533CD)',
  bgImpersonate:'linear-gradient(to right, #F59E0B, #D97706)',

  // ── Text ─────────────────────────────────────────────────────────────────
  textPrimary:   '#111827',
  textSecondary: '#6B7280',
  textTertiary:  '#9CA3AF',
  textOnNavy:    '#FFFFFF',
  textOnGold:    '#FFFFFF',

  // ── Borders ──────────────────────────────────────────────────────────────
  borderDefault: '#E5E7EB',
  borderHover:   '#D1D5DB',
  borderNavy12:  'rgba(10,20,40,0.12)',  // canonical card border
  borderNavy19:  'rgba(10,20,40,0.19)',  // hover-stronger border
  borderGold:    '#D4AF37',

  // ── Status (flat fills only — never gradient) ────────────────────────────
  statusSuccess: '#10B981',
  statusError:   '#EF4444',
  statusWarning: '#F59E0B',
  statusInfo:    '#3B82F6',

  // ── Interactive (gold-based) ─────────────────────────────────────────────
  hoverGold:  'rgba(212,175,55,0.10)',
  activeGold: 'rgba(212,175,55,0.20)',
  focusRing:  '0 0 0 2px rgba(212,175,55,0.45)',

  // ── Shadows ──────────────────────────────────────────────────────────────
  shadow:   '0 1px 3px 0 rgb(0 0 0 / 0.1)',
  shadowLg: '0 10px 15px -3px rgb(0 0 0 / 0.1)',

  // ─────────────────────────────────────────────────────────────────────────
  // DEPRECATED legacy aliases — do NOT use in new code.
  // Kept so existing references compile during the palette-migration sprint.
  // Each is followed by the canonical replacement. Iterations 2–4 progressively
  // remove these.
  // ─────────────────────────────────────────────────────────────────────────
  background:           '#F9FAFB',                                                    // → bgApp
  surface:              '#FFFFFF',                                                    // → bgSurface
  sidebarBg:            'linear-gradient(180deg, #000000 0%, #1A19A0 50%, #3533CD 100%)', // → bgSidebar
  headerBg:             'linear-gradient(90deg, #000000, #3533CD)',                       // → bgHeader
  border:               '#E5E7EB',                                                    // → borderDefault
  success:              '#10B981',                                                    // → statusSuccess
  error:                '#EF4444',                                                    // → statusError
  warning:              '#F59E0B',                                                    // → statusWarning
  info:                 '#3B82F6',                                                    // → statusInfo
  hover:                'rgba(212,175,55,0.10)',                                      // → hoverGold
  active:               'rgba(212,175,55,0.20)',                                      // → activeGold
  impersonationBanner:  'linear-gradient(to right, #F59E0B, #D97706)',                // → bgImpersonate
  notificationDot:      '#4169E1',                                                    // was #110AFF, now royalBlue per spec
};

// ============================================================================
// SECTORS - Default list (Admin can add/remove via Settings)
// ============================================================================
const EVE_LOGO_SRC = "data:image/jpeg;base64,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";

const DEFAULT_SECTORS = [
  'Cybersecurity',
  'Real Estate',
  'Medical / Healthcare',
  'Defense & HLS',
  'FinTech',
  'Technology / SaaS',
  'Energy & CleanTech',
  'Manufacturing',
  'Artificial Intelligence (AI)',
  'Semiconductors',
  'Biotechnology',
  'ClimateTech',
  'Robotics & DeepTech',
  'Infrastructure',
  'Aerospace & Aviation',
  'PropTech',
];

const SECTOR_ICONS = {
  'Cybersecurity': Shield,
  'Real Estate': Home,
  'Medical / Healthcare': Heart,
  'Defense & HLS': ShieldAlert,
  'FinTech': CreditCard,
  'Technology / SaaS': Laptop,
  'Energy & CleanTech': Zap,
  'Manufacturing': Factory,
  'Artificial Intelligence (AI)': Brain,
  'Semiconductors': Cpu,
  'Biotechnology': Dna,
  'ClimateTech': Leaf,
  'Robotics & DeepTech': Bot,
  'Infrastructure': Building,
  'Aerospace & Aviation': Rocket,
  'PropTech': Building2,
};

const ICON_OPTIONS = [
  { key: 'Shield', label: 'Shield', component: Shield },
  { key: 'Home', label: 'Home', component: Home },
  { key: 'Heart', label: 'Heart', component: Heart },
  { key: 'ShieldAlert', label: 'Defense', component: ShieldAlert },
  { key: 'CreditCard', label: 'Finance', component: CreditCard },
  { key: 'Laptop', label: 'Tech', component: Laptop },
  { key: 'Zap', label: 'Energy', component: Zap },
  { key: 'Factory', label: 'Factory', component: Factory },
  { key: 'Brain', label: 'AI', component: Brain },
  { key: 'Cpu', label: 'Chip', component: Cpu },
  { key: 'Dna', label: 'Biotech', component: Dna },
  { key: 'Leaf', label: 'Green', component: Leaf },
  { key: 'Bot', label: 'Robot', component: Bot },
  { key: 'Building', label: 'Building', component: Building },
  { key: 'Building2', label: 'Property', component: Building2 },
  { key: 'Rocket', label: 'Rocket', component: Rocket },
  { key: 'Target', label: 'Target', component: Target },
  { key: 'Landmark', label: 'Government', component: Landmark },
  { key: 'ShoppingBag', label: 'Retail', component: ShoppingBag },
  { key: 'Lightbulb', label: 'Innovation', component: Lightbulb },
  { key: 'Activity', label: 'Activity', component: Activity },
  { key: 'Layers', label: 'Layers', component: Layers },
  { key: 'Database', label: 'Data', component: Database },
  { key: 'Send', label: 'Send', component: Send },
  { key: 'PiggyBank', label: 'Savings', component: PiggyBank },
  { key: 'Tag', label: 'Tag', component: Tag },
  { key: 'TrendingUp', label: 'Growth', component: TrendingUp },
  { key: 'Lock', label: 'Security', component: Lock },
  { key: 'Mail', label: 'Mail', component: Mail },
  { key: 'DollarSign', label: 'Dollar', component: DollarSign },
];

const ICON_MAP = Object.fromEntries(ICON_OPTIONS.map(i => [i.key, i.component]));

const parseSectorsField = (val) => {
  if (Array.isArray(val)) return val;
  if (typeof val === 'string' && val.trim()) {
    try { const parsed = JSON.parse(val); if (Array.isArray(parsed)) return parsed; } catch (_) {}
  }
  return [];
};

// Mirror of parseSectorsField for geography ({ continents, countries } object).
// Handles both real objects (jsonb) and JSON strings (text column).
const parseGeographyField = (val) => {
  if (val && typeof val === 'object' && !Array.isArray(val)) return val;
  if (typeof val === 'string' && val.trim()) {
    try { const parsed = JSON.parse(val); if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed; } catch (_) {}
  }
  return { continents: [], countries: [] };
};

let _sectorIconsRegistry = {};
const setSectorIconsRegistry = (icons) => { _sectorIconsRegistry = icons || {}; };
const getSectorIcon = (sectorName) => {
  const dynamicKey = _sectorIconsRegistry[sectorName];
  if (dynamicKey && ICON_MAP[dynamicKey]) return ICON_MAP[dynamicKey];
  return SECTOR_ICONS[sectorName] || null;
};


// ── Currency Support ──────────────────────────────────────────
// CURRENCIES, FALLBACK_RATES, convertAmount, fmtCurrency, computeDealTotals,
// safePct → moved to ./lib/money (imported at top of file).

// parseStorageUrl, getSignedFileUrl, openSignedFile → moved to ./lib/storage
// (imported at top of file).

// ── Wizard drafts ─────────────────────────────────────────────
// A row with isDraft = true is a wizard the user parked mid-way via
// "Save & Close". It is a real row, but it is not finished, so it must never
// appear in a default list or contribute to any revenue / commission /
// analytics figure. `liveOnly` is the default lens applied everywhere;
// `draftsOnly` backs each view's "Drafts" filter chip.
const liveOnly   = (rows = []) => rows.filter(r => !r?.isDraft);
const draftsOnly = (rows = []) => rows.filter(r => r?.isDraft);

const useIsMobile = (mobileBp = 768, tabletBp = 1024) => {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return { isMobile: width < mobileBp, isTablet: width < tabletBp, width };
};

// ────────────────────────────────────────────────────────────────────────
// Sortable table helper — single source of truth for table sorting.
// Usage:
//   const { sortedRows, sortKey, sortDir, toggleSort } = useSortableTable(
//     rows,
//     'name',          // default sort key
//     'asc',           // default direction
//     { name: r => r.name, status: r => r.status }, // optional resolver map
//   );
// Click cycle on a header: asc → desc → reset to default.
// ────────────────────────────────────────────────────────────────────────
const useSortableTable = (rows, defaultKey, defaultDir = 'asc', resolvers = {}) => {
  const [sortKey, setSortKey] = useState(defaultKey);
  const [sortDir, setSortDir] = useState(defaultDir);

  const getValue = (row, key) => {
    if (resolvers[key]) return resolvers[key](row);
    // Default: read row[key], lowercase strings for case-insensitive compare.
    const v = row?.[key];
    return typeof v === 'string' ? v.toLowerCase() : v;
  };

  const toggleSort = (key) => {
    if (sortKey !== key) { setSortKey(key); setSortDir('asc'); return; }
    if (sortDir === 'asc') { setSortDir('desc'); return; }
    // Third click on the same column: reset to default.
    setSortKey(defaultKey);
    setSortDir(defaultDir);
  };

  const sortedRows = React.useMemo(() => {
    if (!sortKey) return rows;
    const out = [...(rows || [])];
    out.sort((a, b) => {
      const av = getValue(a, sortKey);
      const bv = getValue(b, sortKey);
      // null/undefined sort to the end regardless of direction
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ?  1 : -1;
      return 0;
    });
    return out;
  }, [rows, sortKey, sortDir]);

  return { sortedRows, sortKey, sortDir, toggleSort };
};

// Clickable table header for use with useSortableTable. Drop-in replacement
// for a plain <th>: same className/style props, but with a chevron showing
// the current sort direction when this column is active.
const SortableTh = ({ columnKey, sortKey, sortDir, onSort, children, className = '', style = {} }) => {
  const active = sortKey === columnKey;
  return (
    <th
      className={`${className} cursor-pointer select-none`}
      style={style}
      onClick={() => onSort(columnKey)}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        <span style={{ opacity: active ? 1 : 0.25, fontSize: '10px', lineHeight: 1 }}>
          {active ? (sortDir === 'asc' ? '▲' : '▼') : '▲▼'}
        </span>
      </span>
    </th>
  );
};


// ============================================================================
// EXPORT
// ============================================================================

// Add these to the global scope or export as needed
// Usage example:
//   if (hasPermission(currentUser.role, 'prospects', 'delete')) {
//     // Show delete button
//   }

// EvE Logo - Embedded as base64 from actual logo file
const EVE_LOGO_BASE64 = `data:image/png;base64,${(() => {
  // Base64 data embedded inline
  return `/9j/4AAQSkZJRgABAgAAAQABAAD/wAARCAREBEQDACIAAREBAhEB/9sAQwAIBgYHBgUIBwcHCQkICgwUDQwLCwwZEhMPFB0aHx4dGhwcICQuJyAiLCMcHCg3KSwwMTQ0NB8nOT04MjwuMzQy/9sAQwEJCQkMCwwYDQ0YMiEcITIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIy`;
})()}`;

// EvE Logo - The actual logo should be displayed as the circular gold brush stroke with "EvE" text
// For now, we'll use a styled representation

// ============================================================================
// UTILITY FUNCTIONS & HELPERS
// ============================================================================

// addDays, addMonths, formatDate, isDatePast, daysBetween → moved to ./lib/dates
// (imported at top of file).

// ============================================================================
// DATA MODELS & INITIAL DATA
// ============================================================================

// createPartner, createProspect, createPool, createTeaser, logAudit → moved to
// ./lib/factories (imported at top of file).

// ============================================================================
// MAIN APP COMPONENT
// ============================================================================

const PartnersPortal = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [forgotPasswordMsg, setForgotPasswordMsg] = useState('');
  // Shown on the login screen when an authenticated identity has no
  // user_profiles row (i.e. was never provisioned by an admin). See applySession.
  const [accessDeniedMsg, setAccessDeniedMsg] = useState('');
  
  // Impersonation state
  const [impersonatedPartnerId, setImpersonatedPartnerId] = useState(null);
  const [realUser, setRealUser] = useState(null); // Store the actual admin user
  
  // Data states
  const [partners, setPartners] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [pools, setPools] = useState([]);
  const [revenueDeals, setRevenueDeals] = useState([]);
  const [teasers, setTeasers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [users, setUsers] = useState([]);
  const [appAlert, setAppAlert] = useState(null); // { title, message }
  const [deletionRequests, setDeletionRequests] = useState([]);
  const [partnerThreads, setPartnerThreads] = useState([]);
  const [partnerPosts, setPartnerPosts] = useState([]);
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  const [newMessageContext, setNewMessageContext] = useState(null); // {category,refId,refName}
  // Hot Topics — announcement popups for partners
  const [hotTopics, setHotTopics] = useState([]);
  const [activeHotTopic, setActiveHotTopic] = useState(null);
  const hotTopicShownThisSessionRef = React.useRef(false); // max one popup per login/mount
  const tourAutoCheckedRef = React.useRef(false); // auto-start the walkthrough at most once per mount
  const [showHelpMenu, setShowHelpMenu] = useState(false); // header ? dropdown
  const isAdminCreatingUserRef = React.useRef(false);
  // Tracks the last user ID that completed applySession so we never run it
  // twice for the same session (getSession() + SIGNED_IN can both fire on load).
  const lastAppliedUserIdRef = React.useRef(undefined);
  const [sectors, setSectors] = useState(DEFAULT_SECTORS);
  const [sectorIcons, setSectorIcons] = useState({});
  useEffect(() => { setSectorIconsRegistry(sectorIcons); }, [sectorIcons]);
  const [agreementTypes, setAgreementTypes] = useState(DEFAULT_AGREEMENT_TYPES);
  const [commissionDefaults, setCommissionDefaults] = useState(DEFAULT_COMMISSION_DEFAULTS);
  const [businessRules, setBusinessRules] = useState(DEFAULT_BUSINESS_RULES);
  const [generalSettings, setGeneralSettings] = useState({
    portalName: 'EvE Partner Portal',
    portalTagline: '',
    primaryContactEmail: '',
    timezone: 'UTC',
  });
  // ⚠️ securitySettings values are persisted to app_settings but are NOT yet
  // enforced anywhere in the codebase (sessionTimeout, passwordMinLength,
  // require2FA, failedLoginLockout). Enforcement is filed as future work in
  // DIAGNOSTIC-REPORT-2026-04-29 #UI-1.2.
  const [securitySettings, setSecuritySettings] = useState({
    sessionTimeout: '30min',
    passwordMinLength: 8,
    require2FA: false,
    failedLoginLockout: 5,
  });
  const [exchangeRates, setExchangeRates] = useState(FALLBACK_RATES);

  // UI states
  const [activeTab, setActiveTab] = useState(() => localStorage.getItem('activeTab') || 'dashboard');
  const setActiveTabPersisted = (tab) => { setActiveTab(tab); localStorage.setItem('activeTab', tab); };
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { isMobile, isTablet } = useIsMobile();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [showNewProspectModal, setShowNewProspectModal] = useState(false);
  const [showNewPartnerModal, setShowNewPartnerModal] = useState(false);
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [autoOpenTeaserUpload, setAutoOpenTeaserUpload] = useState(false);
  const [showNewWorkflowModal, setShowNewWorkflowModal] = useState(false);
  const [showEditWorkflowModal, setShowEditWorkflowModal] = useState(false);
  const [showNewUserModal, setShowNewUserModal] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ show: false, message: '', onConfirm: null, confirmLabel: 'Confirm' });
  const [blockModal, setBlockModal] = useState({ show: false, title: '', items: [] });
  const [showEditPartnerModal, setShowEditPartnerModal] = useState(false);
  const [showEditProspectModal, setShowEditProspectModal] = useState(false);
  const [showEditTeaserModal, setShowEditTeaserModal] = useState(false);
  // Wizard drafts — rows saved mid-wizard via "Save & Close". They are kept out
  // of the live arrays entirely (see loadData) so no list, dropdown, KPI or
  // Analytics figure has to remember to filter them out.
  const [wizardDrafts, setWizardDrafts] = useState({ partner: [], prospect: [], deal: [], workflow: [] });
  // Setting one of these alongside the matching New* modal flag reopens that
  // wizard pre-filled and positioned on the step it was left on (draftStep).
  const [draftPartner, setDraftPartner]   = useState(null);
  const [draftProspect, setDraftProspect] = useState(null);
  const [draftWorkflow, setDraftWorkflow] = useState(null);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [editingProspect, setEditingProspect] = useState(null);
  const [editingTeaser, setEditingTeaser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const [showNotificationsPanel, setShowNotificationsPanel] = useState(false);
  const [showViewAsDropdown, setShowViewAsDropdown] = useState(false);
  const [showHamburgerMenu, setShowHamburgerMenu] = useState(false);
  const [expandedSubmenu, setExpandedSubmenu] = useState(null); // 'viewAs', 'create', 'navigate'
  const [viewAsSearchQuery, setViewAsSearchQuery] = useState('');
  const [selectedProspect, setSelectedProspect] = useState(null);

  useEffect(() => { setMobileSidebarOpen(false); }, [activeTab]);
  useEffect(() => { if (isMobile) setSidebarCollapsed(true); }, [isMobile]);

  // Quick view drawer states // 'prospect', 'partner', 'pool'
  
  // Kebab menu states
  const [activeKebabMenu, setActiveKebabMenu] = useState(null);
  
  // Search and filter states
  const [globalSearch, setGlobalSearch] = useState('');
  const [searchHistory, setSearchHistory] = useState([]);
  const [advancedFilters, setAdvancedFilters] = useState({
    dateRange: { start: '', end: '' },
    partners: [],
    statuses: [],
    sectors: [],
    continents: [],
    raiseRange: { min: '', max: '' }
  });
  const [savedFilters, setSavedFilters] = useState([]);
  
  // Comments/notes state

  // ============================================================================
  // TAB NAVIGATION EVENT LISTENER
  // ============================================================================

  useEffect(() => {
    const handleNavigate = (event) => {
      setActiveTabPersisted(event.detail);
    };
    
    window.addEventListener('navigate-to-tab', handleNavigate);
    
    return () => {
      window.removeEventListener('navigate-to-tab', handleNavigate);
    };
  }, []);
  
  // Theme persistence
  
  
  // Get current theme colors
  const colors = BRAND_COLORS;

  // ============================================================================
  // STORAGE INTEGRATION
  // ============================================================================





  // Fetch exchange rates once — cached in localStorage for 24h
  useEffect(() => {
    const CACHE_KEY = 'eve_exchange_rates';
    const CACHE_TTL = 24 * 60 * 60 * 1000;
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { rates, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_TTL) { setExchangeRates(rates); return; }
      }
    } catch (_) {}
    fetch('https://open.er-api.com/v6/latest/USD')
      .then(r => r.json())
      .then(data => {
        if (data?.rates) {
          const rates = { USD: 1, EUR: data.rates.EUR, GBP: data.rates.GBP, ILS: data.rates.ILS };
          setExchangeRates(rates);
          localStorage.setItem(CACHE_KEY, JSON.stringify({ rates, timestamp: Date.now() }));
        }
      })
      .catch(() => {}); // silently fall back to FALLBACK_RATES
  }, []);

  // Restore Supabase session on refresh
  const loadData = async () => {
    try {
      const { db } = await import('./dataLayer');

      // ── Phase 1: critical data for first paint (awaited — gates the spinner) ──
      // Everything the dashboard + primary views need to render. Notifications
      // are capped (only the bell's recent list matters at load).
      const [
        partners,
        prospects,
        revenueDeals,
        teasers,
        users,
        allSettings,
        notifications,
      ] = await Promise.all([
        db.partners.getAll(),
        db.prospects.getAll(),
        db.deals.getAll(),
        db.teasers.getAll(),
        db.users.getAll(),
        db.appSettings.getAll(), // single query replaces 7 individual gets
        db.notifications.getAll(100), // cap — unbounded table
      ]);

      // Unfinished wizard drafts are split off into their own store rather than
      // being mixed into the live arrays. That way every existing consumer —
      // lists, dropdowns, KPIs, Analytics, partner statements — excludes them
      // for free, with no risk of one call site forgetting to filter.
      if (partners?.length  > 0) setPartners(liveOnly(partners));
      if (prospects?.length > 0) setProspects(liveOnly(prospects));
      if (revenueDeals?.length > 0) {
        setRevenueDeals(liveOnly(revenueDeals).map(deal => ({
          paymentStatus: 'Unpaid',
          payments: [],
          totalPaid: 0,
          exposure: { showTotal: false, showDeductions: false, showOtherPartners: false },
          ...deal,
        })));
      }
      setWizardDrafts({
        partner:  draftsOnly(partners),
        prospect: draftsOnly(prospects),
        deal:     draftsOnly(revenueDeals),
        workflow: [],   // workflows load in phase 2 below
      });
      if (teasers?.length > 0) setTeasers(teasers);
      if (users?.length > 0) setUsers(users);
      setSectors(allSettings.sectors ?? DEFAULT_SECTORS);
      setBusinessRules(allSettings.businessRules ?? DEFAULT_BUSINESS_RULES);
      setAgreementTypes(allSettings.agreementTypes ?? DEFAULT_AGREEMENT_TYPES);
      setCommissionDefaults(allSettings.commissionDefaults ?? DEFAULT_COMMISSION_DEFAULTS);
      if (allSettings.generalSettings) setGeneralSettings(prev => ({ ...prev, ...allSettings.generalSettings }));
      if (allSettings.securitySettings) setSecuritySettings(prev => ({ ...prev, ...allSettings.securitySettings }));
      if (allSettings.sectorIcons) setSectorIcons(allSettings.sectorIcons);
      if (notifications?.length > 0) setNotifications(notifications);

      // ── Phase 2: cold data, deferred — NOT awaited, so it doesn't block the
      // spinner. These views (Workflows, Audit Log, Delete Requests, Messages)
      // aren't on the first screen; they populate a beat later. audit_logs is
      // capped (grows on every action). Errors here are non-fatal.
      (async () => {
        try {
          const [workflows, auditLogs, deletionRequests, msgThreads, msgPosts, topics] = await Promise.all([
            db.workflows.getAll(),
            db.auditLogs.getAll(100), // cap — unbounded table
            db.deletionRequests.getAll(),
            db.messageThreads.getAll(),
            db.messagePosts.getAll(),
            db.hotTopics.getAll(), // RLS pre-filters: partners only see topics targeted at them
          ]);
          if (workflows?.length > 0) {
            setWorkflows(liveOnly(workflows));
            setWizardDrafts(d => ({ ...d, workflow: draftsOnly(workflows) }));
          }
          if (auditLogs?.length > 0) setAuditLogs(auditLogs);
          if (deletionRequests?.length > 0) setDeletionRequests(deletionRequests);
          if (msgThreads?.length > 0) setPartnerThreads(msgThreads);
          if (msgPosts?.length > 0) setPartnerPosts(msgPosts);
          if (topics?.length > 0) setHotTopics(topics);
        } catch (e) {
          console.error('Error loading deferred data:', e);
        }
      })();
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };
  useEffect(() => {
    let authSubscription;

    const stampLastLogin = (token) => {
      if (!token) return;
      fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/stamp-last-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${token}`,
        },
        body: '{}',
      })
        .then(r => r.json())
        .then(d => console.log('[stampLastLogin] ok:', d))
        .catch(e => console.error('[stampLastLogin] failed:', e.message));
    };

    const applySession = async (supabase, session) => {
      // Deduplicate: both getSession() and onAuthStateChange(SIGNED_IN) can fire
      // for the same session on initial load. Skip if we already applied this user.
      const incomingUserId = session?.user?.id ?? null;
      if (incomingUserId !== null && incomingUserId === lastAppliedUserIdRef.current) {
        console.log('[applySession] skipping duplicate for same user');
        return;
      }
      lastAppliedUserIdRef.current = incomingUserId;

      const user = session?.user ?? null;
      if (user) {
        try {
          const { data: profile } = await supabase
            .from('user_profiles').select('*').eq('id', user.id).single();
          if (profile) {
            setCurrentUser({
              id: profile.id,
              email: user.email,
              name: profile.name || user.email,
              role: profile.role || 'Partner',
              partnerId: profile.partner_id || null,
              onboardingCompleted: profile.onboarding_completed === true,
            });
          } else {
            // Authenticated, but never provisioned by an admin (no user_profiles
            // row). Access is denied: end the session immediately rather than
            // leaving them on a blank screen. RLS + Edge Functions already return
            // nothing/403 for such a user — this makes the rule explicit in the
            // app, so it holds even if Supabase signup settings ever change.
            console.warn('[applySession] no user_profiles row — denying access and signing out:', user.id);
            lastAppliedUserIdRef.current = null;
            setCurrentUser(null);
            setAccessDeniedMsg('Your account is not registered in the portal. Please contact your administrator.');
            try { await supabase.auth.signOut(); } catch (_) {}
            setSessionLoading(false);
            return;
          }
          // Stamp last_login via Edge Function (bypasses RLS).
          // Fires on every session establishment — covers password login, Google
          // OAuth redirect, and session restore. Idempotent.
          if (!isAdminCreatingUserRef.current) {
            stampLastLogin(session?.access_token);
          }
        } catch (e) {}
      }
      await loadData();
      setSessionLoading(false);
    };

    const init = async () => {
      const { supabase } = await import('./supabaseClient');

      // Single source of truth for auth state — onAuthStateChange fires
      // INITIAL_SESSION immediately on mount with the stored session, so we
      // no longer need a separate getSession() call. The old pattern caused
      // lock contention: getSession() held the Supabase auth lock while
      // applySession() + loadData() ran (up to several seconds), making
      // onAuthStateChange wait 5000ms to forcefully acquire it.
      const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
        // Intentionally NOT async / NOT awaiting applySession.
        // Supabase holds the auth lock while this callback runs — awaiting a
        // multi-second async function here causes the "lock not released within
        // 5000ms" warning and blocks subsequent auth events. Let the callback
        // return synchronously; applySession runs in the background and the
        // lastAppliedUserIdRef dedup guard prevents concurrent double-loads.
        console.log('[onAuthStateChange] event:', event, 'user:', session?.user?.id || '(none)');
        // Detect recovery context — Supabase appends `type=recovery` to the
        // URL hash (or query for PKCE flows) when the user clicks the email
        // link. On a COLD MOUNT after the redirect, the session is already
        // in storage by the time this listener attaches, so Supabase fires
        // INITIAL_SESSION (not PASSWORD_RECOVERY). Same pattern as OAuth
        // redirect remount documented in CLAUDE.md. Without this sniff, the
        // user is silently signed in as normal and never gets the reset modal.
        const url = typeof window !== 'undefined' ? window.location : { hash: '', search: '' };
        const isRecoveryUrl =
          /[?&#]type=recovery\b/.test(url.hash || '') ||
          /[?&]type=recovery\b/.test(url.search || '');

        if (event === 'PASSWORD_RECOVERY' || (event === 'INITIAL_SESSION' && isRecoveryUrl)) {
          // Show the password-reset modal. Skip applySession — we don't want
          // to drop the user into the app with side-effects (loadData, stamp
          // last login) before they've completed the reset.
          console.log('[onAuthStateChange] recovery context detected, opening reset modal');
          setShowPasswordResetModal(true);
          // Clean the hash so a refresh doesn't re-trigger the modal after the
          // user has already changed their password.
          if (typeof window !== 'undefined' && isRecoveryUrl) {
            try { window.history.replaceState(null, '', window.location.pathname); } catch (_) {}
          }
        } else if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN') {
          if (!isAdminCreatingUserRef.current) {
            applySession(supabase, session); // fire-and-forget — dedup guard handles races
          }
        } else if (event === 'SIGNED_OUT') {
          setCurrentUser(null);
          lastAppliedUserIdRef.current = null;
        }
      });
      authSubscription = subscription;
    };

    init();

    return () => { authSubscription?.unsubscribe(); };
  }, []);

  const saveData = async (key, data) => {
    const { db } = await import('./dataLayer');
    try {
      const appSettingsKeys = ['sectors', 'businessRules', 'agreementTypes', 'commissionDefaults', 'generalSettings', 'securitySettings', 'sectorIcons'];
      const upsertManyKeys = {
        partners: db.partners,
        prospects: db.prospects,
        revenueDeals: db.deals,
        teasers: db.teasers,
        workflows: db.workflows,
        notifications: db.notifications,
        auditLogs: db.auditLogs,
        deletionRequests: db.deletionRequests,
        savedFilters: db.savedFilters,
        searchHistory: db.searchHistory,
      };
      if (appSettingsKeys.includes(key)) {
        await db.appSettings.set(key, data);
      } else if (upsertManyKeys[key]) {
        const store = upsertManyKeys[key];
        if (Array.isArray(data)) {
          await store.upsertMany(data);
        } else {
          await store.upsert(data);
        }
      }
    } catch (error) {
      console.error(`[saveData] failed for ${key}:`, error.message ?? error);
      throw error; // propagate — callers must handle
    }
  };

  // ── Supabase sync helpers ────────────────────────────────────────────────
  const syncToSupabase = async (table, row, operation = 'upsert') => {
    try {
      const { supabase } = await import('./supabaseClient');
      if (operation === 'delete') {
        const { data, error } = await supabase.from(table).delete().eq('id', row.id).select();
        if (error) throw error;
        if (!data?.length) throw new Error(`Delete matched 0 rows in ${table} - check RLS`);
      } else {
        const { data, error } = await supabase.from(table).upsert(row).select().single();
        if (error) throw error;
        if (!data) throw new Error(`Upsert returned no data for ${table} - check RLS`);
        return data;
      }
    } catch (e) {
      console.error(`[syncToSupabase] ${table} ${operation} failed:`, e.message, e);
      throw e;
    }
  };

  const partnerToRow = (p) => ({
    id: p.id, name: p.name || '', email: p.email || null, phone: p.phone || null,
    status: p.status || 'Active', contact_person: p.contactPerson || null,
    agreement_type: p.agreementType || null, start_date: p.startDate || null,
    end_date: p.endDate || null, tail_end_date: p.tailEndDate || null,
    tail_months: p.tailMonths || 18, sectors: p.sectors || [],
    geography: p.geography || {}, commission_terms: p.commissionTerms || null,
    last_login_date: p.lastLoginDate || null, last_activity_date: p.lastActivityDate || null,
    last_modified_by: p.lastModifiedBy || null, created_at: p.createdAt || new Date().toISOString(),
    nda_signed: p.ndaSigned || false, nda_type: p.ndaType || null, nda_signed_date: p.ndaSignedDate || null,
    is_draft: p.isDraft || false, draft_step: p.isDraft ? (p.draftStep || 1) : null,
  });

  const prospectToRow = (p) => ({
    id: p.id,
    name: p.name || p.companyName || '',
    company_name: p.companyName || null,
    prospect_type: p.prospectType || null,
    contract_type: p.contractType || null,
    introduced_by: p.introducedBy || null,
    introducing_partner_id: p.contractType === 'ThroughPartner' ? (p.introducingPartnerId || null) : null,
    created_by_user_id: p.createdByUserId || null,
    sector: p.sector || null,
    sectors: p.sectors || [],
    country: p.country || null,
    countries: p.countries || [],
    description: p.description || null,
    notes: p.notes || null,
    approval_status: p.approvalStatus || 'Pending',
    approval_date: p.approvalDate || null,
    approval_request_date: p.approvalRequestDate || null,
    auto_reject_deadline: p.autoRejectDeadline || null,
    intro_deadline: p.introDeadline || null,
    intro_occurred: p.introOccurred || false,
    intro_date: p.introDate || null,
    validity_end_date: p.validityEndDate || null,
    investment_focus: p.investmentFocus || null,
    investment_min: p.investmentMin || null,
    investment_max: p.investmentMax || null,
    investment_currency: p.investmentCurrency || null,
    investment_body_type: p.investmentBodyType || null,
    preferred_stage: p.preferredStage || null,
    service_types: p.serviceTypes || [],
    fundraising_min: p.fundraisingMin || null,
    fundraising_max: p.fundraisingMax || null,
    potential_description: p.potentialDescription || null,
    teaser_ids: p.teaserIds || [],
    website_url: p.websiteUrl || null,
    linkedin_person: p.linkedinPerson || null,
    linkedin_company: p.linkedinCompany || null,
    investors_deck_url: p.investorsDeckUrl || null,
    investor_deck_file_url:  p.investorDeckFileUrl  || null,
    investor_deck_file_name: p.investorDeckFileName || null,
    investor_deck_file_size: p.investorDeckFileSize || null,
    hidden_from_partners:    p.hiddenFromPartners || false,
    archived:                p.archived || false,
    is_draft:                p.isDraft || false,
    draft_step:              p.isDraft ? (p.draftStep || 1) : null,
    stage: p.stage || null,
    target_raise: p.targetRaise || null,
    current_valuation: p.currentValuation || null,
    revenue: p.revenue || null,
    created_at: p.createdAt || new Date().toISOString(),
    updated_at: new Date().toISOString(),
  });

  const dealToRow = (d) => ({
    id: d.id,
    deal_name: d.dealName || '',
    deal_state: d.dealState || 'Draft',
    deal_type: d.dealType || null,
    prospect_id: d.prospectId || null,
    teaser_id: d.teaserId || null,
    investor_prospect_id: d.investorProspectId || null,
    service_type: d.serviceType || null,
    currency: d.currency || 'USD',
    deal_size: d.dealSize || null,
    commission_rate: d.commissionRate || null,
    total_agreed: d.totalAgreed || null,
    total_received: d.totalReceived || null,
    is_active_introduction: d.isActiveIntroduction || false,
    override_percentage: d.overridePercentage || null,
    deductions: d.deductions || [],
    strategic_partners: d.strategicPartners || [],
    total_deductions: d.totalDeductions || 0,
    pool_amount: d.poolAmount || 0,
    partner_payouts: d.partnerPayouts || [],
    total_partner_payout: d.totalPartnerPayout || 0,
    company_keeps: d.companyKeeps || 0,
    payments: d.payments || [],
    payment_status: d.paymentStatus || 'Unpaid',
    total_paid: d.totalPaid || 0,
    exposure: d.exposure || {},
    hidden_from_partners: d.hiddenFromPartners || false,
    is_draft: d.isDraft || false,
    draft_step: d.isDraft ? (d.draftStep || 1) : null,
    created_at: d.createdAt || new Date().toISOString(),
    updated_at: new Date().toISOString(),
  });

  const teaserToRow = (t) => ({
    id: t.id,
    name: t.name || '',
    alias: t.alias || null,
    description: t.description || null,
    file_name: t.fileName || null,
    file_size: t.fileSize || null,
    file_url: t.fileUrl || null,
    prospect_id: t.prospectId || null,
    visibility: t.visibility || [],
    version: t.version || '1',
    seen_by: t.seenBy || [],
    sectors: t.sectors || [],
    geography: t.geography || null,
    target_raise: t.targetRaise || null,
    raise_currency: t.raiseCurrency || 'USD',
    stage: t.stage || null,
    hidden_from_partners: t.hiddenFromPartners || false,
    upload_date: t.uploadDate || null,
    uploaded_by: t.uploadedBy || null,
    last_updated: t.lastUpdated || null,
    created_at: t.createdAt || new Date().toISOString(),
  });

  const workflowToRow = (w) => ({
    id:         w.id,
    name:       w.name       || '',
    trigger:    w.trigger    || null,
    conditions: w.conditions || [],
    actions:    w.actions    || [],
    enabled:    w.enabled    ?? true,
    is_draft:   w.isDraft    || false,
    draft_step: w.isDraft ? (w.draftStep || 1) : null,
    created_at: w.createdAt  || w.created_at || new Date().toISOString(),
    updated_at: new Date().toISOString(),
  });

  // ============================================================================
  // AUTOMATED PROCESSES & TIMERS
  // ============================================================================

  useEffect(() => {
    // Automated processes (auto-reject, workflows, notifications) require
    // Editor role or higher. Partners cannot write to notifications and cannot
    // call execute-workflow EF — skip entirely for Partner/Viewer roles.
    if (!currentUser || currentUser.role === 'Partner' || currentUser.role === 'Viewer') return;

    const timer = setInterval(() => {
      checkAutomatedProcesses();
    }, 60000); // Check every minute

    checkAutomatedProcesses(); // Run immediately

    return () => clearInterval(timer);
  }, [prospects, partners, workflows, currentUser?.role]);

  const checkAutomatedProcesses = () => {
    const now = new Date();
    let updatedProspects = [...prospects];
    let newNotifications = [...notifications];
    let hasChanges = false;

    // Auto-reject pending prospects after configured days
    updatedProspects.forEach(prospect => {
      if (prospect.approvalStatus === 'Pending' || prospect.approvalStatus === 'Waiting for Approval') {
        const autoRejectDate = new Date(prospect.autoRejectDeadline);
        if (now > autoRejectDate) {
          prospect.approvalStatus = 'Rejected';
          hasChanges = true;
          
          if (!newNotifications.find(n => n.id === `auto_auto-reject_${prospect.id}`)) {
            newNotifications.push({
              id: `auto_auto-reject_${prospect.id}`,
              type: 'auto-reject',
              message: `Prospect "${prospect.companyName}" was auto-rejected after ${businessRules.autoRejectDays} days`,
              prospectId: prospect.id,
              createdAt: new Date().toISOString(),
              read: false,
              dismissed: false
            });
          }
          
          addAuditLog('Prospect', prospect.id, 'Auto-Rejected', 'system', `Auto-rejected after ${businessRules.autoRejectDays} days without approval`);
          
          executeWorkflows('prospect_auto_rejected', { prospect });
        }
      }
      
      // Check intro deadlines
      if (prospect.approvalStatus === 'Approved' && !prospect.introOccurred && prospect.introDeadline) {
        const introDeadlineDate = new Date(prospect.introDeadline);
        const daysUntilDeadline = daysBetween(now, introDeadlineDate);
        
        if (daysUntilDeadline <= 7 && daysUntilDeadline > 0) {
          const existingNotif = newNotifications.find(n => n.id === `auto_intro-deadline_${prospect.id}`);

          if (!existingNotif) {
            newNotifications.push({
              id: `auto_intro-deadline_${prospect.id}`,
              type: 'intro-deadline',
              message: `Intro deadline approaching for "${prospect.companyName}" (${daysUntilDeadline} days remaining)`,
              prospectId: prospect.id,
              createdAt: new Date().toISOString(),
              read: false,
              dismissed: false
            });

            // Execute workflows for approaching deadline
            executeWorkflows('intro_deadline_approaching', { prospect, daysUntilDeadline });
          }
        }

        // Intro overdue — deadline passed and intro not completed
        if (now > introDeadlineDate && !prospect.introOccurred) {
          const existingNotif = newNotifications.find(n => n.id === `auto_intro-overdue_${prospect.id}`);
          if (!existingNotif) {
            newNotifications.push({
              id: `auto_intro-overdue_${prospect.id}`,
              type: 'intro-overdue',
              message: `Introduction overdue for "${prospect.companyName}"`,
              prospectId: prospect.id,
              createdAt: new Date().toISOString(),
              read: false,
              dismissed: false
            });
            executeWorkflows('intro_overdue', { prospect });
          }
        }
      }

      // Validity expiring in 30 days
      if (prospect.approvalStatus === 'Approved' && prospect.validityEndDate) {
        const daysUntilExpiry = daysBetween(now, new Date(prospect.validityEndDate));
        if (daysUntilExpiry <= 30 && daysUntilExpiry > 0) {
          const existingNotif = newNotifications.find(n => n.id === `auto_validity-expiring_${prospect.id}`);
          if (!existingNotif) {
            newNotifications.push({
              id: `auto_validity-expiring_${prospect.id}`,
              type: 'validity-expiring',
              message: `Validity period expiring in ${daysUntilExpiry} days for "${prospect.companyName}"`,
              prospectId: prospect.id,
              createdAt: new Date().toISOString(),
              read: false,
              dismissed: false
            });
            executeWorkflows('validity_expiring_soon', { prospect, daysUntilExpiry });
          }
        }
      }
    });

    // Update partner statuses
    let updatedPartners = [...partners];
    updatedPartners.forEach(partner => {
      const start = new Date(partner.startDate);
      const end = new Date(partner.endDate);
      const tailEnd = new Date(partner.tailEndDate);
      const validStart = partner.startDate && !isNaN(start);
      const validEnd   = partner.endDate   && !isNaN(end);
      const validTail  = partner.tailEndDate && !isNaN(tailEnd);

      // Default to LEAVING THE STATUS ALONE — never invent 'Terminated'.
      // Previously this defaulted to 'Terminated' and fell through to it whenever
      // the date comparisons were all false, which silently mislabelled (and
      // PERSISTED, see saveData below) two legitimate cases:
      //   1. contract hasn't started yet (start date in the future);
      //   2. missing/invalid dates — `new Date(undefined)` is Invalid Date, so
      //      every comparison is false.
      // Terminated is now only set when the contract has genuinely ended.
      // Matches the logic already used by updatePartner.
      let newStatus = partner.status;
      if (validStart && validEnd) {
        if (now >= start && now <= end) {
          newStatus = 'Active';
        } else if (now < start) {
          newStatus = 'Active';           // agreement signed, not yet started
        } else if (validTail && now > end && now <= tailEnd) {
          newStatus = 'In Tail';
        } else if (now > end) {
          newStatus = 'Terminated';
        }
      }

      if (partner.status !== newStatus) {
        partner.status = newStatus;
        hasChanges = true;
        addAuditLog('Partner', partner.id, 'Status Changed', 'system', `Status changed to ${newStatus}`);
        executeWorkflows('partner_status_changed', { partner, newStatus, oldStatus: partner.status });
        if (newStatus === 'In Tail') executeWorkflows('partner_entering_tail', { partner });
      }

      // Agreement expiring in 30 days
      if (partner.status === 'Active') {
        const daysUntilEnd = daysBetween(now, end);
        if (daysUntilEnd <= 30 && daysUntilEnd > 0) {
          const existingNotif = newNotifications.find(n => n.id === `auto_agreement-expiring_${partner.id}`);
          if (!existingNotif) {
            newNotifications.push({
              id: `auto_agreement-expiring_${partner.id}`,
              type: 'agreement-expiring',
              message: `Agreement expiring in ${daysUntilEnd} days for partner "${partner.name}"`,
              partnerId: partner.id,
              createdAt: new Date().toISOString(),
              read: false,
              dismissed: false
            });
            executeWorkflows('agreement_expiring_soon', { partner, daysUntilEnd });
          }
        }
      }

      // Tail period ending in 30 days
      if (partner.status === 'In Tail') {
        const daysUntilTailEnd = daysBetween(now, tailEnd);
        if (daysUntilTailEnd <= 30 && daysUntilTailEnd > 0) {
          const existingNotif = newNotifications.find(n => n.id === `auto_tail-ending_${partner.id}`);
          if (!existingNotif) {
            newNotifications.push({
              id: `auto_tail-ending_${partner.id}`,
              type: 'tail-ending',
              message: `Tail period ending in ${daysUntilTailEnd} days for partner "${partner.name}"`,
              partnerId: partner.id,
              createdAt: new Date().toISOString(),
              read: false,
              dismissed: false
            });
            executeWorkflows('tail_period_ending', { partner, daysUntilTailEnd });
          }
        }
      }
    });

    if (hasChanges) {
      setProspects(updatedProspects);
      saveData('prospects', updatedProspects);
      setPartners(updatedPartners);
      saveData('partners', updatedPartners);
    }
    
    if (newNotifications.length > notifications.length) {
      setNotifications(newNotifications);
      saveData('notifications', newNotifications);
    }
  };

  // ============================================================================
  // ESC KEY HANDLER FOR DIALOGS
  // ============================================================================
  
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        // Close notifications panel
        if (showNotificationsPanel) {
          setShowNotificationsPanel(false);
        }
        // Close view as dropdown
        if (showViewAsDropdown) {
          setShowViewAsDropdown(false);
          setViewAsSearchQuery('');
        }
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showNotificationsPanel, showViewAsDropdown]);

  // ============================================================================
  // WORKFLOW AUTOMATION ENGINE
  // ============================================================================

  // Fire-and-forget backend workflow execution — handles DB persistence + future emails
  const executeWorkflowsOnBackend = async (triggerEvent, context) => {
    try {
      const { invokeFunction } = await import('./supabaseClient');
      const { data, error } = await invokeFunction('execute-workflow', {
        body: { triggerEvent, context },
      });
      if (error || data?.error) console.error('[executeWorkflows] backend error:', error?.message || data?.error);
      else console.log(`[executeWorkflows] backend executed ${data?.executed}/${data?.total} workflows for: ${triggerEvent}`);
    } catch (e) {
      console.error('[executeWorkflows] backend unexpected error:', e.message, e);
    }
  };

  // Server-only workflow execution. The Edge Function (execute-workflow) is
  // the single source of truth — it filters by trigger, evaluates conditions,
  // executes all action types (send_notification, send_email, update_field,
  // create_task), and writes its own audit-log entry.
  //
  // Earlier this function ALSO ran a client-side action loop alongside the
  // backend call, which produced duplicate notification rows on every trigger
  // and a write race for update_field. See DIAGNOSTIC-REPORT-2026-04-29 #UI-1.5
  // and #3.4. The client loop and its 4 helpers (checkWorkflowConditions,
  // getContextValue, executeWorkflowAction, replaceVariables) were removed in
  // commit fix/workflows-server-only.
  const executeWorkflows = (triggerEvent, context) => {
    // execute-workflow EF requires Editor+. Partners/Viewers would hit 403.
    // For partner-initiated events (e.g. prospect_submitted on partner self-
    // submit), the trigger fires client-side here but the EF rejects. Skip
    // silently — the corresponding workflows for partner actions should be
    // fired server-side from manage-* EFs in a future refactor.
    if (currentUser?.role === 'Partner' || currentUser?.role === 'Viewer') return;
    executeWorkflowsOnBackend(triggerEvent, context);
  };

  // ── Notification Settings → Workflows bridge ──────────────────────────
  // The Settings → Notifications matrix is persisted as individual workflow
  // rows. Each checked cell creates one workflow with two actions
  // (in-app send_notification + send_email). The workflow name follows the
  // pattern "[NotifSettings] {role} · {event label}" so we can round-trip
  // between the matrix and the workflows table.
  const NOTIF_SETTINGS_PREFIX = '[NotifSettings]';
  const NOTIF_EVENT_MAP = {
    'Prospect Submitted': { trigger: 'prospect_submitted',         template: 'prospect-submitted',         subject: 'New Prospect Submitted — *|COMPANY_NAME|*' },
    'Prospect Approved':  { trigger: 'prospect_approved',          template: 'prospect-approved',          subject: 'Prospect Approved — *|COMPANY_NAME|*' },
    'Prospect Rejected':  { trigger: 'prospect_rejected',          template: 'prospect-rejected',          subject: 'Prospect Rejected — *|COMPANY_NAME|*' },
    'Deal Created':       { trigger: 'revenue_deal_created',       template: 'revenue-deal-created',       subject: 'New Deal Created — *|DEAL_NAME|*' },
    'Intro Deadline':     { trigger: 'intro_deadline_approaching', template: 'intro-deadline-approaching', subject: 'Intro Deadline Approaching — *|COMPANY_NAME|*' },
  };
  const NOTIF_ROLE_TOKEN = { Admin: '{admin.emails}', Editor: '{editors.emails}', Partner: '{partner.email}' };

  const saveNotificationSettings = async (matrix) => {
    const { invokeFunction } = await import('./supabaseClient');

    // Target (event, role) pairs that should exist as workflows
    const targetKeys = new Set();
    for (const [event, roleMap] of Object.entries(matrix)) {
      for (const [role, enabled] of Object.entries(roleMap)) {
        if (enabled && NOTIF_EVENT_MAP[event] && NOTIF_ROLE_TOKEN[role]) {
          targetKeys.add(`${role}|${event}`);
        }
      }
    }

    // Existing notification-driven workflows, indexed by parsed key
    const existingByKey = new Map();
    for (const w of workflows) {
      const m = (w.name || '').match(/^\[NotifSettings\] (.+?) · (.+)$/);
      if (m) existingByKey.set(`${m[1]}|${m[2]}`, w);
    }

    // Diff
    const toCreateKeys = [...targetKeys].filter(k => !existingByKey.has(k));
    const toDeleteIds = [];
    for (const [k, w] of existingByKey) {
      if (!targetKeys.has(k)) toDeleteIds.push(w.id);
    }

    if (toCreateKeys.length === 0 && toDeleteIds.length === 0) {
      return { ok: true, saved: 0, failed: 0, noop: true };
    }

    // Build new workflow records for creation
    const now = new Date().toISOString();
    const newWorkflows = toCreateKeys.map(key => {
      const [role, event] = key.split('|');
      const evtCfg = NOTIF_EVENT_MAP[event];
      return {
        id: `workflow_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name: `${NOTIF_SETTINGS_PREFIX} ${role} · ${event}`,
        description: `Auto-generated from Settings → Notifications. Notifies ${role} when ${event}.`,
        trigger: evtCfg.trigger,
        conditions: [],
        actions: [
          { type: 'send_notification', message: event, targetRole: role },
          { type: 'send_email', to: NOTIF_ROLE_TOKEN[role], subject: evtCfg.subject, template_name: evtCfg.template },
        ],
        createdAt: now,
        enabled: true,
      };
    });

    // Run all upserts + deletes in parallel and capture per-call results.
    // The previous implementation used sequential `await` inside try/catch,
    // but invokeFunction never throws on HTTP/EF errors — it returns
    // { data, error }. Result: every EF failure was silently swallowed and
    // the user always saw "Saved!" even on partial/total failure.
    const upsertResults = await Promise.all(newWorkflows.map(async w => {
      const { data, error } = await invokeFunction('manage-workflow', {
        body: { action: 'upsert', payload: workflowToRow(w) },
      });
      return { w, error: error || data?.error || null };
    }));
    const deleteResults = await Promise.all(toDeleteIds.map(async id => {
      const { data, error } = await invokeFunction('manage-workflow', {
        body: { action: 'delete', payload: { id } },
      });
      return { id, error: error || data?.error || null };
    }));

    const succeededUpserts    = upsertResults.filter(r => !r.error).map(r => r.w);
    const failedUpserts       = upsertResults.filter(r =>  r.error);
    const succeededDeleteIds  = deleteResults.filter(r => !r.error).map(r => r.id);
    const failedDeletes       = deleteResults.filter(r =>  r.error);

    // Edge-Function-First: state only reflects what the backend confirmed.
    // Failed creates/deletes are NOT optimistically applied — F5 would
    // expose the lie otherwise.
    if (succeededUpserts.length > 0 || succeededDeleteIds.length > 0) {
      setWorkflows(prev => [
        ...prev.filter(w => !succeededDeleteIds.includes(w.id)),
        ...succeededUpserts,
      ]);
      addAuditLog(
        'Workflow', 'notification-settings', 'Settings Saved',
        currentUser?.id || 'admin',
        `Notification settings saved: +${succeededUpserts.length} −${succeededDeleteIds.length}` +
          (failedUpserts.length || failedDeletes.length
            ? ` (failed: +${failedUpserts.length} −${failedDeletes.length})`
            : '')
      );
    }

    if (failedUpserts.length > 0 || failedDeletes.length > 0) {
      const detailLines = [
        ...failedUpserts.map(r => `· create "${r.w.name}": ${r.error.message || r.error}`),
        ...failedDeletes.map(r => `· delete ${r.id}: ${r.error.message || r.error}`),
      ];
      console.error('[saveNotificationSettings] failures:\n' + detailLines.join('\n'));
      const headline =
        succeededUpserts.length + succeededDeleteIds.length === 0
          ? 'Notification Settings Save Failed'
          : 'Notification Settings — Partial Failure';
      setAppAlert({
        title: headline,
        message:
          `${failedUpserts.length} create(s) failed, ${failedDeletes.length} delete(s) failed. ` +
          `${succeededUpserts.length + succeededDeleteIds.length} change(s) saved successfully. ` +
          `See console for details.`,
      });
    }

    return {
      ok: failedUpserts.length === 0 && failedDeletes.length === 0,
      saved: succeededUpserts.length + succeededDeleteIds.length,
      failed: failedUpserts.length + failedDeletes.length,
    };
  };

  // ============================================================================
  // WIZARD DRAFTS — "Save & Close" on any step of a multi-step wizard
  // ============================================================================
  // Wizards used to be all-or-nothing: only the final step had a submit button,
  // so closing on step 3 threw the work away. Save & Close writes the partial
  // row immediately with is_draft = true plus the step to resume at. Draft rows
  // are filtered out of default lists, revenue/commission totals and Analytics
  // (see `liveOnly` / `draftsOnly`) and surface under each view's "Drafts" chip.
  // Completing the wizard's final step clears is_draft and the row goes live.
  //
  // Edit wizards do NOT go through here — their record is already live, so their
  // Save & Close just calls the normal update handler and closes.

  const DRAFT_ENTITIES = {
    partner:  { fn: 'manage-partner',  toRow: partnerToRow,  audit: 'Partner',  nameOf: r => r.name },
    prospect: { fn: 'manage-prospect', toRow: prospectToRow, audit: 'Prospect', nameOf: r => r.companyName || r.name },
    deal:     { fn: 'manage-deal',     toRow: dealToRow,     audit: 'Deal',     nameOf: r => r.dealName },
    workflow: { fn: 'manage-workflow', toRow: workflowToRow, audit: 'Workflow', nameOf: r => r.name },
  };

  // Look a draft up by id across the draft store.
  const findDraft = (kind, id) => (id ? wizardDrafts[kind]?.find(r => r.id === id) : null) || null;

  // Persist a partially-filled wizard. `row` is the entity object built by the
  // wizard (same shape its final submit would send); `step` is the 1-based step
  // to resume on. Returns true on success so the wizard only closes if the write
  // landed — losing the form to a failed save would defeat the whole feature.
  const saveWizardDraft = async (kind, row, step) => {
    const cfg = DRAFT_ENTITIES[kind];
    if (!cfg) { console.error('[saveWizardDraft] unknown entity:', kind); return false; }

    const existing = findDraft(kind, row.id);
    const draftRow = {
      ...existing,
      ...row,
      id: row.id || `${kind}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      isDraft: true,
      draftStep: step,
      createdAt: existing?.createdAt || new Date().toISOString(),
    };

    // Workflows use a single 'upsert' action; the others split create/update.
    const action = kind === 'workflow' ? 'upsert' : (existing ? 'update' : 'create');

    try {
      const { invokeFunction } = await import('./supabaseClient');
      const { data, error } = await invokeFunction(cfg.fn, {
        body: { action, payload: cfg.toRow(draftRow) },
      });
      if (error || data?.error) {
        console.error(`[saveWizardDraft] ${kind} function error:`, error?.message || data?.error);
        setAppAlert({ title: 'Draft Not Saved', message: error?.message || data?.error });
        return false;
      }
    } catch (e) {
      console.error(`[saveWizardDraft] ${kind} unexpected error:`, e.message, e);
      setAppAlert({ title: 'Draft Not Saved', message: e.message });
      return false;
    }

    setWizardDrafts(d => ({
      ...d,
      [kind]: existing
        ? d[kind].map(r => (r.id === draftRow.id ? draftRow : r))
        : [...d[kind], draftRow],
    }));
    addAuditLog(cfg.audit, draftRow.id, existing ? 'Draft Updated' : 'Draft Saved',
      currentUser?.id || 'admin', `Saved draft at step ${step}: ${cfg.nameOf(draftRow) || '(unnamed)'}`);
    setAppAlert({
      title: 'Draft Saved',
      message: `"${cfg.nameOf(draftRow) || 'Untitled'}" was saved as a draft at step ${step}. Reopen it any time from the Drafts filter to carry on.`,
    });
    return true;
  };

  // Discard a draft outright (the row only ever existed as an unfinished wizard).
  const discardWizardDraft = async (kind, id) => {
    const cfg = DRAFT_ENTITIES[kind];
    if (!cfg) return;
    try {
      const { invokeFunction } = await import('./supabaseClient');
      const { data, error } = await invokeFunction(cfg.fn, { body: { action: 'delete', payload: { id } } });
      if (error || data?.error) {
        setAppAlert({ title: 'Could Not Discard Draft', message: error?.message || data?.error });
        return;
      }
    } catch (e) {
      setAppAlert({ title: 'Could Not Discard Draft', message: e.message });
      return;
    }
    setWizardDrafts(d => ({ ...d, [kind]: d[kind].filter(r => r.id !== id) }));
    addAuditLog(cfg.audit, id, 'Draft Discarded', currentUser?.id || 'admin', 'Discarded unfinished draft');
  };

  // Called when a wizard's final step completes a previously-parked draft: the
  // row is no longer a draft, so drop it from the draft store (the completion
  // handler adds the finished record to the live array).
  const clearDraftFromStore = (kind, id) =>
    setWizardDrafts(d => ({ ...d, [kind]: d[kind].filter(r => r.id !== id) }));

  // Reopen a parked wizard, pre-filled and on the step it was left on.
  const resumePartnerDraft  = (d) => { setDraftPartner(d);  setShowNewPartnerModal(true); };
  const resumeProspectDraft = (d) => { setDraftProspect(d); setShowNewProspectModal(true); };
  const resumeWorkflowDraft = (d) => { setDraftWorkflow(d); setShowNewWorkflowModal(true); };

  // Discarding a draft is destructive and unrecoverable, so it always confirms.
  // Curried so each view can pass its own label resolver straight as a prop.
  const confirmDiscardDraft = (kind, labelOf) => (d) => showConfirm(
    `Discard the draft "${labelOf(d) || 'Untitled'}"? Everything entered so far will be lost.`,
    () => discardWizardDraft(kind, d.id),
    'Discard Draft',
  );

  // Single handler behind both RevenueManagerView instances' onUpdateDeal.
  // The deal wizard reaches this on its final step for BOTH an ordinary edit and
  // the completion of a parked draft — a draft deal lives in the draft store, not
  // in revenueDeals, so it has to be moved across rather than mapped over.
  const updateRevenueDeal = async (dealId, updates) => {
    const draft = findDraft('deal', dealId);
    const target = draft
      ? { ...draft, ...updates, isDraft: false, draftStep: null }
      : (() => {
          const next = revenueDeals.map(d => (d.id === dealId ? { ...d, ...updates } : d));
          setRevenueDeals(next);
          return next.find(d => d.id === dealId);
        })();

    if (draft) {
      clearDraftFromStore('deal', dealId);
      setRevenueDeals([...revenueDeals, target]);
    }
    if (!target) return;

    try {
      const { invokeFunction } = await import('./supabaseClient');
      const { data, error } = await invokeFunction('manage-deal', {
        body: { action: 'update', payload: dealToRow(target) },
      });
      if (error || data?.error) console.error('[updateRevenueDeal] function error:', error?.message || data?.error);
      else console.log('[updateRevenueDeal] deal updated on backend:', dealId);
    } catch (e) { console.error('[updateRevenueDeal] unexpected error:', e.message, e); }

    if (draft) addAuditLog('Deal', dealId, 'Created', currentUser?.id || 'admin', `Completed drafted deal: ${target.dealName}`);
  };

  const addWorkflow = async (workflowData) => {
    // Finishing a draft reuses its id (and original createdAt) so the parked row
    // becomes the live workflow instead of leaving a duplicate behind.
    const draft = findDraft('workflow', workflowData.id);
    const newWorkflow = {
      ...workflowData,
      id: draft?.id || workflowData.id || `workflow_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: draft?.createdAt || new Date().toISOString(),
      enabled: true,
      isDraft: false,
      draftStep: null,
    };

    // A completed draft leaves the draft store and joins the live list.
    if (draft) clearDraftFromStore('workflow', newWorkflow.id);
    const updated = [...workflows, newWorkflow];
    setWorkflows(updated);
    try {
      const { invokeFunction } = await import('./supabaseClient');
      const { data, error } = await invokeFunction('manage-workflow', {
        body: { action: 'upsert', payload: workflowToRow(newWorkflow) },
      });
      if (error || data?.error) console.error('[addWorkflow] function error:', error?.message || data?.error);
      else console.log('[addWorkflow] workflow saved to backend:', newWorkflow.id);
    } catch (e) { console.error('[addWorkflow] unexpected error:', e.message, e); }
    addAuditLog('Workflow', newWorkflow.id, 'Created', currentUser?.id || 'admin', `Created workflow: ${newWorkflow.name}`);
    setShowNewWorkflowModal(false);
    setDraftWorkflow(null);
  };

  const updateWorkflow = async (id, workflowData) => {
    const updatedWorkflow = { ...workflows.find(w => w.id === id), ...workflowData };
    const updated = workflows.map(w => w.id === id ? updatedWorkflow : w);
    setWorkflows(updated);
    try {
      const { invokeFunction } = await import('./supabaseClient');
      const { data, error } = await invokeFunction('manage-workflow', {
        body: { action: 'upsert', payload: workflowToRow(updatedWorkflow) },
      });
      if (error || data?.error) console.error('[updateWorkflow] function error:', error?.message || data?.error);
      else console.log('[updateWorkflow] workflow updated on backend:', id);
    } catch (e) { console.error('[updateWorkflow] unexpected error:', e.message, e); }
    addAuditLog('Workflow', id, 'Updated', currentUser?.id || 'admin', `Updated workflow: ${workflowData.name}`);
    setShowEditWorkflowModal(false);
    setEditingWorkflow(null);
  };

  const toggleWorkflow = (workflowId) => {
    const updated = workflows.map(w => 
      w.id === workflowId ? { ...w, enabled: !w.enabled } : w
    );
    
    setWorkflows(updated);
    saveData('workflows', updated);
    
    const workflow = updated.find(w => w.id === workflowId);
    addAuditLog('Workflow', workflowId, workflow.enabled ? 'Enabled' : 'Disabled', currentUser?.id || 'admin', '');
  };

  const deleteWorkflow = async (workflowId) => {
    try {
      const { invokeFunction } = await import('./supabaseClient');
      const { data, error } = await invokeFunction('manage-workflow', {
        body: { action: 'delete', payload: { id: workflowId } },
      });
      if (error || data?.error) {
        console.error('[deleteWorkflow] function error:', error?.message || data?.error);
        return;
      }
      setWorkflows(prev => prev.filter(w => w.id !== workflowId));
      console.log('[deleteWorkflow] workflow deleted on backend:', workflowId);
    } catch (e) { console.error('[deleteWorkflow] unexpected error:', e.message, e); return; }
    addAuditLog('Workflow', workflowId, 'Deleted', currentUser?.id || 'admin', '');
  };

  // ============================================================================
  // CRUD OPERATIONS
  // ============================================================================

  const addPartner = async (partnerData) => {
    const additionalDates = {
      lastLoginDate: partnerData.lastLoginDate || null,
      lastActivityDate: partnerData.lastActivityDate || null,
      lastModifiedBy: currentUser?.name || currentUser?.id || 'Admin',
      sectors: partnerData.sectors || [],
      geography: partnerData.geography || { continents: [], countries: [] },
      commissionTerms: partnerData.commissionTerms || null,
      tailMonths: partnerData.tailMonths || 18,
    };
    
    const newPartner = createPartner(
      partnerData.name,
      partnerData.contactPerson,
      partnerData.email,
      partnerData.phone,
      partnerData.agreementType,
      partnerData.startDate,
      partnerData.endDate,
      additionalDates
    );

    // Finishing a draft: the row already exists, so update it in place and clear
    // the draft flag instead of inserting a duplicate. createdAt is preserved so
    // the record dates from when the user first started the wizard.
    const draft = findDraft('partner', partnerData.id);
    if (draft) {
      newPartner.id        = draft.id;
      newPartner.createdAt = draft.createdAt || newPartner.createdAt;
    }
    newPartner.isDraft   = false;
    newPartner.draftStep = null;

    try {
      const { invokeFunction } = await import('./supabaseClient');
      const { data, error } = await invokeFunction('manage-partner', {
        body: { action: draft ? 'update' : 'create', payload: partnerToRow(newPartner) },
      });
      if (error || data?.error) {
        console.error('[addPartner] function error:', error?.message || data?.error);
        setAppAlert({ title: 'Failed to Add Partner', message: error?.message || data?.error });
        return;
      }
      console.log('[addPartner] partner saved to backend:', newPartner.id);
    } catch (e) {
      console.error('[addPartner] unexpected error:', e.message, e);
      return;
    }
    // A completed draft leaves the draft store and joins the live list.
    if (draft) clearDraftFromStore('partner', newPartner.id);
    setPartners([...partners, newPartner]);
    addAuditLog('Partner', newPartner.id, 'Created', currentUser?.id || 'admin', `Created partner: ${newPartner.name}`);
    executeWorkflows('partner_created', { partner: newPartner });
    setShowNewPartnerModal(false);
    setDraftPartner(null);
  };

  const updatePartner = async (partnerId, partnerData) => {
    const updated = partners.map(partner => {
      if (partner.id === partnerId) {
        // Recalculate status and tail end date if dates changed
        const start    = partnerData.startDate ? new Date(partnerData.startDate) : (partner.startDate ? new Date(partner.startDate) : null);
        const end      = partnerData.endDate   ? new Date(partnerData.endDate)   : (partner.endDate   ? new Date(partner.endDate)   : null);
        const tailMonths = partnerData.tailMonths || partner.tailMonths || 18;
        const tailEnd  = end ? addMonths(end, tailMonths) : null;
        const now      = new Date();

        let status = partner.status || 'Active';
        if (start && end) {
          if (now >= start && now <= end)              status = 'Active';
          else if (tailEnd && now > end && now <= tailEnd) status = 'In Tail';
          else if (now > (tailEnd || end))             status = 'Terminated';
        }

        return {
          ...partner,
          name: partnerData.name,
          contactPerson: partnerData.contactPerson,
          email: partnerData.email,
          phone: partnerData.phone,
          agreementType: partnerData.agreementType,
          startDate:   start   ? start.toISOString()   : (partner.startDate   || null),
          endDate:     end     ? end.toISOString()     : (partner.endDate     || null),
          tailEndDate: tailEnd ? tailEnd.toISOString() : (partner.tailEndDate || null),
          tailMonths,
          status,
          // Activity tracking
          lastLoginDate: partnerData.lastLoginDate || partner.lastLoginDate,
          lastActivityDate: partnerData.lastActivityDate || partner.lastActivityDate,
          // NDA / MNDA
          ndaSigned: partnerData.ndaSigned ?? partner.ndaSigned ?? false,
          ndaType: partnerData.ndaType ?? partner.ndaType ?? null,
          ndaSignedDate: partnerData.ndaSignedDate || partner.ndaSignedDate || null, // || not ?? — empty string must fall through to null
          location: partnerData.location || partner.location || null,
          // Commission Terms
          commissionTerms: partnerData.commissionTerms || partner.commissionTerms || null,
          sectors: partnerData.sectors || partner.sectors || [],
          geography: partnerData.geography || partner.geography || { continents: [], countries: [] },
          // Administrative
          lastUpdatedDate: new Date().toISOString(),
          lastModifiedBy: currentUser?.name || currentUser?.id || 'Admin'
        };
      }
      return partner;
    });
    
    setPartners(updated);
    saveData('partners', updated);
    const pUpdated = updated.find(p => p.id === partnerId);
    if (pUpdated) {
      try {
        const { invokeFunction } = await import('./supabaseClient');
        const { data, error } = await invokeFunction('manage-partner', {
          body: { action: 'update', payload: partnerToRow(pUpdated) },
        });
        if (error || data?.error) console.error('[updatePartner] function error:', error?.message || data?.error);
        else console.log('[updatePartner] partner updated on backend:', partnerId);
      } catch (e) { console.error('[updatePartner] unexpected error:', e.message, e); }
    }
    addAuditLog('Partner', partnerId, 'Updated', currentUser?.id || 'admin', `Updated partner information`);
    setShowEditPartnerModal(false);
    setEditingPartner(null);
  };

  // Deletion Request System
  const sendDeleteRequest = async (type, entityId, entityName) => {
    const req = {
      id: `req_${Date.now()}`,
      type,           // 'partner' | 'prospect' | 'deal' | 'workflow' | 'user' | 'teaser'
      entityId,
      entityName,
      requestedBy: currentUser.id,
      requestedByName: currentUser.name,
      requestedAt: new Date().toISOString(),
      status: 'Pending',
      adminNote: ''
    };
    try {
      const { db } = await import('./dataLayer');
      const saved = await db.deletionRequests.upsert(req);
      setDeletionRequests(prev => [...prev, saved]);

      const adminNotif = {
        id: `notif_${Date.now()}`,
        type: 'deletion-request',
        message: `${currentUser.name} requested deletion of ${type}: "${entityName}"`,
        requestId: req.id,
        createdAt: new Date().toISOString(),
        read: false,
        targetRole: 'Admin',
      };
      const savedNotif = await db.notifications.upsert(adminNotif);
      setNotifications(prev => [...prev, savedNotif]);
    } catch (e) {
      console.error('[sendDeleteRequest] error:', e.message);
    }
    window.alert(`Deletion request sent to Admin for "${entityName}"`);
    addAuditLog(type, entityId, 'Deletion Requested', currentUser.id, `${currentUser.name} requested deletion of ${type}: ${entityName}`);
  };

  const processDeletionRequest = async (requestId, approved, adminNote = '') => {
    const req = deletionRequests.find(r => r.id === requestId);
    if (!req) return;

    const { db } = await import('./dataLayer');
    const now = new Date().toISOString();

    if (approved) {
      // Call the appropriate Edge Function to delete from Supabase
      const reqType = req.type?.toLowerCase();
      const fnMap = {
        partner:  'manage-partner',
        prospect: 'manage-prospect',
        teaser:   'manage-teaser',
        deal:     'manage-deal',
        workflow: 'manage-workflow',
        user:     'manage-user',
      };
      const fnName = fnMap[reqType];
      if (fnName) {
        try {
          const { invokeFunction } = await import('./supabaseClient');
          const { data, error } = await invokeFunction(fnName, { body: { action: 'delete', payload: { id: req.entityId } } });
          if (error || data?.error) {
            console.error(`[processDeletionRequest] Edge Function error for ${req.type}:`, error?.message || data?.error);
            window.alert(`Failed to delete ${req.type}: ${error?.message || data?.error}`);
            return;
          }
        } catch (e) {
          console.error('[processDeletionRequest] invoke error:', e.message);
          window.alert(`Failed to delete ${req.type}: ${e.message}`);
          return;
        }
      }

      // Remove from React state only after successful Edge Function call
      switch (reqType) {
        case 'partner':  setPartners(prev => prev.filter(p => p.id !== req.entityId)); break;
        case 'prospect': setProspects(prev => prev.filter(p => p.id !== req.entityId)); break;
        case 'deal':     setRevenueDeals(prev => prev.filter(d => d.id !== req.entityId)); break;
        case 'teaser':   setTeasers(prev => prev.filter(t => t.id !== req.entityId)); break;
        case 'workflow': setWorkflows(prev => prev.filter(w => w.id !== req.entityId)); break;
        case 'user':     setUsers(prev => prev.filter(u => u.id !== req.entityId)); break;
      }
      addAuditLog(req.type, req.entityId, 'Deleted (Approved Request)', currentUser.id, `Admin approved deletion of ${req.type}: ${req.entityName}`);
    }

    // Update deletion request in Supabase (both Approve and Reject)
    try {
      const updated = await db.deletionRequests.update(requestId, {
        status: approved ? 'Approved' : 'Rejected',
        adminNote,
        processedBy: currentUser.id,
        processedAt: now,
      });
      setDeletionRequests(prev => prev.map(r => r.id === requestId ? updated : r));
    } catch (e) {
      console.error('[processDeletionRequest] DB update error:', e.message);
      setDeletionRequests(prev => prev.map(r =>
        r.id === requestId ? { ...r, status: approved ? 'Approved' : 'Rejected', adminNote, processedBy: currentUser.id, processedAt: now } : r
      ));
    }

    // Notify the requester
    try {
      const resultNotif = {
        id: `notif_${Date.now()}`,
        type: approved ? 'request-approved' : 'request-rejected',
        message: `Your deletion request for "${req.entityName}" was ${approved ? 'approved' : 'rejected'}${adminNote ? `: ${adminNote}` : ''}`,
        createdAt: now,
        read: false,
        targetUserId: req.requestedBy,
      };
      const savedNotif = await db.notifications.upsert(resultNotif);
      setNotifications(prev => [...prev, savedNotif]);
    } catch (e) {
      console.error('[processDeletionRequest] notification error:', e.message);
    }
  };

  const deletePartner = async (partnerId) => {
    const partner = partners.find(p => p.id === partnerId);
    try {
      const { invokeFunction } = await import('./supabaseClient');
      const { data, error } = await invokeFunction('manage-partner', {
        body: { action: 'delete', payload: { id: partnerId } },
      });
      if (error || data?.error) {
        console.error('[deletePartner] function error:', error?.message || data?.error);
        return;
      }
      setPartners(prev => prev.filter(p => p.id !== partnerId));
      console.log('[deletePartner] partner deleted on backend:', partnerId);
    } catch (e) { console.error('[deletePartner] unexpected error:', e.message, e); return; }
    addAuditLog('Partner', partnerId, 'Deleted', currentUser?.id || 'admin', `Deleted partner: ${partner?.name}`);
  };

  const deleteProspect = async (prospectId) => {
    const linkedDeals = revenueDeals.filter(d => d.prospectId === prospectId);
    const linkedTeasers = teasers.filter(t => t.prospectId === prospectId);
    if (linkedDeals.length > 0 || linkedTeasers.length > 0) {
      const items = [
        ...linkedDeals.map(d => `Deal: "${d.dealName}"`),
        ...linkedTeasers.map(t => `Teaser: "${t.name}"`),
      ];
      setBlockModal({ show: true, title: 'Cannot Delete Prospect', items });
      return;
    }
    const prospect = prospects.find(p => p.id === prospectId);
    try {
      const { invokeFunction } = await import('./supabaseClient');
      const { data, error } = await invokeFunction('manage-prospect', {
        body: { action: 'delete', payload: { id: prospectId } },
      });
      if (error || data?.error) {
        console.error('[deleteProspect] function error:', error?.message || data?.error);
        return;
      }
      setProspects(prev => prev.filter(p => p.id !== prospectId));
      console.log('[deleteProspect] prospect deleted on backend:', prospectId);
    } catch (e) { console.error('[deleteProspect] unexpected error:', e.message, e); return; }
    addAuditLog('Prospect', prospectId, 'Deleted', currentUser?.id || 'admin', `Deleted prospect: ${prospect?.companyName || prospect?.name}`);
  };

  // Calculate commission % based on partner agreement type
  const calculateCommissionPercentage = (partner, dealAmount, isActive, prospectType) => {
    const terms = partner?.commissionTerms || DEFAULT_COMMISSION_TERMS[partner?.agreementType] || null;
    if (!terms) return null;

    switch (terms.type) {
      case 'fixed':
        return terms.percentage ?? 0;

      case 'basic':
        // client vs investor based on prospect type
        const isInvestor = prospectType === 'Angel' || prospectType === 'InvestmentBody';
        return isInvestor ? terms.investorPercentage : terms.clientPercentage;

      case 'table':
        const amount = parseFloat(dealAmount) || 0;
        const bracket = terms.brackets?.find(b => b.upTo === null || amount <= b.upTo);
        if (!bracket) return null;
        return isActive ? bracket.active : bracket.nonActive;

      case 'pool':
        return terms.poolSplitPercentage ?? null;

      default:
        return null;
    }
  };

  const deleteDeal = async (dealId) => {
    const deal = revenueDeals.find(d => d.id === dealId);
    try {
      const { invokeFunction } = await import('./supabaseClient');
      const { data, error } = await invokeFunction('manage-deal', {
        body: { action: 'delete', payload: { id: dealId } },
      });
      if (error || data?.error) {
        console.error('[deleteDeal] function error:', error?.message || data?.error);
        return;
      }
      setRevenueDeals(prev => prev.filter(d => d.id !== dealId));
      console.log('[deleteDeal] deal deleted on backend:', dealId);
    } catch (e) { console.error('[deleteDeal] unexpected error:', e.message, e); return; }
    addAuditLog('Deal', dealId, 'Deleted', currentUser?.id || 'admin', `Deleted deal: ${deal?.dealName}`);
  };

  const addPaymentToDeal = async (dealId, payment) => {
    const deal = revenueDeals.find(d => d.id === dealId);
    if (!deal) return;
    const payments = [...(deal.payments || []), payment];
    const totalPaid = payments.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
    const totalAgreed = parseFloat(deal.totalAgreed) || 0;
    const paymentStatus = totalPaid >= totalAgreed && totalAgreed > 0 ? 'Paid in Full' : totalPaid > 0 ? 'Partial' : 'Unpaid';
    const updated = { ...deal, payments, totalPaid, paymentStatus };
    try {
      const { invokeFunction } = await import('./supabaseClient');
      await invokeFunction('manage-deal', { body: { action: 'update', payload: dealToRow(updated) } });
    } catch (e) {
      console.error('[addPaymentToDeal] unexpected error:', e.message, e);
      return;
    }
    setRevenueDeals(prev => prev.map(d => d.id === dealId ? updated : d));
    addAuditLog('Deal', dealId, 'Payment Recorded', currentUser?.id || 'admin', `Payment of $${payment.amount} recorded`);
  };

  // Cancel/delete a recorded payment by index. Admin/Super Admin only — gated
  // at the call site. Mirrors addPaymentToDeal: rebuild payments array,
  // recompute totalPaid + paymentStatus, persist via manage-deal EF first,
  // then update state and write an audit-log entry.
  const deletePaymentFromDeal = async (dealId, paymentIndex) => {
    const deal = revenueDeals.find(d => d.id === dealId);
    if (!deal) return;
    const removed = (deal.payments || [])[paymentIndex];
    if (!removed) return;
    const payments = (deal.payments || []).filter((_, i) => i !== paymentIndex);
    const totalPaid = payments.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
    const totalAgreed = parseFloat(deal.totalAgreed) || 0;
    const paymentStatus = totalPaid >= totalAgreed && totalAgreed > 0 ? 'Paid in Full' : totalPaid > 0 ? 'Partial' : 'Unpaid';
    const updated = { ...deal, payments, totalPaid, paymentStatus };
    try {
      const { invokeFunction } = await import('./supabaseClient');
      const { data, error } = await invokeFunction('manage-deal', { body: { action: 'update', payload: dealToRow(updated) } });
      if (error || data?.error) {
        console.error('[deletePaymentFromDeal] function error:', error?.message || data?.error);
        setAppAlert({ title: 'Cancel Failed', message: error?.message || data?.error || 'Unknown error' });
        return;
      }
    } catch (e) {
      console.error('[deletePaymentFromDeal] unexpected error:', e.message, e);
      return;
    }
    setRevenueDeals(prev => prev.map(d => d.id === dealId ? updated : d));
    addAuditLog('Deal', dealId, 'Payment Cancelled', currentUser?.id || 'admin', `Payment of $${removed.amount} (${removed.type || 'Payment'}) cancelled`);
  };

  // Toggle the master "hide from partners" switch on a prospect/deal/teaser.
  // Editor+ only. Goes through manage-<entity> update (Edge-Function-First) so
  // RLS sees the new value; partner SELECT policies already enforce the hide.
  const toggleHiddenFromPartners = async (entity, id, nextValue) => {
    const efName = entity === 'prospect' ? 'manage-prospect'
                 : entity === 'deal'     ? 'manage-deal'
                 : entity === 'teaser'   ? 'manage-teaser'
                 : null;
    if (!efName) return;
    const listSetter = entity === 'prospect' ? setProspects
                     : entity === 'deal'     ? setRevenueDeals
                     : setTeasers;
    const list = entity === 'prospect' ? prospects
               : entity === 'deal'     ? revenueDeals
               : teasers;
    const current = list.find(r => r.id === id);
    if (!current) return;
    const updated = { ...current, hiddenFromPartners: !!nextValue };
    const row = entity === 'prospect' ? prospectToRow(updated)
              : entity === 'deal'     ? dealToRow(updated)
              : teaserToRow(updated);
    try {
      const { invokeFunction } = await import('./supabaseClient');
      const { data, error } = await invokeFunction(efName, { body: { action: 'update', payload: row } });
      if (error || data?.error) {
        setAppAlert({ title: 'Update Failed', message: error?.message || data?.error || 'Unknown error' });
        return;
      }
    } catch (e) {
      console.error('[toggleHiddenFromPartners] unexpected error:', e.message, e);
      return;
    }
    listSetter(prev => prev.map(r => r.id === id ? updated : r));
    const entityLabel = entity.charAt(0).toUpperCase() + entity.slice(1);
    addAuditLog(entityLabel, id, nextValue ? 'Hidden from partners' : 'Shown to partners',
      currentUser?.id || 'admin', `Set hidden_from_partners=${nextValue}`);
  };

  // Archive / unarchive a prospect (Editor+; reversible). Edge-Function-First:
  // persist via manage-prospect, then update state + audit log.
  const setProspectArchived = async (id, nextValue) => {
    const current = prospects.find(p => p.id === id);
    if (!current) return;
    const updated = { ...current, archived: !!nextValue };
    try {
      const { invokeFunction } = await import('./supabaseClient');
      const { data, error } = await invokeFunction('manage-prospect', {
        body: { action: 'update', payload: prospectToRow(updated) },
      });
      if (error || data?.error) {
        setAppAlert({ title: 'Update Failed', message: error?.message || data?.error || 'Unknown error' });
        return;
      }
    } catch (e) {
      console.error('[setProspectArchived] unexpected error:', e.message, e);
      return;
    }
    setProspects(prev => prev.map(p => p.id === id ? updated : p));
    addAuditLog('Prospect', id, nextValue ? 'Archived' : 'Unarchived',
      currentUser?.id || 'admin', `Set archived=${nextValue}`);
  };

  const updateProspect = async (prospectId, prospectData) => {
    const updated = prospects.map(prospect => {
      if (prospect.id === prospectId) {
        return {
          ...prospect,
          name: prospectData.name || prospectData.companyName || prospect.name,
          companyName: prospectData.companyName || prospectData.name || prospect.companyName,
          contractType: prospectData.contractType || prospect.contractType || 'Direct',
          introducingPartnerId: prospectData.contractType === 'ThroughPartner' ? (prospectData.introducingPartnerId || prospect.introducingPartnerId || null) : null,
          createdByUserId: prospect.createdByUserId || null,
          sector: prospectData.sector || '',
          country: prospectData.country || '',
          sectors: prospectData.sectors || [],
          countries: prospectData.countries || [],
          notes: prospectData.notes || '',
          description: prospectData.description ?? prospect.description ?? '',
          investmentFocus: prospectData.investmentFocus || [],
          investmentMin: prospectData.investmentMin || '',
          investmentMax: prospectData.investmentMax || '',
          investmentCurrency: prospectData.investmentCurrency || 'USD',
          investmentBodyType: prospectData.investmentBodyType || prospect.investmentBodyType || '',
          preferredStage: prospectData.preferredStage || prospect.preferredStage || '',
          serviceTypes: prospectData.serviceTypes || [],
          fundraisingMin: prospectData.fundraisingMin || prospect.fundraisingMin || '',
          fundraisingMax: prospectData.fundraisingMax || prospect.fundraisingMax || '',
          potentialDescription: prospectData.potentialDescription || '',
          websiteUrl: prospectData.websiteUrl ?? prospect.websiteUrl ?? '',
          linkedinPerson: prospectData.linkedinPerson ?? prospect.linkedinPerson ?? '',
          linkedinCompany: prospectData.linkedinCompany ?? prospect.linkedinCompany ?? '',
          investorsDeckUrl: prospectData.investorsDeckUrl ?? prospect.investorsDeckUrl ?? '',
          investorDeckFileUrl:  prospectData.investorDeckFileUrl  ?? prospect.investorDeckFileUrl  ?? '',
          investorDeckFileName: prospectData.investorDeckFileName ?? prospect.investorDeckFileName ?? '',
          investorDeckFileSize: prospectData.investorDeckFileSize ?? prospect.investorDeckFileSize ?? 0,
          stage: prospectData.stage ?? prospect.stage ?? '',
          targetRaise: prospectData.targetRaise ?? prospect.targetRaise ?? '',
          currentValuation: prospectData.currentValuation ?? prospect.currentValuation ?? '',
          revenue: prospectData.revenue ?? prospect.revenue ?? '',
          ndaSigned: prospectData.ndaSigned ?? prospect.ndaSigned ?? false,
          ndaType: prospectData.ndaType || prospect.ndaType || null,
          ndaSignedDate: prospectData.ndaSignedDate || prospect.ndaSignedDate || null,
        };
      }
      return prospect;
    });

    const pUpdated = updated.find(p => p.id === prospectId);
    if (!pUpdated) return;

    // Edge-Function-First: confirm backend write before mutating local state.
    try {
      const { invokeFunction } = await import('./supabaseClient');
      const { data, error } = await invokeFunction('manage-prospect', {
        body: { action: 'update', payload: prospectToRow(pUpdated) },
      });
      if (error || data?.error) {
        const msg = error?.message || data?.error || 'Unknown error.';
        console.error('[updateProspect] function error:', msg);
        setAppAlert({ title: 'Update Failed', message: msg });
        return;
      }
      console.log('[updateProspect] prospect updated on backend:', prospectId);
    } catch (e) {
      console.error('[updateProspect] unexpected error:', e.message, e);
      setAppAlert({ title: 'Update Failed', message: e.message || 'Network error — please try again.' });
      return;
    }

    setProspects(updated);
    addAuditLog('Prospect', prospectId, 'Updated', currentUser?.id || 'admin', `Updated prospect: ${prospectData.companyName || prospectData.name}`);
    setShowEditProspectModal(false);
    setEditingProspect(null);
  };

  const approveProspect = (prospectId) => {
    const updated = prospects.map(p => {
      if (p.id === prospectId) {
        const now = new Date();
        return {
          ...p,
          approvalStatus: 'Approved',
          approvalDate: now.toISOString(),
          introDeadline: addDays(now, businessRules.introWindowDays).toISOString(),
          validityEndDate: addMonths(now, businessRules.tailMonths).toISOString()
        };
      }
      return p;
    });
    
    setProspects(updated);
    saveData('prospects', updated);
    
    const prospect = updated.find(p => p.id === prospectId);
    addAuditLog('Prospect', prospectId, 'Approved', currentUser?.id || 'admin', `Approved prospect: ${prospect.companyName}`);
    
    // Send email notification
    sendInAppNotification(prospect.introducingPartnerId, 'prospect-approved', prospect);
    
    // Execute workflows
    executeWorkflows('prospect_approved', { prospect });
  };

  const rejectProspect = (prospectId) => {
    const updated = prospects.map(p => 
      p.id === prospectId ? { ...p, approvalStatus: 'Rejected' } : p
    );
    
    setProspects(updated);
    saveData('prospects', updated);
    
    const prospect = updated.find(p => p.id === prospectId);
    addAuditLog('Prospect', prospectId, 'Rejected', currentUser?.id || 'admin', `Rejected prospect: ${prospect.companyName}`);
    
    // Send email notification
    sendInAppNotification(prospect.introducingPartnerId, 'prospect-rejected', prospect);
    
    // Execute workflows
    executeWorkflows('prospect_rejected', { prospect });
  };

  const submitProspect = async (prospectData) => {
    // Partner role: use their own partner record as the introducing partner.
    // Admin/Editor: use whatever the form selected (could be null for Direct contracts).
    const partnerFromProfile = partners.find(p => p.id === currentUser?.partnerId || p.email === currentUser?.email);
    const introducingPartnerId = partnerFromProfile?.id || prospectData.introducingPartnerId || null;

    // Force ThroughPartner for partners — otherwise prospectToRow drops the
    // partner link (line ~1429) and the prospect appears as orphaned.
    const data = currentUser?.role === 'Partner'
      ? { ...prospectData, contractType: 'ThroughPartner' }
      : prospectData;

    const newProspect = createProspect(
      data,
      introducingPartnerId,
      currentUser?.id || null,
      businessRules.autoRejectDays
    );
    // Honour caller-supplied id (used when a file was uploaded under that id).
    if (data.id) newProspect.id = data.id;

    // Finishing a draft: update the existing row and clear the draft flag rather
    // than inserting a duplicate. createdAt dates from when the wizard started.
    const draft = findDraft('prospect', data.id);
    if (draft) newProspect.createdAt = draft.createdAt || newProspect.createdAt;
    newProspect.isDraft   = false;
    newProspect.draftStep = null;

    try {
      const { invokeFunction } = await import('./supabaseClient');
      const { data, error } = await invokeFunction('manage-prospect', {
        body: { action: draft ? 'update' : 'create', payload: prospectToRow(newProspect) },
      });
      if (error || data?.error) {
        setAppAlert({ title: 'Failed to Submit Prospect', message: error?.message || data?.error });
        return;
      }
      console.log('[submitProspect] prospect saved to backend:', newProspect.id);
    } catch (e) { console.error('[submitProspect] unexpected error:', e.message, e); return; }
    // A completed draft leaves the draft store and joins the live list.
    if (draft) clearDraftFromStore('prospect', newProspect.id);
    const updated = [...prospects, newProspect];
    setProspects(updated);
    addAuditLog('Prospect', newProspect.id, 'Created', currentUser?.id, `Submitted prospect: ${newProspect.companyName || newProspect.name}`);

    // Execute workflows
    executeWorkflows('prospect_submitted', { prospect: newProspect, partner: partnerFromProfile });

    setShowNewProspectModal(false);
    setDraftProspect(null);
    setAppAlert({ title: 'Prospect Submitted', message: `Prospect "${newProspect.companyName || newProspect.name}" submitted successfully and awaiting approval.` });
  };

  const logIntro = (prospectId, introDate) => {
    const updated = prospects.map(p => 
      p.id === prospectId ? { 
        ...p, 
        introOccurred: true, 
        introDate: new Date(introDate).toISOString() 
      } : p
    );
    
    setProspects(updated);
    saveData('prospects', updated);
    
    const prospect = updated.find(p => p.id === prospectId);
    addAuditLog('Prospect', prospectId, 'Intro Logged', currentUser?.id || 'admin', `Intro logged for: ${prospect.companyName}`);
    
    // Execute workflows
    executeWorkflows('intro_completed', { prospect, introDate });
  };

  // Denormalize raise/stage from linked prospect onto the teaser row.
  // Partners can't read prospects via RLS, so they need this data on the teaser itself.
  const enrichTeaserFromProspect = (teaser) => {
    if (!teaser?.prospectId) return teaser;
    const p = prospects.find(x => x.id === teaser.prospectId);
    if (!p) return teaser;
    return {
      ...teaser,
      targetRaise:   teaser.targetRaise   || p.targetRaise || p.investmentMax || p.fundraisingMax || null,
      raiseCurrency: teaser.raiseCurrency || p.investmentCurrency || 'USD',
      stage:         teaser.stage         || p.preferredStage || null,
    };
  };

  const addTeaser = async (teaserData, visibility) => {
    // TeasersView's upload wizard passes a pre-built teaser (has id + fileUrl).
    const newTeaser = enrichTeaserFromProspect(teaserData.id
      ? { ...teaserData, visibility: teaserData.visibility || [] }
      : createTeaser(teaserData, visibility));
    const updated = [...teasers, newTeaser];
    setTeasers(updated);
    try {
      const { invokeFunction } = await import('./supabaseClient');
      const { data, error } = await invokeFunction('manage-teaser', {
        body: { action: 'create', payload: teaserToRow(newTeaser) },
      });
      if (error || data?.error) console.error('[addTeaser] function error:', error?.message || data?.error);
      else console.log('[addTeaser] teaser saved to backend:', newTeaser.id);
    } catch (e) { console.error('[addTeaser] unexpected error:', e.message, e); }
    addAuditLog('Teaser', newTeaser.id, 'Created', currentUser?.id || 'admin', `Created teaser: ${newTeaser.name || newTeaser.companyName}`);
    executeWorkflows('teaser_published', { teaser: newTeaser });

    // If linked to a prospect, update that prospect's teaserIds
    if (newTeaser.prospectId) {
      const updatedProspects = prospects.map(p =>
        p.id === newTeaser.prospectId
          ? { ...p, teaserIds: [...(p.teaserIds || []), newTeaser.id] }
          : p
      );
      setProspects(updatedProspects);
      saveData('prospects', updatedProspects);
    }
    
    // Send email notifications to relevant partners
    if (visibility === 'all') {
      partners.forEach(partner => {
        sendInAppNotification(partner.id, 'new-teaser', newTeaser);
      });
    } else if (Array.isArray(visibility)) {
      visibility.forEach(partnerId => {
        sendInAppNotification(partnerId, 'new-teaser', newTeaser);
      });
    }
  };

  const updateTeaser = async (teaserId, teaserData, visibility) => {
    // If a new file was selected, upload it to Storage first
    let newFileUrl = null;
    if (teaserData._newRawFile) {
      try {
        const { uploadTeaserFile } = await import('./supabaseClient');
        newFileUrl = await uploadTeaserFile(teaserId, teaserData._newRawFile);
      } catch (e) {
        console.error('[updateTeaser] file upload failed:', e.message);
        window.alert('File upload failed: ' + e.message);
        return;
      }
    }

    const updated = teasers.map(t => {
      if (t.id === teaserId) {
        const currentVersion = parseFloat(t.version) || 1.0;
        const newVersion = (currentVersion + 0.1).toFixed(1);
        return {
          ...t,
          name: teaserData.name || teaserData.companyName || t.name,
          alias: teaserData.alias !== undefined ? teaserData.alias : t.alias,
          description: teaserData.description || teaserData.summary || t.description,
          companyName: teaserData.name || teaserData.companyName || t.companyName,
          prospectId: teaserData.prospectId !== undefined ? (teaserData.prospectId || null) : t.prospectId,
          prospectName: teaserData.prospectId !== undefined
            ? (prospects.find(p => p.id === teaserData.prospectId)?.companyName || null)
            : t.prospectName,
          sectors: teaserData.sectors !== undefined ? teaserData.sectors : t.sectors,
          geography: teaserData.geography !== undefined ? teaserData.geography : t.geography,
          targetRaise:   teaserData.targetRaise   !== undefined ? teaserData.targetRaise   : t.targetRaise,
          raiseCurrency: teaserData.raiseCurrency !== undefined ? teaserData.raiseCurrency : t.raiseCurrency,
          stage:         teaserData.stage         !== undefined ? teaserData.stage         : t.stage,
          version: newVersion,
          visibility,
          lastUpdated: new Date().toISOString(),
          lastModifiedBy: currentUser?.id || 'admin',
          ...(newFileUrl ? { fileUrl: newFileUrl, fileName: teaserData._newFileName, fileSize: teaserData._newFileSize } : {}),
        };
      }
      return t;
    });
    
    // Enrich the edited teaser with prospect fallback before persisting
    const enrichedUpdated = updated.map(t => t.id === teaserId ? enrichTeaserFromProspect(t) : t);
    setTeasers(enrichedUpdated);
    // NOTE: do NOT saveData('teasers', ...) here. The manage-teaser EF call
    // below is the canonical write (uses teaserToRow which omits company_name).
    // dataLayer's global toSnake maps companyName -> company_name, but the
    // teasers table has no such column, so a direct upsert via saveData fails
    // with PGRST204 'Could not find company_name column' (May 18, 2026).
    const tUpdated = enrichedUpdated.find(t => t.id === teaserId);
    if (tUpdated) {
      try {
        const { invokeFunction } = await import('./supabaseClient');
        const { data, error } = await invokeFunction('manage-teaser', {
          body: { action: 'update', payload: teaserToRow(tUpdated) },
        });
        if (error || data?.error) console.error('[updateTeaser] function error:', error?.message || data?.error);
        else console.log('[updateTeaser] teaser updated on backend:', teaserId);
      } catch (e) { console.error('[updateTeaser] unexpected error:', e.message, e); }
    }
    
    const teaser = tUpdated;
    addAuditLog('Teaser', teaserId, 'Updated', currentUser?.id || 'admin', `Updated teaser: ${teaser.companyName} to version ${teaser.version}`);
    
    // Send email notifications about significant update
    if (visibility === 'all') {
      partners.forEach(partner => {
        sendInAppNotification(partner.id, 'teaser-updated', teaser);
      });
    } else if (Array.isArray(visibility)) {
      visibility.forEach(partnerId => {
        sendInAppNotification(partnerId, 'teaser-updated', teaser);
      });
    }
    
    setShowEditTeaserModal(false);
  };


  const showConfirm = (message, onConfirm, confirmLabel = 'Confirm') => setConfirmDialog({ show: true, message, onConfirm, confirmLabel });

  const addAuditLog = async (entityType, entityId, action, _userId, details) => {
    // Optimistic local update for immediate UI feedback
    const localLog = logAudit(entityType, entityId, action, currentUser?.id || '', details);
    setAuditLogs(prev => [...prev, localLog]);

    // Write via backend — server stamps userId + timestamp (cannot be faked by client)
    try {
      const { invokeFunction } = await import('./supabaseClient');
      const { error } = await invokeFunction('log-audit', {
        body: { entityType, entityId, action, details },
      });
      if (error) console.error('[addAuditLog] function error:', error.message, error);
    } catch (e) {
      console.error('[addAuditLog] unexpected error:', e.message, e);
    }
  };

  // sendDeleteRequest is defined below near deletePartner

  // Writes an in-app notification row only — does NOT send email.
  // Email is sent (when a matching workflow + Mandrill template exist) by the
  // execute-workflow Edge Function path. Renamed from sendEmailNotification
  // (April 2026) because the old name was misleading; behaviour unchanged.
  const sendInAppNotification = (partnerId, type, data) => {
    const partner = partners.find(p => p.id === partnerId);
    if (!partner) return;
    
    let message = '';
    switch(type) {
      case 'prospect-approved':
        message = `Your prospect "${data.companyName || data.name}" has been approved!`;
        break;
      case 'prospect-rejected':
        message = `Your prospect "${data.companyName || data.name}" has been rejected.`;
        break;
      case 'new-teaser':
        message = `A new teaser "${data.alias || data.companyName || data.name}" has been published.`;
        break;
      case 'teaser-updated':
        message = `A teaser has been updated: "${data.alias || data.companyName || data.name}".`;
        break;
      case 'deal-closed':
        message = `A new revenue deal "${data.dealName}" has been created that involves you.`;
        break;
      case 'payment-received':
        message = `Payment received for deal "${data.dealName}".`;
        break;
    }
    
    
    const newNotif = {
      id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type,
      message,
      partnerId,
      createdAt: new Date().toISOString(),
      read: false
    };
    
    const updated = [...notifications, newNotif];
    setNotifications(updated);
    saveData('notifications', updated);
  };

  // ============================================================================
  // SEARCH & FILTERING
  // ============================================================================

  const performGlobalSearch = (searchTerm) => {
    if (!searchTerm) return { partners: [], prospects: [], teasers: [] };
    
    const term = searchTerm.toLowerCase();
    const isPartner = currentUser?.role === 'Partner';
    const partnerIdForFilter = currentUser?.partnerId || currentUser?.id;

    // Partners: Partner role cannot search other partners
    const searchedPartners = isPartner ? [] : partners.filter(p =>
      p.name?.toLowerCase().includes(term) ||
      p.contactPerson?.toLowerCase().includes(term) ||
      p.email?.toLowerCase().includes(term) ||
      p.agreementType?.toLowerCase().includes(term)
    );
    
    // Prospects: Partner sees only their own
    const prospectsPool = isPartner
      ? prospects.filter(p => p.introducingPartnerId === partnerIdForFilter)
      : prospects;

    const searchedProspects = prospectsPool.filter(p =>
      p.companyName?.toLowerCase().includes(term) ||
      p.investorName?.toLowerCase().includes(term) ||
      p.sector?.toLowerCase().includes(term) ||
      p.country?.toLowerCase().includes(term) ||
      p.description?.toLowerCase().includes(term) ||
      (p.introducingPartnerId && partners.find(pt => pt.id === p.introducingPartnerId)?.name?.toLowerCase().includes(term))
    );
    
    const searchedTeasers = teasers.filter(t =>
      t.companyName?.toLowerCase().includes(term) ||
      t.opportunityName?.toLowerCase().includes(term) ||
      t.summary?.toLowerCase().includes(term) ||
      t.content?.toLowerCase().includes(term)
    );
    
    return { 
      partners: searchedPartners, 
      prospects: searchedProspects, 
      teasers: searchedTeasers 
    };
  };

  const applyAdvancedFilters = (data, entityType) => {
    let filtered = [...data];
    
    // Date range filter
    if (advancedFilters.dateRange.start && advancedFilters.dateRange.end) {
      const startDate = new Date(advancedFilters.dateRange.start);
      const endDate = new Date(advancedFilters.dateRange.end);
      
      filtered = filtered.filter(item => {
        const itemDate = new Date(item.createdAt || item.approvalDate || item.startDate);
        return itemDate >= startDate && itemDate <= endDate;
      });
    }
    
    // Partner filter
    if (advancedFilters.partners.length > 0 && entityType === 'prospects') {
      filtered = filtered.filter(item => 
        advancedFilters.partners.includes(item.introducingPartnerId)
      );
    }
    
    // Status filter
    if (advancedFilters.statuses.length > 0) {
      filtered = filtered.filter(item => 
        advancedFilters.statuses.includes(item.approvalStatus || item.status)
      );
    }
    
    // Sector filter — prospects use scalar `sector`, teasers use `sectors[]`
    if (advancedFilters.sectors.length > 0) {
      if (entityType === 'prospects') {
        filtered = filtered.filter(item => advancedFilters.sectors.includes(item.sector));
      } else if (entityType === 'teasers') {
        filtered = filtered.filter(item =>
          (item.sectors || []).some(s => advancedFilters.sectors.includes(s))
        );
      }
    }

    // Geography (continents) filter — teasers only
    if (entityType === 'teasers' && advancedFilters.continents?.length > 0) {
      filtered = filtered.filter(item =>
        (item.geography?.continents || []).some(c => advancedFilters.continents.includes(c))
      );
    }

    // Raise amount range — teasers only
    if (entityType === 'teasers') {
      const min = parseFloat(advancedFilters.raiseRange?.min);
      const max = parseFloat(advancedFilters.raiseRange?.max);
      if (!isNaN(min)) filtered = filtered.filter(item => (parseFloat(item.targetRaise) || 0) >= min);
      if (!isNaN(max)) filtered = filtered.filter(item => (parseFloat(item.targetRaise) || 0) <= max);
    }

    return filtered;
  };

  const clearFilters = () => {
    setAdvancedFilters({
      dateRange: { start: '', end: '' },
      partners: [],
      statuses: [],
      sectors: [],
      continents: [],
      raiseRange: { min: '', max: '' }
    });
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (advancedFilters.dateRange.start || advancedFilters.dateRange.end) count++;
    if (advancedFilters.partners.length > 0) count++;
    if (advancedFilters.statuses.length > 0) count++;
    if (advancedFilters.sectors.length > 0) count++;
    if (advancedFilters.continents?.length > 0) count++;
    if (advancedFilters.raiseRange?.min || advancedFilters.raiseRange?.max) count++;
    return count;
  };

  // ============================================================================
  // NEW FEATURES: Quick Actions, Comments, Saved Filters, Enhanced Search
  // ============================================================================

  // Quick View functions
  const openQuickView = (item, type) => {
    setQuickViewItem(item);
    setQuickViewType(type);
    setShowQuickView(true);
  };

  const closeQuickView = () => {
    setQuickViewItem(null);
    setQuickViewType(null);
    setShowQuickView(false);
  };

  // Comments functions  


  // Saved Filters functions
  const saveFilterView = (name) => {
    const newSavedFilter = {
      id: `filter_${Date.now()}`,
      name,
      filters: { ...advancedFilters },
      search: globalSearch,
      createdAt: new Date().toISOString()
    };
    
    const updated = [...savedFilters, newSavedFilter];
    setSavedFilters(updated);
    saveData('savedFilters', updated);
    window.alert(`Filter view "${name}" saved successfully!`);
  };

  const loadFilterView = (filterId) => {
    const filter = savedFilters.find(f => f.id === filterId);
    if (filter) {
      setAdvancedFilters(filter.filters);
      setGlobalSearch(filter.search);
    }
  };

  const deleteFilterView = (filterId) => {
    showConfirm('Delete this saved filter?', () => {
      const updated = savedFilters.filter(f => f.id !== filterId);
      setSavedFilters(updated);
      saveData('savedFilters', updated);
    }, 'Delete');
  };

  // Global search with history
  const updateGlobalSearch = (searchTerm) => {
    setGlobalSearch(searchTerm);
    
    if (searchTerm && !searchHistory.includes(searchTerm)) {
      const updated = [searchTerm, ...searchHistory].slice(0, 10);
      setSearchHistory(updated);
      saveData('searchHistory', updated);
    }
  };

  // Smart filters
  const getSmartFilters = () => {
    const now = new Date();
    
    return {
      expiringThisWeek: prospects.filter(p => {
        if (!p.introDeadline) return false;
        const deadline = new Date(p.introDeadline);
        const daysUntil = Math.floor((deadline - now) / (1000 * 60 * 60 * 24));
        return daysUntil >= 0 && daysUntil <= 7;
      }),
      noActivityLast30Days: prospects.filter(p => {
        const lastActivity = new Date(p.updatedAt || p.createdAt);
        const daysSince = Math.floor((now - lastActivity) / (1000 * 60 * 60 * 24));
        return daysSince >= 30;
      }),
      overdueIntros: prospects.filter(p => {
        if (!p.introDeadline || p.introOccurred) return false;
        return new Date(p.introDeadline) < now;
      }),
      highValueDeals: transactions.filter(t => t.transactionAmount > 50000000)
    };
  };

  // Calculate trends for dashboard
  const calculateTrends = () => {
    const now = new Date();
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    
    const lastMonthProspects = prospects.filter(p => {
      const created = new Date(p.createdAt);
      return created >= lastMonth && created < thisMonth;
    }).length;
    
    const thisMonthProspects = prospects.filter(p => {
      const created = new Date(p.createdAt);
      return created >= thisMonth;
    }).length;
    
    const prospectsTrend = lastMonthProspects > 0 
      ? ((thisMonthProspects - lastMonthProspects) / lastMonthProspects * 100).toFixed(1)
      : 0;
    
    const approvalRate = prospects.filter(p => p.approvalStatus !== 'Pending').length > 0
      ? (prospects.filter(p => p.approvalStatus === 'Approved').length / 
         prospects.filter(p => p.approvalStatus !== 'Pending').length * 100).toFixed(1)
      : 0;
    
    return {
      prospectsTrend: Number(prospectsTrend),
      approvalRate: Number(approvalRate),
      totalDeals: transactions.length,
      avgDealSize: transactions.length > 0 
        ? (transactions.reduce((sum, t) => sum + t.transactionAmount, 0) / transactions.length / 1000000).toFixed(1)
        : 0
    };
  };

  // Get recent activity
  const getRecentActivity = () => {
    return auditLogs
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, 10);
  };

  // ============================================================================
  // AUTHENTICATION
  // ============================================================================

  const handleLogin = async () => {
    if (!loginEmail || !loginPassword) {
      window.alert('Please enter email and password');
      return;
    }

    try {
      // ── Supabase Auth (primary) ───────────────────────────────
      const { signIn: supaSignIn, supabase } = await import('./supabaseClient');
      const { user } = await supaSignIn(loginEmail, loginPassword);

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      const role = profile?.role || 'Partner';
      let partnerId = profile?.partner_id || null;

      if (role === 'Partner') {
        if (partnerId) {
          const rec = partners.find(p => p.id === partnerId);
          if (!rec) {
            const byEmail = partners.find(p => p.email?.toLowerCase() === loginEmail.toLowerCase());
            if (byEmail) partnerId = byEmail.id;
          }
        } else {
          const byEmail = partners.find(p => p.email?.toLowerCase() === loginEmail.toLowerCase());
          if (byEmail) partnerId = byEmail.id;
        }
      }

      const loggedInUser = {
        id: user.id,
        email: user.email,
        role,
        name: profile?.name || user.email,
        partnerId,
      };
      setCurrentUser(loggedInUser);
      // No need to save to localStorage — Supabase JWT handles persistence
      // last_login is stamped centrally in onAuthStateChange (covers Google
      // SSO as well), no need to duplicate here.

    } catch (supabaseError) {
      window.alert('Invalid credentials. Please check your email and password.');
    }
  };

  const handleLogout = async () => {
    try {
      const { signOut } = await import('./supabaseClient');
      await signOut();
    } catch (_) {}
    setCurrentUser(null);
    setLoginEmail('');
    setLoginPassword('');
    setActiveTabPersisted('dashboard');
    setImpersonatedPartnerId(null);
    setRealUser(null);
  };

  // Impersonation functions
  const startImpersonation = (partnerId) => {
    const partner = partners.find(p => p.id === partnerId);
    if (!partner) return;
    
    // Store the real admin user
    setRealUser(currentUser);
    
    // Create a partner user object with correct field names
    const partnerUser = {
      id: partner.id,
      partnerId: partner.id,
      name: partner.name || 'Partner',
      email: partner.contactEmail || partner.email || '',
      role: 'Partner'
    };
    
    // Switch to partner view
    setImpersonatedPartnerId(partnerId);
    setCurrentUser(partnerUser);
    setActiveTabPersisted('dashboard');
    
    addAuditLog('User', partnerId, 'Impersonation Started', realUser?.id || currentUser.id, `Admin ${currentUser.name} started viewing as ${partnerUser.name}`);
  };

  const stopImpersonation = () => {
    if (!realUser) return;
    
    // Add audit log before switching back
    addAuditLog('User', impersonatedPartnerId, 'Impersonation Ended', realUser.id, `Admin ${realUser.name} stopped viewing as ${currentUser.name}`);
    
    // Restore admin user
    setCurrentUser(realUser);
    setImpersonatedPartnerId(null);
    setRealUser(null);
    setActiveTabPersisted('dashboard');
  };

  // Check if currently impersonating
  const isImpersonating = impersonatedPartnerId !== null;
  const actualUser = isImpersonating ? realUser : currentUser;

  // ============================================================================
  // USER MANAGEMENT FUNCTIONS
  // ============================================================================

  const addUser = async (userData) => {
    let partnerId = userData.partnerId || null;
    let createdPartnerRecord = null; // track so we can roll back on EF failure

    // When creating a Partner user — also create the full partner record
    if (userData.role === 'Partner' && userData.newPartnerData) {
      const pd = userData.newPartnerData;
      const newPartner = {
        id: `partner_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name: pd.name,
        contactPerson: pd.contactPerson || pd.name,
        email: pd.email || userData.email,
        phone: pd.phone || '',
        agreementType: pd.agreementType || 'MCA - Referral',
        startDate: pd.startDate || new Date().toISOString(),
        endDate: pd.endDate || null,
        status: 'Active',
        sectors: pd.sectors || [],
        geography: pd.geography || { continents: [], countries: [] },
        createdAt: new Date().toISOString(),
        createdBy: currentUser.name,
        lastUpdatedDate: new Date().toISOString(),
      };
      try {
        const updatedPartners = [...partners, newPartner];
        await saveData('partners', updatedPartners);
        setPartners(updatedPartners);
        addAuditLog('Partner', newPartner.id, 'Created', currentUser.id, `Partner created via User Management: ${newPartner.name}`);
        partnerId = newPartner.id;
        createdPartnerRecord = newPartner;
      } catch (partnerErr) {
        console.error('[addUser] partner creation failed:', partnerErr?.message ?? partnerErr);
        setAppAlert({ title: 'Partner Creation Failed', message: `Could not save partner record: ${partnerErr?.message ?? 'Unknown error'}` });
        return;
      }
    }

    // Guard: Partner role must have a linked partnerId before we call the EF.
    // If this fires it means newPartnerData was absent and no existing partnerId
    // was supplied — surface it clearly rather than hitting the DB constraint.
    if (userData.role === 'Partner' && !partnerId) {
      console.error('[addUser] Partner role but no partnerId — aborting EF call');
      setAppAlert({ title: 'User Creation Failed', message: 'Partner users must be linked to a partner record. Please fill in the partner details.' });
      return;
    }

    console.log(`[addUser] invoking manage-user create: role=${userData.role} partnerId=${partnerId ?? 'null'}`);

    // Close modal immediately to prevent double-submit
    setShowNewUserModal(false);

    isAdminCreatingUserRef.current = true;
    let newUser = null;
    try {
      const { invokeFunction } = await import('./supabaseClient');
      const { data, error } = await invokeFunction('manage-user', {
        body: { action: 'create', payload: { name: userData.name, email: userData.email, role: userData.role, partnerId: partnerId ?? undefined } },
      });
      if (error || data?.error) {
        const msg = error?.message ?? data?.error;
        console.error('[addUser] EF error:', msg);
        // Roll back the partner record we just created so the DB stays clean
        if (createdPartnerRecord) {
          try {
            const { db } = await import('./dataLayer');
            await db.partners.delete(createdPartnerRecord.id);
            setPartners(prev => prev.filter(p => p.id !== createdPartnerRecord.id));
            console.log('[addUser] rolled back partner:', createdPartnerRecord.id);
          } catch (rbErr) {
            console.error('[addUser] partner rollback failed (ghost record):', rbErr?.message ?? rbErr);
          }
        }
        setAppAlert({ title: 'Invite Failed', message: msg });
        return;
      }
      if (!data?.user) {
        console.error('[addUser] no user profile in response:', data);
        setAppAlert({ title: 'User Creation Warning', message: 'User was created in auth but profile data is missing. Please refresh the page.' });
        return;
      }
      console.log('[addUser] user created successfully:', data.user.id);
      newUser = {
        id: data.user.id,
        name: data.user.name,
        email: data.user.email,
        role: data.user.role,
        partnerId: data.user.partner_id || partnerId,
        status: data.user.status,
        createdAt: data.user.created_at,
        createdBy: currentUser.name,
        lastLogin: null,
      };
    } catch (e) {
      console.error('[addUser] unexpected error:', e.message, e);
      return;
    } finally {
      isAdminCreatingUserRef.current = false;
    }

    if (!newUser) return;

    setUsers(prev => [...prev, newUser]);
    addAuditLog('User', newUser.id, 'Created', currentUser.id, `Created user: ${newUser.name} (${newUser.email}) [${newUser.role}]`);
    executeWorkflows('user_created', { user: newUser });
    setNotifications(prev => [...prev, {
      id: `notif_${Date.now()}`,
      message: `New user account created: ${newUser.name} (${newUser.role})`,
      createdAt: new Date().toISOString(),
      read: false,
      type: 'user_created',
      userId: currentUser?.id
    }]);
  };

  const updateUser = async (userId, updates) => {
    try {
      const { invokeFunction } = await import('./supabaseClient');
      console.log('[updateUser] calling manage-user function, userId:', userId);
      const { data, error } = await invokeFunction('manage-user', {
        body: { action: 'update', payload: { userId, ...updates } },
      });
      if (error) {
        console.error('[updateUser] function error:', error.message, error);
        return;
      }
      if (data?.error) {
        console.error('[updateUser] server error:', data.error);
        return;
      }
      console.log('[updateUser] updated successfully:', userId);
    } catch (e) {
      console.error('[updateUser] unexpected error:', e.message, e);
      return;
    }

    setUsers(prev => prev.map(u =>
      u.id === userId
        ? { ...u, ...updates, updatedAt: new Date().toISOString(), updatedBy: currentUser.name }
        : u
    ));

    const user = users.find(u => u.id === userId);
    addAuditLog('User', userId, 'Updated', currentUser.id, `Updated user: ${user?.name || userId}`);
    setShowEditUserModal(false);
    setEditingUser(null);
  };

  const toggleUserStatus = async (userId) => {
    const user = users.find(u => u.id === userId);
    const newStatus = user.status === 'Active' ? 'Inactive' : 'Active';
    
    await updateUser(userId, { status: newStatus });
    executeWorkflows('user_status_changed', { user, newStatus, oldStatus: user.status });

    setNotifications(prev => [...prev, {
      id: `notif_${Date.now()}`,
      message: `User ${user.name} ${newStatus === 'Active' ? 'activated' : 'deactivated'}`,
      createdAt: new Date().toISOString(),
      read: false,
      type: 'user_status_changed',
      userId: currentUser?.id
    }]);
  };

  const deleteUser = async (userId) => {
    const deletedUser = users.find(u => u.id === userId);
    try {
      const { invokeFunction } = await import('./supabaseClient');
      console.log('[deleteUser] calling manage-user function, userId:', userId);
      const { data, error } = await invokeFunction('manage-user', {
        body: { action: 'delete', payload: { userId } },
      });
      if (error) {
        console.error('[deleteUser] function error:', error.message, error);
        return;
      }
      if (data?.error) {
        console.error('[deleteUser] server error:', data.error);
        return;
      }
      console.log('[deleteUser] deleted successfully:', userId);
    } catch (e) {
      console.error('[deleteUser] unexpected error:', e.message, e);
      return;
    }

    setUsers(prev => prev.filter(u => u.id !== userId));
    try {
      addAuditLog('User', userId, 'Deleted', currentUser.id, `Deleted user: ${deletedUser?.name || userId}`);
    } catch (e) {
      console.warn('[deleteUser] audit log failed:', e.message);
    }

    if (deletedUser) {
      setNotifications(prev => [...prev, {
        id: `notif_${Date.now()}`,
        message: `User account deleted: ${deletedUser.name}`,
        createdAt: new Date().toISOString(),
        read: false,
        type: 'user_deleted',
        userId: currentUser?.id
      }]);
    }
  };

  const resetUserPassword = async (userId) => {
    const user = users.find(u => u.id === userId);
    
    addAuditLog('User', userId, 'Password Reset', currentUser.id, `Password reset requested for: ${user.name}`);
    
    setNotifications(prev => [...prev, {
      id: `notif_${Date.now()}`,
      message: `Password reset link sent to ${user.email}`,
      createdAt: new Date().toISOString(),
      read: false,
      type: 'password_reset',
      userId: currentUser?.id
    }]);
    
    window.alert(`Password reset link sent to ${user.email}`);
  };

  const resendConfirmationEmail = async (userId) => {
    const user = users.find(u => u.id === userId);
    if (!user?.email) {
      window.alert('User has no email on record.');
      return;
    }
    try {
      const { supabase } = await import('./supabaseClient');
      const { error } = await supabase.auth.resend({ type: 'signup', email: user.email });
      if (error) {
        console.error('[resendConfirmationEmail] error:', error.message);
        window.alert(`Failed to resend confirmation email: ${error.message}`);
        return;
      }
      addAuditLog('User', userId, 'Confirmation Email Resent', currentUser.id, `Confirmation email resent to: ${user.email}`);
      window.alert(`Confirmation email resent to ${user.email}`);
    } catch (e) {
      console.error('[resendConfirmationEmail] unexpected error:', e.message);
      window.alert(`Failed to resend confirmation email: ${e.message}`);
    }
  };

  // ============================================================================
  // IDENTITY MERGE — unified view of `user_profiles` + un-invited partners
  // ============================================================================
  // Today (Phase A): many partners exist only in the `partners` table without
  // a matching `user_profiles` row — they were added by admin but never
  // invited. This memo augments the real `users` array with synthetic
  // "pending invite" rows so the Users tab reflects all identities.
  //
  // Phase B (next sprint): when partner CREATION auto-provisions a
  // `user_profiles` row, every partner is already a real user. Delete this
  // memo and pass `users` straight through to UsersView. No other change
  // needed — the row shape is identical.
  //
  // Synthetic-row contract:
  //   - id            = `pending_${partner.id}` (never collides with real ids)
  //   - role          = 'Partner'
  //   - status        = 'Invited'
  //   - partnerId     = partner.id (FK)
  //   - _pendingInvite= true  ← UI marker; gates Edit/Delete and shows Invite button
  const identities = React.useMemo(() => {
    const provisionedPartnerIds = new Set(
      users.filter(u => u.partnerId).map(u => u.partnerId)
    );
    const provisionedEmails = new Set(
      users.filter(u => u.email).map(u => u.email.toLowerCase())
    );
    const pending = partners
      .filter(p =>
        !provisionedPartnerIds.has(p.id) &&
        (!p.email || !provisionedEmails.has(p.email.toLowerCase()))
      )
      .map(p => ({
        id:             `pending_${p.id}`,
        partnerId:      p.id,
        name:           p.name || p.contactPerson || '(no name)',
        email:          p.email || '',
        phone:          p.phone || '',
        role:           'Partner',
        status:         'Invited',
        lastLogin:      null,
        lastLoginDate:  null,
        createdAt:      p.createdAt || null,
        _pendingInvite: true,
      }));
    return [...users, ...pending];
  }, [users, partners]);

  // Send a partner-user invite. Called from the UsersView "Invite" button on a
  // pending row. In Phase B this becomes: PATCH existing user_profile from
  // status='Invited' to fire the magic-link — same call site, different
  // server-side implementation.
  const inviteUserForPartner = async (pendingUser) => {
    const partner = partners.find(p => p.id === pendingUser.partnerId);
    if (!partner) {
      window.alert('Partner record not found.');
      return;
    }
    if (!partner.email) {
      window.alert('Partner has no email on record. Edit the partner to add one before inviting.');
      return;
    }
    try {
      const { invokeFunction } = await import('./supabaseClient');
      const { data, error } = await invokeFunction('manage-user', {
        body: {
          action: 'create',
          payload: {
            name:       partner.name || partner.contactPerson || partner.email,
            email:      partner.email,
            role:       'Partner',
            // manage-user validateCreateUser reads payload.partnerId (camelCase).
            // Sending snake_case partner_id here made data.partnerId undefined →
            // "Partner role requires a partnerId." (matches the New User modal path.)
            partnerId:  partner.id,
          },
        },
      });
      if (error || data?.error) {
        const msg = error?.message || data?.error || 'Unknown error.';
        setAppAlert({ title: 'Invite Failed', message: msg });
        return;
      }
      // Optimistic state update — new user_profile appears, phantom row drops
      // out automatically because provisionedPartnerIds now includes this id.
      if (data?.user) setUsers(prev => [...prev, data.user]);
      addAuditLog('User', data?.user?.id || partner.id, 'Invite Sent', currentUser?.id, `Invited partner ${partner.name} (${partner.email}).`);
    } catch (e) {
      console.error('[inviteUserForPartner] unexpected error:', e.message);
      setAppAlert({ title: 'Invite Failed', message: e.message });
    }
  };

  // ============================================================================
  // SECTOR MANAGEMENT FUNCTIONS
  // ============================================================================

  const addSector = async (sectorName, iconKey) => {
    const trimmed = sectorName.trim();
    if (!trimmed) return;
    if (sectors.includes(trimmed)) {
      window.alert(`Sector "${trimmed}" already exists.`);
      return;
    }
    const updated = [...sectors, trimmed].sort();
    setSectors(updated);
    await saveData('sectors', updated);
    if (iconKey) {
      const updatedIcons = { ...sectorIcons, [trimmed]: iconKey };
      setSectorIcons(updatedIcons);
      await saveData('sectorIcons', updatedIcons);
    }
    addAuditLog('System', 'sectors', 'Sector Added', currentUser.id, `Added sector: ${trimmed}`);
  };

  const removeSector = (sectorName) => {
    // Check how many partners use this sector
    const affectedPartners = partners.filter(p => p.sectors?.includes(sectorName));
    const affectedTeasers = teasers.filter(t => t.sectors?.includes(sectorName));
    const totalAffected = affectedPartners.length + affectedTeasers.length;

    const doRemove = async () => {
      // Remove sector from all partners
      if (affectedPartners.length > 0) {
        const updatedPartners = partners.map(p => ({
          ...p,
          sectors: (p.sectors || []).filter(s => s !== sectorName)
        }));
        setPartners(updatedPartners);
        await saveData('partners', updatedPartners);
      }

      // Remove sector from all teasers
      if (affectedTeasers.length > 0) {
        const updatedTeasers = teasers.map(t => ({
          ...t,
          sectors: (t.sectors || []).filter(s => s !== sectorName)
        }));
        setTeasers(updatedTeasers);
        await saveData('teasers', updatedTeasers);
      }

      const updated = sectors.filter(s => s !== sectorName);
      setSectors(updated);
      await saveData('sectors', updated);
      if (sectorIcons[sectorName]) {
        const { [sectorName]: _, ...remaining } = sectorIcons;
        setSectorIcons(remaining);
        await saveData('sectorIcons', remaining);
      }
      addAuditLog('System', 'sectors', 'Sector Removed', currentUser.id, `Removed sector: ${sectorName}`);
    };

    if (totalAffected > 0) {
      showConfirm(`Warning: "${sectorName}" is currently used by ${affectedPartners.length} partner(s) and ${affectedTeasers.length} teaser(s).\n\nRemoving it will clear this sector from all of them.\n\nContinue?`, doRemove, 'Remove');
    } else {
      showConfirm(`Remove sector "${sectorName}"?`, doRemove, 'Remove');
    }
  };

  // ============================================================================
  // BUSINESS RULES
  // ============================================================================

  const saveBusinessRules = async (rules) => {
    setBusinessRules(rules);
    await saveData('businessRules', rules);
    addAuditLog('System', 'businessRules', 'Updated', currentUser.id,
      `Business rules updated: auto-reject=${rules.autoRejectDays}d, intro=${rules.introWindowDays}d, tail=${rules.tailMonths}mo`
    );
  };

  // ── Agreement Types ──────────────────────────────────────────────────────────
  const addAgreementType = async (name) => {
    const trimmed = name.trim();
    if (!trimmed || agreementTypes.includes(trimmed)) return;
    const updated = [...agreementTypes, trimmed];
    setAgreementTypes(updated);
    await saveData('agreementTypes', updated);
    // Add a default commission entry for the new type
    const updatedDefaults = { ...commissionDefaults, [trimmed]: { percentage: 10, description: '' } };
    setCommissionDefaults(updatedDefaults);
    await saveData('commissionDefaults', updatedDefaults);
    addAuditLog('System', 'agreementTypes', 'Created', currentUser.id, `Added agreement type: ${trimmed}`);
  };

  const removeAgreementType = (name) => {
    const inUse = partners.filter(p => p.agreementType === name).length;
    const doRemove = async () => {
      const updated = agreementTypes.filter(t => t !== name);
      setAgreementTypes(updated);
      await saveData('agreementTypes', updated);
      addAuditLog('System', 'agreementTypes', 'Deleted', currentUser.id, `Removed agreement type: ${name}`);
    };
    if (inUse > 0) {
      showConfirm(`"${name}" is used by ${inUse} partner(s). Remove anyway?`, doRemove, 'Remove');
    } else {
      doRemove();
    }
  };

  const saveCommissionDefaults = async (defaults) => {
    setCommissionDefaults(defaults);
    await saveData('commissionDefaults', defaults);
    addAuditLog('System', 'commissionDefaults', 'Updated', currentUser.id, 'Commission defaults updated');
  };

  const saveGeneralSettings = async (settings) => {
    setGeneralSettings(settings);
    await saveData('generalSettings', settings);
    addAuditLog('System', 'generalSettings', 'Updated', currentUser.id, 'General settings updated');
  };

  // ⚠️ Persists Security tab values to app_settings, but the values are NOT
  // yet enforced by any auth code (sessionTimeout, passwordMinLength,
  // require2FA, failedLoginLockout). Tracked as enforcement work in
  // DIAGNOSTIC-REPORT-2026-04-29 #UI-1.2.
  const saveSecuritySettings = async (settings) => {
    setSecuritySettings(settings);
    await saveData('securitySettings', settings);
    addAuditLog('System', 'securitySettings', 'Updated', currentUser.id, 'Security settings updated');
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => !prev);
  };

  const updateUserProfile = async (updates) => {
    // Edge-Function-First: persist to user_profiles via manage-user (self-update
    // path) BEFORE mutating React state. If the EF fails, abort with an alert and
    // leave state untouched so the modal stays open and the user can retry.
    //
    // The self-update path in manage-user (added April 2026) lets any authenticated
    // user edit their own name/email/phone without requiring Admin role.
    // role/status/partnerId are NEVER allowed through this path — privilege
    // escalation guard. See supabase/functions/manage-user/index.ts.
    const payload = {
      userId: currentUser.id,
      ...(updates.name !== undefined ? { name: updates.name } : {}),
      ...(updates.email !== undefined ? { email: updates.email } : {}),
      ...(updates.phone !== undefined ? { phone: updates.phone } : {}),
    };
    const { invokeFunction } = await import('./supabaseClient');
    const { data, error } = await invokeFunction('manage-user', {
      body: { action: 'update', payload },
    });
    if (error || data?.error) {
      setAppAlert({
        title: 'Update Failed',
        message: error?.message || data?.error || 'Could not save profile changes. Please try again.',
      });
      return;
    }

    // Backend confirmed — sync local state.
    const updatedUsers = users.map(u =>
      u.id === currentUser.id ? { ...u, ...updates } : u
    );
    setUsers(updatedUsers);

    // Partner-role: also keep the partners row in sync (name/email/phone/sectors/geography).
    if (currentUser.role === 'Partner') {
      const partnerIdToUpdate = currentUser.partnerId || currentUser.id;
      const updatedPartners = partners.map(p =>
        p.id === partnerIdToUpdate
          ? {
              ...p,
              name: updates.name || p.name,
              email: updates.email || p.email,
              phone: updates.phone || p.phone,
              contactPerson: updates.name || p.contactPerson,
              ...(updates.sectors !== undefined ? { sectors: updates.sectors } : {}),
              ...(updates.geography !== undefined ? { geography: updates.geography } : {}),
              lastUpdatedDate: new Date().toISOString(),
            }
          : p
      );
      setPartners(updatedPartners);
      await saveData('partners', updatedPartners);
    }

    setCurrentUser(prev => ({ ...prev, ...updates }));
    addAuditLog('User', currentUser.id, 'Profile Updated', currentUser.id, `Updated profile: ${Object.keys(updates).join(', ')}`);
    window.alert('Profile updated successfully.');
  };

  // ============================================================================
  // PARTNER MESSAGING
  // ============================================================================

  const openMessageFor = (ctx) => {
    setNewMessageContext(ctx);
    setShowNewMessageModal(true);
  };

  // ── Hot Topics ─────────────────────────────────────────────────────────
  // Pick the newest qualifying topic for the logged-in partner and show it
  // as a popup — max one per login (ref guard). RLS already filters rows
  // server-side; the client re-checks as defense-in-depth.
  useEffect(() => {
    if (hotTopicShownThisSessionRef.current) return;
    if (currentUser?.role !== 'Partner' || hotTopics.length === 0) return;
    // Precedence: a brand-new partner gets the walkthrough this login; their
    // hot topics wait until next login (after onboarding_completed flips true).
    if (!currentUser.onboardingCompleted) return;
    const myPid = currentUser.partnerId || currentUser.id;
    const now = new Date();
    const qualifying = hotTopics
      .filter(t =>
        t.active !== false &&
        (!t.startsAt || new Date(t.startsAt) <= now) &&
        (!t.endsAt || new Date(t.endsAt) >= now) &&
        (t.visibility === 'all' || (Array.isArray(t.visibility) && t.visibility.includes(myPid))) &&
        !(Array.isArray(t.dismissedBy) && t.dismissedBy.includes(myPid))
      )
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    if (qualifying.length > 0) {
      setActiveHotTopic(qualifying[0]);
      hotTopicShownThisSessionRef.current = true;
    }
  }, [hotTopics, currentUser]);

  // Create or update a hot topic (Editor+; enforced again server-side).
  const saveHotTopic = async (topic) => {
    const isUpdate = hotTopics.some(t => t.id === topic.id);
    const payload = {
      id: topic.id || `hottopic_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      title: topic.title,
      body: topic.body || null,
      cta_label: topic.ctaLabel || null,
      ref_teaser_id: topic.refTeaserId || null,
      visibility: topic.visibility ?? 'all',
      starts_at: topic.startsAt || null,
      ends_at: topic.endsAt || null,
      active: topic.active ?? true,
    };
    try {
      const { invokeFunction } = await import('./supabaseClient');
      const { data, error } = await invokeFunction('manage-hot-topic', {
        body: { action: isUpdate ? 'update' : 'create', payload },
      });
      if (error || data?.error) {
        setAppAlert({ title: 'Hot Topic Save Failed', message: error?.message || data?.error });
        return false;
      }
      const saved = data.topic; // EF returns the snake_case row — map manually

      const camel = {
        id: saved.id, title: saved.title, body: saved.body, ctaLabel: saved.cta_label,
        refTeaserId: saved.ref_teaser_id, visibility: saved.visibility,
        startsAt: saved.starts_at, endsAt: saved.ends_at, active: saved.active,
        dismissedBy: saved.dismissed_by, createdBy: saved.created_by, createdAt: saved.created_at,
      };
      setHotTopics(prev => isUpdate ? prev.map(t => t.id === camel.id ? camel : t) : [camel, ...prev]);
      addAuditLog('HotTopic', camel.id, isUpdate ? 'Updated' : 'Created', currentUser?.id || 'admin', `Hot topic: ${camel.title}`);
      return true;
    } catch (e) {
      console.error('[saveHotTopic] unexpected error:', e.message, e);
      return false;
    }
  };

  const deleteHotTopic = async (topicId) => {
    try {
      const { invokeFunction } = await import('./supabaseClient');
      const { data, error } = await invokeFunction('manage-hot-topic', {
        body: { action: 'delete', payload: { id: topicId } },
      });
      if (error || data?.error) {
        setAppAlert({ title: 'Hot Topic Delete Failed', message: error?.message || data?.error });
        return;
      }
      setHotTopics(prev => prev.filter(t => t.id !== topicId));
      addAuditLog('HotTopic', topicId, 'Deleted', currentUser?.id || 'admin', 'Hot topic deleted');
    } catch (e) { console.error('[deleteHotTopic] unexpected error:', e.message, e); }
  };

  // Partner opted out permanently — EF appends THEIR id server-side.
  const dismissHotTopicForever = async (topicId) => {
    setActiveHotTopic(null); // close immediately; EF call is fire-and-confirm
    try {
      const { invokeFunction } = await import('./supabaseClient');
      const { data, error } = await invokeFunction('manage-hot-topic', {
        body: { action: 'dismiss', payload: { id: topicId } },
      });
      if (error || data?.error) { console.error('[dismissHotTopic]', error?.message || data?.error); return; }
      const myPid = currentUser?.partnerId || currentUser?.id;
      setHotTopics(prev => prev.map(t => t.id === topicId
        ? { ...t, dismissedBy: [...(t.dismissedBy || []), myPid] } : t));
    } catch (e) { console.error('[dismissHotTopic] unexpected error:', e.message, e); }
  };

  // ── Guided walkthrough ───────────────────────────────────────────────────
  // Mark the tour seen (server flag via manage-user self-update; optimistic local).
  const markTourComplete = async () => {
    // Never write the flag during impersonation — currentUser.id is the
    // partner id (not a user_profiles id), so manage-user would 404.
    if (impersonatedPartnerId) return;
    if (!currentUser || currentUser.onboardingCompleted) return;
    setCurrentUser(u => u ? { ...u, onboardingCompleted: true } : u);
    try {
      const { invokeFunction } = await import('./supabaseClient');
      await invokeFunction('manage-user', {
        body: { action: 'update', payload: { userId: currentUser.id, onboardingCompleted: true } },
      });
    } catch (e) { console.error('[tour] markComplete failed:', e.message); }
  };

  // Shared popover renderer for all three tours. driver.js calls this on every
  // step render, so it re-applies itself automatically as the user moves.
  //   1. Replaces the built-in "2 of 7" progress text with a dot row (plus a
  //      muted count kept for screen readers / low-vision users).
  //   2. Wires the finish-step CTA, which only exists on hero steps that declare
  //      one. Clicking it runs the action then destroys the driver via the
  //      normal path, so onDestroyed still fires markTourComplete untouched.
  // `total` is passed in rather than read from config so the mini-tour and the
  // two main tours can share this without hardcoding any count.
  const renderTourPopover = (popover, opts, total, getDriver, onCta) => {
    const active = opts?.state?.activeIndex ?? 0;
    if (popover?.progress) {
      const dots = Array.from({ length: total }, (_, i) => {
        const cls = i === active ? 'eve-tour-dot is-active' : i < active ? 'eve-tour-dot is-done' : 'eve-tour-dot';
        return `<span class="${cls}"></span>`;
      }).join('');
      popover.progress.innerHTML =
        `<span class="eve-tour-dots" role="img" aria-label="Step ${active + 1} of ${total}">${dots}</span>` +
        `<span class="eve-tour-count">Step ${active + 1} of ${total}</span>`;
    }
    const cta = popover?.description?.querySelector?.('#eve-tour-cta');
    if (cta && onCta) {
      cta.onclick = () => {
        const d = getDriver?.();
        onCta();
        d?.destroy();   // normal destroy → onDestroyed → markTourComplete
      };
    }
  };

  // Controlled, cross-tab driver: each step switches to its tab, waits one
  // render tick, then highlights the (always-mounted) sidebar anchor.
  const startTour = (role) => {
    const steps = role === 'Partner' ? PARTNER_TOUR_STEPS : ADMIN_TOUR_STEPS;
    let idx = 0;
    let obj;
    const goTo = (target, move) => {
      idx = target;
      const step = steps[target];
      if (step?.tab) {
        setActiveTabPersisted(step.tab);
        setMobileSidebarOpen(false);
        setTimeout(move, 380); // let the destination tab render before measuring
      } else {
        move();
      }
    };
    // Finish-step CTA: partner lands in the New Prospect wizard, staff just
    // ends on the dashboard. The driver destroy is handled by the shared
    // renderer, so the onboarding flag is written exactly as on a normal finish.
    const handleCta = () => {
      const finish = steps[steps.length - 1];
      if (finish?.cta === 'prospect') {
        setActiveTabPersisted('prospects');
        setShowNewProspectModal(true);
      } else {
        setActiveTabPersisted('dashboard');
      }
    };
    obj = driver({
      showProgress: true,          // required: the dots replace this element
      allowClose: true,
      animate: true,
      overlayColor: 'rgba(10,20,40,0.72)',
      stagePadding: 6,
      stageRadius: 10,
      popoverClass: 'eve-tour',
      nextBtnText: 'Next →',
      prevBtnText: '← Back',
      doneBtnText: 'Done',
      steps: steps.map(s => ({
        element: s.element || undefined,
        popover: {
          title: s.title,
          // EVE_LOGO_SRC is interpolated here (runtime), not in the module-scope
          // step arrays above; see the note on `logo:` there.
          description: s.logo
            ? `<img src="${EVE_LOGO_SRC}" alt="EvE" class="eve-tour-logo" />${s.description}`
            : s.description,
          side: s.element ? 'right' : 'over',
          align: s.element ? 'start' : 'center',
          ...(s.hero ? { popoverClass: 'eve-tour eve-tour-hero' } : {}),
          ...(s.nextBtnText ? { nextBtnText: s.nextBtnText } : {}),
        },
      })),
      onPopoverRender: (popover, o) => renderTourPopover(popover, o, steps.length, () => obj, handleCta),
      onNextClick: () => {
        if (idx + 1 >= steps.length) { obj.destroy(); return; }
        goTo(idx + 1, () => obj.moveNext());
      },
      onPrevClick: () => {
        if (idx <= 0) return;
        // driver.js 1.4 exposes movePrevious(); the old movePrev() was undefined,
        // so Back threw a TypeError and did nothing. Fixed in passing.
        goTo(idx - 1, () => obj.movePrevious());
      },
      onCloseClick: () => obj.destroy(),
      onDestroyed: () => { markTourComplete(); },
    });
    goTo(0, () => obj.drive());
  };

  // Opt-in "how to submit a prospect" mini-tour. Switches to the Prospects tab,
  // opens the New Prospect wizard, spotlights Step-1 elements, and closes the
  // modal when the tour ends. Does NOT touch the onboarding flag.
  const startProspectFormTour = () => {
    setActiveTabPersisted('prospects');
    setMobileSidebarOpen(false);
    setShowNewProspectModal(true);
    let obj;
    const begin = () => {
      obj = driver({
        showProgress: true,          // required: the dots replace this element
        allowClose: true,
        animate: true,
        overlayColor: 'rgba(10,20,40,0.72)',
        stagePadding: 6,
        stageRadius: 10,
        popoverClass: 'eve-tour',
        nextBtnText: 'Next →',
        prevBtnText: '← Back',
        doneBtnText: 'Got it',
        steps: PROSPECT_FORM_TOUR_STEPS.map(s => ({
          element: s.element,
          popover: { title: s.title, description: s.description, side: 'bottom', align: 'start' },
        })),
        onPopoverRender: (popover, o) => renderTourPopover(popover, o, PROSPECT_FORM_TOUR_STEPS.length),
        onDestroyed: () => { setShowNewProspectModal(false); }, // always close the form
      });
      obj.drive();
    };
    // Wait for the modal to mount before measuring its elements.
    setTimeout(begin, 420);
  };

  // Auto-start the walkthrough once for brand-new users (desktop only).
  useEffect(() => {
    if (tourAutoCheckedRef.current) return;
    if (isMobile) return;                       // no auto-tour on mobile (v1)
    if (impersonatedPartnerId) return;          // not while an admin previews a partner
    if (sessionLoading || !currentUser) return;
    if (currentUser.onboardingCompleted) return;
    if (showPasswordResetModal) return;         // recovery modal takes precedence
    tourAutoCheckedRef.current = true;
    hotTopicShownThisSessionRef.current = true; // suppress hot-topic popup this login
    const t = setTimeout(() => startTour(currentUser.role), 600); // let first paint settle
    return () => clearTimeout(t);
  }, [currentUser, sessionLoading, isMobile, showPasswordResetModal, impersonatedPartnerId]);

  const createMessageThread = async ({ subject, category, refId, refName, body }) => {
    try {
      const { db } = await import('./dataLayer');
      const partnerId = currentUser.partnerId || currentUser.id;
      const thread = await db.messageThreads.upsert({
        partnerId,
        subject,
        category,
        refId: refId || null,
        refName: refName || null,
        status: 'open',
        lastPostAt: new Date().toISOString(),
        partnerUnread: false,
        adminUnread: true,
      });
      const post = await db.messagePosts.upsert({
        threadId: thread.id,
        authorId: currentUser.id,
        authorRole: 'partner',
        body,
      });
      setPartnerThreads(prev => [thread, ...prev]);
      setPartnerPosts(prev => [post, ...prev]);
      return thread;
    } catch (e) {
      setAppAlert({ title: 'Error', message: 'Could not send message: ' + e.message });
      return null;
    }
  };

  const addMessagePost = async (threadId, body) => {
    try {
      const { db } = await import('./dataLayer');
      const isPartner = currentUser.role === 'Partner';
      const post = await db.messagePosts.upsert({
        threadId,
        authorId: currentUser.id,
        authorRole: isPartner ? 'partner' : 'admin',
        body,
      });
      const threadUpdates = {
        lastPostAt: new Date().toISOString(),
        ...(isPartner ? { adminUnread: true, partnerUnread: false } : { partnerUnread: true, adminUnread: false }),
      };
      const updatedThread = await db.messageThreads.update(threadId, threadUpdates);
      setPartnerPosts(prev => [post, ...prev]);
      setPartnerThreads(prev => prev.map(t => t.id === threadId ? { ...t, ...threadUpdates } : t));
      return post;
    } catch (e) {
      setAppAlert({ title: 'Error', message: 'Could not send reply: ' + e.message });
      return null;
    }
  };

  const markThreadRead = async (threadId) => {
    try {
      const { db } = await import('./dataLayer');
      const isPartner = currentUser.role === 'Partner';
      const field = isPartner ? { partnerUnread: false } : { adminUnread: false };
      await db.messageThreads.update(threadId, field);
      setPartnerThreads(prev => prev.map(t => t.id === threadId ? { ...t, ...field } : t));
    } catch (_) {}
  };

  const closeThread = async (threadId) => {
    try {
      const { db } = await import('./dataLayer');
      await db.messageThreads.update(threadId, { status: 'closed' });
      setPartnerThreads(prev => prev.map(t => t.id === threadId ? { ...t, status: 'closed' } : t));
    } catch (e) {
      setAppAlert({ title: 'Error', message: 'Could not close thread: ' + e.message });
    }
  };

  // ============================================================================
  // COMPUTED DATA
  // ============================================================================

  const stats = {
    activePartners: partners.filter(p => p.status === 'Active').length,
    inTailPartners: partners.filter(p => p.status === 'In Tail').length,
    terminatedPartners: partners.filter(p => p.status === 'Terminated').length,
    pendingProspects: prospects.filter(p => p.approvalStatus === 'Pending' || p.approvalStatus === 'Waiting for Approval').length,
    approvedProspects: prospects.filter(p => p.approvalStatus === 'Approved').length,
    rejectedProspects: prospects.filter(p => p.approvalStatus === 'Rejected').length,
    totalTransactions: transactions.length,
    totalPools: pools.length,
    totalPoolValue: transactions.reduce((sum, t) => {
      const hasPool = pools.find(p => p.transactionId === t.id);
      return hasPool ? sum + t.transactionAmount : sum;
    }, 0)
  };

  const userProspects = currentUser?.role === 'Partner' 
    ? prospects.filter(p => {
        const pid = currentUser.partnerId || currentUser.id;
        const partnerByEmail = partners.find(pt => pt.email?.toLowerCase() === currentUser.email?.toLowerCase());
        return (
          p.hiddenFromPartners !== true &&
          (
            p.introducingPartnerId === pid ||
            p.introducingPartnerId === currentUser.id ||
            p.introducingPartnerId === currentUser.partnerId ||
            (partnerByEmail && p.introducingPartnerId === partnerByEmail.id)
          )
        );
      })
    : prospects;

  // hiddenFromPartners is enforced in RLS (see hidden_from_partners.sql), so a
  // real partner login never receives these rows — this filter is
  // defense-in-depth AND what makes admin "view as" faithful: under
  // impersonation the arrays hold ALL rows (fetched with the admin token), so
  // without it the preview shows hidden teasers/prospects a partner can't see.
  const userTeasers = currentUser?.role === 'Partner'
    ? (() => {
        const pid = currentUser.partnerId || currentUser.id;
        return teasers.filter(t =>
          t.hiddenFromPartners !== true &&
          (
            t.visibility === 'all' ||
            (Array.isArray(t.visibility) && (t.visibility.includes(pid) || t.visibility.includes(currentUser.id)))
          )
        );
      })()
    : teasers;

  // Partner-scoped message threads/posts. For a real partner login RLS already
  // limits partnerThreads to their own rows, so this is a no-op + defense-in-depth.
  // Under admin "view as", partnerThreads holds ALL partners' threads (fetched
  // with the admin token), so this filter is what makes the preview faithful —
  // without it the impersonated view shows every partner's messages. Mirrors the
  // userProspects/userTeasers pattern (pid = partnerId || id handles the
  // createMessageThread fallback where partner_id was stamped with the user id).
  const userThreads = currentUser?.role === 'Partner'
    ? (() => {
        const pid = currentUser.partnerId || currentUser.id;
        return partnerThreads.filter(t => t.partnerId === pid || t.partnerId === currentUser.id);
      })()
    : partnerThreads;

  const userThreadIds = new Set(userThreads.map(t => t.id));
  const userPosts = currentUser?.role === 'Partner'
    ? partnerPosts.filter(p => userThreadIds.has(p.threadId))
    : partnerPosts;

  const unreadNotifications = notifications.filter(n => {
    if (n.dismissed) return false;
    if (!n.read) {
      if (!currentUser) return false;
      if (currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') return true;
      const pid = currentUser.partnerId || currentUser.id;
      return n.partnerId === pid || n.partnerId === currentUser.id || n.userId === currentUser.id;
    }
    return false;
  });

  const userNotifications = notifications.filter(n => {
    if (n.dismissed) return false;
    if (!currentUser) return false;
    if (currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') return true;
    const pid = currentUser.partnerId || currentUser.id;
    return n.partnerId === pid || n.partnerId === currentUser.id || n.userId === currentUser.id;
  });

  // ============================================================================
  // ANALYTICS DATA
  // ============================================================================

  const getAnalyticsData = () => {
    // Prospects over time (last 6 months)
    const prospectsOverTime = [];
    const now = new Date();
    
    for (let i = 5; i >= 0; i--) {
      const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthName = monthDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      
      const monthProspects = prospects.filter(p => {
        const pDate = new Date(p.createdAt);
        return pDate.getMonth() === monthDate.getMonth() && 
               pDate.getFullYear() === monthDate.getFullYear();
      });
      
      prospectsOverTime.push({
        month: monthName,
        submitted: monthProspects.length,
        approved: monthProspects.filter(p => p.approvalStatus === 'Approved').length,
        rejected: monthProspects.filter(p => p.approvalStatus === 'Rejected').length,
        pending: monthProspects.filter(p => p.approvalStatus === 'Pending' || p.approvalStatus === 'Waiting for Approval').length
      });
    }

    // Status distribution
    const statusDistribution = [
      { name: 'Pending', value: stats.pendingProspects, color: '#f59e0b' },
      { name: 'Approved', value: stats.approvedProspects, color: '#10b981' },
      { name: 'Rejected', value: stats.rejectedProspects, color: '#ef4444' }
    ];

    // Partner performance
    const partnerPerformance = partners.map(partner => {
      const partnerProspects = prospects.filter(p => p.introducingPartnerId === partner.id);
      const approved = partnerProspects.filter(p => p.approvalStatus === 'Approved').length;
      const total = partnerProspects.length;
      
      return {
        name: partner.name,
        prospects: total,
        approved: approved,
        approvalRate: total > 0 ? Math.round((approved / total) * 100) : 0
      };
    }).filter(p => p.prospects > 0).sort((a, b) => b.prospects - a.prospects).slice(0, 5);

    // Sector distribution
    const sectorCounts = {};
    prospects.forEach(p => {
      if (p.sector) {
        sectorCounts[p.sector] = (sectorCounts[p.sector] || 0) + 1;
      }
    });
    
    const sectorDistribution = Object.entries(sectorCounts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);

    // Conversion funnel
    const conversionFunnel = [
      { stage: 'Submitted', count: prospects.length },
      { stage: 'Approved', count: stats.approvedProspects },
      { stage: 'Intro Done', count: prospects.filter(p => p.introOccurred).length },
      { stage: 'Transaction', count: transactions.length }
    ];

    // Time to approval (average days)
    const approvedWithDates = prospects.filter(p => p.approvalStatus === 'Approved' && p.approvalDate && p.approvalRequestDate);
    const avgTimeToApproval = approvedWithDates.length > 0
      ? Math.round(approvedWithDates.reduce((sum, p) => {
          return sum + daysBetween(new Date(p.approvalRequestDate), new Date(p.approvalDate));
        }, 0) / approvedWithDates.length)
      : 0;

    return {
      prospectsOverTime,
      statusDistribution,
      partnerPerformance,
      sectorDistribution,
      conversionFunnel,
      avgTimeToApproval
    };
  };

  // ============================================================================
  // RENDER: LOGIN SCREEN
  // ============================================================================

  if (sessionLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center"
        style={{ background: BRAND_COLORS.bgSidebar }}>
        <style>{`
          @keyframes eve-logo-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        `}</style>
        <div className="flex justify-center" style={{ animation: 'eve-logo-spin 4s linear infinite' }}>
          <EvELogo size={128} />
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-4"
        style={{ background: BRAND_COLORS.bgApp }}
      >
        <div className="bg-white rounded-lg shadow-2xl p-8 w-full max-w-md" style={{ borderTop: `4px solid ${BRAND_COLORS.gold}` }}>
          <div className="text-center mb-8">
            <div className="mb-4 flex justify-center" style={{ color: BRAND_COLORS.navy }}>
              <NauticalMark name="compass" size={144} />
            </div>
            <h1 className="text-3xl font-bold mb-2" style={{ color: BRAND_COLORS.navy, fontFamily: 'Figtree, sans-serif' }}>Partners Portal</h1>
            
          </div>
          
          <div className="space-y-4">
            {accessDeniedMsg && (
              <div className="rounded-lg border px-3 py-2 text-sm text-center"
                   style={{ borderColor: '#FCA5A5', backgroundColor: '#FEF2F2', color: '#B91C1C' }}>
                {accessDeniedMsg}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent"
                style={{ '--tw-ring-color': BRAND_COLORS.gold }}
                placeholder="your@email.com"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                type="password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent"
                style={{ '--tw-ring-color': BRAND_COLORS.gold }}
                placeholder="Enter Password"
              />
            </div>
            
            <button
              onClick={handleLogin}
              className="w-full py-2 rounded-lg transition-all font-medium shadow-lg hover:opacity-90"
              style={{ background: BRAND_COLORS.gold, color: BRAND_COLORS.textOnGold }}
            >
              Sign In
            </button>

            <div className="text-right">
              <button
                type="button"
                onClick={async () => {
                  if (!loginEmail) { setForgotPasswordMsg('Please enter your email above first.'); return; }
                  const { supabase } = await import('./supabaseClient');
                  const { error } = await supabase.auth.resetPasswordForEmail(loginEmail, { redirectTo: 'https://www.gardenofeve-g.com' });
                  setForgotPasswordMsg(error ? `Error: ${error.message}` : 'Password reset email sent — check your inbox.');
                }}
                className="text-xs font-medium hover:underline"
                style={{ color: BRAND_COLORS.navy }}
              >
                Forgot password?
              </button>
            </div>
            {forgotPasswordMsg && (
              <p className="text-xs text-center" style={{ color: BRAND_COLORS.navy }}>{forgotPasswordMsg}</p>
            )}

            <div className="flex items-center gap-3 my-2">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-xs text-gray-400 font-medium">or</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            <button
              type="button"
              onClick={async () => {
                const { signInWithGoogle } = await import('./supabaseClient');
                await signInWithGoogle();
              }}
              className="w-full flex items-center justify-center gap-3 py-2 px-4 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 transition-all font-medium text-gray-700 shadow-sm"
            >
              <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                <path fill="none" d="M0 0h48v48H0z"/>
              </svg>
              Continue with Google
            </button>
          </div>

          {/* Public legal pages. Served as static files from public/ (terms.html,
              privacy.html) so they sit OUTSIDE this auth gate — required because
              Google's OAuth consent-screen verification fetches them logged out. */}
          <div className="mt-6 pt-4 border-t text-center" style={{ borderColor: '#E5E7EB' }}>
            <a href="/terms.html" target="_blank" rel="noopener noreferrer"
               className="text-xs hover:underline" style={{ color: BRAND_COLORS.navy }}>
              Terms and Conditions
            </a>
            <span className="text-xs mx-2" style={{ color: '#9CA3AF' }}>·</span>
            <a href="/privacy.html" target="_blank" rel="noopener noreferrer"
               className="text-xs hover:underline" style={{ color: BRAND_COLORS.navy }}>
              Privacy Policy
            </a>
          </div>

        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER: MAIN APP
  // ============================================================================

  return (
    <div className="h-screen flex flex-col transition-colors duration-200" style={{ backgroundColor: colors.background }}>
      {/* Impersonation Banner */}
      {isImpersonating && (
        <div
          className="py-3 px-4"
          style={{
            background: BRAND_COLORS.bgImpersonate,
            borderBottom: '2px solid #92400E'
          }}
        >
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Eye className="w-5 h-5 text-white" />
              <div>
                <p className="text-white font-bold text-sm">
                   Viewing as Partner: {currentUser.name}
                </p>
                <p className="text-white text-xs opacity-90">
                  You are seeing what {currentUser.name} sees in their portal
                </p>
              </div>
            </div>
            <button
              onClick={stopImpersonation}
              className="flex items-center space-x-2 px-4 py-2 bg-white text-orange-700 rounded-lg hover:bg-gray-100 transition-colors font-medium text-sm shadow-lg"
            >
              <X className="w-4 h-4" />
              <span>Exit Partner View</span>
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <header 
        className="shadow-lg transition-colors duration-200"
        style={{ 
          background: colors.headerBg,
          borderBottom: `2px solid ${colors.gold}`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              {isMobile && (
                <button
                  onClick={() => setMobileSidebarOpen(prev => !prev)}
                  className="p-2 rounded-lg transition-all hover:bg-white/10"
                  title="Toggle menu"
                >
                  <Menu className="w-6 h-6 text-white" />
                </button>
              )}
              <EvELogo size={isMobile ? 32 : 50} />
              <div>
                <h1 className={`${isMobile ? 'text-lg' : 'text-2xl'} font-bold text-white`} style={{ fontFamily: 'Figtree, sans-serif' }}>Partners Portal</h1>
                {!isMobile && <p className="text-sm" style={{ color: colors.gold }}>{currentUser.role} Dashboard</p>}
              </div>
            </div>

            <div className={`flex items-center ${isMobile ? 'space-x-2' : 'space-x-4'}`}>
              {/* Hamburger quick-actions menu — Admin/Editor/Super Admin, desktop only.
                  On mobile the left-side sidebar toggle covers navigation,
                  and a second hamburger here looks like a layout bug. */}
              {!isMobile && (currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') && (
                <div className="relative">
                  <button
                    onClick={() => {
                      setShowHamburgerMenu(!showHamburgerMenu);
                      if (!showHamburgerMenu) setExpandedSubmenu(null);
                    }}
                    className="p-2 rounded-lg transition-all hover:bg-white/10"
                    title="Menu"
                  >
                    <Menu className="w-6 h-6 text-white" />
                  </button>

                  {showHamburgerMenu && (
                    <>
                      {/* Backdrop */}
                      <div 
                        className="fixed inset-0 z-40" 
                        onClick={() => {
                          setShowHamburgerMenu(false);
                          setExpandedSubmenu(null);
                        }}
                      />
                      
                      {/* Menu Dropdown */}
                      <div 
                        className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-2xl z-50 border-2"
                        style={{ borderColor: BRAND_COLORS.gold }}
                      >
                        {/* Header */}
                        <div 
                          className="px-4 py-3 rounded-t-lg"
                          style={{ 
                            background: BRAND_COLORS.navy,
                            borderBottom: `2px solid ${BRAND_COLORS.gold}`
                          }}
                        >
                          <p className="text-sm font-bold text-white">Menu</p>
                          <p className="text-xs" style={{ color: BRAND_COLORS.cream }}>Quick access & tools</p>
                        </div>
                        
                        <div className="py-2">
                          {/* View As Partner - Expandable */}
                          {!isImpersonating && (
                            <>
                              <button
                                onClick={() => setExpandedSubmenu(expandedSubmenu === 'viewAs' ? null : 'viewAs')}
                                className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
                              >
                                <div className="flex items-center space-x-3">
                                  <div 
                                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                                    style={{ backgroundColor: `${BRAND_COLORS.gold}15` }}
                                  >
                                    <Eye className="w-5 h-5" style={{ color: BRAND_COLORS.gold }} />
                                  </div>
                                  <div>
                                    <p className="font-semibold text-sm" style={{ color: BRAND_COLORS.navy }}>View As Partner</p>
                                    <p className="text-xs text-gray-500">See partner perspective</p>
                                  </div>
                                </div>
                                <ChevronDown 
                                  className={`w-5 h-5 text-gray-400 transition-transform ${expandedSubmenu === 'viewAs' ? 'rotate-180' : ''}`}
                                />
                              </button>
                              
                              {/* Submenu - Partner List */}
                              {expandedSubmenu === 'viewAs' && (
                                <div className="bg-gray-50 border-l-4" style={{ borderLeftColor: BRAND_COLORS.gold }}>
                                  <div className="px-4 py-2">
                                    <div className="relative">
                                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                      <input
                                        type="text"
                                        placeholder="Search partners..."
                                        value={viewAsSearchQuery}
                                        onChange={(e) => setViewAsSearchQuery(e.target.value)}
                                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
                                        style={{ focusRing: BRAND_COLORS.gold }}
                                      />
                                    </div>
                                  </div>
                                  <div className="max-h-64 overflow-y-auto">
                                    {(() => {
                                      const activePartners = partners.filter(p => p.status === 'Active' || p.status === 'In Tail');
                                      if (activePartners.length === 0) {
                                        return (
                                          <div className="p-4 text-center text-gray-500 text-sm">
                                            No partners available
                                          </div>
                                        );
                                      }
                                      
                                      const searchLower = viewAsSearchQuery.toLowerCase().trim();
                                      const filteredPartners = searchLower
                                        ? activePartners.filter(partner => 
                                            partner.name?.toLowerCase().includes(searchLower) ||
                                            partner.email?.toLowerCase().includes(searchLower)
                                          )
                                        : activePartners;
                                      
                                      if (filteredPartners.length === 0) {
                                        return (
                                          <div className="p-4 text-center">
                                            <p className="text-sm text-gray-500">No matches</p>
                                          </div>
                                        );
                                      }
                                      
                                      return filteredPartners.map(partner => (
                                        <button
                                          key={partner.id}
                                          onClick={() => {
                                            startImpersonation(partner.id);
                                            setShowHamburgerMenu(false);
                                            setExpandedSubmenu(null);
                                          }}
                                          className="w-full px-4 py-2 flex items-center space-x-2 hover:bg-white transition-colors text-left"
                                        >
                                          <div 
                                            className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
                                            style={{ background: BRAND_COLORS.gold }}
                                          >
                                            {partner.name.charAt(0)}
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium truncate" style={{ color: BRAND_COLORS.navy }}>
                                              {partner.name}
                                            </p>
                                          </div>
                                        </button>
                                      ));
                                    })()}
                                  </div>
                                </div>
                              )}
                              <div className="border-t border-gray-200"></div>
                            </>
                          )}

                          {/* Create New - Expandable */}
                          <button
                            onClick={() => setExpandedSubmenu(expandedSubmenu === 'create' ? null : 'create')}
                            className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
                          >
                            <div className="flex items-center space-x-3">
                              <div 
                                className="w-10 h-10 rounded-lg flex items-center justify-center"
                                style={{ backgroundColor: `${BRAND_COLORS.royalBlue}15` }}
                              >
                                <Plus className="w-5 h-5" style={{ color: BRAND_COLORS.royalBlue }} />
                              </div>
                              <div>
                                <p className="font-semibold text-sm" style={{ color: BRAND_COLORS.navy }}>Create New</p>
                                <p className="text-xs text-gray-500">Partners, prospects, deals & more</p>
                              </div>
                            </div>
                            <ChevronDown 
                              className={`w-5 h-5 text-gray-400 transition-transform ${expandedSubmenu === 'create' ? 'rotate-180' : ''}`}
                            />
                          </button>
                          
                          {/* Submenu - Create Options */}
                          {expandedSubmenu === 'create' && (
                            <div className="bg-gray-50 border-l-4" style={{ borderLeftColor: BRAND_COLORS.royalBlue }}>
                              <button
                                onClick={() => {
                                  setShowNewPartnerModal(true);
                                  setShowHamburgerMenu(false);
                                  setExpandedSubmenu(null);
                                }}
                                className="w-full px-4 py-2 flex items-center space-x-2 hover:bg-white transition-colors text-left"
                              >
                                <Users className="w-5 h-5" style={{ color: BRAND_COLORS.navy }} />
                                <span className="text-sm" style={{ color: BRAND_COLORS.navy }}>New Partner</span>
                              </button>
                              
                              <button
                                onClick={() => {
                                  setShowNewProspectModal(true);
                                  setShowHamburgerMenu(false);
                                  setExpandedSubmenu(null);
                                }}
                                className="w-full px-4 py-2 flex items-center space-x-2 hover:bg-white transition-colors text-left"
                              >
                                <Building2 className="w-5 h-5" style={{ color: BRAND_COLORS.royalBlue }} />
                                <span className="text-sm" style={{ color: BRAND_COLORS.navy }}>New Prospect</span>
                              </button>
                              
                              <button
                                onClick={() => {
                                  setActiveTabPersisted('teasers');
                                  setAutoOpenTeaserUpload(true);
                                  setShowHamburgerMenu(false);
                                  setExpandedSubmenu(null);
                                }}
                                className="w-full px-4 py-2 flex items-center space-x-2 hover:bg-white transition-colors text-left"
                              >
                                <FileText className="w-5 h-5" style={{ color: BRAND_COLORS.gold }} />
                                <span className="text-sm" style={{ color: BRAND_COLORS.navy }}>New Teaser</span>
                              </button>
                              
                              <button
                                onClick={() => {
                                  setActiveTabPersisted('revenue');
                                  setShowHamburgerMenu(false);
                                  setExpandedSubmenu(null);
                                  // Small delay to ensure tab switches, then trigger calculator
                                  setTimeout(() => {
                                    // The Revenue Manager will auto-open calculator if we navigate to it
                                  }, 100);
                                }}
                                className="w-full px-4 py-2 flex items-center space-x-2 hover:bg-white transition-colors text-left"
                              >
                                <DollarSign className="w-5 h-5" style={{ color: BRAND_COLORS.gold }} />
                                <span className="text-sm" style={{ color: BRAND_COLORS.navy }}>New Revenue Deal</span>
                              </button>
                              
                              <button
                                onClick={() => {
                                  setShowNewUserModal(true);
                                  setShowHamburgerMenu(false);
                                  setExpandedSubmenu(null);
                                }}
                                className="w-full px-4 py-2 flex items-center space-x-2 hover:bg-white transition-colors text-left"
                              >
                                <UserCog className="w-5 h-5" style={{ color: BRAND_COLORS.royalBlue }} />
                                <span className="text-sm" style={{ color: BRAND_COLORS.navy }}>New User</span>
                              </button>
                            </div>
                          )}
                          
                          <div className="border-t border-gray-200"></div>

                          {/* Navigate - Expandable */}
                          <button
                            onClick={() => setExpandedSubmenu(expandedSubmenu === 'navigate' ? null : 'navigate')}
                            className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
                          >
                            <div className="flex items-center space-x-3">
                              <div 
                                className="w-10 h-10 rounded-lg flex items-center justify-center"
                                style={{ backgroundColor: `${BRAND_COLORS.gold}15` }}
                              >
                                <BarChart3 className="w-5 h-5" style={{ color: BRAND_COLORS.gold }} />
                              </div>
                              <div>
                                <p className="font-semibold text-sm" style={{ color: BRAND_COLORS.navy }}>Navigate</p>
                                <p className="text-xs text-gray-500">Go to sections</p>
                              </div>
                            </div>
                            <ChevronDown 
                              className={`w-5 h-5 text-gray-400 transition-transform ${expandedSubmenu === 'navigate' ? 'rotate-180' : ''}`}
                            />
                          </button>
                          
                          {/* Submenu - Navigation Options */}
                          {expandedSubmenu === 'navigate' && (
                            <div className="bg-gray-50 border-l-4" style={{ borderLeftColor: BRAND_COLORS.gold }}>
                              <button
                                onClick={() => {
                                  setActiveTabPersisted('analytics');
                                  setShowHamburgerMenu(false);
                                  setExpandedSubmenu(null);
                                }}
                                className="w-full px-4 py-2 flex items-center space-x-2 hover:bg-white transition-colors text-left"
                              >
                                <BarChart3 className="w-5 h-5" style={{ color: BRAND_COLORS.royalBlue }} />
                                <span className="text-sm" style={{ color: BRAND_COLORS.navy }}>Analytics</span>
                              </button>
                              
                              <button
                                onClick={() => {
                                  setActiveTabPersisted('partners');
                                  setShowHamburgerMenu(false);
                                  setExpandedSubmenu(null);
                                }}
                                className="w-full px-4 py-2 flex items-center space-x-2 hover:bg-white transition-colors text-left"
                              >
                                <Users className="w-5 h-5" style={{ color: BRAND_COLORS.navy }} />
                                <span className="text-sm" style={{ color: BRAND_COLORS.navy }}>Partners</span>
                              </button>
                              
                              <button
                                onClick={() => {
                                  setActiveTabPersisted('prospects');
                                  setShowHamburgerMenu(false);
                                  setExpandedSubmenu(null);
                                }}
                                className="w-full px-4 py-2 flex items-center space-x-2 hover:bg-white transition-colors text-left"
                              >
                                <FileText className="w-5 h-5" style={{ color: BRAND_COLORS.navy }} />
                                <span className="text-sm" style={{ color: BRAND_COLORS.navy }}>Prospects</span>
                              </button>
                              
                              <button
                                onClick={() => {
                                  setActiveTabPersisted('teasers');
                                  setShowHamburgerMenu(false);
                                  setExpandedSubmenu(null);
                                }}
                                className="w-full px-4 py-2 flex items-center space-x-2 hover:bg-white transition-colors text-left"
                              >
                                <Eye className="w-5 h-5" style={{ color: BRAND_COLORS.gold }} />
                                <span className="text-sm" style={{ color: BRAND_COLORS.navy }}>Teasers</span>
                              </button>
                              
                              <button
                                onClick={() => {
                                  setActiveTabPersisted('revenue');
                                  setShowHamburgerMenu(false);
                                  setExpandedSubmenu(null);
                                }}
                                className="w-full px-4 py-2 flex items-center space-x-2 hover:bg-white transition-colors text-left"
                              >
                                <DollarSign className="w-5 h-5" style={{ color: BRAND_COLORS.gold }} />
                                <span className="text-sm" style={{ color: BRAND_COLORS.navy }}>Revenue Manager</span>
                              </button>
                              
                              <button
                                onClick={() => {
                                  setActiveTabPersisted('workflows');
                                  setShowHamburgerMenu(false);
                                  setExpandedSubmenu(null);
                                }}
                                className="w-full px-4 py-2 flex items-center space-x-2 hover:bg-white transition-colors text-left"
                              >
                                <Zap className="w-5 h-5 text-purple-500" />
                                <span className="text-sm" style={{ color: BRAND_COLORS.navy }}>Workflows</span>
                              </button>
                              
                              <button
                                onClick={() => {
                                  setActiveTabPersisted('users');
                                  setShowHamburgerMenu(false);
                                  setExpandedSubmenu(null);
                                }}
                                className="w-full px-4 py-2 flex items-center space-x-2 hover:bg-white transition-colors text-left"
                              >
                                <UserCog className="w-5 h-5" style={{ color: BRAND_COLORS.royalBlue }} />
                                <span className="text-sm" style={{ color: BRAND_COLORS.navy }}>Users</span>
                              </button>
                              
                              <button
                                onClick={() => {
                                  setActiveTabPersisted('audit');
                                  setShowHamburgerMenu(false);
                                  setExpandedSubmenu(null);
                                }}
                                className="w-full px-4 py-2 flex items-center space-x-2 hover:bg-white transition-colors text-left"
                              >
                                <Clock className="w-5 h-5 text-gray-500" />
                                <span className="text-sm" style={{ color: BRAND_COLORS.navy }}>Audit Log</span>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
              
              {/* Help menu — replay walkthrough / how-to mini-tours. Desktop only. */}
              {!isMobile && (
                <div className="relative">
                  <button
                    onClick={() => setShowHelpMenu(v => !v)}
                    className="hover:opacity-80 transition-opacity"
                    title="Help & tours"
                  >
                    <HelpCircle className="w-6 h-6 text-white" />
                  </button>
                  {showHelpMenu && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setShowHelpMenu(false)} />
                      <div className="absolute right-0 mt-2 w-60 rounded-lg shadow-2xl border z-50 overflow-hidden"
                        style={{ backgroundColor: 'white', borderColor: BRAND_COLORS.borderNavy12 }}>
                        <button
                          onClick={() => { setShowHelpMenu(false); startTour(currentUser.role); }}
                          className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors"
                          style={{ color: BRAND_COLORS.navy }}>
                          🧭 Take the tour
                        </button>
                        {currentUser.role === 'Partner' && (
                          <button
                            onClick={() => { setShowHelpMenu(false); startProspectFormTour(); }}
                            className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors border-t"
                            style={{ color: BRAND_COLORS.navy, borderColor: BRAND_COLORS.borderNavy12 }}>
                            📝 How to submit a prospect
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Theme Toggle */}

              <div className="relative">
                <button
                  onClick={() => setShowNotificationsPanel(!showNotificationsPanel)}
                  className="relative hover:opacity-80 transition-opacity"
                  title="View notifications"
                >
                  <Bell className="w-6 h-6 text-white" />
                  {unreadNotifications.length > 0 && (
                    <span 
                      className="absolute -top-1 -right-1 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center shadow-lg font-bold"
                      style={{ backgroundColor: BRAND_COLORS.royalBlue }}
                    >
                      {unreadNotifications.length}
                    </span>
                  )}
                </button>
              </div>
              
              
              <button
                onClick={() => setShowUserProfile(true)}
                className="flex items-center space-x-2 hover:opacity-80 transition-opacity group"
                title="View / Edit Profile"
              >
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white shadow"
                  style={{ background: BRAND_COLORS.gold }}
                >
                  {currentUser.name.charAt(0).toUpperCase()}
                </div>
                {!isMobile && (
                  <div className="text-right">
                    <p className="text-sm font-medium text-white group-hover:underline">{currentUser.name}</p>
                    <p className="text-xs" style={{ color: '#D4AF37' }}>{currentUser.role}</p>
                  </div>
                )}
              </button>
              
            </div>
          </div>
        </div>
      </header>

      {/* Main Layout */}
      <div className="flex flex-1 overflow-hidden">

        {/* ── PARTNER: top nav bar (no sidebar) ─────────────────────────────── */}
        {currentUser.role === 'Partner' ? (
          <>
            {/* ── PARTNER: collapsible sidebar ──────────────────────────────── */}
            {isMobile && mobileSidebarOpen && (
              <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setMobileSidebarOpen(false)} />
            )}
            <aside
              className={`flex-shrink-0 flex flex-col border-r-2 transition-all duration-200 overflow-y-auto ${isMobile ? 'fixed top-0 left-0 h-full z-50' : ''}`}
              style={{
                width: isMobile ? (mobileSidebarOpen ? 240 : 0) : (sidebarCollapsed ? 64 : 240),
                background: colors.sidebarBg,
                borderColor: colors.gold,
                ...(isMobile && !mobileSidebarOpen ? { overflow: 'hidden', border: 'none' } : {}),
              }}
            >
              <div className="flex-1 py-4">
                {/* Section: Main */}
                {!sidebarCollapsed && (
                  <p className="px-4 pb-1 text-xs font-semibold uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.35)' }}>Main</p>
                )}
                <SidebarButton active={activeTab === 'dashboard'} onClick={() => setActiveTabPersisted('dashboard')}
                  icon={TrendingUp} label="Dashboard" colors={colors} collapsed={sidebarCollapsed} dataTour="nav-dashboard" />

                {/* Section: My Activity */}
                {!sidebarCollapsed && (
                  <p className="px-4 pt-4 pb-1 text-xs font-semibold uppercase tracking-wider" style={{ color: colors.textSecondary }}>My Activity</p>
                )}
                {sidebarCollapsed && <div className="my-3 mx-4 border-t" style={{ borderColor: 'rgba(212,175,55,0.2)' }} />}
                <SidebarButton active={activeTab === 'prospects'} onClick={() => setActiveTabPersisted('prospects')}
                  icon={FileText} label="Prospects" colors={colors} collapsed={sidebarCollapsed} dataTour="nav-prospects" />
                <SidebarButton active={activeTab === 'teasers'} onClick={() => setActiveTabPersisted('teasers')}
                  icon={Eye} label="Teasers" dataTour="nav-teasers"
                  badge={(() => {
                    const uid = currentUser.partnerId || currentUser.id;
                    const unseen = teasers.filter(t => !(t.seenBy || []).includes(uid)).length;
                    return unseen > 0 ? unseen : null;
                  })()}
                  colors={colors} collapsed={sidebarCollapsed} />
                <SidebarButton active={activeTab === 'revenue'} onClick={() => setActiveTabPersisted('revenue')}
                  icon={DollarSign} label="My Revenue" dataTour="nav-revenue"
                  badge={revenueDeals.filter(d => {
                    const pid = currentUser.partnerId || currentUser.id;
                    const lp = d.prospectId ? prospects.find(p => p.id === d.prospectId) : null;
                    const ivp = d.investorProspectId ? prospects.find(p => p.id === d.investorProspectId) : null;
                    return lp?.introducingPartnerId === pid || ivp?.introducingPartnerId === pid ||
                      d.strategicPartners?.some(sp => sp.partnerId === pid);
                  }).length || null}
                  colors={colors} collapsed={sidebarCollapsed} />

                <SidebarButton active={activeTab === 'messages'} onClick={() => setActiveTabPersisted('messages')}
                  icon={MessageCircle} label="My Messages" dataTour="nav-messages"
                  badge={userThreads.filter(t => t.partnerUnread).length || null}
                  colors={colors} collapsed={sidebarCollapsed} />

                {/* Section: Account */}
                {!sidebarCollapsed && (
                  <p className="px-4 pt-4 pb-1 text-xs font-semibold uppercase tracking-wider" style={{ color: colors.textSecondary }}>Account</p>
                )}
                {sidebarCollapsed && <div className="my-3 mx-4 border-t" style={{ borderColor: 'rgba(212,175,55,0.2)' }} />}
                <SidebarButton active={activeTab === 'profile'} onClick={() => setActiveTabPersisted('profile')}
                  icon={UserCog} label="My Profile" colors={colors} collapsed={sidebarCollapsed} dataTour="nav-profile" />
              </div>

              {/* Bottom: Logout + collapse toggle */}
              <div className="border-t-2 py-2" style={{ borderColor: colors.gold }}>
                <SidebarButton onClick={handleLogout} icon={LogOut} label="Logout"
                  colors={colors} collapsed={sidebarCollapsed} active={false} />
                {!isMobile && (
                  <button
                    onClick={toggleSidebar}
                    title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    className="w-full flex items-center justify-center py-2 transition-colors"
                    style={{ color: colors.textSecondary }}
                    onMouseEnter={e => e.currentTarget.style.color = BRAND_COLORS.gold}
                    onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.5)'}
                  >
                    {sidebarCollapsed
                      ? <ChevronRight className="w-4 h-4" />
                      : <ChevronLeft className="w-4 h-4" />
                    }
                    {!sidebarCollapsed && <span className="text-xs ml-1">Collapse</span>}
                  </button>
                )}
              </div>
            </aside>

            {/* Partner main content */}
            <div className="flex-1 overflow-auto transition-colors duration-200" style={{ backgroundColor: colors.background }}>
              <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">

        {/* Global Search Bar */}
        {activeTab !== 'dashboard' && activeTab !== 'analytics' && (
          <div className="mb-6">
            <GlobalSearchBar 
              searchTerm={globalSearch}
              onSearchChange={setGlobalSearch}
              filters={advancedFilters}
              onFilterChange={setAdvancedFilters}
              onClearFilters={clearFilters}
              activeFilterCount={getActiveFilterCount()}
              partners={partners}
              entityType={activeTab}
              sectors={sectors}
            />
          </div>
        )}
        
        {activeTab === 'dashboard' && (
  currentUser.role === 'Partner' 
    ? <PartnerDashboard 
        currentUser={currentUser}
        prospects={prospects}
        transactions={transactions}
        partners={partners}
        revenueDeals={revenueDeals}
        theme={BRAND_COLORS}
        onAddProspect={() => setShowNewProspectModal(true)}
      />
    : <DashboardView 
        stats={stats} 
        notifications={unreadNotifications} 
        prospects={prospects} 
        partners={partners} 
        role={currentUser.role} 
        revenueDeals={revenueDeals}
        teasers={teasers}
        workflows={workflows}
        deletionRequests={deletionRequests}
        auditLogs={auditLogs}
      />
)}
        {activeTab === 'analytics' && (currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') && (
          <AnalyticsView 
            analyticsData={getAnalyticsData()} 
            stats={stats} 
            prospects={prospects}
            partners={partners}
            teasers={teasers}
            pools={pools}
            transactions={transactions}
            revenueDeals={revenueDeals}
          />
        )}
        {activeTab === 'partners' && (currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') && (
          <PartnersView
            partners={globalSearch ? performGlobalSearch(globalSearch).partners : partners}
            prospects={prospects}
            revenueDeals={revenueDeals}
            users={users}
            onAdd={() => setShowNewPartnerModal(true)}
            onEditPartner={(partner) => {
              setEditingPartner(partner);
              setShowEditPartnerModal(true);
            }}
            onEditProspect={(prospect) => {
              setEditingProspect(prospect);
              setShowEditProspectModal(true);
            }}
            role={currentUser.role}
            currentUserRole={currentUser.role}
            showConfirm={showConfirm}
            draftPartners={wizardDrafts.partner}
            onResumeDraft={resumePartnerDraft}
            onDiscardDraft={confirmDiscardDraft('partner', d => d.name)}
          />
        )}
        {activeTab === 'prospects' && <ProspectsView prospects={applyAdvancedFilters(userProspects, 'prospects')} partners={partners} users={users} onAdd={() => setShowNewProspectModal(true)} onApprove={approveProspect} onReject={rejectProspect} onLogIntro={logIntro} onEditProspect={(prospect) => { setEditingProspect(prospect); setShowEditProspectModal(true); }} onDeleteProspect={(currentUser.role === 'Admin' || currentUser.role === 'Super Admin') ? deleteProspect : null} onRequestDelete={currentUser.role === 'Editor' ? sendDeleteRequest : null} onToggleHidden={(currentUser.role === 'Editor' || currentUser.role === 'Admin' || currentUser.role === 'Super Admin') ? (id, next) => toggleHiddenFromPartners('prospect', id, next) : null} role={currentUser.role} currentUserRole={currentUser.role} teasers={teasers} revenueDeals={revenueDeals} showConfirm={showConfirm} onAskQuestion={currentUser.role === 'Partner' ? (ctx) => openMessageFor(ctx) : null} onShowHowTo={currentUser.role === 'Partner' ? startProspectFormTour : null} onArchive={(currentUser.role === 'Editor' || currentUser.role === 'Admin' || currentUser.role === 'Super Admin') ? setProspectArchived : null} draftProspects={wizardDrafts.prospect} onResumeDraft={resumeProspectDraft} onDiscardDraft={confirmDiscardDraft('prospect', d => d.companyName || d.name)} />}
        {activeTab === 'teasers' && (
          <TeasersView
            teasers={applyAdvancedFilters(userTeasers, 'teasers')}
            partners={partners}
            prospects={userProspects}
            deals={revenueDeals}
            autoOpenUpload={autoOpenTeaserUpload} onConsumeAutoOpen={() => setAutoOpenTeaserUpload(false)} 
            onEdit={(currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') ? (teaser) => {
              setEditingTeaser(teaser);
              setShowEditTeaserModal(true);
            } : null}
            role={currentUser.role}
            sectors={sectors}
            currentUserProp={currentUser}
            onToggleHidden={(currentUser.role === 'Editor' || currentUser.role === 'Admin' || currentUser.role === 'Super Admin') ? (id, next) => toggleHiddenFromPartners('teaser', id, next) : null}
            onDeleteTeaser={(currentUser.role === 'Admin' || currentUser.role === 'Super Admin') ? async (teaserId, updatedTeasers) => {
              try {
                const { invokeFunction } = await import('./supabaseClient');
                const { data, error } = await invokeFunction('manage-teaser', { body: { action: 'delete', payload: { id: teaserId } } });
                if (error || data?.error) { console.error('[deleteTeaser]', error?.message || data?.error); return; }
              } catch(e) { console.error('[deleteTeaser]', e.message); return; }
              setTeasers(updatedTeasers);
              addAuditLog('Teaser', teaserId, 'Deleted', currentUser.id, 'Deleted teaser');
            } : null}
            onRequestDelete={currentUser.role === 'Editor' ? sendDeleteRequest : null}
            onAddTeaser={(currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') ? async (newTeaser, updatedTeasers) => {
              setTeasers(updatedTeasers);
              addAuditLog('Teaser', newTeaser.id, 'Uploaded', currentUser.id, `Uploaded teaser: ${newTeaser.name}`);
              try {
                const { invokeFunction } = await import('./supabaseClient');
                const { data, error } = await invokeFunction('manage-teaser', { body: { action: 'create', payload: teaserToRow(newTeaser) } });
                if (error || data?.error) console.error('[addTeaser]', error?.message || data?.error);
              } catch(e) { console.error('[addTeaser]', e.message); }
            } : null}
            onUpdateTeaser={(currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') ? (teaserId, updatedTeasers) => {
              setTeasers(updatedTeasers);
              saveData('teasers', updatedTeasers);
              addAuditLog('Teaser', teaserId, 'Updated', currentUser.id, 'Updated teaser');
            } : null}
            onMarkSeen={(teaserId, updatedTeasers) => {
              setTeasers(updatedTeasers);
              saveData('teasers', updatedTeasers);
            }}
            onAskQuestion={currentUser.role === 'Partner' ? (ctx) => openMessageFor(ctx) : null}
            showConfirm={showConfirm}
          />
        )}
        {activeTab === 'workflows' && (currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') && <WorkflowsView workflows={workflows} onAdd={(currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') ? () => setShowNewWorkflowModal(true) : null} onToggle={toggleWorkflow} onDelete={(currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') ? (wfId) => { const wf = workflows.find(w => w.id === wfId); if (currentUser.role === 'Editor' && wf?.createdBy !== currentUser.id && wf?.createdBy !== currentUser.name) { window.alert('Editors can only delete workflows they created.'); return; } deleteWorkflow(wfId); } : null} onRequestDelete={currentUser.role === 'Editor' ? sendDeleteRequest : null} role={currentUser.role} showConfirm={showConfirm} hotTopics={hotTopics} partners={partners} teasers={teasers} onSaveHotTopic={saveHotTopic} onDeleteHotTopic={deleteHotTopic} draftWorkflows={wizardDrafts.workflow} onResumeDraft={resumeWorkflowDraft} onDiscardDraft={confirmDiscardDraft('workflow', d => d.name)} />}
        {activeTab === 'revenue' && (
          <RevenueManagerView
            deals={liveOnly(revenueDeals)}
            draftDeals={draftsOnly(revenueDeals)}
            onSaveDealDraft={(row, step) => saveWizardDraft('deal', row, step)}
            onDiscardDealDraft={(id) => discardWizardDraft('deal', id)}
            partners={partners}
            prospects={prospects}
            currentUser={currentUser}
            commissionDefaults={commissionDefaults}
            teasers={teasers}
            exchangeRates={exchangeRates}
            onSaveDeal={async (deal) => {
              const updated = [...revenueDeals, deal];
              setRevenueDeals(updated);
              try {
                const { invokeFunction } = await import('./supabaseClient');
                const { data, error } = await invokeFunction('manage-deal', {
                  body: { action: 'create', payload: dealToRow(deal) },
                });
                if (error || data?.error) console.error('[onSaveDeal] function error:', error?.message || data?.error);
                else console.log('[onSaveDeal] deal saved to backend:', deal.id);
              } catch (e) { console.error('[onSaveDeal] unexpected error:', e.message, e); }
              addAuditLog('Deal', deal.id, 'Created', currentUser.id, `Created revenue deal: ${deal.dealName}`);
              executeWorkflows('revenue_deal_created', { deal });
              const linkedProspect = deal.prospectId ? prospects.find(p => p.id === deal.prospectId) : null;
              if (linkedProspect?.introducingPartnerId) sendInAppNotification(linkedProspect.introducingPartnerId, 'deal-closed', deal);
              const investorProsp = deal.investorProspectId ? prospects.find(p => p.id === deal.investorProspectId) : null;
              if (investorProsp?.introducingPartnerId) sendInAppNotification(investorProsp.introducingPartnerId, 'deal-closed', deal);
            }}
            onUpdateDeal={updateRevenueDeal}
            onDeleteDeal={(currentUser.role === 'Admin' || currentUser.role === 'Super Admin') ? deleteDeal : null}
            onRequestDelete={currentUser.role === 'Editor' ? sendDeleteRequest : null}
            onAddPayment={addPaymentToDeal}
            onDeletePayment={(currentUser.role === 'Admin' || currentUser.role === 'Super Admin') ? deletePaymentFromDeal : null}
            onToggleHidden={(currentUser.role === 'Editor' || currentUser.role === 'Admin' || currentUser.role === 'Super Admin') ? (id, next) => toggleHiddenFromPartners('deal', id, next) : null}
            showConfirm={showConfirm}
          />
        )}
        {activeTab === 'profile' && currentUser.role === 'Partner' && (
          <PartnerProfileView
            currentUser={currentUser}
            partner={partners.find(p => p.id === (currentUser.partnerId || currentUser.id))}
            prospects={userProspects}
            revenueDeals={revenueDeals}
            onEditProfile={() => setShowUserProfile(true)}
          />
        )}
        {activeTab === 'messages' && currentUser.role === 'Partner' && (
          <PartnerMessagesView
            currentUser={currentUser}
            threads={userThreads}
            posts={userPosts}
            teasers={userTeasers}
            prospects={userProspects}
            onNewMessage={() => setShowNewMessageModal(true)}
            onAddPost={addMessagePost}
            onMarkRead={markThreadRead}
            onClose={closeThread}
          />
        )}
        {activeTab === 'messages' && currentUser.role !== 'Partner' && (
          <AdminMessagesView
            currentUser={currentUser}
            threads={partnerThreads}
            posts={partnerPosts}
            partners={partners}
            onAddPost={addMessagePost}
            onMarkRead={markThreadRead}
            onClose={closeThread}
          />
        )}
        {activeTab === 'users' && (currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') && (
          <UsersView
            users={identities}
            partners={partners}
            onAdd={(currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') ? () => setShowNewUserModal(true) : null}
            onEdit={(currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') ? (user) => { setEditingUser(user); setShowEditUserModal(true); } : null}
            onToggleStatus={(currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') ? toggleUserStatus : null}
            onDelete={(currentUser.role === 'Admin' || currentUser.role === 'Super Admin') ? deleteUser : null}
            onResetPassword={(currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') ? resetUserPassword : null}
            onResendConfirmation={(currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') ? resendConfirmationEmail : null}
            onInvitePartner={(currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') ? inviteUserForPartner : null}
            showConfirm={showConfirm}
          />
        )}
        {activeTab === 'audit' && (currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') && <AuditLogView logs={auditLogs} users={users} />}
        {activeTab === 'requests' && (currentUser.role === 'Admin' || currentUser.role === 'Super Admin') && <DeleteRequestsView requests={deletionRequests} onProcess={processDeletionRequest} showConfirm={showConfirm} />}
        {activeTab === 'settings' && currentUser.role === 'Super Admin' && (
          <SettingsView
            sectors={sectors}
            onAddSector={addSector}
            onRemoveSector={removeSector}
            businessRules={businessRules}
            onSaveBusinessRules={saveBusinessRules}
            agreementTypes={agreementTypes}
            onAddAgreementType={addAgreementType}
            onRemoveAgreementType={removeAgreementType}
            commissionDefaults={commissionDefaults}
            onSaveCommissionDefaults={saveCommissionDefaults}
            generalSettings={generalSettings}
            onSaveSettings={saveGeneralSettings}
            securitySettings={securitySettings}
            onSaveSecuritySettings={saveSecuritySettings}
            currentUserRole={currentUser?.role}
            showConfirm={showConfirm}
          />
        )}
              </main>
            </div>
          </>
        ) : (
          /* ── ADMIN / EDITOR: collapsible sidebar ─────────────────────────── */
          <>
            {isMobile && mobileSidebarOpen && (
              <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setMobileSidebarOpen(false)} />
            )}
            <aside
              className={`flex-shrink-0 flex flex-col border-r-2 transition-all duration-200 overflow-y-auto ${isMobile ? 'fixed top-0 left-0 h-full z-50' : ''}`}
              style={{
                width: isMobile ? (mobileSidebarOpen ? 240 : 0) : (sidebarCollapsed ? 64 : 240),
                background: colors.sidebarBg,
                borderColor: colors.gold,
                ...(isMobile && !mobileSidebarOpen ? { overflow: 'hidden', border: 'none' } : {}),
              }}
            >
              <div className="flex-1 py-4">
                {/* Section: Main */}
                {!sidebarCollapsed && (
                  <p className="px-4 pb-1 text-xs font-semibold uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.35)' }}>Main</p>
                )}
                <SidebarButton active={activeTab === 'dashboard'} onClick={() => setActiveTabPersisted('dashboard')}
                  icon={TrendingUp} label="Dashboard" colors={colors} collapsed={sidebarCollapsed} dataTour="nav-dashboard" />

                {/* Section: Management */}
                {!sidebarCollapsed && (
                  <p className="px-4 pt-4 pb-1 text-xs font-semibold uppercase tracking-wider" style={{ color: colors.textSecondary }}>Management</p>
                )}
                {sidebarCollapsed && <div className="my-3 mx-4 border-t" style={{ borderColor: 'rgba(212,175,55,0.2)' }} />}
                <SidebarButton active={activeTab === 'partners'} onClick={() => setActiveTabPersisted('partners')}
                  icon={Users} label="Partners" colors={colors} collapsed={sidebarCollapsed} dataTour="nav-partners" />
                <SidebarButton active={activeTab === 'prospects'} onClick={() => setActiveTabPersisted('prospects')}
                  icon={FileText} label="Prospects" badge={stats.pendingProspects} colors={colors} collapsed={sidebarCollapsed} dataTour="nav-prospects" />
                <SidebarButton active={activeTab === 'teasers'} onClick={() => setActiveTabPersisted('teasers')}
                  icon={Eye} label="Teasers" colors={colors} collapsed={sidebarCollapsed} dataTour="nav-teasers" />
                <SidebarButton active={activeTab === 'revenue'} onClick={() => setActiveTabPersisted('revenue')}
                  icon={DollarSign} label="Revenue Manager" colors={colors} collapsed={sidebarCollapsed} dataTour="nav-revenue" />

                {/* Section: Automation */}
                {!sidebarCollapsed && (
                  <p className="px-4 pt-4 pb-1 text-xs font-semibold uppercase tracking-wider" style={{ color: colors.textSecondary }}>Automation</p>
                )}
                {sidebarCollapsed && <div className="my-3 mx-4 border-t" style={{ borderColor: 'rgba(212,175,55,0.2)' }} />}
                <SidebarButton active={activeTab === 'workflows'} onClick={() => setActiveTabPersisted('workflows')}
                  icon={Zap} label="Workflows" colors={colors} collapsed={sidebarCollapsed} dataTour="nav-workflows" />
                <SidebarButton active={activeTab === 'analytics'} onClick={() => setActiveTabPersisted('analytics')}
                  icon={BarChart3} label="Analytics" colors={colors} collapsed={sidebarCollapsed} dataTour="nav-analytics" />

                {/* Section: System */}
                {!sidebarCollapsed && (
                  <p className="px-4 pt-4 pb-1 text-xs font-semibold uppercase tracking-wider" style={{ color: colors.textSecondary }}>System</p>
                )}
                {sidebarCollapsed && <div className="my-3 mx-4 border-t" style={{ borderColor: 'rgba(212,175,55,0.2)' }} />}
                <SidebarButton active={activeTab === 'audit'} onClick={() => setActiveTabPersisted('audit')}
                  icon={Clock} label="Audit Log" colors={colors} collapsed={sidebarCollapsed} />
                <SidebarButton active={activeTab === 'messages'} onClick={() => setActiveTabPersisted('messages')}
                  icon={Inbox} label="Partner Messages"
                  badge={partnerThreads.filter(t => t.adminUnread).length || null}
                  colors={colors} collapsed={sidebarCollapsed} />
                {(currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') && (
                  <>
                    <SidebarButton active={activeTab === 'users'} onClick={() => setActiveTabPersisted('users')}
                      icon={UserCog} label="Users" colors={colors} collapsed={sidebarCollapsed} />
                    <SidebarButton active={activeTab === 'settings'} onClick={() => setActiveTabPersisted('settings')}
                      icon={Settings} label="Settings" colors={colors} collapsed={sidebarCollapsed} />
                  </>
                )}
                {(currentUser.role === 'Admin' || currentUser.role === 'Super Admin') && (
                  <SidebarButton active={activeTab === 'requests'} onClick={() => setActiveTabPersisted('requests')}
                    icon={AlertCircle} label="Requests"
                    badge={deletionRequests.filter(r => r.status === 'Pending').length || null}
                    colors={colors} collapsed={sidebarCollapsed} />
                )}
              </div>

              {/* Bottom: Logout + collapse toggle */}
              <div className="border-t-2 py-2" style={{ borderColor: colors.gold }}>
                <SidebarButton onClick={handleLogout} icon={LogOut} label="Logout"
                  colors={colors} collapsed={sidebarCollapsed} active={false} />
                {!isMobile && (
                  <button
                    onClick={toggleSidebar}
                    title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    className="w-full flex items-center justify-center py-2 transition-colors"
                    style={{ color: colors.textSecondary }}
                    onMouseEnter={e => e.currentTarget.style.color = BRAND_COLORS.gold}
                    onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.5)'}
                  >
                    {sidebarCollapsed
                      ? <ChevronRight className="w-4 h-4" />
                      : <ChevronLeft className="w-4 h-4" />
                    }
                    {!sidebarCollapsed && <span className="text-xs ml-1">Collapse</span>}
                  </button>
                )}
              </div>
            </aside>

            {/* Admin/Editor main content */}
            <div className="flex-1 overflow-auto transition-colors duration-200" style={{ backgroundColor: colors.background }}>
              <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {activeTab !== 'dashboard' && activeTab !== 'analytics' && (
          <div className="mb-6">
            <GlobalSearchBar
              searchTerm={globalSearch}
              onSearchChange={setGlobalSearch}
              filters={advancedFilters}
              onFilterChange={setAdvancedFilters}
              onClearFilters={clearFilters}
              activeFilterCount={getActiveFilterCount()}
              partners={partners}
              entityType={activeTab}
              sectors={sectors}
            />
          </div>
        )}

        {activeTab === 'dashboard' && (
          <DashboardView
            stats={stats}
            notifications={unreadNotifications}
            prospects={prospects}
            partners={partners}
            role={currentUser.role}
            revenueDeals={revenueDeals}
            teasers={teasers}
            workflows={workflows}
            deletionRequests={deletionRequests}
            auditLogs={auditLogs}
          />
        )}
        {activeTab === 'analytics' && (
          <AnalyticsView
            analyticsData={getAnalyticsData()}
            stats={stats}
            prospects={prospects}
            partners={partners}
            teasers={teasers}
            pools={pools}
            transactions={transactions}
            revenueDeals={revenueDeals}
          />
        )}
        {activeTab === 'partners' && (
          <PartnersView
            partners={globalSearch ? performGlobalSearch(globalSearch).partners : partners}
            prospects={prospects}
            revenueDeals={revenueDeals}
            users={users}
            onAdd={() => setShowNewPartnerModal(true)}
            onEditPartner={(partner) => { setEditingPartner(partner); setShowEditPartnerModal(true); }}
            onEditProspect={(prospect) => { setEditingProspect(prospect); setShowEditProspectModal(true); }}
            onDeletePartner={(currentUser.role === 'Admin' || currentUser.role === 'Super Admin') ? deletePartner : null}
            onRequestDelete={currentUser.role === 'Editor' ? sendDeleteRequest : null}
            role={currentUser.role}
            currentUserRole={currentUser.role}
            showConfirm={showConfirm}
            draftPartners={wizardDrafts.partner}
            onResumeDraft={resumePartnerDraft}
            onDiscardDraft={confirmDiscardDraft('partner', d => d.name)}
          />
        )}
        {activeTab === 'prospects' && (
          <ProspectsView
            prospects={applyAdvancedFilters(userProspects, 'prospects')}
            partners={partners}
            users={users}
            onAdd={() => setShowNewProspectModal(true)}
            onApprove={approveProspect}
            onReject={rejectProspect}
            onLogIntro={logIntro}
            onEditProspect={(prospect) => { setEditingProspect(prospect); setShowEditProspectModal(true); }}
            onDeleteProspect={(currentUser.role === 'Admin' || currentUser.role === 'Super Admin') ? deleteProspect : null}
            onRequestDelete={currentUser.role === 'Editor' ? sendDeleteRequest : null}
            role={currentUser.role}
            currentUserRole={currentUser.role}
            teasers={teasers}
            revenueDeals={revenueDeals}
            showConfirm={showConfirm}
            onShowHowTo={currentUser.role === 'Partner' ? startProspectFormTour : null}
            onArchive={(currentUser.role === 'Editor' || currentUser.role === 'Admin' || currentUser.role === 'Super Admin') ? setProspectArchived : null}
            draftProspects={wizardDrafts.prospect}
            onResumeDraft={resumeProspectDraft}
            onDiscardDraft={confirmDiscardDraft('prospect', d => d.companyName || d.name)}
          />
        )}
        {activeTab === 'teasers' && (
          <TeasersView
            teasers={applyAdvancedFilters(userTeasers, 'teasers')}
            partners={partners}
            prospects={prospects}
            deals={revenueDeals}
            autoOpenUpload={autoOpenTeaserUpload} onConsumeAutoOpen={() => setAutoOpenTeaserUpload(false)}
            onEdit={(currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') ? (teaser) => { setEditingTeaser(teaser); setShowEditTeaserModal(true); } : null}
            role={currentUser.role}
            sectors={sectors}
            currentUserProp={currentUser}
            onToggleHidden={(currentUser.role === 'Editor' || currentUser.role === 'Admin' || currentUser.role === 'Super Admin') ? (id, next) => toggleHiddenFromPartners('teaser', id, next) : null}
            onDeleteTeaser={(currentUser.role === 'Admin' || currentUser.role === 'Super Admin') ? async (teaserId, updatedTeasers) => {
              try {
                const { invokeFunction } = await import('./supabaseClient');
                const { data, error } = await invokeFunction('manage-teaser', { body: { action: 'delete', payload: { id: teaserId } } });
                if (error || data?.error) { console.error('[deleteTeaser]', error?.message || data?.error); return; }
              } catch(e) { console.error('[deleteTeaser]', e.message); return; }
              setTeasers(updatedTeasers);
              addAuditLog('Teaser', teaserId, 'Deleted', currentUser.id, 'Deleted teaser');
            } : null}
            onRequestDelete={currentUser.role === 'Editor' ? sendDeleteRequest : null}
            onAddTeaser={(currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') ? async (newTeaser, updatedTeasers) => {
              setTeasers(updatedTeasers);
              addAuditLog('Teaser', newTeaser.id, 'Uploaded', currentUser.id, `Uploaded teaser: ${newTeaser.name}`);
              try {
                const { invokeFunction } = await import('./supabaseClient');
                const { data, error } = await invokeFunction('manage-teaser', { body: { action: 'create', payload: teaserToRow(newTeaser) } });
                if (error || data?.error) console.error('[addTeaser]', error?.message || data?.error);
              } catch(e) { console.error('[addTeaser]', e.message); }
            } : null}
            onUpdateTeaser={(currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') ? (teaserId, updatedTeasers) => {
              setTeasers(updatedTeasers);
              saveData('teasers', updatedTeasers);
              addAuditLog('Teaser', teaserId, 'Updated', currentUser.id, 'Updated teaser');
            } : null}
            onMarkSeen={(teaserId, updatedTeasers) => {
              setTeasers(updatedTeasers);
              saveData('teasers', updatedTeasers);
            }}
            onAskQuestion={currentUser.role === 'Partner' ? (ctx) => openMessageFor(ctx) : null}
            showConfirm={showConfirm}
          />
        )}
        {activeTab === 'workflows' && (
          <WorkflowsView
            workflows={workflows}
            onAdd={(currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') ? () => setShowNewWorkflowModal(true) : null}
            onToggle={toggleWorkflow}
            onDelete={(currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') ? (wfId) => { const wf = workflows.find(w => w.id === wfId); if (currentUser.role === 'Editor' && wf?.createdBy !== currentUser.id && wf?.createdBy !== currentUser.name) { window.alert('Editors can only delete workflows they created.'); return; } deleteWorkflow(wfId); } : null}
            onRequestDelete={currentUser.role === 'Editor' ? sendDeleteRequest : null}
            onEdit={(currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') ? (wf) => { setEditingWorkflow(wf); setShowEditWorkflowModal(true); } : null}
            onSaveNotificationSettings={(currentUser.role === 'Admin' || currentUser.role === 'Super Admin') ? saveNotificationSettings : null}
            role={currentUser.role}
            showConfirm={showConfirm}
            hotTopics={hotTopics}
            partners={partners}
            teasers={teasers}
            onSaveHotTopic={saveHotTopic}
            onDeleteHotTopic={deleteHotTopic}
            draftWorkflows={wizardDrafts.workflow}
            onResumeDraft={resumeWorkflowDraft}
            onDiscardDraft={confirmDiscardDraft('workflow', d => d.name)}
          />
        )}
        {activeTab === 'revenue' && (
          <RevenueManagerView
            deals={liveOnly(revenueDeals)}
            draftDeals={draftsOnly(revenueDeals)}
            onSaveDealDraft={(row, step) => saveWizardDraft('deal', row, step)}
            onDiscardDealDraft={(id) => discardWizardDraft('deal', id)}
            partners={partners}
            prospects={prospects}
            currentUser={currentUser}
            commissionDefaults={commissionDefaults}
            teasers={teasers}
            exchangeRates={exchangeRates}
            onSaveDeal={async (deal) => {
              const updated = [...revenueDeals, deal];
              setRevenueDeals(updated);
              try {
                const { invokeFunction } = await import('./supabaseClient');
                const { data, error } = await invokeFunction('manage-deal', {
                  body: { action: 'create', payload: dealToRow(deal) },
                });
                if (error || data?.error) console.error('[onSaveDeal] function error:', error?.message || data?.error);
                else console.log('[onSaveDeal] deal saved to backend:', deal.id);
              } catch (e) { console.error('[onSaveDeal] unexpected error:', e.message, e); }
              addAuditLog('Deal', deal.id, 'Created', currentUser.id, `Created revenue deal: ${deal.dealName}`);
            }}
            onUpdateDeal={updateRevenueDeal}
            onDeleteDeal={(currentUser.role === 'Admin' || currentUser.role === 'Super Admin') ? deleteDeal : null}
            onRequestDelete={currentUser.role === 'Editor' ? sendDeleteRequest : null}
            onAddPayment={addPaymentToDeal}
            onDeletePayment={(currentUser.role === 'Admin' || currentUser.role === 'Super Admin') ? deletePaymentFromDeal : null}
            onToggleHidden={(currentUser.role === 'Editor' || currentUser.role === 'Admin' || currentUser.role === 'Super Admin') ? (id, next) => toggleHiddenFromPartners('deal', id, next) : null}
            showConfirm={showConfirm}
          />
        )}
        {activeTab === 'messages' && (
          <AdminMessagesView
            currentUser={currentUser}
            threads={partnerThreads}
            posts={partnerPosts}
            partners={partners}
            onAddPost={addMessagePost}
            onMarkRead={markThreadRead}
            onClose={closeThread}
          />
        )}
        {activeTab === 'users' && (currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') && (
          <UsersView
            users={identities}
            partners={partners}
            onAdd={(currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') ? () => setShowNewUserModal(true) : null}
            onEdit={(currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') ? (user) => { setEditingUser(user); setShowEditUserModal(true); } : null}
            onToggleStatus={(currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') ? toggleUserStatus : null}
            onDelete={(currentUser.role === 'Admin' || currentUser.role === 'Super Admin') ? deleteUser : null}
            onResetPassword={(currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') ? resetUserPassword : null}
            onResendConfirmation={(currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') ? resendConfirmationEmail : null}
            onInvitePartner={(currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') ? inviteUserForPartner : null}
            showConfirm={showConfirm}
          />
        )}
        {activeTab === 'audit' && <AuditLogView logs={auditLogs} users={users} />}
        {activeTab === 'requests' && (currentUser.role === 'Admin' || currentUser.role === 'Super Admin') && <DeleteRequestsView requests={deletionRequests} onProcess={processDeletionRequest} showConfirm={showConfirm} />}
        {activeTab === 'settings' && currentUser.role === 'Super Admin' && (
          <SettingsView
            sectors={sectors}
            onAddSector={addSector}
            onRemoveSector={removeSector}
            businessRules={businessRules}
            onSaveBusinessRules={saveBusinessRules}
            agreementTypes={agreementTypes}
            onAddAgreementType={addAgreementType}
            onRemoveAgreementType={removeAgreementType}
            commissionDefaults={commissionDefaults}
            onSaveCommissionDefaults={saveCommissionDefaults}
            generalSettings={generalSettings}
            onSaveSettings={saveGeneralSettings}
            securitySettings={securitySettings}
            onSaveSecuritySettings={saveSecuritySettings}
            currentUserRole={currentUser?.role}
            showConfirm={showConfirm}
          />
        )}
              </main>
            </div>
          </>
        )}
      </div>
      {showNewMessageModal && (
        <NewMessageModal
          currentUser={currentUser}
          context={newMessageContext}
          teasers={userTeasers}
          prospects={userProspects}
          onClose={() => { setShowNewMessageModal(false); setNewMessageContext(null); }}
          onSubmit={async (data) => {
            const thread = await createMessageThread(data);
            if (thread) {
              setShowNewMessageModal(false);
              setNewMessageContext(null);
              setActiveTabPersisted('messages');
            }
          }}
        />
      )}
      {showUserProfile && (
        <UserProfileModal
          currentUser={currentUser}
          partner={currentUser.role === 'Partner' ? partners.find(p => p.id === (currentUser.partnerId || currentUser.id)) : null}
          onClose={() => setShowUserProfile(false)}
          onSave={updateUserProfile}
          sectors={sectors}
        />
      )}
      {confirmDialog.show && (
        <ConfirmDialog
          message={confirmDialog.message}
          confirmLabel={confirmDialog.confirmLabel}
          onConfirm={() => { confirmDialog.onConfirm?.(); setConfirmDialog({ show: false, message: '', onConfirm: null, confirmLabel: 'Confirm' }); }}
          onClose={() => setConfirmDialog({ show: false, message: '', onConfirm: null, confirmLabel: 'Confirm' })}
        />
      )}
      {blockModal.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <div className="rounded-xl overflow-hidden shadow-2xl w-full max-w-sm mx-4" style={{ background: '#fff' }}>
            <div className="px-6 py-4" style={{ backgroundColor: BRAND_COLORS.navy }}>
              <h2 className="text-base font-bold text-white tracking-wide">{blockModal.title}</h2>
            </div>
            <div className="px-6 py-5">
              <p className="text-sm text-gray-700 mb-3">This prospect cannot be deleted because it is linked to:</p>
              <ul className="text-sm text-gray-800 space-y-1 mb-3">
                {blockModal.items.map((item, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-sm text-gray-500">Please delete these first before deleting the prospect.</p>
            </div>
            <div className="px-6 pb-5 flex justify-end">
              <button
                onClick={() => setBlockModal({ show: false, title: '', items: [] })}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors"
                style={{ backgroundColor: BRAND_COLORS.navy }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {showNewPartnerModal && <NewPartnerModal onClose={() => { setShowNewPartnerModal(false); setDraftPartner(null); }} onSubmit={addPartner} onSaveDraft={(row, step) => saveWizardDraft('partner', row, step)} draft={draftPartner} sectors={sectors} agreementTypes={agreementTypes} currentUserRole={currentUser.role} />}
      {showNewProspectModal && <NewProspectModal onClose={() => { setShowNewProspectModal(false); setDraftProspect(null); }} onSubmit={submitProspect} onSaveDraft={(row, step) => saveWizardDraft('prospect', row, step)} draft={draftProspect} sectors={sectors} partners={partners} currentUserRole={currentUser.role} />}
      <NewWorkflowModal show={showNewWorkflowModal} onClose={() => { setShowNewWorkflowModal(false); setDraftWorkflow(null); }} onSubmit={addWorkflow} onSaveDraft={(row, step) => saveWizardDraft('workflow', row, step)} draft={draftWorkflow} partners={partners} />
      {showEditWorkflowModal && editingWorkflow && <EditWorkflowModal show={showEditWorkflowModal} onClose={() => { setShowEditWorkflowModal(false); setEditingWorkflow(null); }} workflow={editingWorkflow} updateWorkflow={updateWorkflow} partners={partners} />}
      {showNewUserModal && <NewUserModal onClose={() => setShowNewUserModal(false)} onSubmit={addUser} partners={partners} users={users} sectors={sectors} agreementTypes={agreementTypes} />}
      {showEditPartnerModal && editingPartner && <EditPartnerModal onClose={() => { setShowEditPartnerModal(false); setEditingPartner(null); }} onSubmit={updatePartner} partner={editingPartner} sectors={sectors} agreementTypes={agreementTypes} currentUserRole={currentUser.role} />}
      {showEditProspectModal && editingProspect && <EditProspectModal onClose={() => { setShowEditProspectModal(false); setEditingProspect(null); }} onSubmit={updateProspect} prospect={editingProspect} sectors={sectors} partners={partners} currentUserRole={currentUser.role} />}
      {showEditTeaserModal && editingTeaser && <EditTeaserModal onClose={() => { setShowEditTeaserModal(false); setEditingTeaser(null); }} onSubmit={updateTeaser} teaser={editingTeaser} partners={partners} prospects={prospects} sectors={sectors} currentUserRole={currentUser.role} />}
      {showEditUserModal && editingUser && <EditUserModal onClose={() => { setShowEditUserModal(false); setEditingUser(null); }} onSubmit={updateUser} user={editingUser} partners={partners} />}
      {appAlert && <AlertDialog title={appAlert.title} message={appAlert.message} onClose={() => setAppAlert(null)} />}
      {showPasswordResetModal && <PasswordResetModal onClose={() => setShowPasswordResetModal(false)} />}
      {/* EDEN styling for the driver.js walkthrough popover */}
      <style>{`
        /* ── Card ──────────────────────────────────────────────────────────── */
        /* NOTE: never set position or overflow here. driver.js relies on
           .driver-popover { position: fixed } to place the popover, and a
           .driver-popover.eve-tour rule outranks it (0,2,0 vs 0,1,0), which
           drops the popover out of the fixed layer, flows it to the end of the
           document and visibly breaks the page. overflow: hidden likewise
           clips the (absolutely positioned) arrow. The gold accent is therefore
           a border-top rather than an ::before bar. */
        .driver-popover.eve-tour {
          background: #FFFFFF; border: 1px solid #E5E7EB;
          border-top: 3px solid #D4AF37;   /* KPI-card accent-stripe language */
          border-radius: 16px;
          box-shadow: 0 12px 40px rgba(10,20,40,0.18);
          max-width: 360px; padding: 20px;
          animation: eveTourIn 180ms ease-out;
        }
        @keyframes eveTourIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }

        /* ── Title row + icon badge ────────────────────────────────────────── */
        .driver-popover.eve-tour .driver-popover-title {
          display: flex; align-items: center; gap: 10px;
          color: #0A1428; font-size: 16px; font-weight: 700; letter-spacing: -0.01em;
          line-height: 1.3; padding-right: 24px;
        }
        .driver-popover.eve-tour .eve-tour-badge {
          flex: 0 0 auto; width: 32px; height: 32px; border-radius: 999px;
          background: rgba(212,175,55,0.12); color: #D4AF37;
          display: inline-flex; align-items: center; justify-content: center;
        }
        .driver-popover.eve-tour .eve-tour-badge svg { width: 16px; height: 16px; }

        /* ── Description ───────────────────────────────────────────────────── */
        .driver-popover.eve-tour .driver-popover-description {
          color: #4B5563; font-size: 13.5px; line-height: 1.55; margin-top: 8px;
        }

        /* ── Hero steps (welcome + finish) ─────────────────────────────────── */
        .driver-popover.eve-tour.eve-tour-hero { max-width: 420px; padding: 28px 26px; text-align: center; }
        .driver-popover.eve-tour.eve-tour-hero .driver-popover-title { justify-content: center; padding-right: 0; }
        .driver-popover.eve-tour.eve-tour-hero .driver-popover-description { margin-top: 10px; }
        .driver-popover.eve-tour .eve-tour-logo { display: block; width: 56px; height: auto; margin: 0 auto 14px; }
        /* Block, not inline-block: the CTA is appended to the description HTML,
           so as an inline box it joined the text flow and the closing sentence
           wrapped around it ("...from the ? icon | up top. [button]"). */
        .driver-popover.eve-tour .eve-tour-cta {
          display: block; width: fit-content; margin: 16px auto 0; cursor: pointer;
          background: #D4AF37; color: #0A1428; border: none; border-radius: 999px;
          font-size: 13px; font-weight: 600; padding: 9px 20px;
          transition: background 150ms ease, transform 150ms ease, box-shadow 150ms ease;
        }
        .driver-popover.eve-tour .eve-tour-cta:hover {
          background: #B8941F; transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(212,175,55,0.35);
        }
        .driver-popover.eve-tour .eve-tour-cta:focus-visible { outline: 2px solid #D4AF37; outline-offset: 2px; }

        /* ── Footer: dots left, buttons right ──────────────────────────────── */
        .driver-popover.eve-tour .driver-popover-footer {
          display: flex; align-items: center; justify-content: space-between;
          margin-top: 18px; gap: 14px;
        }
        .driver-popover.eve-tour.eve-tour-hero .driver-popover-footer { justify-content: center; flex-direction: column; }
        /* display needs !important: driver.js writes an inline display style on
           this element, which would otherwise beat the stylesheet and keep the
           dots and the step count on one cramped line. */
        .driver-popover.eve-tour .driver-popover-progress-text {
          display: flex !important; flex-direction: column; align-items: flex-start;
          gap: 7px; flex: 0 0 auto; color: #9CA3AF; font-size: 11px;
        }
        .driver-popover.eve-tour.eve-tour-hero .driver-popover-progress-text { align-items: center; }
        .driver-popover.eve-tour .eve-tour-dots { display: inline-flex; align-items: center; gap: 5px; }
        .driver-popover.eve-tour .eve-tour-dot {
          width: 6px; height: 6px; border-radius: 999px; background: #E5E7EB;
          transition: all 150ms ease;
        }
        .driver-popover.eve-tour .eve-tour-dot.is-done { background: rgba(184,148,31,0.6); }
        .driver-popover.eve-tour .eve-tour-dot.is-active { width: 9px; height: 9px; background: #D4AF37; }
        .driver-popover.eve-tour .eve-tour-count { color: #9CA3AF; font-size: 10.5px; }

        /* ── Buttons ───────────────────────────────────────────────────────── */
        /* driver.js applies "all: unset" to these, which resets white-space to
           normal; combined with flex-grow:1 on the button container the labels
           were wrapping onto two lines inside the 360px card. */
        .driver-popover.eve-tour .driver-popover-navigation-btns {
          display: flex; align-items: center; gap: 8px; flex: 0 0 auto;
        }
        .driver-popover.eve-tour .driver-popover-navigation-btns button + button { margin-left: 0; }
        .driver-popover.eve-tour .driver-popover-footer button {
          white-space: nowrap; flex: 0 0 auto;
          border-radius: 999px; text-shadow: none; font-size: 13px; font-weight: 600;
          padding: 8px 18px; transition: background 150ms ease, transform 150ms ease, box-shadow 150ms ease;
        }
        .driver-popover.eve-tour .driver-popover-footer button:focus-visible { outline: 2px solid #D4AF37; outline-offset: 2px; }
        /* Next / Done: flat gold pill */
        .driver-popover.eve-tour .driver-popover-next-btn {
          background: #D4AF37 !important; color: #0A1428 !important; border: none !important;
        }
        .driver-popover.eve-tour .driver-popover-next-btn:hover {
          background: #B8941F !important; transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(212,175,55,0.35);
        }
        /* Back: ghost pill, so it stops competing with Next */
        .driver-popover.eve-tour .driver-popover-prev-btn {
          background: transparent !important; color: #0A1428 !important;
          border: 1px solid #E5E7EB !important;
        }
        .driver-popover.eve-tour .driver-popover-prev-btn:hover { background: rgba(10,20,40,0.05) !important; }
        /* Close × */
        .driver-popover.eve-tour .driver-popover-close-btn {
          width: 28px; height: 28px; line-height: 28px; color: #9CA3AF;
          transition: color 150ms ease;
        }
        .driver-popover.eve-tour .driver-popover-close-btn:hover { color: #0A1428; background: transparent; }

        /* ── Arrow: match the white card ───────────────────────────────────── */
        .driver-popover.eve-tour .driver-popover-arrow-side-left   { border-left-color: #FFFFFF; }
        .driver-popover.eve-tour .driver-popover-arrow-side-right  { border-right-color: #FFFFFF; }
        .driver-popover.eve-tour .driver-popover-arrow-side-top    { border-top-color: #FFFFFF; }
        .driver-popover.eve-tour .driver-popover-arrow-side-bottom { border-bottom-color: #FFFFFF; }

        @media (prefers-reduced-motion: reduce) {
          .driver-popover.eve-tour,
          .driver-popover.eve-tour .eve-tour-dot,
          .driver-popover.eve-tour .eve-tour-cta,
          .driver-popover.eve-tour .driver-popover-footer button { animation: none !important; transition: none !important; }
          .driver-popover.eve-tour .driver-popover-next-btn:hover,
          .driver-popover.eve-tour .eve-tour-cta:hover { transform: none; }
        }
      `}</style>

      {activeHotTopic && currentUser.role === 'Partner' && (
        <HotTopicModal
          topic={activeHotTopic}
          teasers={userTeasers}
          onClose={() => setActiveHotTopic(null)}
          onDismissForever={dismissHotTopicForever}
          onViewTeaser={() => { setActiveHotTopic(null); setActiveTabPersisted('teasers'); }}
        />
      )}

      {/* Notifications Panel */}
      {showNotificationsPanel && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-30" onClick={() => setShowNotificationsPanel(false)}>
          <div 
            className="absolute top-20 right-4 w-96 bg-white rounded-lg shadow-2xl border-2 overflow-hidden"
            style={{ borderColor: BRAND_COLORS.gold, maxHeight: '80vh' }} 
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div 
              className="p-4 border-b flex justify-between items-center"
              style={{ backgroundColor: BRAND_COLORS.navy }}
            >
              <div className="flex items-center space-x-2">
                <Bell className="w-5 h-5 text-white" />
                <h3 className="text-lg font-bold text-white">Notifications</h3>
                {unreadNotifications.length > 0 && (
                  <span 
                    className="px-2 py-0.5 text-xs font-bold rounded-full text-white"
                    style={{ backgroundColor: BRAND_COLORS.royalBlue }}
                  >
                    {unreadNotifications.length} new
                  </span>
                )}
              </div>
              <button
                onClick={() => setShowNotificationsPanel(false)}
                className="text-white hover:opacity-80 transition-opacity"
                title="Close (ESC)"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Notifications List */}
            <div className="overflow-y-auto" style={{ maxHeight: 'calc(80vh - 140px)' }}>
              {userNotifications.length === 0 ? (
                <div className="p-12 text-center">
                  <div 
                    className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: BRAND_COLORS.cream }}
                  >
                    <Bell className="w-8 h-8" style={{ color: BRAND_COLORS.gold }} />
                  </div>
                  <p className="text-gray-600 font-medium mb-1">No notifications yet</p>
                  <p className="text-sm text-gray-400">We'll notify you when something important happens</p>
                </div>
              ) : (
                <div className="divide-y" style={{ borderColor: BRAND_COLORS.cream }}>
                  {userNotifications.slice(0, 20).map((notif, index) => (
                    <div 
                      key={notif.id}
                      className="p-4 hover:bg-gray-50 transition-colors cursor-pointer relative"
                      style={{ 
                        backgroundColor: !notif.read ? BRAND_COLORS.cream : 'transparent'
                      }}
                      onClick={() => {
                        // Mark as read
                        const updated = notifications.map(n =>
                          n.id === notif.id ? { ...n, read: true } : n
                        );
                        setNotifications(updated);
                        (async () => {
                          try {
                            const { supabase } = await import('./supabaseClient');
                            await supabase.from('notifications').update({ read: true }).eq('id', notif.id);
                          } catch (e) { console.error('[markNotifRead]', e.message); }
                        })();
                        
                        // Navigate if there's a related prospect
                        if (notif.prospectId) {
                          setActiveTabPersisted('prospects');
                          setShowNotificationsPanel(false);
                        }
                      }}
                    >
                      {!notif.read && (
                        <div 
                          className="absolute left-2 top-1/2 transform -translate-y-1/2 w-2 h-2 rounded-full"
                          style={{ backgroundColor: BRAND_COLORS.royalBlue }}
                        />
                      )}
                      <div className="ml-4">
                        <p 
                          className="text-sm font-medium mb-1"
                          style={{ color: BRAND_COLORS.navy }}
                        >
                          {notif.message}
                        </p>
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-gray-500">
                            {formatDate(notif.createdAt)}
                          </p>
                          {notif.prospectId && (
                            <span 
                              className="text-xs px-2 py-0.5 rounded"
                              style={{ 
                                backgroundColor: `${BRAND_COLORS.gold}20`,
                                color: BRAND_COLORS.gold 
                              }}
                            >
                              View 
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Footer */}
            {userNotifications.length > 0 && (
              <div 
                className="p-3 border-t flex justify-between items-center"
                style={{ borderColor: BRAND_COLORS.cream, backgroundColor: '#F9FAFB' }}
              >
                <button
                  onClick={() => {
                    const updated = notifications.map(n => ({ ...n, read: true }));
                    setNotifications(updated);
                    (async () => {
                      try {
                        const { supabase } = await import('./supabaseClient');
                        const ids = userNotifications.map(n => n.id);
                        if (ids.length > 0) await supabase.from('notifications').update({ read: true }).in('id', ids);
                      } catch (e) { console.error('[markAllNotifRead]', e.message); }
                    })();
                  }}
                  className="text-xs font-medium hover:opacity-80 transition-opacity"
                  style={{ color: BRAND_COLORS.navy }}
                >
                  Mark all as read
                </button>
                <button
                  onClick={() => {
                    const ids = userNotifications.map(n => n.id);
                    setShowNotificationsPanel(false);
                    showConfirm('Clear all notifications?', () => {
                      // Persist as dismissed (not delete). The auto-generator keys
                      // off deterministic ids and skips anything already known, so a
                      // dismissed condition alert stays cleared instead of being
                      // regenerated. A worsening condition escalates to a new `type`
                      // (different id) and re-alerts.
                      setNotifications(prev => prev.map(n => ids.includes(n.id) ? { ...n, dismissed: true, read: true } : n));
                      (async () => {
                        try {
                          const { supabase } = await import('./supabaseClient');
                          if (ids.length > 0) await supabase.from('notifications').update({ dismissed: true, read: true }).in('id', ids);
                        } catch (e) { console.error('[clearNotifications]', e.message); }
                      })();
                    }, 'Clear');
                  }}
                  className="text-xs font-medium text-red-600 hover:text-red-700 transition-colors"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// COMPONENT: KPICard — canonical KPI / stat card used across all views
// ============================================================================
// Single source of truth for the flat-card / accent-stripe pattern. Extracted
// 2026-05-08 after the palette migration paid the duplication tax 7×.
//
// Props:
//   label    — required, the small uppercase title
//   value    — required, the primary number/string
//   icon     — optional lucide component; renders in the top-right tinted square
//   accent   — colour for the 4px left stripe + icon tint; defaults to gold
//   sub      — optional, string or React node; rendered below the value
//   size     — 'default' (28px value, 100px min-height) | 'compact' (22px value,
//              90px min-height) | 'tiny' (18px value, no min-height)
//   valueSize — optional explicit override for the value font-size
//   onClick  — when provided, makes the card clickable + adds a hover lift
const KPICard = ({ label, value, icon: Icon, accent, sub, size = 'default', valueSize, onClick, delta }) => {
  const stripe = accent || BRAND_COLORS.gold;
  const shape = {
    default: { padding: '16px 18px', minHeight: '100px', radius: '12px', valueFs: '28px', iconBox: 30, iconPx: 16 },
    compact: { padding: '14px 16px', minHeight: '90px',  radius: '12px', valueFs: '22px', iconBox: 30, iconPx: 16 },
    tiny:    { padding: '12px 14px', minHeight: 0,        radius: '10px', valueFs: '18px', iconBox: 0,  iconPx: 0  },
  }[size] || {};
  return (
    <div
      onClick={onClick}
      style={{
        background: BRAND_COLORS.bgSurface,
        border: `1px solid ${BRAND_COLORS.borderNavy12}`,
        borderLeft: `4px solid ${stripe}`,
        borderRadius: shape.radius,
        padding: shape.padding,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        minHeight: shape.minHeight || undefined,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'box-shadow 0.15s',
      }}
      onMouseEnter={onClick ? (e) => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(10,20,40,0.08)'; } : undefined}
      onMouseLeave={onClick ? (e) => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)'; } : undefined}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: '11px', fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase', color: BRAND_COLORS.textSecondary, margin: '0 0 8px' }}>{label}</p>
          <p style={{ fontSize: valueSize || shape.valueFs, fontWeight: 700, lineHeight: 1, color: BRAND_COLORS.navy, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums', margin: 0 }}>{value}</p>
          {delta !== undefined && delta !== 0 && (
            <p style={{ fontSize: '11px', fontWeight: 600, margin: '4px 0 0', color: delta > 0 ? BRAND_COLORS.statusSuccess : BRAND_COLORS.statusError }}>
              {delta > 0 ? `↑${delta}` : `↓${Math.abs(delta)}`} this month
            </p>
          )}
          {sub && (typeof sub === 'string'
            ? <p style={{ fontSize: '11px', color: BRAND_COLORS.textTertiary, margin: '4px 0 0' }}>{sub}</p>
            : <div style={{ marginTop: '4px' }}>{sub}</div>)}
        </div>
        {Icon && shape.iconBox > 0 && (
          <div style={{ width: shape.iconBox, height: shape.iconBox, borderRadius: '7px', backgroundColor: `${stripe}1A`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon style={{ width: shape.iconPx, height: shape.iconPx, color: stripe }} />
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// COMPONENT: Tab Button
// ============================================================================

const SidebarButton = ({ active, onClick, icon: Icon, label, badge, colors = BRAND_COLORS, collapsed = false, dataTour }) => (
  <button
    onClick={onClick}
    data-tour={dataTour}
    title={collapsed ? label : undefined}
    className="relative w-full flex items-center transition-all group"
    style={{
      padding: collapsed ? '10px 0' : '10px 16px',
      justifyContent: collapsed ? 'center' : 'flex-start',
      gap: collapsed ? 0 : 12,
      backgroundColor: active ? `${BRAND_COLORS.gold}22` : 'transparent',
      color: active ? BRAND_COLORS.gold : 'rgba(255,255,255,0.6)',
      borderLeft: collapsed ? 'none' : (active ? `3px solid ${BRAND_COLORS.gold}` : '3px solid transparent'),
      borderRadius: collapsed ? 0 : undefined,
    }}
    onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)'; }}
    onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
  >
    <Icon className="w-5 h-5 flex-shrink-0" style={{ color: active ? BRAND_COLORS.gold : 'rgba(255,255,255,0.55)' }} />
    {!collapsed && <span className="font-medium text-sm truncate" style={{ color: active ? BRAND_COLORS.gold : 'rgba(255,255,255,0.75)' }}>{label}</span>}
    {!collapsed && badge > 0 && (
      <span className="ml-auto text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold"
        style={{ backgroundColor: colors.notificationDot }}>
        {badge}
      </span>
    )}
    {collapsed && badge > 0 && (
      <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full"
        style={{ backgroundColor: colors.notificationDot }} />
    )}
  </button>
);

// ============================================================================
// VIEW: Dashboard
// ============================================================================
// ============================================================================
// PARTNER DASHBOARD - Personal View for External Partners
// ============================================================================
// This component shows partners only THEIR data:
// - Their prospects
// - Their deals
// - Their commissions
// - Their MCA details
// ============================================================================

// Small auto-rotating globe with a single gold pin at the partner's `location`.
// Reuses the same Globe stack as the admin GlobeVisualization but sized for the
// partner-dashboard hero. Falls back to Israel (EvE HQ) when location is empty.
const PartnerMiniGlobe = ({ location }) => {
  const globeRef = useRef(null);
  const [worldPolygons, setWorldPolygons] = useState([]);

  useEffect(() => {
    fetch('//unpkg.com/world-atlas@2/countries-110m.json')
      .then(r => r.json())
      .then(world => setWorldPolygons(feature(world, world.objects.countries).features))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const g = globeRef.current;
    if (!g) return;
    const t = setTimeout(() => {
      g.controls().autoRotate      = true;
      g.controls().autoRotateSpeed = 0.5;
      g.controls().enableZoom      = false;
      g.pointOfView({ lat: 20, lng: 15, altitude: 2.0 }, 0);
    }, 100);
    return () => clearTimeout(t);
  }, []);

  // Resolve partner location to a [lat, lng] pin. Match exact, then partial.
  let pin = null;
  if (location) {
    const trimmed = location.trim();
    if (GLOBE_COUNTRY_COORDS[trimmed]) {
      pin = { country: trimmed, lat: GLOBE_COUNTRY_COORDS[trimmed][0], lng: GLOBE_COUNTRY_COORDS[trimmed][1] };
    } else {
      const lower = trimmed.toLowerCase();
      const match = Object.keys(GLOBE_COUNTRY_COORDS).find(c => lower.includes(c.toLowerCase()));
      if (match) pin = { country: match, lat: GLOBE_COUNTRY_COORDS[match][0], lng: GLOBE_COUNTRY_COORDS[match][1] };
    }
  }
  if (!pin) pin = { country: 'Israel', lat: 31.5, lng: 34.75 };

  return (
    <div style={{ flexShrink: 0, width: 180, height: 180 }}>
      <Globe
        ref={globeRef}
        width={180}
        height={180}
        globeImageUrl={null}
        globeMaterial={GLOBE_SPHERE_MATERIAL}
        backgroundColor="rgba(0,0,0,0)"
        showAtmosphere={true}
        atmosphereColor="#3533CD"
        atmosphereAltitude={0.18}
        polygonsData={worldPolygons}
        polygonCapColor={() => 'rgba(26,25,160,0.06)'}
        polygonSideColor={() => 'transparent'}
        polygonStrokeColor={() => 'rgba(53,51,205,0.45)'}
        polygonAltitude={0.001}
        pointsData={[pin]}
        pointLat="lat"
        pointLng="lng"
        pointColor={() => BRAND_COLORS.gold}
        pointAltitude={0.05}
        pointRadius={1.2}
        pointLabel={d => d.country}
        enablePointerInteraction={false}
      />
    </div>
  );
};

const PartnerDashboard = ({ currentUser, prospects, transactions, partners, revenueDeals = [], theme, onAddProspect }) => {
  const { isMobile } = useIsMobile();
  const pid = currentUser?.partnerId || currentUser?.id;
  const partnerByEmail = partners.find(p => p.email?.toLowerCase() === currentUser?.email?.toLowerCase());
  // currentUser.partnerId is the explicit FK — it wins over email-based lookup.
  // Email lookup is only a fallback when partnerId is not set (e.g. during initial onboarding).
  const effectivePartnerId = currentUser?.partnerId || partnerByEmail?.id || currentUser?.id;

  // Filter prospects belonging to this partner
  const myProspects = prospects.filter(p =>
    p.introducingPartnerId === effectivePartnerId ||
    p.introducingPartnerId === pid ||
    p.introducingPartnerId === currentUser?.id
  );

  // Filter revenue deals where this partner is involved (client side or investor side or strategic partner)
  const myRevenueDeals = revenueDeals.filter(deal => {
    const linkedProspect = deal.prospectId ? prospects.find(p => p.id === deal.prospectId) : null;
    const isClientPartner = linkedProspect && (
      linkedProspect.introducingPartnerId === effectivePartnerId ||
      linkedProspect.introducingPartnerId === pid
    );
    const investorProspect = deal.investorProspectId ? prospects.find(p => p.id === deal.investorProspectId) : null;
    const isInvestorPartner = investorProspect?.introducingPartnerId === effectivePartnerId || investorProspect?.introducingPartnerId === pid;
    const isStrategicPartner = deal.strategicPartners?.some(sp =>
      sp.partnerId === effectivePartnerId || sp.partnerId === pid
    );
    return isClientPartner || isInvestorPartner || isStrategicPartner;
  });

  // Get partner's MCA details
  const myPartnerProfile = partners.find(p => p.id === effectivePartnerId || p.id === pid);

  // Calculate my commission from revenue deals (strategic partner share)
  const myCommissionFromDeals = myRevenueDeals.reduce((sum, deal) => {
    const dt = computeDealTotals(deal);
    const myShare = dt.partnerPayouts
      .filter(sp => sp.partnerId === effectivePartnerId || sp.partnerId === pid)
      .reduce((s, sp) => s + (sp.amount || 0), 0);
    return sum + myShare;
  }, 0);

  const approvedProspects = myProspects.filter(p => p.approvalStatus === 'Approved').length;
  const waitingProspects = myProspects.filter(p =>
    p.approvalStatus === 'Waiting for Approval' || p.approvalStatus === 'Pending'
  ).length;
  const rejectedProspects = myProspects.filter(p => p.approvalStatus === 'Rejected').length;

  // Delta calculations — compare this month vs last month
  const now = new Date();
  const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const prospectsThisMonth = myProspects.filter(p => p.createdAt && new Date(p.createdAt) >= thisMonthStart).length;
  const prospectsLastMonth = myProspects.filter(p => { const d = p.createdAt && new Date(p.createdAt); return d && d >= lastMonthStart && d < thisMonthStart; }).length;
  const prospectDelta = prospectsThisMonth - prospectsLastMonth;
  const dealsThisMonth = myRevenueDeals.filter(d => d.createdAt && new Date(d.createdAt) >= thisMonthStart).length;
  const dealsLastMonth = myRevenueDeals.filter(d => { const dt = d.createdAt && new Date(d.createdAt); return dt && dt >= lastMonthStart && dt < thisMonthStart; }).length;
  const dealDelta = dealsThisMonth - dealsLastMonth;

  // On mobile, abbreviate large values ($2.0B / $50M / $120K) so KPI cards
  // don't overflow on narrow screens. Desktop keeps the full grouped number.
  const formatCurrency = (amount) => {
    const n = Number(amount) || 0;
    if (isMobile) {
      const opts = { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: n >= 1e9 || n < 1e6 ? 1 : 0 };
      return new Intl.NumberFormat('en-US', opts).format(n);
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(n);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const d = new Date(dateString);
    if (isNaN(d)) return 'N/A';
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const statusStyle = (status) => {
    if (status === 'Approved') return { color: BRAND_COLORS.statusSuccess };
    if (status === 'Waiting for Approval' || status === 'Pending') return { color: BRAND_COLORS.statusWarning };
    if (status === 'Rejected') return { color: BRAND_COLORS.statusError };
    return { color: BRAND_COLORS.textSecondary };
  };
  
  return (
    <div className="p-6 space-y-6">
      {/* Hero — Welcome + context chips + mini globe, all framed in one card */}
      <div style={{
        background:   BRAND_COLORS.bgSurface,
        border:       `1px solid ${BRAND_COLORS.borderNavy12}`,
        borderLeft:   `4px solid ${BRAND_COLORS.gold}`,
        borderRadius: '16px',
        padding:      '28px 32px',
        boxShadow:    '0 1px 3px rgba(0,0,0,0.06)',
        display:      'flex',
        alignItems:   'center',
        justifyContent: 'space-between',
        gap:          '24px',
        flexWrap:     'wrap',
      }}>
        <div style={{ flex: '1 1 320px', minWidth: 0 }}>
          <h1 className="text-3xl font-bold mb-1" style={{ color: BRAND_COLORS.navy, lineHeight: 1.15 }}>
            Welcome, {currentUser.name}
          </h1>
          <p style={{ fontSize: '15px', color: BRAND_COLORS.textSecondary, marginBottom: '14px' }}>
            Your Personal Partner Portal
          </p>
          {/* Context chips — only render the ones with data */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {myPartnerProfile?.location && (
              <span style={{ padding: '3px 9px', borderRadius: '999px', fontSize: '11px', fontWeight: 500, background: `${BRAND_COLORS.navy}0D`, color: BRAND_COLORS.textSecondary, border: `1px solid ${BRAND_COLORS.borderNavy12}` }}>
                📍 {myPartnerProfile.location}
              </span>
            )}
            {(myPartnerProfile?.agreementType || myPartnerProfile?.mcaType) && (
              <span style={{ padding: '3px 9px', borderRadius: '999px', fontSize: '11px', fontWeight: 500, background: `${BRAND_COLORS.navy}0D`, color: BRAND_COLORS.textSecondary, border: `1px solid ${BRAND_COLORS.borderNavy12}` }}>
                {myPartnerProfile.agreementType || myPartnerProfile.mcaType}
              </span>
            )}
            {(() => {
              const endDate = myPartnerProfile?.endDate || myPartnerProfile?.mcaEndDate;
              if (!endDate) return null;
              const daysLeft = Math.ceil((new Date(endDate) - new Date()) / 86400000);
              if (daysLeft < 0) return null;
              const tone = daysLeft <= 30 ? BRAND_COLORS.statusError : daysLeft <= 90 ? BRAND_COLORS.statusWarning : BRAND_COLORS.statusSuccess;
              return (
                <span style={{ padding: '3px 9px', borderRadius: '999px', fontSize: '11px', fontWeight: 600, background: `${tone}18`, color: tone, border: `1px solid ${tone}40` }}>
                  ⏰ {daysLeft}d left
                </span>
              );
            })()}
            {myPartnerProfile?.ndaSigned && (
              <span style={{ padding: '3px 9px', borderRadius: '999px', fontSize: '11px', fontWeight: 600, background: `${BRAND_COLORS.statusSuccess}18`, color: BRAND_COLORS.statusSuccess, border: `1px solid ${BRAND_COLORS.statusSuccess}40` }}>
                ✓ {myPartnerProfile.ndaType || 'NDA'}
              </span>
            )}
          </div>
        </div>
        <PartnerMiniGlobe location={myPartnerProfile?.location} />
      </div>

      {/* Stats Grid — flat canonical KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          {
            label: 'Prospects Submitted', value: myProspects.length, icon: Users, accent: BRAND_COLORS.gold,
            delta: prospectDelta,
            sub: (
              <div style={{ fontSize: '11px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                <span style={{ color: BRAND_COLORS.statusSuccess, fontWeight: 600 }}>✓ {approvedProspects} Approved</span>
                <span style={{ color: BRAND_COLORS.statusWarning, fontWeight: 600 }}>⏳ {waitingProspects} Waiting</span>
                {rejectedProspects > 0 && <span style={{ color: BRAND_COLORS.statusError, fontWeight: 600 }}>✗ {rejectedProspects} Rejected</span>}
              </div>
            ),
          },
          { label: 'Revenue Deals',     value: myRevenueDeals.length,                 delta: dealDelta, icon: TrendingUp, accent: BRAND_COLORS.statusSuccess, sub: 'Deals you are involved in' },
          { label: 'Total Commissions', value: formatCurrency(myCommissionFromDeals),                   icon: DollarSign, accent: BRAND_COLORS.gold },
          { label: 'Agreement Type',    value: myPartnerProfile?.agreementType || myPartnerProfile?.mcaType || '—', icon: FileText, accent: BRAND_COLORS.statusInfo, valueSize: '18px',
            sub: myPartnerProfile?.startDate || myPartnerProfile?.mcaStartDate ? `Since ${formatDate(myPartnerProfile.startDate || myPartnerProfile.mcaStartDate || myPartnerProfile.mcaDate)}` : undefined },
        ].map(c => <KPICard key={c.label} {...c} />)}
      </div>

      {/* Contract + Recent Activity — two-column equal row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Contract & Agreement (left) */}
        {myPartnerProfile && (() => {
          const endDate = myPartnerProfile.endDate || myPartnerProfile.mcaEndDate;
          const startDate = myPartnerProfile.startDate || myPartnerProfile.mcaStartDate || myPartnerProfile.mcaDate;
          const daysLeft = endDate ? Math.ceil((new Date(endDate) - new Date()) / 86400000) : null;
          const isExpiringSoon = daysLeft !== null && daysLeft <= 90;
          const isExpired = daysLeft !== null && daysLeft < 0;
          const ndaSigned = myPartnerProfile.ndaSigned;
          const ndaDate = myPartnerProfile.ndaSignedDate;
          const accentColor = isExpired ? BRAND_COLORS.statusError : isExpiringSoon ? BRAND_COLORS.statusWarning : BRAND_COLORS.statusSuccess;
          return (
            <div style={{
              background: BRAND_COLORS.bgSurface,
              border: `1px solid ${BRAND_COLORS.borderNavy12}`,
              borderLeft: `4px solid ${accentColor}`,
              borderRadius: '12px',
              padding: '20px 24px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: BRAND_COLORS.navy, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '26px', height: '26px', borderRadius: '7px', backgroundColor: `${accentColor}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <FileText style={{ width: '13px', height: '13px', color: accentColor }} />
                  </div>
                  Contract & Agreement
                </h3>
                {isExpiringSoon && !isExpired && (
                  <span style={{ fontSize: '11px', fontWeight: 700, color: BRAND_COLORS.statusWarning, letterSpacing: '0.04em', textTransform: 'uppercase' }}>⚠ Expiring Soon</span>
                )}
                {isExpired && (
                  <span style={{ fontSize: '11px', fontWeight: 700, color: BRAND_COLORS.statusError, letterSpacing: '0.04em', textTransform: 'uppercase' }}>✗ Expired</span>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '16px' }}>
                <div>
                  <p style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: BRAND_COLORS.textTertiary, margin: '0 0 4px' }}>Agreement</p>
                  <p style={{ fontSize: '14px', fontWeight: 600, color: BRAND_COLORS.navy, margin: 0 }}>{myPartnerProfile.agreementType || myPartnerProfile.mcaType || '—'}</p>
                  {startDate && <p style={{ fontSize: '11px', color: BRAND_COLORS.textTertiary, margin: '2px 0 0' }}>Since {formatDate(startDate)}</p>}
                </div>
                <div>
                  <p style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: BRAND_COLORS.textTertiary, margin: '0 0 4px' }}>Expires</p>
                  <p style={{ fontSize: '14px', fontWeight: 600, color: isExpired ? BRAND_COLORS.statusError : isExpiringSoon ? BRAND_COLORS.statusWarning : BRAND_COLORS.navy, margin: 0 }}>
                    {endDate ? formatDate(endDate) : '—'}
                  </p>
                  {daysLeft !== null && !isExpired && (
                    <p style={{ fontSize: '11px', fontWeight: 600, color: isExpiringSoon ? BRAND_COLORS.statusWarning : BRAND_COLORS.statusSuccess, margin: '2px 0 0' }}>{daysLeft}d left</p>
                  )}
                  {isExpired && <p style={{ fontSize: '11px', fontWeight: 600, color: BRAND_COLORS.statusError, margin: '2px 0 0' }}>{Math.abs(daysLeft)}d ago</p>}
                </div>
                <div>
                  <p style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: BRAND_COLORS.textTertiary, margin: '0 0 4px' }}>NDA / MNDA</p>
                  <p style={{ fontSize: '14px', fontWeight: 600, color: ndaSigned ? BRAND_COLORS.statusSuccess : BRAND_COLORS.statusWarning, margin: 0 }}>
                    {ndaSigned ? `✓ ${myPartnerProfile.ndaType || 'Signed'}` : '⏳ Not Yet Signed'}
                  </p>
                  {ndaSigned && ndaDate && <p style={{ fontSize: '11px', color: BRAND_COLORS.textTertiary, margin: '2px 0 0' }}>Signed {formatDate(ndaDate)}</p>}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Recent Activity (right) */}
        {(() => {
          // Build unified activity feed from prospects + deals
          const events = [];
          myProspects.forEach(p => {
            if (p.createdAt) events.push({
              ts: p.createdAt, kind: 'submitted',
              label: `Submitted "${p.companyName || p.name}"`,
              color: BRAND_COLORS.navy,
            });
            if (p.approvalStatus === 'Approved' && p.approvalDate) events.push({
              ts: p.approvalDate, kind: 'approved',
              label: `Approved "${p.companyName || p.name}"`,
              color: BRAND_COLORS.statusSuccess,
            });
            if (p.approvalStatus === 'Rejected' && p.approvalDate) events.push({
              ts: p.approvalDate, kind: 'rejected',
              label: `Rejected "${p.companyName || p.name}"`,
              color: BRAND_COLORS.statusError,
            });
            if (p.introOccurred && p.introDate) events.push({
              ts: p.introDate, kind: 'intro',
              label: `Intro logged for "${p.companyName || p.name}"`,
              color: BRAND_COLORS.statusInfo,
            });
          });
          myRevenueDeals.forEach(d => {
            if (d.createdAt) events.push({
              ts: d.createdAt, kind: 'deal',
              label: `New deal "${d.dealName}"`,
              color: BRAND_COLORS.gold,
            });
            (d.payments || []).forEach(pmt => {
              if (pmt.date) events.push({
                ts: pmt.date, kind: 'payment',
                label: `Payment on "${d.dealName}"`,
                color: BRAND_COLORS.statusSuccess,
              });
            });
          });
          events.sort((a, b) => new Date(b.ts) - new Date(a.ts));
          const recent = events.slice(0, 6);
          return (
            <div style={{
              background: BRAND_COLORS.bgSurface,
              border: `1px solid ${BRAND_COLORS.borderNavy12}`,
              borderRadius: '12px',
              padding: '20px 24px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: BRAND_COLORS.navy, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '26px', height: '26px', borderRadius: '7px', backgroundColor: `${BRAND_COLORS.gold}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Activity style={{ width: '13px', height: '13px', color: BRAND_COLORS.gold }} />
                  </div>
                  Recent Activity
                </h3>
                {recent.length > 0 && (
                  <span style={{ fontSize: '11px', fontWeight: 600, color: BRAND_COLORS.textTertiary, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                    Last {recent.length}
                  </span>
                )}
              </div>
              {recent.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px 0' }}>
                  <Activity className="mx-auto mb-2" style={{ width: '32px', height: '32px', color: BRAND_COLORS.textTertiary, opacity: 0.5 }} />
                  <p style={{ fontSize: '13px', color: BRAND_COLORS.textSecondary, margin: 0 }}>No activity yet</p>
                  <p style={{ fontSize: '11px', color: BRAND_COLORS.textTertiary, margin: '4px 0 0' }}>Submit a prospect to start your timeline</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {recent.map((e, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                      <span style={{
                        width: '8px', height: '8px', borderRadius: '50%',
                        backgroundColor: e.color, marginTop: '5px', flexShrink: 0,
                        boxShadow: `0 0 0 2px ${e.color}25`,
                      }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: '13px', color: BRAND_COLORS.textPrimary, margin: 0, lineHeight: 1.35 }}>{e.label}</p>
                        <p style={{ fontSize: '11px', color: BRAND_COLORS.textTertiary, margin: '2px 0 0' }}>{formatDate(e.ts)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* My Prospects */}
        <div className="p-6 rounded-lg" style={{ backgroundColor: BRAND_COLORS.surface, border: `1px solid ${BRAND_COLORS.border}` }}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold" style={{ color: BRAND_COLORS.navy }}>My Prospects</h3>
            <button
              onClick={() => onAddProspect && onAddProspect()}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium"
              style={{ backgroundColor: BRAND_COLORS.navy, color: 'white' }}
            >
              <Plus className="w-4 h-4" /> Submit
            </button>
          </div>
          {myProspects.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 mx-auto mb-3" style={{ color: BRAND_COLORS.textTertiary }} />
              <p style={{ color: BRAND_COLORS.textSecondary }}>No prospects yet</p>
              <p className="text-sm mt-1" style={{ color: BRAND_COLORS.textTertiary }}>
                Submit your first prospect to get started
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {myProspects.slice(0, 5).map(prospect => {
                const s = statusStyle(prospect.approvalStatus);
                return (
                  <div key={prospect.id} className="p-3 rounded-lg" style={{ backgroundColor: BRAND_COLORS.background }}>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="font-semibold text-sm" style={{ color: BRAND_COLORS.textPrimary }}>
                          {prospect.companyName || prospect.name}
                        </p>
                        <p className="text-xs mt-0.5" style={{ color: BRAND_COLORS.textTertiary }}>
                          {prospect.prospectType} • {formatDate(prospect.createdAt)}
                        </p>
                      </div>
                      <span className="text-xs font-semibold ml-2" style={{ color: s.color }}>
                        {prospect.approvalStatus === 'Waiting for Approval' ? 'Waiting' : prospect.approvalStatus}
                      </span>
                    </div>
                    {prospect.approvalStatus === 'Approved' && prospect.introDeadline && (
                      <p className="text-xs mt-1" style={{ color: '#F59E0B' }}>
                        ⏰ Intro deadline: {formatDate(prospect.introDeadline)}
                      </p>
                    )}
                  </div>
                );
              })}
              {myProspects.length > 5 && (
                <p className="text-xs text-center pt-1" style={{ color: BRAND_COLORS.textTertiary }}>
                  +{myProspects.length - 5} more — go to Prospects tab
                </p>
              )}
            </div>
          )}
        </div>

        {/* My Revenue Deals */}
        <div className="p-6 rounded-lg" style={{ backgroundColor: BRAND_COLORS.surface, border: `1px solid ${BRAND_COLORS.border}` }}>
          <h3 className="text-lg font-bold mb-4" style={{ color: BRAND_COLORS.navy }}>My Revenue Deals</h3>
          {myRevenueDeals.length === 0 ? (
            <div className="text-center py-8">
              <DollarSign className="w-12 h-12 mx-auto mb-3" style={{ color: BRAND_COLORS.textTertiary }} />
              <p style={{ color: BRAND_COLORS.textSecondary }}>No deals yet</p>
              <p className="text-sm mt-1" style={{ color: BRAND_COLORS.textTertiary }}>
                Deals will appear here once your prospects convert
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {myRevenueDeals.slice(0, 5).map(deal => {
                // Calculate my share in this deal
                const myShare = computeDealTotals(deal).partnerPayouts
                  .filter(sp => sp.partnerId === effectivePartnerId || sp.partnerId === pid)
                  .reduce((s, sp) => s + (sp.amount || 0), 0);
                const paymentStatus = deal.paymentStatus || 'Pending';
                return (
                  <div key={deal.id} className="p-3 rounded-lg" style={{ backgroundColor: BRAND_COLORS.background }}>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="font-semibold text-sm" style={{ color: BRAND_COLORS.textPrimary }}>
                          {deal.dealName}
                        </p>
                        <p className="text-xs mt-0.5" style={{ color: BRAND_COLORS.textTertiary }}>
                          {formatDate(deal.createdAt)}
                          {deal.serviceType && ` • ${deal.serviceType}`}
                        </p>
                      </div>
                      <div className="text-right ml-2">
                        {myShare > 0 && (
                          <p className="font-bold text-sm" style={{ color: BRAND_COLORS.gold }}>
                            {formatCurrency(myShare)}
                          </p>
                        )}
                        <span className="text-xs font-semibold"
                          style={{ color: paymentStatus === 'Paid' ? BRAND_COLORS.statusSuccess : BRAND_COLORS.statusWarning }}>
                          {paymentStatus}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
              {myRevenueDeals.length > 5 && (
                <p className="text-xs text-center pt-1" style={{ color: BRAND_COLORS.textTertiary }}>
                  +{myRevenueDeals.length - 5} more — go to My Revenue tab
                </p>
              )}
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

// ─── COMPONENT: GlobeVisualization ──────────────────────────────────────────
// ─── COMPONENT: CountryAutocomplete ─────────────────────────────────────────
// Used in NewPartnerModal + EditPartnerModal Step 2 for the location field.
const CountryAutocomplete = ({ value = '', onChange, label }) => {
  const [open, setOpen] = useState(false);

  const matches = value.length >= 1
    ? Object.keys(GLOBE_COUNTRY_COORDS)
        .filter(c => c.toLowerCase().includes(value.toLowerCase()))
        .sort((a, b) => {
          const v = value.toLowerCase();
          return (b.toLowerCase().startsWith(v) ? 1 : 0) - (a.toLowerCase().startsWith(v) ? 1 : 0)
              || a.localeCompare(b);
        })
        .slice(0, 8)
    : [];

  return (
    <div style={{ position: 'relative' }}>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        value={value}
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Type to search countries…"
        style={{
          width: '100%', padding: '8px 12px', borderRadius: '6px',
          border: `1px solid ${BRAND_COLORS.borderDefault}`,
          fontSize: '14px', outline: 'none', boxSizing: 'border-box',
        }}
        onFocus={e  => { e.target.style.borderColor = BRAND_COLORS.gold;          setOpen(true); }}
        onBlur={e   => { e.target.style.borderColor = BRAND_COLORS.borderDefault; setTimeout(() => setOpen(false), 150); }}
      />
      {open && matches.length > 0 && (
        <ul style={{
          position: 'absolute', top: 'calc(100% + 2px)', left: 0, right: 0, zIndex: 200,
          background: 'white', border: `1px solid ${BRAND_COLORS.borderDefault}`,
          borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          padding: '4px 0', maxHeight: '200px', overflowY: 'auto',
          listStyle: 'none', margin: 0,
        }}>
          {matches.map(country => (
            <li key={country}
              onMouseDown={() => { onChange(country); setOpen(false); }}
              style={{ padding: '7px 12px', cursor: 'pointer', fontSize: '13px', color: BRAND_COLORS.textPrimary }}
              onMouseEnter={e => e.currentTarget.style.background = BRAND_COLORS.bgApp}
              onMouseLeave={e => e.currentTarget.style.background = ''}
            >
              {country}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// Created once at module scope — not inside the component — so Three.js
// doesn't allocate a new material object on every render.
const GLOBE_SPHERE_MATERIAL = new THREE.MeshPhongMaterial({ color: '#FFFFFF', shininess: 8 });

const GLOBE_COUNTRY_COORDS = {
  'Israel':         [ 31.5,  34.75], 'United States':  [ 38,   -97  ],
  'United Kingdom': [ 55,    -3   ], 'Germany':        [ 51,    10  ],
  'France':         [ 46,     2   ], 'Switzerland':    [ 47,     8  ],
  'Netherlands':    [ 52,     5   ], 'Spain':          [ 40,    -4  ],
  'Italy':          [ 42,    12   ], 'Poland':         [ 52,    20  ],
  'Russia':         [ 60,    60   ], 'China':          [ 35,   105  ],
  'India':          [ 20,    77   ], 'Japan':          [ 36,   138  ],
  'Australia':      [-25,   134   ], 'Brazil':         [-10,   -55  ],
  'Canada':         [ 57,   -95   ], 'South Africa':   [-30,    26  ],
  'Nigeria':        [ 10,     8   ], 'Kenya':          [  0,    38  ],
  'Mexico':         [ 24,  -102   ], 'Argentina':      [-34,   -64  ],
  'UAE':            [ 24,    54   ], 'Singapore':      [  1.3, 104  ],
  'Hong Kong':      [ 22,   114   ], 'South Korea':    [ 37,   128  ],
  'Turkey':         [ 39,    35   ], 'Egypt':          [ 26,    30  ],
  'Morocco':        [ 32,    -5   ], 'Sweden':         [ 60,    18  ],
  'Denmark':        [ 56,    10   ], 'Austria':        [ 47,    14  ],
  'Belgium':        [ 51,     4   ], 'Portugal':       [ 39,    -8  ],
  'Greece':         [ 39,    22   ], 'Czech Republic': [ 50,    15  ],
  'Romania':        [ 46,    25   ], 'Hungary':        [ 47,    19  ],
  'Ukraine':        [ 49,    32   ], 'Thailand':       [ 15,   101  ],
  'Malaysia':       [  3,   110   ], 'Philippines':    [ 13,   122  ],
  'Saudi Arabia':   [ 24,    45   ], 'Jordan':         [ 31,    36  ],
  'Lebanon':        [ 33,    35   ], 'Cyprus':         [ 35,    33  ],
  'Panama':         [  9,   -80   ], 'Colombia':       [  4,   -74  ],
  'Chile':          [-30,   -71   ], 'Peru':           [ -9,   -75  ],
};

const GlobeVisualization = ({ partners = [], navigate, showActions = false }) => {
  const { isMobile } = useIsMobile();
  const globeRef    = useRef(null);
  const [worldPolygons, setWorldPolygons] = useState([]);
  const activeCount = partners.filter(p => p.status === 'Active').length;
  const totalCount  = partners.length;
  const globeSize   = isMobile ? 180 : 240;

  // Fetch world TopoJSON once and convert to GeoJSON polygon features
  useEffect(() => {
    fetch('//unpkg.com/world-atlas@2/countries-110m.json')
      .then(r => r.json())
      .then(world => setWorldPolygons(feature(world, world.objects.countries).features))
      .catch(() => {}); // silent fail — globe still renders without polygons
  }, []);

  // Auto-rotate after mount
  useEffect(() => {
    const g = globeRef.current;
    if (!g) return;
    const t = setTimeout(() => {
      g.controls().autoRotate      = true;
      g.controls().autoRotateSpeed = 0.4;
      g.controls().enableZoom      = false;
      g.pointOfView({ lat: 20, lng: 15, altitude: 1.9 }, 0);
    }, 100);
    return () => clearTimeout(t);
  }, []);

  // Use partner.location (home country) for globe pins.
  // Tries exact match first, then partial match (e.g. "Berlin, Germany" → "Germany").
  const countryMap = {};
  partners.forEach(p => {
    if (!p.location) return;
    const loc = p.location.trim();
    if (GLOBE_COUNTRY_COORDS[loc]) {
      countryMap[loc] = (countryMap[loc] || 0) + 1;
    } else {
      const lower = loc.toLowerCase();
      const match = Object.keys(GLOBE_COUNTRY_COORDS).find(c => lower.includes(c.toLowerCase()));
      if (match) countryMap[match] = (countryMap[match] || 0) + 1;
    }
  });

  let pointsData = Object.entries(countryMap).map(([country, count]) => ({
    country, count,
    lat: GLOBE_COUNTRY_COORDS[country][0],
    lng: GLOBE_COUNTRY_COORDS[country][1],
  }));
  if (pointsData.length === 0 && totalCount > 0)
    pointsData = [{ country: 'Israel', count: totalCount, lat: 31.5, lng: 34.75 }];

  const uniqueCount = Object.keys(countryMap).length || (totalCount > 0 ? 1 : 0);

  return (
    <div style={{
      background:    BRAND_COLORS.bgSurface,
      border:        `1px solid ${BRAND_COLORS.borderNavy12}`,
      borderRadius:  '16px',
      padding:       isMobile ? '16px' : '20px 28px',
      display:       'flex',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems:    isMobile ? 'stretch' : 'center',
      gap:           isMobile ? '16px' : '28px',
      boxShadow:     '0 1px 4px rgba(10,20,80,0.05)',
    }}>

      {/* ── 3D Globe — transparent sphere + continent outlines + pins ── */}
      <div style={{ flexShrink: 0, width: globeSize, height: globeSize, alignSelf: isMobile ? 'center' : 'auto' }}>
        <Globe
          ref={globeRef}
          width={globeSize}
          height={globeSize}
          // White sphere — outlines come from polygonsData
          globeImageUrl={null}
          globeMaterial={GLOBE_SPHERE_MATERIAL}
          backgroundColor="rgba(0,0,0,0)"
          showAtmosphere={true}
          atmosphereColor="#3533CD"
          atmosphereAltitude={0.18}
          // Continent outlines
          polygonsData={worldPolygons}
          polygonCapColor={() => 'rgba(26,25,160,0.06)'}
          polygonSideColor={() => 'transparent'}
          polygonStrokeColor={() => 'rgba(53,51,205,0.45)'}
          polygonAltitude={0.001}
          // Partner pins
          pointsData={pointsData}
          pointLat="lat"
          pointLng="lng"
          pointColor={() => BRAND_COLORS.gold}
          pointAltitude={0.05}
          pointRadius={d => 0.6 + Math.min(d.count, 5) * 0.15}
          pointLabel={d => `${d.country}: ${d.count} partner${d.count > 1 ? 's' : ''}`}
          enablePointerInteraction={false}
        />
      </div>

      {/* ── Right panel ── */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Active chip */}
        <div style={{
          display:      'inline-flex',
          alignItems:   'center',
          gap:          '7px',
          padding:      '4px 12px',
          borderRadius: '999px',
          background:   'rgba(53,51,205,0.08)',
          border:       '1px solid rgba(53,51,205,0.28)',
          marginBottom: '14px',
        }}>
          <span style={{
            width: '7px', height: '7px', borderRadius: '50%',
            background: '#22C55E',
            boxShadow:  '0 0 0 2px rgba(34,197,94,0.25)',
            display:    'inline-block',
          }} />
          <span style={{ fontSize: '10.5px', fontWeight: 700, color: '#3533CD', letterSpacing: '0.07em' }}>
            {activeCount} ACTIVE PARTNER{activeCount !== 1 ? 'S' : ''}
          </span>
        </div>

        <p style={{ fontSize: '26px', fontWeight: 700, color: BRAND_COLORS.navy, lineHeight: 1.1, marginBottom: '4px' }}>
          {totalCount} Partner{totalCount !== 1 ? 's' : ''}
        </p>
        <p style={{ fontSize: '12.5px', color: BRAND_COLORS.textSecondary, marginBottom: '18px' }}>
          across {uniqueCount} {uniqueCount === 1 ? 'country' : 'countries'}
        </p>

        {pointsData.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
            {pointsData.slice(0, 7).map(({ country, count }) => (
              <span key={country} style={{
                padding:      '3px 9px',
                borderRadius: '999px',
                fontSize:     '11px',
                fontWeight:   500,
                background:   `${BRAND_COLORS.navy}0D`,
                color:        BRAND_COLORS.textSecondary,
                border:       `1px solid ${BRAND_COLORS.borderNavy12}`,
                whiteSpace:   'nowrap',
              }}>
                {country}{count > 1 ? ` ×${count}` : ''}
              </span>
            ))}
            {pointsData.length > 7 && (
              <span style={{ padding: '3px 9px', fontSize: '11px', color: BRAND_COLORS.textTertiary }}>
                +{pointsData.length - 7} more
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── Quick Actions column ── */}
      {showActions && navigate && (
        <div style={{
          flexShrink: 0,
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : '1fr 1fr',
          gap: '8px',
          alignSelf: isMobile ? 'stretch' : 'center',
          width: isMobile ? '100%' : 'auto',
        }}>
          {[
            { label: 'Revenue',        icon: DollarSign, tab: 'revenue',   color: BRAND_COLORS.statusSuccess },
            { label: 'New Partner',    icon: UserPlus,   tab: 'partners',  color: BRAND_COLORS.navy },
            { label: 'Upload Teaser',  icon: Upload,     tab: 'teasers',   color: BRAND_COLORS.gold },
            { label: 'New Prospect',   icon: FilePlus,   tab: 'prospects', color: BRAND_COLORS.statusInfo },
          ].map(({ label, icon: Icon, tab, color }) => (
            <button key={tab} onClick={() => navigate(tab)} style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '9px 13px', borderRadius: '9px',
              border: `1px solid ${color}28`, background: `${color}0A`,
              cursor: 'pointer', fontSize: '12px', fontWeight: 600, color,
              whiteSpace: 'nowrap', transition: 'background 0.15s, border-color 0.15s',
            }}
              onMouseEnter={e => { e.currentTarget.style.background = `${color}18`; e.currentTarget.style.borderColor = `${color}55`; }}
              onMouseLeave={e => { e.currentTarget.style.background = `${color}0A`; e.currentTarget.style.borderColor = `${color}28`; }}
            >
              <Icon style={{ width: '13px', height: '13px' }} />
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const DashboardView = ({ stats, notifications, prospects, partners, role, revenueDeals, teasers = [], workflows = [], deletionRequests = [], auditLogs = [] }) => {
  const approachingDeadlines = prospects.filter(p => {
    if (p.approvalStatus !== 'Approved' || p.introOccurred || !p.introDeadline) return false;
    const daysUntil = daysBetween(new Date(), new Date(p.introDeadline));
    return daysUntil <= 7 && daysUntil >= 0;
  });

  const waitingForIntro = prospects.filter(p =>
    p.approvalStatus === 'Approved' && !p.introOccurred && p.introDeadline
  );

  const totalRevenue = revenueDeals.reduce((sum, d) => sum + (parseFloat(d.totalAgreed) || parseFloat(d.totalReceived) || 0), 0);
  const pendingRequests = deletionRequests.filter(r => r.status === 'Pending').length;
  const activeWorkflows = workflows.filter(w => w.enabled).length;

  // KPI deltas — "this month vs last month" counts
  const _now = new Date();
  const _thisMonthStart = new Date(_now.getFullYear(), _now.getMonth(), 1);
  const _lastMonthStart = new Date(_now.getFullYear(), _now.getMonth() - 1, 1);
  const _mCount = (items, field = 'createdAt', from, to) =>
    items.filter(i => { const d = new Date(i[field]); return !isNaN(d) && d >= from && d < to; }).length;
  const partnerDelta  = _mCount(partners,    'createdAt', _thisMonthStart, _now) - _mCount(partners,    'createdAt', _lastMonthStart, _thisMonthStart);
  const pendingDelta  = _mCount(prospects.filter(p => p.approvalStatus === 'Pending'),  'createdAt', _thisMonthStart, _now) - _mCount(prospects.filter(p => p.approvalStatus === 'Pending'),  'createdAt', _lastMonthStart, _thisMonthStart);
  const approvedDelta = _mCount(prospects.filter(p => p.approvalStatus === 'Approved'), 'createdAt', _thisMonthStart, _now) - _mCount(prospects.filter(p => p.approvalStatus === 'Approved'), 'createdAt', _lastMonthStart, _thisMonthStart);
  const dealsDelta    = _mCount(revenueDeals, 'createdAt', _thisMonthStart, _now) - _mCount(revenueDeals, 'createdAt', _lastMonthStart, _thisMonthStart);
  const teasersDelta  = _mCount(teasers,      'createdAt', _thisMonthStart, _now) - _mCount(teasers,      'createdAt', _lastMonthStart, _thisMonthStart);

  // Revenue trend — last 12 months from payments
  const revenueByMonth = Array.from({ length: 12 }, (_, i) => {
    const d     = new Date(_now.getFullYear(), _now.getMonth() - (11 - i), 1);
    const nextD = new Date(d.getFullYear(), d.getMonth() + 1, 1);
    const rev   = revenueDeals.reduce((sum, deal) =>
      sum + (deal.payments || [])
        .filter(p => { const pd = new Date(p.date); return !isNaN(pd) && pd >= d && pd < nextD; })
        .reduce((s, p) => s + (parseFloat(p.amount) || 0), 0), 0);
    return { month: d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }), revenue: rev };
  });
  const hasRevenueData = revenueByMonth.some(r => r.revenue > 0);

  // Top partners by revenue (partner payouts across all deals)
  const _partnerRevMap = {};
  revenueDeals.forEach(deal => {
    const { partnerPayouts } = computeDealTotals(deal);
    (partnerPayouts || []).forEach(pp => {
      if (pp.partnerId) _partnerRevMap[pp.partnerId] = (_partnerRevMap[pp.partnerId] || 0) + (pp.amount || 0);
    });
  });
  const topPartners = Object.entries(_partnerRevMap)
    .map(([id, revenue]) => ({ partner: partners.find(p => p.id === id), revenue }))
    .filter(x => x.partner && x.revenue > 0)
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 5);

  // Pipeline funnel
  const _prospectIdsWithDeals = new Set(revenueDeals.map(d => d.prospectId).filter(Boolean));
  const funnelSteps = [
    { label: 'Submitted',  count: prospects.length,                                                          color: BRAND_COLORS.statusInfo    },
    { label: 'Approved',   count: prospects.filter(p => p.approvalStatus === 'Approved').length,             color: BRAND_COLORS.statusWarning  },
    { label: 'In Deals',   count: prospects.filter(p => _prospectIdsWithDeals.has(p.id)).length,             color: BRAND_COLORS.gold           },
    { label: 'Closed',     count: revenueDeals.filter(d => d.dealState === 'Closed').length,                 color: BRAND_COLORS.statusSuccess  },
  ];

  // Upcoming agreement expirations — next 90 days
  const expiringPartners = partners
    .filter(p => p.status === 'Active' && p.endDate)
    .map(p => ({ ...p, _daysLeft: Math.ceil((new Date(p.endDate) - _now) / (1000 * 60 * 60 * 24)) }))
    .filter(p => p._daysLeft >= 0 && p._daysLeft <= 90)
    .sort((a, b) => a._daysLeft - b._daysLeft);

  const navigate = (tab) => window.dispatchEvent(new CustomEvent('navigate-to-tab', { detail: tab }));

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold" style={{ color: BRAND_COLORS.navy }}>Dashboard Overview</h2>

      <GlobeVisualization
        partners={partners}
        navigate={navigate}
        showActions={role === 'Admin' || role === 'Editor' || role === 'Super Admin'}
      />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Partners — Admin + Editor + Super Admin */}
        {(role === 'Admin' || role === 'Editor' || role === 'Super Admin') && (
          <>
            <ClickableStatCard title="Active Partners" value={stats.activePartners}
              icon={Users} color="green" onClick={() => navigate('partners')} delta={partnerDelta} />
            <ClickableStatCard title="In Tail" value={stats.inTailPartners}
              icon={Clock} color="yellow" onClick={() => navigate('partners')} />
          </>
        )}

        {/* Prospects — all roles */}
        <ClickableStatCard title="Pending Prospects" value={stats.pendingProspects}
          icon={AlertCircle} color="orange" onClick={() => navigate('prospects')} delta={pendingDelta} />
        <ClickableStatCard title="Approved Prospects" value={stats.approvedProspects}
          icon={Check} color="blue" onClick={() => navigate('prospects')} delta={approvedDelta} />

        {/* Revenue — Admin + Editor + Super Admin */}
        {(role === 'Admin' || role === 'Editor' || role === 'Super Admin') && (
          <>
            <ClickableStatCard title="Revenue Deals" value={revenueDeals.length}
              icon={DollarSign} color="purple" onClick={() => navigate('revenue')} delta={dealsDelta} />
            <ClickableStatCard
              title="Total Revenue"
              value={totalRevenue >= 1000000 ? `$${(totalRevenue/1000000).toFixed(1)}M` : totalRevenue >= 1000 ? `$${(totalRevenue/1000).toFixed(0)}K` : `$${totalRevenue.toLocaleString()}`}
              icon={TrendingUp} color="green" onClick={() => navigate('revenue')} />
          </>
        )}

        {/* Teasers — Admin + Editor + Super Admin */}
        {(role === 'Admin' || role === 'Editor' || role === 'Super Admin') && (
          <ClickableStatCard title="Teasers" value={teasers.length}
            icon={FileText} color="blue" onClick={() => navigate('teasers')} delta={teasersDelta} />
        )}

        {/* Active Workflows — Admin + Editor + Super Admin */}
        {(role === 'Admin' || role === 'Editor' || role === 'Super Admin') && (
          <ClickableStatCard title="Active Workflows" value={activeWorkflows}
            icon={Zap} color="purple" onClick={() => navigate('workflows')} />
        )}

        {/* Pending Deletion Requests — Admin + Super Admin */}
        {(role === 'Admin' || role === 'Super Admin') && pendingRequests > 0 && (
          <ClickableStatCard title="Pending Requests" value={pendingRequests}
            icon={AlertCircle} color="orange" onClick={() => navigate('requests')} />
        )}
      </div>

      {/* Top Partners by Revenue — Admin / Editor / Super Admin only */}
      {(role === 'Admin' || role === 'Editor' || role === 'Super Admin') && (
        <div style={{
          background: BRAND_COLORS.bgSurface,
          border: `1px solid ${BRAND_COLORS.borderNavy12}`,
          borderRadius: '12px',
          padding: '20px 24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
            <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: BRAND_COLORS.navy, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '26px', height: '26px', borderRadius: '7px', backgroundColor: `${BRAND_COLORS.gold}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Users style={{ width: '13px', height: '13px', color: BRAND_COLORS.gold }} />
              </div>
              Top Partners by Revenue
            </h3>
            <span style={{ fontSize: '11px', fontWeight: 600, color: BRAND_COLORS.textTertiary, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
              from deal payouts
            </span>
          </div>
          {topPartners.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: `${BRAND_COLORS.gold}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px' }}>
                <DollarSign style={{ width: '16px', height: '16px', color: BRAND_COLORS.gold }} />
              </div>
              <p style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: BRAND_COLORS.navy }}>No payouts yet</p>
              <p style={{ margin: '3px 0 0', fontSize: '11px', color: BRAND_COLORS.textTertiary }}>Partner commissions will appear here once deals are closed</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {topPartners.map(({ partner, revenue }, i) => {
                const maxRev = topPartners[0].revenue;
                const pct    = Math.round((revenue / maxRev) * 100);
                const fmtRev = revenue >= 1000000 ? `$${(revenue/1000000).toFixed(2)}M` : revenue >= 1000 ? `$${(revenue/1000).toFixed(0)}K` : `$${revenue.toLocaleString()}`;
                return (
                  <div key={partner.id} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: i === 0 ? BRAND_COLORS.gold : BRAND_COLORS.navy, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span style={{ fontSize: '10px', fontWeight: 700, color: i === 0 ? BRAND_COLORS.navy : BRAND_COLORS.gold }}>{i + 1}</span>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '3px' }}>
                        <span style={{ fontSize: '12px', fontWeight: 600, color: BRAND_COLORS.navy, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{partner.name}</span>
                        <span style={{ fontSize: '12px', fontWeight: 700, color: BRAND_COLORS.gold, flexShrink: 0, marginLeft: '8px' }}>{fmtRev}</span>
                      </div>
                      <div style={{ height: '4px', borderRadius: '2px', background: BRAND_COLORS.borderNavy12, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${pct}%`, borderRadius: '2px', background: i === 0 ? BRAND_COLORS.gold : BRAND_COLORS.navy, transition: 'width 0.4s ease' }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Pipeline Funnel — Admin / Editor / Super Admin only */}
      {(role === 'Admin' || role === 'Editor' || role === 'Super Admin') && (
        <div style={{
          background: BRAND_COLORS.bgSurface,
          border: `1px solid ${BRAND_COLORS.borderNavy12}`,
          borderRadius: '12px',
          padding: '20px 24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '18px' }}>
            <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: BRAND_COLORS.navy, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '26px', height: '26px', borderRadius: '7px', backgroundColor: `${BRAND_COLORS.statusInfo}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <TrendingUp style={{ width: '13px', height: '13px', color: BRAND_COLORS.statusInfo }} />
              </div>
              Deal Pipeline
            </h3>
          </div>
          <div style={{ display: 'flex', alignItems: 'stretch', gap: '0' }}>
            {funnelSteps.map((step, i) => {
              const prev = funnelSteps[i - 1];
              const rate = prev && prev.count > 0 ? Math.round((step.count / prev.count) * 100) : null;
              return (
                <React.Fragment key={step.label}>
                  {i > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '0 4px', gap: '2px' }}>
                      <ChevronRight style={{ width: '14px', height: '14px', color: BRAND_COLORS.textTertiary }} />
                      {rate !== null && (
                        <span style={{ fontSize: '9px', fontWeight: 600, color: BRAND_COLORS.textTertiary }}>{rate}%</span>
                      )}
                    </div>
                  )}
                  <div style={{
                    flex: 1, textAlign: 'center', padding: '12px 8px',
                    borderRadius: '10px', background: `${step.color}0D`,
                    border: `1px solid ${step.color}30`,
                  }}>
                    <p style={{ margin: 0, fontSize: '28px', fontWeight: 700, lineHeight: 1, color: step.color, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' }}>
                      {step.count}
                    </p>
                    <p style={{ margin: '5px 0 0', fontSize: '11px', fontWeight: 500, color: BRAND_COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      {step.label}
                    </p>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}

      {/* Expiring Agreements — Admin / Editor / Super Admin only */}
      {(role === 'Admin' || role === 'Editor' || role === 'Super Admin') && (
        <div style={{
          background: BRAND_COLORS.bgSurface,
          border: `1px solid ${BRAND_COLORS.borderNavy12}`,
          borderRadius: '12px',
          padding: '20px 24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
            <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: BRAND_COLORS.navy, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '26px', height: '26px', borderRadius: '7px', backgroundColor: `${BRAND_COLORS.statusWarning}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Clock style={{ width: '13px', height: '13px', color: BRAND_COLORS.statusWarning }} />
              </div>
              Expiring Agreements
            </h3>
            <span style={{ fontSize: '11px', fontWeight: 600, color: BRAND_COLORS.textTertiary, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
              next 90 days · {expiringPartners.length} partner{expiringPartners.length !== 1 ? 's' : ''}
            </span>
          </div>
          {expiringPartners.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: `${BRAND_COLORS.statusSuccess}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px' }}>
                <Check style={{ width: '16px', height: '16px', color: BRAND_COLORS.statusSuccess }} />
              </div>
              <p style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: BRAND_COLORS.navy }}>All clear</p>
              <p style={{ margin: '3px 0 0', fontSize: '11px', color: BRAND_COLORS.textTertiary }}>No agreements expiring in the next 90 days</p>
            </div>
          ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {expiringPartners.map(p => {
              const urgency = p._daysLeft <= 14
                ? { bg: `${BRAND_COLORS.statusError}12`,   badge: BRAND_COLORS.statusError,   label: `${p._daysLeft}d left` }
                : p._daysLeft <= 30
                ? { bg: `${BRAND_COLORS.statusWarning}12`, badge: BRAND_COLORS.statusWarning, label: `${p._daysLeft}d left` }
                : { bg: `${BRAND_COLORS.borderNavy12}`,    badge: BRAND_COLORS.textTertiary,  label: `${p._daysLeft}d left` };
              return (
                <div key={p.id}
                  onClick={() => navigate('partners')}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '8px 12px', borderRadius: '8px', background: urgency.bg,
                    cursor: 'pointer', transition: 'opacity 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.opacity = '0.75'}
                  onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: BRAND_COLORS.navy, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span style={{ fontSize: '11px', fontWeight: 700, color: BRAND_COLORS.gold }}>
                        {(p.name || '?').charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: BRAND_COLORS.navy }}>{p.name}</p>
                      <p style={{ margin: 0, fontSize: '11px', color: BRAND_COLORS.textTertiary }}>{p.agreementType || 'Agreement'} · expires {new Date(p.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                    </div>
                  </div>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: urgency.badge, whiteSpace: 'nowrap' }}>
                    {urgency.label}
                  </span>
                </div>
              );
            })}
          </div>
          )}
        </div>
      )}

      {/* Revenue Trend Chart — Admin / Editor / Super Admin only */}
      {(role === 'Admin' || role === 'Editor' || role === 'Super Admin') && (
        <div style={{
          background: BRAND_COLORS.bgSurface,
          border: `1px solid ${BRAND_COLORS.borderNavy12}`,
          borderRadius: '12px',
          padding: '20px 24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: BRAND_COLORS.navy, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '26px', height: '26px', borderRadius: '7px', backgroundColor: `${BRAND_COLORS.gold}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <TrendingUp style={{ width: '13px', height: '13px', color: BRAND_COLORS.gold }} />
              </div>
              Revenue — Last 12 Months
            </h3>
            <span style={{ fontSize: '11px', fontWeight: 600, color: BRAND_COLORS.textTertiary, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
              from payments
            </span>
          </div>
          {!hasRevenueData ? (
            <div style={{ textAlign: 'center', padding: '28px 0' }}>
              <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: `${BRAND_COLORS.gold}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px' }}>
                <TrendingUp style={{ width: '16px', height: '16px', color: BRAND_COLORS.gold }} />
              </div>
              <p style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: BRAND_COLORS.navy }}>No payments recorded yet</p>
              <p style={{ margin: '3px 0 0', fontSize: '11px', color: BRAND_COLORS.textTertiary }}>Log a payment in Revenue Manager to see the trend</p>
            </div>
          ) : (
          <ResponsiveContainer width="100%" height={160}>
            <AreaChart data={revenueByMonth} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="dashRevGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor={BRAND_COLORS.gold} stopOpacity={0.18} />
                  <stop offset="95%" stopColor={BRAND_COLORS.gold} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={BRAND_COLORS.borderNavy12} vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: BRAND_COLORS.textTertiary }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: BRAND_COLORS.textTertiary }} axisLine={false} tickLine={false} width={48}
                tickFormatter={v => v >= 1000000 ? `$${(v/1000000).toFixed(1)}M` : v >= 1000 ? `$${(v/1000).toFixed(0)}K` : `$${v}`} />
              <Tooltip
                contentStyle={{ background: BRAND_COLORS.bgSurface, border: `1px solid ${BRAND_COLORS.borderNavy12}`, borderRadius: '8px', fontSize: '12px' }}
                formatter={v => [`$${v >= 1000000 ? (v/1000000).toFixed(2)+'M' : v >= 1000 ? (v/1000).toFixed(0)+'K' : v.toLocaleString()}`, 'Revenue']}
              />
              <Area type="monotone" dataKey="revenue" stroke={BRAND_COLORS.navy} strokeWidth={2}
                fill="url(#dashRevGrad)" dot={false} activeDot={{ r: 4, fill: BRAND_COLORS.gold, stroke: BRAND_COLORS.navy, strokeWidth: 2 }} />
            </AreaChart>
          </ResponsiveContainer>
          )}
        </div>
      )}

      {/* Activity + Notifications — side by side */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', alignItems: 'start' }}>

      {/* Recent Activity Feed — Admin / Editor / Super Admin only */}
      {(role === 'Admin' || role === 'Editor' || role === 'Super Admin') && auditLogs.length > 0 && (() => {
        const ENTITY_ICON = { Partner: Users, Prospect: FileText, Deal: DollarSign, Teaser: FileText, Workflow: Zap, Settings: Settings, Notification: Bell };
        const ENTITY_COLOR = { Partner: BRAND_COLORS.navy, Prospect: BRAND_COLORS.statusInfo, Deal: BRAND_COLORS.statusSuccess, Teaser: BRAND_COLORS.gold, Workflow: '#7C3AED', Settings: BRAND_COLORS.textTertiary, Notification: BRAND_COLORS.statusWarning };
        const timeAgo = ts => {
          const s = Math.floor((Date.now() - new Date(ts)) / 1000);
          if (s < 60)  return `${s}s ago`;
          if (s < 3600) return `${Math.floor(s/60)}m ago`;
          if (s < 86400) return `${Math.floor(s/3600)}h ago`;
          return `${Math.floor(s/86400)}d ago`;
        };
        const recent = [...auditLogs].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 8);
        return (
          <div style={{
            background: BRAND_COLORS.bgSurface,
            border: `1px solid ${BRAND_COLORS.borderNavy12}`,
            borderRadius: '12px',
            padding: '20px 24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
              <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: BRAND_COLORS.navy, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '26px', height: '26px', borderRadius: '7px', backgroundColor: `${BRAND_COLORS.navy}12`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Activity style={{ width: '13px', height: '13px', color: BRAND_COLORS.navy }} />
                </div>
                Recent Activity
              </h3>
              <span style={{ fontSize: '11px', fontWeight: 600, color: BRAND_COLORS.textTertiary, letterSpacing: '0.04em', textTransform: 'uppercase' }}>last 8 events</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              {recent.map((log, idx) => {
                const Icon = ENTITY_ICON[log.entityType] || Activity;
                const dot  = ENTITY_COLOR[log.entityType] || BRAND_COLORS.textTertiary;
                return (
                  <div key={log.id || idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '7px 8px', borderRadius: '7px' }}
                    onMouseEnter={e => e.currentTarget.style.background = BRAND_COLORS.bgApp}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{ width: '24px', height: '24px', borderRadius: '6px', backgroundColor: `${dot}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>
                      <Icon style={{ width: '12px', height: '12px', color: dot }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: 0, fontSize: '12px', fontWeight: 600, color: BRAND_COLORS.navy }}>
                        {log.entityType} <span style={{ fontWeight: 400, color: BRAND_COLORS.textSecondary }}>{log.action}</span>
                      </p>
                      {log.details && (
                        <p style={{ margin: '1px 0 0', fontSize: '11px', color: BRAND_COLORS.textTertiary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {log.details}
                        </p>
                      )}
                    </div>
                    <span style={{ fontSize: '10px', color: BRAND_COLORS.textTertiary, whiteSpace: 'nowrap', marginTop: '2px' }}>{timeAgo(log.createdAt)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Notifications */}
      {notifications.length > 0 && (
        <div className="bg-white rounded-xl p-6" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: `1px solid ${BRAND_COLORS.border}` }}>
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-base font-semibold flex items-center gap-2" style={{ color: BRAND_COLORS.navy }}>
              <div style={{ width: '28px', height: '28px', borderRadius: '8px', backgroundColor: `${BRAND_COLORS.gold}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Bell style={{ width: '14px', height: '14px', color: BRAND_COLORS.gold }} />
              </div>
              Recent Notifications
            </h3>
            <span style={{ fontSize: '11px', fontWeight: 600, color: BRAND_COLORS.textTertiary, letterSpacing: '0.04em', textTransform: 'uppercase' }}>{notifications.length} total</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {notifications.slice(0, 5).map((notif, idx) => {
              const isAutoReject = notif.type === 'auto-reject';
              const isDeadline = notif.type === 'intro-deadline';
              const iconColor = isAutoReject ? '#EF4444' : isDeadline ? '#F59E0B' : BRAND_COLORS.navy;
              const NotifIcon = isAutoReject ? X : isDeadline ? Clock : AlertCircle;
              return (
                <div key={notif.id}
                  onClick={() => window.dispatchEvent(new CustomEvent('navigate-to-tab', { detail: 'prospects' }))}
                  style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px', borderRadius: '8px', cursor: 'pointer', transition: 'background 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <div style={{ width: '32px', height: '32px', borderRadius: '8px', flexShrink: 0, backgroundColor: `${iconColor}12`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <NotifIcon style={{ width: '15px', height: '15px', color: iconColor }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: '13px', color: BRAND_COLORS.textPrimary, fontWeight: 500, lineHeight: 1.4 }}>{notif.message}</p>
                    <p style={{ fontSize: '11px', color: BRAND_COLORS.textTertiary, marginTop: '3px' }}>{formatDate(notif.createdAt)}</p>
                  </div>
                  {idx === 0 && (
                    <span style={{ fontSize: '10px', fontWeight: 700, color: BRAND_COLORS.gold, backgroundColor: `${BRAND_COLORS.gold}15`, padding: '2px 8px', borderRadius: '999px', flexShrink: 0 }}>NEW</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      </div>{/* end activity+notifications grid */}

      {/* Waiting for Intro — Admin + Editor */}
      {(role === 'Admin' || role === 'Editor' || role === 'Super Admin') && waitingForIntro.length > 0 && (
        <div style={{ background: BRAND_COLORS.bgSurface, border: `1px solid ${BRAND_COLORS.borderNavy12}`, borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <h3 className="text-base font-semibold mb-4 flex items-center gap-2" style={{ color: BRAND_COLORS.navy }}>
            <div style={{ width: '28px', height: '28px', borderRadius: '8px', backgroundColor: '#DBEAFE', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Clock style={{ width: '14px', height: '14px', color: '#1E40AF' }} />
            </div>
            Waiting for Introduction ({waitingForIntro.length})
          </h3>
          <div className="space-y-2">
            {waitingForIntro.slice(0, 5).map(prospect => {
              const daysLeft = prospect.introDeadline ? daysBetween(new Date(), new Date(prospect.introDeadline)) : null;
              return (
                <div key={prospect.id} className="flex justify-between items-center p-3 rounded-lg" style={{ background: BRAND_COLORS.bgSurface, border: `1px solid ${BRAND_COLORS.borderNavy12}` }}>
                  <div>
                    <p className="font-medium text-sm" style={{ color: BRAND_COLORS.navy }}>{prospect.companyName || prospect.name}</p>
                    <p className="text-xs" style={{ color: BRAND_COLORS.textSecondary }}>By: {prospect.introducingPartnerId ? (partners.find(pt => pt.id === prospect.introducingPartnerId)?.name || 'Partner') : 'Direct'}</p>
                  </div>
                  {daysLeft !== null && (
                    <span className="text-xs font-semibold px-2 py-1 rounded-full"
                      style={{ backgroundColor: daysLeft <= 7 ? '#FEE2E2' : '#FEF3C7', color: daysLeft <= 7 ? '#991B1B' : '#92400E' }}>
                      {daysLeft}d left
                    </span>
                  )}
                </div>
              );
            })}
            {waitingForIntro.length > 5 && (
              <p className="text-xs text-gray-400 text-center pt-1">+{waitingForIntro.length - 5} more</p>
            )}
          </div>
        </div>
      )}

      {/* Approaching Deadlines — Admin + Editor */}
      {(role === 'Admin' || role === 'Editor' || role === 'Super Admin') && approachingDeadlines.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6" style={{ borderTop: `2px solid ${BRAND_COLORS.gold}` }}>
          <h3 className="text-lg font-semibold mb-4 flex items-center" style={{ color: BRAND_COLORS.navy }}>
            <Clock className="w-5 h-5 mr-2 text-orange-600" />
            Intro Deadlines Approaching
          </h3>
          <div className="space-y-2">
            {approachingDeadlines.map(prospect => {
              const daysLeft = daysBetween(new Date(), new Date(prospect.introDeadline));
              return (
                <div key={prospect.id}
                  className="flex justify-between items-center p-3 bg-orange-50 rounded-lg cursor-pointer transition-all hover:shadow-md hover:bg-orange-100"
                  onClick={() => window.dispatchEvent(new CustomEvent('navigate-to-tab', { detail: 'prospects' }))}
                >
                  <div>
                    <p className="font-medium text-gray-900">{prospect.companyName || prospect.name}</p>
                    <p className="text-sm text-gray-600">Introduced by: {prospect.introducingPartnerId ? (partners.find(pt => pt.id === prospect.introducingPartnerId)?.name || 'Partner') : 'Direct'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-orange-600">{daysLeft} days left</p>
                    <p className="text-xs text-gray-500">{formatDate(prospect.introDeadline)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Map legacy `color` prop ("green"/"yellow"/"orange"/"blue"/"purple") to a
// semantic accent stripe colour. Explicit `accent` prop wins if provided.
const STAT_ACCENT_BY_COLOR = {
  green:  '#10B981',
  yellow: '#F59E0B',
  orange: '#F59E0B',
  red:    '#EF4444',
  blue:   '#3B82F6',
  purple: '#3B82F6',
};

const ClickableStatCard = ({ title, value, icon: Icon, color, accent, onClick, delta }) => {
  // Flat canonical KPI card. Per-stat colour shows up as a 4px left stripe.
  // Defaults to gold when no accent/colour is supplied.
  const stripe = accent || STAT_ACCENT_BY_COLOR[color] || BRAND_COLORS.gold;
  return (
    <div
      onClick={onClick}
      className="cursor-pointer"
      style={{
        background: BRAND_COLORS.bgSurface,
        border: `1px solid ${BRAND_COLORS.borderNavy12}`,
        borderLeft: `4px solid ${stripe}`,
        borderRadius: '12px',
        padding: '16px 18px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: '12px',
        minHeight: '100px',
        transition: 'box-shadow 0.15s, border-color 0.15s',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(10,20,40,0.08)';
        e.currentTarget.style.borderTopColor = BRAND_COLORS.borderNavy19;
        e.currentTarget.style.borderRightColor = BRAND_COLORS.borderNavy19;
        e.currentTarget.style.borderBottomColor = BRAND_COLORS.borderNavy19;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)';
        e.currentTarget.style.borderTopColor = BRAND_COLORS.borderNavy12;
        e.currentTarget.style.borderRightColor = BRAND_COLORS.borderNavy12;
        e.currentTarget.style.borderBottomColor = BRAND_COLORS.borderNavy12;
      }}
    >
      {/* Label */}
      <p style={{
        fontSize: '11px', fontWeight: 500, letterSpacing: '0.04em',
        textTransform: 'uppercase', color: BRAND_COLORS.textSecondary,
        margin: 0,
      }}>
        {title}
      </p>

      {/* Number + Icon */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
        <div>
          <p style={{
            fontSize: '28px', fontWeight: 700, lineHeight: 1,
            color: BRAND_COLORS.navy,
            letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums',
            margin: 0,
          }}>
            {value}
          </p>
          {delta !== undefined && delta !== 0 && (
            <p style={{
              fontSize: '11px', fontWeight: 600, margin: '4px 0 0',
              color: delta > 0 ? BRAND_COLORS.statusSuccess : BRAND_COLORS.statusError,
            }}>
              {delta > 0 ? `↑${delta}` : `↓${Math.abs(delta)}`} this month
            </p>
          )}
        </div>
        <div style={{
          width: '30px', height: '30px', borderRadius: '7px',
          backgroundColor: `${stripe}1A`,  // 10% tint of the accent
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Icon style={{ width: '16px', height: '16px', color: stripe }} />
        </div>
      </div>
    </div>
  );
};

// EvE Logo Component - CSS-based logo matching brand design
const EvELogo = ({ size = 80 }) => (
  <div style={{
    width: `${size}px`, height: `${size}px`,
    borderRadius: '50%', overflow: 'hidden',
    flexShrink: 0,
    boxShadow: `0 0 ${Math.round(size * 0.15)}px rgba(212,175,55,0.5)`,
    background: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: `${Math.round(size * 0.08)}px`,
  }}>
    <img src={EVE_LOGO_SRC} alt="EvE Logo"
      style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
  </div>
);

// ============================================================================
// COMPONENT: PartnerProfileDrawer
// ============================================================================

const PartnerProfileDrawer = ({ partner, prospects = [], revenueDeals = [], onClose, onEdit, currentUserRole }) => {
  const { isMobile } = useIsMobile();
  const [activeTab, setActiveTab] = useState('overview');

  const formatDate = (d) => {
    if (!d) return 'N/A';
    const date = new Date(d);
    return isNaN(date) ? 'N/A' : date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const pid = partner?.id;

  const partnerProspects = prospects.filter(p => p.introducingPartnerId === pid);

  const partnerDeals = revenueDeals.filter(deal => {
    const lp = deal.prospectId ? prospects.find(p => p.id === deal.prospectId) : null;
    const ivp = deal.investorProspectId ? prospects.find(p => p.id === deal.investorProspectId) : null;
    return lp?.introducingPartnerId === pid || ivp?.introducingPartnerId === pid ||
      deal.strategicPartners?.some(sp => sp.partnerId === pid);
  });

  const statusColor = partner?.status === 'Active' ? '#10B981'
    : partner?.status === 'In Tail' ? '#F59E0B' : '#EF4444';

  const tabs = [
    { key: 'overview',  label: 'Overview',  icon: Users },
    { key: 'focus',     label: 'Focus',     icon: Target },
    { key: 'prospects', label: 'Prospects', icon: Layers, count: partnerProspects.length },
    { key: 'deals',     label: 'Deals',     icon: DollarSign, count: partnerDeals.length },
    { key: 'notes',     label: 'Notes',     icon: ClipboardList },
    { key: 'activity',  label: 'Activity',  icon: Activity },
  ];

  const renderCommission = (terms) => {
    if (!terms) return <span className="text-gray-400">—</span>;
    if (terms.type === 'fixed') return <span>{terms.percentage}%</span>;
    if (terms.type === 'basic') return (
      <span>Client {terms.clientPercentage}% / Investor {terms.investorPercentage}%</span>
    );
    if (terms.type === 'pool') return <span>Pool {terms.poolSplitPercentage ?? 50}%</span>;
    if (terms.type === 'table' && terms.brackets?.length) return (
      <div className="mt-1 space-y-0.5">
        {terms.brackets.map((b, i) => (
          <div key={i} className="text-xs text-gray-600">
            Up to {b.upTo ? `$${Number(b.upTo).toLocaleString()}` : '∞'}: {b.nonActive}% / {b.active}% active
          </div>
        ))}
      </div>
    );
    return <span className="capitalize">{terms.type}</span>;
  };

  const prospectStatusColor = (status) => {
    if (status === 'Approved') return { bg: '#D1FAE5', text: '#065F46' };
    if (status === 'Rejected') return { bg: '#FEE2E2', text: '#991B1B' };
    return { bg: '#FEF3C7', text: '#92400E' };
  };

  const dealStatusColor = (status) => {
    if (!status) return { bg: '#F3F4F6', text: '#6B7280' };
    if (status === 'Closed' || status === 'Won') return { bg: '#D1FAE5', text: '#065F46' };
    if (status === 'Lost') return { bg: '#FEE2E2', text: '#991B1B' };
    return { bg: '#DBEAFE', text: '#1E40AF' };
  };

  const geo = partner?.geography || {};
  const continents = geo.continents || [];
  const countries = geo.countries || [];

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-30 z-40"
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className="fixed z-50 flex flex-col shadow-2xl overflow-hidden"
        style={isMobile ? {
          inset: 0,
          width: '100%',
          backgroundColor: '#F9FAFB',
          borderTop: `4px solid ${BRAND_COLORS.gold}`,
          borderRadius: 0,
        } : {
          right: '12px', top: '12px', bottom: '12px',
          width: '62%',
          backgroundColor: '#F9FAFB',
          border: '1px solid #E5E7EB',
          borderLeft: `4px solid ${BRAND_COLORS.gold}`,
          borderRadius: '16px',
        }}
      >
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-4 flex items-center gap-4" style={{ background: BRAND_COLORS.bgHeader }}>
          <div className="w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold text-white flex-shrink-0"
            style={{ backgroundColor: BRAND_COLORS.gold }}>
            {(partner?.name || '?')[0].toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl font-bold text-white truncate">{partner?.name}</h2>
              {partner?.status && (
                <span className="px-2 py-0.5 rounded-full text-xs font-semibold text-white flex-shrink-0"
                  style={{ backgroundColor: statusColor }}>
                  {partner.status}
                </span>
              )}
            </div>
            {partner?.agreementType && (
              <p className="text-sm mt-0.5" style={{ color: BRAND_COLORS.gold }}>{partner.agreementType}</p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {onEdit && (
              <button
                onClick={() => onEdit(partner)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all hover:opacity-90"
                style={{ backgroundColor: BRAND_COLORS.gold, color: BRAND_COLORS.navy }}
              >
                <Edit2 className="w-3.5 h-3.5" />
                Edit
              </button>
            )}
            <button onClick={onClose} className="text-white hover:text-gray-300 transition-colors">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex-shrink-0 flex border-b border-gray-200 bg-white overflow-x-auto">
          {tabs.map(({ key, label, icon: Icon, count }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className="flex items-center gap-1.5 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors"
              style={{
                borderBottomColor: activeTab === key ? BRAND_COLORS.navy : 'transparent',
                color: activeTab === key ? BRAND_COLORS.navy : '#6B7280',
              }}
            >
              <Icon className="w-4 h-4" />
              {label}
              {count !== undefined && (
                <span className="px-1.5 py-0.5 rounded-full text-xs"
                  style={{ backgroundColor: activeTab === key ? BRAND_COLORS.navy : '#E5E7EB', color: activeTab === key ? 'white' : '#374151' }}>
                  {count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">

          {/* ── OVERVIEW ── */}
          {activeTab === 'overview' && (
            <>
              {/* Contact */}
              <div className="bg-white rounded-xl p-5 space-y-3" style={{ border: `1px solid #E5E7EB` }}>
                <h3 className="text-sm font-semibold uppercase tracking-wide" style={{ color: BRAND_COLORS.navy }}>Contact</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {partner?.contactPerson && (
                    <div>
                      <p className="text-xs text-gray-400 mb-0.5">Contact Person</p>
                      <p className="font-medium" style={{ color: BRAND_COLORS.navy }}>{partner.contactPerson}</p>
                    </div>
                  )}
                  {partner?.email && (
                    <div>
                      <p className="text-xs text-gray-400 mb-0.5">Email</p>
                      <a href={`mailto:${partner.email}`} className="font-medium hover:underline" style={{ color: BRAND_COLORS.royalBlue || '#4169E1' }}>{partner.email}</a>
                    </div>
                  )}
                  {partner?.phone && (
                    <div>
                      <p className="text-xs text-gray-400 mb-0.5">Phone</p>
                      <p className="font-medium" style={{ color: BRAND_COLORS.navy }}>{partner.phone}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* NDA */}
              <div className="bg-white rounded-xl p-5 space-y-3" style={{ border: `1px solid #E5E7EB` }}>
                <h3 className="text-sm font-semibold uppercase tracking-wide" style={{ color: BRAND_COLORS.navy }}>NDA</h3>
                <div className="flex items-center gap-3">
                  <span className="px-2.5 py-1 rounded-full text-sm font-semibold"
                    style={{ backgroundColor: partner?.ndaSigned ? '#D1FAE5' : '#FEE2E2', color: partner?.ndaSigned ? '#065F46' : '#991B1B' }}>
                    {partner?.ndaSigned ? `✓ ${partner.ndaType || 'NDA'} Signed` : 'Not Signed'}
                  </span>
                  {partner?.ndaSigned && partner?.ndaSignedDate && (
                    <span className="text-sm text-gray-500">{formatDate(partner.ndaSignedDate)}</span>
                  )}
                </div>
              </div>

              {/* Contract Dates & Commission */}
              <div className="bg-white rounded-xl p-5 space-y-3" style={{ border: `1px solid #E5E7EB` }}>
                <h3 className="text-sm font-semibold uppercase tracking-wide" style={{ color: BRAND_COLORS.navy }}>Contract</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Start Date</p>
                    <p className="font-medium" style={{ color: BRAND_COLORS.navy }}>{formatDate(partner?.startDate || partner?.mcaStartDate)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">End Date</p>
                    <p className="font-medium" style={{ color: BRAND_COLORS.navy }}>{formatDate(partner?.endDate)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Tail Period</p>
                    <p className="font-medium" style={{ color: BRAND_COLORS.navy }}>{partner?.tailMonths ?? 18} months</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Tail End</p>
                    <p className="font-medium" style={{ color: partner?.status === 'In Tail' ? '#F59E0B' : BRAND_COLORS.navy }}>
                      {formatDate(partner?.tailEndDate)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Commission</p>
                    <p className="font-medium" style={{ color: BRAND_COLORS.navy }}>
                      {renderCommission(partner?.commissionTerms)}
                    </p>
                  </div>
                  {partner?.commissionTerms?.paymentTermsDays != null && (
                    <div>
                      <p className="text-xs text-gray-400 mb-0.5">Payment Terms</p>
                      <p className="font-medium" style={{ color: BRAND_COLORS.navy }}>{partner.commissionTerms.paymentTermsDays} days</p>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* ── FOCUS ── */}
          {activeTab === 'focus' && (
            <>
              {partner?.sectors?.length > 0 && (
                <div className="bg-white rounded-xl p-5 space-y-3" style={{ border: `1px solid #E5E7EB` }}>
                  <h3 className="text-sm font-semibold uppercase tracking-wide" style={{ color: BRAND_COLORS.navy }}>Sectors of Interest</h3>
                  <div className="flex flex-wrap gap-2">
                    {partner.sectors.map(s => (
                      <span key={s} className="px-3 py-1 text-xs font-medium rounded-full text-white" style={{ backgroundColor: BRAND_COLORS.navy }}>
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {(continents.length > 0 || countries.length > 0) && (
                <div className="bg-white rounded-xl p-5 space-y-4" style={{ border: `1px solid #E5E7EB` }}>
                  <h3 className="text-sm font-semibold uppercase tracking-wide" style={{ color: BRAND_COLORS.navy }}>Geographic Focus</h3>
                  {continents.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-400 mb-2">Continents</p>
                      <div className="flex flex-wrap gap-2">
                        {continents.map(c => (
                          <span key={c} className="px-3 py-1 text-xs font-medium rounded-full" style={{ backgroundColor: `${BRAND_COLORS.navy}15`, color: BRAND_COLORS.navy }}>
                            {c}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {countries.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-400 mb-2">Countries ({countries.length})</p>
                      <div className="flex flex-wrap gap-2">
                        {countries.map(c => (
                          <span key={c} className="px-2.5 py-0.5 text-xs rounded-full border" style={{ borderColor: '#D1D5DB', color: '#374151' }}>
                            {c}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {!partner?.sectors?.length && continents.length === 0 && countries.length === 0 && (
                <div className="text-center py-12 text-gray-400 text-sm">No focus areas defined</div>
              )}
            </>
          )}

          {/* ── PROSPECTS ── */}
          {activeTab === 'prospects' && (
            partnerProspects.length === 0
              ? <div className="text-center py-12 text-gray-400 text-sm">No prospects submitted by this partner</div>
              : <div className="space-y-3">
                  {partnerProspects.map(p => {
                    const colors = prospectStatusColor(p.approvalStatus);
                    return (
                      <div key={p.id} className="bg-white rounded-xl p-4" style={{ border: `1px solid #E5E7EB` }}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-semibold text-sm truncate" style={{ color: BRAND_COLORS.navy }}>{p.name || p.companyName || 'Unnamed'}</p>
                            <p className="text-xs text-gray-400 mt-0.5">{p.prospectType || p.type || '—'}</p>
                          </div>
                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0"
                            style={{ backgroundColor: colors.bg, color: colors.text }}>
                            {p.approvalStatus || 'Pending'}
                          </span>
                        </div>
                        <div className="flex gap-4 mt-3 text-xs text-gray-400">
                          {p.submittedAt && <span>Submitted: {formatDate(p.submittedAt)}</span>}
                          {p.approvalDate && <span>Approved: {formatDate(p.approvalDate)}</span>}
                          {p.introDeadline && <span>Intro deadline: {formatDate(p.introDeadline)}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
          )}

          {/* ── DEALS ── */}
          {activeTab === 'deals' && (
            partnerDeals.length === 0
              ? <div className="text-center py-12 text-gray-400 text-sm">No deals linked to this partner</div>
              : <div className="space-y-3">
                  {partnerDeals.map(deal => {
                    const colors = dealStatusColor(deal.status);
                    const linkedProspect = deal.prospectId ? prospects.find(p => p.id === deal.prospectId) : null;
                    const totals = computeDealTotals(deal);
                    const partnerShare = totals.partnerPayouts
                      .filter(pp => pp.partnerId === pid)
                      .reduce((sum, pp) => sum + (pp.amount || 0), 0);
                    return (
                      <div key={deal.id} className="bg-white rounded-xl p-4" style={{ border: `1px solid #E5E7EB` }}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-semibold text-sm truncate" style={{ color: BRAND_COLORS.navy }}>{deal.dealName || 'Unnamed Deal'}</p>
                            {linkedProspect && (
                              <p className="text-xs text-gray-400 mt-0.5">{linkedProspect.name || linkedProspect.companyName}</p>
                            )}
                          </div>
                          {deal.status && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0"
                              style={{ backgroundColor: colors.bg, color: colors.text }}>
                              {deal.status}
                            </span>
                          )}
                        </div>
                        <div className="flex gap-4 mt-3 text-xs text-gray-500">
                          {deal.dealType && <span className="font-medium">{deal.dealType}</span>}
                          {deal.totalAgreed > 0 && (
                            <span>Total: {deal.currency || '$'}{Number(deal.totalAgreed).toLocaleString()}</span>
                          )}
                          {partnerShare > 0 && (
                            <span style={{ color: BRAND_COLORS.gold, fontWeight: 600 }}>
                              Commission: {deal.currency || '$'}{Number(partnerShare).toLocaleString()}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
          )}

          {/* ── NOTES ── */}
          {activeTab === 'notes' && (
            partner?.notes
              ? <div className="bg-white rounded-xl p-5" style={{ border: `1px solid #E5E7EB` }}>
                  <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">{partner.notes}</p>
                </div>
              : <div className="text-center py-12 text-gray-400 text-sm">No notes for this partner</div>
          )}

          {/* ── ACTIVITY ── */}
          {activeTab === 'activity' && (
            <div className="bg-white rounded-xl p-5 space-y-4" style={{ border: `1px solid #E5E7EB` }}>
              <h3 className="text-sm font-semibold uppercase tracking-wide" style={{ color: BRAND_COLORS.navy }}>Activity</h3>
              <div className="space-y-3 text-sm">
                {[
                  { label: 'Created', value: formatDate(partner?.createdAt) },
                  { label: 'Last Modified By', value: partner?.lastModifiedBy || 'N/A' },
                  { label: 'Last Login', value: formatDate(partner?.lastLoginDate) },
                  { label: 'Last Activity', value: formatDate(partner?.lastActivityDate) },
                ].map(({ label, value }) => (
                  <div key={label} className="flex justify-between items-center py-2 border-b border-gray-50 last:border-0">
                    <span className="text-gray-500">{label}</span>
                    <span className="font-medium" style={{ color: BRAND_COLORS.navy }}>{value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
};

// ============================================================================
// VIEW: Partners
// ============================================================================

const PartnersView = ({ partners, prospects, revenueDeals = [], users = [], onAdd, onEditPartner, onEditProspect, onDeletePartner, onRequestDelete, showConfirm, currentUserRole = 'Admin', draftPartners = [], onResumeDraft, onDiscardDraft }) => {
  // "Last Active" per partner.
  // Definition: most recent timestamp from the linked user_profile's last_login
  // (set by the stamp-last-login EF after every successful sign-in).
  //
  // Lookup priority — partner_id FK first (most reliable), then email match.
  // The FK is needed because some user_profiles have a different email from
  // the partner row's contact email.
  const lastActiveForPartner = (partner) => {
    if (!partner) return null;
    const u = users.find(x => x.partnerId === partner.id)
           || users.find(x => x.email && partner.email && x.email.toLowerCase() === partner.email.toLowerCase());
    // Prefer the live user_profile last_login. Fall back to legacy partner row fields.
    return u?.lastLogin || u?.lastLoginDate || partner.lastLoginDate || partner.lastActivityDate || null;
  };
  const [filter, setFilter] = useState('all');
  const [expandedPartner, setExpandedPartner] = useState(null);
  const [profilePartner, setProfilePartner] = useState(null);

  // ── Display field config ────────────────────────────────────────────────────
  const [displayCfg, setDisplayCfg] = useState({});
  useEffect(() => {
    (async () => {
      try {
        const { supabase } = await import('./supabaseClient');
        const { data } = await supabase
          .from('form_field_config')
          .select('is_visible, role_permissions, custom_field_definitions!inner(field_name, entity_type)')
          .eq('form_type', 'display')
          .eq('custom_field_definitions.entity_type', 'partner');
        if (data?.length) {
          const map = {};
          data.forEach(row => {
            map[row.custom_field_definitions.field_name] = {
              is_visible:       row.is_visible,
              role_permissions: row.role_permissions || {},
            };
          });
          setDisplayCfg(map);
        }
      } catch (_) {}
    })();
  }, []);
  const dv = (fn) => {
    if (displayCfg[fn]?.is_visible === false) return false;
    if (displayCfg[fn]?.role_permissions?.[currentUserRole] === false) return false;
    return true;
  };

  // "Drafts" swaps the list for unfinished New Partner wizards. They are held in
  // a separate collection, so the status filter doesn't apply to them.
  const showingDrafts = filter === 'drafts';

  const filteredPartners = filter === 'all' || showingDrafts
    ? partners
    : partners.filter(p => p.status === filter);

  // Sortable table: default ABC by partner name; resolvers normalise per column.
  const { sortedRows: sortedPartners, sortKey: pSortKey, sortDir: pSortDir, toggleSort: togglePartnerSort } =
    useSortableTable(filteredPartners, 'name', 'asc', {
      name:          p => (p.name || '').toLowerCase(),
      agreementType: p => (p.agreementType || '').toLowerCase(),
      status:        p => (p.status || '').toLowerCase(),
      endDate:       p => p.endDate ? new Date(p.endDate).getTime() : 0,
      lastActive:    p => { const v = lastActiveForPartner(p); return v ? new Date(v).getTime() : 0; },
    });

  const toggleExpand = (partnerId) => {
    setExpandedPartner(expandedPartner === partnerId ? null : partnerId);
  };

  // Helper function to get partner's prospects
  const getPartnerProspects = (partnerId) => {
    return prospects.filter(p => p.introducingPartnerId === partnerId);
  };

  // Calculate chart data
  const statusData = [
    { name: 'Active', value: partners.filter(p => p.status === 'Active').length, color: '#10B981' },
    { name: 'In Tail', value: partners.filter(p => p.status === 'In Tail').length, color: '#F59E0B' },
    { name: 'Terminated', value: partners.filter(p => p.status === 'Terminated').length, color: '#EF4444' }
  ];

  const agreementTypeData = [
    { name: 'Pool', count: partners.filter(p => p.agreementType === 'MCA - Pool').length },
    { name: 'Referral', count: partners.filter(p => p.agreementType === 'MCA - Referral').length },
    { name: 'Single Deal', count: partners.filter(p => p.agreementType === 'MCA - Single Deal').length },
    { name: 'Other', count: partners.filter(p => p.agreementType === 'Mutual Collaboration').length }
  ];

  // Partner growth over time (group by month)
  const growthData = React.useMemo(() => {
    if (partners.length === 0) return [];
    
    const monthMap = {};
    partners.forEach(partner => {
      if (partner.startDate) {
        const date = new Date(partner.startDate);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        monthMap[monthKey] = (monthMap[monthKey] || 0) + 1;
      }
    });
    
    let cumulative = 0;
    return Object.keys(monthMap).sort().map(month => {
      cumulative += monthMap[month];
      return {
        month: month.substring(5) + '/' + month.substring(2, 4),
        count: cumulative
      };
    }).slice(-12); // Last 12 months
  }, [partners]);

  // Upcoming renewals (next 3 months)
  const upcomingRenewals = React.useMemo(() => {
    const now = new Date();
    const threeMonthsFromNow = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000);
    return partners.filter(p => {
      const endDate = new Date(p.endDate);
      return endDate >= now && endDate <= threeMonthsFromNow && p.status === 'Active';
    }).length;
  }, [partners]);

  // Total prospects from partners
  const totalProspects = prospects.filter(p => p.introducingPartnerId).length;
  const approvedProspects = prospects.filter(p => p.introducingPartnerId && p.approvalStatus === 'Approved').length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <h2 className="text-2xl font-bold" style={{ color: BRAND_COLORS.navy }}>Partners Management</h2>
        <button
          onClick={onAdd}
          className="flex items-center space-x-2 px-4 py-2 text-white rounded-lg transition-all shadow-lg"
          style={{ backgroundColor: BRAND_COLORS.navy }}
          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
        >
          <Plus className="w-5 h-5" />
          <span>Add Partner</span>
        </button>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Total Partners',  value: partners.length,     icon: Users,    accent: BRAND_COLORS.gold },
          { label: 'Active',          value: statusData[0].value, icon: Check,    accent: BRAND_COLORS.statusSuccess },
          { label: 'Total Prospects', value: totalProspects,      icon: FileText, accent: BRAND_COLORS.statusInfo },
          { label: 'Renewals Due',    value: upcomingRenewals,    icon: Clock,    accent: BRAND_COLORS.statusWarning, sub: 'Next 90 days' },
        ].map(c => <KPICard key={c.label} {...c} />)}
      </div>

      {/* Filter */}
      <div className="flex space-x-2">
        <FilterButton active={filter === 'all'} onClick={() => setFilter('all')} label="All" />
        <FilterButton active={filter === 'Active'} onClick={() => setFilter('Active')} label="Active" />
        <FilterButton active={filter === 'In Tail'} onClick={() => setFilter('In Tail')} label="In Tail" />
        <FilterButton active={filter === 'Terminated'} onClick={() => setFilter('Terminated')} label="Terminated" />
        {/* Unfinished New Partner wizards parked with Save & Close. */}
        <FilterButton active={showingDrafts} onClick={() => setFilter('drafts')}
          label={`Drafts${draftPartners.length ? ` (${draftPartners.length})` : ''}`} />
      </div>

      {/* Drafts — resumed at the step they were left on */}
      {showingDrafts && (
        <DraftsPanel
          drafts={draftPartners}
          labelOf={d => d.name}
          stepLabels={['Agreement Type', 'Partner Info', 'Sector', 'Geography', 'Agreement Dates']}
          onResume={onResumeDraft}
          onDiscard={onDiscardDraft}
          emptyText="No unfinished partner drafts. Use Save & Close in the New Partner wizard to park one."
        />
      )}

      {/* Partners List — mobile cards (md:hidden) */}
      {!showingDrafts && <div className="md:hidden space-y-3">
        {sortedPartners.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center text-sm" style={{ color: BRAND_COLORS.textSecondary }}>
            No partners to show.
          </div>
        ) : sortedPartners.map(partner => {
          const now = new Date();
          const daysUntilEnd = partner.endDate ? Math.floor((new Date(partner.endDate) - now) / 86400000) : null;
          const statusColor = partner.status === 'Active' ? BRAND_COLORS.statusSuccess
            : partner.status === 'Terminated' ? BRAND_COLORS.statusError
            : partner.status === 'In Tail' ? BRAND_COLORS.statusInfo
            : BRAND_COLORS.textSecondary;
          const lastActive = lastActiveForPartner(partner);
          let lastActiveLabel = 'Never';
          if (lastActive) {
            const diffDays = Math.floor((Date.now() - new Date(lastActive).getTime()) / 86400000);
            if (diffDays === 0)       lastActiveLabel = 'Today';
            else if (diffDays === 1)  lastActiveLabel = 'Yesterday';
            else if (diffDays < 7)    lastActiveLabel = `${diffDays}d ago`;
            else if (diffDays < 30)   lastActiveLabel = `${Math.floor(diffDays/7)}w ago`;
            else if (diffDays < 365)  lastActiveLabel = `${Math.floor(diffDays/30)}mo ago`;
            else                      lastActiveLabel = `${Math.floor(diffDays/365)}y ago`;
          }
          return (
            <div
              key={partner.id}
              onClick={() => setProfilePartner(partner)}
              className="bg-white rounded-xl shadow-sm border p-4 active:bg-gray-50 cursor-pointer"
              style={{ borderColor: BRAND_COLORS.borderNavy12 }}
            >
              {/* Top row: avatar + name + status */}
              <div className="flex items-start gap-3 mb-3">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg, #CBD5E1 0%, #64748B 100%)' }}
                >
                  {partner.name?.charAt(0) || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate" style={{ color: BRAND_COLORS.navy }}>{partner.name}</p>
                  {dv('contact_person') && partner.contactPerson && (
                    <p className="text-xs truncate" style={{ color: BRAND_COLORS.textSecondary }}>{partner.contactPerson}</p>
                  )}
                  {dv('email') && partner.email && (
                    <p className="text-xs truncate" style={{ color: BRAND_COLORS.textTertiary }}>{partner.email}</p>
                  )}
                </div>
                <span
                  className="text-xs font-semibold whitespace-nowrap px-2 py-1 rounded-full"
                  style={{ color: statusColor, background: `${statusColor}18`, border: `1px solid ${statusColor}40` }}
                >
                  {partner.status}
                </span>
              </div>

              {/* Tag row: agreement + NDA */}
              <div className="flex flex-wrap gap-1.5 mb-2">
                {partner.agreementType && (
                  <span className="text-xs px-2 py-0.5 rounded-full"
                    style={{ background: `${BRAND_COLORS.navy}0D`, color: BRAND_COLORS.textSecondary, border: `1px solid ${BRAND_COLORS.borderNavy12}` }}>
                    {partner.agreementType}
                  </span>
                )}
                {dv('nda_signed') && partner.ndaSigned && (
                  <span className="text-xs px-2 py-0.5 rounded-full"
                    style={{ background: `${BRAND_COLORS.statusSuccess}18`, color: BRAND_COLORS.statusSuccess, border: `1px solid ${BRAND_COLORS.statusSuccess}40`, fontWeight: 600 }}>
                    ✓ {partner.ndaType || 'NDA'}
                  </span>
                )}
              </div>

              {/* Footer row: contract + last active */}
              <div className="flex items-center justify-between text-xs">
                <div style={{ color: BRAND_COLORS.textTertiary }}>
                  {partner.endDate ? (
                    <>
                      ends <span style={{ color: BRAND_COLORS.textSecondary, fontWeight: 500 }}>{formatDate(partner.endDate)}</span>
                      {partner.status === 'Active' && daysUntilEnd !== null && daysUntilEnd <= 90 && daysUntilEnd > 0 && (
                        <span className="ml-1 font-bold" style={{ color: '#F59E0B' }}>· {daysUntilEnd}d</span>
                      )}
                    </>
                  ) : '—'}
                </div>
                <div style={{ color: BRAND_COLORS.textTertiary }}>
                  Active: <span style={{ color: BRAND_COLORS.textSecondary }}>{lastActiveLabel}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>}

      {/* Partners List — desktop table (hidden on mobile) */}
      {!showingDrafts && <div className="hidden md:block bg-white rounded-lg shadow overflow-hidden overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead style={{ backgroundColor: BRAND_COLORS.bgSurface, borderBottom: `1px solid ${BRAND_COLORS.borderNavy12}` }}>
            <tr>
              <SortableTh columnKey="name"          sortKey={pSortKey} sortDir={pSortDir} onSort={togglePartnerSort}
                className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: BRAND_COLORS.textTertiary }}>Partner</SortableTh>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: BRAND_COLORS.textTertiary }}>Contact</th>
              <SortableTh columnKey="agreementType" sortKey={pSortKey} sortDir={pSortDir} onSort={togglePartnerSort}
                className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: BRAND_COLORS.textTertiary }}>Agreement</SortableTh>
              <SortableTh columnKey="status"        sortKey={pSortKey} sortDir={pSortDir} onSort={togglePartnerSort}
                className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: BRAND_COLORS.textTertiary }}>Status</SortableTh>
              <SortableTh columnKey="endDate"       sortKey={pSortKey} sortDir={pSortDir} onSort={togglePartnerSort}
                className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: BRAND_COLORS.textTertiary }}>Contract Dates</SortableTh>
              <SortableTh columnKey="lastActive"    sortKey={pSortKey} sortDir={pSortDir} onSort={togglePartnerSort}
                className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: BRAND_COLORS.textTertiary }}>Last Active</SortableTh>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: BRAND_COLORS.textTertiary }}>Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedPartners.map(partner => {
              const now = new Date();
              const endDate = new Date(partner.endDate);
              const tailEndDate = new Date(partner.tailEndDate);
              const daysUntilEnd = Math.floor((endDate - now) / (1000 * 60 * 60 * 24));
              const daysUntilTailEnd = Math.floor((tailEndDate - now) / (1000 * 60 * 60 * 24));
              const isExpanded = expandedPartner === partner.id;
              
              return (
                <React.Fragment key={partner.id}>
                  <tr 
                    className={`transition-all cursor-pointer ${
                      isExpanded 
                        ? 'bg-blue-50 hover:bg-blue-100' 
                        : 'hover:bg-gray-50'
                    }`}
                    onClick={() => toggleExpand(partner.id)}
                    title={isExpanded ? "Click to collapse details" : "Click to expand details"}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <div 
                          className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                          style={{ background: 'linear-gradient(135deg, #CBD5E1 0%, #64748B 100%)' }}
                        >
                          {partner.name.charAt(0)}
                        </div>
                        <div className="font-medium" style={{ color: BRAND_COLORS.navy }}>{partner.name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {dv('contact_person') && <div className="text-sm text-gray-900">{partner.contactPerson}</div>}
                      {dv('email') && <div className="text-sm text-gray-500">{partner.email}</div>}
                      {dv('phone') && <div className="text-sm text-gray-500">{partner.phone}</div>}
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm font-semibold whitespace-nowrap" style={{ color: BRAND_COLORS.navy }}>
                        {partner.agreementType}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1 items-start">
                        {(() => {
                          const color = partner.status === 'Active' ? BRAND_COLORS.statusSuccess
                            : partner.status === 'Terminated' ? BRAND_COLORS.statusError
                            : partner.status === 'In Tail' ? BRAND_COLORS.statusInfo
                            : BRAND_COLORS.textSecondary;
                          return <span className="text-sm font-semibold whitespace-nowrap" style={{ color }}>{partner.status}</span>;
                        })()}
                        {dv('nda_signed') && partner.ndaSigned && (
                          <span className="text-xs font-semibold whitespace-nowrap" style={{ color: '#15803D' }}>
                            ✓ {partner.ndaType || 'NDA'}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        {(dv('start_date') || dv('end_date')) && (
                          <div className="flex items-center gap-2 text-sm" style={{ color: BRAND_COLORS.navy }}>
                            <span className="font-medium">{partner.startDate ? formatDate(partner.startDate) : '—'}</span>
                            <ChevronRight className="w-3.5 h-3.5" style={{ color: BRAND_COLORS.textTertiary }} />
                            <span className="font-medium">{partner.endDate ? formatDate(partner.endDate) : '—'}</span>
                            {partner.status === 'Active' && daysUntilEnd <= 90 && daysUntilEnd > 0 && (
                              <span className="ml-1 text-xs font-bold whitespace-nowrap" style={{ color: '#F59E0B' }}>
                                {daysUntilEnd}d left
                              </span>
                            )}
                          </div>
                        )}
                        {dv('tail_months') && partner.tailEndDate && (
                          <div className="flex items-center gap-2 text-xs" style={{ color: BRAND_COLORS.textTertiary }}>
                            <span>Tail: <span style={{ color: BRAND_COLORS.gold }}>{formatDate(partner.tailEndDate)}</span></span>
                            {partner.status === 'In Tail' && (
                              <span className="text-xs font-bold whitespace-nowrap" style={{
                                color: daysUntilTailEnd <= 90 ? '#DC2626' : '#2563EB'
                              }}>
                                {daysUntilTailEnd}d left
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: BRAND_COLORS.textSecondary }}>
                      {(() => {
                        const v = lastActiveForPartner(partner);
                        if (!v) return <span className="text-gray-400">Never</span>;
                        const d = new Date(v);
                        const diffMs = Date.now() - d.getTime();
                        const diffDays = Math.floor(diffMs / 86400000);
                        let rel = '';
                        if (diffDays === 0)       rel = 'Today';
                        else if (diffDays === 1)  rel = 'Yesterday';
                        else if (diffDays < 7)    rel = `${diffDays}d ago`;
                        else if (diffDays < 30)   rel = `${Math.floor(diffDays/7)}w ago`;
                        else if (diffDays < 365)  rel = `${Math.floor(diffDays/30)}mo ago`;
                        else                       rel = `${Math.floor(diffDays/365)}y ago`;
                        return (
                          <div className="flex flex-col">
                            <span style={{ color: BRAND_COLORS.navy }}>{rel}</span>
                            <span className="text-xs" style={{ color: BRAND_COLORS.textTertiary }}>{formatDate(v)}</span>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); setProfilePartner(partner); }}
                          className="p-1.5 rounded-md transition-colors hover:bg-gray-100"
                          title="View Profile"
                        >
                          <Eye className="w-4 h-4" style={{ color: BRAND_COLORS.textSecondary }} />
                        </button>
                        <div className="p-1.5 rounded-md" title={isExpanded ? "Collapse" : "Expand"}>
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} />
                          ) : (
                            <ChevronDown className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} />
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                  
                  {/* Expanded Details Row */}
                  {isExpanded && (
                    <tr>
                      <td colSpan="7" className="px-6 py-4 bg-gray-50">
                        {/* Edit + Delete Buttons */}
                        <div className="flex justify-end gap-2 mb-4">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditPartner(partner);
                            }}
                            className="flex items-center space-x-2 px-4 py-2 rounded-lg transition-all hover:shadow-md"
                            style={{ backgroundColor: BRAND_COLORS.gold, color: 'white' }}
                          >
                            <Edit2 className="w-4 h-4" />
                            <span className="font-medium">Edit Partner</span>
                          </button>
                          {onDeletePartner && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                showConfirm(`Delete partner "${partner.name}"? This cannot be undone.`, () => onDeletePartner(partner.id), 'Delete');
                              }}
                              className="flex items-center space-x-2 px-4 py-2 rounded-lg transition-all hover:shadow-md"
                              style={{ backgroundColor: '#EF4444', color: 'white' }}
                            >
                              <Trash2 className="w-4 h-4" />
                              <span className="font-medium">Delete</span>
                            </button>
                          )}
                          {onRequestDelete && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onRequestDelete('Partner', partner.id, partner.name);
                              }}
                              className="flex items-center space-x-2 px-4 py-2 rounded-lg transition-all hover:shadow-md"
                              style={{ backgroundColor: '#FEF3C7', color: '#92400E', border: '1px solid #F59E0B' }}
                            >
                              <Trash2 className="w-4 h-4" />
                              <span className="font-medium">Request Delete</span>
                            </button>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          {/* Contact Details */}
                          <div className="space-y-3">
                            <h4 className="text-sm font-semibold uppercase tracking-wide flex items-center space-x-2" style={{ color: BRAND_COLORS.navy }}>
                              <Users className="w-4 h-4" />
                              <span>Contact</span>
                            </h4>
                            <div className="space-y-2 text-sm">
                              {partner.contactPerson && (
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Contact Person:</span>
                                  <span className="font-medium" style={{ color: BRAND_COLORS.navy }}>{partner.contactPerson}</span>
                                </div>
                              )}
                              {partner.email && (
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Email:</span>
                                  <a href={`mailto:${partner.email}`} className="font-medium hover:underline" style={{ color: BRAND_COLORS.gold }}>{partner.email}</a>
                                </div>
                              )}
                              {partner.phone && (
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Phone:</span>
                                  <span className="font-medium" style={{ color: BRAND_COLORS.navy }}>{partner.phone}</span>
                                </div>
                              )}
                              {!partner.contactPerson && !partner.email && !partner.phone && (
                                <p className="text-gray-400 text-xs italic">No contact details</p>
                              )}
                            </div>
                          </div>

                          {/* Activity Tracking */}
                          <div className="space-y-3">
                            <h4 className="text-sm font-semibold uppercase tracking-wide flex items-center space-x-2" style={{ color: BRAND_COLORS.navy }}>
                              <Activity className="w-4 h-4" />
                              <span>Activity</span>
                            </h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Last Login:</span>
                                <span className="font-medium" style={{ color: BRAND_COLORS.navy }}>
                                  {formatDate(partner.lastLoginDate)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Last Activity:</span>
                                <span className="font-medium" style={{ color: BRAND_COLORS.navy }}>
                                  {formatDate(partner.lastActivityDate)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Created:</span>
                                <span className="font-medium" style={{ color: BRAND_COLORS.navy }}>{formatDate(partner.createdAt)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Modified By:</span>
                                <span className="font-medium" style={{ color: BRAND_COLORS.navy }}>{partner.lastModifiedBy || '—'}</span>
                              </div>
                            </div>
                          </div>

                          {/* NDA & Commission */}
                          <div className="space-y-3">
                            <h4 className="text-sm font-semibold uppercase tracking-wide flex items-center space-x-2" style={{ color: BRAND_COLORS.navy }}>
                              <FileText className="w-4 h-4" />
                              <span>Agreement & NDA</span>
                            </h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Tail Period:</span>
                                <span className="font-medium" style={{ color: BRAND_COLORS.navy }}>{partner.tailMonths ? `${partner.tailMonths} months` : '—'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">NDA:</span>
                                <span className="font-medium" style={{ color: partner.ndaSigned ? '#15803D' : BRAND_COLORS.navy }}>
                                  {partner.ndaSigned ? `✓ ${partner.ndaType || 'Signed'}` : 'Not signed'}
                                </span>
                              </div>
                              {partner.ndaSigned && partner.ndaSignedDate && (
                                <div className="flex justify-between">
                                  <span className="text-gray-600">NDA Date:</span>
                                  <span className="font-medium" style={{ color: BRAND_COLORS.navy }}>{formatDate(partner.ndaSignedDate)}</span>
                                </div>
                              )}
                              {partner.commissionTerms?.type && (
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Commission:</span>
                                  <span className="font-medium capitalize" style={{ color: BRAND_COLORS.navy }}>{partner.commissionTerms.type}</span>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Notes */}
                          {partner.notes && (
                            <div className="space-y-2 md:col-span-3 pt-4 border-t border-gray-200">
                              <h4 className="text-sm font-semibold uppercase tracking-wide flex items-center space-x-2" style={{ color: BRAND_COLORS.navy }}>
                                <FileText className="w-4 h-4" />
                                <span>Notes</span>
                              </h4>
                              <p className="text-sm text-gray-700 whitespace-pre-line">{partner.notes}</p>
                            </div>
                          )}

                          {/* Sectors of Interest */}
                          {dv('sectors') && (partner.sectors?.length > 0) && (
                            <div className="space-y-3 md:col-span-3 pt-4 border-t border-gray-200">
                              <h4 className="text-sm font-semibold uppercase tracking-wide flex items-center space-x-2" style={{ color: BRAND_COLORS.navy }}>
                                <BarChart3 className="w-4 h-4" />
                                <span>Sectors of Interest</span>
                              </h4>
                              <div className="flex flex-wrap gap-2">
                                {partner.sectors.map(s => (
                                  <span key={s} className="px-3 py-1 text-xs font-medium rounded-full text-white" style={{ backgroundColor: BRAND_COLORS.navy }}>
                                    {s}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Geographic Focus */}
                          {dv('geography') && (partner.geography?.continents?.length > 0 || partner.geography?.countries?.length > 0) && (
                            <div className="space-y-3 md:col-span-3 pt-4 border-t border-gray-200">
                              <h4 className="text-sm font-semibold uppercase tracking-wide flex items-center space-x-2" style={{ color: BRAND_COLORS.navy }}>
                                <Building2 className="w-4 h-4" />
                                <span>Geographic Focus</span>
                              </h4>
                              {partner.geography.continents?.length > 0 && (
                                <div className="mb-2">
                                  <p className="text-xs text-gray-500 mb-1 font-medium">Continents</p>
                                  <div className="flex flex-wrap gap-2">
                                    {partner.geography.continents.map(c => (
                                      <span key={c} className="px-3 py-1 text-xs font-medium rounded-full text-white" style={{ backgroundColor: BRAND_COLORS.gold }}>
                                        {c}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {partner.geography.countries?.length > 0 && (
                                <div>
                                  <p className="text-xs text-gray-500 mb-1 font-medium">{partner.geography.countries.length} countries</p>
                                  <div className="flex flex-wrap gap-1">
                                    {partner.geography.countries.map(c => (
                                      <span key={c} className="px-2 py-0.5 text-xs rounded border" style={{ color: BRAND_COLORS.navy, borderColor: BRAND_COLORS.navy + '40', backgroundColor: BRAND_COLORS.cream }}>
                                        {c}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Partner's Prospects */}
                          <div className="space-y-3 md:col-span-3 pt-4 border-t border-gray-200">
                            <h4 className="text-sm font-semibold uppercase tracking-wide flex items-center space-x-2" style={{ color: BRAND_COLORS.navy }}>
                              <Building2 className="w-4 h-4" />
                              <span>Prospects Submitted ({getPartnerProspects(partner.id).length})</span>
                            </h4>
                            
                            {getPartnerProspects(partner.id).length === 0 ? (
                              <div className="text-center py-6 text-gray-500 text-sm">
                                <Building2 className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                                <p>No prospects submitted yet</p>
                              </div>
                            ) : (
                              <div className="space-y-2">
                                {getPartnerProspects(partner.id).map(prospect => (
                                  <div 
                                    key={prospect.id}
                                    className="bg-white rounded-lg p-4 border hover:shadow-md transition-shadow"
                                    style={{ borderLeft: `3px solid ${
                                      prospect.approvalStatus === 'Approved' ? '#10B981' :
                                      (prospect.approvalStatus === 'Pending' || prospect.approvalStatus === 'Waiting for Approval') ? '#F59E0B' :
                                      '#EF4444'
                                    }` }}
                                  >
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <div className="flex items-center flex-wrap gap-2 mb-1">
                                          <h5 className="font-semibold text-sm" style={{ color: BRAND_COLORS.navy }}>
                                            {prospect.companyName}
                                          </h5>
                                          <span 
                                            className="px-2 py-0.5 text-xs font-medium rounded"
                                            style={{ 
                                              backgroundColor: 
                                                prospect.approvalStatus === 'Approved' ? '#D1FAE5' :
                                                (prospect.approvalStatus === 'Pending' || prospect.approvalStatus === 'Waiting for Approval') ? '#FEF3C7' :
                                                '#FEE2E2',
                                              color:
                                                prospect.approvalStatus === 'Approved' ? '#065F46' :
                                                (prospect.approvalStatus === 'Pending' || prospect.approvalStatus === 'Waiting for Approval') ? '#92400E' :
                                                '#991B1B'
                                            }}
                                          >
                                            {prospect.approvalStatus}
                                          </span>
                                        </div>
                                        
                                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600 mt-2">
                                          {prospect.investorName && (
                                            <div className="flex items-center space-x-1">
                                              <span className="font-medium">Investor:</span>
                                              <span>{prospect.investorName}</span>
                                            </div>
                                          )}
                                          {prospect.sector && (
                                            <div className="flex items-center space-x-1">
                                              <span className="font-medium">Sector:</span>
                                              <span>{prospect.sector}</span>
                                            </div>
                                          )}
                                          {prospect.country && (
                                            <div className="flex items-center space-x-1">
                                              <span className="font-medium">Country:</span>
                                              <span>{prospect.country}</span>
                                            </div>
                                          )}
                                          <div className="flex items-center space-x-1">
                                            <span className="font-medium">Submitted:</span>
                                            <span>{formatDate(prospect.approvalRequestDate)}</span>
                                          </div>
                                          
                                          {prospect.approvalStatus === 'Approved' && prospect.approvalDate && (
                                            <div className="flex items-center space-x-1">
                                              <span className="font-medium">Approved:</span>
                                              <span>{formatDate(prospect.approvalDate)}</span>
                                            </div>
                                          )}
                                          
                                          {prospect.approvalStatus === 'Approved' && prospect.introDeadline && (
                                            <div className="flex items-center space-x-1">
                                              <span className="font-medium">Intro Deadline:</span>
                                              <span className={
                                                new Date(prospect.introDeadline) < new Date() 
                                                  ? 'text-red-600 font-semibold' 
                                                  : 'text-gray-600'
                                              }>
                                                {formatDate(prospect.introDeadline)}
                                              </span>
                                            </div>
                                          )}
                                          
                                          {prospect.introOccurred && prospect.introDate && (
                                            <div className="flex items-center space-x-1 text-green-600">
                                              <Check className="w-3 h-3" />
                                              <span className="font-medium">Intro on {formatDate(prospect.introDate)}</span>
                                            </div>
                                          )}
                                        </div>
                                        
                                        {prospect.description && (
                                          <p className="text-xs text-gray-500 mt-2 line-clamp-2">
                                            {prospect.description}
                                          </p>
                                        )}
                                      </div>
                                      
                                      {/* Edit Button */}
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onEditProspect(prospect);
                                        }}
                                        className="ml-3 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                                        title="Edit prospect"
                                      >
                                        <Edit2 className="w-4 h-4" style={{ color: BRAND_COLORS.navy }} />
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>}

      {/* Partner Profile Drawer */}
      {profilePartner && (
        <PartnerProfileDrawer
          partner={profilePartner}
          prospects={prospects}
          revenueDeals={revenueDeals}
          onClose={() => setProfilePartner(null)}
          onEdit={onEditPartner}
          currentUserRole={currentUserRole}
        />
      )}
    </div>
  );
};

// ============================================================================
// COMPONENT: ProspectProfileDrawer
// ============================================================================

const ProspectProfileDrawer = ({ prospect, partners = [], revenueDeals = [], teasers = [], onClose, onEdit, onToggleHidden, onArchive, currentUserRole }) => {
  const { isMobile } = useIsMobile();
  const [activeTab, setActiveTab] = useState('overview');

  const formatDate = (d) => {
    if (!d) return 'N/A';
    const date = new Date(d);
    return isNaN(date) ? 'N/A' : date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const pType = prospect?.prospectType || prospect?.type || '';

  const introducingPartner = partners.find(p => p.id === prospect?.introducingPartnerId);

  const prospectDeals = revenueDeals.filter(d =>
    d.prospectId === prospect?.id || d.investorProspectId === prospect?.id
  );

  const prospectTeasers = teasers.filter(t => t.prospectId === prospect?.id);

  const typeColors = {
    'Client':          { bg: '#DBEAFE', text: '#1E40AF' },
    'Angel':           { bg: '#FEF3C7', text: '#92400E' },
    'InvestmentBody':  { bg: '#F3E8FF', text: '#6B21A8' },
    'Connection':      { bg: '#F0FDF4', text: '#14532D' },
  };
  const typeColor = typeColors[pType] || { bg: '#F3F4F6', text: '#374151' };

  const approvalColor = prospect?.approvalStatus === 'Approved'
    ? { bg: '#D1FAE5', text: '#065F46' }
    : prospect?.approvalStatus === 'Rejected'
    ? { bg: '#FEE2E2', text: '#991B1B' }
    : { bg: '#FEF3C7', text: '#92400E' };

  const dealStatusColor = (status) => {
    if (!status) return { bg: '#F3F4F6', text: '#6B7280' };
    if (status === 'Closed' || status === 'Won') return { bg: '#D1FAE5', text: '#065F46' };
    if (status === 'Lost') return { bg: '#FEE2E2', text: '#991B1B' };
    return { bg: '#DBEAFE', text: '#1E40AF' };
  };

  const showFinancials = ['Client', 'Angel', 'InvestmentBody'].includes(pType);

  const tabs = [
    { key: 'overview',   label: 'Overview',   icon: Users },
    ...(showFinancials ? [{ key: 'financials', label: 'Financials', icon: DollarSign }] : []),
    { key: 'deals',      label: 'Deals',      icon: Layers,    count: prospectDeals.length },
    { key: 'teasers',    label: 'Teasers',    icon: FileText,  count: prospectTeasers.length },
    ...(introducingPartner ? [{ key: 'partner', label: 'Partner', icon: Users }] : []),
    { key: 'activity',   label: 'Activity',   icon: Activity },
  ];

  const currency = prospect?.investmentCurrency || 'USD';
  const currencySymbol = CURRENCIES.find(c => c.code === currency)?.symbol || '$';
  const fmtMoney = (val) => val ? `${currencySymbol}${Number(val).toLocaleString('en-US')}` : '—';

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-30 z-40" onClick={onClose} />

      {/* Drawer */}
      <div
        className="fixed z-50 flex flex-col shadow-2xl overflow-hidden"
        style={isMobile ? {
          inset: 0,
          width: '100%',
          backgroundColor: '#F9FAFB',
          borderTop: `4px solid ${BRAND_COLORS.gold}`,
          borderRadius: 0,
        } : {
          right: '12px', top: '12px', bottom: '12px',
          width: '62%',
          backgroundColor: '#F9FAFB',
          border: '1px solid #E5E7EB',
          borderLeft: `4px solid ${BRAND_COLORS.gold}`,
          borderRadius: '16px',
        }}
      >
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-4 flex items-center gap-4" style={{ background: BRAND_COLORS.bgHeader }}>
          <div className="w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold text-white flex-shrink-0"
            style={{ backgroundColor: BRAND_COLORS.gold }}>
            {(prospect?.name || prospect?.companyName || '?')[0].toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl font-bold text-white truncate">{prospect?.name || prospect?.companyName}</h2>
              {pType && (
                <span className="px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0"
                  style={{ backgroundColor: typeColor.bg, color: typeColor.text }}>
                  {pType}
                </span>
              )}
            </div>
            {prospect?.approvalStatus && (
              <span className="mt-1 inline-block px-2 py-0.5 rounded-full text-xs font-semibold"
                style={{ backgroundColor: approvalColor.bg, color: approvalColor.text }}>
                {prospect.approvalStatus}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {onToggleHidden && (
              <button
                onClick={() => onToggleHidden(prospect.id, !prospect.hiddenFromPartners)}
                title={prospect.hiddenFromPartners ? 'Show to partners' : 'Hide from partners'}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all hover:opacity-90"
                style={prospect.hiddenFromPartners
                  ? { backgroundColor: 'rgba(255,255,255,0.18)', color: 'white', border: '1px solid rgba(255,255,255,0.35)' }
                  : { backgroundColor: 'transparent', color: 'rgba(255,255,255,0.85)', border: '1px solid rgba(255,255,255,0.25)' }}
              >
                {prospect.hiddenFromPartners ? '🙈 Hidden' : 'Hide from Partners'}
              </button>
            )}
            {onArchive && (
              <button
                onClick={() => onArchive(prospect.id, !prospect.archived)}
                title={prospect.archived ? 'Restore from archive' : 'Archive this prospect'}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all hover:opacity-90"
                style={prospect.archived
                  ? { backgroundColor: 'rgba(255,255,255,0.18)', color: 'white', border: '1px solid rgba(255,255,255,0.35)' }
                  : { backgroundColor: 'transparent', color: 'rgba(255,255,255,0.85)', border: '1px solid rgba(255,255,255,0.25)' }}
              >
                {prospect.archived ? '♻ Unarchive' : '🗄 Archive'}
              </button>
            )}
            {onEdit && (
              <button
                onClick={() => { onEdit(prospect); onClose(); }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all hover:opacity-90"
                style={{ backgroundColor: BRAND_COLORS.gold, color: BRAND_COLORS.navy }}
              >
                <Edit2 className="w-3.5 h-3.5" />
                Edit
              </button>
            )}
            <button onClick={onClose} className="text-white hover:text-gray-300 transition-colors">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Quick Stats Strip */}
        <div className="flex-shrink-0 px-6 py-2.5 flex items-center gap-6 border-b" style={{ backgroundColor: `${BRAND_COLORS.navy}08`, borderColor: '#E5E7EB' }}>
          <div className="flex items-center gap-1.5">
            <Layers className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-xs text-gray-500">Deals</span>
            <span className="text-xs font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: BRAND_COLORS.navy, color: 'white' }}>{prospectDeals.length}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <FileText className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-xs text-gray-500">Teasers</span>
            <span className="text-xs font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: BRAND_COLORS.navy, color: 'white' }}>{prospectTeasers.length}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-xs text-gray-500">Source</span>
            <span className="text-xs font-semibold" style={{ color: BRAND_COLORS.navy }}>{introducingPartner?.name || 'Direct'}</span>
          </div>
          {prospect?.contractType && (
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-gray-500">Contract</span>
              <span className="text-xs font-semibold" style={{ color: BRAND_COLORS.navy }}>{prospect.contractType}</span>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex-shrink-0 flex border-b border-gray-200 bg-white overflow-x-auto">
          {tabs.map(({ key, label, icon: Icon, count }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className="flex items-center gap-1.5 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors"
              style={{
                borderBottomColor: activeTab === key ? BRAND_COLORS.navy : 'transparent',
                color: activeTab === key ? BRAND_COLORS.navy : '#6B7280',
              }}
            >
              <Icon className="w-4 h-4" />
              {label}
              {count !== undefined && (
                <span className="px-1.5 py-0.5 rounded-full text-xs"
                  style={{ backgroundColor: activeTab === key ? BRAND_COLORS.navy : '#E5E7EB', color: activeTab === key ? 'white' : '#374151' }}>
                  {count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">

          {/* ── OVERVIEW ── */}
          {activeTab === 'overview' && (
            <>
              {/* Contact details — only render card if there's something to show */}
              {(prospect?.email || prospect?.phone || prospect?.websiteUrl || prospect?.linkedinPerson || prospect?.linkedinCompany) ? (
                <div className="bg-white rounded-xl p-5 space-y-3" style={{ border: '1px solid #E5E7EB', borderLeft: `4px solid ${BRAND_COLORS.navy}` }}>
                  <h3 className="text-sm font-semibold uppercase tracking-wide" style={{ color: BRAND_COLORS.navy }}>Contact</h3>
                  <div className="space-y-2.5">
                    {prospect?.email && (
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-400 w-28 flex-shrink-0">Email</span>
                        <a href={`mailto:${prospect.email}`} className="text-sm font-medium hover:underline truncate" style={{ color: '#4169E1' }}>{prospect.email}</a>
                      </div>
                    )}
                    {prospect?.phone && (
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-400 w-28 flex-shrink-0">Phone</span>
                        <span className="text-sm font-medium" style={{ color: BRAND_COLORS.navy }}>{prospect.phone}</span>
                      </div>
                    )}
                    {prospect?.websiteUrl && (
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-400 w-28 flex-shrink-0">Website</span>
                        <a href={prospect.websiteUrl} target="_blank" rel="noopener noreferrer" className="text-sm font-medium hover:underline truncate" style={{ color: '#4169E1' }}>{prospect.websiteUrl}</a>
                      </div>
                    )}
                    {prospect?.linkedinPerson && (
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-400 w-28 flex-shrink-0">LinkedIn (Person)</span>
                        <a href={prospect.linkedinPerson} target="_blank" rel="noopener noreferrer" className="text-sm font-medium hover:underline" style={{ color: '#4169E1' }}>View Profile ↗</a>
                      </div>
                    )}
                    {prospect?.linkedinCompany && (
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-400 w-28 flex-shrink-0">LinkedIn (Co.)</span>
                        <a href={prospect.linkedinCompany} target="_blank" rel="noopener noreferrer" className="text-sm font-medium hover:underline" style={{ color: '#4169E1' }}>View Page ↗</a>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-xl p-5 flex items-center gap-3" style={{ border: '1px solid #E5E7EB' }}>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#F3F4F6' }}>
                    <Users className="w-4 h-4 text-gray-400" />
                  </div>
                  <p className="text-sm text-gray-400">No contact details on file — click <span className="font-medium" style={{ color: BRAND_COLORS.navy }}>Edit</span> to add them.</p>
                </div>
              )}

              {prospect?.sectors?.length > 0 && (
                <div className="bg-white rounded-xl p-5 space-y-3" style={{ border: '1px solid #E5E7EB' }}>
                  <h3 className="text-sm font-semibold uppercase tracking-wide" style={{ color: BRAND_COLORS.navy }}>Sectors</h3>
                  <div className="flex flex-wrap gap-2">
                    {prospect.sectors.map(s => (
                      <span key={s} className="px-3 py-1 text-xs font-medium rounded-full text-white" style={{ backgroundColor: BRAND_COLORS.navy }}>{s}</span>
                    ))}
                  </div>
                </div>
              )}

              {(() => {
                const geo = prospect?.geography || {};
                const continents = geo.continents || [];
                const countries = geo.countries || [];
                if (!continents.length && !countries.length) return null;
                return (
                  <div className="bg-white rounded-xl p-5 space-y-4" style={{ border: '1px solid #E5E7EB' }}>
                    <h3 className="text-sm font-semibold uppercase tracking-wide" style={{ color: BRAND_COLORS.navy }}>Geography</h3>
                    {continents.length > 0 && (
                      <div>
                        <p className="text-xs text-gray-400 mb-2">Continents</p>
                        <div className="flex flex-wrap gap-2">
                          {continents.map(c => (
                            <span key={c} className="px-3 py-1 text-xs font-medium rounded-full" style={{ backgroundColor: `${BRAND_COLORS.navy}15`, color: BRAND_COLORS.navy }}>{c}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {countries.length > 0 && (
                      <div>
                        <p className="text-xs text-gray-400 mb-2">Countries ({countries.length})</p>
                        <div className="flex flex-wrap gap-2">
                          {countries.map(c => (
                            <span key={c} className="px-2.5 py-0.5 text-xs rounded-full border" style={{ borderColor: '#D1D5DB', color: '#374151' }}>{c}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {prospect?.notes && (
                <div className="bg-white rounded-xl p-5 space-y-3" style={{ border: '1px solid #E5E7EB' }}>
                  <h3 className="text-sm font-semibold uppercase tracking-wide" style={{ color: BRAND_COLORS.navy }}>Notes</h3>
                  <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">{prospect.notes}</p>
                </div>
              )}
            </>
          )}

          {/* ── FINANCIALS ── */}
          {activeTab === 'financials' && showFinancials && (
            <>
              {pType === 'Client' && (
                <>
                  {/* Hero number: Target Raise */}
                  {prospect?.targetRaise && (
                    <div className="bg-white rounded-xl p-5" style={{ border: `1px solid ${BRAND_COLORS.gold}50`, borderLeft: `4px solid ${BRAND_COLORS.gold}` }}>
                      <p className="text-xs text-gray-400 mb-1 uppercase tracking-wide">Target Raise</p>
                      <p className="text-3xl font-bold" style={{ color: BRAND_COLORS.navy }}>{fmtMoney(prospect.targetRaise)}</p>
                      {prospect?.stage && (
                        <span className="mt-2 inline-block px-2.5 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: `${BRAND_COLORS.navy}12`, color: BRAND_COLORS.navy }}>
                          {prospect.stage}
                        </span>
                      )}
                    </div>
                  )}
                  {/* Secondary metrics */}
                  {(prospect?.currentValuation || prospect?.revenue) && (
                    <div className="grid grid-cols-2 gap-3">
                      {prospect?.currentValuation && (
                        <div className="bg-white rounded-xl p-4" style={{ border: '1px solid #E5E7EB', borderLeft: `4px solid ${BRAND_COLORS.navy}` }}>
                          <p className="text-xs text-gray-400 mb-1 uppercase tracking-wide">Valuation</p>
                          <p className="text-xl font-bold" style={{ color: BRAND_COLORS.navy }}>{fmtMoney(prospect.currentValuation)}</p>
                        </div>
                      )}
                      {prospect?.revenue && (
                        <div className="bg-white rounded-xl p-4" style={{ border: '1px solid #E5E7EB', borderLeft: '4px solid #10B981' }}>
                          <p className="text-xs text-gray-400 mb-1 uppercase tracking-wide">Revenue</p>
                          <p className="text-xl font-bold" style={{ color: '#065F46' }}>{fmtMoney(prospect.revenue)}</p>
                        </div>
                      )}
                    </div>
                  )}
                  {/* Uploaded deck file (PDF/PPT/PPTX) — preferred over legacy URL.
                      Opens via signed URL (private bucket — direct URL would 400). */}
                  {prospect?.investorDeckFileUrl && (
                    <button type="button" onClick={() => openSignedFile(prospect.investorDeckFileUrl)}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all hover:opacity-90 cursor-pointer"
                      style={{ backgroundColor: BRAND_COLORS.navy, color: 'white', border: 'none' }}>
                      <FileText className="w-4 h-4" />
                      <span>{prospect.investorDeckFileName || 'Open Investor Deck'}</span>
                      {prospect.investorDeckFileSize > 0 && (
                        <span style={{ opacity: 0.7, fontSize: '11px' }}>
                          · {(prospect.investorDeckFileSize / 1024 / 1024).toFixed(2)} MB
                        </span>
                      )}
                      <span style={{ opacity: 0.7 }}>↗</span>
                    </button>
                  )}
                  {/* Legacy URL — only show if no uploaded file (back-compat) */}
                  {!prospect?.investorDeckFileUrl && prospect?.investorsDeckUrl && (
                    <a href={prospect.investorsDeckUrl} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all hover:opacity-90"
                      style={{ backgroundColor: BRAND_COLORS.navy, color: 'white' }}>
                      <FileText className="w-4 h-4" />
                      Open Investor Deck ↗
                    </a>
                  )}
                </>
              )}
              {(pType === 'Angel' || pType === 'InvestmentBody') && (
                <>
                  {(prospect?.investmentMin || prospect?.investmentMax) && (
                    <div className="bg-white rounded-xl p-5" style={{ border: `1px solid ${BRAND_COLORS.gold}50`, borderLeft: `4px solid ${BRAND_COLORS.gold}` }}>
                      <p className="text-xs text-gray-400 mb-1 uppercase tracking-wide">Investment Range</p>
                      <p className="text-2xl font-bold" style={{ color: BRAND_COLORS.navy }}>
                        {fmtMoney(prospect.investmentMin)} – {fmtMoney(prospect.investmentMax)}
                      </p>
                      {prospect?.preferredStage && (
                        <span className="mt-2 inline-block px-2.5 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: `${BRAND_COLORS.navy}12`, color: BRAND_COLORS.navy }}>
                          Preferred stage: {prospect.preferredStage}
                        </span>
                      )}
                    </div>
                  )}
                  {(prospect?.investmentFocus || (pType === 'InvestmentBody' && prospect?.investmentBodyType)) && (
                    <div className="bg-white rounded-xl p-5 space-y-3" style={{ border: '1px solid #E5E7EB', borderLeft: `4px solid ${BRAND_COLORS.navy}` }}>
                      <h3 className="text-sm font-semibold uppercase tracking-wide" style={{ color: BRAND_COLORS.navy }}>Investment Profile</h3>
                      {prospect?.investmentFocus && (
                        <div>
                          <p className="text-xs text-gray-400 mb-0.5">Investment Focus</p>
                          <p className="text-sm font-medium" style={{ color: BRAND_COLORS.navy }}>{prospect.investmentFocus}</p>
                        </div>
                      )}
                      {pType === 'InvestmentBody' && prospect?.investmentBodyType && (
                        <div>
                          <p className="text-xs text-gray-400 mb-0.5">Body Type</p>
                          <p className="text-sm font-medium" style={{ color: BRAND_COLORS.navy }}>{prospect.investmentBodyType}</p>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* ── DEALS ── */}
          {activeTab === 'deals' && (
            prospectDeals.length === 0
              ? <div className="text-center py-12 text-gray-400 text-sm">No deals linked to this prospect</div>
              : <div className="space-y-3">
                  {prospectDeals.map(deal => {
                    const colors = dealStatusColor(deal.status);
                    const totals = computeDealTotals(deal);
                    const accentColor = deal.status === 'Closed' || deal.status === 'Won' ? '#10B981'
                      : deal.status === 'Lost' ? '#EF4444'
                      : BRAND_COLORS.navy;
                    return (
                      <div key={deal.id} className="bg-white rounded-xl p-4" style={{ border: '1px solid #E5E7EB', borderLeft: `4px solid ${accentColor}` }}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-semibold text-sm truncate" style={{ color: BRAND_COLORS.navy }}>{deal.dealName || 'Unnamed Deal'}</p>
                            {deal.dealType && <p className="text-xs text-gray-400 mt-0.5">{deal.dealType}</p>}
                          </div>
                          {deal.status && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0"
                              style={{ backgroundColor: colors.bg, color: colors.text }}>
                              {deal.status}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 mt-3">
                          {deal.totalAgreed > 0 && (
                            <div>
                              <p className="text-xs text-gray-400">Total</p>
                              <p className="text-base font-bold" style={{ color: BRAND_COLORS.navy }}>
                                {deal.currency || '$'}{Number(deal.totalAgreed).toLocaleString()}
                              </p>
                            </div>
                          )}
                          {totals.companyKeeps > 0 && (
                            <div>
                              <p className="text-xs text-gray-400">Net to EvE</p>
                              <p className="text-base font-bold" style={{ color: BRAND_COLORS.gold }}>
                                {deal.currency || '$'}{Number(totals.companyKeeps).toLocaleString()}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
          )}

          {/* ── TEASERS ── */}
          {activeTab === 'teasers' && (
            prospectTeasers.length === 0
              ? <div className="text-center py-12 text-gray-400 text-sm">No teasers linked to this prospect</div>
              : <div className="space-y-3">
                  {prospectTeasers.map(t => (
                    <div key={t.id} className="bg-white rounded-xl p-4" style={{ border: '1px solid #E5E7EB', borderLeft: `4px solid ${BRAND_COLORS.gold}` }}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-sm truncate" style={{ color: BRAND_COLORS.navy }}>{t.name || t.fileName || 'Unnamed'}</p>
                          {t.alias && (
                            <p className="text-xs mt-0.5" style={{ color: BRAND_COLORS.gold }}>
                              Alias shown to partners: <span className="font-medium">{t.alias}</span>
                            </p>
                          )}
                        </div>
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0"
                          style={{
                            backgroundColor: t.visibility === 'all' ? '#D1FAE5' : '#DBEAFE',
                            color: t.visibility === 'all' ? '#065F46' : '#1E40AF',
                          }}>
                          {t.visibility === 'all' ? 'All Partners' : 'Specific'}
                        </span>
                      </div>
                      {t.sectors?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {t.sectors.map(s => (
                            <span key={s} className="px-2 py-0.5 text-xs rounded-full" style={{ backgroundColor: `${BRAND_COLORS.navy}15`, color: BRAND_COLORS.navy }}>{s}</span>
                          ))}
                        </div>
                      )}
                      {t.fileUrl && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <button
                            onClick={() => openSignedFile(t.fileUrl)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:opacity-90"
                            style={{ backgroundColor: BRAND_COLORS.navy, color: 'white' }}
                          >
                            <Eye className="w-3.5 h-3.5" />
                            View PDF
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
          )}

          {/* ── PARTNER ── */}
          {activeTab === 'partner' && introducingPartner && (
            <div className="bg-white rounded-xl p-5 space-y-4" style={{ border: '1px solid #E5E7EB' }}>
              <h3 className="text-sm font-semibold uppercase tracking-wide" style={{ color: BRAND_COLORS.navy }}>Introducing Partner</h3>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold text-white flex-shrink-0"
                  style={{ backgroundColor: BRAND_COLORS.gold }}>
                  {(introducingPartner.name || '?')[0].toUpperCase()}
                </div>
                <div>
                  <p className="font-semibold text-sm" style={{ color: BRAND_COLORS.navy }}>{introducingPartner.name}</p>
                  {introducingPartner.agreementType && (
                    <p className="text-xs text-gray-400">{introducingPartner.agreementType}</p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {introducingPartner.contactPerson && (
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Contact Person</p>
                    <p className="font-medium" style={{ color: BRAND_COLORS.navy }}>{introducingPartner.contactPerson}</p>
                  </div>
                )}
                {introducingPartner.email && (
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Email</p>
                    <a href={`mailto:${introducingPartner.email}`} className="font-medium hover:underline" style={{ color: '#4169E1' }}>{introducingPartner.email}</a>
                  </div>
                )}
                {introducingPartner.status && (
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Status</p>
                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold"
                      style={{
                        backgroundColor: introducingPartner.status === 'Active' ? '#D1FAE5' : introducingPartner.status === 'In Tail' ? '#FEF3C7' : '#FEE2E2',
                        color: introducingPartner.status === 'Active' ? '#065F46' : introducingPartner.status === 'In Tail' ? '#92400E' : '#991B1B',
                      }}>
                      {introducingPartner.status}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── ACTIVITY ── */}
          {activeTab === 'activity' && (
            <div className="bg-white rounded-xl p-5 space-y-4" style={{ border: '1px solid #E5E7EB' }}>
              <h3 className="text-sm font-semibold uppercase tracking-wide" style={{ color: BRAND_COLORS.navy }}>Activity</h3>
              <div className="space-y-3 text-sm">
                {[
                  { label: 'Submitted',      value: formatDate(prospect?.submittedAt) },
                  { label: 'Approved',       value: formatDate(prospect?.approvalDate) },
                  { label: 'Intro Deadline', value: formatDate(prospect?.introDeadline) },
                  { label: 'Created',        value: formatDate(prospect?.createdAt) },
                  { label: 'Last Modified',  value: prospect?.lastModifiedBy || 'N/A' },
                ].map(({ label, value }) => (
                  <div key={label} className="flex justify-between items-center py-2 border-b border-gray-50 last:border-0">
                    <span className="text-gray-500">{label}</span>
                    <span className="font-medium" style={{ color: BRAND_COLORS.navy }}>{value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
};

// ============================================================================
// VIEW: Prospects
// ============================================================================

const ProspectsView = ({ prospects, partners, users = [], onAdd, onApprove, onReject, onLogIntro, onEditProspect, onDeleteProspect, onRequestDelete, onToggleHidden, onArchive, role, teasers = [], revenueDeals = [], showConfirm, currentUserRole = 'Admin', onAskQuestion, onShowHowTo, draftProspects = [], onResumeDraft, onDiscardDraft }) => {
  const [typeTab, setTypeTab] = useState('All');
  const [filter, setFilter] = useState('all');
  // Editor+ visibility filter — Active (default), Hidden, All. Partners never
  // see hidden rows anyway (RLS), so this control is hidden for that role.
  const [visibilityFilter, setVisibilityFilter] = useState('visible');
  const [archivedFilter, setArchivedFilter] = useState('active'); // 'active' | 'archived' (staff only)
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [profileProspect, setProfileProspect] = useState(null);

  // ── Display field config ────────────────────────────────────────────────────
  const [displayCfg, setDisplayCfg] = useState({});
  useEffect(() => {
    (async () => {
      try {
        const { supabase } = await import('./supabaseClient');
        const { data } = await supabase
          .from('form_field_config')
          .select('is_visible, role_permissions, custom_field_definitions!inner(field_name, entity_type)')
          .eq('form_type', 'display')
          .eq('custom_field_definitions.entity_type', 'prospect');
        if (data?.length) {
          const map = {};
          data.forEach(row => {
            map[row.custom_field_definitions.field_name] = {
              is_visible:       row.is_visible,
              role_permissions: row.role_permissions || {},
            };
          });
          setDisplayCfg(map);
        }
      } catch (_) {}
    })();
  }, []);
  const dv = (fn) => {
    if (displayCfg[fn]?.is_visible === false) return false;
    if (displayCfg[fn]?.role_permissions?.[currentUserRole] === false) return false;
    return true;
  };

  const typeTabs = [
    { key: 'All',           label: 'All',                icon: Layers,    color: BRAND_COLORS.navy },
    { key: 'Client',        label: 'Clients',            icon: Building2, color: BRAND_COLORS.navy },
    { key: 'Angel',         label: 'Angel Investors',    icon: Star,      color: '#7c3aed' },
    { key: 'InvestmentBody',label: 'Investment Bodies',  icon: Landmark,  color: '#0369a1' },
    { key: 'Connection',    label: 'Connections',        icon: Link,      color: '#065f46' }
  ];

  const typeFiltered = typeTab === 'All'
    ? prospects
    : prospects.filter(p => p.prospectType === typeTab);

  const statusFiltered = filter === 'all'
    ? typeFiltered
    : typeFiltered.filter(p => p.approvalStatus === filter);

  // "Drafts" shows unfinished New Prospect wizards instead of the live list.
  // They are a separate collection (never mixed into `prospects`), so the
  // type/status/visibility filters below don't apply to them.
  const showingDrafts = archivedFilter === 'drafts';

  // Archived filter — default hides archived. Both staff and partners can
  // switch to "Archived" to review (partners see only their own, via the
  // already-filtered prospects list + DB RLS). Only staff can un/archive.
  const archivedFiltered = archivedFilter === 'archived'
    ? statusFiltered.filter(p => p.archived === true)
    : statusFiltered.filter(p => p.archived !== true);

  // Editor+ visibility filter (Partner ignores this — they never see hidden).
  const visibilityFiltered = (role === 'Partner' || visibilityFilter === 'all')
    ? archivedFiltered
    : visibilityFilter === 'hidden'
      ? archivedFiltered.filter(p => p.hiddenFromPartners === true)
      : archivedFiltered.filter(p => p.hiddenFromPartners !== true);

  const filteredProspects = showingDrafts ? draftProspects : visibilityFiltered;

  // Sortable: ABC by company name. Partner column resolves to the introducing
  // partner's display name so it sorts alphabetically by partner.
  const { sortedRows: sortedProspects, sortKey: prSortKey, sortDir: prSortDir, toggleSort: toggleProspectSort } =
    useSortableTable(filteredProspects, 'name', 'asc', {
      name:           p => (p.companyName || p.name || '').toLowerCase(),
      introducingPartner: p => {
        const partner = partners.find(x => x.id === p.introducingPartnerId);
        return (partner?.name || '').toLowerCase();
      },
      approvalStatus: p => (p.approvalStatus || '').toLowerCase(),
      approvalRequestDate: p => p.approvalRequestDate ? new Date(p.approvalRequestDate).getTime() : 0,
    });

  // Calculate chart data
  const statusData = [
    { name: 'Waiting', value: prospects.filter(p => p.approvalStatus === 'Waiting for Approval').length, color: '#F59E0B' },
    { name: 'Approved', value: prospects.filter(p => p.approvalStatus === 'Approved').length, color: '#10B981' },
    { name: 'Rejected', value: prospects.filter(p => p.approvalStatus === 'Rejected').length, color: '#EF4444' }
  ];

  // Prospects by sector
  const sectorData = React.useMemo(() => {
    if (prospects.length === 0) return [];
    
    const sectorMap = {};
    prospects.forEach(p => {
      if (p.sector) {
        sectorMap[p.sector] = (sectorMap[p.sector] || 0) + 1;
      }
    });
    return Object.entries(sectorMap)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);
  }, [prospects]);

  // Approval timeline (last 6 months)
  const approvalTimeline = React.useMemo(() => {
    if (prospects.length === 0) return [];
    
    const monthMap = {};
    prospects.forEach(prospect => {
      if (prospect.approvalDate) {
        const date = new Date(prospect.approvalDate);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        monthMap[monthKey] = (monthMap[monthKey] || 0) + 1;
      }
    });
    
    return Object.keys(monthMap).sort().map(month => ({
      month: month.substring(5) + '/' + month.substring(2, 4),
      count: monthMap[month]
    })).slice(-6);
  }, [prospects]);

  // Intro success metrics
  const approvedProspects = prospects.filter(p => p.approvalStatus === 'Approved');
  const withIntro = approvedProspects.filter(p => p.introOccurred).length;
  const introSuccessRate = approvedProspects.length > 0 
    ? Math.round((withIntro / approvedProspects.length) * 100) 
    : 0;

  // Average time to approval
  const avgTimeToApproval = React.useMemo(() => {
    const approved = prospects.filter(p => p.approvalDate && p.approvalRequestDate);
    if (approved.length === 0) return 0;
    
    const totalDays = approved.reduce((sum, p) => {
      const request = new Date(p.approvalRequestDate);
      const approval = new Date(p.approvalDate);
      return sum + Math.floor((approval - request) / (1000 * 60 * 60 * 24));
    }, 0);
    
    return Math.round(totalDays / approved.length);
  }, [prospects]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <h2 className="text-2xl font-bold text-gray-900">Prospects Management</h2>
        <div className="flex items-center gap-3">
          {role === 'Partner' && onShowHowTo && (
            <button
              onClick={onShowHowTo}
              className="inline-flex items-center gap-1.5 text-sm font-medium hover:underline"
              style={{ color: BRAND_COLORS.gold }}
              title="Guided walkthrough of the prospect form"
            >
              <HelpCircle className="w-4 h-4" /> Show me how
            </button>
          )}
          <button
            onClick={onAdd}
            className="flex items-center space-x-2 px-4 py-2 text-white rounded-lg transition-colors shadow-lg"
            style={{ backgroundColor: BRAND_COLORS.navy }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
          >
            <Plus className="w-5 h-5" />
            <span>{role === 'Partner' ? 'Submit Prospect' : 'New Prospect'}</span>
          </button>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Total Prospects', value: prospects.length,       icon: FileText,   accent: BRAND_COLORS.gold },
          { label: 'Pending',         value: statusData[0].value,    icon: Clock,      accent: BRAND_COLORS.statusWarning, sub: 'Awaiting approval' },
          { label: 'Intro Success',   value: `${introSuccessRate}%`, icon: TrendingUp, accent: BRAND_COLORS.statusSuccess, sub: `${withIntro} / ${approvedProspects.length}` },
          { label: 'Avg. Approval',   value: avgTimeToApproval,      icon: Zap,        accent: BRAND_COLORS.statusInfo,    sub: 'days' },
        ].map(c => <KPICard key={c.label} {...c} />)}
      </div>

      {/* Type Tabs */}
      <div className="flex space-x-1 border-b border-gray-200 overflow-x-auto">
        {typeTabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => { setTypeTab(tab.key); setFilter('all'); }}
            className="px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-all inline-flex items-center gap-1.5"
            style={{
              borderColor: typeTab === tab.key ? tab.color : 'transparent',
              color: typeTab === tab.key ? tab.color : '#6b7280'
            }}
          >
            <tab.icon className="w-3.5 h-3.5" />
            {tab.label}
            <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-xs"
              style={{
                backgroundColor: typeTab === tab.key ? `${tab.color}20` : '#f3f4f6',
                color: typeTab === tab.key ? tab.color : '#6b7280'
              }}>
              {tab.key === 'All' ? prospects.length : prospects.filter(p => p.prospectType === tab.key).length}
            </span>
          </button>
        ))}
      </div>

      {/* Status Filter */}
      <div className="flex flex-wrap gap-2">
        <FilterButton active={filter === 'all'} onClick={() => setFilter('all')} label="All" />
        <FilterButton active={filter === 'Waiting for Approval'} onClick={() => setFilter('Waiting for Approval')} label="Waiting" />
        <FilterButton active={filter === 'Approved'} onClick={() => setFilter('Approved')} label="Approved" />
        <FilterButton active={filter === 'Rejected'} onClick={() => setFilter('Rejected')} label="Rejected" />
      </div>

      {/* Filters: visibility (staff only) + active/archived (all roles) */}
      <div className="flex items-center gap-2 text-xs flex-wrap" style={{ color: BRAND_COLORS.textTertiary }}>
        <span style={{ letterSpacing: '0.04em', textTransform: 'uppercase', fontWeight: 600 }}>Show:</span>
        {role !== 'Partner' && (
          <>
            {[
              { key: 'visible', label: 'Visible to Partners' },
              { key: 'hidden',  label: 'Hidden from Partners' },
              { key: 'all',     label: 'All' },
            ].map(o => (
              <button key={o.key} onClick={() => setVisibilityFilter(o.key)}
                className="px-2.5 py-1 rounded-full border transition-colors"
                style={visibilityFilter === o.key
                  ? { background: BRAND_COLORS.navy, color: 'white', borderColor: BRAND_COLORS.navy }
                  : { background: 'white', color: BRAND_COLORS.textSecondary, borderColor: BRAND_COLORS.borderNavy12 }}>
                {o.label}
              </button>
            ))}
            <span className="mx-1" style={{ opacity: 0.4 }}>·</span>
          </>
        )}
        {[
          { key: 'active',   label: 'Active' },
          { key: 'archived', label: 'Archived' },
          // Unfinished New Prospect wizards parked with Save & Close.
          { key: 'drafts',   label: `Drafts${draftProspects.length ? ` (${draftProspects.length})` : ''}` },
        ].map(o => (
          <button key={o.key} onClick={() => setArchivedFilter(o.key)}
            className="px-2.5 py-1 rounded-full border transition-colors"
            style={archivedFilter === o.key
              ? { background: BRAND_COLORS.gold, color: BRAND_COLORS.navy, borderColor: BRAND_COLORS.gold }
              : { background: 'white', color: BRAND_COLORS.textSecondary, borderColor: BRAND_COLORS.borderNavy12 }}>
            {o.label}
          </button>
        ))}
      </div>

      {/* Drafts — unfinished New Prospect wizards, resumed at their saved step */}
      {showingDrafts && (
        <DraftsPanel
          drafts={draftProspects}
          labelOf={d => d.companyName || d.name}
          stepLabels={['Prospect Type', 'Basic Info', 'Sector', 'Geography', 'Details']}
          onResume={onResumeDraft}
          onDiscard={onDiscardDraft}
          emptyText="No unfinished prospect drafts. Use Save & Close in the New Prospect wizard to park one."
        />
      )}

      {/* Prospects List — mobile cards (md:hidden) */}
      {!showingDrafts && <div className="md:hidden space-y-3">
        {sortedProspects.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center text-sm" style={{ color: BRAND_COLORS.textSecondary }}>
            No prospects to show.
          </div>
        ) : sortedProspects.map(prospect => {
          const status = prospect.approvalStatus || 'Pending';
          const statusColor = status === 'Approved' ? BRAND_COLORS.statusSuccess
            : status === 'Rejected' ? BRAND_COLORS.statusError
            : status === 'Waiting for Approval' || status === 'Pending' ? BRAND_COLORS.statusWarning
            : BRAND_COLORS.textSecondary;
          const statusLabel = status === 'Waiting for Approval' ? 'Waiting' : status;
          const introducing = partners.find(x => x.id === prospect.introducingPartnerId);
          return (
            <div
              key={prospect.id}
              onClick={() => setProfileProspect(prospect)}
              className="bg-white rounded-xl shadow-sm border p-4 active:bg-gray-50 cursor-pointer"
              style={{ borderColor: BRAND_COLORS.borderNavy12 }}
            >
              {/* Top row: company name + status */}
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate" style={{ color: BRAND_COLORS.navy }}>
                    {prospect.companyName || prospect.name}
                  </p>
                  <p className="text-xs mt-0.5" style={{ color: BRAND_COLORS.textTertiary }}>
                    {prospect.prospectType || 'Client'}
                    {introducing ? ` · via ${introducing.name}` : prospect.contractType === 'Direct' ? ' · Direct' : ''}
                  </p>
                </div>
                <span
                  className="text-xs font-semibold whitespace-nowrap px-2 py-1 rounded-full"
                  style={{ color: statusColor, background: `${statusColor}18`, border: `1px solid ${statusColor}40` }}
                >
                  {statusLabel}
                </span>
              </div>

              {/* Sector / geography chips */}
              {(prospect.sectors?.length > 0 || prospect.geography?.continents?.length > 0) && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {(prospect.sectors || []).slice(0, 2).map(s => (
                    <span key={s} className="text-xs px-2 py-0.5 rounded-full"
                      style={{ background: `${BRAND_COLORS.navy}0D`, color: BRAND_COLORS.textSecondary, border: `1px solid ${BRAND_COLORS.borderNavy12}` }}>
                      {s}
                    </span>
                  ))}
                  {(prospect.geography?.continents || []).slice(0, 2).map(c => (
                    <span key={c} className="text-xs px-2 py-0.5 rounded-full"
                      style={{ background: `${BRAND_COLORS.navy}0D`, color: BRAND_COLORS.textSecondary, border: `1px solid ${BRAND_COLORS.borderNavy12}` }}>
                      🌍 {c}
                    </span>
                  ))}
                </div>
              )}

              {/* Footer: dates */}
              <div className="flex items-center justify-between text-xs" style={{ color: BRAND_COLORS.textTertiary }}>
                <span>Submitted <span style={{ color: BRAND_COLORS.textSecondary }}>{formatDate(prospect.approvalRequestDate || prospect.createdAt)}</span></span>
                {status === 'Approved' && prospect.introDeadline && (
                  <span style={{ color: BRAND_COLORS.statusWarning, fontWeight: 600 }}>
                    Intro by {formatDate(prospect.introDeadline)}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>}

      {/* Prospects List — desktop table (hidden on mobile) */}
      {!showingDrafts && <div className="hidden md:block bg-white rounded-lg shadow overflow-hidden overflow-x-auto">
        {filteredProspects.length === 0 ? (
          <div className="text-center py-16">
            <div style={{ color: '#1A1C5C', opacity: 0.55, marginBottom: 16, display: 'flex', justifyContent: 'center' }}>
              <NauticalMark name="lighthouse" size={80} />
            </div>
            <p className="text-gray-700 text-sm font-medium">No prospects to show</p>
            <p className="text-gray-400 text-xs mt-1">Submit a new prospect or change the filters above.</p>
          </div>
        ) : (
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <SortableTh columnKey="name"               sortKey={prSortKey} sortDir={prSortDir} onSort={toggleProspectSort}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</SortableTh>
              <SortableTh columnKey="introducingPartner" sortKey={prSortKey} sortDir={prSortDir} onSort={toggleProspectSort}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Introduced By</SortableTh>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created By</th>
              <SortableTh columnKey="approvalStatus"     sortKey={prSortKey} sortDir={prSortDir} onSort={toggleProspectSort}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</SortableTh>
              <SortableTh columnKey="approvalRequestDate" sortKey={prSortKey} sortDir={prSortDir} onSort={toggleProspectSort}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dates</SortableTh>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Intro</th>
              {(role === 'Admin' || role === 'Editor' || role === 'Super Admin') && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              )}
              {role === 'Partner' && onEditProspect && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Edit</th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedProspects.map(prospect => (
              <tr key={prospect.id} className="hover:bg-gray-50" style={prospect.hiddenFromPartners ? { opacity: 0.6 } : {}}>
                <td className="px-6 py-4">
                  <div className="font-medium text-gray-900 flex items-center gap-2">
                    {prospect.companyName || prospect.name}
                    {prospect.hiddenFromPartners && role !== 'Partner' && (
                      <span style={{ padding: '1px 7px', borderRadius: '999px', fontSize: '10px', fontWeight: 700, background: `${BRAND_COLORS.textTertiary}1A`, color: BRAND_COLORS.textTertiary, border: `1px solid ${BRAND_COLORS.borderNavy12}`, letterSpacing: '0.04em' }}>
                        HIDDEN
                      </span>
                    )}
                  </div>
                  {prospect.prospectType && (
                    <div className="mt-0.5 flex flex-wrap gap-1">
                      <span className="inline-flex items-center gap-1 text-xs font-semibold"
                        style={{
                          color:
                            prospect.prospectType === 'Angel' ? '#7c3aed' :
                            prospect.prospectType === 'InvestmentBody' ? '#0369a1' :
                            prospect.prospectType === 'Connection' ? '#065f46' : BRAND_COLORS.navy
                        }}>
                        {prospect.prospectType === 'Angel' ? <Star className="w-3 h-3" /> :
                         prospect.prospectType === 'InvestmentBody' ? <Landmark className="w-3 h-3" /> :
                         prospect.prospectType === 'Connection' ? <Link className="w-3 h-3" /> :
                         <Building2 className="w-3 h-3" />}
                        {prospect.prospectType === 'Angel' ? 'Angel' :
                         prospect.prospectType === 'InvestmentBody' ? (prospect.investmentBodyType || 'Investment Body') :
                         prospect.prospectType === 'Connection' ? 'Connection' : 'Client'}
                      </span>
                      {dv('contract_type') && prospect.contractType && prospect.contractType !== 'Direct' && (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold"
                          style={{ color: '#92400E' }}>
                          <Link className="w-3 h-3" />Through Partner
                        </span>
                      )}
                      {dv('service_types') && prospect.prospectType === 'Client' && prospect.serviceTypes?.length > 0 && (
                        <span className="ml-1 text-xs text-gray-500">{prospect.serviceTypes.join(' · ')}</span>
                      )}
                    </div>
                  )}
                  {dv('sectors') && prospect.sector && (
                    <div className="text-xs text-gray-400 mt-0.5">{prospect.sector}{dv('geography') && prospect.country ? ` • ${prospect.country}` : ''}</div>
                  )}
                  {(() => {
                    const linkedTeasers = teasers.filter(t => t.prospectId === prospect.id || (prospect.teaserIds || []).includes(t.id));
                    return linkedTeasers.length > 0 ? (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {linkedTeasers.map(t => (
                          <span key={t.id} className="inline-flex items-center gap-1 text-xs font-semibold text-purple-700">
                            <FileText className="w-3 h-3" />{t.name || t.companyName}
                          </span>
                        ))}
                      </div>
                    ) : null;
                  })()}
                  {(() => {
                    const linkedDeals = revenueDeals.filter(d => d.prospectId === prospect.id);
                    if (linkedDeals.length === 0) return null;
                    return (
                      <div className="mt-1.5 space-y-0.5">
                        {linkedDeals.map(d => (
                          <div key={d.id} className="inline-flex items-center gap-1 text-xs font-semibold mr-1"
                            style={{ color: '#065F46' }}>
                            <DollarSign className="w-3 h-3" />{d.dealName}
                            {d.dealState && (
                              <span className="opacity-70 font-normal">· {d.dealState}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                  {prospect.introducingPartnerId ? (partners.find(pt => pt.id === prospect.introducingPartnerId)?.name || 'Partner') : 'Direct'}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                  {prospect.createdByUserId ? (users.find(u => u.id === prospect.createdByUserId)?.name || '—') : (prospect.introducedBy || '—')}
                </td>
                <td className="px-6 py-4">
                  {(() => {
                    const s = prospect.approvalStatus;
                    const color = s === 'Approved' ? BRAND_COLORS.statusSuccess
                      : s === 'Rejected' ? BRAND_COLORS.statusError
                      : BRAND_COLORS.statusWarning;
                    return <span className="text-sm font-semibold whitespace-nowrap" style={{ color }}>{s}</span>;
                  })()}
                </td>
                <td className="px-6 py-4 text-xs">
                  {prospect.approvalDate ? (
                    <div className="space-y-1 whitespace-nowrap">
                      <div><span className="text-gray-400">Approved</span> <span className="text-gray-700 font-medium">{formatDate(prospect.approvalDate)}</span></div>
                      <div><span className="text-gray-400">Intro by</span> <span className="text-gray-700 font-medium">{formatDate(prospect.introDeadline)}</span></div>
                      <div><span className="text-gray-400">Valid until</span> <span className="text-gray-700 font-medium">{formatDate(prospect.validityEndDate)}</span></div>
                    </div>
                  ) : (prospect.approvalStatus === 'Pending' || prospect.approvalStatus === 'Waiting for Approval') ? (
                    <div className="whitespace-nowrap" style={{ color: BRAND_COLORS.statusWarning }}>
                      <span className="text-gray-400">Auto-reject</span> <span className="font-medium">{formatDate(prospect.autoRejectDeadline)}</span>
                    </div>
                  ) : null}
                </td>
                <td className="px-6 py-4">
                  {prospect.introOccurred ? (
                    <div className="text-sm">
                      <div className="flex items-center text-green-600">
                        <Check className="w-4 h-4 mr-1" />
                        Completed
                      </div>
                      <div className="text-xs text-gray-500">{formatDate(prospect.introDate)}</div>
                    </div>
                  ) : prospect.approvalStatus === 'Approved' ? (
                    <button
                      onClick={() => setSelectedProspect(prospect)}
                      className="text-sm text-[#1A2C5C] hover:text-indigo-900"
                    >
                      Log Intro
                    </button>
                  ) : (
                    <span className="text-sm text-gray-400">N/A</span>
                  )}
                </td>
                {role === 'Partner' && onAskQuestion && (
                  <td className="px-4 py-4">
                    <button
                      onClick={() => onAskQuestion({ category: 'prospect', refId: prospect.id, refName: prospect.companyName || prospect.name || 'Prospect' })}
                      className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                      title="Ask a question about this prospect"
                    >
                      <MessageCircle className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} />
                    </button>
                  </td>
                )}
                {(role === 'Admin' || role === 'Editor' || role === 'Super Admin') && (
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); setProfileProspect(prospect); }}
                        className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                        title="View profile"
                      >
                        <Eye className="w-4 h-4" style={{ color: BRAND_COLORS.navy }} />
                      </button>
                      {(prospect.approvalStatus === 'Waiting for Approval' || prospect.approvalStatus === 'Pending') && (
                        <>
                          <button
                            onClick={() => onApprove(prospect.id)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                            title="Approve"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => onReject(prospect.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                            title="Reject"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      {onEditProspect && (
                        <button
                          onClick={() => onEditProspect(prospect)}
                          className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      )}
                      {(role === 'Admin' || role === 'Super Admin') && onDeleteProspect && (
                        <button
                          onClick={() => {
                            showConfirm(`Delete prospect "${prospect.companyName || prospect.name}"?`, () => onDeleteProspect(prospect.id), 'Delete');
                          }}
                          className="p-2 text-red-400 hover:bg-red-50 rounded-lg"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                      {role === 'Editor' && onRequestDelete && (
                        <button
                          onClick={() => onRequestDelete('Prospect', prospect.id, prospect.companyName || prospect.name)}
                          className="p-2 hover:bg-yellow-50 rounded-lg"
                          title="Request delete"
                          style={{ color: '#92400E' }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                )}
                {role === 'Partner' && (
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); setProfileProspect(prospect); }}
                        className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                        title="View profile"
                      >
                        <Eye className="w-4 h-4" style={{ color: BRAND_COLORS.navy }} />
                      </button>
                      {onEditProspect && (
                        <button
                          onClick={() => onEditProspect(prospect)}
                          className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        )}
      </div>}

      {/* Intro Modal */}
      {selectedProspect && (
        <IntroModal
          prospect={selectedProspect}
          onClose={() => setSelectedProspect(null)}
          onSubmit={(date) => {
            onLogIntro(selectedProspect.id, date);
            setSelectedProspect(null);
          }}
        />
      )}

      {/* Prospect Profile Drawer */}
      {profileProspect && (
        <ProspectProfileDrawer
          prospect={profileProspect}
          partners={partners}
          revenueDeals={revenueDeals}
          teasers={teasers}
          onClose={() => setProfileProspect(null)}
          onEdit={onEditProspect}
          onToggleHidden={onToggleHidden}
          onArchive={onArchive}
          currentUserRole={currentUserRole}
        />
      )}
    </div>
  );
};

// ============================================================================
// VIEW: Teasers
// ============================================================================
// ============================================================================
// TEASERS VIEW - FIXED VERSION
// ============================================================================

// ─── COMPONENT: TeaserThumbnail ─────────────────────────────────────────────
// Renders the first page of a PDF as a small preview image using PDF.js.
// Lazy-loads the library on first mount; shows a spinner while rendering,
// falls back to a FileText icon on any error (CORS, bad URL, etc.).
const TeaserThumbnail = ({ fileUrl }) => {
  const canvasRef = useRef(null);
  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'error'

  useEffect(() => {
    if (!fileUrl) { setStatus('error'); return; }
    let cancelled = false;
    setStatus('loading');

    (async () => {
      try {
        // Bucket is private — request a short-lived signed URL before
        // pdf.js fetches the file. Data URLs (legacy) pass through unchanged.
        let renderUrl = fileUrl;
        if (typeof fileUrl === 'string' && !fileUrl.startsWith('data:')) {
          try { renderUrl = await getSignedFileUrl(fileUrl); }
          catch { /* fall through with raw url; render likely fails -> error state */ }
        }
        const { getDocument, GlobalWorkerOptions } = await import('pdfjs-dist');
        if (!GlobalWorkerOptions.workerSrc) {
          GlobalWorkerOptions.workerSrc = (process.env.PUBLIC_URL || '') + '/pdf.worker.min.mjs';
        }
        const pdf = await getDocument({ url: renderUrl, withCredentials: false }).promise;
        if (cancelled) return;
        const page = await pdf.getPage(1);
        if (cancelled || !canvasRef.current) return;

        const viewport = page.getViewport({ scale: 1 });
        const dpr = window.devicePixelRatio || 1;
        const TARGET_W = 72;
        const scale = (TARGET_W / viewport.width) * dpr;
        const scaled = page.getViewport({ scale });

        const canvas = canvasRef.current;
        // Physical pixel dimensions for crisp rendering
        canvas.width  = scaled.width;
        canvas.height = scaled.height;
        // CSS dimensions at logical pixel size (DPR-compensated)
        canvas.style.width  = TARGET_W + 'px';
        canvas.style.height = Math.round(TARGET_W * (viewport.height / viewport.width)) + 'px';

        await page.render({ canvasContext: canvas.getContext('2d'), viewport: scaled }).promise;
        if (!cancelled) setStatus('ready');
      } catch (e) {
        if (!cancelled) setStatus('error');
      }
    })();

    return () => { cancelled = true; };
  }, [fileUrl]);

  const box = {
    width: 72, minWidth: 72, flexShrink: 0, alignSelf: 'flex-start',
    borderRadius: '6px', overflow: 'hidden',
    border: `1px solid ${BRAND_COLORS.borderNavy12}`,
    background: BRAND_COLORS.bgApp,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    // Fixed fallback height (A4 ratio) shown only during loading/error states
    minHeight: 101,
  };

  if (status === 'error') return (
    <div style={box}>
      <FileText style={{ width: 28, height: 28, color: BRAND_COLORS.gold }} />
    </div>
  );

  return (
    <div style={box}>
      {status === 'loading' && (
        <div className="animate-spin" style={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${BRAND_COLORS.gold}`, borderTopColor: 'transparent' }} />
      )}
      <canvas ref={canvasRef} style={{ display: status === 'ready' ? 'block' : 'none' }} />
    </div>
  );
};

const TeasersView = ({ teasers, partners, prospects = [], deals = [], role, sectors = DEFAULT_SECTORS, currentUserProp, onDeleteTeaser, onRequestDelete, onUpdateTeaser, onAddTeaser, onMarkSeen, showConfirm, onEdit, onToggleHidden, autoOpenUpload = false, onConsumeAutoOpen, onAskQuestion }) => {
  const [teaserVisibilityFilter, setTeaserVisibilityFilter] = useState('visible');
  const theme = BRAND_COLORS;
  const currentUser = currentUserProp || { name: 'Admin User', id: 'admin' };
  const userId = currentUser.partnerId || currentUser.id;

  // ── Display field config ────────────────────────────────────────────────────
  const [displayCfg, setDisplayCfg] = useState({});
  useEffect(() => {
    (async () => {
      try {
        const { supabase } = await import('./supabaseClient');
        const { data } = await supabase
          .from('form_field_config')
          .select('is_visible, role_permissions, custom_field_definitions!inner(field_name, entity_type)')
          .eq('form_type', 'display')
          .eq('custom_field_definitions.entity_type', 'teaser');
        if (data?.length) {
          const map = {};
          data.forEach(row => {
            map[row.custom_field_definitions.field_name] = {
              is_visible:       row.is_visible,
              role_permissions: row.role_permissions || {},
            };
          });
          setDisplayCfg(map);
        }
      } catch (_) {}
    })();
  }, []);
  const dv = (fn) => {
    if (displayCfg[fn]?.is_visible === false) return false;
    if (displayCfg[fn]?.role_permissions?.[role] === false) return false;
    return true;
  };

  const isNew = (teaser) => {
    if (role !== 'Partner') return false;
    const seenBy = teaser.seenBy || [];
    return !seenBy.includes(userId);
  };

  const markAsSeen = (teaser) => {
    if (role !== 'Partner') return;
    if (!isNew(teaser)) return;
    const updatedTeasers = localTeasers.map(t =>
      t.id === teaser.id ? { ...t, seenBy: [...(t.seenBy || []), userId] } : t
    );
    if (onMarkSeen) {
      onMarkSeen(teaser.id, updatedTeasers);
    }
    setLocalTeasers(updatedTeasers);
  };
  
  const [localTeasers, setLocalTeasers] = useState([]);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadWizardStep, setUploadWizardStep] = useState(1);

  const [uploadForm, setUploadForm] = useState({
    name: '',
    alias: '',
    description: '',
    prospectId: '',
    targetRaise: '',
    raiseCurrency: 'USD',
    stage: '',
    _rawFile: null,
    fileName: '',
    fileSize: 0
  });
  const [uploadSectors, setUploadSectors] = useState([]);
  const [uploadGeo, setUploadGeo] = useState({ continents: [], countries: [] });
  const [uploadSectorSource, setUploadSectorSource] = useState('manual');
  const [uploadGeoSource, setUploadGeoSource] = useState('manual');
  const [uploadVisibility, setUploadVisibility] = useState('all');
  const [uploadSelectedPartners, setUploadSelectedPartners] = useState([]);
  const [uploadFilterSectors, setUploadFilterSectors] = useState([]);
  // Sync with parent state (source of truth)
  useEffect(() => {
    if (teasers && teasers.length > 0) {
      setLocalTeasers(teasers);
    }
  }, [teasers]);

  // Auto-open upload wizard when triggered from elsewhere (e.g. hamburger "New Teaser")
  useEffect(() => {
    if (autoOpenUpload) {
      setUploadModalOpen(true);
      onConsumeAutoOpen?.();
    }
  }, [autoOpenUpload, onConsumeAutoOpen]);

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== 'application/pdf') { window.alert('Please select a PDF file'); return; }
    if (file.size > 10 * 1024 * 1024) { window.alert('File size must be less than 10MB'); return; }
    setUploadForm(prev => ({
      ...prev,
      _rawFile: file,
      fileName: file.name,
      fileSize: file.size,
      name: prev.name || file.name.replace('.pdf', '')
    }));
  };

  // Handle upload
  const handleUpload = async () => {
    if (!uploadForm.prospectId) { window.alert('Please select an associated prospect'); return; }
    if (!uploadForm.name.trim()) { window.alert('Please enter a name for the teaser'); return; }
    if (!uploadForm._rawFile) { window.alert('Please select a PDF file'); return; }

    const teaserId = Date.now().toString();
    let fileUrl = null;
    try {
      const { uploadTeaserFile } = await import('./supabaseClient');
      fileUrl = await uploadTeaserFile(teaserId, uploadForm._rawFile);
    } catch (err) {
      console.error('[handleUpload] storage upload failed:', err);
      window.alert('File upload failed: ' + err.message);
      return;
    }

    const linkedProspect = prospects.find(p => p.id === uploadForm.prospectId);
    let visibilityValue;
    if (uploadVisibility === 'all') {
      visibilityValue = 'all';
    } else if (uploadVisibility === 'sectors') {
      visibilityValue = partners
        .filter(p => p.status === 'Active' && (p.sectors || []).some(s => uploadFilterSectors.includes(s)))
        .map(p => p.id);
    } else {
      visibilityValue = uploadSelectedPartners;
    }
    const newTeaser = {
      id: teaserId,
      name: uploadForm.name.trim(),
      alias: uploadForm.alias.trim(),
      description: uploadForm.description.trim(),
      prospectId: uploadForm.prospectId,
      prospectName: linkedProspect?.companyName || linkedProspect?.investorName || '—',
      sectors: uploadSectors,
      geography: uploadGeo,
      targetRaise: parseFloat(uploadForm.targetRaise) || null,
      raiseCurrency: uploadForm.raiseCurrency || 'USD',
      stage: uploadForm.stage.trim() || null,
      visibility: visibilityValue,
      fileName: uploadForm.fileName,
      fileSize: uploadForm.fileSize,
      fileUrl,
      uploadedBy: currentUser.name,
      uploadedById: currentUser.id,
      uploadDate: new Date().toISOString()
    };

    const updatedTeasers = [...localTeasers, newTeaser];
    if (onAddTeaser) onAddTeaser(newTeaser, updatedTeasers);
    setLocalTeasers(updatedTeasers);

    setUploadForm({ name: '', alias: '', description: '', prospectId: '', targetRaise: '', raiseCurrency: 'USD', stage: '', _rawFile: null, fileName: '', fileSize: 0 });
    setUploadSectors([]);
    setUploadGeo({ continents: [], countries: [] });
    setUploadSectorSource('manual');
    setUploadGeoSource('manual');
    setUploadVisibility('all');
    setUploadSelectedPartners([]);
    setUploadFilterSectors([]);
    setUploadWizardStep(1);
    setUploadModalOpen(false);
    window.alert('Teaser uploaded successfully!');
  };

  // Handle delete
  const handleDelete = (teaser) => {
    if (role === 'Editor' && onRequestDelete) {
      onRequestDelete('Teaser', teaser.id, teaser.name);
      return;
    }
    showConfirm(`Delete "${teaser.name}"?`, () => {
      const updatedTeasers = localTeasers.filter(t => t.id !== teaser.id);
      if (onDeleteTeaser) {
        onDeleteTeaser(teaser.id, updatedTeasers);
      }
      setLocalTeasers(updatedTeasers);
      window.alert('Teaser deleted successfully!');
    }, 'Delete');
  };

  // Validate teaser file src before navigation/download.
  // Allows only Supabase storage URLs from the configured project, or
  // PDF data URLs. Blocks javascript:, data:text/html, foreign domains,
  // and any other XSS/phishing sink that could be smuggled in via the
  // teaser.fileUrl/fileData columns (see DIAGNOSTIC #A3, #S-7).
  const isSafePdfSrc = (src) => {
    if (typeof src !== 'string' || !src) return false;
    // Accept any URL that parseStorageUrl can resolve — covers all storage URL shapes
    // (public, sign, bare bucket/path) without depending on REACT_APP_SUPABASE_URL matching exactly.
    if (parseStorageUrl(src) !== null) return true;
    if (/^data:application\/pdf(;[\w=-]+)*,/i.test(src)) return true;
    return false;
  };

  // View PDF — bucket is private, so fetch a signed URL before opening.
  const handleView = async (teaser) => {
    markAsSeen(teaser);
    const src = teaser.fileUrl || teaser.fileData;
    if (!src) { window.alert('No file available for this teaser.'); return; }
    if (!isSafePdfSrc(src)) {
      console.error('[handleView] blocked unsafe src:', String(src).slice(0, 80));
      window.alert('This teaser file URL is not from a trusted source and was blocked.');
      return;
    }
    // Data URLs (legacy) can open directly; storage URLs need to be signed.
    if (src.startsWith('data:')) {
      window.open(src, '_blank', 'noopener,noreferrer');
      return;
    }
    try {
      const signed = await getSignedFileUrl(src);
      window.open(signed, '_blank', 'noopener,noreferrer');
    } catch (e) {
      window.alert('Failed to open teaser: ' + e.message);
    }
  };

  // Download PDF — bucket is private, fetch a signed URL before triggering.
  const handleDownload = async (teaser) => {
    const src = teaser.fileUrl || teaser.fileData;
    if (!src) { window.alert('No file available for this teaser.'); return; }
    if (!isSafePdfSrc(src)) {
      console.error('[handleDownload] blocked unsafe src:', String(src).slice(0, 80));
      window.alert('This teaser file URL is not from a trusted source and was blocked.');
      return;
    }
    let downloadHref = src;
    if (!src.startsWith('data:')) {
      try {
        downloadHref = await getSignedFileUrl(src);
      } catch (e) {
        window.alert('Failed to download teaser: ' + e.message);
        return;
      }
    }
    const link = document.createElement('a');
    link.href = downloadHref;
    link.download = teaser.fileName;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div className="p-3 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: BRAND_COLORS.textPrimary }}>
            Teasers
          </h2>
          <p className="text-sm" style={{ color: BRAND_COLORS.textSecondary }}>
            Upload and manage PDF teasers
          </p>
        </div>
        {(role === 'Admin' || role === 'Editor' || role === 'Super Admin') && (
          <button
            onClick={() => setUploadModalOpen(true)}
            className="px-4 py-2 rounded-lg flex items-center gap-2"
            style={{ backgroundColor: BRAND_COLORS.gold, color: '#FFFFFF' }}
          >
            <Plus className="w-5 h-5" />
            Upload Teaser
          </button>
        )}
      </div>

      {/* Visibility filter (Editor+ only) — Partner uses RLS-filtered data. */}
      {role !== 'Partner' && (
        <div className="flex items-center gap-2 text-xs mb-2" style={{ color: BRAND_COLORS.textTertiary }}>
          <span style={{ letterSpacing: '0.04em', textTransform: 'uppercase', fontWeight: 600 }}>Show:</span>
          {[
            { key: 'visible', label: 'Visible to Partners' },
            { key: 'hidden',  label: 'Hidden from Partners' },
            { key: 'all',     label: 'All' },
          ].map(o => (
            <button key={o.key} onClick={() => setTeaserVisibilityFilter(o.key)}
              className="px-2.5 py-1 rounded-full border transition-colors"
              style={teaserVisibilityFilter === o.key
                ? { background: BRAND_COLORS.navy, color: 'white', borderColor: BRAND_COLORS.navy }
                : { background: 'white', color: BRAND_COLORS.textSecondary, borderColor: BRAND_COLORS.borderNavy12 }}>
              {o.label}
            </button>
          ))}
        </div>
      )}

      {(() => {
        const visibleTeasers = (role === 'Partner' || teaserVisibilityFilter === 'all')
          ? localTeasers
          : teaserVisibilityFilter === 'hidden'
            ? localTeasers.filter(t => t.hiddenFromPartners === true)
            : localTeasers.filter(t => t.hiddenFromPartners !== true);
        return visibleTeasers.length === 0 ? (
        <div
          className="text-center py-12 rounded-lg"
          style={{ backgroundColor: BRAND_COLORS.surface, border: `2px dashed ${BRAND_COLORS.border}` }}
        >
          <div style={{ color: BRAND_COLORS.navy, opacity: 0.55, marginBottom: 16, display: 'flex', justifyContent: 'center' }}>
            <NauticalMark name="lighthouse" size={80} />
          </div>
          <p className="text-lg font-semibold mb-2" style={{ color: BRAND_COLORS.textPrimary }}>
            {localTeasers.length === 0 ? 'No teasers yet' : 'No teasers match this filter'}
          </p>
          <p style={{ color: BRAND_COLORS.textSecondary }}>
            {localTeasers.length === 0 ? 'Upload your first PDF teaser to get started' : 'Try a different visibility option above.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {visibleTeasers.map(teaser => (
            <div key={teaser.id}
              className="p-4 rounded-lg"
              style={{
                backgroundColor: BRAND_COLORS.bgSurface,
                border: `1px solid ${BRAND_COLORS.gold}`,
                boxShadow: isNew(teaser) ? `0 0 0 2px ${BRAND_COLORS.gold}55` : '0 1px 3px rgba(0,0,0,0.06)',
                opacity: teaser.hiddenFromPartners && role !== 'Partner' ? 0.6 : 1,
              }}>
              <div className="flex flex-col sm:flex-row items-start gap-4">
                {/* ── Thumbnail ── */}
                <TeaserThumbnail fileUrl={teaser.fileUrl} />

                <div className="flex-1 min-w-0">
                  {/* ── Title row ── */}
                  <div className="flex items-start gap-2 mb-1">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-base font-semibold leading-snug" style={{ color: BRAND_COLORS.textPrimary }}>
                          {teaser.alias || teaser.name || teaser.companyName || '—'}
                        </h3>
                        {isNew(teaser) && (
                          <span className="px-2 py-0.5 text-xs font-bold rounded-full flex-shrink-0 animate-pulse"
                            style={{ backgroundColor: BRAND_COLORS.gold, color: BRAND_COLORS.navy }}>
                            NEW
                          </span>
                        )}
                        {teaser.hiddenFromPartners && role !== 'Partner' && (
                          <span style={{ padding: '1px 7px', borderRadius: '999px', fontSize: '10px', fontWeight: 700, background: `${BRAND_COLORS.textTertiary}1A`, color: BRAND_COLORS.textTertiary, border: `1px solid ${BRAND_COLORS.borderNavy12}`, letterSpacing: '0.04em', flexShrink: 0 }}>
                            HIDDEN
                          </span>
                        )}
                      </div>
                      {/* For admins: show filename as secondary when alias is set */}
                      {role !== 'Partner' && teaser.alias && teaser.name && (
                        <p className="text-xs mt-0.5 truncate" style={{ color: BRAND_COLORS.textTertiary }}>{teaser.name}</p>
                      )}
                    </div>
                  </div>

                  {/* ── Metadata chips: raise · stage · sectors · geography ── */}
                  {(() => {
                    // Fallback: pull from linked prospect when teaser fields are empty
                    const lp = teaser.prospectId ? prospects.find(p => p.id === teaser.prospectId) : null;
                    const displayRaise = teaser.targetRaise || lp?.targetRaise || lp?.investmentMax || null;
                    const displayCurrency = teaser.raiseCurrency || 'USD';
                    const displayStage = teaser.stage || lp?.preferredStage || null;
                    const hasMeta = displayRaise > 0 || displayStage || (dv('sectors') && teaser.sectors?.length > 0) || (dv('geography') && teaser.geography?.continents?.length > 0);
                    if (!hasMeta) return null;
                    return (
                      <div className="flex flex-wrap items-center gap-1.5 mb-2 mt-2">
                        {displayRaise > 0 && (() => {
                          const sym = displayCurrency === 'EUR' ? '€' : displayCurrency === 'GBP' ? '£' : displayCurrency === 'ILS' ? '₪' : '$';
                          const amt = displayRaise >= 1000000
                            ? `${sym}${(displayRaise / 1000000).toFixed(displayRaise % 1000000 === 0 ? 0 : 1)}M`
                            : `${sym}${(displayRaise / 1000).toFixed(0)}K`;
                          return <span style={{ padding: '3px 9px', borderRadius: '999px', fontSize: '11px', fontWeight: 600, background: `${BRAND_COLORS.statusSuccess}0D`, color: BRAND_COLORS.statusSuccess, border: `1px solid ${BRAND_COLORS.statusSuccess}28` }}>{amt} raise</span>;
                        })()}
                        {displayStage && (
                          <span style={{ padding: '3px 9px', borderRadius: '999px', fontSize: '11px', fontWeight: 500, background: `${BRAND_COLORS.statusInfo}0D`, color: BRAND_COLORS.statusInfo, border: `1px solid ${BRAND_COLORS.statusInfo}28` }}>
                            {displayStage}
                          </span>
                        )}
                        {dv('sectors') && teaser.sectors?.map(s => (
                          <span key={s} style={{ padding: '3px 9px', borderRadius: '999px', fontSize: '11px', fontWeight: 500, background: `${BRAND_COLORS.navy}0D`, color: BRAND_COLORS.textSecondary, border: `1px solid ${BRAND_COLORS.borderNavy12}` }}>
                            {s}
                          </span>
                        ))}
                        {dv('geography') && teaser.geography?.continents?.map(c => (
                          <span key={c} style={{ padding: '3px 9px', borderRadius: '999px', fontSize: '11px', fontWeight: 500, background: `${BRAND_COLORS.navy}0D`, color: BRAND_COLORS.textSecondary, border: `1px solid ${BRAND_COLORS.borderNavy12}` }}>
                            🌍 {c}
                          </span>
                        ))}
                      </div>
                    );
                  })()}

                  {/* ── Description (one-line teaser pitch) ── */}
                  {teaser.description && (
                    <p style={{
                      fontSize: '13px',
                      color: BRAND_COLORS.textSecondary,
                      margin: '4px 0 0',
                      lineHeight: 1.4,
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: 2,
                      overflow: 'hidden',
                    }}>
                      {teaser.description}
                    </p>
                  )}

                  {/* ── Footer: date · by · size · prospect (admin only) ── */}
                  <div className="flex items-center gap-3 text-xs mt-2" style={{ color: BRAND_COLORS.textTertiary }}>
                    {dv('upload_date') && (teaser.uploadDate || teaser.lastUpdated || teaser.createdAt) && (
                      <span>Uploaded {formatDate(teaser.uploadDate || teaser.lastUpdated || teaser.createdAt)}</span>
                    )}
                    {dv('uploaded_by') && (teaser.uploadedBy || teaser.uploadedById) && (
                      <><span>·</span><span>By {teaser.uploadedBy || teaser.uploadedById}</span></>
                    )}
                    {dv('file_size') && teaser.fileSize > 0 && (
                      <><span>·</span><span>{formatFileSize(teaser.fileSize)}</span></>
                    )}
                    {role !== 'Partner' && dv('prospect_id') && (teaser.prospectName || teaser.companyName) && (
                      <><span>·</span><span className="font-medium" style={{ color: BRAND_COLORS.navy }}>📋 {teaser.prospectName || teaser.companyName}</span></>
                    )}
                  </div>

                  {/* ── Linked deals ── */}
                  {(() => {
                    const linkedDeals = deals.filter(d => d.teaserId === teaser.id);
                    if (!linkedDeals.length) return null;
                    if (role === 'Partner') {
                      // Partners see count only — no deal names or states
                      return (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                            style={{ backgroundColor: `${BRAND_COLORS.gold}18`, color: BRAND_COLORS.gold, border: `1px solid ${BRAND_COLORS.gold}40` }}>
                            {linkedDeals.length} deal{linkedDeals.length !== 1 ? 's' : ''}
                            {(() => {
                              const total = linkedDeals.reduce((s, d) => s + (parseFloat(d.totalAgreed || d.dealSize) || 0), 0);
                              if (!total) return null;
                              const currency = linkedDeals[0].currency || 'USD';
                              const fmt = total >= 1e6 ? `${(total/1e6).toFixed(total%1e6===0?0:1)}M` : total >= 1e3 ? `${(total/1e3).toFixed(0)}K` : total.toLocaleString();
                              const sym = currency === 'EUR' ? '€' : currency === 'ILS' ? '₪' : '$';
                              return <span style={{ opacity: 0.75, marginLeft: 3 }}>· {sym}{fmt}</span>;
                            })()}
                          </span>
                        </div>
                      );
                    }
                    // Admins/Editors see deal name + state
                    return (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {linkedDeals.map(d => (
                          <span key={d.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                            style={{ backgroundColor: `${BRAND_COLORS.navy}0D`, color: BRAND_COLORS.navy, border: `1px solid ${BRAND_COLORS.borderNavy12}` }}>
                            {d.dealName}
                            <span style={{ opacity: 0.6, marginLeft: 2 }}>· {d.dealState}</span>
                          </span>
                        ))}
                      </div>
                    );
                  })()}
                </div>

                <div className="flex items-center gap-2 ml-4">
                  <button onClick={() => handleView(teaser)} className="p-2 rounded hover:bg-opacity-10" style={{ color: BRAND_COLORS.navy }} title="View PDF">
                    <Eye className="w-5 h-5" />
                  </button>

                  <button onClick={() => handleDownload(teaser)} className="p-2 rounded hover:bg-opacity-10" style={{ color: BRAND_COLORS.navy }} title="Download PDF">
                    <Download className="w-5 h-5" />
                  </button>

                  {role === 'Partner' && onAskQuestion && (
                    <button
                      onClick={() => onAskQuestion({ category: 'teaser', refId: teaser.id, refName: teaser.alias || teaser.name || teaser.companyName || 'Teaser' })}
                      className="p-2 rounded hover:bg-opacity-10"
                      style={{ color: BRAND_COLORS.gold }}
                      title="Ask a question about this teaser"
                    >
                      <MessageCircle className="w-5 h-5" />
                    </button>
                  )}

                  {(role === 'Admin' || role === 'Editor' || role === 'Super Admin') && (
                    <>
                      <button onClick={() => onEdit && onEdit(teaser)} className="p-2 rounded hover:bg-opacity-10" style={{ color: BRAND_COLORS.gold }} title="Edit">
                        <Edit2 className="w-5 h-5" />
                      </button>

                      {onToggleHidden && (
                        <button onClick={() => onToggleHidden(teaser.id, !teaser.hiddenFromPartners)}
                          className="p-2 rounded hover:bg-opacity-10"
                          style={{ color: teaser.hiddenFromPartners ? BRAND_COLORS.textTertiary : BRAND_COLORS.textSecondary }}
                          title={teaser.hiddenFromPartners ? 'Show to partners' : 'Hide from partners'}>
                          <span style={{ fontSize: '18px', lineHeight: 1 }}>{teaser.hiddenFromPartners ? '🙈' : '👁'}</span>
                        </button>
                      )}

                      <button onClick={() => handleDelete(teaser)} className="p-2 rounded hover:bg-opacity-10" style={{ color: BRAND_COLORS.error }} title="Delete">
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      );
      })()}

      {uploadModalOpen && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
          onClick={() => { setUploadModalOpen(false); setUploadWizardStep(1); }}
        >
          <div
            className="bg-white rounded-lg shadow-xl border-2 w-full mx-4 flex flex-col"
            style={{ borderColor: BRAND_COLORS.gold, maxWidth: 560, maxHeight: '90vh' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Wizard Header */}
            <div className="px-6 pt-4 pb-2 flex items-center justify-between border-b border-gray-100 flex-shrink-0">
              <h2 className="text-lg font-bold" style={{ color: BRAND_COLORS.navy }}>Upload Teaser</h2>
              <button onClick={() => { setUploadModalOpen(false); setUploadWizardStep(1); }} className="text-gray-400 hover:text-gray-600 text-xl font-bold">✕</button>
            </div>

            {/* Step Indicators */}
            <div className="px-6 pt-5 pb-4 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center justify-between">
                {[
                  { n: 1, label: 'Teaser Info' },
                  { n: 2, label: 'Sector' },
                  { n: 3, label: 'Geography' },
                  { n: 4, label: 'Distribution' },
                ].map(({ n, label }, idx, arr) => (
                  <React.Fragment key={n}>
                    <button onClick={() => setUploadWizardStep(n)} className="flex flex-col items-center gap-1">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all"
                        style={{
                          backgroundColor: uploadWizardStep === n ? BRAND_COLORS.gold : uploadWizardStep > n ? '#10B981' : '#E5E7EB',
                          color: uploadWizardStep >= n ? 'white' : '#9CA3AF'
                        }}>
                        {uploadWizardStep > n ? '✓' : n}
                      </div>
                      <span className="text-xs font-medium" style={{ color: uploadWizardStep === n ? BRAND_COLORS.navy : '#9CA3AF' }}>{label}</span>
                    </button>
                    {idx < arr.length - 1 && (
                      <div className="flex-1 h-0.5 mx-2 mb-4 rounded" style={{ backgroundColor: uploadWizardStep > n ? '#10B981' : '#E5E7EB' }} />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            <div className="overflow-y-auto flex-1">
            {/* Step 1: Teaser Info */}
            {uploadWizardStep === 1 && (<>
            <div className="p-6 border-b border-gray-200 space-y-4">
              <h3 className="text-lg font-semibold mb-1" style={{ color: BRAND_COLORS.navy }}>Step 1: Teaser Info</h3>

              {/* Prospect — required, first */}
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: BRAND_COLORS.navy }}>
                  Associated Prospect <span className="text-red-500">*</span>
                </label>
                <select
                  value={uploadForm.prospectId}
                  onChange={(e) => {
                    const p = prospects.find(pr => pr.id === e.target.value);
                    setUploadForm(prev => ({
                      ...prev,
                      prospectId: e.target.value,
                      name: prev.name || (p ? (p.companyName || p.investorName || '') : '')
                    }));
                  }}
                  className="w-full px-3 py-2 rounded-lg border text-sm"
                  style={{ backgroundColor: BRAND_COLORS.background, borderColor: uploadForm.prospectId ? BRAND_COLORS.gold : BRAND_COLORS.border, color: BRAND_COLORS.textPrimary }}
                  required
                >
                  <option value="">— Select a prospect —</option>
                  {prospects.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.companyName || p.investorName || p.id}
                      {p.approvalStatus ? ` (${p.approvalStatus})` : ''}
                    </option>
                  ))}
                </select>
                {prospects.length === 0 && (
                  <p className="text-xs text-orange-600 mt-1">No prospects found. Create a prospect first before uploading a teaser.</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: BRAND_COLORS.navy }}>
                  Teaser Name *
                </label>
                <input
                  type="text"
                  value={uploadForm.name}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Imagry Investment Opportunity"
                  className="w-full px-3 py-2 rounded-lg border text-sm"
                  style={{ backgroundColor: BRAND_COLORS.background, borderColor: BRAND_COLORS.border, color: BRAND_COLORS.textPrimary }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: BRAND_COLORS.navy }}>
                  Client Alias <span className="text-xs font-normal text-gray-400">(optional — shown to partners instead of the real client name)</span>
                </label>
                <input
                  type="text"
                  value={uploadForm.alias}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, alias: e.target.value }))}
                  placeholder="e.g., Israeli B2B SaaS — Series A"
                  className="w-full px-3 py-2 rounded-lg border text-sm"
                  style={{ backgroundColor: BRAND_COLORS.background, borderColor: BRAND_COLORS.border, color: BRAND_COLORS.textPrimary }}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: BRAND_COLORS.navy }}>
                    Stage <span className="text-xs font-normal text-gray-400">(optional)</span>
                  </label>
                  <select
                    value={uploadForm.stage}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, stage: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border text-sm"
                    style={{ backgroundColor: BRAND_COLORS.background, borderColor: BRAND_COLORS.border, color: BRAND_COLORS.textPrimary }}
                  >
                    <option value="">— Select stage —</option>
                    {['Pre-Seed','Seed','Series A','Series B','Series C+','Growth','Late Stage','IPO-Ready','Other'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: BRAND_COLORS.navy }}>
                    Target Raise <span className="text-xs font-normal text-gray-400">(optional)</span>
                  </label>
                  <div className="flex gap-1.5">
                    <select
                      value={uploadForm.raiseCurrency}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, raiseCurrency: e.target.value }))}
                      className="px-2 py-2 rounded-lg border text-sm w-20 flex-shrink-0"
                      style={{ backgroundColor: BRAND_COLORS.background, borderColor: BRAND_COLORS.border, color: BRAND_COLORS.textPrimary }}
                    >
                      {['USD','EUR','GBP','ILS'].map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <input
                      type="number"
                      min="0"
                      value={uploadForm.targetRaise}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, targetRaise: e.target.value }))}
                      placeholder="e.g. 2500000"
                      className="flex-1 px-3 py-2 rounded-lg border text-sm"
                      style={{ backgroundColor: BRAND_COLORS.background, borderColor: BRAND_COLORS.border, color: BRAND_COLORS.textPrimary }}
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: BRAND_COLORS.navy }}>
                  Description
                </label>
                <textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of the teaser..."
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg border resize-none text-sm"
                  style={{ backgroundColor: BRAND_COLORS.background, borderColor: BRAND_COLORS.border, color: BRAND_COLORS.textPrimary }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: BRAND_COLORS.navy }}>
                  Select PDF File *
                </label>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileSelect}
                  className="w-full px-3 py-2 rounded-lg border text-sm"
                  style={{ backgroundColor: BRAND_COLORS.background, borderColor: BRAND_COLORS.border, color: BRAND_COLORS.textPrimary }}
                />
                {uploadForm.fileName && (
                  <p className="text-xs mt-1" style={{ color: BRAND_COLORS.textSecondary }}>
                    ✓ {uploadForm.fileName} ({formatFileSize(uploadForm.fileSize)})
                  </p>
                )}
              </div>
            </div>
            <div className="px-6 pb-5 pt-4 flex justify-between sticky bottom-0 bg-white border-t border-gray-100 z-10">
              <button
                onClick={() => { setUploadModalOpen(false); setUploadWizardStep(1); setUploadForm({ name: '', alias: '', description: '', prospectId: '', fileData: null, fileName: '', fileSize: 0 }); }}
                className="px-4 py-2 rounded border"
                style={{ borderColor: BRAND_COLORS.border, color: BRAND_COLORS.textPrimary }}
              >
                Cancel
              </button>
              <button onClick={() => setUploadWizardStep(2)} className="px-6 py-2 rounded-lg font-medium text-white" style={{ backgroundColor: BRAND_COLORS.gold }}>
                Next →
              </button>
            </div>
            </>)}

            {/* Step 2: Sector */}
            {uploadWizardStep === 2 && (<>
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold mb-1" style={{ color: BRAND_COLORS.navy }}>Step 2: Sector</h3>
              <p className="text-sm text-gray-500 mb-3">Sectors this teaser is relevant to</p>
              {/* Targeting source toggle — only shown when a prospect is linked */}
              {uploadForm.prospectId && (() => {
                const lp = prospects.find(p => p.id === uploadForm.prospectId);
                if (!lp) return null;
                return (
                  <div className="flex gap-2 mb-4">
                    <button type="button"
                      onClick={() => { setUploadSectorSource('prospect'); setUploadSectors(parseSectorsField(lp.sectors)); }}
                      className="flex-1 py-2 px-3 text-sm rounded-lg border-2 font-medium transition-all"
                      style={{ borderColor: uploadSectorSource === 'prospect' ? BRAND_COLORS.navy : '#e5e7eb', backgroundColor: uploadSectorSource === 'prospect' ? `${BRAND_COLORS.navy}15` : 'white', color: uploadSectorSource === 'prospect' ? BRAND_COLORS.navy : '#6b7280' }}>
                      📎 Pull from prospect
                    </button>
                    <button type="button"
                      onClick={() => setUploadSectorSource('manual')}
                      className="flex-1 py-2 px-3 text-sm rounded-lg border-2 font-medium transition-all"
                      style={{ borderColor: uploadSectorSource === 'manual' ? BRAND_COLORS.navy : '#e5e7eb', backgroundColor: uploadSectorSource === 'manual' ? `${BRAND_COLORS.navy}15` : 'white', color: uploadSectorSource === 'manual' ? BRAND_COLORS.navy : '#6b7280' }}>
                      ✏️ Enter manually
                    </button>
                  </div>
                );
              })()}
              {uploadSectorSource === 'prospect' ? (
                <div className="rounded-lg p-3 bg-gray-50 border border-gray-200">
                  {uploadSectors.length > 0
                    ? <div className="flex flex-wrap gap-2">{uploadSectors.map(s => <span key={s} className="px-3 py-1 rounded-full text-sm font-medium" style={{ backgroundColor: `${BRAND_COLORS.gold}25`, color: BRAND_COLORS.navy }}>{s}</span>)}</div>
                    : <p className="text-sm text-gray-400 italic">Prospect has no sectors defined — switch to "Enter manually" to add them.</p>}
                </div>
              ) : (
                <SectorSelector sectors={sectors} selectedSectors={uploadSectors} onSectorsChange={setUploadSectors} />
              )}
            </div>
            <div className="px-6 pb-5 pt-4 flex justify-between sticky bottom-0 bg-white border-t border-gray-100 z-10">
              <button onClick={() => setUploadWizardStep(1)} className="px-4 py-2 rounded border" style={{ borderColor: BRAND_COLORS.border, color: BRAND_COLORS.textPrimary }}>
                ← Back
              </button>
              <button onClick={() => setUploadWizardStep(3)} className="px-6 py-2 rounded-lg font-medium text-white" style={{ backgroundColor: BRAND_COLORS.gold }}>
                Next →
              </button>
            </div>
            </>)}

            {/* Step 3: Geography */}
            {uploadWizardStep === 3 && (<>
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold mb-1" style={{ color: BRAND_COLORS.navy }}>Step 3: Geography</h3>
              <p className="text-sm text-gray-500 mb-3">Geographic regions this teaser is relevant to</p>
              {uploadForm.prospectId && (() => {
                const lp = prospects.find(p => p.id === uploadForm.prospectId);
                if (!lp) return null;
                return (
                  <div className="flex gap-2 mb-4">
                    <button type="button"
                      onClick={() => { setUploadGeoSource('prospect'); setUploadGeo(parseGeographyField(lp.geography)); }}
                      className="flex-1 py-2 px-3 text-sm rounded-lg border-2 font-medium transition-all"
                      style={{ borderColor: uploadGeoSource === 'prospect' ? BRAND_COLORS.navy : '#e5e7eb', backgroundColor: uploadGeoSource === 'prospect' ? `${BRAND_COLORS.navy}15` : 'white', color: uploadGeoSource === 'prospect' ? BRAND_COLORS.navy : '#6b7280' }}>
                      📎 Pull from prospect
                    </button>
                    <button type="button"
                      onClick={() => setUploadGeoSource('manual')}
                      className="flex-1 py-2 px-3 text-sm rounded-lg border-2 font-medium transition-all"
                      style={{ borderColor: uploadGeoSource === 'manual' ? BRAND_COLORS.navy : '#e5e7eb', backgroundColor: uploadGeoSource === 'manual' ? `${BRAND_COLORS.navy}15` : 'white', color: uploadGeoSource === 'manual' ? BRAND_COLORS.navy : '#6b7280' }}>
                      ✏️ Enter manually
                    </button>
                  </div>
                );
              })()}
              {uploadGeoSource === 'prospect' ? (
                <div className="rounded-lg p-3 bg-gray-50 border border-gray-200">
                  {(uploadGeo.continents?.length > 0 || uploadGeo.countries?.length > 0) ? (
                    <div className="text-sm text-gray-700 space-y-1">
                      {uploadGeo.continents?.length > 0 && <p><span className="font-medium">Continents:</span> {uploadGeo.continents.join(', ')}</p>}
                      {uploadGeo.countries?.length > 0 && <p><span className="font-medium">Countries:</span> {uploadGeo.countries.join(', ')}</p>}
                    </div>
                  ) : <p className="text-sm text-gray-400 italic">Prospect has no geography defined — go back to Step 2 and switch to "Enter manually".</p>}
                </div>
              ) : (
                <GeographySelector selectedGeo={uploadGeo} onGeoChange={setUploadGeo} />
              )}
            </div>
            <div className="px-6 pb-5 pt-4 flex justify-between sticky bottom-0 bg-white border-t border-gray-100 z-10">
              <button onClick={() => setUploadWizardStep(2)} className="px-4 py-2 rounded border" style={{ borderColor: BRAND_COLORS.border, color: BRAND_COLORS.textPrimary }}>
                ← Back
              </button>
              <button onClick={() => setUploadWizardStep(4)} className="px-6 py-2 rounded-lg font-medium text-white" style={{ backgroundColor: BRAND_COLORS.gold }}>
                Next →
              </button>
            </div>
            </>)}

            {/* Step 4: Distribution (which partners can see this teaser) */}
            {uploadWizardStep === 4 && (<>
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold mb-1" style={{ color: BRAND_COLORS.navy }}>Step 4: Distribution</h3>
              <p className="text-sm text-gray-500 mb-4">Choose which partners can see this teaser in their portal</p>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    checked={uploadVisibility === 'all'}
                    onChange={() => setUploadVisibility('all')}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">All MCA Partners</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    checked={uploadVisibility === 'sectors'}
                    onChange={() => {
                      setUploadVisibility('sectors');
                      if (uploadFilterSectors.length === 0 && uploadSectors.length > 0) {
                        setUploadFilterSectors(uploadSectors);
                      }
                    }}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">MCA Partners interested in specific sectors</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    checked={uploadVisibility === 'specific'}
                    onChange={() => setUploadVisibility('specific')}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Specific Partners</span>
                </label>
              </div>

              {uploadVisibility === 'sectors' && (() => {
                const matchingPartners = partners.filter(p =>
                  p.status === 'Active' && (p.sectors || []).some(s => uploadFilterSectors.includes(s))
                );
                return (
                  <div className="mt-3 space-y-3">
                    <div className="border rounded-lg p-3" style={{ borderColor: BRAND_COLORS.border, backgroundColor: BRAND_COLORS.background }}>
                      <p className="text-xs font-medium text-gray-700 mb-2">Pick sectors — partners with matching focus will receive this teaser:</p>
                      <div className="flex flex-wrap gap-1.5">
                        {sectors.map(s => {
                          const active = uploadFilterSectors.includes(s);
                          return (
                            <button
                              key={s}
                              type="button"
                              onClick={() => {
                                if (active) setUploadFilterSectors(uploadFilterSectors.filter(x => x !== s));
                                else setUploadFilterSectors([...uploadFilterSectors, s]);
                              }}
                              className="px-2.5 py-1 rounded-full text-xs font-medium border transition-colors"
                              style={{
                                borderColor: active ? BRAND_COLORS.gold : BRAND_COLORS.border,
                                backgroundColor: active ? `${BRAND_COLORS.gold}20` : 'white',
                                color: active ? BRAND_COLORS.navy : '#6b7280',
                              }}
                            >
                              {s}
                            </button>
                          );
                        })}
                      </div>
                      {uploadSectors.length > 0 && uploadFilterSectors.join('|') !== uploadSectors.join('|') && (
                        <button
                          type="button"
                          onClick={() => setUploadFilterSectors(uploadSectors)}
                          className="mt-2 text-xs underline"
                          style={{ color: BRAND_COLORS.gold }}
                        >
                          Reset to teaser's sectors ({uploadSectors.join(', ')})
                        </button>
                      )}
                    </div>

                    <div className="border rounded-lg p-3" style={{ borderColor: BRAND_COLORS.border, backgroundColor: BRAND_COLORS.background }}>
                      <p className="text-xs font-medium text-gray-700 mb-2">
                        {uploadFilterSectors.length === 0
                          ? 'No sectors picked yet.'
                          : `${matchingPartners.length} active partner${matchingPartners.length === 1 ? '' : 's'} match`}
                      </p>
                      {matchingPartners.length === 0 && uploadFilterSectors.length > 0 && (
                        <p className="text-xs text-amber-600">⚠️ No active partners are interested in these sectors — this teaser will not be visible to anyone.</p>
                      )}
                      {matchingPartners.length > 0 && (
                        <ul className="text-sm text-gray-700 space-y-0.5 max-h-40 overflow-y-auto">
                          {matchingPartners.map(p => (
                            <li key={p.id}>• {p.name} <span className="text-xs text-gray-400">— {(p.sectors || []).filter(s => uploadFilterSectors.includes(s)).join(', ')}</span></li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                );
              })()}

              {uploadVisibility === 'specific' && (
                <div className="mt-3 space-y-2 max-h-60 overflow-y-auto border rounded-lg p-3" style={{ borderColor: BRAND_COLORS.border, backgroundColor: BRAND_COLORS.background }}>
                  {partners.filter(p => p.status === 'Active' || uploadSelectedPartners.includes(p.id)).length === 0 ? (
                    <p className="text-xs text-gray-500">No active partners available.</p>
                  ) : (
                    partners.filter(p => p.status === 'Active' || uploadSelectedPartners.includes(p.id)).map(partner => (
                      <label key={partner.id} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={uploadSelectedPartners.includes(partner.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setUploadSelectedPartners([...uploadSelectedPartners, partner.id]);
                            } else {
                              setUploadSelectedPartners(uploadSelectedPartners.filter(id => id !== partner.id));
                            }
                          }}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">{partner.name}</span>
                      </label>
                    ))
                  )}
                  {uploadSelectedPartners.length === 0 && (
                    <p className="text-xs text-amber-600 mt-2">⚠️ No partners selected — this teaser will not be visible to anyone.</p>
                  )}
                </div>
              )}
            </div>
            <div className="px-6 pb-5 pt-4 flex justify-between sticky bottom-0 bg-white border-t border-gray-100 z-10">
              <button onClick={() => setUploadWizardStep(3)} className="px-4 py-2 rounded border" style={{ borderColor: BRAND_COLORS.border, color: BRAND_COLORS.textPrimary }}>
                ← Back
              </button>
              <button onClick={handleUpload} className="px-6 py-2 rounded-lg font-medium text-white" style={{ backgroundColor: BRAND_COLORS.gold }}>
                Upload
              </button>
            </div>
            </>)}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};


// Teaser Detail Modal Component
const UserManagementDashboard = ({ users, partners }) => {
  // Calculate statistics
  const totalUsers = users.length;
  const activeUsers = users.filter(u => u.status === 'Active').length;
  const inactiveUsers = users.filter(u => u.status === 'Inactive').length;
  const adminUsers = users.filter(u => u.role === 'Admin').length;
  const partnerUsers = users.filter(u => u.role === 'Partner').length;
  
  // Calculate login stats
  const usersWithLogins = users.filter(u => u.lastLogin).length;
  const recentLogins = users.filter(u => {
    if (!u.lastLogin) return false;
    const daysSince = Math.floor((Date.now() - new Date(u.lastLogin).getTime()) / (1000 * 60 * 60 * 24));
    return daysSince <= 7;
  }).length;
  
  const neverLoggedIn = users.filter(u => !u.lastLogin).length;
  
  // User growth over time (last 6 months)
  const now = new Date();
  const userGrowthData = [];
  for (let i = 5; i >= 0; i--) {
    const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const monthName = monthDate.toLocaleString('default', { month: 'short' });
    const usersCreatedByThen = users.filter(u => new Date(u.createdAt) <= monthDate).length;
    userGrowthData.push({
      month: monthName,
      total: usersCreatedByThen,
      active: users.filter(u => new Date(u.createdAt) <= monthDate && u.status === 'Active').length
    });
  }
  
  // Role distribution
  const roleDistribution = [
    { name: 'Admin', value: adminUsers, color: BRAND_COLORS.gold },
    { name: 'Partner', value: partnerUsers, color: BRAND_COLORS.royalBlue }
  ];
  
  // Status distribution
  const statusDistribution = [
    { name: 'Active', value: activeUsers, color: '#10b981' },
    { name: 'Inactive', value: inactiveUsers, color: '#ef4444' }
  ];
  
  // Partner association breakdown
  const partnerAssociations = partners.map(partner => ({
    name: partner.name,
    users: users.filter(u => u.partnerId === partner.id).length
  })).filter(p => p.users > 0).sort((a, b) => b.users - a.users);
  
  // Recent user activity (last 10 users by creation date)
  const recentUsers = [...users]
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, 10);
    
  // Login activity (users by last login)
  const loginActivity = [...users]
    .filter(u => u.lastLogin)
    .sort((a, b) => new Date(b.lastLogin) - new Date(a.lastLogin))
    .slice(0, 10);

  return (
    <div className="space-y-6">
      {/* Key Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-white rounded-lg shadow-md p-4 border-l-4" style={{ borderLeftColor: BRAND_COLORS.navy }}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600 font-medium">Total Users</p>
            <Users className="w-5 h-5" style={{ color: BRAND_COLORS.navy }} />
          </div>
          <p className="text-3xl font-bold" style={{ color: BRAND_COLORS.navy }}>{totalUsers}</p>
          <p className="text-xs text-gray-500 mt-1">All accounts</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 border-l-4" style={{ borderLeftColor: '#10b981' }}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600 font-medium">Active</p>
            <Check className="w-5 h-5 text-green-500" />
          </div>
          <p className="text-3xl font-bold text-green-600">{activeUsers}</p>
          <p className="text-xs text-gray-500 mt-1">{totalUsers > 0 ? Math.round((activeUsers / totalUsers) * 100) : 0}% active rate</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 border-l-4" style={{ borderLeftColor: '#ef4444' }}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600 font-medium">Inactive</p>
            <X className="w-5 h-5 text-red-500" />
          </div>
          <p className="text-3xl font-bold text-red-600">{inactiveUsers}</p>
          <p className="text-xs text-gray-500 mt-1">{totalUsers > 0 ? Math.round((inactiveUsers / totalUsers) * 100) : 0}% inactive</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 border-l-4" style={{ borderLeftColor: BRAND_COLORS.gold }}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600 font-medium">Admins</p>
            <Shield className="w-5 h-5" style={{ color: BRAND_COLORS.gold }} />
          </div>
          <p className="text-3xl font-bold" style={{ color: BRAND_COLORS.gold }}>{adminUsers}</p>
          <p className="text-xs text-gray-500 mt-1">Admin accounts</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 border-l-4" style={{ borderLeftColor: BRAND_COLORS.royalBlue }}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600 font-medium">Partners</p>
            <Users className="w-5 h-5" style={{ color: BRAND_COLORS.royalBlue }} />
          </div>
          <p className="text-3xl font-bold" style={{ color: BRAND_COLORS.royalBlue }}>{partnerUsers}</p>
          <p className="text-xs text-gray-500 mt-1">Partner accounts</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 border-l-4" style={{ borderLeftColor: '#8b5cf6' }}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600 font-medium">Recent Logins</p>
            <Activity className="w-5 h-5 text-purple-500" />
          </div>
          <p className="text-3xl font-bold text-purple-600">{recentLogins}</p>
          <p className="text-xs text-gray-500 mt-1">Last 7 days</p>
        </div>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Growth Over Time */}
        <div className="bg-white rounded-lg shadow-md p-6 border-t-2" style={{ borderTopColor: BRAND_COLORS.gold }}>
          <h3 className="text-lg font-semibold mb-4 flex items-center" style={{ color: BRAND_COLORS.navy }}>
            <TrendingUp className="w-5 h-5 mr-2" style={{ color: BRAND_COLORS.gold }} />
            User Growth Trend
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={userGrowthData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Area type="monotone" dataKey="total" stackId="1" stroke={BRAND_COLORS.navy} fill={BRAND_COLORS.navy} name="Total Users" />
              <Area type="monotone" dataKey="active" stackId="2" stroke="#10b981" fill="#10b981" name="Active Users" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Role Distribution */}
        <div className="bg-white rounded-lg shadow-md p-6 border-t-2" style={{ borderTopColor: BRAND_COLORS.gold }}>
          <h3 className="text-lg font-semibold mb-4 flex items-center" style={{ color: BRAND_COLORS.navy }}>
            <PieChart className="w-5 h-5 mr-2" style={{ color: BRAND_COLORS.gold }} />
            Role Distribution
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <RechartsPie>
              <Pie
                data={roleDistribution}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {roleDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </RechartsPie>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Distribution */}
        <div className="bg-white rounded-lg shadow-md p-6 border-t-2" style={{ borderTopColor: BRAND_COLORS.gold }}>
          <h3 className="text-lg font-semibold mb-4 flex items-center" style={{ color: BRAND_COLORS.navy }}>
            <Activity className="w-5 h-5 mr-2" style={{ color: BRAND_COLORS.gold }} />
            Status Distribution
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <RechartsPie>
              <Pie
                data={statusDistribution}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {statusDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </RechartsPie>
          </ResponsiveContainer>
        </div>

        {/* Partner User Distribution */}
        <div className="bg-white rounded-lg shadow-md p-6 border-t-2" style={{ borderTopColor: BRAND_COLORS.gold }}>
          <h3 className="text-lg font-semibold mb-4 flex items-center" style={{ color: BRAND_COLORS.navy }}>
            <BarChart3 className="w-5 h-5 mr-2" style={{ color: BRAND_COLORS.gold }} />
            Users per Partner
          </h3>
          {partnerAssociations.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={partnerAssociations} layout="horizontal">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={100} />
                <Tooltip />
                <Bar dataKey="users" fill={BRAND_COLORS.royalBlue} name="Users" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              No partner associations yet
            </div>
          )}
        </div>
      </div>

      {/* Activity Tables Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Users */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold flex items-center" style={{ color: BRAND_COLORS.navy }}>
              <Clock className="w-5 h-5 mr-2" style={{ color: BRAND_COLORS.gold }} />
              Recently Created Users
            </h3>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {recentUsers.map(user => {
                const partner = user.partnerId ? partners.find(p => p.id === user.partnerId) : null;
                return (
                  <div key={user.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: user.role === 'Admin' ? `${BRAND_COLORS.gold}20` : `${BRAND_COLORS.royalBlue}20` }}>
                        {user.role === 'Admin' ? (
                          <Shield className="w-5 h-5" style={{ color: BRAND_COLORS.gold }} />
                        ) : (
                          <Users className="w-5 h-5" style={{ color: BRAND_COLORS.royalBlue }} />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{user.name}</p>
                        <p className="text-xs text-gray-500">{partner ? partner.name : user.role}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">{new Date(user.createdAt).toLocaleDateString()}</p>
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                        style={{
                          backgroundColor: user.status === 'Active' ? '#d1fae520' : '#fee2e220',
                          color: user.status === 'Active' ? '#10b981' : '#ef4444'
                        }}
                      >
                        {user.status}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Login Activity */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold flex items-center" style={{ color: BRAND_COLORS.navy }}>
              <Activity className="w-5 h-5 mr-2" style={{ color: BRAND_COLORS.gold }} />
              Recent Login Activity
            </h3>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {loginActivity.length > 0 ? (
                loginActivity.map(user => {
                  const partner = user.partnerId ? partners.find(p => p.id === user.partnerId) : null;
                  const daysSince = Math.floor((Date.now() - new Date(user.lastLogin).getTime()) / (1000 * 60 * 60 * 24));
                  return (
                    <div key={user.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: user.role === 'Admin' ? `${BRAND_COLORS.gold}20` : `${BRAND_COLORS.royalBlue}20` }}>
                          {user.role === 'Admin' ? (
                            <Shield className="w-5 h-5" style={{ color: BRAND_COLORS.gold }} />
                          ) : (
                            <Users className="w-5 h-5" style={{ color: BRAND_COLORS.royalBlue }} />
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{user.name}</p>
                          <p className="text-xs text-gray-500">{partner ? partner.name : user.role}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">{new Date(user.lastLogin).toLocaleDateString()}</p>
                        <p className="text-xs text-gray-400">
                          {daysSince === 0 ? 'Today' : daysSince === 1 ? 'Yesterday' : `${daysSince}d ago`}
                        </p>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Activity className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                  <p>No login activity yet</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Insights Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg shadow-md p-6 border-l-4" style={{ borderLeftColor: BRAND_COLORS.royalBlue }}>
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="text-sm font-medium text-gray-700 mb-1">Login Rate</p>
              <p className="text-3xl font-bold" style={{ color: BRAND_COLORS.royalBlue }}>
                {totalUsers > 0 ? Math.round((usersWithLogins / totalUsers) * 100) : 0}%
              </p>
            </div>
            <Activity className="w-8 h-8" style={{ color: BRAND_COLORS.royalBlue, opacity: 0.5 }} />
          </div>
          <p className="text-sm text-gray-600">
            {usersWithLogins} of {totalUsers} users have logged in
          </p>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg shadow-md p-6 border-l-4 border-purple-500">
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="text-sm font-medium text-gray-700 mb-1">Never Logged In</p>
              <p className="text-3xl font-bold text-purple-600">{neverLoggedIn}</p>
            </div>
            <AlertCircle className="w-8 h-8 text-purple-500 opacity-50" />
          </div>
          <p className="text-sm text-gray-600">
            Users who haven't accessed the portal yet
          </p>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg shadow-md p-6 border-l-4 border-green-500">
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="text-sm font-medium text-gray-700 mb-1">Active This Week</p>
              <p className="text-3xl font-bold text-green-600">{recentLogins}</p>
            </div>
            <TrendingUp className="w-8 h-8 text-green-500 opacity-50" />
          </div>
          <p className="text-sm text-gray-600">
            Users with logins in the last 7 days
          </p>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// VIEW: Revenue Pool & Commission Manager
// ============================================================================

// ============================================================================
// COMPONENT: DealProfileDrawer — right-side floating panel for a revenue deal
// ============================================================================
const DealProfileDrawer = ({ deal, prospects = [], partners = [], onClose, onEdit, onToggleHidden }) => {
  const { isMobile } = useIsMobile();
  const [activeTab, setActiveTab] = useState('overview');
  if (!deal) return null;

  const dt = computeDealTotals(deal);
  const clientProspect   = prospects.find(p => p.id === deal.prospectId);
  const investorProspect = prospects.find(p => p.id === deal.investorProspectId);
  const introPartner = clientProspect?.introducingPartnerId
    ? partners.find(p => p.id === clientProspect.introducingPartnerId)
    : null;
  const strategicPartners = (deal.strategicPartners || []).map(sp => ({
    ...sp,
    partner: partners.find(p => p.id === sp.partnerId),
    payout: dt.partnerPayouts?.find(pp => pp.partnerId === sp.partnerId)?.amount || 0,
  })).filter(sp => sp.partner);
  const payments = deal.payments || [];

  const statusColor =
    deal.dealState === 'Active' ? BRAND_COLORS.statusSuccess :
    deal.dealState === 'Closed' ? BRAND_COLORS.statusInfo    :
    deal.dealState === 'Draft'  ? BRAND_COLORS.textSecondary :
    BRAND_COLORS.statusWarning;

  const tabs = [
    { key: 'overview',   label: 'Overview',   icon: Activity },
    { key: 'financials', label: 'Financials', icon: DollarSign },
    { key: 'payments',   label: 'Payments',   icon: CreditCard, count: payments.length },
    { key: 'partners',   label: 'Partners',   icon: Users,      count: strategicPartners.length },
    { key: 'prospects',  label: 'Prospects',  icon: FileText },
  ];

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-30 z-40" onClick={onClose} />
      <div className="fixed z-50 flex flex-col shadow-2xl overflow-hidden"
        style={isMobile ? {
          inset: 0,
          width: '100%',
          backgroundColor: '#F9FAFB',
          borderTop: `4px solid ${BRAND_COLORS.gold}`,
          borderRadius: 0,
        } : {
          right: '12px', top: '12px', bottom: '12px',
          width: '62%',
          backgroundColor: '#F9FAFB',
          border: '1px solid #E5E7EB',
          borderLeft: `4px solid ${BRAND_COLORS.gold}`,
          borderRadius: '16px',
        }}>
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-4 flex items-center gap-4" style={{ background: BRAND_COLORS.bgHeader }}>
          <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: BRAND_COLORS.gold }}>
            <DollarSign className="w-6 h-6" style={{ color: BRAND_COLORS.navy }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl font-bold text-white truncate">{deal.dealName || 'Unnamed Deal'}</h2>
              {deal.dealState && (
                <span className="px-2 py-0.5 rounded-full text-xs font-semibold text-white flex-shrink-0"
                  style={{ backgroundColor: statusColor }}>
                  {deal.dealState}
                </span>
              )}
            </div>
            <p className="text-sm mt-0.5" style={{ color: BRAND_COLORS.gold }}>
              {deal.dealType || 'One-time'} · ${(dt.totalReceived || 0).toLocaleString()}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {onToggleHidden && (
              <button
                onClick={() => onToggleHidden(deal.id, !deal.hiddenFromPartners)}
                title={deal.hiddenFromPartners ? 'Show to partners' : 'Hide from partners'}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all hover:opacity-90"
                style={deal.hiddenFromPartners
                  ? { backgroundColor: 'rgba(255,255,255,0.18)', color: 'white', border: '1px solid rgba(255,255,255,0.35)' }
                  : { backgroundColor: 'transparent', color: 'rgba(255,255,255,0.85)', border: '1px solid rgba(255,255,255,0.25)' }}>
                {deal.hiddenFromPartners ? '🙈 Hidden' : 'Hide from Partners'}
              </button>
            )}
            {onEdit && (
              <button onClick={() => onEdit(deal)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all hover:opacity-90"
                style={{ backgroundColor: BRAND_COLORS.gold, color: BRAND_COLORS.navy }}>
                <Edit2 className="w-3.5 h-3.5" />
                Edit
              </button>
            )}
            <button onClick={onClose} className="text-white hover:text-gray-300 transition-colors">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex-shrink-0 flex border-b border-gray-200 bg-white overflow-x-auto">
          {tabs.map(({ key, label, icon: Icon, count }) => (
            <button key={key} onClick={() => setActiveTab(key)}
              className="flex items-center gap-1.5 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors"
              style={{
                borderBottomColor: activeTab === key ? BRAND_COLORS.navy : 'transparent',
                color: activeTab === key ? BRAND_COLORS.navy : '#6B7280',
              }}>
              <Icon className="w-4 h-4" />
              {label}
              {count !== undefined && (
                <span className="px-1.5 py-0.5 rounded-full text-xs"
                  style={{ backgroundColor: activeTab === key ? BRAND_COLORS.navy : '#E5E7EB', color: activeTab === key ? 'white' : '#374151' }}>
                  {count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {activeTab === 'overview' && (
            <>
              <div className="bg-white rounded-xl p-5 space-y-3" style={{ border: '1px solid #E5E7EB' }}>
                <h3 className="text-sm font-semibold uppercase tracking-wide" style={{ color: BRAND_COLORS.navy }}>Deal Info</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><div className="text-xs text-gray-500">Deal Type</div><div className="font-medium" style={{ color: BRAND_COLORS.navy }}>{deal.dealType || 'One-time'}</div></div>
                  <div><div className="text-xs text-gray-500">Currency</div><div className="font-medium" style={{ color: BRAND_COLORS.navy }}>{deal.currency || 'USD'}</div></div>
                  <div><div className="text-xs text-gray-500">Total Deal Size</div><div className="font-medium" style={{ color: BRAND_COLORS.navy }}>${(parseFloat(deal.totalAgreed) || 0).toLocaleString()}</div></div>
                  <div><div className="text-xs text-gray-500">Commission Rate</div><div className="font-medium" style={{ color: BRAND_COLORS.navy }}>{deal.commissionRate || 0}%</div></div>
                  {deal.createdAt && <div><div className="text-xs text-gray-500">Created</div><div className="font-medium" style={{ color: BRAND_COLORS.navy }}>{formatDate(deal.createdAt)}</div></div>}
                  {introPartner && <div><div className="text-xs text-gray-500">Introducing Partner</div><div className="font-medium" style={{ color: BRAND_COLORS.navy }}>{introPartner.name}</div></div>}
                </div>
              </div>
              {deal.notes && (
                <div className="bg-white rounded-xl p-5 space-y-2" style={{ border: '1px solid #E5E7EB' }}>
                  <h3 className="text-sm font-semibold uppercase tracking-wide" style={{ color: BRAND_COLORS.navy }}>Notes</h3>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{deal.notes}</p>
                </div>
              )}
            </>
          )}

          {activeTab === 'financials' && (
            <div className="bg-white rounded-xl p-5 space-y-2" style={{ border: '1px solid #E5E7EB' }}>
              <h3 className="text-sm font-semibold uppercase tracking-wide mb-3" style={{ color: BRAND_COLORS.navy }}>Financial Breakdown</h3>
              <div className="flex justify-between items-center py-2 border-b border-gray-100"><span className="text-sm text-gray-600">Total Received</span><span className="text-base font-bold" style={{ color: BRAND_COLORS.navy }}>${(dt.totalReceived || 0).toLocaleString()}</span></div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100"><span className="text-sm text-gray-600">Deductions</span><span className="text-base font-medium text-red-600">−${(dt.totalDeductions || 0).toLocaleString()}</span></div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100"><span className="text-sm text-gray-600">Pool (Available)</span><span className="text-base font-bold" style={{ color: BRAND_COLORS.gold }}>${(dt.poolAmount || 0).toLocaleString()}</span></div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100"><span className="text-sm text-gray-600">Partner Payouts</span><span className="text-base font-medium" style={{ color: BRAND_COLORS.navy }}>${(dt.totalPartnerPayout || 0).toLocaleString()}</span></div>
              <div className="flex justify-between items-center py-2"><span className="text-sm font-semibold" style={{ color: BRAND_COLORS.statusSuccess }}>Company Keeps</span><span className="text-lg font-bold" style={{ color: BRAND_COLORS.statusSuccess }}>${(dt.companyKeeps || 0).toLocaleString()}</span></div>
            </div>
          )}

          {activeTab === 'payments' && (
            <div className="bg-white rounded-xl p-5 space-y-3" style={{ border: '1px solid #E5E7EB' }}>
              <h3 className="text-sm font-semibold uppercase tracking-wide" style={{ color: BRAND_COLORS.navy }}>Payment History</h3>
              {payments.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">No payments recorded yet</p>
              ) : (
                <div className="divide-y divide-gray-100">
                  {payments.map((pmt, idx) => (
                    <div key={pmt.id || idx} className="py-3 flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium" style={{ color: BRAND_COLORS.navy }}>
                          {pmt.type || 'Payment'} · {pmt.date ? new Date(pmt.date).toLocaleDateString() : '—'}
                        </div>
                        {pmt.notes && <div className="text-xs text-gray-500">{pmt.notes}</div>}
                      </div>
                      <div className="text-base font-bold" style={{ color: BRAND_COLORS.statusSuccess }}>
                        ${(parseFloat(pmt.amount) || 0).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'partners' && (
            <div className="bg-white rounded-xl p-5 space-y-3" style={{ border: '1px solid #E5E7EB' }}>
              <h3 className="text-sm font-semibold uppercase tracking-wide" style={{ color: BRAND_COLORS.navy }}>Strategic Partners</h3>
              {strategicPartners.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">No strategic partners on this deal</p>
              ) : (
                <div className="divide-y divide-gray-100">
                  {strategicPartners.map((sp, idx) => (
                    <div key={sp.partnerId || idx} className="py-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white"
                          style={{ background: 'linear-gradient(135deg, #CBD5E1 0%, #64748B 100%)' }}>
                          {(sp.partner.name || '?')[0].toUpperCase()}
                        </div>
                        <div>
                          <div className="text-sm font-medium" style={{ color: BRAND_COLORS.navy }}>{sp.partner.name}</div>
                          <div className="text-xs text-gray-500">{sp.partner.agreementType}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-bold" style={{ color: BRAND_COLORS.gold }}>${(sp.payout || 0).toLocaleString()}</div>
                        <div className="text-xs text-gray-500">{safePct(sp.payout, dt.poolAmount)}% of pool</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'prospects' && (
            <div className="bg-white rounded-xl p-5 space-y-3" style={{ border: '1px solid #E5E7EB' }}>
              <h3 className="text-sm font-semibold uppercase tracking-wide" style={{ color: BRAND_COLORS.navy }}>Linked Prospects</h3>
              {!clientProspect && !investorProspect ? (
                <p className="text-sm text-gray-400 text-center py-6">No prospects linked</p>
              ) : (
                <div className="space-y-3">
                  {clientProspect && (
                    <div className="p-3 rounded-lg border border-gray-100">
                      <div className="text-xs text-gray-500 mb-1">Client</div>
                      <div className="text-sm font-semibold" style={{ color: BRAND_COLORS.navy }}>{clientProspect.companyName || clientProspect.name}</div>
                      {clientProspect.sector && <div className="text-xs text-gray-500">{clientProspect.sector}{clientProspect.country ? ` · ${clientProspect.country}` : ''}</div>}
                    </div>
                  )}
                  {investorProspect && (
                    <div className="p-3 rounded-lg border border-gray-100">
                      <div className="text-xs text-gray-500 mb-1">Investor</div>
                      <div className="text-sm font-semibold" style={{ color: BRAND_COLORS.navy }}>{investorProspect.companyName || investorProspect.name}</div>
                      {investorProspect.prospectType && <div className="text-xs text-gray-500">{investorProspect.prospectType}</div>}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

const RevenueManagerView = ({ deals, partners, prospects, onSaveDeal, onUpdateDeal, onDeleteDeal, onAddPayment, onDeletePayment, onToggleHidden, onRequestDelete, currentUser, commissionDefaults = DEFAULT_COMMISSION_DEFAULTS, teasers = [], showConfirm, exchangeRates = FALLBACK_RATES, draftDeals = [], onSaveDealDraft, onDiscardDealDraft }) => {
  // Editor+ visibility filter — Active (default), Hidden, All. Partner role
  // never sees hidden rows (RLS), so the control is hidden for that role.
  const [dealVisibilityFilter, setDealVisibilityFilter] = useState('visible');
  const [showCalculator, setShowCalculator] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [editingDealId, setEditingDealId] = useState(null);
  const [draggedDeductionIdx, setDraggedDeductionIdx] = useState(null); // null = new deal, string = editing existing

  const openEditDeal = (deal) => {
    setCurrentDeal({
      dealName: deal.dealName || '',
      serviceType: deal.serviceType || 'One-time',
      dealType: deal.dealType || '',
      dealState: deal.dealState || 'Draft',
      currency: deal.currency || 'USD',
      prospectId: deal.prospectId || '',
      teaserId: deal.teaserId || '',
      investorProspectId: deal.investorProspectId || '',
      dealSize: deal.dealSize || '',
      commissionRate: deal.commissionRate || '',
      totalAgreed: deal.totalAgreed || deal.totalReceived || '',
      isActiveIntroduction: deal.isActiveIntroduction || false,
      overridePercentage: deal.overridePercentage || '',
      deductions: (deal.deductions || []).map(d => ({ ...d })),
      strategicPartners: (deal.strategicPartners || []).map(p => ({ ...p })),
    });
    setEditingDealId(deal.id);
    // A draft resumes on the step it was parked at; a finished deal opens at 1.
    setWizardStep(deal.isDraft ? (deal.draftStep || 1) : 1);
    setShowCalculator(true);
    setTimeout(() => {
      document.querySelector('[data-wizard]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };
  const [expandedDealId, setExpandedDealId] = useState(null);
  const [profileDeal, setProfileDeal] = useState(null);
  const [currentDeal, setCurrentDeal] = useState({
    dealName: '',
    serviceType: 'One-time',
    dealType: '',
    dealState: 'Draft',
    currency: 'USD',
    prospectId: '',
    teaserId: '',
    investorProspectId: '',
    dealSize: '',
    commissionRate: '',
    totalAgreed: '',
    isActiveIntroduction: false,
    overridePercentage: '',
    deductions: [],
    strategicPartners: []
  });
  const [displayCurrencies, setDisplayCurrencies] = useState({}); // dealId → currency code for view-only conversion
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentDealId, setPaymentDealId] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ amount: '', date: new Date().toISOString().split('T')[0], type: 'Success Fee', notes: '' });

  // ── Form config (add / edit / display) ────────────────────────────────────
  const [dealCfg, setDealCfg] = useState({ add: {}, edit: {}, display: {} });
  useEffect(() => {
    (async () => {
      try {
        const { supabase } = await import('./supabaseClient');
        const { data } = await supabase
          .from('form_field_config')
          .select('form_type, is_visible, is_required, role_permissions, custom_field_definitions!inner(field_name, entity_type)')
          .eq('custom_field_definitions.entity_type', 'deal');
        if (data?.length) {
          const map = { add: {}, edit: {}, display: {} };
          data.forEach(row => {
            const fn = row.custom_field_definitions.field_name;
            if (map[row.form_type]) {
              map[row.form_type][fn] = {
                is_visible:       row.is_visible,
                is_required:      row.is_required,
                role_permissions: row.role_permissions || {},
              };
            }
          });
          setDealCfg(map);
        }
      } catch (_) {}
    })();
  }, []);
  const _dealCfgVisible = (fn, ft) => {
    const cfg = dealCfg[ft]?.[fn];
    if (cfg?.is_visible === false) return false;
    if (cfg?.role_permissions?.[currentUser?.role] === false) return false;
    return true;
  };
  const fv  = (fn) => _dealCfgVisible(fn, editingDealId ? 'edit' : 'add');
  const fr  = (fn) => dealCfg[editingDealId ? 'edit' : 'add'][fn]?.is_required  ?? (fn === 'deal_name' || fn === 'prospect_id');
  const fdv = (fn) => _dealCfgVisible(fn, 'display');

  // Auto-apply commission default when prospect changes
  const handleProspectChange = (prospectId) => {
    const prospect = prospects.find(p => p.id === prospectId);
    if (!prospect) {
      setCurrentDeal(d => ({
        ...d,
        prospectId,
        dealName: '',
        teaserId: '',
        strategicPartners: d.strategicPartners.filter(sp => !sp.id?.startsWith('sp_auto_client')),
      }));
      return;
    }

    // Find the partner linked to this prospect
    const partner = prospect.introducingPartnerId ? partners.find(p => p.id === prospect.introducingPartnerId) : null;

    const agreementType = partner?.agreementType;
    const terms = partner?.commissionTerms || DEFAULT_COMMISSION_TERMS[agreementType];

    // Build auto strategic partner entry for the introducing partner.
    // If the deal has an explicit overridePercentage, that wins over the
    // computed default — the UI promises "Override active — default
    // commission will not be applied" and this is where we honor it.
    let autoClientSP = null;
    const overrideVal = parseFloat(currentDeal.overridePercentage);
    const hasOverride = !isNaN(overrideVal) && overrideVal > 0;
    if (partner && (terms || hasOverride)) {
      let defaultPct = null;
      if (hasOverride) {
        defaultPct = overrideVal;
      } else if (terms) {
        if (terms.type === 'fixed') defaultPct = terms.percentage;
        else if (terms.type === 'basic') defaultPct = terms.clientPercentage;
        else if (terms.type === 'table') defaultPct = terms.brackets?.[0]?.nonActive;
        else if (terms.type === 'pool') defaultPct = terms.poolSplitPercentage ?? 50;

        if (!defaultPct && commissionDefaults[agreementType]) {
          defaultPct = commissionDefaults[agreementType].percentage;
        }
      }

      if (defaultPct) {
        autoClientSP = {
          id: `sp_auto_client_${Date.now()}`,
          partnerId: partner.id,
          name: partner.name,
          percentage: defaultPct,
          isExternal: false,
        };
      }
    }

    // Auto-select teaser if prospect has exactly one linked teaser
    const linkedTeaserIds = prospect.teaserIds || [];
    const autoTeaserId = linkedTeaserIds.length === 1 ? linkedTeaserIds[0] : '';

    setCurrentDeal(d => {
      const filteredSPs = d.strategicPartners.filter(sp => !sp.id?.startsWith('sp_auto_client'));
      return {
        ...d,
        prospectId,
        teaserId: autoTeaserId,
        dealName: d.dealName || prospect.companyName || '',
        strategicPartners: autoClientSP ? [...filteredSPs, autoClientSP] : filteredSPs,
      };
    });

    if (!showCalculator) setShowCalculator(true);
  };
  
  // Filter deals based on user role
  const _visibleDealsUnsorted = currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin'
    ? deals
    : deals.filter(deal => {
        const pid = currentUser.partnerId || currentUser.id;
        const linkedProspect = deal.prospectId ? prospects.find(p => p.id === deal.prospectId) : null;
        const isClientPartner = linkedProspect && (
          linkedProspect.introducingPartnerId === pid ||
          linkedProspect.introducingPartnerId === currentUser.id
        );
        const investorProspect = deal.investorProspectId ? prospects.find(p => p.id === deal.investorProspectId) : null;
        const isInvestorPartner = investorProspect?.introducingPartnerId === pid || investorProspect?.introducingPartnerId === currentUser.id;
        const isStrategicPartner = deal.strategicPartners?.some(sp =>
          sp.partnerId === pid || sp.partnerId === currentUser.id
        );
        return isClientPartner || isInvestorPartner || isStrategicPartner;
      });

  // Apply Editor+ visibility filter (skipped for Partner — RLS already hides).
  const _visibilityFiltered = (currentUser.role === 'Partner' || dealVisibilityFilter === 'all')
    ? _visibleDealsUnsorted
    : dealVisibilityFilter === 'hidden'
      ? _visibleDealsUnsorted.filter(d => d.hiddenFromPartners === true)
      : _visibleDealsUnsorted.filter(d => d.hiddenFromPartners !== true);

  // Sortable: default ABC by deal name. Cards aren't <th>-clickable, so we
  // expose a dropdown below for manual sort.
  const { sortedRows: visibleDeals, sortKey: dSortKey, sortDir: dSortDir, toggleSort: toggleDealSort } =
    useSortableTable(_visibilityFiltered, 'dealName', 'asc', {
      dealName:    d => (d.dealName || '').toLowerCase(),
      dealState:   d => (d.dealState || '').toLowerCase(),
      totalAgreed: d => parseFloat(d.totalAgreed) || 0,
      dealStartDate: d => d.dealStartDate ? new Date(d.dealStartDate).getTime() : 0,
    });
  const dealSortOptions = [
    { key: 'dealName',      label: 'Name (A–Z)' },
    { key: 'dealState',     label: 'State' },
    { key: 'totalAgreed',   label: 'Total Agreed' },
    { key: 'dealStartDate', label: 'Date' },
  ];
  
  // Tooltip component
  const InfoTooltip = ({ text }) => {
    const [show, setShow] = useState(false);
    return (
      <div className="relative inline-block ml-1">
        <button
          onMouseEnter={() => setShow(true)}
          onMouseLeave={() => setShow(false)}
          onClick={() => setShow(!show)}
          className="w-4 h-4 rounded-full flex items-center justify-center text-xs font-bold transition-colors"
          style={{ 
            backgroundColor: `${BRAND_COLORS.gold}30`,
            color: BRAND_COLORS.gold
          }}
        >
          i
        </button>
        {show && (
          <div 
            className="absolute left-0 top-6 w-64 bg-gray-900 text-white text-xs rounded-lg p-3 shadow-xl z-50"
            style={{ whiteSpace: 'normal' }}
          >
            {text}
            <div className="absolute -top-1 left-2 w-2 h-2 bg-gray-900 transform rotate-45"></div>
          </div>
        )}
      </div>
    );
  };
  
  const applyPreset = (_preset) => {
    setShowCalculator(true);
  };
  
  // Calculate values
  const totalAgreed = parseFloat(currentDeal.totalAgreed) || 0;
  const totalReceived = totalAgreed; // alias for backward compat in calculator
  
  const calculateDeductions = () => {
    return currentDeal.deductions.reduce((sum, ded) => {
      if (ded.type === 'percentage') {
        return sum + (totalReceived * (parseFloat(ded.value) || 0) / 100);
      } else {
        return sum + (parseFloat(ded.value) || 0);
      }
    }, 0);
  };
  
  const totalDeductions = calculateDeductions();
  const poolAmount = totalReceived - totalDeductions;
  
  // Mirrors computeDealTotals() — see module scope for the full Pool rule.
  // Returns { payouts, slice, poolCount } so the caller can also compute EvE's share.
  const calculatePoolSplit = () => {
    const sps = currentDeal.strategicPartners || [];
    const isPoolMember = (sp) => sp.treatAsFixed === false;
    const sumFixedLocal = sps
      .filter(sp => !isPoolMember(sp))
      .reduce((s, sp) => s + poolAmount * (parseFloat(sp.percentage) || 0) / 100, 0);
    const poolFundLocal = Math.max(0, poolAmount - sumFixedLocal);
    const poolCount = sps.filter(isPoolMember).length;
    const slice = poolCount > 0 ? poolFundLocal / (poolCount + 1) : 0;
    const payouts = sps.map(p => ({
      ...p,
      amount: isPoolMember(p) ? slice : poolAmount * (parseFloat(p.percentage) || 0) / 100,
    }));
    return { payouts, slice, poolCount };
  };
  const calculatePartnerPayouts = () => calculatePoolSplit().payouts;

  const _split = calculatePoolSplit();
  const partnerPayouts = _split.payouts;
  const totalPartnerPayout = partnerPayouts.reduce((sum, p) => sum + p.amount, 0);
  // EvE: one Pool slice when any pool members exist; otherwise the residual.
  const companyKeeps = _split.poolCount > 0 ? _split.slice : poolAmount - totalPartnerPayout;
  
  // Warnings
  const overAllocated = totalDeductions + totalPartnerPayout > totalReceived;
  // Sum of fixed-style partners only (treatAsFixed !== false). Pool members
  // don't contribute — their share is computed from what's left of the pool.
  // Warning fires only when fixed partners alone exceed 100%, which is the
  // genuine over-allocation case.
  const fixedPartnersSum = currentDeal.strategicPartners
    .filter(sp => sp.treatAsFixed !== false)
    .reduce((sum, p) => sum + (parseFloat(p.percentage) || 0), 0);
  // Input display value: stored % for fixed/user-edited rows, dynamic
  // equal-share for unedited Pool members. Keeps the wizard truthful about
  // what will actually be saved. Typing into the input flips the row to
  // fixed mode via updateStrategicPartner, so the typed value is then shown.
  const displayPercentage = (sp) => {
    if (sp.treatAsFixed !== false) return sp.percentage;
    if (poolAmount <= 0) return '';
    const payout = partnerPayouts.find(p => p.id === sp.id);
    if (!payout) return '';
    return Number((payout.amount / poolAmount * 100).toFixed(2));
  };
  
  // Add deduction
  const addDeduction = () => {
    setCurrentDeal({
      ...currentDeal,
      deductions: [...currentDeal.deductions, { 
        id: `ded_${Date.now()}`, 
        name: '', 
        type: 'fixed', 
        value: '', 
        description: '' 
      }]
    });
  };
  
  // Remove deduction
  const removeDeduction = (id) => {
    setCurrentDeal({
      ...currentDeal,
      deductions: currentDeal.deductions.filter(d => d.id !== id)
    });
  };
  
  // Update deduction
  const updateDeduction = (id, field, value) => {
    setCurrentDeal({
      ...currentDeal,
      deductions: currentDeal.deductions.map(d => 
        d.id === id ? { ...d, [field]: value } : d
      )
    });
  };
  
  // Add strategic partner
  const addStrategicPartner = () => {
    setCurrentDeal({
      ...currentDeal,
      strategicPartners: [...currentDeal.strategicPartners, {
        id: `sp_${Date.now()}`,
        isExternal: false,
        partnerId: '',
        name: '',
        externalName: '',
        externalCompany: '',
        externalPaymentDetails: '',
        percentage: '',
        description: ''
      }]
    });
  };
  
  // Remove strategic partner
  const removeStrategicPartner = (id) => {
    setCurrentDeal(d => ({
      ...d,
      strategicPartners: d.strategicPartners.filter(p => p.id !== id)
    }));
  };
  
  // Update strategic partner
  const updateStrategicPartner = (id, updates) => {
    setCurrentDeal(d => ({
      ...d,
      strategicPartners: d.strategicPartners.map(p => {
        if (p.id !== id) return p;
        const next = { ...p, ...updates };
        // Manual percentage edit → mark as user-edited and treat as fixed
        // (so a Pool member's edit takes effect via the Basic path). The
        // auto-rebuild step skips rows with isUserEdited=true, and a Reset
        // button on the row clears these flags back to defaults.
        if ('percentage' in updates && !('isUserEdited' in updates)) {
          next.isUserEdited = true;
          next.treatAsFixed = true;
        }
        return next;
      })
    }));
  };
  
  // Save deal
  const saveDeal = () => {
    if (!currentDeal.dealName) { window.alert('Please fill in deal name'); return; }
    if (!currentDeal.prospectId) { window.alert('Please select a prospect — every deal must be linked to a prospect'); return; }

    const computed = {
      totalAgreed: totalAgreed || null,
      totalReceived: totalAgreed || null,
      totalDeductions: currentDeal.dealState === 'Draft' ? 0 : totalDeductions,
      poolAmount: currentDeal.dealState === 'Draft' ? 0 : poolAmount,
      partnerPayouts: currentDeal.dealState === 'Draft' ? [] : partnerPayouts,
      totalPartnerPayout: currentDeal.dealState === 'Draft' ? 0 : totalPartnerPayout,
      companyKeeps: currentDeal.dealState === 'Draft' ? 0 : companyKeeps,
      // Reaching the final step completes the wizard — clear the draft flag so
      // the deal joins the live lists and totals. (Distinct from dealState's
      // business 'Draft' value above, which the user picks deliberately.)
      isDraft: false,
      draftStep: null,
    };

    if (editingDealId) {
      // Update existing deal
      onUpdateDeal && onUpdateDeal(editingDealId, { ...currentDeal, ...computed });
    } else {
      // Create new deal
      const deal = {
        id: `deal_${Date.now()}`,
        ...currentDeal,
        ...computed,
        payments: [],
        exposure: { showTotal: false, showDeductions: false, showOtherPartners: false },
        createdAt: new Date().toISOString(),
        createdBy: 'Admin'
      };
      onSaveDeal(deal);
    }

    const resetDeal = { dealName: '', serviceType: 'One-time', dealType: '', dealState: 'Draft', currency: 'USD', prospectId: '', teaserId: '', investorProspectId: '', dealSize: '', commissionRate: '', totalAgreed: '', isActiveIntroduction: false, overridePercentage: '', deductions: [], strategicPartners: [] };
    setCurrentDeal(resetDeal);
    setEditingDealId(null);
    setShowCalculator(false);
    setWizardStep(1);
  };

  // ── Save & Close (draft) ───────────────────────────────────────────────────
  // Park the wizard on any step. The commission maths is deliberately NOT
  // snapshotted for a draft: a half-configured deal's pool/payout figures are
  // meaningless, and zeroing them keeps a draft from ever skewing a total even
  // if some future code path forgets to filter drafts out.
  // deal_name + prospect_id are hard server-side requirements on every deal, and
  // both live on step 2 — hence `draftBlockedReason` below.
  const draftBlockedReason =
    !currentDeal.dealName?.trim() ? 'Give the deal a name on Step 2 first — it identifies the draft.'
    : !currentDeal.prospectId     ? 'Link the deal to a prospect on Step 2 first — every deal needs one.'
    : '';

  // This wizard doubles as the EDIT wizard for deals already in the list. When
  // that is what is open, Save & Close must commit to the live deal — flagging
  // an existing deal as a draft would silently pull it out of every total.
  const editingLiveDeal = !!editingDealId && deals.some(d => d.id === editingDealId);

  const saveDealDraftAndClose = async () => {
    if (draftBlockedReason) return;

    if (editingLiveDeal) {
      onUpdateDeal && onUpdateDeal(editingDealId, {
        ...currentDeal,
        totalAgreed: totalAgreed || null,
        totalReceived: totalAgreed || null,
        totalDeductions, poolAmount, partnerPayouts, totalPartnerPayout, companyKeeps,
      });
    } else {
      const draftDeal = {
        ...currentDeal,
        id: editingDealId || `deal_${Date.now()}`,
        totalAgreed: totalAgreed || null,
        totalReceived: totalAgreed || null,
        totalDeductions: 0,
        poolAmount: 0,
        partnerPayouts: [],
        totalPartnerPayout: 0,
        companyKeeps: 0,
        payments: currentDeal.payments || [],
        exposure: currentDeal.exposure || { showTotal: false, showDeductions: false, showOtherPartners: false },
        createdBy: currentDeal.createdBy || currentUser?.name || 'Admin',
      };
      const ok = await onSaveDealDraft?.(draftDeal, wizardStep);
      if (!ok) return;   // keep the wizard open so nothing is lost
    }

    setCurrentDeal({ dealName: '', serviceType: 'One-time', dealType: '', dealState: 'Draft', currency: 'USD', prospectId: '', teaserId: '', investorProspectId: '', dealSize: '', commissionRate: '', totalAgreed: '', isActiveIntroduction: false, overridePercentage: '', deductions: [], strategicPartners: [] });
    setEditingDealId(null);
    setShowCalculator(false);
    setWizardStep(1);
  };

  // Display-currency helper — converts from deal's native currency to the viewer's selected display currency
  const displayAmt = (amount, deal) => {
    const from = deal?.currency || 'USD';
    const to   = displayCurrencies[deal?.id] || from;
    return fmtCurrency(amount, to, from, exchangeRates);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: BRAND_COLORS.navy }}>
            Revenue Pool & Commission Manager
          </h2>
          <p className="text-gray-600 mt-1">
            {(currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin')
              ? 'Calculate complex revenue splits with cascading pool logic'
              : 'View revenue deals where you are a strategic partner'}
          </p>
        </div>
        {(currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') && (
          <button
            onClick={() => setShowCalculator(!showCalculator)}
            className="flex items-center space-x-2 px-4 py-2 rounded-lg font-medium shadow-lg transition-all"
            style={{ background: BRAND_COLORS.gold, color: 'white' }}
          >
            <Plus className="w-5 h-5" />
            <span>New Deal</span>
          </button>
        )}
      </div>

      {/* Key Metrics Cards — Admin/Editor/Super Admin only */}
      {(currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') && (() => {
        const totalDealsCount = visibleDeals.length;
        const activeDealsCount = visibleDeals.filter(d => d.dealState === 'Active').length;
        const closedDealsCount = visibleDeals.filter(d => d.dealState === 'Closed').length;
        const totalRevenue = visibleDeals.reduce((s, d) => s + (d.totalReceived || 0), 0);
        return (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Total Deals',   value: totalDealsCount,                     icon: DollarSign,  accent: BRAND_COLORS.gold },
              { label: 'Active',        value: activeDealsCount,                    icon: PlayCircle,  accent: BRAND_COLORS.statusSuccess },
              { label: 'Closed',        value: closedDealsCount,                    icon: CheckCircle, accent: BRAND_COLORS.statusInfo },
              { label: 'Total Revenue', value: `$${totalRevenue.toLocaleString()}`, icon: TrendingUp,  accent: BRAND_COLORS.gold },
            ].map(c => <KPICard key={c.label} {...c} />)}
          </div>
        );
      })()}

      {/* Calculator — Wizard */}
      {showCalculator && (currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') && (
        <div data-wizard className="bg-white rounded-lg shadow-xl border-2 flex flex-col" style={{ borderColor: BRAND_COLORS.gold, maxHeight: '90vh' }}>

          {/* Wizard Header */}
          <div className="px-6 pt-4 pb-2 flex items-center justify-between border-b border-gray-100 flex-shrink-0">
            <h2 className="text-lg font-bold" style={{ color: BRAND_COLORS.navy }}>
              {editingDealId ? '✏️ Edit Deal' : '+ New Deal'}
            </h2>
            <button onClick={() => { setShowCalculator(false); setEditingDealId(null); setWizardStep(1); }}
              className="text-gray-400 hover:text-gray-600 text-xl font-bold">✕</button>
          </div>

          {/* Wizard Step Indicators */}
          <div className="px-6 pt-5 pb-4 border-b border-gray-100 flex-shrink-0">
            <div className="flex items-center justify-between">
              {[
                { n: 1, label: 'Deal Type' },
                { n: 2, label: 'Deal Info' },
                { n: 3, label: 'Amount' },
                { n: 4, label: 'Deductions' },
                { n: 5, label: 'Partners' },
              ].map(({ n, label }, idx, arr) => (
                <React.Fragment key={n}>
                  <button
                    onClick={() => setWizardStep(n)}
                    className="flex flex-col items-center gap-1"
                  >
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all"
                      style={{
                        backgroundColor: wizardStep === n ? BRAND_COLORS.navy : wizardStep > n ? BRAND_COLORS.gold : '#E5E7EB',
                        color: wizardStep >= n ? 'white' : '#9CA3AF'
                      }}>
                      {wizardStep > n ? '✓' : n}
                    </div>
                    <span className={`text-xs font-medium whitespace-nowrap ${wizardStep === n ? 'inline' : 'hidden'} sm:inline`} style={{ color: wizardStep === n ? BRAND_COLORS.navy : '#9CA3AF' }}>{label}</span>
                  </button>
                  {idx < arr.length - 1 && (
                    <div className="flex-1 h-0.5 mx-2 mb-4 rounded" style={{ backgroundColor: wizardStep > n ? BRAND_COLORS.gold : '#E5E7EB' }} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="overflow-y-auto flex-1">
          {/* Step 1: Deal Type */}
          {wizardStep === 1 && (<>
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold mb-1" style={{ color: BRAND_COLORS.navy }}>Step 1: Deal Type</h3>
            <p className="text-sm text-gray-500 mb-6">Select the type of deal you are working on</p>
            {[
              {
                category: 'M&A',
                items: [
                  { value: 'Raise Equity', label: 'Raise Equity', desc: 'Bring in a minority investor', Icon: TrendingUp },
                  { value: 'Raise Debt', label: 'Raise Debt', desc: 'Secure debt financing', Icon: CreditCard },
                  { value: 'Sell Company', label: 'Sell Company', desc: 'Find a buyer (majority stake)', Icon: ArrowRightLeft },
                  { value: 'Buy Company', label: 'Buy Company', desc: 'Find an acquisition target', Icon: ShoppingBag },
                  { value: 'Invest in Company', label: 'Invest in Company', desc: 'Find an investment target', Icon: PiggyBank },
                ]
              },
              {
                category: 'Real Estate',
                items: [
                  { value: 'RE Sell', label: 'RE Sell', desc: 'Find a buyer for a property', Icon: Home },
                  { value: 'RE Buy', label: 'RE Buy', desc: 'Find a property to purchase', Icon: Key },
                  { value: 'RE Invest', label: 'RE Invest', desc: 'Find a real estate investment', Icon: Building2 },
                ]
              },
              {
                category: 'Other',
                items: [
                  { value: 'Combined', label: 'Combined', desc: 'Multi-type deal', Icon: Layers },
                  { value: 'Advisory', label: 'Advisory', desc: 'Advisory engagement', Icon: Lightbulb },
                  { value: 'Other', label: 'Other', desc: 'Other deal type', Icon: MoreHorizontal },
                ]
              }
            ].map(({ category, items }) => (
              <div key={category} className="mb-6">
                <div className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">{category}</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {items.map(({ value, label, desc, Icon }) => {
                    const isSelected = currentDeal.dealType === value;
                    return (
                      <button
                        key={value}
                        onClick={() => setCurrentDeal(d => ({ ...d, dealType: d.dealType === value ? '' : value }))}
                        className="py-4 px-5 rounded-xl border-2 transition-all text-left"
                        style={{
                          borderColor: isSelected ? BRAND_COLORS.navy : '#e5e7eb',
                          backgroundColor: isSelected ? `${BRAND_COLORS.navy}0d` : 'white',
                          color: isSelected ? BRAND_COLORS.navy : '#374151'
                        }}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className="w-5 h-5 flex-shrink-0" style={{ color: isSelected ? BRAND_COLORS.navy : '#9CA3AF' }} />
                          <span className="font-bold text-sm">{label}</span>
                        </div>
                        <div className="text-xs" style={{ color: isSelected ? `${BRAND_COLORS.navy}99` : '#9CA3AF' }}>{desc}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Deal Currency */}
          <div className="px-6 pb-4">
            <p className="text-sm font-medium text-gray-700 mb-2">Deal Currency</p>
            <div className="flex gap-2">
              {CURRENCIES.map(({ code, symbol }) => (
                <button
                  key={code}
                  onClick={() => setCurrentDeal(d => ({ ...d, currency: code }))}
                  className="px-4 py-2 rounded-lg border-2 text-sm font-semibold transition-all"
                  style={{
                    borderColor: currentDeal.currency === code ? BRAND_COLORS.gold : '#e5e7eb',
                    backgroundColor: currentDeal.currency === code ? `${BRAND_COLORS.gold}20` : 'white',
                    color: currentDeal.currency === code ? BRAND_COLORS.navy : '#6b7280',
                  }}
                >
                  {symbol} {code}
                </button>
              ))}
            </div>
          </div>

          {/* Wizard Step 1 Nav */}
          {/* Save & Close is disabled here: a deal needs a name and a linked
              prospect (both on Step 2) before there is anything to save. */}
          <div className="px-6 pb-5 flex justify-end items-center gap-2">
            <WizardSaveClose onClick={saveDealDraftAndClose} disabledReason={draftBlockedReason} />
            <button onClick={() => setWizardStep(2)}
              className="px-6 py-2 rounded-lg font-medium text-white"
              style={{ backgroundColor: BRAND_COLORS.gold }}>
              Next →
            </button>
          </div>
          </>)}

          {/* Step 2: Deal Info */}
          {wizardStep === 2 && (<>
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold mb-4" style={{ color: BRAND_COLORS.navy }}>Step 2: Deal Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {fv('deal_name') && <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Deal Name{fr('deal_name') ? ' *' : ''}
                </label>
                <input
                  type="text"
                  value={currentDeal.dealName}
                  onChange={(e) => setCurrentDeal({ ...currentDeal, dealName: e.target.value })}
                  placeholder="e.g., Office Building Tel Aviv"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2"
                  style={{ focusRing: BRAND_COLORS.gold }}
                />
              </div>}

              {/* Service Type */}
              {fv('service_type') && <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Service Type
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'One-time', icon: 'Zap', label: 'One-time' },
                    { value: 'Retainer', icon: 'RefreshCw', label: 'Retainer' },
                    { value: 'Success based', icon: 'Target', label: 'Success based' },
                    { value: 'Project', icon: 'ClipboardList', label: 'Project' },
                  ].map(({ value, icon, label }) => {
                    const IconComp = { Zap, RefreshCw, Target, ClipboardList }[icon];
                    return (
                    <button
                      key={value}
                      onClick={() => setCurrentDeal(d => ({ ...d, serviceType: value }))}
                      className="py-2 px-3 text-sm rounded-lg border-2 font-medium transition-all text-left flex items-center gap-2"
                      style={{
                        borderColor: currentDeal.serviceType === value ? BRAND_COLORS.gold : '#e5e7eb',
                        backgroundColor: currentDeal.serviceType === value ? `${BRAND_COLORS.gold}20` : 'white',
                        color: currentDeal.serviceType === value ? BRAND_COLORS.navy : '#6b7280'
                      }}
                    >
                      <IconComp className="w-4 h-4" />{label}
                    </button>);
                  })}
                </div>
                {currentDeal.serviceType === 'Retainer' && (
                  <p className="text-xs text-blue-600 mt-1.5 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> Recurring payments — commission calculated per payment
                  </p>
                )}
                {currentDeal.serviceType === 'Success based' && (
                  <p className="text-xs text-green-600 mt-1.5 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> Commission paid only on successful deal close
                  </p>
                )}
              </div>}

              {/* Deal State */}
              {fv('deal_state') && <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Deal State
                </label>
                <div className="flex gap-2">
                  {[
                    { value: 'Draft', icon: 'FileEdit', label: 'Draft', color: '#9CA3AF', desc: 'Commission not yet agreed' },
                    { value: 'Active', icon: 'PlayCircle', label: 'Active', color: '#3B82F6', desc: 'Commission agreed, payments can be recorded' },
                    { value: 'Closed', icon: 'CheckCircle', label: 'Closed', color: '#10B981', desc: 'Deal fully completed' }
                  ].map(s => {
                    const IconComp = { FileEdit, PlayCircle, CheckCircle }[s.icon];
                    return (
                    <button
                      key={s.value}
                      onClick={() => setCurrentDeal(d => ({ ...d, dealState: s.value }))}
                      className="flex-1 py-2 px-3 text-sm rounded-lg border-2 font-medium transition-all flex items-center justify-center gap-2"
                      style={{
                        borderColor: currentDeal.dealState === s.value ? s.color : '#e5e7eb',
                        backgroundColor: currentDeal.dealState === s.value ? `${s.color}15` : 'white',
                        color: currentDeal.dealState === s.value ? s.color : '#6b7280'
                      }}
                    >
                      <IconComp className="w-4 h-4" />{s.label}
                    </button>);
                  })}
                </div>
                {currentDeal.dealState === 'Draft' && (
                  <p className="text-xs text-gray-400 mt-1.5">💡 Commission structure can be added later when agreed</p>
                )}
                {currentDeal.dealState === 'Active' && (
                  <p className="text-xs text-blue-600 mt-1.5">💡 Payments can be recorded once this deal is saved</p>
                )}
              </div>}

              {fv('prospect_id') && <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Related Prospect <span className="text-red-500">*</span>
                  {currentDeal.prospectId && (() => {
                    const p = prospects.find(pr => pr.id === currentDeal.prospectId);
                    const partner = p?.introducingPartnerId ? partners.find(pt => pt.id === p.introducingPartnerId) : null;
                    const type = partner?.agreementType;
                    const existingDeals = deals.filter(d => d.prospectId === currentDeal.prospectId).length;
                    return (
                      <span className="ml-2 gap-1 inline-flex">
                        {type && (
                          <span className="text-xs font-normal px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: BRAND_COLORS.gold }}>
                            {type}
                          </span>
                        )}
                        {existingDeals > 0 && (
                          <span className="text-xs font-normal px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                            {existingDeals} existing deal{existingDeals > 1 ? 's' : ''}
                          </span>
                        )}
                      </span>
                    );
                  })()}
                </label>
                <select
                  value={currentDeal.prospectId}
                  onChange={(e) => handleProspectChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2"
                  style={{ focusRing: BRAND_COLORS.gold, borderColor: !currentDeal.prospectId ? '#FCA5A5' : '#D1D5DB' }}
                >
                  <option value="">⚠️ Select prospect (required)...</option>
                  {prospects.map(p => {
                    const dealCount = deals.filter(d => d.prospectId === p.id).length;
                    return (
                      <option key={p.id} value={p.id}>
                        {p.companyName || p.name}{dealCount > 0 ? ` (${dealCount} deal${dealCount > 1 ? 's' : ''})` : ''}
                      </option>
                    );
                  })}
                </select>
                {currentDeal.prospectId && (() => {
                  const sp = prospects.find(p => p.id === currentDeal.prospectId);
                  const hasAutoSP = currentDeal.strategicPartners.some(s => s.id?.startsWith('sp_auto_client'));
                  const noPartnerLinked = sp && sp.contractType !== 'ThroughPartner';
                  if (hasAutoSP) return (
                    <p className="text-xs mt-1.5 text-green-700 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> Commission default applied from agreement type
                    </p>
                  );
                  if (noPartnerLinked) return (
                    <p className="text-xs mt-1.5 text-amber-600 flex items-center gap-1">
                      ⚠️ This prospect has no introducing partner linked. Edit the prospect → set Contract Type to "Through Partner" → select the partner. Or add the partner manually in Step 5.
                    </p>
                  );
                  return null;
                })()}
              </div>}

              {/* Investor Prospect */}
              {fv('investor_prospect_id') && <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Investor
                  <span className="ml-2 text-xs font-normal text-gray-400">(Angel or Investment Body — leave empty if none)</span>
                </label>
                <select
                  value={currentDeal.investorProspectId}
                  onChange={(e) => {
                    const investorId = e.target.value;
                    const investorProspect = investorId ? prospects.find(p => p.id === investorId) : null;
                    // Find the partner who introduced this investor prospect (if any)
                    const introducingPartner = investorProspect?.introducingPartnerId
                      ? partners.find(p => p.id === investorProspect.introducingPartnerId)
                      : null;
                    const agreementType = introducingPartner?.agreementType;
                    const terms = introducingPartner?.commissionTerms || (agreementType ? DEFAULT_COMMISSION_TERMS[agreementType] : null);
                    setCurrentDeal(d => {
                      const filteredSPs = d.strategicPartners.filter(sp => !sp.id?.startsWith('sp_auto_investor'));
                      let autoInvestorSP = null;
                      if (investorId && introducingPartner && terms) {
                        let defaultPct = null;
                        if (terms.type === 'fixed') defaultPct = terms.percentage;
                        else if (terms.type === 'basic') defaultPct = terms.investorPercentage;
                        else if (terms.type === 'table') defaultPct = terms.brackets?.[0]?.nonActive;
                        else if (terms.type === 'pool') defaultPct = terms.poolSplitPercentage ?? 50;
                        if (defaultPct) {
                          autoInvestorSP = {
                            id: `sp_auto_investor_${Date.now()}`,
                            partnerId: introducingPartner.id,
                            name: introducingPartner.name,
                            percentage: defaultPct,
                            isExternal: false,
                          };
                        }
                      }
                      return {
                        ...d,
                        investorProspectId: investorId,
                        strategicPartners: autoInvestorSP ? [...filteredSPs, autoInvestorSP] : filteredSPs,
                      };
                    });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2"
                >
                  <option value="">— No investor / EvE directly —</option>
                  {prospects.filter(p => p.prospectType === 'Angel' || p.prospectType === 'InvestmentBody').map(p => (
                    <option key={p.id} value={p.id}>
                      {p.companyName || p.name} ({p.prospectType === 'InvestmentBody' ? p.investmentBodyType || 'Investment Body' : 'Angel'})
                    </option>
                  ))}
                </select>
                {currentDeal.investorProspectId && (() => {
                  const ip = prospects.find(p => p.id === currentDeal.investorProspectId);
                  if (!ip) return null;
                  const intPartner = ip.introducingPartnerId ? partners.find(p => p.id === ip.introducingPartnerId) : null;
                  return (
                    <p className="text-xs mt-1.5 text-green-700 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      {ip.companyName || ip.name}
                      {intPartner
                        ? ` — introduced by ${intPartner.name} (${intPartner.agreementType})`
                        : ' — introduced directly by EvE'}
                    </p>
                  );
                })()}
              </div>}

              {/* Active Introduction Toggle + Commission Override */}
              {currentDeal.prospectId && (() => {
                const prospect = prospects.find(p => p.id === currentDeal.prospectId);
                const partner = prospect?.introducingPartnerId ? partners.find(p => p.id === prospect.introducingPartnerId) : null;
                const agreementType = partner?.agreementType;
                const terms = partner?.commissionTerms || DEFAULT_COMMISSION_TERMS[agreementType];
                // fixed type no longer has active/non-active split — only basic and table do
                const showActiveToggle = terms && (terms.type === 'basic' || terms.type === 'table');
                return showActiveToggle && (fv('is_active_introduction') || fv('override_percentage')) ? (
                  <div className="space-y-3">
                    {/* Active/Non-active */}
                    {fv('is_active_introduction') && <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Introduction Type
                        <span className="ml-2 text-xs font-normal text-gray-400">affects commission %</span>
                      </label>
                      <div className="flex gap-2">
                        {[
                          { value: false, label: 'Non-active', desc: 'Standard referral' },
                          { value: true,  label: '⚡ Active',   desc: 'Significant involvement' }
                        ].map(opt => (
                          <button key={String(opt.value)} type="button"
                            onClick={() => {
                              const overrideVal = parseFloat(currentDeal.overridePercentage);
                              const hasOverride = !isNaN(overrideVal) && overrideVal > 0;
                              const pct = hasOverride ? overrideVal : (partner && terms ? (
                                opt.value
                                  ? (terms.type === 'table' ? terms.brackets?.[0]?.active : terms.type === 'basic' ? terms.investorPercentage : null)
                                  : (terms.type === 'basic' ? terms.clientPercentage : terms.type === 'table' ? terms.brackets?.[0]?.nonActive : null)
                              ) : null);
                              setCurrentDeal(d => ({
                                ...d,
                                isActiveIntroduction: opt.value,
                                ...(pct ? {
                                  // Don't overwrite a user-edited auto-SP (Pool rule fix May 2026).
                                  strategicPartners: d.strategicPartners.map(sp =>
                                    sp.id?.startsWith('sp_auto_client') && !sp.isUserEdited
                                      ? { ...sp, percentage: pct }
                                      : sp
                                  )
                                } : {}),
                              }));
                            }}
                            className="flex-1 py-2 px-3 text-sm rounded-lg border-2 font-medium transition-all"
                            style={{
                              borderColor: currentDeal.isActiveIntroduction === opt.value ? BRAND_COLORS.navy : '#e5e7eb',
                              backgroundColor: currentDeal.isActiveIntroduction === opt.value ? `${BRAND_COLORS.navy}10` : 'white',
                              color: currentDeal.isActiveIntroduction === opt.value ? BRAND_COLORS.navy : '#6b7280'
                            }}
                          >
                            <div>{opt.label}</div>
                            <div className="text-xs opacity-60 font-normal">{opt.desc}</div>
                          </button>
                        ))}
                      </div>
                    </div>}

                    {/* Override % */}
                    {fv('override_percentage') && <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Commission Override
                        <span className="ml-2 text-xs font-normal text-gray-400">leave blank to use default</span>
                      </label>
                      <div className="relative max-w-xs">
                        <input
                          type="number"
                          value={currentDeal.overridePercentage}
                          onChange={e => {
                            const val = e.target.value;
                            const overrideVal = parseFloat(val);
                            const hasOverride = !isNaN(overrideVal) && overrideVal > 0;
                            const fallbackPct = partner && terms ? (
                              currentDeal.isActiveIntroduction
                                ? (terms.type === 'table' ? terms.brackets?.[0]?.active : terms.type === 'basic' ? terms.investorPercentage : null)
                                : (terms.type === 'basic' ? terms.clientPercentage : terms.type === 'table' ? terms.brackets?.[0]?.nonActive : (terms.type === 'fixed' ? terms.percentage : terms.type === 'pool' ? (terms.poolSplitPercentage ?? 50) : null))
                            ) : null;
                            const targetPct = hasOverride ? overrideVal : fallbackPct;
                            setCurrentDeal(d => ({
                              ...d,
                              overridePercentage: val,
                              ...(targetPct != null ? {
                                // Override set ⇒ pin auto-SP to fixed (treatAsFixed=true) and mark as user-edited.
                                // Override cleared ⇒ revert to MCA default (treatAsFixed reflects agreementType).
                                strategicPartners: d.strategicPartners.map(sp =>
                                  sp.id?.startsWith('sp_auto_client')
                                    ? {
                                        ...sp,
                                        percentage: targetPct,
                                        isUserEdited: hasOverride,
                                        treatAsFixed: hasOverride ? true : (sp.agreementType !== 'pool'),
                                      }
                                    : sp
                                )
                              } : {}),
                            }));
                          }}
                          placeholder={`Default: calculated`}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm pr-8"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
                      </div>
                      {currentDeal.overridePercentage && (
                        <p className="text-xs text-amber-600 mt-1">⚠️ Override active — default commission will not be applied</p>
                      )}
                    </div>}
                  </div>
                ) : null;
              })()}

              {/* Linked Teaser */}
              {currentDeal.prospectId && (() => {
                const prospect = prospects.find(p => p.id === currentDeal.prospectId);
                const linkedTeaserIds = prospect?.teaserIds || [];
                const availableTeasers = teasers ? teasers.filter(t => linkedTeaserIds.includes(t.id)) : [];
                if (availableTeasers.length === 0 || !fv('teaser_id')) return null;
                return (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Linked Teaser
                      <span className="ml-2 text-xs font-normal text-gray-400">(optional)</span>
                    </label>
                    <select
                      value={currentDeal.teaserId}
                      onChange={e => setCurrentDeal(d => ({ ...d, teaserId: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 text-sm"
                    >
                      <option value="">— No teaser linked —</option>
                      {availableTeasers.map(t => (
                        <option key={t.id} value={t.id}>{t.name || t.companyName}</option>
                      ))}
                    </select>
                    {currentDeal.teaserId && (
                      <p className="text-xs mt-1.5 text-green-700 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" /> Teaser linked to this deal
                      </p>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Wizard Step 2 Nav */}
          <div className="px-6 pb-5 flex justify-between items-center">
            <button onClick={() => setWizardStep(1)}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">
              ← Back
            </button>
            <WizardSaveClose onClick={saveDealDraftAndClose} disabledReason={draftBlockedReason} />
            <button onClick={() => setWizardStep(3)}
              className="px-6 py-2 rounded-lg font-medium text-white"
              style={{ backgroundColor: BRAND_COLORS.gold }}>
              Next →
            </button>
          </div>
          </>)}

          {/* Step 3: Total Agreed */}
          {wizardStep === 3 && (<>
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center mb-2">
              <h3 className="text-lg font-semibold" style={{ color: BRAND_COLORS.navy }}>
                Step 3: Commission Amount
              </h3>
              <InfoTooltip text="The total commission EvE is expected to receive from this deal. Use the calculator to derive it from deal size × commission rate, or enter manually." />
            </div>
            <p className="text-xs text-gray-400 mb-4">Optional at Draft stage — required before recording payments</p>

            {/* Deal structure calculator */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4 max-w-lg">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Deal Structure (optional)</p>
              <div className="flex items-end gap-3 flex-wrap">
                <div className="flex-1 min-w-[140px]">
                  <label className="block text-xs text-gray-500 mb-1">Total Deal Size</label>
                  <div className="relative">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                      {CURRENCIES.find(c => c.code === (currentDeal.currency || 'USD'))?.symbol || '$'}
                    </span>
                    <input
                      type="number"
                      value={currentDeal.dealSize}
                      onChange={(e) => setCurrentDeal(d => ({ ...d, dealSize: e.target.value }))}
                      placeholder="150,000,000"
                      className="w-full pl-6 pr-2 py-2 text-sm border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
                <div className="text-gray-400 font-bold pb-2">×</div>
                <div className="w-28">
                  <label className="block text-xs text-gray-500 mb-1">Commission Rate</label>
                  <div className="relative">
                    <input
                      type="number"
                      value={currentDeal.commissionRate}
                      onChange={(e) => setCurrentDeal(d => ({ ...d, commissionRate: e.target.value }))}
                      placeholder="5"
                      min="0" max="100" step="0.1"
                      className="w-full pl-2 pr-6 py-2 text-sm border border-gray-300 rounded-lg"
                    />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
                  </div>
                </div>
                <div className="text-gray-400 font-bold pb-2">=</div>
                <div className="pb-1">
                  {(() => {
                    const size = parseFloat(currentDeal.dealSize) || 0;
                    const rate = parseFloat(currentDeal.commissionRate) || 0;
                    const calc = size * rate / 100;
                    const sym = CURRENCIES.find(c => c.code === (currentDeal.currency || 'USD'))?.symbol || '$';
                    return calc > 0 ? (
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-bold" style={{ color: BRAND_COLORS.navy }}>
                          {sym}{Math.round(calc).toLocaleString()}
                        </span>
                        <button
                          onClick={() => setCurrentDeal(d => ({ ...d, totalAgreed: String(Math.round(calc)) }))}
                          className="text-xs px-2 py-1 rounded font-medium text-white whitespace-nowrap"
                          style={{ backgroundColor: BRAND_COLORS.gold }}
                        >
                          Use ↓
                        </button>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">—</span>
                    );
                  })()}
                </div>
              </div>
            </div>

            {fv('total_agreed') && <div className="max-w-md">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Commission Agreed</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-bold">
                  {CURRENCIES.find(c => c.code === (currentDeal.currency || 'USD'))?.symbol || '$'}
                </span>
                <input
                  type="number"
                  value={currentDeal.totalAgreed}
                  onChange={(e) => setCurrentDeal({ ...currentDeal, totalAgreed: e.target.value })}
                  placeholder="7,500,000"
                  className="w-full pl-8 pr-3 py-3 text-2xl font-bold border-2 rounded-lg focus:ring-2"
                  style={{
                    borderColor: BRAND_COLORS.gold,
                    color: BRAND_COLORS.navy
                  }}
                />
              </div>
              <p className="text-xs text-gray-400 mt-1">Amounts stored in {currentDeal.currency || 'USD'}</p>
            </div>}
          </div>

          {/* Wizard Step 3 Nav */}
          <div className="px-6 pb-5 flex justify-between items-center">
            <button onClick={() => setWizardStep(2)}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">
              ← Back
            </button>
            <WizardSaveClose onClick={saveDealDraftAndClose} disabledReason={draftBlockedReason} />
            <button onClick={() => setWizardStep(4)}
              className="px-6 py-2 rounded-lg font-medium text-white"
              style={{ backgroundColor: BRAND_COLORS.gold }}>
              Next →
            </button>
          </div>
          </>)}

          {/* Step 4: External Deductions */}
          {wizardStep === 4 && (<>
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <h3 className="text-lg font-semibold" style={{ color: BRAND_COLORS.navy }}>
                  Step 4: External Deductions
                </h3>
                <InfoTooltip text="These are payments to third parties (finders, vendors, referral fees) that are subtracted BEFORE we calculate the pool for internal sharing." />
              </div>
              {fv('deductions') && <button
                onClick={addDeduction}
                className="flex items-center space-x-1 px-3 py-1 text-sm rounded-lg"
                style={{ backgroundColor: `${BRAND_COLORS.royalBlue}20`, color: BRAND_COLORS.royalBlue }}
              >
                <Plus className="w-4 h-4" />
                <span>Add Deduction</span>
              </button>}
            </div>

            {fv('deductions') && (currentDeal.deductions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p className="text-sm">No deductions added. Click "Add Deduction" to start.</p>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-gray-500 mb-1">Drag to reorder — deductions are applied top to bottom</p>
                {currentDeal.deductions.map((ded, idx) => (
                  <div
                    key={ded.id}
                    draggable
                    onDragStart={() => setDraggedDeductionIdx(idx)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => {
                      if (draggedDeductionIdx === null || draggedDeductionIdx === idx) return;
                      const reordered = [...currentDeal.deductions];
                      const [moved] = reordered.splice(draggedDeductionIdx, 1);
                      reordered.splice(idx, 0, moved);
                      setCurrentDeal(d => ({ ...d, deductions: reordered }));
                      setDraggedDeductionIdx(null);
                    }}
                    onDragEnd={() => setDraggedDeductionIdx(null)}
                    className="grid grid-cols-12 gap-3 items-start p-3 bg-gray-50 rounded-lg transition-all"
                    style={{ opacity: draggedDeductionIdx === idx ? 0.4 : 1, cursor: 'grab' }}
                  >
                    <div className="col-span-1 flex items-center gap-1">
                      <span className="text-xs font-bold text-gray-400">#{idx+1}</span>
                      <div className="flex flex-col gap-0.5">
                        <div className="w-4 h-0.5 bg-gray-300 rounded" />
                        <div className="w-4 h-0.5 bg-gray-300 rounded" />
                        <div className="w-4 h-0.5 bg-gray-300 rounded" />
                      </div>
                    </div>
                    <div className="col-span-3">
                      <input type="text" value={ded.name}
                        onChange={(e) => updateDeduction(ded.id, 'name', e.target.value)}
                        placeholder="Name" className="w-full px-2 py-1 text-sm border border-gray-300 rounded" />
                    </div>
                    <div className="col-span-2">
                      <select value={ded.type} onChange={(e) => updateDeduction(ded.id, 'type', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded">
                        <option value="fixed">Fixed $</option>
                        <option value="percentage">Percentage %</option>
                      </select>
                    </div>
                    <div className="col-span-2">
                      <input type="number" value={ded.value}
                        onChange={(e) => updateDeduction(ded.id, 'value', e.target.value)}
                        placeholder={ded.type === 'percentage' ? '10' : '5000'}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded" />
                    </div>
                    <div className="col-span-3">
                      <input type="text" value={ded.description}
                        onChange={(e) => updateDeduction(ded.id, 'description', e.target.value)}
                        placeholder="Note" className="w-full px-2 py-1 text-sm border border-gray-300 rounded" />
                    </div>
                    <div className="col-span-1 flex justify-end">
                      <button onClick={() => removeDeduction(ded.id)} className="p-1 text-red-500 hover:bg-red-50 rounded">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* Wizard Step 4 Nav */}
          <div className="px-6 pb-5 flex justify-between items-center">
            <button onClick={() => setWizardStep(3)}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">
              ← Back
            </button>
            <WizardSaveClose onClick={saveDealDraftAndClose} disabledReason={draftBlockedReason} />
            <button onClick={() => {
                // Recalculate auto-SPs from current partner terms (handles partner MCA changes + missing SPs).
                // Skips any auto-SP that was manually edited (`isUserEdited === true`) — the user's
                // value wins. New auto-SPs created here record their MCA `agreementType` and set
                // `treatAsFixed: false` for Pool partners so the equal-share rule applies.
                setCurrentDeal(d => {
                  // Keep manual SPs AND any auto-SPs flagged as user-edited.
                  let sps = d.strategicPartners.filter(sp => {
                    const isAuto = sp.id?.startsWith('sp_auto_client') || sp.id?.startsWith('sp_auto_investor');
                    return !isAuto || sp.isUserEdited === true;
                  });
                  const hasEditedClientAuto = sps.some(sp => sp.id?.startsWith('sp_auto_client'));
                  const hasEditedInvestorAuto = sps.some(sp => sp.id?.startsWith('sp_auto_investor'));

                  const calcPct = (terms, isActive, side) => {
                    if (!terms) return null;
                    if (terms.type === 'fixed') return terms.percentage;
                    if (terms.type === 'basic') return side === 'investor' ? terms.investorPercentage : (isActive ? terms.investorPercentage : terms.clientPercentage);
                    if (terms.type === 'table') return terms.brackets?.[0]?.[isActive ? 'active' : 'nonActive'];
                    if (terms.type === 'pool') return terms.poolSplitPercentage ?? 50;
                    return null;
                  };

                  // Client side — override (when set) wins over computed default
                  if (d.prospectId && !hasEditedClientAuto) {
                    const prospect = prospects.find(p => p.id === d.prospectId);
                    const partner = prospect?.introducingPartnerId ? partners.find(p => p.id === prospect.introducingPartnerId) : null;
                    if (partner) {
                      const terms = partner.commissionTerms || DEFAULT_COMMISSION_TERMS[partner.agreementType];
                      const overrideVal = parseFloat(d.overridePercentage);
                      const hasOverride = !isNaN(overrideVal) && overrideVal > 0;
                      const pct = hasOverride ? overrideVal : calcPct(terms, d.isActiveIntroduction, 'client');
                      if (pct) sps = [...sps, {
                        id: `sp_auto_client_${Date.now()}`,
                        partnerId: partner.id,
                        name: partner.name,
                        percentage: pct,
                        isExternal: false,
                        agreementType: terms?.type,
                        treatAsFixed: terms?.type !== 'pool' || hasOverride, // Pool default = equal-share; explicit override pins to fixed
                        isUserEdited: false,
                      }];
                    }
                  }

                  // Investor side
                  if (d.investorProspectId && !hasEditedInvestorAuto) {
                    const invProspect = prospects.find(p => p.id === d.investorProspectId);
                    const partner = invProspect?.introducingPartnerId ? partners.find(p => p.id === invProspect.introducingPartnerId) : null;
                    if (partner) {
                      const terms = partner.commissionTerms || DEFAULT_COMMISSION_TERMS[partner.agreementType];
                      const pct = calcPct(terms, false, 'investor');
                      if (pct) sps = [...sps, {
                        id: `sp_auto_investor_${Date.now()}`,
                        partnerId: partner.id,
                        name: partner.name,
                        percentage: pct,
                        isExternal: false,
                        agreementType: terms?.type,
                        treatAsFixed: terms?.type !== 'pool',
                        isUserEdited: false,
                      }];
                    }
                  }

                  return { ...d, strategicPartners: sps };
                });
                setWizardStep(5);
              }}
              className="px-6 py-2 rounded-lg font-medium text-white"
              style={{ backgroundColor: BRAND_COLORS.gold }}>
              Next →
            </button>
          </div>
          </>)}

          {/* Step 5: Strategic Partners */}
          {wizardStep === 5 && (<>
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <h3 className="text-lg font-semibold" style={{ color: BRAND_COLORS.navy }}>
                  Step 5: Strategic Partners Share
                </h3>
                <InfoTooltip text="These partners receive their percentage from THE POOL (money left after external deductions), not from the original gross amount." />
              </div>
              {fv('strategic_partners') && <button
                onClick={addStrategicPartner}
                className="flex items-center space-x-1 px-3 py-1 text-sm rounded-lg"
                style={{ backgroundColor: `${BRAND_COLORS.gold}20`, color: BRAND_COLORS.gold }}
              >
                <Plus className="w-4 h-4" />
                <span>Add Partner</span>
              </button>}
            </div>

            {fv('strategic_partners') && (currentDeal.strategicPartners.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p className="text-sm">No strategic partners added.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Pool rule (May 2026): warn when fixed-style partners' % sum > 100% */}
                {(() => {
                  const fixedSum = currentDeal.strategicPartners
                    .filter(sp => sp.treatAsFixed !== false)
                    .reduce((s, sp) => s + (parseFloat(sp.percentage) || 0), 0);
                  if (fixedSum > 100) {
                    return (
                      <div className="px-4 py-3 rounded-lg border-2 flex items-start gap-2"
                        style={{ borderColor: '#DC2626', backgroundColor: '#FEF2F2' }}>
                        <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#DC2626' }} />
                        <div className="text-sm" style={{ color: '#7F1D1D' }}>
                          <strong>Fixed-percentage partners total {fixedSum.toFixed(1)}%</strong> — over 100%.
                          The pool fund and EvE's share will be 0. Reduce a percentage or remove a partner.
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
                {currentDeal.strategicPartners.map((partner) => (
                  <div key={partner.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    {/* Toggle: Internal / External */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => updateStrategicPartner(partner.id, { isExternal: false })}
                          className="px-3 py-1 text-xs rounded-full border font-medium transition-all"
                          style={{
                            borderColor: !partner.isExternal ? BRAND_COLORS.navy : '#e5e7eb',
                            backgroundColor: !partner.isExternal ? BRAND_COLORS.navy : 'white',
                            color: !partner.isExternal ? 'white' : '#6b7280'
                          }}
                        >
                          👤 System User
                        </button>
                        <button
                          onClick={() => updateStrategicPartner(partner.id, { isExternal: true })}
                          className="px-3 py-1 text-xs rounded-full border font-medium transition-all"
                          style={{
                            borderColor: partner.isExternal ? BRAND_COLORS.gold : '#e5e7eb',
                            backgroundColor: partner.isExternal ? `${BRAND_COLORS.gold}20` : 'white',
                            color: partner.isExternal ? BRAND_COLORS.navy : '#6b7280'
                          }}
                        >
                          🌐 External
                        </button>
                      </div>
                      <button
                        onClick={() => removeStrategicPartner(partner.id)}
                        className="p-1 text-red-500 hover:bg-red-50 rounded"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-12 gap-3 items-start">
                      {/* Name field — internal or external */}
                      <div className="col-span-5">
                        {!partner.isExternal ? (
                          <select
                            value={partner.partnerId}
                            onChange={(e) => {
                              const selectedPartner = partners.find(p => p.id === e.target.value);
                              // When a Pool-MCA partner is picked, default the row to Pool member
                              // (treatAsFixed=false). Anything else stays Basic (treatAsFixed=true).
                              const terms = selectedPartner?.commissionTerms || DEFAULT_COMMISSION_TERMS[selectedPartner?.agreementType];
                              const isPool = terms?.type === 'pool';
                              updateStrategicPartner(partner.id, {
                                partnerId: e.target.value,
                                ...(selectedPartner ? {
                                  name: selectedPartner.name,
                                  agreementType: terms?.type,
                                  treatAsFixed: !isPool,
                                  // Reset isUserEdited so the row goes back to "default" mode for the new partner
                                  isUserEdited: false,
                                } : {}),
                              });
                            }}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                          >
                            <option value="">Select partner...</option>
                            {partners.filter(p => p.status === 'Active' || p.status === 'In Tail').map(p => (
                              <option key={p.id} value={p.id}>{p.name} ({p.status})</option>
                            ))}
                          </select>
                        ) : (
                          <div className="space-y-1.5">
                            <input
                              type="text"
                              value={partner.externalName}
                              onChange={(e) => updateStrategicPartner(partner.id, { externalName: e.target.value })}
                              placeholder="Full name"
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                            />
                            <input
                              type="text"
                              value={partner.externalCompany}
                              onChange={(e) => updateStrategicPartner(partner.id, { externalCompany: e.target.value })}
                              placeholder="Company (optional)"
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                            />
                            <input
                              type="text"
                              value={partner.externalPaymentDetails}
                              onChange={(e) => updateStrategicPartner(partner.id, { externalPaymentDetails: e.target.value })}
                              placeholder="Payment details (optional)"
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                            />
                          </div>
                        )}
                      </div>

                      {/* Percentage */}
                      <div className="col-span-2">
                        <div className="relative">
                          <input
                            type="number"
                            value={displayPercentage(partner)}
                            onChange={(e) => updateStrategicPartner(partner.id, { percentage: e.target.value })}
                            placeholder="20"
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded pr-6"
                            style={partner.isUserEdited ? { borderColor: BRAND_COLORS.gold, backgroundColor: '#FFFBEB' } : undefined}
                            title={
                              partner.isUserEdited
                                ? 'Manually edited — auto-rebuild will not overwrite this value'
                                : (partner.treatAsFixed === false ? 'Live equal-share of pool — type to override with a fixed percentage' : undefined)
                            }
                          />
                          <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">%</span>
                        </div>
                        {/* Reset to MCA default — only for user-edited auto-SPs */}
                        {partner.isUserEdited && partner.id?.startsWith('sp_auto_') && (
                          <button
                            type="button"
                            onClick={() => {
                              // Recompute MCA default and clear flags. agreementType is captured on the SP at rebuild time.
                              const partnerRec = partners.find(p => p.id === partner.partnerId);
                              const terms = partnerRec?.commissionTerms || DEFAULT_COMMISSION_TERMS[partnerRec?.agreementType];
                              const isInvestor = partner.id?.startsWith('sp_auto_investor');
                              let pct = null;
                              if (terms?.type === 'fixed') pct = terms.percentage;
                              else if (terms?.type === 'basic') pct = isInvestor ? terms.investorPercentage : (currentDeal.isActiveIntroduction ? terms.investorPercentage : terms.clientPercentage);
                              else if (terms?.type === 'table') pct = terms.brackets?.[0]?.[currentDeal.isActiveIntroduction ? 'active' : 'nonActive'];
                              else if (terms?.type === 'pool') pct = terms.poolSplitPercentage ?? 50;
                              updateStrategicPartner(partner.id, {
                                percentage: pct ?? partner.percentage,
                                isUserEdited: false,
                                treatAsFixed: terms?.type !== 'pool',
                              });
                            }}
                            className="text-xs mt-1 underline"
                            style={{ color: BRAND_COLORS.gold }}
                          >
                            Reset to default
                          </button>
                        )}
                      </div>

                      {/* Description */}
                      <div className="col-span-5">
                        <input
                          type="text"
                          value={partner.description}
                          onChange={(e) => updateStrategicPartner(partner.id, { description: e.target.value })}
                          placeholder="Role / note"
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        />
                      </div>
                    </div>

                    {/* Sub-allocation */}
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-medium text-gray-500">
                          Sub-recipients <span className="text-gray-400 font-normal">(receive % of {partner.name || partner.externalName || 'this partner'}'s share)</span>
                        </p>
                        <button
                          onClick={() => {
                            const sub = { id: `sub_${Date.now()}`, name: '', percentage: 0, note: '' };
                            updateStrategicPartner(partner.id, { subRecipients: [...(partner.subRecipients || []), sub] });
                          }}
                          className="text-xs px-2 py-1 rounded text-white"
                          style={{ backgroundColor: BRAND_COLORS.royalBlue }}
                        >
                          + Add sub-recipient
                        </button>
                      </div>
                      {(partner.subRecipients || []).map((sub, si) => {
                        const subAbsolute = ((parseFloat(partner.percentage) || 0) * (parseFloat(sub.percentage) || 0) / 100);
                        return (
                          <div key={sub.id} className="flex items-center gap-2 mb-1.5 pl-3 border-l-2" style={{ borderColor: BRAND_COLORS.gold }}>
                            <span className="text-xs text-gray-400">↳</span>
                            <input type="text" value={sub.name}
                              onChange={e => {
                                const updated = (partner.subRecipients || []).map((s,i) => i===si ? {...s, name: e.target.value} : s);
                                updateStrategicPartner(partner.id, { subRecipients: updated });
                              }}
                              placeholder="Name" className="flex-1 px-2 py-1 text-xs border border-gray-200 rounded" />
                            <div className="relative">
                              <input type="number" value={sub.percentage} min="0" max="100"
                                onChange={e => {
                                  const updated = (partner.subRecipients || []).map((s,i) => i===si ? {...s, percentage: e.target.value} : s);
                                  updateStrategicPartner(partner.id, { subRecipients: updated });
                                }}
                                placeholder="%" className="w-16 px-2 py-1 text-xs border border-gray-200 rounded pr-5" />
                              <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">%</span>
                            </div>
                            {totalReceived > 0 && (
                              <span className="text-xs font-medium px-1.5 py-0.5 rounded" style={{ backgroundColor: `${BRAND_COLORS.gold}20`, color: BRAND_COLORS.navy }}>
                                = {subAbsolute.toFixed(1)}% of pool
                              </span>
                            )}
                            <input type="text" value={sub.note}
                              onChange={e => {
                                const updated = (partner.subRecipients || []).map((s,i) => i===si ? {...s, note: e.target.value} : s);
                                updateStrategicPartner(partner.id, { subRecipients: updated });
                              }}
                              placeholder="Note" className="flex-1 px-2 py-1 text-xs border border-gray-200 rounded" />
                            <button onClick={() => {
                              const updated = (partner.subRecipients || []).filter((_,i) => i!==si);
                              updateStrategicPartner(partner.id, { subRecipients: updated });
                            }} className="text-red-400 hover:text-red-600">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        );
                      })}
                      {(partner.subRecipients || []).length > 0 && (
                        <p className="text-xs text-gray-400 pl-3 mt-1">
                          Sub-recipients total: {(partner.subRecipients || []).reduce((s,r) => s + (parseFloat(r.percentage)||0), 0).toFixed(0)}% of {partner.name || 'partner'}'s share
                          {(partner.subRecipients || []).reduce((s,r) => s + (parseFloat(r.percentage)||0), 0) > 100 && (
                            <span className="text-red-500 ml-1">⚠️ exceeds 100%</span>
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
                
                {Math.round(fixedPartnersSum * 100) / 100 > 100 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm text-red-700">
                        Warning: Fixed partners total {fixedPartnersSum}% — exceeds 100% of the pool. Pool members and EvE will receive nothing.
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Visual Breakdown - Waterfall */}
          {totalReceived > 0 && (
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center mb-5">
                <h3 className="text-lg font-semibold" style={{ color: BRAND_COLORS.navy }}>
                  Money Flow
                </h3>
                <InfoTooltip text="Shows how money flows from total received down to each party." />
              </div>

              {/* Tier styles via JS object */}
              {(() => {
                const barStyle = (borderColor, textColor, fullWidth = false, maxW = null) => ({
                  background: '#fff',
                  border: `2px solid ${borderColor}`,
                  borderRadius: '10px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0 16px',
                  width: fullWidth ? '100%' : undefined,
                  maxWidth: maxW || undefined,
                });
                const lbl = (color) => ({ color, fontWeight: 600, fontSize: '14px' });
                const pct = (color) => ({ color, fontSize: '12px', opacity: 0.65 });
                const indentLine = { position: 'absolute', left: '10px', top: 0, bottom: 0, width: '1px', background: 'rgba(0,0,0,0.1)', borderRadius: '1px' };
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {/* Total */}
                    <div style={barStyle('#1A1C5C', '#1A1C5C', true)}>
                      <span style={lbl('#1A1C5C')}>${totalReceived.toLocaleString()} — Total received</span>
                      <span style={pct('#1A1C5C')}>100%</span>
                    </div>

                    {/* Indent level 1 */}
                    <div style={{ paddingLeft: '24px', position: 'relative', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <div style={indentLine} />

                      {/* Deductions */}
                      {totalDeductions > 0 && (
                        <div style={{ ...barStyle('#8a3018', '#8a3018'), maxWidth: '280px' }}>
                          <span style={lbl('#8a3018')}>−${totalDeductions.toLocaleString()} Deductions</span>
                          <span style={pct('#8a3018')}>{safePct(totalDeductions, totalReceived)}%</span>
                        </div>
                      )}

                      {/* Pool */}
                      <div style={barStyle('#b8860b', '#7a5018', true)}>
                        <span style={lbl('#7a5018')}>${poolAmount.toLocaleString()} — Pool</span>
                        <span style={pct('#7a5018')}>{safePct(poolAmount, totalReceived)}%</span>
                      </div>

                      {/* Indent level 2 — partners + company */}
                      <div style={{ paddingLeft: '24px', position: 'relative', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <div style={indentLine} />
                        {partnerPayouts.map((payout, idx) => payout.amount > 0 && (
                          <div key={idx} style={{ ...barStyle('#185FA5', '#185FA5'), maxWidth: '360px' }}>
                            <span style={lbl('#185FA5')}>{payout.name}</span>
                            <span style={pct('#185FA5')}>${payout.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} · {safePct(payout.amount, poolAmount)}%</span>
                          </div>
                        ))}
                        <div style={barStyle('#0F6E56', '#0F6E56', true)}>
                          <span style={lbl('#0F6E56')}>Company keeps</span>
                          <span style={pct('#0F6E56')}>${companyKeeps.toLocaleString()} · {safePct(companyKeeps, poolAmount)}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Summary cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: '10px', marginTop: '20px' }}>
                {[
                  { label: 'Deductions',      value: `$${totalDeductions.toLocaleString()}`,    sub: `${safePct(totalDeductions, totalReceived)}% of total`, accent: BRAND_COLORS.statusError },
                  { label: 'Partner payouts', value: `$${totalPartnerPayout.toLocaleString()}`, sub: `${safePct(totalPartnerPayout, poolAmount)}% of pool`, accent: BRAND_COLORS.statusInfo },
                  { label: 'Company keeps',   value: `$${companyKeeps.toLocaleString()}`,       sub: `${safePct(companyKeeps, poolAmount)}% of pool`,        accent: BRAND_COLORS.statusSuccess },
                ].map(c => <KPICard key={c.label} size="compact" {...c} />)}
              </div>
            </div>
          )}

          {/* Summary Sentence */}
          {totalReceived > 0 && (
            <div className="p-6 border-b border-gray-200">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                <h4 className="font-semibold text-blue-900 mb-2">Deal Summary</h4>
                <p className="text-blue-800 text-sm leading-relaxed">
                  Out of <strong>${totalReceived.toLocaleString()}</strong> total received,
                  <strong> ${totalDeductions.toLocaleString()}</strong> went to external parties (deductions).
                  This left <strong>${poolAmount.toLocaleString()}</strong> as the pool for internal sharing.
                  {partnerPayouts.map((p, idx) => (
                    p.amount > 0 && (
                      <span key={idx}>
                        {' '}<strong>{p.name}</strong> gets <strong>${p.amount.toLocaleString()}</strong>
                        {idx < partnerPayouts.length - 1 ? ',' : ''}
                      </span>
                    )
                  ))}
                  {' '}and the company keeps <strong>${companyKeeps.toLocaleString()}</strong>.
                </p>
              </div>
            </div>
          )}
          
          {/* Warnings */}
          {overAllocated && (
            <div className="p-6 border-b border-gray-200">
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 mr-2" />
                  <div>
                    <h4 className="font-semibold text-red-900 mb-1">  Over-Allocated!</h4>
                    <p className="text-red-800 text-sm">
                      The total payouts (${(totalDeductions + totalPartnerPayout).toLocaleString()}) exceed the gross revenue (${totalReceived.toLocaleString()}). 
                      Please adjust the deductions or partner percentages.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Action Buttons — Step 5 */}
          <div className="p-6 flex justify-between">
            <button
              onClick={() => setWizardStep(4)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              ← Back
            </button>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowCalculator(false);
                  setWizardStep(1);
                  setEditingDealId(null);
                  setCurrentDeal({
                    dealName: '', serviceType: 'One-time', dealType: '', dealState: 'Draft', currency: 'USD',
                    prospectId: '', teaserId: '', investorProspectId: '', totalAgreed: '',
                    isActiveIntroduction: false, overridePercentage: '', deductions: [], strategicPartners: []
                  });
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={saveDeal}
                disabled={overAllocated || !currentDeal.dealName}
                className="px-6 py-2 rounded-lg font-medium text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: BRAND_COLORS.gold }}
              >
                Save Deal
              </button>
            </div>
          </div>
          </>)}
          </div>
        </div>
      )}

      {/* Unfinished deal wizards parked with Save & Close. Kept in their own
          section above the saved-deals list — a draft deal's commission figures
          are deliberately zero, so it must never sit inside the totalled list. */}
      {draftDeals.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: BRAND_COLORS.textSecondary }}>
            Draft Deals ({draftDeals.length}) · excluded from all totals
          </h3>
          <DraftsPanel
            drafts={draftDeals}
            labelOf={d => d.dealName}
            stepLabels={['Deal Type', 'Deal Info', 'Amount', 'Deductions', 'Partners']}
            onResume={openEditDeal}
            onDiscard={onDiscardDealDraft && (d => showConfirm(
              `Discard the draft deal "${d.dealName || 'Untitled'}"? Everything entered so far will be lost.`,
              () => onDiscardDealDraft(d.id),
              'Discard Draft',
            ))}
          />
        </div>
      )}

      {/* Saved Deals List */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <h3 className="text-lg font-semibold" style={{ color: BRAND_COLORS.navy }}>
              {(currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') ? 'All Revenue Deals' : 'My Revenue Deals'}
            </h3>
            <div className="flex items-center gap-3 flex-wrap">
              {currentUser.role !== 'Partner' && (
                <div className="flex items-center gap-1.5">
                  <label className="text-xs text-gray-500">Show:</label>
                  <select
                    value={dealVisibilityFilter}
                    onChange={(e) => setDealVisibilityFilter(e.target.value)}
                    className="text-xs px-2 py-1 border border-gray-300 rounded"
                    style={{ color: BRAND_COLORS.navy }}>
                    <option value="visible">Visible to Partners</option>
                    <option value="hidden">Hidden from Partners</option>
                    <option value="all">All</option>
                  </select>
                </div>
              )}
              <div className="flex items-center gap-1.5">
                <label className="text-xs text-gray-500">Sort by:</label>
                <select
                  value={dSortKey}
                  onChange={(e) => { if (dSortKey !== e.target.value) toggleDealSort(e.target.value); }}
                  className="text-xs px-2 py-1 border border-gray-300 rounded"
                  style={{ color: BRAND_COLORS.navy }}>
                  {dealSortOptions.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
                </select>
                <button
                  type="button"
                  onClick={() => toggleDealSort(dSortKey)}
                  className="text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-50"
                  style={{ color: BRAND_COLORS.navy }}
                  title={dSortDir === 'asc' ? 'Ascending' : 'Descending'}>
                  {dSortDir === 'asc' ? '▲' : '▼'}
                </button>
              </div>
              <span className="text-sm text-gray-500">
                {visibleDeals.length} {visibleDeals.length === 1 ? 'deal' : 'deals'} {currentUser.role === 'Partner' ? 'with your participation' : 'saved'}
              </span>
            </div>
          </div>
        </div>
        <div className="p-6">
          {visibleDeals.length === 0 ? (
            <div className="text-center py-12">
              <DollarSign className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">
                {(currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin')
                  ? 'No deals saved yet. Create your first deal above!'
                  : 'No revenue deals found where you are a strategic partner.'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {(() => {
                if (currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') {
                  // Group deals by prospect
                  const groups = {};
                  const ungrouped = [];
                  visibleDeals.forEach(deal => {
                    if (deal.prospectId) {
                      if (!groups[deal.prospectId]) groups[deal.prospectId] = [];
                      groups[deal.prospectId].push(deal);
                    } else {
                      ungrouped.push(deal);
                    }
                  });

                  const allItems = [];

                  Object.entries(groups).forEach(([prospectId, groupDeals]) => {
                    const prospect = prospects.find(p => p.id === prospectId);
                    allItems.push(
                      <div key={`group_${prospectId}`} className="border border-gray-200 rounded-xl overflow-hidden">
                        <div className="px-4 py-2.5 flex items-center gap-2"
                          style={{ backgroundColor: `${BRAND_COLORS.navy}08`, borderBottom: '1px solid #E5E7EB' }}>
                          <div className="flex-1">
                            <span className="font-semibold text-sm" style={{ color: BRAND_COLORS.navy }}>
                              📋 {prospect?.companyName || prospect?.name || 'Unknown Prospect'}
                            </span>
                            <span className="ml-2 text-xs text-gray-500">
                              {groupDeals.length} deal{groupDeals.length > 1 ? 's' : ''}
                            </span>
                            {prospect?.contractType === 'ThroughPartner' && (
                              <StatusChip variant="warning" className="ml-2">Through Partner</StatusChip>
                            )}
                          </div>
                        </div>
                        <div className="divide-y divide-gray-100">
                          {groupDeals.map(deal => {
                            const isExpanded = expandedDealId === deal.id;
                            const linkedProspect = prospect;
                            const linkedTeaser = deal.teaserId ? teasers.find(t => t.id === deal.teaserId) : null;
                            const dt = computeDealTotals(deal);
                            return (
                  <div key={deal.id} className="border-2 rounded-lg overflow-hidden transition-all" style={{ borderColor: isExpanded ? BRAND_COLORS.gold : '#e5e7eb' }}>
                    {/* Collapsed View */}
                    <div className="p-4 bg-white">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <div className="flex items-center flex-wrap gap-2 mb-1">
                            <h4 className="font-semibold text-lg" style={{ color: BRAND_COLORS.navy }}>
                              {deal.dealName}
                            </h4>
                            {fdv('service_type') && deal.serviceType && (
                              <span className="px-2 py-0.5 text-xs rounded-full font-medium"
                                style={{
                                  backgroundColor: deal.serviceType === 'Retainer' ? '#dbeafe' : deal.serviceType === 'Project' ? '#fef3c7' : '#f3f4f6',
                                  color: deal.serviceType === 'Retainer' ? '#1d4ed8' : deal.serviceType === 'Project' ? '#92400e' : '#374151'
                                }}>
                                {deal.serviceType === 'One-time' ? '⚡' : deal.serviceType === 'Retainer' ? '🔄' : '📋'} {deal.serviceType}
                              </span>
                            )}
                            {deal.dealType && (
                              <span className="px-2 py-0.5 text-xs rounded-full font-medium"
                                style={{ backgroundColor: `${BRAND_COLORS.navy}10`, color: BRAND_COLORS.navy }}>
                                {deal.dealType}
                              </span>
                            )}
                            {fdv('deal_state') && deal.dealState && (
                              <span className="px-2 py-0.5 text-xs rounded-full font-medium"
                                style={{
                                  backgroundColor: deal.dealState === 'Closed' ? '#D1FAE5' : deal.dealState === 'Active' ? '#DBEAFE' : '#F3F4F6',
                                  color: deal.dealState === 'Closed' ? '#065F46' : deal.dealState === 'Active' ? '#1D4ED8' : '#6B7280'
                                }}>
                                {deal.dealState === 'Closed' ? '✅' : deal.dealState === 'Active' ? '🔄' : '📝'} {deal.dealState}
                              </span>
                            )}
                            {fdv('prospect_id') && linkedProspect && (
                              <StatusChip variant="info">📋 {linkedProspect.companyName}</StatusChip>
                            )}
                            {fdv('teaser_id') && linkedTeaser && (
                              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                                📄 {linkedTeaser.name || linkedTeaser.companyName}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span>Created {new Date(deal.createdAt).toLocaleDateString()} by {deal.createdBy}</span>
                            {fdv('investor_prospect_id') && deal.investorProspectId && (() => {
                              const ip = prospects.find(p => p.id === deal.investorProspectId);
                              return ip ? <span className="text-green-700">👤 Investor: {ip.companyName || ip.name}</span> : null;
                            })()}
                          </div>
                          {(deal.dealSize || deal.commissionRate) && (
                            <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                              {deal.dealSize && <span>Deal: {displayAmt(deal.dealSize, deal)}</span>}
                              {deal.dealSize && deal.commissionRate && <span>·</span>}
                              {deal.commissionRate && <span>Commission: {deal.commissionRate}%</span>}
                              {deal.dealSize && deal.commissionRate && (
                                <span className="font-medium" style={{ color: BRAND_COLORS.navy }}>
                                  = {displayAmt(parseFloat(deal.dealSize) * parseFloat(deal.commissionRate) / 100, deal)}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="text-right flex items-start space-x-3">
                          {fdv('total_agreed') && <div className="text-right">
                            {currentUser.role === 'Partner' ? (() => {
                              const pid = currentUser.partnerId || currentUser.id;
                              const myShare = computeDealTotals(deal).partnerPayouts
                                .filter(sp => sp.partnerId === pid || sp.partnerId === currentUser.id)
                                .reduce((s, sp) => s + (sp.amount || 0), 0);
                              return myShare > 0 ? (
                                <>
                                  <p className="text-2xl font-bold" style={{ color: BRAND_COLORS.gold }}>
                                    {displayAmt(myShare, deal)}
                                  </p>
                                  <p className="text-xs text-gray-500">My Commission</p>
                                </>
                              ) : (
                                <>
                                  <p className="text-lg font-semibold text-gray-400">—</p>
                                  <p className="text-xs text-gray-400">Commission TBD</p>
                                </>
                              );
                            })() : (
                              <>
                                <p className="text-2xl font-bold" style={{ color: BRAND_COLORS.gold }}>
                                  {displayAmt(deal.totalReceived, deal)}
                                </p>
                                <p className="text-xs text-gray-500">Total Received</p>
                              </>
                            )}
                            {/* View-only currency toggle */}
                            <div className="flex gap-1 mt-1.5 justify-end">
                              {CURRENCIES.map(({ code }) => (
                                <button
                                  key={code}
                                  onClick={(e) => { e.stopPropagation(); setDisplayCurrencies(prev => ({ ...prev, [deal.id]: code })); }}
                                  className="text-xs px-1.5 py-0.5 rounded font-medium transition-all"
                                  style={{
                                    backgroundColor: (displayCurrencies[deal.id] || deal.currency || 'USD') === code ? BRAND_COLORS.navy : '#f3f4f6',
                                    color: (displayCurrencies[deal.id] || deal.currency || 'USD') === code ? 'white' : '#9CA3AF',
                                  }}
                                >
                                  {code}
                                </button>
                              ))}
                            </div>
                          </div>}
                          <button
                            onClick={(e) => { e.stopPropagation(); setProfileDeal(deal); }}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            title="View deal profile"
                          >
                            <Eye className="w-4 h-4" style={{ color: BRAND_COLORS.navy }} />
                          </button>
                          <button
                            onClick={() => setExpandedDealId(isExpanded ? null : deal.id)}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            title={isExpanded ? "Collapse" : "Expand details"}
                          >
                            <ChevronDown
                              className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                              style={{ color: BRAND_COLORS.navy }}
                            />
                          </button>
                          {(currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') && (
                            <button
                              onClick={() => openEditDeal(deal)}
                              className="p-2 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Edit deal"
                            >
                              <Edit2 className="w-4 h-4" style={{ color: BRAND_COLORS.navy }} />
                            </button>
                          )}
                          {(currentUser.role === 'Admin' || currentUser.role === 'Super Admin') && onDeleteDeal && (
                            <button
                              onClick={() => {
                                showConfirm(`Delete deal "${deal.dealName}"? This cannot be undone.`, () => onDeleteDeal(deal.id), 'Delete');
                              }}
                              className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete deal"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </button>
                          )}
                          {currentUser.role === 'Editor' && onRequestDelete && (
                            <button
                              onClick={() => onRequestDelete('Deal', deal.id, deal.dealName)}
                              className="p-2 hover:bg-yellow-50 rounded-lg transition-colors"
                              title="Request delete"
                              style={{ color: '#92400E' }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                      
                      {/* Payment status row + Admin exposure controls */}
                      <div className="flex items-center justify-between mt-2">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm flex-1">
                          {/* Total — hidden from partner unless exposed */}
                          {fdv('total_agreed') && (currentUser.role !== 'Partner' || deal.exposure?.showTotal) && (
                            <div>
                              <p className="text-gray-500">Total Received</p>
                              <p className="font-semibold" style={{ color: BRAND_COLORS.navy }}>{displayAmt(deal.totalReceived, deal)}</p>
                            </div>
                          )}
                          {/* Deductions — hidden from partner unless exposed */}
                          {fdv('deductions') && (currentUser.role !== 'Partner' || deal.exposure?.showDeductions) && (
                            <div>
                              <p className="text-gray-500">Deductions</p>
                              <p className="font-semibold text-red-600">-{displayAmt(dt.totalDeductions, deal)}</p>
                            </div>
                          )}
                          <div>
                            <p className="text-gray-500">Pool</p>
                            <p className="font-semibold" style={{ color: BRAND_COLORS.gold }}>{displayAmt(dt.poolAmount, deal)}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Payment</p>
                            <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium"
                              style={{
                                backgroundColor: deal.paymentStatus === 'Paid' ? '#D1FAE5' : '#FEF3C7',
                                color: deal.paymentStatus === 'Paid' ? '#065F46' : '#92400E'
                              }}>
                              {deal.paymentStatus || 'Pending'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Admin: Exposure Controls + Payment toggle */}
                      {(currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <div className="flex items-center gap-4 flex-wrap">
                            <span className="text-xs font-medium text-gray-500">Partner sees:</span>
                            {[
                              { key: 'showTotal', label: 'Total Amount' },
                              { key: 'showDeductions', label: 'Deductions' },
                              { key: 'showOtherPartners', label: 'Other Partners' },
                            ].map(({ key, label }) => {
                              const isOn = deal.exposure?.[key] || false;
                              return (
                                <button
                                  key={key}
                                  onClick={() => onUpdateDeal && onUpdateDeal(deal.id, {
                                    exposure: { ...(deal.exposure || {}), [key]: !isOn }
                                  })}
                                  className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-all"
                                  style={{
                                    backgroundColor: isOn ? '#D1FAE5' : '#F3F4F6',
                                    color: isOn ? '#065F46' : '#9CA3AF',
                                    border: `1px solid ${isOn ? '#10B981' : '#E5E7EB'}`
                                  }}
                                >
                                  {isOn ? <Unlock className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                                  {label}
                                </button>
                              );
                            })}
                            {/* Payment status */}
                            <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ml-auto"
                              style={{
                                backgroundColor: deal.paymentStatus === 'Paid in Full' ? '#D1FAE5' : deal.paymentStatus === 'Partial' ? '#DBEAFE' : '#FEF3C7',
                                color: deal.paymentStatus === 'Paid in Full' ? '#065F46' : deal.paymentStatus === 'Partial' ? '#1D4ED8' : '#92400E',
                              }}>
                              {deal.paymentStatus === 'Paid in Full' ? '✅' : deal.paymentStatus === 'Partial' ? '🔄' : '⏳'}
                              {deal.paymentStatus || 'Unpaid'}
                              {deal.totalPaid > 0 && ` · $${deal.totalPaid.toLocaleString()}`}
                            </span>
                            {/* Record Payment button — Active/Closed deals only */}
                            {(deal.dealState === 'Active' || deal.dealState === 'Closed') && onAddPayment && (
                              <button
                                onClick={() => {
                                  setPaymentDealId(deal.id);
                                  setPaymentForm({ amount: '', date: new Date().toISOString().split('T')[0], type: 'Success Fee', notes: '' });
                                  setShowPaymentModal(true);
                                }}
                                className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ml-1"
                                style={{ backgroundColor: BRAND_COLORS.navy, color: 'white' }}
                              >
                                <DollarSign className="w-3 h-3" />
                                Record Payment
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="border-t border-gray-200 bg-gray-50">
                        <div className="p-6 space-y-6">
                          {/* Deal Structure — shown if dealSize or commissionRate is set */}
                          {(deal.dealSize || deal.commissionRate) && currentUser.role !== 'Partner' && (
                            <div className="bg-white rounded-lg border border-gray-200 p-4">
                              <h5 className="font-semibold mb-3 flex items-center text-sm" style={{ color: BRAND_COLORS.navy }}>
                                <BarChart3 className="w-4 h-4 mr-2" style={{ color: BRAND_COLORS.gold }} />
                                Deal Structure
                              </h5>
                              <div className="flex items-center gap-4 flex-wrap text-sm">
                                {deal.dealSize && (
                                  <div>
                                    <p className="text-xs text-gray-400 mb-0.5">Total Deal Size</p>
                                    <p className="font-bold text-lg" style={{ color: BRAND_COLORS.navy }}>{displayAmt(deal.dealSize, deal)}</p>
                                  </div>
                                )}
                                {deal.dealSize && deal.commissionRate && (
                                  <div className="text-gray-300 text-xl font-light">×</div>
                                )}
                                {deal.commissionRate && (
                                  <div>
                                    <p className="text-xs text-gray-400 mb-0.5">Commission Rate</p>
                                    <p className="font-bold text-lg" style={{ color: BRAND_COLORS.navy }}>{deal.commissionRate}%</p>
                                  </div>
                                )}
                                {deal.dealSize && deal.commissionRate && (
                                  <>
                                    <div className="text-gray-300 text-xl font-light">=</div>
                                    <div>
                                      <p className="text-xs text-gray-400 mb-0.5">Derived Commission</p>
                                      <p className="font-bold text-lg" style={{ color: BRAND_COLORS.gold }}>
                                        {displayAmt(parseFloat(deal.dealSize) * parseFloat(deal.commissionRate) / 100, deal)}
                                      </p>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          )}
                          {/* Deductions Breakdown — hidden from partner unless exposed */}
                          {fdv('deductions') && deal.deductions && deal.deductions.length > 0 &&
                            (currentUser.role !== 'Partner' || deal.exposure?.showDeductions) && (
                            <div>
                              <h5 className="font-semibold mb-3 flex items-center" style={{ color: BRAND_COLORS.navy }}>
                                <TrendingDown className="w-4 h-4 mr-2 text-red-500" />
                                External Deductions ({deal.deductions.length})
                              </h5>
                              <div className="bg-white rounded-lg border border-gray-200 divide-y">
                                {deal.deductions.map((ded, idx) => {
                                  const amount = ded.type === 'percentage' 
                                    ? deal.totalReceived * (parseFloat(ded.value) / 100)
                                    : parseFloat(ded.value);
                                  
                                  return (
                                    <div key={idx} className="p-3 flex items-center justify-between">
                                      <div className="flex-1">
                                        <p className="font-medium text-sm" style={{ color: BRAND_COLORS.navy }}>
                                          {ded.name}
                                        </p>
                                        <p className="text-xs text-gray-500">{ded.description}</p>
                                      </div>
                                      <div className="text-right">
                                        <p className="font-semibold text-red-600">
                                          -${amount.toLocaleString()}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                          {ded.type === 'percentage' ? `${ded.value}%` : 'Fixed'}
                                        </p>
                                      </div>
                                    </div>
                                  );
                                })}
                                <div className="p-3 bg-red-50 flex justify-between items-center">
                                  <p className="font-semibold text-sm">Total Deductions</p>
                                  <p className="font-bold text-red-600">
                                    -${dt.totalDeductions.toLocaleString()}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {/* The Pool */}
                          <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 border-2 rounded-lg p-4" style={{ borderColor: BRAND_COLORS.gold }}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                <div className="w-12 h-12 rounded-full flex items-center justify-center mr-3" style={{ backgroundColor: BRAND_COLORS.gold }}>
                                  <DollarSign className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                  <p className="text-sm text-gray-700 font-medium">The Pool (Available for Sharing)</p>
                                  <p className="text-xs text-gray-600">
                                    {safePct(dt.poolAmount, deal.totalReceived)}% of total received
                                  </p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-3xl font-bold" style={{ color: BRAND_COLORS.gold }}>
                                  ${dt.poolAmount.toLocaleString()}
                                </p>
                              </div>
                            </div>
                          </div>
                          
                          {/* Strategic Partners Breakdown — hidden from partner unless exposed */}
                          {deal.strategicPartners && deal.strategicPartners.length > 0 &&
                            (currentUser.role !== 'Partner' || deal.exposure?.showOtherPartners) && (
                            <div>
                              <h5 className="font-semibold mb-3 flex items-center" style={{ color: BRAND_COLORS.navy }}>
                                <Users className="w-4 h-4 mr-2" style={{ color: BRAND_COLORS.royalBlue }} />
                                Strategic Partners ({deal.strategicPartners.length})
                              </h5>
                              <div className="bg-white rounded-lg border border-gray-200 divide-y">
                                {deal.strategicPartners.map((sp, idx) => {
                                  const payout = dt.partnerPayouts?.find(p => p.id === sp.id || p.name === (sp.isExternal ? sp.externalName : sp.name)) || {};
                                  const displayName = sp.isExternal ? sp.externalName : sp.name;
                                  const amount = payout.amount ?? (dt.poolAmount * (parseFloat(sp.percentage) || 0) / 100);
                                  return (
                                    <div key={sp.id || idx} className="p-3">
                                      <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                          <div className="flex items-center gap-2">
                                            <p className="font-medium text-sm" style={{ color: BRAND_COLORS.navy }}>
                                              {displayName || '—'}
                                            </p>
                                            {sp.isExternal ? (
                                              <StatusChip variant="warning">External</StatusChip>
                                            ) : (
                                              <StatusChip variant="info">Partner</StatusChip>
                                            )}
                                          </div>
                                          {sp.isExternal && sp.externalCompany && (
                                            <p className="text-xs text-gray-500 mt-0.5">🏢 {sp.externalCompany}</p>
                                          )}
                                          {sp.description && (
                                            <p className="text-xs text-gray-400">{sp.description}</p>
                                          )}
                                          {sp.isExternal && sp.externalPaymentDetails && (
                                            <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                                              <span className="font-medium">Payment:</span> {sp.externalPaymentDetails}
                                            </p>
                                          )}
                                        </div>
                                        <div className="text-right ml-4">
                                          <p className="font-semibold" style={{ color: BRAND_COLORS.royalBlue }}>
                                            ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                          </p>
                                          <p className="text-xs text-gray-500">{safePct(amount, dt.poolAmount)}% of pool</p>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                                <div className="p-3 flex justify-between items-center" style={{ backgroundColor: `${BRAND_COLORS.royalBlue}10` }}>
                                  <p className="font-semibold text-sm">Total Partners Payout</p>
                                  <p className="font-bold" style={{ color: BRAND_COLORS.royalBlue }}>
                                    ${dt.totalPartnerPayout.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {/* Company Keeps */}
                          <div className="bg-gradient-to-r from-green-50 to-green-100 border-2 border-green-500 rounded-lg p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center mr-3">
                                  <Building2 className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                  <p className="text-sm text-gray-700 font-medium">Company Keeps</p>
                                  <p className="text-xs text-gray-600">
                                    {safePct(dt.companyKeeps, dt.poolAmount)}% of pool
                                  </p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-3xl font-bold text-green-600">
                                  ${dt.companyKeeps.toLocaleString()}
                                </p>
                              </div>
                            </div>
                          </div>
                          
                          {/* Visual Waterfall — hidden from partner */}
                          {currentUser.role !== 'Partner' && deal.totalReceived > 0 && dt.poolAmount > 0 && (() => {
                            // gloss/mkbar/lbl/pct were leftover style objects from a previous gradient-bar render;
                            // current waterfall uses white-bg bars with coloured borders. Only `vline` is still used.
                            const vline = { position:'absolute', left:'10px', top:0, bottom:0, width:'1px', background:'rgba(0,0,0,0.1)', borderRadius:'1px' };
                            return (
                              <div style={{ marginBottom:'16px' }}>
                                <h5 className="font-semibold mb-4 flex items-center" style={{ color: BRAND_COLORS.navy }}>
                                  <BarChart3 className="w-4 h-4 mr-2" style={{ color: BRAND_COLORS.gold }} />
                                  Money Flow
                                </h5>
                                <div style={{ display:'flex', flexDirection:'column', gap:'5px' }}>
                                  <div style={{ background:'#fff', border:'2px solid #1A1C5C', borderRadius:'10px', height:'40px', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 14px' }}>
                                    <span style={{ color:'#1A1C5C', fontWeight:600, fontSize:'13px' }}>${deal.totalReceived.toLocaleString()} — Total received</span><span style={{ color:'#1A1C5C', fontSize:'11px', opacity:0.6 }}>100%</span>
                                  </div>
                                  <div style={{ paddingLeft:'22px', position:'relative', display:'flex', flexDirection:'column', gap:'5px' }}>
                                    <div style={vline}/>
                                    {dt.totalDeductions > 0 && (
                                      <div style={{ background:'#fff', border:'2px solid #8a3018', borderRadius:'10px', height:'40px', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 14px', maxWidth:'280px' }}>
                                        <span style={{ color:'#8a3018', fontWeight:600, fontSize:'13px' }}>−${dt.totalDeductions.toLocaleString()} Deductions</span><span style={{ color:'#8a3018', fontSize:'11px', opacity:0.6 }}>{safePct(dt.totalDeductions, deal.totalReceived)}%</span>
                                      </div>
                                    )}
                                    <div style={{ background:'#fff', border:'2px solid #b8860b', borderRadius:'10px', height:'40px', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 14px' }}>
                                      <span style={{ color:'#7a5018', fontWeight:600, fontSize:'13px' }}>${dt.poolAmount.toLocaleString()} — Pool</span><span style={{ color:'#7a5018', fontSize:'11px', opacity:0.6 }}>{safePct(dt.poolAmount, deal.totalReceived)}%</span>
                                    </div>
                                    <div style={{ paddingLeft:'22px', position:'relative', display:'flex', flexDirection:'column', gap:'4px' }}>
                                      <div style={vline}/>
                                      {(dt.partnerPayouts||[]).map((payout,idx) => payout.amount > 0 && (
                                        <div key={idx} style={{ background:'#fff', border:'2px solid #185FA5', borderRadius:'10px', height:'40px', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 14px', maxWidth:'340px' }}>
                                          <span style={{ color:'#185FA5', fontWeight:600, fontSize:'13px' }}>{payout.name}</span><span style={{ color:'#185FA5', fontSize:'11px', opacity:0.65 }}>${payout.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} · {safePct(payout.amount, dt.poolAmount)}%</span>
                                        </div>
                                      ))}
                                      <div style={{ background:'#fff', border:'2px solid #0F6E56', borderRadius:'10px', height:'40px', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 14px' }}>
                                        <span style={{ color:'#0F6E56', fontWeight:600, fontSize:'13px' }}>Company keeps</span><span style={{ color:'#0F6E56', fontSize:'11px', opacity:0.65 }}>${dt.companyKeeps.toLocaleString()} · {safePct(dt.companyKeeps, dt.poolAmount)}%</span>
                                      </div>
                                    </div>
                                  </div>
                                  <div style={{ display:'grid', gridTemplateColumns:'repeat(3,minmax(0,1fr))', gap:'8px', marginTop:'12px' }}>
                                    {[
                                      { label:'Deductions',      value:displayAmt(dt.totalDeductions, deal),       sub:`${safePct(dt.totalDeductions, deal.totalReceived)}% of total`, accent: BRAND_COLORS.statusError },
                                      { label:'Partner payouts', value:displayAmt(dt.totalPartnerPayout||0, deal), sub:`${safePct(dt.totalPartnerPayout||0, dt.poolAmount)}% of pool`,  accent: BRAND_COLORS.statusInfo },
                                      { label:'Company keeps',   value:displayAmt(dt.companyKeeps, deal),          sub:`${safePct(dt.companyKeeps, dt.poolAmount)}% of pool`,         accent: BRAND_COLORS.statusSuccess },
                                    ].map(c => <KPICard key={c.label} size="tiny" {...c} />)}
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                          {/* Summary Sentence — hidden from partner */}
                          {currentUser.role !== 'Partner' && (
                          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                            <h5 className="font-semibold text-blue-900 text-sm mb-2">Deal Summary</h5>
                            <p className="text-blue-800 text-sm leading-relaxed">
                              Out of <strong>{displayAmt(deal.totalReceived, deal)}</strong> total received,
                              <strong> {displayAmt(dt.totalDeductions, deal)}</strong> went to external parties.
                              This left <strong>{displayAmt(dt.poolAmount, deal)}</strong> as the pool for sharing.
                              {dt.partnerPayouts.map((p, idx) => (
                                p.amount > 0 && (
                                  <span key={idx}>
                                    {' '}<strong>{p.name}</strong> gets <strong>{displayAmt(p.amount, deal)}</strong>
                                    {idx < dt.partnerPayouts.length - 1 ? ',' : ''}
                                  </span>
                                )
                              ))}
                              {' '}and the company keeps <strong>{displayAmt(dt.companyKeeps, deal)}</strong>.
                            </p>
                          </div>
                          )}

                          {/* Payment History — Admin/Editor only */}
                          {currentUser.role !== 'Partner' && (deal.payments || []).length > 0 && (
                            <div>
                              <h5 className="font-semibold mb-3 flex items-center" style={{ color: BRAND_COLORS.navy }}>
                                <DollarSign className="w-4 h-4 mr-2 text-green-600" />
                                Payment History ({deal.payments.length})
                              </h5>
                              <div className="bg-white rounded-lg border border-gray-200 divide-y">
                                {deal.payments.map((pmt, idx) => (
                                  <div key={pmt.id || idx} className="p-3 flex items-center justify-between gap-3">
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium text-sm" style={{ color: BRAND_COLORS.navy }}>
                                        {pmt.type} — {new Date(pmt.date).toLocaleDateString()}
                                      </p>
                                      {pmt.notes && <p className="text-xs text-gray-500">{pmt.notes}</p>}
                                      <p className="text-xs text-gray-400">Recorded by {pmt.recordedBy}</p>
                                    </div>
                                    <p className="font-bold text-green-600 whitespace-nowrap">${parseFloat(pmt.amount).toLocaleString()}</p>
                                    {onDeletePayment && (
                                      <button
                                        type="button"
                                        onClick={() => showConfirm(
                                          `Cancel this payment? Remove the ${pmt.type} of $${parseFloat(pmt.amount).toLocaleString()} recorded on ${new Date(pmt.date).toLocaleDateString()}? This cannot be undone.`,
                                          () => onDeletePayment(deal.id, idx),
                                          'Cancel Payment'
                                        )}
                                        title="Cancel payment"
                                        className="p-1.5 rounded hover:bg-red-50 transition-colors"
                                      >
                                        <Trash2 className="w-4 h-4" style={{ color: BRAND_COLORS.statusError }} />
                                      </button>
                                    )}
                                  </div>
                                ))}
                                <div className="p-3 bg-green-50 flex justify-between items-center">
                                  <p className="font-semibold text-sm">Total Paid</p>
                                  <p className="font-bold text-green-600">${(deal.totalPaid || 0).toLocaleString()}</p>
                                </div>
                                {deal.totalAgreed && (
                                  <div className="p-3 bg-blue-50 flex justify-between items-center">
                                    <p className="font-semibold text-sm">Balance Remaining</p>
                                    <p className="font-bold text-blue-600">${Math.max(0, (parseFloat(deal.totalAgreed) || 0) - (deal.totalPaid || 0)).toLocaleString()}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Partner: show only their commission + payment status */}
                          {currentUser.role === 'Partner' && (() => {
                            const pid = currentUser.partnerId || currentUser.id;
                            const myShare = computeDealTotals(deal).partnerPayouts
                              .filter(sp => sp.partnerId === pid || sp.partnerId === currentUser.id)
                              .reduce((s, sp) => s + (sp.amount || 0), 0);
                            return (
                              <div className="p-4 rounded-lg" style={{ backgroundColor: `${BRAND_COLORS.gold}15`, border: `1px solid ${BRAND_COLORS.gold}` }}>
                                <h5 className="font-semibold mb-2" style={{ color: BRAND_COLORS.navy }}>Your Commission</h5>
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="text-3xl font-bold" style={{ color: BRAND_COLORS.gold }}>
                                      {myShare > 0 ? `$${myShare.toLocaleString()}` : '—'}
                                    </p>
                                    <p className="text-xs text-gray-500 mt-0.5">
                                      {myShare > 0 ? 'Your share from this deal' : 'Commission amount to be confirmed'}
                                    </p>
                                  </div>
                                  <span className="px-3 py-1.5 rounded-full text-sm font-semibold"
                                    style={{
                                      backgroundColor: deal.paymentStatus === 'Paid' ? '#D1FAE5' : '#FEF3C7',
                                      color: deal.paymentStatus === 'Paid' ? '#065F46' : '#92400E'
                                    }}>
                                    {deal.paymentStatus === 'Paid' ? '✓ Paid' : '⏳ Pending Payment'}
                                  </span>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  });

                  ungrouped.forEach(deal => {
                    const isExpanded = expandedDealId === deal.id;
                    const linkedProspect = null;
                    const linkedTeaser = deal.teaserId ? teasers.find(t => t.id === deal.teaserId) : null;
                    const dt = computeDealTotals(deal);
                    allItems.push(
                  <div key={`ungrouped_${deal.id}`} className="border-2 rounded-lg overflow-hidden transition-all" style={{ borderColor: isExpanded ? BRAND_COLORS.gold : '#e5e7eb' }}>
                    {/* Collapsed View */}
                    <div className="p-4 bg-white">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <div className="flex items-center flex-wrap gap-2 mb-1">
                            <h4 className="font-semibold text-lg" style={{ color: BRAND_COLORS.navy }}>
                              {deal.dealName}
                            </h4>
                            {fdv('service_type') && deal.serviceType && (
                              <span className="px-2 py-0.5 text-xs rounded-full font-medium"
                                style={{
                                  backgroundColor: deal.serviceType === 'Retainer' ? '#dbeafe' : deal.serviceType === 'Project' ? '#fef3c7' : '#f3f4f6',
                                  color: deal.serviceType === 'Retainer' ? '#1d4ed8' : deal.serviceType === 'Project' ? '#92400e' : '#374151'
                                }}>
                                {deal.serviceType === 'One-time' ? '⚡' : deal.serviceType === 'Retainer' ? '🔄' : '📋'} {deal.serviceType}
                              </span>
                            )}
                            {deal.dealType && (
                              <span className="px-2 py-0.5 text-xs rounded-full font-medium"
                                style={{ backgroundColor: `${BRAND_COLORS.navy}10`, color: BRAND_COLORS.navy }}>
                                {deal.dealType}
                              </span>
                            )}
                            {fdv('deal_state') && deal.dealState && (
                              <span className="px-2 py-0.5 text-xs rounded-full font-medium"
                                style={{
                                  backgroundColor: deal.dealState === 'Closed' ? '#D1FAE5' : deal.dealState === 'Active' ? '#DBEAFE' : '#F3F4F6',
                                  color: deal.dealState === 'Closed' ? '#065F46' : deal.dealState === 'Active' ? '#1D4ED8' : '#6B7280'
                                }}>
                                {deal.dealState === 'Closed' ? '✅' : deal.dealState === 'Active' ? '🔄' : '📝'} {deal.dealState}
                              </span>
                            )}
                            {fdv('prospect_id') && linkedProspect && (
                              <StatusChip variant="info">📋 {linkedProspect.companyName}</StatusChip>
                            )}
                            {fdv('teaser_id') && linkedTeaser && (
                              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                                📄 {linkedTeaser.name || linkedTeaser.companyName}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span>Created {new Date(deal.createdAt).toLocaleDateString()} by {deal.createdBy}</span>
                            {fdv('investor_prospect_id') && deal.investorProspectId && (() => {
                              const ip = prospects.find(p => p.id === deal.investorProspectId);
                              return ip ? <span className="text-green-700">👤 Investor: {ip.companyName || ip.name}</span> : null;
                            })()}
                          </div>
                          {(deal.dealSize || deal.commissionRate) && (
                            <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                              {deal.dealSize && <span>Deal: {displayAmt(deal.dealSize, deal)}</span>}
                              {deal.dealSize && deal.commissionRate && <span>·</span>}
                              {deal.commissionRate && <span>Commission: {deal.commissionRate}%</span>}
                              {deal.dealSize && deal.commissionRate && (
                                <span className="font-medium" style={{ color: BRAND_COLORS.navy }}>
                                  = {displayAmt(parseFloat(deal.dealSize) * parseFloat(deal.commissionRate) / 100, deal)}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="text-right flex items-start space-x-3">
                          {fdv('total_agreed') && <div className="text-right">
                            {currentUser.role === 'Partner' ? (() => {
                              const pid = currentUser.partnerId || currentUser.id;
                              const myShare = computeDealTotals(deal).partnerPayouts
                                .filter(sp => sp.partnerId === pid || sp.partnerId === currentUser.id)
                                .reduce((s, sp) => s + (sp.amount || 0), 0);
                              return myShare > 0 ? (
                                <>
                                  <p className="text-2xl font-bold" style={{ color: BRAND_COLORS.gold }}>
                                    {displayAmt(myShare, deal)}
                                  </p>
                                  <p className="text-xs text-gray-500">My Commission</p>
                                </>
                              ) : (
                                <>
                                  <p className="text-lg font-semibold text-gray-400">—</p>
                                  <p className="text-xs text-gray-400">Commission TBD</p>
                                </>
                              );
                            })() : (
                              <>
                                <p className="text-2xl font-bold" style={{ color: BRAND_COLORS.gold }}>
                                  {displayAmt(deal.totalReceived, deal)}
                                </p>
                                <p className="text-xs text-gray-500">Total Received</p>
                              </>
                            )}
                            {/* View-only currency toggle */}
                            <div className="flex gap-1 mt-1.5 justify-end">
                              {CURRENCIES.map(({ code }) => (
                                <button
                                  key={code}
                                  onClick={(e) => { e.stopPropagation(); setDisplayCurrencies(prev => ({ ...prev, [deal.id]: code })); }}
                                  className="text-xs px-1.5 py-0.5 rounded font-medium transition-all"
                                  style={{
                                    backgroundColor: (displayCurrencies[deal.id] || deal.currency || 'USD') === code ? BRAND_COLORS.navy : '#f3f4f6',
                                    color: (displayCurrencies[deal.id] || deal.currency || 'USD') === code ? 'white' : '#9CA3AF',
                                  }}
                                >
                                  {code}
                                </button>
                              ))}
                            </div>
                          </div>}
                          <button
                            onClick={(e) => { e.stopPropagation(); setProfileDeal(deal); }}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            title="View deal profile"
                          >
                            <Eye className="w-4 h-4" style={{ color: BRAND_COLORS.navy }} />
                          </button>
                          <button
                            onClick={() => setExpandedDealId(isExpanded ? null : deal.id)}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            title={isExpanded ? "Collapse" : "Expand details"}
                          >
                            <ChevronDown
                              className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                              style={{ color: BRAND_COLORS.navy }}
                            />
                          </button>
                          {(currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') && (
                            <button
                              onClick={() => openEditDeal(deal)}
                              className="p-2 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Edit deal"
                            >
                              <Edit2 className="w-4 h-4" style={{ color: BRAND_COLORS.navy }} />
                            </button>
                          )}
                          {(currentUser.role === 'Admin' || currentUser.role === 'Super Admin') && onDeleteDeal && (
                            <button
                              onClick={() => {
                                showConfirm(`Delete deal "${deal.dealName}"? This cannot be undone.`, () => onDeleteDeal(deal.id), 'Delete');
                              }}
                              className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete deal"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </button>
                          )}
                          {currentUser.role === 'Editor' && onRequestDelete && (
                            <button
                              onClick={() => onRequestDelete('Deal', deal.id, deal.dealName)}
                              className="p-2 hover:bg-yellow-50 rounded-lg transition-colors"
                              title="Request delete"
                              style={{ color: '#92400E' }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                      
                      {/* Payment status row + Admin exposure controls */}
                      <div className="flex items-center justify-between mt-2">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm flex-1">
                          {/* Total — hidden from partner unless exposed */}
                          {fdv('total_agreed') && (currentUser.role !== 'Partner' || deal.exposure?.showTotal) && (
                            <div>
                              <p className="text-gray-500">Total Received</p>
                              <p className="font-semibold" style={{ color: BRAND_COLORS.navy }}>{displayAmt(deal.totalReceived, deal)}</p>
                            </div>
                          )}
                          {/* Deductions — hidden from partner unless exposed */}
                          {fdv('deductions') && (currentUser.role !== 'Partner' || deal.exposure?.showDeductions) && (
                            <div>
                              <p className="text-gray-500">Deductions</p>
                              <p className="font-semibold text-red-600">-{displayAmt(dt.totalDeductions, deal)}</p>
                            </div>
                          )}
                          <div>
                            <p className="text-gray-500">Pool</p>
                            <p className="font-semibold" style={{ color: BRAND_COLORS.gold }}>{displayAmt(dt.poolAmount, deal)}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Payment</p>
                            <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium"
                              style={{
                                backgroundColor: deal.paymentStatus === 'Paid' ? '#D1FAE5' : '#FEF3C7',
                                color: deal.paymentStatus === 'Paid' ? '#065F46' : '#92400E'
                              }}>
                              {deal.paymentStatus || 'Pending'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Admin: Exposure Controls + Payment toggle */}
                      {(currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <div className="flex items-center gap-4 flex-wrap">
                            <span className="text-xs font-medium text-gray-500">Partner sees:</span>
                            {[
                              { key: 'showTotal', label: 'Total Amount' },
                              { key: 'showDeductions', label: 'Deductions' },
                              { key: 'showOtherPartners', label: 'Other Partners' },
                            ].map(({ key, label }) => {
                              const isOn = deal.exposure?.[key] || false;
                              return (
                                <button
                                  key={key}
                                  onClick={() => onUpdateDeal && onUpdateDeal(deal.id, {
                                    exposure: { ...(deal.exposure || {}), [key]: !isOn }
                                  })}
                                  className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-all"
                                  style={{
                                    backgroundColor: isOn ? '#D1FAE5' : '#F3F4F6',
                                    color: isOn ? '#065F46' : '#9CA3AF',
                                    border: `1px solid ${isOn ? '#10B981' : '#E5E7EB'}`
                                  }}
                                >
                                  {isOn ? <Unlock className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                                  {label}
                                </button>
                              );
                            })}
                            {/* Payment status */}
                            <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ml-auto"
                              style={{
                                backgroundColor: deal.paymentStatus === 'Paid in Full' ? '#D1FAE5' : deal.paymentStatus === 'Partial' ? '#DBEAFE' : '#FEF3C7',
                                color: deal.paymentStatus === 'Paid in Full' ? '#065F46' : deal.paymentStatus === 'Partial' ? '#1D4ED8' : '#92400E',
                              }}>
                              {deal.paymentStatus === 'Paid in Full' ? '✅' : deal.paymentStatus === 'Partial' ? '🔄' : '⏳'}
                              {deal.paymentStatus || 'Unpaid'}
                              {deal.totalPaid > 0 && ` · $${deal.totalPaid.toLocaleString()}`}
                            </span>
                            {(deal.dealState === 'Active' || deal.dealState === 'Closed') && onAddPayment && (
                              <button
                                onClick={() => {
                                  setPaymentDealId(deal.id);
                                  setPaymentForm({ amount: '', date: new Date().toISOString().split('T')[0], type: 'Success Fee', notes: '' });
                                  setShowPaymentModal(true);
                                }}
                                className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ml-1"
                                style={{ backgroundColor: BRAND_COLORS.navy, color: 'white' }}
                              >
                                <DollarSign className="w-3 h-3" />
                                Record Payment
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="border-t border-gray-200 bg-gray-50">
                        <div className="p-6 space-y-6">
                          {/* Deal Structure — shown if dealSize or commissionRate is set */}
                          {(deal.dealSize || deal.commissionRate) && currentUser.role !== 'Partner' && (
                            <div className="bg-white rounded-lg border border-gray-200 p-4">
                              <h5 className="font-semibold mb-3 flex items-center text-sm" style={{ color: BRAND_COLORS.navy }}>
                                <BarChart3 className="w-4 h-4 mr-2" style={{ color: BRAND_COLORS.gold }} />
                                Deal Structure
                              </h5>
                              <div className="flex items-center gap-4 flex-wrap text-sm">
                                {deal.dealSize && (
                                  <div>
                                    <p className="text-xs text-gray-400 mb-0.5">Total Deal Size</p>
                                    <p className="font-bold text-lg" style={{ color: BRAND_COLORS.navy }}>{displayAmt(deal.dealSize, deal)}</p>
                                  </div>
                                )}
                                {deal.dealSize && deal.commissionRate && (
                                  <div className="text-gray-300 text-xl font-light">×</div>
                                )}
                                {deal.commissionRate && (
                                  <div>
                                    <p className="text-xs text-gray-400 mb-0.5">Commission Rate</p>
                                    <p className="font-bold text-lg" style={{ color: BRAND_COLORS.navy }}>{deal.commissionRate}%</p>
                                  </div>
                                )}
                                {deal.dealSize && deal.commissionRate && (
                                  <>
                                    <div className="text-gray-300 text-xl font-light">=</div>
                                    <div>
                                      <p className="text-xs text-gray-400 mb-0.5">Derived Commission</p>
                                      <p className="font-bold text-lg" style={{ color: BRAND_COLORS.gold }}>
                                        {displayAmt(parseFloat(deal.dealSize) * parseFloat(deal.commissionRate) / 100, deal)}
                                      </p>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          )}
                          {/* Deductions Breakdown — hidden from partner unless exposed */}
                          {fdv('deductions') && deal.deductions && deal.deductions.length > 0 &&
                            (currentUser.role !== 'Partner' || deal.exposure?.showDeductions) && (
                            <div>
                              <h5 className="font-semibold mb-3 flex items-center" style={{ color: BRAND_COLORS.navy }}>
                                <TrendingDown className="w-4 h-4 mr-2 text-red-500" />
                                External Deductions ({deal.deductions.length})
                              </h5>
                              <div className="bg-white rounded-lg border border-gray-200 divide-y">
                                {deal.deductions.map((ded, idx) => {
                                  const amount = ded.type === 'percentage' 
                                    ? deal.totalReceived * (parseFloat(ded.value) / 100)
                                    : parseFloat(ded.value);
                                  
                                  return (
                                    <div key={idx} className="p-3 flex items-center justify-between">
                                      <div className="flex-1">
                                        <p className="font-medium text-sm" style={{ color: BRAND_COLORS.navy }}>
                                          {ded.name}
                                        </p>
                                        <p className="text-xs text-gray-500">{ded.description}</p>
                                      </div>
                                      <div className="text-right">
                                        <p className="font-semibold text-red-600">
                                          -${amount.toLocaleString()}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                          {ded.type === 'percentage' ? `${ded.value}%` : 'Fixed'}
                                        </p>
                                      </div>
                                    </div>
                                  );
                                })}
                                <div className="p-3 bg-red-50 flex justify-between items-center">
                                  <p className="font-semibold text-sm">Total Deductions</p>
                                  <p className="font-bold text-red-600">
                                    -${dt.totalDeductions.toLocaleString()}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {/* The Pool */}
                          <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 border-2 rounded-lg p-4" style={{ borderColor: BRAND_COLORS.gold }}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                <div className="w-12 h-12 rounded-full flex items-center justify-center mr-3" style={{ backgroundColor: BRAND_COLORS.gold }}>
                                  <DollarSign className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                  <p className="text-sm text-gray-700 font-medium">The Pool (Available for Sharing)</p>
                                  <p className="text-xs text-gray-600">
                                    {safePct(dt.poolAmount, deal.totalReceived)}% of total received
                                  </p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-3xl font-bold" style={{ color: BRAND_COLORS.gold }}>
                                  ${dt.poolAmount.toLocaleString()}
                                </p>
                              </div>
                            </div>
                          </div>
                          
                          {/* Strategic Partners Breakdown — hidden from partner unless exposed */}
                          {deal.strategicPartners && deal.strategicPartners.length > 0 &&
                            (currentUser.role !== 'Partner' || deal.exposure?.showOtherPartners) && (
                            <div>
                              <h5 className="font-semibold mb-3 flex items-center" style={{ color: BRAND_COLORS.navy }}>
                                <Users className="w-4 h-4 mr-2" style={{ color: BRAND_COLORS.royalBlue }} />
                                Strategic Partners ({deal.strategicPartners.length})
                              </h5>
                              <div className="bg-white rounded-lg border border-gray-200 divide-y">
                                {deal.strategicPartners.map((sp, idx) => {
                                  const payout = dt.partnerPayouts?.find(p => p.id === sp.id || p.name === (sp.isExternal ? sp.externalName : sp.name)) || {};
                                  const displayName = sp.isExternal ? sp.externalName : sp.name;
                                  const amount = payout.amount ?? (dt.poolAmount * (parseFloat(sp.percentage) || 0) / 100);
                                  return (
                                    <div key={sp.id || idx} className="p-3">
                                      <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                          <div className="flex items-center gap-2">
                                            <p className="font-medium text-sm" style={{ color: BRAND_COLORS.navy }}>
                                              {displayName || '—'}
                                            </p>
                                            {sp.isExternal ? (
                                              <StatusChip variant="warning">External</StatusChip>
                                            ) : (
                                              <StatusChip variant="info">Partner</StatusChip>
                                            )}
                                          </div>
                                          {sp.isExternal && sp.externalCompany && (
                                            <p className="text-xs text-gray-500 mt-0.5">🏢 {sp.externalCompany}</p>
                                          )}
                                          {sp.description && (
                                            <p className="text-xs text-gray-400">{sp.description}</p>
                                          )}
                                          {sp.isExternal && sp.externalPaymentDetails && (
                                            <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                                              <span className="font-medium">Payment:</span> {sp.externalPaymentDetails}
                                            </p>
                                          )}
                                        </div>
                                        <div className="text-right ml-4">
                                          <p className="font-semibold" style={{ color: BRAND_COLORS.royalBlue }}>
                                            ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                          </p>
                                          <p className="text-xs text-gray-500">{safePct(amount, dt.poolAmount)}% of pool</p>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                                <div className="p-3 flex justify-between items-center" style={{ backgroundColor: `${BRAND_COLORS.royalBlue}10` }}>
                                  <p className="font-semibold text-sm">Total Partners Payout</p>
                                  <p className="font-bold" style={{ color: BRAND_COLORS.royalBlue }}>
                                    ${dt.totalPartnerPayout.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {/* Company Keeps */}
                          <div className="bg-gradient-to-r from-green-50 to-green-100 border-2 border-green-500 rounded-lg p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center mr-3">
                                  <Building2 className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                  <p className="text-sm text-gray-700 font-medium">Company Keeps</p>
                                  <p className="text-xs text-gray-600">
                                    {safePct(dt.companyKeeps, dt.poolAmount)}% of pool
                                  </p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-3xl font-bold text-green-600">
                                  ${dt.companyKeeps.toLocaleString()}
                                </p>
                              </div>
                            </div>
                          </div>
                          
                          {/* Visual Waterfall — hidden from partner */}
                          {currentUser.role !== 'Partner' && deal.totalReceived > 0 && dt.poolAmount > 0 && (() => {
                            // gloss/mkbar/lbl/pct were leftover style objects from a previous gradient-bar render;
                            // current waterfall uses white-bg bars with coloured borders. Only `vline` is still used.
                            const vline = { position:'absolute', left:'10px', top:0, bottom:0, width:'1px', background:'rgba(0,0,0,0.1)', borderRadius:'1px' };
                            return (
                              <div style={{ marginBottom:'16px' }}>
                                <h5 className="font-semibold mb-4 flex items-center" style={{ color: BRAND_COLORS.navy }}>
                                  <BarChart3 className="w-4 h-4 mr-2" style={{ color: BRAND_COLORS.gold }} />
                                  Money Flow
                                </h5>
                                <div style={{ display:'flex', flexDirection:'column', gap:'5px' }}>
                                  <div style={{ background:'#fff', border:'2px solid #1A1C5C', borderRadius:'10px', height:'40px', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 14px' }}>
                                    <span style={{ color:'#1A1C5C', fontWeight:600, fontSize:'13px' }}>${deal.totalReceived.toLocaleString()} — Total received</span><span style={{ color:'#1A1C5C', fontSize:'11px', opacity:0.6 }}>100%</span>
                                  </div>
                                  <div style={{ paddingLeft:'22px', position:'relative', display:'flex', flexDirection:'column', gap:'5px' }}>
                                    <div style={vline}/>
                                    {dt.totalDeductions > 0 && (
                                      <div style={{ background:'#fff', border:'2px solid #8a3018', borderRadius:'10px', height:'40px', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 14px', maxWidth:'280px' }}>
                                        <span style={{ color:'#8a3018', fontWeight:600, fontSize:'13px' }}>−${dt.totalDeductions.toLocaleString()} Deductions</span><span style={{ color:'#8a3018', fontSize:'11px', opacity:0.6 }}>{safePct(dt.totalDeductions, deal.totalReceived)}%</span>
                                      </div>
                                    )}
                                    <div style={{ background:'#fff', border:'2px solid #b8860b', borderRadius:'10px', height:'40px', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 14px' }}>
                                      <span style={{ color:'#7a5018', fontWeight:600, fontSize:'13px' }}>${dt.poolAmount.toLocaleString()} — Pool</span><span style={{ color:'#7a5018', fontSize:'11px', opacity:0.6 }}>{safePct(dt.poolAmount, deal.totalReceived)}%</span>
                                    </div>
                                    <div style={{ paddingLeft:'22px', position:'relative', display:'flex', flexDirection:'column', gap:'4px' }}>
                                      <div style={vline}/>
                                      {(dt.partnerPayouts||[]).map((payout,idx) => payout.amount > 0 && (
                                        <div key={idx} style={{ background:'#fff', border:'2px solid #185FA5', borderRadius:'10px', height:'40px', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 14px', maxWidth:'340px' }}>
                                          <span style={{ color:'#185FA5', fontWeight:600, fontSize:'13px' }}>{payout.name}</span><span style={{ color:'#185FA5', fontSize:'11px', opacity:0.65 }}>${payout.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} · {safePct(payout.amount, dt.poolAmount)}%</span>
                                        </div>
                                      ))}
                                      <div style={{ background:'#fff', border:'2px solid #0F6E56', borderRadius:'10px', height:'40px', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 14px' }}>
                                        <span style={{ color:'#0F6E56', fontWeight:600, fontSize:'13px' }}>Company keeps</span><span style={{ color:'#0F6E56', fontSize:'11px', opacity:0.65 }}>${dt.companyKeeps.toLocaleString()} · {safePct(dt.companyKeeps, dt.poolAmount)}%</span>
                                      </div>
                                    </div>
                                  </div>
                                  <div style={{ display:'grid', gridTemplateColumns:'repeat(3,minmax(0,1fr))', gap:'8px', marginTop:'12px' }}>
                                    {[
                                      { label:'Deductions',      value:displayAmt(dt.totalDeductions, deal),       sub:`${safePct(dt.totalDeductions, deal.totalReceived)}% of total`, accent: BRAND_COLORS.statusError },
                                      { label:'Partner payouts', value:displayAmt(dt.totalPartnerPayout||0, deal), sub:`${safePct(dt.totalPartnerPayout||0, dt.poolAmount)}% of pool`,  accent: BRAND_COLORS.statusInfo },
                                      { label:'Company keeps',   value:displayAmt(dt.companyKeeps, deal),          sub:`${safePct(dt.companyKeeps, dt.poolAmount)}% of pool`,         accent: BRAND_COLORS.statusSuccess },
                                    ].map(c => <KPICard key={c.label} size="tiny" {...c} />)}
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                          {/* Summary Sentence — hidden from partner */}
                          {currentUser.role !== 'Partner' && (
                          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                            <h5 className="font-semibold text-blue-900 text-sm mb-2">Deal Summary</h5>
                            <p className="text-blue-800 text-sm leading-relaxed">
                              Out of <strong>{displayAmt(deal.totalReceived, deal)}</strong> total received,
                              <strong> {displayAmt(dt.totalDeductions, deal)}</strong> went to external parties.
                              This left <strong>{displayAmt(dt.poolAmount, deal)}</strong> as the pool for sharing.
                              {dt.partnerPayouts.map((p, idx) => (
                                p.amount > 0 && (
                                  <span key={idx}>
                                    {' '}<strong>{p.name}</strong> gets <strong>{displayAmt(p.amount, deal)}</strong>
                                    {idx < dt.partnerPayouts.length - 1 ? ',' : ''}
                                  </span>
                                )
                              ))}
                              {' '}and the company keeps <strong>{displayAmt(dt.companyKeeps, deal)}</strong>.
                            </p>
                          </div>
                          )}

                          {/* Payment History — Admin/Editor only */}
                          {currentUser.role !== 'Partner' && (deal.payments || []).length > 0 && (
                            <div>
                              <h5 className="font-semibold mb-3 flex items-center" style={{ color: BRAND_COLORS.navy }}>
                                <DollarSign className="w-4 h-4 mr-2 text-green-600" />
                                Payment History ({deal.payments.length})
                              </h5>
                              <div className="bg-white rounded-lg border border-gray-200 divide-y">
                                {deal.payments.map((pmt, idx) => (
                                  <div key={pmt.id || idx} className="p-3 flex items-center justify-between gap-3">
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium text-sm" style={{ color: BRAND_COLORS.navy }}>
                                        {pmt.type} — {new Date(pmt.date).toLocaleDateString()}
                                      </p>
                                      {pmt.notes && <p className="text-xs text-gray-500">{pmt.notes}</p>}
                                      <p className="text-xs text-gray-400">Recorded by {pmt.recordedBy}</p>
                                    </div>
                                    <p className="font-bold text-green-600 whitespace-nowrap">${parseFloat(pmt.amount).toLocaleString()}</p>
                                    {onDeletePayment && (
                                      <button
                                        type="button"
                                        onClick={() => showConfirm(
                                          `Cancel this payment? Remove the ${pmt.type} of $${parseFloat(pmt.amount).toLocaleString()} recorded on ${new Date(pmt.date).toLocaleDateString()}? This cannot be undone.`,
                                          () => onDeletePayment(deal.id, idx),
                                          'Cancel Payment'
                                        )}
                                        title="Cancel payment"
                                        className="p-1.5 rounded hover:bg-red-50 transition-colors"
                                      >
                                        <Trash2 className="w-4 h-4" style={{ color: BRAND_COLORS.statusError }} />
                                      </button>
                                    )}
                                  </div>
                                ))}
                                <div className="p-3 bg-green-50 flex justify-between items-center">
                                  <p className="font-semibold text-sm">Total Paid</p>
                                  <p className="font-bold text-green-600">${(deal.totalPaid || 0).toLocaleString()}</p>
                                </div>
                                {deal.totalAgreed && (
                                  <div className="p-3 bg-blue-50 flex justify-between items-center">
                                    <p className="font-semibold text-sm">Balance Remaining</p>
                                    <p className="font-bold text-blue-600">${Math.max(0, (parseFloat(deal.totalAgreed) || 0) - (deal.totalPaid || 0)).toLocaleString()}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Partner: show only their commission + payment status */}
                          {currentUser.role === 'Partner' && (() => {
                            const pid = currentUser.partnerId || currentUser.id;
                            const myShare = computeDealTotals(deal).partnerPayouts
                              .filter(sp => sp.partnerId === pid || sp.partnerId === currentUser.id)
                              .reduce((s, sp) => s + (sp.amount || 0), 0);
                            return (
                              <div className="p-4 rounded-lg" style={{ backgroundColor: `${BRAND_COLORS.gold}15`, border: `1px solid ${BRAND_COLORS.gold}` }}>
                                <h5 className="font-semibold mb-2" style={{ color: BRAND_COLORS.navy }}>Your Commission</h5>
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="text-3xl font-bold" style={{ color: BRAND_COLORS.gold }}>
                                      {myShare > 0 ? `$${myShare.toLocaleString()}` : '—'}
                                    </p>
                                    <p className="text-xs text-gray-500 mt-0.5">
                                      {myShare > 0 ? 'Your share from this deal' : 'Commission amount to be confirmed'}
                                    </p>
                                  </div>
                                  <span className="px-3 py-1.5 rounded-full text-sm font-semibold"
                                    style={{
                                      backgroundColor: deal.paymentStatus === 'Paid' ? '#D1FAE5' : '#FEF3C7',
                                      color: deal.paymentStatus === 'Paid' ? '#065F46' : '#92400E'
                                    }}>
                                    {deal.paymentStatus === 'Paid' ? '✓ Paid' : '⏳ Pending Payment'}
                                  </span>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                    );
                  });

                  return allItems;
                }

                // Partner: flat list
                return visibleDeals.map(deal => {
                  const isExpanded = expandedDealId === deal.id;
                  const linkedProspect = deal.prospectId ? prospects.find(p => p.id === deal.prospectId) : null;
                  const linkedTeaser = deal.teaserId ? teasers.find(t => t.id === deal.teaserId) : null;
                  const dt = computeDealTotals(deal);
                  return (
                  <div key={deal.id} className="border-2 rounded-lg overflow-hidden transition-all" style={{ borderColor: isExpanded ? BRAND_COLORS.gold : '#e5e7eb' }}>
                    {/* Collapsed View */}
                    <div className="p-4 bg-white">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <div className="flex items-center flex-wrap gap-2 mb-1">
                            <h4 className="font-semibold text-lg" style={{ color: BRAND_COLORS.navy }}>
                              {deal.dealName}
                            </h4>
                            {fdv('service_type') && deal.serviceType && (
                              <span className="px-2 py-0.5 text-xs rounded-full font-medium"
                                style={{
                                  backgroundColor: deal.serviceType === 'Retainer' ? '#dbeafe' : deal.serviceType === 'Project' ? '#fef3c7' : '#f3f4f6',
                                  color: deal.serviceType === 'Retainer' ? '#1d4ed8' : deal.serviceType === 'Project' ? '#92400e' : '#374151'
                                }}>
                                {deal.serviceType === 'One-time' ? '⚡' : deal.serviceType === 'Retainer' ? '🔄' : '📋'} {deal.serviceType}
                              </span>
                            )}
                            {deal.dealType && (
                              <span className="px-2 py-0.5 text-xs rounded-full font-medium"
                                style={{ backgroundColor: `${BRAND_COLORS.navy}10`, color: BRAND_COLORS.navy }}>
                                {deal.dealType}
                              </span>
                            )}
                            {fdv('deal_state') && deal.dealState && (
                              <span className="px-2 py-0.5 text-xs rounded-full font-medium"
                                style={{
                                  backgroundColor: deal.dealState === 'Closed' ? '#D1FAE5' : deal.dealState === 'Active' ? '#DBEAFE' : '#F3F4F6',
                                  color: deal.dealState === 'Closed' ? '#065F46' : deal.dealState === 'Active' ? '#1D4ED8' : '#6B7280'
                                }}>
                                {deal.dealState === 'Closed' ? '✅' : deal.dealState === 'Active' ? '🔄' : '📝'} {deal.dealState}
                              </span>
                            )}
                            {fdv('prospect_id') && linkedProspect && (
                              <StatusChip variant="info">📋 {linkedProspect.companyName}</StatusChip>
                            )}
                            {fdv('teaser_id') && linkedTeaser && (
                              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                                📄 {linkedTeaser.name || linkedTeaser.companyName}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span>Created {new Date(deal.createdAt).toLocaleDateString()} by {deal.createdBy}</span>
                            {fdv('investor_prospect_id') && deal.investorProspectId && (() => {
                              const ip = prospects.find(p => p.id === deal.investorProspectId);
                              return ip ? <span className="text-green-700">👤 Investor: {ip.companyName || ip.name}</span> : null;
                            })()}
                          </div>
                          {(deal.dealSize || deal.commissionRate) && (
                            <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                              {deal.dealSize && <span>Deal: {displayAmt(deal.dealSize, deal)}</span>}
                              {deal.dealSize && deal.commissionRate && <span>·</span>}
                              {deal.commissionRate && <span>Commission: {deal.commissionRate}%</span>}
                              {deal.dealSize && deal.commissionRate && (
                                <span className="font-medium" style={{ color: BRAND_COLORS.navy }}>
                                  = {displayAmt(parseFloat(deal.dealSize) * parseFloat(deal.commissionRate) / 100, deal)}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="text-right flex items-start space-x-3">
                          {fdv('total_agreed') && <div className="text-right">
                            {currentUser.role === 'Partner' ? (() => {
                              const pid = currentUser.partnerId || currentUser.id;
                              const myShare = computeDealTotals(deal).partnerPayouts
                                .filter(sp => sp.partnerId === pid || sp.partnerId === currentUser.id)
                                .reduce((s, sp) => s + (sp.amount || 0), 0);
                              return myShare > 0 ? (
                                <>
                                  <p className="text-2xl font-bold" style={{ color: BRAND_COLORS.gold }}>
                                    {displayAmt(myShare, deal)}
                                  </p>
                                  <p className="text-xs text-gray-500">My Commission</p>
                                </>
                              ) : (
                                <>
                                  <p className="text-lg font-semibold text-gray-400">—</p>
                                  <p className="text-xs text-gray-400">Commission TBD</p>
                                </>
                              );
                            })() : (
                              <>
                                <p className="text-2xl font-bold" style={{ color: BRAND_COLORS.gold }}>
                                  {displayAmt(deal.totalReceived, deal)}
                                </p>
                                <p className="text-xs text-gray-500">Total Received</p>
                              </>
                            )}
                            {/* View-only currency toggle */}
                            <div className="flex gap-1 mt-1.5 justify-end">
                              {CURRENCIES.map(({ code }) => (
                                <button
                                  key={code}
                                  onClick={(e) => { e.stopPropagation(); setDisplayCurrencies(prev => ({ ...prev, [deal.id]: code })); }}
                                  className="text-xs px-1.5 py-0.5 rounded font-medium transition-all"
                                  style={{
                                    backgroundColor: (displayCurrencies[deal.id] || deal.currency || 'USD') === code ? BRAND_COLORS.navy : '#f3f4f6',
                                    color: (displayCurrencies[deal.id] || deal.currency || 'USD') === code ? 'white' : '#9CA3AF',
                                  }}
                                >
                                  {code}
                                </button>
                              ))}
                            </div>
                          </div>}
                          <button
                            onClick={(e) => { e.stopPropagation(); setProfileDeal(deal); }}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            title="View deal profile"
                          >
                            <Eye className="w-4 h-4" style={{ color: BRAND_COLORS.navy }} />
                          </button>
                          <button
                            onClick={() => setExpandedDealId(isExpanded ? null : deal.id)}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            title={isExpanded ? "Collapse" : "Expand details"}
                          >
                            <ChevronDown
                              className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                              style={{ color: BRAND_COLORS.navy }}
                            />
                          </button>
                          {(currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') && (
                            <button
                              onClick={() => openEditDeal(deal)}
                              className="p-2 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Edit deal"
                            >
                              <Edit2 className="w-4 h-4" style={{ color: BRAND_COLORS.navy }} />
                            </button>
                          )}
                          {(currentUser.role === 'Admin' || currentUser.role === 'Super Admin') && onDeleteDeal && (
                            <button
                              onClick={() => {
                                showConfirm(`Delete deal "${deal.dealName}"? This cannot be undone.`, () => onDeleteDeal(deal.id), 'Delete');
                              }}
                              className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete deal"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </button>
                          )}
                          {currentUser.role === 'Editor' && onRequestDelete && (
                            <button
                              onClick={() => onRequestDelete('Deal', deal.id, deal.dealName)}
                              className="p-2 hover:bg-yellow-50 rounded-lg transition-colors"
                              title="Request delete"
                              style={{ color: '#92400E' }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                      
                      {/* Payment status row + Admin exposure controls */}
                      <div className="flex items-center justify-between mt-2">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm flex-1">
                          {/* Total — hidden from partner unless exposed */}
                          {fdv('total_agreed') && (currentUser.role !== 'Partner' || deal.exposure?.showTotal) && (
                            <div>
                              <p className="text-gray-500">Total Received</p>
                              <p className="font-semibold" style={{ color: BRAND_COLORS.navy }}>{displayAmt(deal.totalReceived, deal)}</p>
                            </div>
                          )}
                          {/* Deductions — hidden from partner unless exposed */}
                          {fdv('deductions') && (currentUser.role !== 'Partner' || deal.exposure?.showDeductions) && (
                            <div>
                              <p className="text-gray-500">Deductions</p>
                              <p className="font-semibold text-red-600">-{displayAmt(dt.totalDeductions, deal)}</p>
                            </div>
                          )}
                          <div>
                            <p className="text-gray-500">Pool</p>
                            <p className="font-semibold" style={{ color: BRAND_COLORS.gold }}>{displayAmt(dt.poolAmount, deal)}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Payment</p>
                            <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium"
                              style={{
                                backgroundColor: deal.paymentStatus === 'Paid' ? '#D1FAE5' : '#FEF3C7',
                                color: deal.paymentStatus === 'Paid' ? '#065F46' : '#92400E'
                              }}>
                              {deal.paymentStatus || 'Pending'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Admin: Exposure Controls + Payment toggle */}
                      {(currentUser.role === 'Admin' || currentUser.role === 'Editor' || currentUser.role === 'Super Admin') && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <div className="flex items-center gap-4 flex-wrap">
                            <span className="text-xs font-medium text-gray-500">Partner sees:</span>
                            {[
                              { key: 'showTotal', label: 'Total Amount' },
                              { key: 'showDeductions', label: 'Deductions' },
                              { key: 'showOtherPartners', label: 'Other Partners' },
                            ].map(({ key, label }) => {
                              const isOn = deal.exposure?.[key] || false;
                              return (
                                <button
                                  key={key}
                                  onClick={() => onUpdateDeal && onUpdateDeal(deal.id, {
                                    exposure: { ...(deal.exposure || {}), [key]: !isOn }
                                  })}
                                  className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-all"
                                  style={{
                                    backgroundColor: isOn ? '#D1FAE5' : '#F3F4F6',
                                    color: isOn ? '#065F46' : '#9CA3AF',
                                    border: `1px solid ${isOn ? '#10B981' : '#E5E7EB'}`
                                  }}
                                >
                                  {isOn ? <Unlock className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                                  {label}
                                </button>
                              );
                            })}
                            {/* Payment status */}
                            <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ml-auto"
                              style={{
                                backgroundColor: deal.paymentStatus === 'Paid in Full' ? '#D1FAE5' : deal.paymentStatus === 'Partial' ? '#DBEAFE' : '#FEF3C7',
                                color: deal.paymentStatus === 'Paid in Full' ? '#065F46' : deal.paymentStatus === 'Partial' ? '#1D4ED8' : '#92400E',
                              }}>
                              {deal.paymentStatus === 'Paid in Full' ? '✅' : deal.paymentStatus === 'Partial' ? '🔄' : '⏳'}
                              {deal.paymentStatus || 'Unpaid'}
                              {deal.totalPaid > 0 && ` · $${deal.totalPaid.toLocaleString()}`}
                            </span>
                            {(deal.dealState === 'Active' || deal.dealState === 'Closed') && onAddPayment && (
                              <button
                                onClick={() => {
                                  setPaymentDealId(deal.id);
                                  setPaymentForm({ amount: '', date: new Date().toISOString().split('T')[0], type: 'Success Fee', notes: '' });
                                  setShowPaymentModal(true);
                                }}
                                className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ml-1"
                                style={{ backgroundColor: BRAND_COLORS.navy, color: 'white' }}
                              >
                                <DollarSign className="w-3 h-3" />
                                Record Payment
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="border-t border-gray-200 bg-gray-50">
                        <div className="p-6 space-y-6">
                          {/* Deal Structure — shown if dealSize or commissionRate is set */}
                          {(deal.dealSize || deal.commissionRate) && currentUser.role !== 'Partner' && (
                            <div className="bg-white rounded-lg border border-gray-200 p-4">
                              <h5 className="font-semibold mb-3 flex items-center text-sm" style={{ color: BRAND_COLORS.navy }}>
                                <BarChart3 className="w-4 h-4 mr-2" style={{ color: BRAND_COLORS.gold }} />
                                Deal Structure
                              </h5>
                              <div className="flex items-center gap-4 flex-wrap text-sm">
                                {deal.dealSize && (
                                  <div>
                                    <p className="text-xs text-gray-400 mb-0.5">Total Deal Size</p>
                                    <p className="font-bold text-lg" style={{ color: BRAND_COLORS.navy }}>{displayAmt(deal.dealSize, deal)}</p>
                                  </div>
                                )}
                                {deal.dealSize && deal.commissionRate && (
                                  <div className="text-gray-300 text-xl font-light">×</div>
                                )}
                                {deal.commissionRate && (
                                  <div>
                                    <p className="text-xs text-gray-400 mb-0.5">Commission Rate</p>
                                    <p className="font-bold text-lg" style={{ color: BRAND_COLORS.navy }}>{deal.commissionRate}%</p>
                                  </div>
                                )}
                                {deal.dealSize && deal.commissionRate && (
                                  <>
                                    <div className="text-gray-300 text-xl font-light">=</div>
                                    <div>
                                      <p className="text-xs text-gray-400 mb-0.5">Derived Commission</p>
                                      <p className="font-bold text-lg" style={{ color: BRAND_COLORS.gold }}>
                                        {displayAmt(parseFloat(deal.dealSize) * parseFloat(deal.commissionRate) / 100, deal)}
                                      </p>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          )}
                          {/* Deductions Breakdown — hidden from partner unless exposed */}
                          {fdv('deductions') && deal.deductions && deal.deductions.length > 0 &&
                            (currentUser.role !== 'Partner' || deal.exposure?.showDeductions) && (
                            <div>
                              <h5 className="font-semibold mb-3 flex items-center" style={{ color: BRAND_COLORS.navy }}>
                                <TrendingDown className="w-4 h-4 mr-2 text-red-500" />
                                External Deductions ({deal.deductions.length})
                              </h5>
                              <div className="bg-white rounded-lg border border-gray-200 divide-y">
                                {deal.deductions.map((ded, idx) => {
                                  const amount = ded.type === 'percentage' 
                                    ? deal.totalReceived * (parseFloat(ded.value) / 100)
                                    : parseFloat(ded.value);
                                  
                                  return (
                                    <div key={idx} className="p-3 flex items-center justify-between">
                                      <div className="flex-1">
                                        <p className="font-medium text-sm" style={{ color: BRAND_COLORS.navy }}>
                                          {ded.name}
                                        </p>
                                        <p className="text-xs text-gray-500">{ded.description}</p>
                                      </div>
                                      <div className="text-right">
                                        <p className="font-semibold text-red-600">
                                          -${amount.toLocaleString()}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                          {ded.type === 'percentage' ? `${ded.value}%` : 'Fixed'}
                                        </p>
                                      </div>
                                    </div>
                                  );
                                })}
                                <div className="p-3 bg-red-50 flex justify-between items-center">
                                  <p className="font-semibold text-sm">Total Deductions</p>
                                  <p className="font-bold text-red-600">
                                    -${dt.totalDeductions.toLocaleString()}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {/* The Pool */}
                          <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 border-2 rounded-lg p-4" style={{ borderColor: BRAND_COLORS.gold }}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                <div className="w-12 h-12 rounded-full flex items-center justify-center mr-3" style={{ backgroundColor: BRAND_COLORS.gold }}>
                                  <DollarSign className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                  <p className="text-sm text-gray-700 font-medium">The Pool (Available for Sharing)</p>
                                  <p className="text-xs text-gray-600">
                                    {safePct(dt.poolAmount, deal.totalReceived)}% of total received
                                  </p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-3xl font-bold" style={{ color: BRAND_COLORS.gold }}>
                                  ${dt.poolAmount.toLocaleString()}
                                </p>
                              </div>
                            </div>
                          </div>
                          
                          {/* Strategic Partners Breakdown — hidden from partner unless exposed */}
                          {deal.strategicPartners && deal.strategicPartners.length > 0 &&
                            (currentUser.role !== 'Partner' || deal.exposure?.showOtherPartners) && (
                            <div>
                              <h5 className="font-semibold mb-3 flex items-center" style={{ color: BRAND_COLORS.navy }}>
                                <Users className="w-4 h-4 mr-2" style={{ color: BRAND_COLORS.royalBlue }} />
                                Strategic Partners ({deal.strategicPartners.length})
                              </h5>
                              <div className="bg-white rounded-lg border border-gray-200 divide-y">
                                {deal.strategicPartners.map((sp, idx) => {
                                  const payout = dt.partnerPayouts?.find(p => p.id === sp.id || p.name === (sp.isExternal ? sp.externalName : sp.name)) || {};
                                  const displayName = sp.isExternal ? sp.externalName : sp.name;
                                  const amount = payout.amount ?? (dt.poolAmount * (parseFloat(sp.percentage) || 0) / 100);
                                  return (
                                    <div key={sp.id || idx} className="p-3">
                                      <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                          <div className="flex items-center gap-2">
                                            <p className="font-medium text-sm" style={{ color: BRAND_COLORS.navy }}>
                                              {displayName || '—'}
                                            </p>
                                            {sp.isExternal ? (
                                              <StatusChip variant="warning">External</StatusChip>
                                            ) : (
                                              <StatusChip variant="info">Partner</StatusChip>
                                            )}
                                          </div>
                                          {sp.isExternal && sp.externalCompany && (
                                            <p className="text-xs text-gray-500 mt-0.5">🏢 {sp.externalCompany}</p>
                                          )}
                                          {sp.description && (
                                            <p className="text-xs text-gray-400">{sp.description}</p>
                                          )}
                                          {sp.isExternal && sp.externalPaymentDetails && (
                                            <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                                              <span className="font-medium">Payment:</span> {sp.externalPaymentDetails}
                                            </p>
                                          )}
                                        </div>
                                        <div className="text-right ml-4">
                                          <p className="font-semibold" style={{ color: BRAND_COLORS.royalBlue }}>
                                            ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                          </p>
                                          <p className="text-xs text-gray-500">{safePct(amount, dt.poolAmount)}% of pool</p>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                                <div className="p-3 flex justify-between items-center" style={{ backgroundColor: `${BRAND_COLORS.royalBlue}10` }}>
                                  <p className="font-semibold text-sm">Total Partners Payout</p>
                                  <p className="font-bold" style={{ color: BRAND_COLORS.royalBlue }}>
                                    ${dt.totalPartnerPayout.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {/* Company Keeps */}
                          <div className="bg-gradient-to-r from-green-50 to-green-100 border-2 border-green-500 rounded-lg p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center mr-3">
                                  <Building2 className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                  <p className="text-sm text-gray-700 font-medium">Company Keeps</p>
                                  <p className="text-xs text-gray-600">
                                    {safePct(dt.companyKeeps, dt.poolAmount)}% of pool
                                  </p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-3xl font-bold text-green-600">
                                  ${dt.companyKeeps.toLocaleString()}
                                </p>
                              </div>
                            </div>
                          </div>
                          
                          {/* Visual Waterfall — hidden from partner */}
                          {currentUser.role !== 'Partner' && deal.totalReceived > 0 && dt.poolAmount > 0 && (() => {
                            // gloss/mkbar/lbl/pct were leftover style objects from a previous gradient-bar render;
                            // current waterfall uses white-bg bars with coloured borders. Only `vline` is still used.
                            const vline = { position:'absolute', left:'10px', top:0, bottom:0, width:'1px', background:'rgba(0,0,0,0.1)', borderRadius:'1px' };
                            return (
                              <div style={{ marginBottom:'16px' }}>
                                <h5 className="font-semibold mb-4 flex items-center" style={{ color: BRAND_COLORS.navy }}>
                                  <BarChart3 className="w-4 h-4 mr-2" style={{ color: BRAND_COLORS.gold }} />
                                  Money Flow
                                </h5>
                                <div style={{ display:'flex', flexDirection:'column', gap:'5px' }}>
                                  <div style={{ background:'#fff', border:'2px solid #1A1C5C', borderRadius:'10px', height:'40px', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 14px' }}>
                                    <span style={{ color:'#1A1C5C', fontWeight:600, fontSize:'13px' }}>${deal.totalReceived.toLocaleString()} — Total received</span><span style={{ color:'#1A1C5C', fontSize:'11px', opacity:0.6 }}>100%</span>
                                  </div>
                                  <div style={{ paddingLeft:'22px', position:'relative', display:'flex', flexDirection:'column', gap:'5px' }}>
                                    <div style={vline}/>
                                    {dt.totalDeductions > 0 && (
                                      <div style={{ background:'#fff', border:'2px solid #8a3018', borderRadius:'10px', height:'40px', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 14px', maxWidth:'280px' }}>
                                        <span style={{ color:'#8a3018', fontWeight:600, fontSize:'13px' }}>−${dt.totalDeductions.toLocaleString()} Deductions</span><span style={{ color:'#8a3018', fontSize:'11px', opacity:0.6 }}>{safePct(dt.totalDeductions, deal.totalReceived)}%</span>
                                      </div>
                                    )}
                                    <div style={{ background:'#fff', border:'2px solid #b8860b', borderRadius:'10px', height:'40px', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 14px' }}>
                                      <span style={{ color:'#7a5018', fontWeight:600, fontSize:'13px' }}>${dt.poolAmount.toLocaleString()} — Pool</span><span style={{ color:'#7a5018', fontSize:'11px', opacity:0.6 }}>{safePct(dt.poolAmount, deal.totalReceived)}%</span>
                                    </div>
                                    <div style={{ paddingLeft:'22px', position:'relative', display:'flex', flexDirection:'column', gap:'4px' }}>
                                      <div style={vline}/>
                                      {(dt.partnerPayouts||[]).map((payout,idx) => payout.amount > 0 && (
                                        <div key={idx} style={{ background:'#fff', border:'2px solid #185FA5', borderRadius:'10px', height:'40px', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 14px', maxWidth:'340px' }}>
                                          <span style={{ color:'#185FA5', fontWeight:600, fontSize:'13px' }}>{payout.name}</span><span style={{ color:'#185FA5', fontSize:'11px', opacity:0.65 }}>${payout.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} · {safePct(payout.amount, dt.poolAmount)}%</span>
                                        </div>
                                      ))}
                                      <div style={{ background:'#fff', border:'2px solid #0F6E56', borderRadius:'10px', height:'40px', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 14px' }}>
                                        <span style={{ color:'#0F6E56', fontWeight:600, fontSize:'13px' }}>Company keeps</span><span style={{ color:'#0F6E56', fontSize:'11px', opacity:0.65 }}>${dt.companyKeeps.toLocaleString()} · {safePct(dt.companyKeeps, dt.poolAmount)}%</span>
                                      </div>
                                    </div>
                                  </div>
                                  <div style={{ display:'grid', gridTemplateColumns:'repeat(3,minmax(0,1fr))', gap:'8px', marginTop:'12px' }}>
                                    {[
                                      { label:'Deductions',      value:displayAmt(dt.totalDeductions, deal),       sub:`${safePct(dt.totalDeductions, deal.totalReceived)}% of total`, accent: BRAND_COLORS.statusError },
                                      { label:'Partner payouts', value:displayAmt(dt.totalPartnerPayout||0, deal), sub:`${safePct(dt.totalPartnerPayout||0, dt.poolAmount)}% of pool`,  accent: BRAND_COLORS.statusInfo },
                                      { label:'Company keeps',   value:displayAmt(dt.companyKeeps, deal),          sub:`${safePct(dt.companyKeeps, dt.poolAmount)}% of pool`,         accent: BRAND_COLORS.statusSuccess },
                                    ].map(c => <KPICard key={c.label} size="tiny" {...c} />)}
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                          {/* Summary Sentence — hidden from partner */}
                          {currentUser.role !== 'Partner' && (
                          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                            <h5 className="font-semibold text-blue-900 text-sm mb-2">Deal Summary</h5>
                            <p className="text-blue-800 text-sm leading-relaxed">
                              Out of <strong>{displayAmt(deal.totalReceived, deal)}</strong> total received,
                              <strong> {displayAmt(dt.totalDeductions, deal)}</strong> went to external parties.
                              This left <strong>{displayAmt(dt.poolAmount, deal)}</strong> as the pool for sharing.
                              {dt.partnerPayouts.map((p, idx) => (
                                p.amount > 0 && (
                                  <span key={idx}>
                                    {' '}<strong>{p.name}</strong> gets <strong>{displayAmt(p.amount, deal)}</strong>
                                    {idx < dt.partnerPayouts.length - 1 ? ',' : ''}
                                  </span>
                                )
                              ))}
                              {' '}and the company keeps <strong>{displayAmt(dt.companyKeeps, deal)}</strong>.
                            </p>
                          </div>
                          )}

                          {/* Partner: show only their commission + payment status */}
                          {currentUser.role === 'Partner' && (() => {
                            const pid = currentUser.partnerId || currentUser.id;
                            const myShare = computeDealTotals(deal).partnerPayouts
                              .filter(sp => sp.partnerId === pid || sp.partnerId === currentUser.id)
                              .reduce((s, sp) => s + (sp.amount || 0), 0);
                            return (
                              <div className="p-4 rounded-lg" style={{ backgroundColor: `${BRAND_COLORS.gold}15`, border: `1px solid ${BRAND_COLORS.gold}` }}>
                                <h5 className="font-semibold mb-2" style={{ color: BRAND_COLORS.navy }}>Your Commission</h5>
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="text-3xl font-bold" style={{ color: BRAND_COLORS.gold }}>
                                      {myShare > 0 ? `$${myShare.toLocaleString()}` : '—'}
                                    </p>
                                    <p className="text-xs text-gray-500 mt-0.5">
                                      {myShare > 0 ? 'Your share from this deal' : 'Commission amount to be confirmed'}
                                    </p>
                                  </div>
                                  <span className="px-3 py-1.5 rounded-full text-sm font-semibold"
                                    style={{
                                      backgroundColor: deal.paymentStatus === 'Paid' ? '#D1FAE5' : '#FEF3C7',
                                      color: deal.paymentStatus === 'Paid' ? '#065F46' : '#92400E'
                                    }}>
                                    {deal.paymentStatus === 'Paid' ? '✓ Paid' : '⏳ Pending Payment'}
                                  </span>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                  );
                });
              })()}
            </div>
          )}
        </div>
      </div>

      {/* Record Payment Modal */}
      {showPaymentModal && (() => {
        const deal = deals.find(d => d.id === paymentDealId);
        if (!deal) return null;
        const totalPaid = (deal.payments || []).reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
        const balance = (parseFloat(deal.totalAgreed) || 0) - totalPaid;

        // Calculate commission preview for this payment
        const payAmount = parseFloat(paymentForm.amount) || 0;
        const previewDeductions = (deal.deductions || []).reduce((s, d) => {
          return s + (d.type === 'percentage' ? payAmount * (parseFloat(d.value) || 0) / 100 : parseFloat(d.value) || 0);
        }, 0);
        const previewPool = payAmount - previewDeductions;
        const previewPartnerTotal = (deal.strategicPartners || []).reduce((s, p) => s + previewPool * (parseFloat(p.percentage) || 0) / 100, 0);
        const previewEvE = previewPool - previewPartnerTotal;

        return (
          <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
              {/* Header */}
              <div className="px-6 py-4 border-b" style={{ background: BRAND_COLORS.navy }}>
                <h3 className="text-lg font-bold text-white">Record Payment</h3>
                <p className="text-sm opacity-70 text-white mt-0.5">{deal.dealName}</p>
              </div>

              <div className="p-6 space-y-4">
                {/* Balance summary */}
                <div className="grid grid-cols-3 gap-3 p-3 rounded-lg" style={{ backgroundColor: `${BRAND_COLORS.gold}10` }}>
                  <div className="text-center">
                    <p className="text-xs text-gray-500">Agreed</p>
                    <p className="font-bold text-sm" style={{ color: BRAND_COLORS.navy }}>${(parseFloat(deal.totalAgreed) || 0).toLocaleString()}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-500">Paid so far</p>
                    <p className="font-bold text-sm text-green-600">${totalPaid.toLocaleString()}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-500">Balance</p>
                    <p className="font-bold text-sm text-blue-600">${balance.toLocaleString()}</p>
                  </div>
                </div>

                {/* Payment amount */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount Received <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-bold">$</span>
                    <input
                      type="number"
                      value={paymentForm.amount}
                      onChange={e => setPaymentForm(f => ({ ...f, amount: e.target.value }))}
                      placeholder="0"
                      className="w-full pl-8 pr-3 py-2 border-2 rounded-lg text-lg font-bold"
                      style={{ borderColor: BRAND_COLORS.gold, color: BRAND_COLORS.navy }}
                    />
                  </div>
                </div>

                {/* Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input
                    type="date"
                    value={paymentForm.date}
                    onChange={e => setPaymentForm(f => ({ ...f, date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                {/* Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Type</label>
                  <div className="flex gap-2">
                    {['Retainer', 'Success Fee', 'Other'].map(t => (
                      <button key={t}
                        onClick={() => setPaymentForm(f => ({ ...f, type: t }))}
                        className="flex-1 py-1.5 px-2 text-xs rounded-lg border-2 font-medium transition-all"
                        style={{
                          borderColor: paymentForm.type === t ? BRAND_COLORS.navy : '#e5e7eb',
                          backgroundColor: paymentForm.type === t ? `${BRAND_COLORS.navy}10` : 'white',
                          color: paymentForm.type === t ? BRAND_COLORS.navy : '#6b7280'
                        }}
                      >{t}</button>
                    ))}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <input
                    type="text"
                    value={paymentForm.notes}
                    onChange={e => setPaymentForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="Optional notes..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>

                {/* Commission preview */}
                {payAmount > 0 && (
                  <div className="p-3 rounded-lg border" style={{ backgroundColor: '#F0FDF4', borderColor: '#BBF7D0' }}>
                    <p className="text-xs font-semibold text-green-800 mb-2">Commission Preview</p>
                    <div className="space-y-1 text-xs text-green-700">
                      <div className="flex justify-between"><span>Payment amount</span><span>${payAmount.toLocaleString()}</span></div>
                      {previewDeductions > 0 && <div className="flex justify-between"><span>Deductions</span><span>-${previewDeductions.toLocaleString()}</span></div>}
                      <div className="flex justify-between"><span>Pool</span><span>${previewPool.toLocaleString()}</span></div>
                      {(deal.strategicPartners || []).map(p => (
                        <div key={p.id} className="flex justify-between">
                          <span>{p.name} ({p.percentage}%)</span>
                          <span>${(previewPool * (parseFloat(p.percentage) || 0) / 100).toLocaleString()}</span>
                        </div>
                      ))}
                      <div className="flex justify-between font-semibold border-t border-green-200 pt-1 mt-1">
                        <span>EvE keeps</span><span>${previewEvE.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
                <button onClick={() => { setShowPaymentModal(false); setPaymentDealId(null); }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 text-sm">
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
                      window.alert('Please enter a valid amount');
                      return;
                    }
                    const payment = {
                      id: `pay_${Date.now()}`,
                      amount: parseFloat(paymentForm.amount),
                      date: paymentForm.date,
                      type: paymentForm.type,
                      notes: paymentForm.notes,
                      commissionSnapshot: (deal.strategicPartners || []).map(p => ({ name: p.name, percentage: p.percentage })),
                      recordedAt: new Date().toISOString(),
                      recordedBy: currentUser.name
                    };
                    onAddPayment(paymentDealId, payment);
                    setShowPaymentModal(false);
                    setPaymentDealId(null);
                    setPaymentForm({ amount: '', date: new Date().toISOString().split('T')[0], type: 'Success Fee', notes: '' });
                  }}
                  className="px-4 py-2 rounded-lg text-white text-sm font-medium"
                  style={{ backgroundColor: BRAND_COLORS.navy }}
                >
                  Record Payment
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {profileDeal && (
        <DealProfileDrawer
          deal={profileDeal}
          prospects={prospects}
          partners={partners}
          onClose={() => setProfileDeal(null)}
          onEdit={(d) => { openEditDeal(d); setProfileDeal(null); }}
          onToggleHidden={onToggleHidden}
        />
      )}
    </div>
  );
};

// ============================================================================
// VIEW: Users
// ============================================================================

const UsersView = ({ users, partners, onAdd, onEdit, onToggleStatus, onDelete, onResetPassword, onResendConfirmation, onInvitePartner, showConfirm }) => {
  const [activeSubTab, setActiveSubTab] = useState('dashboard');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Filter users
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  const activeUsers = users.filter(u => u.status === 'Active').length;
  const adminUsers = users.filter(u => u.role === 'Admin').length;
  const partnerUsers = users.filter(u => u.role === 'Partner').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: BRAND_COLORS.navy }}>User Management</h2>
          <p className="text-gray-600 mt-1">Manage admin and partner user accounts</p>
        </div>
        <button
          onClick={onAdd}
          className="flex items-center space-x-2 px-4 py-2 rounded-lg font-medium shadow-lg transition-all"
          style={{ background: BRAND_COLORS.gold, color: 'white' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 8px 16px rgba(212, 175, 55, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
          }}
        >
          <UserCog className="w-5 h-5" />
          <span>Add User</span>
        </button>
      </div>

      {/* Sub-Tabs */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveSubTab('dashboard')}
            className={`px-6 py-3 font-medium text-sm transition-colors ${
              activeSubTab === 'dashboard'
                ? 'border-b-2 text-gray-900'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            style={activeSubTab === 'dashboard' ? { borderBottomColor: BRAND_COLORS.gold } : {}}
          >
            <div className="flex items-center space-x-2">
              <BarChart3 className="w-4 h-4" />
              <span>Dashboard</span>
            </div>
          </button>
          <button
            onClick={() => setActiveSubTab('users')}
            className={`px-6 py-3 font-medium text-sm transition-colors ${
              activeSubTab === 'users'
                ? 'border-b-2 text-gray-900'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            style={activeSubTab === 'users' ? { borderBottomColor: BRAND_COLORS.gold } : {}}
          >
            <div className="flex items-center space-x-2">
              <Users className="w-4 h-4" />
              <span>All Users</span>
            </div>
          </button>
        </div>
      </div>

      {/* Dashboard Tab */}
      {activeSubTab === 'dashboard' && (
        <UserManagementDashboard users={users} partners={partners} />
      )}

      {/* Users List Tab */}
      {activeSubTab === 'users' && (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        {[
          { label: 'Total Users', value: users.length, icon: Users,  accent: BRAND_COLORS.gold },
          { label: 'Active',      value: activeUsers,  icon: Check,  accent: BRAND_COLORS.statusSuccess },
          { label: 'Admins',      value: adminUsers,   icon: Shield, accent: BRAND_COLORS.statusInfo },
          { label: 'Partners',    value: partnerUsers, icon: Users,  accent: BRAND_COLORS.gold },
        ].map(c => <KPICard key={c.label} {...c} />)}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
                style={{ focusRing: BRAND_COLORS.gold }}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
              style={{ focusRing: BRAND_COLORS.gold }}
            >
              <option value="all">All Roles</option>
              <option value="Admin">Admin</option>
			  <option value="Editor">Editor</option>
              <option value="Partner">Partner</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
              style={{ focusRing: BRAND_COLORS.gold }}
            >
              <option value="all">All Statuses</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users — mobile cards (md:hidden) */}
      <div className="md:hidden space-y-3">
        {filteredUsers.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center text-sm" style={{ color: BRAND_COLORS.textSecondary }}>
            No users to show.
          </div>
        ) : filteredUsers.map(user => {
          const partner = user.partnerId ? partners.find(p => p.id === user.partnerId) : null;
          const status = user.status || 'Active';
          const statusColor = status === 'Active' ? BRAND_COLORS.statusSuccess
            : status === 'Inactive' ? BRAND_COLORS.textSecondary
            : BRAND_COLORS.statusWarning;
          const roleColor = user.role === 'Super Admin' ? BRAND_COLORS.statusError
            : user.role === 'Admin' ? BRAND_COLORS.gold
            : user.role === 'Editor' ? BRAND_COLORS.statusInfo
            : user.role === 'Partner' ? BRAND_COLORS.navy
            : BRAND_COLORS.textSecondary;
          let lastLoginLabel = 'Never';
          if (user.lastLogin) {
            const diffDays = Math.floor((Date.now() - new Date(user.lastLogin).getTime()) / 86400000);
            if (diffDays === 0)       lastLoginLabel = 'Today';
            else if (diffDays === 1)  lastLoginLabel = 'Yesterday';
            else if (diffDays < 7)    lastLoginLabel = `${diffDays}d ago`;
            else if (diffDays < 30)   lastLoginLabel = `${Math.floor(diffDays/7)}w ago`;
            else if (diffDays < 365)  lastLoginLabel = `${Math.floor(diffDays/30)}mo ago`;
            else                      lastLoginLabel = `${Math.floor(diffDays/365)}y ago`;
          }
          return (
            <div
              key={user.id}
              className="bg-white rounded-xl shadow-sm border p-4"
              style={{ borderColor: BRAND_COLORS.borderNavy12 }}
            >
              {/* Top row: avatar + name/email + status */}
              <div className="flex items-start gap-3 mb-3">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg, #CBD5E1 0%, #64748B 100%)' }}
                >
                  {(user.name || user.email || '?').charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate" style={{ color: BRAND_COLORS.navy }}>{user.name || '—'}</p>
                  <p className="text-xs truncate" style={{ color: BRAND_COLORS.textSecondary }}>{user.email}</p>
                </div>
                <span
                  className="text-xs font-semibold whitespace-nowrap px-2 py-1 rounded-full"
                  style={{ color: statusColor, background: `${statusColor}18`, border: `1px solid ${statusColor}40` }}
                >
                  {status}
                </span>
              </div>

              {/* Role + Partner chips */}
              <div className="flex flex-wrap gap-1.5 mb-2">
                <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                  style={{ background: `${roleColor}18`, color: roleColor, border: `1px solid ${roleColor}40` }}>
                  {user.role || 'Viewer'}
                </span>
                {partner && (
                  <span className="text-xs px-2 py-0.5 rounded-full"
                    style={{ background: `${BRAND_COLORS.navy}0D`, color: BRAND_COLORS.textSecondary, border: `1px solid ${BRAND_COLORS.borderNavy12}` }}>
                    {partner.name}
                  </span>
                )}
              </div>

              {/* Footer: last login + actions */}
              <div className="flex items-center justify-between text-xs">
                <span style={{ color: BRAND_COLORS.textTertiary }}>
                  Last login: <span style={{ color: BRAND_COLORS.textSecondary }}>{lastLoginLabel}</span>
                </span>
                <div className="flex items-center gap-1">
                  {onEdit && (
                    <button onClick={() => onEdit(user)} className="p-2 rounded hover:bg-gray-100" title="Edit">
                      <Edit2 className="w-4 h-4" style={{ color: BRAND_COLORS.navy }} />
                    </button>
                  )}
                  {onToggleStatus && (
                    <button onClick={() => onToggleStatus(user.id)} className="p-2 rounded hover:bg-gray-100"
                      title={status === 'Active' ? 'Deactivate' : 'Activate'}>
                      {status === 'Active' ? <Lock className="w-4 h-4" style={{ color: BRAND_COLORS.statusWarning }} /> : <Unlock className="w-4 h-4" style={{ color: BRAND_COLORS.statusSuccess }} />}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Users Table (desktop) */}
      <div className="hidden md:block bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Partner</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Login</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredUsers.map(user => {
                const partner = user.partnerId ? partners.find(p => p.id === user.partnerId) : null;
                
                return (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{user.name}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                        style={{
                          backgroundColor: user.role === 'Admin' ? `${BRAND_COLORS.gold}20` : `${BRAND_COLORS.royalBlue}20`,
                          color: user.role === 'Admin' ? BRAND_COLORS.gold : BRAND_COLORS.royalBlue
                        }}
                      >
                        {user.role === 'Admin' ? <Shield className="w-3 h-3 mr-1" /> : <Users className="w-3 h-3 mr-1" />}
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {partner ? partner.name : '-'}
                    </td>
                    <td className="px-6 py-4">
                      {user._pendingInvite ? (
                        <span
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                          style={{ backgroundColor: `${BRAND_COLORS.gold}20`, color: BRAND_COLORS.goldDeep }}
                          title="Partner exists in the partners table but has not been invited as a user yet."
                        >
                          <Clock className="w-3 h-3 mr-1" />
                          Pending Invite
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                          style={{
                            backgroundColor: user.status === 'Active' ? '#d1fae520' : '#fee2e220',
                            color: user.status === 'Active' ? '#10b981' : '#ef4444'
                          }}
                        >
                          {user.status}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : (user._pendingInvite ? '—' : 'Never')}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        {user._pendingInvite ? (
                          // Pending-invite row: single action — send the magic-link invite.
                          // Edit/Delete/Toggle/Reset are gated until the user_profile exists.
                          onInvitePartner && (
                            <button
                              onClick={() => {
                                showConfirm(
                                  `Send a magic-link invite to ${user.email || user.name}?`,
                                  () => onInvitePartner(user),
                                  'Invite'
                                );
                              }}
                              className="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors flex items-center gap-1"
                              style={{ backgroundColor: BRAND_COLORS.gold, color: BRAND_COLORS.textOnGold }}
                              title="Send invite email"
                            >
                              <Send className="w-3.5 h-3.5" /> Invite
                            </button>
                          )
                        ) : (
                          <>
                            <button
                              onClick={() => onEdit(user)}
                              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                              title="Edit user"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => onToggleStatus(user.id)}
                              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                              title={user.status === 'Active' ? 'Deactivate user' : 'Activate user'}
                            >
                              {user.status === 'Active' ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                            </button>
                            <button
                              onClick={() => onResetPassword(user.id)}
                              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                              title="Reset password"
                            >
                              <Key className="w-4 h-4" />
                            </button>
                            {onResendConfirmation && (
                              <button
                                onClick={() => onResendConfirmation(user.id)}
                                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                title="Resend confirmation email"
                              >
                                <Send className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => {
                                showConfirm(`Are you sure you want to delete ${user.name}?`, () => onDelete(user.id), 'Delete');
                              }}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete user"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <UserCog className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No users found matching your filters.</p>
          </div>
        )}
      </div>
        </>
      )}
    </div>
  );
};

// ============================================================================
// MODAL: User Profile
// ============================================================================

const UserProfileModal = ({ currentUser, partner, onClose, onSave, sectors: allSectors = [] }) => {
  const [form, setForm] = useState({
    name: currentUser.name || '',
    email: currentUser.email || '',
    phone: currentUser.phone || partner?.phone || '',
    ...(currentUser.role === 'Partner' ? {
      sectors: partner?.sectors || [],
      geography: partner?.geography || { continents: [], countries: [] },
    } : {}),
  });
  const [saving, setSaving] = useState(false);
  const [identities, setIdentities] = useState(null);
  const [linkingGoogle, setLinkingGoogle] = useState(false);

  useEffect(() => {
    import('./supabaseClient').then(({ getUserIdentities }) => {
      getUserIdentities().then(setIdentities).catch(() => setIdentities([]));
    });
  }, []);

  const isGoogleLinked = identities?.some(i => i.provider === 'google');

  const handleLinkGoogle = async () => {
    setLinkingGoogle(true);
    try {
      const { linkGoogleAccount } = await import('./supabaseClient');
      await linkGoogleAccount();
    } catch (e) {
      window.alert('Failed to link Google account: ' + e.message);
      setLinkingGoogle(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      window.alert('Name and email are required.');
      return;
    }
    setSaving(true);
    await onSave(form);
    setSaving(false);
    onClose();
  };

  const contractPartner = partner;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg my-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100 flex justify-between items-center rounded-t-2xl" style={{ background: BRAND_COLORS.navy }}>
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold text-white shadow-lg" style={{ background: BRAND_COLORS.gold }}>
              {form.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="text-white font-semibold text-lg">{currentUser.name}</p>
              <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: BRAND_COLORS.gold + '30', color: BRAND_COLORS.gold }}>
                {currentUser.role}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="text-white opacity-70 hover:opacity-100 transition-opacity">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
          {/* Editable Fields */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">Personal Information</h4>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:outline-none"
                  placeholder="+1-555-0100"
                />
              </div>
            </div>
          </div>

          {/* Partner Preferences — Partner role only */}
          {currentUser.role === 'Partner' && (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">My Preferences</h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Sectors of Interest</label>
                  <SectorSelector
                    sectors={allSectors}
                    selectedSectors={form.sectors || []}
                    onSectorsChange={v => setForm(f => ({ ...f, sectors: v }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Geographic Focus</label>
                  <GeographySelector
                    selectedGeo={form.geography || { continents: [], countries: [] }}
                    onGeoChange={v => setForm(f => ({ ...f, geography: v }))}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Linked Accounts */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">Linked Accounts</h4>
            <div className="flex items-center justify-between p-3 rounded-xl border border-gray-200 bg-gray-50">
              <div className="flex items-center gap-3">
                <svg width="20" height="20" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                  <path fill="none" d="M0 0h48v48H0z"/>
                </svg>
                <div>
                  <p className="text-sm font-medium text-gray-800">Google</p>
                  {identities === null
                    ? <p className="text-xs text-gray-400">Checking...</p>
                    : isGoogleLinked
                      ? <p className="text-xs text-green-600 font-medium">Connected</p>
                      : <p className="text-xs text-gray-400">Not connected</p>
                  }
                </div>
              </div>
              {!isGoogleLinked && identities !== null && (
                <button
                  onClick={handleLinkGoogle}
                  disabled={linkingGoogle}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border transition-all disabled:opacity-50"
                  style={{ borderColor: BRAND_COLORS.navy, color: BRAND_COLORS.navy, backgroundColor: 'white' }}
                >
                  {linkingGoogle ? 'Redirecting...' : 'Link account'}
                </button>
              )}
            </div>
          </div>

          {/* Contract Info — Partner only */}
          {currentUser.role === 'Partner' && contractPartner && (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">Agreement Details</h4>
              <div className="rounded-xl p-4 space-y-3" style={{ backgroundColor: BRAND_COLORS.cream, border: `1px solid ${BRAND_COLORS.gold}40` }}>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-gray-500">Agreement Type</p>
                    <p className="text-sm font-semibold" style={{ color: BRAND_COLORS.navy }}>{contractPartner.agreementType || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Status</p>
                    <StatusChip variant={PARTNER_STATUS_VARIANT[contractPartner.status] || 'neutral'}>{contractPartner.status}</StatusChip>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Start Date</p>
                    <p className="text-sm font-medium" style={{ color: BRAND_COLORS.navy }}>
                      {contractPartner.startDate ? new Date(contractPartner.startDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">End Date</p>
                    <p className="text-sm font-medium" style={{ color: BRAND_COLORS.navy }}>
                      {contractPartner.endDate ? new Date(contractPartner.endDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Tail End Date</p>
                    <p className="text-sm font-medium" style={{ color: BRAND_COLORS.gold }}>
                      {contractPartner.tailEndDate ? new Date(contractPartner.tailEndDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Tail Period</p>
                    <p className="text-sm font-medium" style={{ color: BRAND_COLORS.navy }}>18 months</p>
                  </div>
                </div>

                {/* Sectors & Geography — now editable in "My Preferences" above */}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end space-x-3 rounded-b-2xl">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition-colors">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2 text-white rounded-lg text-sm font-medium transition-all disabled:opacity-50"
            style={{ background: BRAND_COLORS.gold }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// VIEW: Audit Log
// ============================================================================
// MESSAGING COMPONENTS
// ============================================================================

// ── NewMessageModal ──────────────────────────────────────────────────────────
const NewMessageModal = ({ currentUser, context, teasers = [], prospects = [], onClose, onSubmit }) => {
  const [form, setForm] = useState({
    subject: context?.refName ? `Question about "${context.refName}"` : '',
    category: context?.category || 'general',
    refId: context?.refId || '',
    refName: context?.refName || '',
    body: '',
  });
  const [saving, setSaving] = useState(false);

  const refOptions = form.category === 'teaser'
    ? teasers.map(t => ({ id: t.id, name: t.alias || t.name || t.companyName || t.id }))
    : form.category === 'prospect'
      ? prospects.map(p => ({ id: p.id, name: p.companyName || p.name || p.id }))
      : [];

  const handleCategoryChange = (cat) => {
    setForm(f => ({ ...f, category: cat, refId: '', refName: '', subject: cat === 'general' ? '' : f.subject }));
  };

  const handleRefChange = (id) => {
    const opt = refOptions.find(o => o.id === id);
    setForm(f => ({ ...f, refId: id, refName: opt?.name || '', subject: opt ? `Question about "${opt.name}"` : f.subject }));
  };

  const handleSubmit = async () => {
    if (!form.subject.trim() || !form.body.trim()) { window.alert('Subject and message are required.'); return; }
    setSaving(true);
    await onSubmit(form);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-5 border-b border-gray-100 flex justify-between items-center rounded-t-2xl" style={{ background: BRAND_COLORS.navy }}>
          <div className="flex items-center gap-3">
            <MessageCircle className="w-5 h-5 text-white" />
            <h3 className="text-white font-semibold text-lg">New Message</h3>
          </div>
          <button onClick={onClose} className="text-white opacity-70 hover:opacity-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Topic</label>
            <div className="flex gap-2">
              {[{ v: 'general', l: 'General' }, { v: 'teaser', l: 'About a Teaser' }, { v: 'prospect', l: 'About a Prospect' }].map(({ v, l }) => (
                <button key={v} onClick={() => handleCategoryChange(v)}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border transition-all"
                  style={form.category === v
                    ? { background: BRAND_COLORS.navy, color: 'white', borderColor: BRAND_COLORS.navy }
                    : { background: 'white', color: BRAND_COLORS.textSecondary, borderColor: BRAND_COLORS.border }}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          {/* Ref picker */}
          {(form.category === 'teaser' || form.category === 'prospect') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {form.category === 'teaser' ? 'Which teaser?' : 'Which prospect?'}
              </label>
              <select value={form.refId} onChange={e => handleRefChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:outline-none">
                <option value="">— Select —</option>
                {refOptions.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
          )}
          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
            <input type="text" value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:outline-none"
              placeholder="What's your question about?" />
          </div>
          {/* Body */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
            <textarea value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:outline-none resize-none"
              placeholder="Write your message here..." />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 rounded-b-2xl">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
          <button onClick={handleSubmit} disabled={saving}
            className="px-5 py-2 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2"
            style={{ background: BRAND_COLORS.gold }}>
            <Send className="w-4 h-4" />
            {saving ? 'Sending...' : 'Send Message'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Shared: ThreadDetail (used by both Partner and Admin views) ───────────────
const ThreadDetail = ({ thread, posts, currentUser, onAddPost, onClose: onCloseThread, onBack }) => {
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const threadPosts = [...(posts.filter(p => p.threadId === thread.id))].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

  const handleSend = async () => {
    if (!reply.trim()) return;
    setSending(true);
    await onAddPost(thread.id, reply.trim());
    setReply('');
    setSending(false);
  };

  const formatTs = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + ' · ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Thread header */}
      <div className="flex items-center gap-3 mb-4">
        <button onClick={onBack} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
          <ChevronLeft className="w-5 h-5" style={{ color: BRAND_COLORS.navy }} />
        </button>
        <div className="flex-1">
          <h3 className="font-semibold text-base" style={{ color: BRAND_COLORS.navy }}>{thread.subject}</h3>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs px-2 py-0.5 rounded-full font-medium"
              style={thread.category === 'teaser'
                ? { background: `${BRAND_COLORS.gold}20`, color: BRAND_COLORS.gold }
                : thread.category === 'prospect'
                  ? { background: `${BRAND_COLORS.navy}15`, color: BRAND_COLORS.navy }
                  : { background: '#f3f4f6', color: '#6b7280' }}>
              {thread.category === 'teaser' ? '📄 Teaser' : thread.category === 'prospect' ? '🏢 Prospect' : '💬 General'}
              {thread.refName ? `: ${thread.refName}` : ''}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${thread.status === 'open' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
              {thread.status}
            </span>
          </div>
        </div>
        {thread.status === 'open' && onCloseThread && (
          <button onClick={() => onCloseThread(thread.id)}
            className="text-xs px-3 py-1.5 rounded-lg border hover:bg-gray-50 transition-colors"
            style={{ borderColor: BRAND_COLORS.border, color: BRAND_COLORS.textSecondary }}>
            Close thread
          </button>
        )}
      </div>

      {/* Posts */}
      <div className="flex-1 overflow-y-auto space-y-4 mb-4" style={{ maxHeight: '420px' }}>
        {threadPosts.map(post => {
          const isMe = post.authorId === currentUser.id;
          const isPartnerPost = post.authorRole === 'partner';
          return (
            <div key={post.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
              <div className="max-w-[80%]">
                <div className="px-4 py-3 rounded-2xl text-sm"
                  style={isMe
                    ? { background: BRAND_COLORS.navy, color: 'white', borderBottomRightRadius: '4px' }
                    : { background: '#f3f4f6', color: BRAND_COLORS.textPrimary, borderBottomLeftRadius: '4px' }}>
                  {post.body}
                </div>
                <p className={`text-xs mt-1 ${isMe ? 'text-right' : 'text-left'}`} style={{ color: BRAND_COLORS.textTertiary }}>
                  {isPartnerPost ? 'You' : 'EvE Team'} · {formatTs(post.createdAt)}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Reply box */}
      {thread.status === 'open' && (
        <div className="flex gap-2 pt-3 border-t border-gray-100">
          <textarea
            value={reply}
            onChange={e => setReply(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
            rows={2}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:outline-none resize-none"
            placeholder="Write a reply… (Enter to send)"
          />
          <button onClick={handleSend} disabled={sending || !reply.trim()}
            className="px-4 py-2 rounded-xl text-white text-sm font-medium disabled:opacity-40 flex items-center gap-1.5 self-end"
            style={{ background: BRAND_COLORS.gold }}>
            <Send className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

// ── PartnerMessagesView ──────────────────────────────────────────────────────
const PartnerMessagesView = ({ currentUser, threads, posts, teasers, prospects, onNewMessage, onAddPost, onMarkRead, onClose }) => {
  const [activeThread, setActiveThread] = useState(null);

  const openThread = async (thread) => {
    setActiveThread(thread);
    if (thread.partnerUnread) onMarkRead(thread.id);
  };

  const formatDate = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const now = new Date();
    const diffDays = Math.floor((now - d) / 86400000);
    if (diffDays === 0) return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    if (diffDays === 1) return 'Yesterday';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      {!activeThread ? (
        <>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold" style={{ color: BRAND_COLORS.navy }}>My Messages</h2>
            <button onClick={onNewMessage}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-medium"
              style={{ background: BRAND_COLORS.gold }}>
              <Plus className="w-4 h-4" /> New Message
            </button>
          </div>
          {threads.length === 0 ? (
            <div className="text-center py-16">
              <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-20" style={{ color: BRAND_COLORS.navy }} />
              <p className="text-sm" style={{ color: BRAND_COLORS.textSecondary }}>No messages yet. Click "New Message" to start a conversation.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {[...threads].sort((a, b) => new Date(b.lastPostAt) - new Date(a.lastPostAt)).map(thread => {
                const postCount = posts.filter(p => p.threadId === thread.id).length;
                return (
                  <div key={thread.id} onClick={() => openThread(thread)}
                    className="p-4 rounded-xl cursor-pointer transition-all hover:shadow-md"
                    style={{
                      backgroundColor: BRAND_COLORS.surface,
                      border: `1px solid ${thread.partnerUnread ? BRAND_COLORS.gold : BRAND_COLORS.border}`,
                      borderLeft: thread.partnerUnread ? `4px solid ${BRAND_COLORS.gold}` : `1px solid ${BRAND_COLORS.border}`,
                    }}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {thread.partnerUnread && <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: BRAND_COLORS.gold }} />}
                          <p className={`text-sm truncate ${thread.partnerUnread ? 'font-semibold' : 'font-medium'}`} style={{ color: BRAND_COLORS.textPrimary }}>
                            {thread.subject}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs px-2 py-0.5 rounded-full"
                            style={{ background: '#f3f4f6', color: BRAND_COLORS.textSecondary }}>
                            {thread.category === 'teaser' ? '📄' : thread.category === 'prospect' ? '🏢' : '💬'} {thread.category}
                            {thread.refName ? ` · ${thread.refName}` : ''}
                          </span>
                          <span className={`text-xs px-1.5 py-0.5 rounded ${thread.status === 'open' ? 'text-green-600' : 'text-gray-400'}`}>
                            {thread.status}
                          </span>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xs" style={{ color: BRAND_COLORS.textTertiary }}>{formatDate(thread.lastPostAt)}</p>
                        <p className="text-xs mt-1" style={{ color: BRAND_COLORS.textTertiary }}>{postCount} message{postCount !== 1 ? 's' : ''}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      ) : (
        <ThreadDetail
          thread={activeThread}
          posts={posts}
          currentUser={currentUser}
          onAddPost={async (tid, body) => {
            await onAddPost(tid, body);
            setActiveThread(prev => ({ ...prev, lastPostAt: new Date().toISOString(), status: prev.status }));
          }}
          onClose={onClose}
          onBack={() => setActiveThread(null)}
        />
      )}
    </div>
  );
};

// ── AdminMessagesView ────────────────────────────────────────────────────────
const AdminMessagesView = ({ currentUser, threads, posts, partners, onAddPost, onMarkRead, onClose }) => {
  const [activeThread, setActiveThread] = useState(null);
  const [filterStatus, setFilterStatus] = useState('open');

  const openThread = async (thread) => {
    setActiveThread(thread);
    if (thread.adminUnread) onMarkRead(thread.id);
  };

  const formatDate = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const now = new Date();
    const diffDays = Math.floor((now - d) / 86400000);
    if (diffDays === 0) return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    if (diffDays === 1) return 'Yesterday';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const filtered = [...threads]
    .filter(t => filterStatus === 'all' || t.status === filterStatus)
    .sort((a, b) => new Date(b.lastPostAt) - new Date(a.lastPostAt));

  const unreadCount = threads.filter(t => t.adminUnread).length;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {!activeThread ? (
        <>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold" style={{ color: BRAND_COLORS.navy }}>Partner Messages</h2>
              {unreadCount > 0 && (
                <p className="text-sm mt-1" style={{ color: BRAND_COLORS.gold }}>{unreadCount} unread message{unreadCount !== 1 ? 's' : ''}</p>
              )}
            </div>
            <div className="flex gap-2">
              {['open', 'closed', 'all'].map(s => (
                <button key={s} onClick={() => setFilterStatus(s)}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border transition-all capitalize"
                  style={filterStatus === s
                    ? { background: BRAND_COLORS.navy, color: 'white', borderColor: BRAND_COLORS.navy }
                    : { background: 'white', color: BRAND_COLORS.textSecondary, borderColor: BRAND_COLORS.border }}>
                  {s}
                </button>
              ))}
            </div>
          </div>
          {filtered.length === 0 ? (
            <div className="text-center py-16">
              <Inbox className="w-12 h-12 mx-auto mb-3 opacity-20" style={{ color: BRAND_COLORS.navy }} />
              <p className="text-sm" style={{ color: BRAND_COLORS.textSecondary }}>No messages with status "{filterStatus}".</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map(thread => {
                const partner = partners.find(p => p.id === thread.partnerId);
                const postCount = posts.filter(p => p.threadId === thread.id).length;
                return (
                  <div key={thread.id} onClick={() => openThread(thread)}
                    className="p-4 rounded-xl cursor-pointer transition-all hover:shadow-md"
                    style={{
                      backgroundColor: BRAND_COLORS.surface,
                      border: `1px solid ${thread.adminUnread ? BRAND_COLORS.gold : BRAND_COLORS.border}`,
                      borderLeft: thread.adminUnread ? `4px solid ${BRAND_COLORS.gold}` : `1px solid ${BRAND_COLORS.border}`,
                    }}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {thread.adminUnread && <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: BRAND_COLORS.gold }} />}
                          <p className={`text-sm truncate ${thread.adminUnread ? 'font-semibold' : 'font-medium'}`} style={{ color: BRAND_COLORS.textPrimary }}>
                            {thread.subject}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-medium" style={{ color: BRAND_COLORS.navy }}>
                            {partner?.name || thread.partnerId}
                          </span>
                          <span className="text-xs px-2 py-0.5 rounded-full"
                            style={{ background: '#f3f4f6', color: BRAND_COLORS.textSecondary }}>
                            {thread.category === 'teaser' ? '📄' : thread.category === 'prospect' ? '🏢' : '💬'} {thread.category}
                            {thread.refName ? ` · ${thread.refName}` : ''}
                          </span>
                          <span className={`text-xs px-1.5 py-0.5 rounded ${thread.status === 'open' ? 'text-green-600' : 'text-gray-400'}`}>
                            {thread.status}
                          </span>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xs" style={{ color: BRAND_COLORS.textTertiary }}>{formatDate(thread.lastPostAt)}</p>
                        <p className="text-xs mt-1" style={{ color: BRAND_COLORS.textTertiary }}>{postCount} msg{postCount !== 1 ? 's' : ''}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      ) : (
        <ThreadDetail
          thread={activeThread}
          posts={posts}
          currentUser={currentUser}
          onAddPost={async (tid, body) => {
            await onAddPost(tid, body);
            setActiveThread(prev => ({ ...prev, lastPostAt: new Date().toISOString() }));
          }}
          onClose={onClose}
          onBack={() => setActiveThread(null)}
        />
      )}
    </div>
  );
};

// ============================================================================

const AuditLogView = ({ logs, users = [] }) => {
  const sortedLogs = [...logs].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const resolveUser = (userId) => {
    if (!userId || userId === 'system') return userId || '—';
    const u = users.find(u => u.id === userId);
    return u ? u.name : userId;
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Audit Log</h2>

      <div className="bg-white rounded-lg shadow overflow-hidden overflow-x-auto">
        {sortedLogs.length === 0 ? (
          <div className="text-center py-16">
            <div style={{ color: BRAND_COLORS.navy, opacity: 0.55, marginBottom: 16, display: 'flex', justifyContent: 'center' }}>
              <NauticalMark name="lighthouse" size={80} />
            </div>
            <p className="text-gray-700 text-sm font-medium">No audit log entries yet</p>
            <p className="text-gray-400 text-xs mt-1">Activity will appear here as users work in the portal.</p>
          </div>
        ) : (
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entity</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedLogs.slice(0, 50).map(log => (
              <tr key={log.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(log.createdAt).toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
                    {log.entityType}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {log.action}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {resolveUser(log.userId)}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {log.details}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// VIEW: Settings
// ============================================================================

// ============================================================================
// VIEW: Deletion Requests (Admin only)
// ============================================================================

const DeleteRequestsView = ({ requests = [], onProcess, showConfirm }) => {
  const [filter, setFilter] = useState('Pending');
  const [noteInput, setNoteInput] = useState({});

  const filtered = requests.filter(r => filter === 'All' || r.status === filter);

  const typeColors = {
    partner:  { bg: '#EEF2FF', color: '#3730A3' },
    prospect: { bg: '#ECFDF5', color: '#065F46' },
    deal:     { bg: '#FFFBEB', color: '#92400E' },
    workflow: { bg: '#F0F9FF', color: '#0C4A6E' },
    user:     { bg: '#FFF1F2', color: '#9F1239' },
  };

  const pendingCount = requests.filter(r => r.status === 'Pending').length;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold" style={{ color: BRAND_COLORS.navy }}>
          Deletion Requests
          {pendingCount > 0 && (
            <span className="ml-3 px-2 py-0.5 rounded-full text-sm font-semibold text-white" style={{ backgroundColor: '#EF4444' }}>
              {pendingCount}
            </span>
          )}
        </h2>
        <p className="text-gray-500 mt-1 text-sm">Review and approve/reject deletion requests from Editors</p>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 border-b" style={{ borderColor: `${BRAND_COLORS.navy}20` }}>
        {['Pending', 'Approved', 'Rejected', 'All'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className="px-4 py-2.5 text-sm font-medium border-b-2 -mb-0.5 transition-all"
            style={{
              borderColor: filter === f ? BRAND_COLORS.gold : 'transparent',
              color: filter === f ? BRAND_COLORS.navy : '#6B7280',
              backgroundColor: filter === f ? `${BRAND_COLORS.cream}60` : 'transparent'
            }}>
            {f}
            {f === 'Pending' && pendingCount > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-xs bg-red-100 text-red-700">{pendingCount}</span>
            )}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center" style={{ border: `1px solid ${BRAND_COLORS.border}` }}>
          <CheckCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500">No {filter.toLowerCase()} requests</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(req => {
            const tc = typeColors[req.type?.toLowerCase()] || { bg: '#F3F4F6', color: '#374151' };
            return (
              <div key={req.id} className="bg-white rounded-xl p-5"
                style={{ border: `1px solid ${req.status === 'Pending' ? '#F59E0B' : BRAND_COLORS.border}` }}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="px-2 py-0.5 rounded text-xs font-semibold uppercase"
                        style={{ backgroundColor: tc.bg, color: tc.color }}>
                        {req.type}
                      </span>
                      <span className="font-semibold text-sm" style={{ color: BRAND_COLORS.navy }}>
                        {req.entityName}
                      </span>
                      <StatusChip variant={req.status === 'Pending' ? 'warning' : req.status === 'Approved' ? 'success' : 'error'}>{req.status}</StatusChip>
                    </div>
                    <p className="text-xs text-gray-500">
                      Requested by <strong>{req.requestedByName}</strong> · {new Date(req.requestedAt).toLocaleDateString()}
                    </p>
                    {req.adminNote && (
                      <p className="text-xs text-gray-600 mt-1 italic">Note: {req.adminNote}</p>
                    )}
                  </div>

                  {req.status === 'Pending' && (
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <input
                        type="text"
                        value={noteInput[req.id] || ''}
                        onChange={e => setNoteInput(n => ({ ...n, [req.id]: e.target.value }))}
                        placeholder="Note (optional)"
                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs w-40"
                      />
                      <button
                        onClick={() => {
                          showConfirm(`Approve deletion of "${req.entityName}"? This will permanently delete the item.`, () => onProcess(req.id, true, noteInput[req.id] || ''), 'Approve');
                        }}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-all"
                        style={{ backgroundColor: '#10B981' }}
                      >
                        <Check className="w-3 h-3" /> Approve
                      </button>
                      <button
                        onClick={() => {
                          if (!noteInput[req.id]?.trim()) {
                            window.alert('Please add a note explaining why this request is rejected.');
                            return;
                          }
                          onProcess(req.id, false, noteInput[req.id]);
                        }}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-all"
                        style={{ backgroundColor: '#EF4444' }}
                      >
                        <X className="w-3 h-3" /> Reject
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// COMPONENT: Field Manager Tab
// ============================================================================
const FieldManagerTab = ({ showConfirm }) => {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeEntity, setActiveEntity] = useState('partner');
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [newField, setNewField] = useState({
    label: '', type: 'text', options: '', section: '',
    is_required: false, display_in_table: false,
  });
  const [saving, setSaving] = useState(false);

  const ENTITIES = [
    { id: 'partner',  label: 'Partner'  },
    { id: 'prospect', label: 'Prospect' },
    { id: 'deal',     label: 'Deal'     },
    { id: 'teaser',   label: 'Teaser'   },
    { id: 'all',      label: 'Show All' },
  ];
  const FIELD_TYPES = ['text', 'number', 'date', 'checkbox', 'select', 'multiselect', 'radio'];
  const SECTIONS    = ['basic', 'identity', 'financial', 'legal', 'compliance', 'contact', 'other'];

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { supabase } = await import('./supabaseClient');
        const { data, error } = await supabase
          .from('custom_field_definitions')
          .select('*')
          .order('sort_order', { ascending: true });
        if (error) throw error;
        setFields(data || []);
      } catch (e) {
        console.error('[FieldManager] load error:', e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggleField = async (field, column) => {
    try {
      const { supabase } = await import('./supabaseClient');
      const { error } = await supabase
        .from('custom_field_definitions')
        .update({ [column]: !field[column] })
        .eq('id', field.id);
      if (!error) setFields(prev => prev.map(f => f.id === field.id ? { ...f, [column]: !f[column] } : f));
    } catch (e) { console.error('[FieldManager] toggle error:', e.message); }
  };

  const addField = async () => {
    if (!newField.label.trim()) return;
    const targetEntity = activeEntity === 'all' ? 'partner' : activeEntity;
    setSaving(true);
    try {
      const { supabase } = await import('./supabaseClient');
      const payload = {
        entity_type:      targetEntity,
        field_label:      newField.label.trim(),
        field_name:       newField.label.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, ''),
        field_type:       newField.type,
        field_options:    newField.type === 'select'
          ? newField.options.split(',').map(o => o.trim()).filter(Boolean)
          : null,
        section:          newField.section || null,
        is_required:      newField.is_required,
        display_in_table: newField.display_in_table,
        is_system:        false,
        is_active:        true,
        sort_order:       fields.filter(f => f.entity_type === targetEntity).length + 1,
      };
      const { data, error } = await supabase
        .from('custom_field_definitions')
        .insert(payload)
        .select()
        .single();
      if (error) throw error;
      setFields(prev => [...prev, data]);
      setNewField({ label: '', type: 'text', options: '', section: '', is_required: false, display_in_table: false });
      setShowAdd(false);
    } catch (e) {
      console.error('[FieldManager] add error:', e.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteField = (field) => {
    showConfirm(`Delete field "${field.field_label}"? This cannot be undone.`, async () => {
      try {
        const { supabase } = await import('./supabaseClient');
        const { error } = await supabase
          .from('custom_field_definitions')
          .delete()
          .eq('id', field.id);
        if (!error) setFields(prev => prev.filter(f => f.id !== field.id));
      } catch (e) { console.error('[FieldManager] delete error:', e.message); }
    }, 'Delete');
  };

  const searchLower = search.toLowerCase();
  const matchesSearch = (f) =>
    !search || f.field_label?.toLowerCase().includes(searchLower) || f.field_name?.toLowerCase().includes(searchLower);

  const visibleFields = fields
    .filter(f => activeEntity === 'all' || f.entity_type === activeEntity)
    .filter(matchesSearch);

  const Toggle = ({ field, column }) => (
    <button onClick={() => toggleField(field, column)}
      className="relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none"
      style={{ backgroundColor: field[column] ? BRAND_COLORS.gold : '#D1D5DB' }}>
      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${field[column] ? 'translate-x-4' : 'translate-x-0.5'}`} />
    </button>
  );

  return (
    <div>
      <div className="mb-5">
        <h3 className="text-xl font-bold" style={{ color: BRAND_COLORS.navy }}>Field Manager</h3>
        <p className="text-sm text-gray-500 mt-0.5">
          View and manage fields per entity. System fields can be toggled. Custom fields can be created and deleted.
        </p>
      </div>

      {/* Search */}
      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by label or field name…"
          className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2" />
        {search && (
          <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Entity tabs */}
      <div className="flex gap-1 border-b mb-5" style={{ borderColor: BRAND_COLORS.navy + '20' }}>
        {ENTITIES.map(e => {
          const count = e.id === 'all'
            ? fields.filter(matchesSearch).length
            : fields.filter(f => f.entity_type === e.id && matchesSearch(f)).length;
          return (
            <button key={e.id} onClick={() => { setActiveEntity(e.id); setShowAdd(false); }}
              className="px-4 py-2.5 text-sm font-medium border-b-2 -mb-0.5 transition-all"
              style={{
                borderColor: activeEntity === e.id ? BRAND_COLORS.gold : 'transparent',
                color: activeEntity === e.id ? BRAND_COLORS.navy : '#6B7280',
                backgroundColor: activeEntity === e.id ? `${BRAND_COLORS.cream}50` : 'transparent',
              }}>
              {e.label}
              <span className="ml-2 px-1.5 py-0.5 rounded-full text-xs"
                style={{ backgroundColor: BRAND_COLORS.navy + '12', color: BRAND_COLORS.navy }}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="py-12 text-center text-gray-400 text-sm">Loading fields…</div>
      ) : (
        <>
          {/* Fields table */}
          <div className="rounded-xl border overflow-hidden mb-4" style={{ borderColor: BRAND_COLORS.navy + '18' }}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ backgroundColor: BRAND_COLORS.navy + '08' }}>
                    {activeEntity === 'all' && (
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Entity</th>
                    )}
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Label</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Field Name</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Type</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Section</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Origin</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Active</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Required</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">In Table</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {visibleFields.length === 0 ? (
                    <tr>
                      <td colSpan={activeEntity === 'all' ? 10 : 9} className="px-4 py-10 text-center text-gray-400 text-sm">
                        {search ? `No fields match "${search}"` : 'No fields defined for this entity'}
                      </td>
                    </tr>
                  ) : visibleFields.map((f, i) => (
                    <tr key={f.id}
                      className={i % 2 !== 0 ? 'bg-gray-50' : 'bg-white'}
                      style={{ borderTop: `1px solid ${BRAND_COLORS.navy}10` }}>
                      {activeEntity === 'all' && (
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 rounded text-xs font-medium capitalize"
                            style={{ backgroundColor: BRAND_COLORS.navy + '10', color: BRAND_COLORS.navy }}>
                            {f.entity_type}
                          </span>
                        </td>
                      )}
                      <td className="px-4 py-3 font-medium text-gray-800">{f.field_label}</td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-400">{f.field_name}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 capitalize">{f.field_type}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-400 capitalize">{f.section || '—'}</td>
                      <td className="px-4 py-3 text-center">
                        {f.is_system
                          ? <span className="px-2 py-0.5 rounded-full text-xs font-semibold"
                              style={{ backgroundColor: BRAND_COLORS.navy + '12', color: BRAND_COLORS.navy }}>System</span>
                          : <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700">Custom</span>
                        }
                      </td>
                      <td className="px-4 py-3 text-center"><Toggle field={f} column="is_active" /></td>
                      <td className="px-4 py-3 text-center"><Toggle field={f} column="is_required" /></td>
                      <td className="px-4 py-3 text-center"><Toggle field={f} column="display_in_table" /></td>
                      <td className="px-4 py-3 text-right">
                        {!f.is_system && (
                          <button onClick={() => deleteField(f)}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Add custom field — hidden on Show All tab */}
          {activeEntity !== 'all' && (
            showAdd ? (
              <div className="rounded-xl border p-4 space-y-3"
                style={{ borderColor: BRAND_COLORS.gold + '60', backgroundColor: `${BRAND_COLORS.cream}30` }}>
                <p className="text-sm font-semibold" style={{ color: BRAND_COLORS.navy }}>New Custom Field</p>
                <div className="flex gap-3 flex-wrap items-end">
                  <div className="flex-1 min-w-[160px]">
                    <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">Label</label>
                    <input type="text" value={newField.label}
                      onChange={e => setNewField(f => ({ ...f, label: e.target.value }))}
                      placeholder="e.g. LinkedIn URL"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2" />
                  </div>
                  <div className="w-32">
                    <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">Type</label>
                    <select value={newField.type}
                      onChange={e => setNewField(f => ({ ...f, type: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2">
                      {FIELD_TYPES.map(t => (
                        <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                  <div className="w-36">
                    <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">Section</label>
                    <select value={newField.section}
                      onChange={e => setNewField(f => ({ ...f, section: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2">
                      <option value="">— None —</option>
                      {SECTIONS.map(s => (
                        <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                  {newField.type === 'select' && (
                    <div className="flex-1 min-w-[200px]">
                      <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">Options (comma-separated)</label>
                      <input type="text" value={newField.options}
                        onChange={e => setNewField(f => ({ ...f, options: e.target.value }))}
                        placeholder="Option A, Option B, Option C"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2" />
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-6 pt-1">
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                    <input type="checkbox" checked={newField.is_required}
                      onChange={e => setNewField(f => ({ ...f, is_required: e.target.checked }))}
                      className="w-4 h-4 rounded" style={{ accentColor: BRAND_COLORS.navy }} />
                    Required
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                    <input type="checkbox" checked={newField.display_in_table}
                      onChange={e => setNewField(f => ({ ...f, display_in_table: e.target.checked }))}
                      className="w-4 h-4 rounded" style={{ accentColor: BRAND_COLORS.navy }} />
                    Show in table
                  </label>
                </div>
                <div className="flex gap-2 pt-1">
                  <button onClick={addField} disabled={saving || !newField.label.trim()}
                    className="flex items-center gap-2 px-4 py-2 text-white rounded-lg text-sm font-medium transition-all disabled:opacity-50"
                    style={{ backgroundColor: BRAND_COLORS.gold }}>
                    {saving ? 'Saving…' : <><Plus className="w-3.5 h-3.5" /> Add Field</>}
                  </button>
                  <button onClick={() => { setShowAdd(false); setNewField({ label: '', type: 'text', options: '', section: '', is_required: false, display_in_table: false }); }}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-all">
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button onClick={() => setShowAdd(true)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium border-2 border-dashed transition-all"
                style={{ borderColor: BRAND_COLORS.gold + '70', color: BRAND_COLORS.navy }}>
                <Plus className="w-4 h-4" /> Add Custom Field
              </button>
            )
          )}
        </>
      )}
    </div>
  );
};

// ============================================================================
// COMPONENT: Form Configurator Tab
// ============================================================================

const SortableFieldRow = ({ f, i, sFieldsLength, cfg, onToggle, onMove, activeRole }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: f.id });
  const isVisible  = activeRole
    ? (cfg?.role_permissions?.[activeRole] !== false)
    : (cfg?.is_visible ?? true);
  const isRequired = cfg?.is_required ?? false;
  return (
    <tr
      ref={setNodeRef}
      {...attributes}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        borderTop: `1px solid ${BRAND_COLORS.navy}08`,
        opacity: isDragging ? 0.4 : isVisible ? 1 : 0.45,
        backgroundColor: isDragging ? `${BRAND_COLORS.navy}06` : i % 2 !== 0 ? '#f9fafb' : 'white',
      }}
    >
      <td className="px-2 py-3 text-center w-8">
        <span {...listeners} style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
          className="text-gray-300 hover:text-gray-500 transition-colors inline-flex items-center">
          <GripVertical className="w-4 h-4" />
        </span>
      </td>
      <td className="px-4 py-3">
        <span className="font-medium text-gray-800">{f.field_label}</span>
        {f.is_system && (
          <span className="ml-2 px-1.5 py-0.5 rounded text-xs"
            style={{ backgroundColor: BRAND_COLORS.navy + '10', color: BRAND_COLORS.navy }}>sys</span>
        )}
      </td>
      <td className="px-4 py-3">
        <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 capitalize">{f.field_type}</span>
      </td>
      <td className="px-4 py-3 text-center">
        <button
          onClick={() => onToggle(f.id, activeRole ? `role_${activeRole}` : 'is_visible')}
          className="relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none"
          style={{ backgroundColor: isVisible ? (activeRole ? '#7C3AED' : BRAND_COLORS.gold) : '#D1D5DB' }}>
          <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${isVisible ? 'translate-x-4' : 'translate-x-0.5'}`} />
        </button>
      </td>
      <td className="px-4 py-3 text-center">
        {activeRole ? (
          <span className="text-xs text-gray-400">—</span>
        ) : (
          <button onClick={() => onToggle(f.id, 'is_required')}
            className="relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none"
            style={{ backgroundColor: isRequired ? BRAND_COLORS.gold : '#D1D5DB' }}>
            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${isRequired ? 'translate-x-4' : 'translate-x-0.5'}`} />
          </button>
        )}
      </td>
      <td className="px-4 py-3 text-center">
        <div className="flex items-center justify-center gap-1">
          <button onClick={() => onMove(f.id, 'up')} disabled={i === 0}
            className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 transition-colors">
            <ChevronUp className="w-3.5 h-3.5 text-gray-500" />
          </button>
          <span className="text-xs text-gray-400 w-6 text-center">{cfg?.sort_order ?? f.sort_order}</span>
          <button onClick={() => onMove(f.id, 'down')} disabled={i === sFieldsLength - 1}
            className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 transition-colors">
            <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
          </button>
        </div>
      </td>
    </tr>
  );
};

const FormConfiguratorTab = () => {

  const ENTITIES   = ['partner', 'prospect', 'deal', 'teaser'];
  const FORM_TYPES = ['add', 'edit', 'display'];
  const SECTIONS   = ['basic', 'identity', 'financial', 'legal', 'compliance', 'contact', 'contract', 'other'];
  const ROLES      = ['Super Admin', 'Admin', 'Editor', 'Partner', 'Viewer'];

  const [activeEntity,   setActiveEntity]   = useState('partner');
  const [activeFormType, setActiveFormType] = useState('add');
  const [activeRole,     setActiveRole]     = useState(null); // null = global config; string = role-specific can_view
  const [fieldDefs,      setFieldDefs]      = useState([]);
  const [config,         setConfig]         = useState({});
  const [loading,        setLoading]        = useState(true);
  const [saving,         setSaving]         = useState(false);
  const [saved,          setSaved]          = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Normalize sort_orders within a section so they are unique before reordering.
  const normalizedOrders = (sectionFields) => {
    const orders = sectionFields.map(f => config[f.id]?.[activeFormType]?.sort_order ?? f.sort_order ?? 0);
    const unique = new Set(orders);
    return unique.size < sectionFields.length ? sectionFields.map((_, i) => i * 10) : orders;
  };

  const handleDragEnd = (event, sectionFields) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = sectionFields.findIndex(f => f.id === active.id);
    const newIndex  = sectionFields.findIndex(f => f.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(sectionFields, oldIndex, newIndex);
    const originalOrders = normalizedOrders(sectionFields);
    const newConfig = { ...config };
    reordered.forEach((f, i) => {
      newConfig[f.id] = {
        ...newConfig[f.id],
        [activeFormType]: { ...newConfig[f.id]?.[activeFormType], sort_order: originalOrders[i] },
      };
    });
    setConfig(newConfig);
  };

  useEffect(() => { loadData(); }, [activeEntity]);

  const loadData = async () => {
    setLoading(true);
    try {
      const { supabase } = await import('./supabaseClient');
      const [defsRes, cfgRes] = await Promise.all([
        supabase.from('custom_field_definitions')
          .select('*')
          .eq('entity_type', activeEntity)
          .eq('is_active', true)
          .order('sort_order', { ascending: true }),
        supabase.from('form_field_config')
          .select('*, custom_field_definitions!inner(entity_type)')
          .eq('custom_field_definitions.entity_type', activeEntity),
      ]);
      if (defsRes.error) throw defsRes.error;
      setFieldDefs(defsRes.data || []);

      // Build config map: field_id → { add: {...}, edit: {...}, display: {...} }
      const map = {};
      (defsRes.data || []).forEach(f => {
        map[f.id] = {
          add:     { is_visible: true,  is_required: f.is_required || false, sort_order: f.sort_order },
          edit:    { is_visible: true,  is_required: f.is_required || false, sort_order: f.sort_order },
          display: { is_visible: true,  is_required: false,                  sort_order: f.sort_order },
        };
      });
      (cfgRes.data || []).forEach(row => {
        if (map[row.field_id]) {
          map[row.field_id][row.form_type] = {
            is_visible:       row.is_visible,
            is_required:      row.is_required,
            sort_order:       row.sort_order,
            role_permissions: row.role_permissions || {},
          };
        }
      });
      setConfig(map);
    } catch (e) {
      console.error('[FormConfigurator] load error:', e.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleConfig = (fieldId, key) => {
    if (key.startsWith('role_')) {
      const role = key.slice(5);
      setConfig(prev => {
        const currentPerms = prev[fieldId]?.[activeFormType]?.role_permissions || {};
        const currentVal   = currentPerms[role] !== false; // default true
        return {
          ...prev,
          [fieldId]: {
            ...prev[fieldId],
            [activeFormType]: {
              ...prev[fieldId]?.[activeFormType],
              role_permissions: { ...currentPerms, [role]: !currentVal },
            },
          },
        };
      });
    } else {
      setConfig(prev => ({
        ...prev,
        [fieldId]: {
          ...prev[fieldId],
          [activeFormType]: {
            ...prev[fieldId][activeFormType],
            [key]: !prev[fieldId][activeFormType][key],
          },
        },
      }));
    }
  };

  const moveField = (fieldId, direction) => {
    const allSorted = getSortedFields();
    const field = allSorted.find(f => f.id === fieldId);
    if (!field) return;
    // Operate within the same section only
    const sectionFields = allSorted.filter(f => (f.section || '') === (field.section || ''));
    const idx = sectionFields.findIndex(f => f.id === fieldId);
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= sectionFields.length) return;
    const reordered = arrayMove(sectionFields, idx, newIdx);
    const orders = normalizedOrders(sectionFields);
    const newConfig = { ...config };
    reordered.forEach((f, i) => {
      newConfig[f.id] = {
        ...newConfig[f.id],
        [activeFormType]: { ...newConfig[f.id]?.[activeFormType], sort_order: orders[i] },
      };
    });
    setConfig(newConfig);
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const { supabase } = await import('./supabaseClient');
      const rows = fieldDefs.flatMap(f =>
        FORM_TYPES.map(ft => ({
          field_id:         f.id,
          form_type:        ft,
          is_visible:       config[f.id]?.[ft]?.is_visible       ?? true,
          is_required:      config[f.id]?.[ft]?.is_required      ?? false,
          sort_order:       config[f.id]?.[ft]?.sort_order       ?? f.sort_order,
          role_permissions: config[f.id]?.[ft]?.role_permissions ?? {},
          updated_at:       new Date().toISOString(),
        }))
      );
      const { error } = await supabase.from('form_field_config').upsert(rows, { onConflict: 'field_id,form_type' });
      if (error) throw error;
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      console.error('[FormConfigurator] save error:', e.message);
    } finally {
      setSaving(false);
    }
  };

  const getSortedFields = () =>
    [...fieldDefs].sort((a, b) =>
      (config[a.id]?.[activeFormType]?.sort_order ?? a.sort_order) -
      (config[b.id]?.[activeFormType]?.sort_order ?? b.sort_order)
    );

  const sortedFields = getSortedFields();
  const groupedBySection = SECTIONS.reduce((acc, s) => {
    const sFields = sortedFields.filter(f => (f.section || 'other') === s);
    if (sFields.length) acc[s] = sFields;
    return acc;
  }, {});
  const unsectioned = sortedFields.filter(f => !f.section);

  return (
    <div>
      <div className="mb-5">
        <h3 className="text-xl font-bold" style={{ color: BRAND_COLORS.navy }}>Form Configurator</h3>
        <p className="text-sm text-gray-500 mt-0.5">
          Control which fields appear in each form, whether they are required, and their display order.
        </p>
      </div>

      {/* Entity + Form Type + Role selectors */}
      <div className="flex items-start gap-6 mb-6 flex-wrap">
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Entity</label>
          <div className="flex gap-1">
            {ENTITIES.map(e => (
              <button key={e} onClick={() => { setActiveEntity(e); setLoading(true); }}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-all capitalize"
                style={{
                  backgroundColor: activeEntity === e ? BRAND_COLORS.navy : `${BRAND_COLORS.navy}10`,
                  color: activeEntity === e ? '#fff' : BRAND_COLORS.navy,
                }}>
                {e}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Form Type</label>
          <div className="flex gap-1">
            {FORM_TYPES.map(ft => (
              <button key={ft} onClick={() => setActiveFormType(ft)}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-all capitalize"
                style={{
                  backgroundColor: activeFormType === ft ? BRAND_COLORS.gold : `${BRAND_COLORS.gold}20`,
                  color: activeFormType === ft ? '#fff' : BRAND_COLORS.navy,
                }}>
                {ft}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
            Role Visibility
            <span className="ml-1.5 text-gray-400 font-normal normal-case tracking-normal">
              (Global = all roles; select a role to set per-role access)
            </span>
          </label>
          <div className="flex gap-1 flex-wrap">
            <button onClick={() => setActiveRole(null)}
              className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all"
              style={{
                backgroundColor: activeRole === null ? BRAND_COLORS.navy : `${BRAND_COLORS.navy}10`,
                color: activeRole === null ? '#fff' : BRAND_COLORS.navy,
              }}>
              Global
            </button>
            {ROLES.map(r => (
              <button key={r} onClick={() => setActiveRole(r)}
                className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all"
                style={{
                  backgroundColor: activeRole === r ? '#7C3AED' : '#EDE9FE',
                  color: activeRole === r ? '#fff' : '#5B21B6',
                }}>
                {r}
              </button>
            ))}
          </div>
          {activeRole && (
            <p className="text-xs text-purple-600 mt-1.5">
              Toggling "Can View" for <strong>{activeRole}</strong>. Other roles are unaffected.
            </p>
          )}
        </div>
      </div>

      {loading ? (
        <div className="py-12 text-center text-gray-400 text-sm">Loading configuration…</div>
      ) : (
        <>
          <div className="space-y-4 mb-6">
            {Object.entries(groupedBySection).map(([section, sFields]) => (
              <div key={section} className="rounded-xl border overflow-hidden" style={{ borderColor: BRAND_COLORS.navy + '18' }}>
                {/* Section header */}
                <div className="flex items-center gap-2 px-4 py-2.5"
                  style={{ backgroundColor: BRAND_COLORS.navy + '08', borderBottom: `1px solid ${BRAND_COLORS.navy}12` }}>
                  <span className="text-xs font-bold uppercase tracking-widest capitalize" style={{ color: BRAND_COLORS.navy }}>{section}</span>
                  <span className="text-xs text-gray-400">({sFields.length} fields)</span>
                </div>
                {/* Fields */}
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ backgroundColor: BRAND_COLORS.navy + '04' }}>
                      <th className="w-8"></th>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-gray-400">Field</th>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-gray-400">Type</th>
                      <th className="text-center px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-gray-400"
                        style={{ color: activeRole ? '#7C3AED' : undefined }}>
                        {activeRole ? 'Can View' : 'Visible'}
                      </th>
                      <th className="text-center px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-gray-400">
                        {activeRole ? '' : 'Required'}
                      </th>
                      <th className="text-center px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-gray-400">Order</th>
                    </tr>
                  </thead>
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleDragEnd(e, sFields)}>
                    <SortableContext items={sFields.map(f => f.id)} strategy={verticalListSortingStrategy}>
                      <tbody>
                        {sFields.map((f, i) => (
                          <SortableFieldRow
                            key={f.id}
                            f={f}
                            i={i}
                            sFieldsLength={sFields.length}
                            cfg={config[f.id]?.[activeFormType]}
                            onToggle={toggleConfig}
                            onMove={moveField}
                            activeRole={activeRole}
                          />
                        ))}
                      </tbody>
                    </SortableContext>
                  </DndContext>
                </table>
              </div>
            ))}

            {/* Fields with no section */}
            {unsectioned.length > 0 && (
              <div className="rounded-xl border overflow-hidden" style={{ borderColor: BRAND_COLORS.navy + '18' }}>
                <div className="flex items-center gap-2 px-4 py-2.5"
                  style={{ backgroundColor: BRAND_COLORS.navy + '08', borderBottom: `1px solid ${BRAND_COLORS.navy}12` }}>
                  <span className="text-xs font-bold uppercase tracking-widest text-gray-400">No Section</span>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ backgroundColor: BRAND_COLORS.navy + '04' }}>
                      <th className="w-8"></th>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-gray-400">Field</th>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-gray-400">Type</th>
                      <th className="text-center px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-gray-400">Visible</th>
                      <th className="text-center px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-gray-400">Required</th>
                      <th className="text-center px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-gray-400">Order</th>
                    </tr>
                  </thead>
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleDragEnd(e, unsectioned)}>
                    <SortableContext items={unsectioned.map(f => f.id)} strategy={verticalListSortingStrategy}>
                      <tbody>
                        {unsectioned.map((f, i) => (
                          <SortableFieldRow
                            key={f.id}
                            f={f}
                            i={i}
                            sFieldsLength={unsectioned.length}
                            cfg={config[f.id]?.[activeFormType]}
                            onToggle={toggleConfig}
                            onMove={moveField}
                          />
                        ))}
                      </tbody>
                    </SortableContext>
                  </DndContext>
                </table>
              </div>
            )}
          </div>

          {/* Save button */}
          <button onClick={saveConfig} disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 text-white rounded-lg font-medium text-sm transition-all disabled:opacity-50"
            style={{ backgroundColor: saved ? '#16a34a' : BRAND_COLORS.gold }}>
            {saved ? <><CheckCircle className="w-4 h-4" /> Saved!</> : saving ? 'Saving…' : <><Save className="w-4 h-4" /> Save Configuration</>}
          </button>
        </>
      )}
    </div>
  );
};

const SettingsView = ({
  sectors, onAddSector, onRemoveSector,
  businessRules, onSaveBusinessRules,
  agreementTypes = DEFAULT_AGREEMENT_TYPES,
  onAddAgreementType, onRemoveAgreementType,
  commissionDefaults = DEFAULT_COMMISSION_DEFAULTS,
  onSaveCommissionDefaults,
  generalSettings: generalSettingsProp,
  onSaveSettings,
  securitySettings: securitySettingsProp,
  onSaveSecuritySettings,
  currentUserRole,
  showConfirm,
}) => {
  const [activeSettingsTab, setActiveSettingsTab] = useState('general');
  const [newSector, setNewSector] = useState('');
  const [newSectorIcon, setNewSectorIcon] = useState(null);
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [newAgreementType, setNewAgreementType] = useState('');
  const [rules, setRules] = useState(businessRules || { autoRejectDays: 7, introWindowDays: 60, tailMonths: 18 });
  const [rulesSaved, setRulesSaved] = useState(false);
  const [commissionSaved, setCommissionSaved] = useState(false);

  const [generalSettings, setGeneralSettings] = useState(() => ({
    portalName: 'EvE Partner Portal',
    portalTagline: '',
    primaryContactEmail: '',
    timezone: 'UTC',
    ...(generalSettingsProp || {}),
  }));
  const [generalSaved, setGeneralSaved] = useState(false);

  // Notification matrix state moved to WorkflowsView (Quick Notifications).

  const [secSettings, setSecSettings] = useState(() => ({
    sessionTimeout: '30min',
    passwordMinLength: 8,
    require2FA: false,
    failedLoginLockout: 5,
    ...(securitySettingsProp || {}),
  }));
  const [secSaved, setSecSaved] = useState(false);

  const [localDefaults, setLocalDefaults] = useState(() => {
    const merged = { ...DEFAULT_COMMISSION_DEFAULTS, ...commissionDefaults };
    agreementTypes.forEach(t => { if (!merged[t]) merged[t] = { percentage: 10, description: '' }; });
    return merged;
  });

  React.useEffect(() => {
    setLocalDefaults(prev => {
      const d = { ...prev };
      agreementTypes.forEach(t => { if (!d[t]) d[t] = { percentage: 10, description: '' }; });
      return d;
    });
  }, [agreementTypes.length]);

  const handleAddSector = () => {
    if (!newSector.trim()) return;
    onAddSector(newSector.trim(), newSectorIcon);
    setNewSector('');
    setNewSectorIcon(null);
    setShowIconPicker(false);
  };

  const handleAddAgreementType = () => {
    if (!newAgreementType.trim()) return;
    onAddAgreementType(newAgreementType.trim());
    setNewAgreementType('');
  };

  const handleSaveRules = () => {
    if (rules.autoRejectDays < 1 || rules.introWindowDays < 1 || rules.tailMonths < 1) {
      window.alert('All values must be at least 1');
      return;
    }
    onSaveBusinessRules(rules);
    setRulesSaved(true);
    setTimeout(() => setRulesSaved(false), 2500);
  };

  const handleSaveCommissions = () => {
    onSaveCommissionDefaults(localDefaults);
    setCommissionSaved(true);
    setTimeout(() => setCommissionSaved(false), 2500);
  };

  const handleSaveGeneral = () => {
    if (onSaveSettings) onSaveSettings(generalSettings);
    setGeneralSaved(true);
    setTimeout(() => setGeneralSaved(false), 2500);
  };

  const handleSaveSecurity = () => {
    if (onSaveSecuritySettings) onSaveSecuritySettings(secSettings);
    setSecSaved(true);
    setTimeout(() => setSecSaved(false), 2500);
  };

  const TABS = [
    { id: 'general',       label: 'General',         icon: Home       },
    { id: 'sectors',       label: 'Sectors',         icon: Tag        },
    { id: 'agreements',    label: 'Agreement Types', icon: FileText   },
    { id: 'commissions',   label: 'Commissions',     icon: DollarSign },
    { id: 'rules',         label: 'Business Rules',  icon: Settings   },
    { id: 'security',      label: 'Security',        icon: Lock       },
    { id: 'permissions',   label: 'Permissions',     icon: Shield        },
    { id: 'fields',        label: 'Field Manager',   icon: ClipboardList },
    { id: 'formconfig',    label: 'Form Config',     icon: Layers        },
    { id: 'data',          label: 'Data',            icon: Database      },
  ];

  const PERMISSION_ROWS = [
    { action: 'View Partners',     'Super Admin': true,  Admin: true,  Editor: true,  Partner: false },
    { action: 'Edit Partners',     'Super Admin': true,  Admin: true,  Editor: true,  Partner: false },
    { action: 'Delete Partners',   'Super Admin': true,  Admin: true,  Editor: false, Partner: false },
    { action: 'View Prospects',    'Super Admin': true,  Admin: true,  Editor: true,  Partner: true  },
    { action: 'Approve Prospects', 'Super Admin': true,  Admin: true,  Editor: true,  Partner: false },
    { action: 'View Revenue',      'Super Admin': true,  Admin: true,  Editor: true,  Partner: false },
    { action: 'Manage Workflows',  'Super Admin': true,  Admin: true,  Editor: false, Partner: false },
    { action: 'Access Settings',   'Super Admin': true,  Admin: false, Editor: false, Partner: false },
  ];

  return (
    <div className="flex min-h-[640px] rounded-xl overflow-hidden shadow-lg border" style={{ borderColor: BRAND_COLORS.navy + '20' }}>

      {/* ── Left Sidebar Navigation ───────────────────────────────────────── */}
      <div className="flex flex-col w-52 flex-shrink-0 py-5" style={{ backgroundColor: BRAND_COLORS.navy }}>
        <div className="px-4 mb-5">
          <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: BRAND_COLORS.gold + 'aa' }}>Settings</p>
        </div>
        {TABS.map(({ id, label, icon: Icon }) => {
          const isActive = activeSettingsTab === id;
          return (
            <button
              key={id}
              onClick={() => setActiveSettingsTab(id)}
              className="relative flex items-center gap-3 mx-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all text-left"
              style={{
                color: isActive ? '#ffffff' : 'rgba(255,255,255,0.5)',
                backgroundColor: isActive ? 'rgba(255,255,255,0.12)' : 'transparent',
              }}
            >
              {isActive && (
                <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full" style={{ backgroundColor: BRAND_COLORS.gold }} />
              )}
              <Icon className="w-4 h-4 flex-shrink-0" style={{ color: isActive ? BRAND_COLORS.gold : 'rgba(255,255,255,0.5)' }} />
              {label}
            </button>
          );
        })}
      </div>

      {/* ── Content Area ──────────────────────────────────────────────────── */}
      <div className="flex-1 bg-white overflow-auto p-8">

        {/* ── TAB: General ────────────────────────────────────────────────── */}
        {activeSettingsTab === 'general' && (
          <div>
            <div className="mb-6">
              <h3 className="text-xl font-bold" style={{ color: BRAND_COLORS.navy }}>General</h3>
              <p className="text-sm text-gray-500 mt-0.5">Portal identity and contact information</p>
            </div>
            <div className="rounded-xl border p-6 space-y-5 max-w-lg" style={{ borderColor: BRAND_COLORS.navy + '18' }}>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Portal Name</label>
                <input
                  type="text"
                  value={generalSettings.portalName}
                  onChange={e => setGeneralSettings(s => ({ ...s, portalName: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
                  placeholder="EvE Partner Portal"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Portal Tagline</label>
                <input
                  type="text"
                  value={generalSettings.portalTagline}
                  onChange={e => setGeneralSettings(s => ({ ...s, portalTagline: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
                  placeholder="e.g. Empowering partnership growth"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Primary Contact Email</label>
                <input
                  type="email"
                  value={generalSettings.primaryContactEmail}
                  onChange={e => setGeneralSettings(s => ({ ...s, primaryContactEmail: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
                  placeholder="admin@example.com"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Timezone</label>
                <select
                  value={generalSettings.timezone}
                  onChange={e => setGeneralSettings(s => ({ ...s, timezone: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 bg-white"
                >
                  <option value="UTC">UTC</option>
                  <option value="America/New_York">Eastern Time (ET)</option>
                  <option value="America/Chicago">Central Time (CT)</option>
                  <option value="America/Denver">Mountain Time (MT)</option>
                  <option value="America/Los_Angeles">Pacific Time (PT)</option>
                  <option value="Europe/London">London (GMT)</option>
                  <option value="Europe/Paris">Central European Time (CET)</option>
                  <option value="Asia/Dubai">Dubai (GST)</option>
                  <option value="Asia/Singapore">Singapore (SGT)</option>
                  <option value="Australia/Sydney">Sydney (AEDT)</option>
                </select>
              </div>
              <div className="pt-2">
                <button
                  onClick={handleSaveGeneral}
                  className="flex items-center gap-2 px-6 py-2.5 text-white rounded-lg font-medium text-sm transition-all"
                  style={{ backgroundColor: generalSaved ? '#16a34a' : BRAND_COLORS.gold }}
                >
                  {generalSaved ? <><CheckCircle className="w-4 h-4" /> Saved!</> : <><Save className="w-4 h-4" /> Save Settings</>}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── TAB: Sectors ────────────────────────────────────────────────── */}
        {activeSettingsTab === 'sectors' && (
          <div>
            <div className="mb-6">
              <h3 className="text-xl font-bold" style={{ color: BRAND_COLORS.navy }}>Sectors</h3>
              <p className="text-sm text-gray-500 mt-0.5">{sectors.length} sectors · Used when tagging partners and teasers</p>
            </div>
            <div className="rounded-xl border p-6" style={{ borderColor: BRAND_COLORS.navy + '18' }}>
              <div className="flex gap-2 mb-2">
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowIconPicker(!showIconPicker)}
                    className="flex items-center justify-center w-10 h-10 border rounded-lg hover:bg-gray-50 transition-colors"
                    style={{ borderColor: newSectorIcon ? BRAND_COLORS.navy : '#d1d5db' }}
                    title="Choose icon"
                  >
                    {newSectorIcon && ICON_MAP[newSectorIcon]
                      ? React.createElement(ICON_MAP[newSectorIcon], { className: 'w-4 h-4', style: { color: BRAND_COLORS.navy } })
                      : <Tag className="w-4 h-4 text-gray-400" />}
                  </button>
                </div>
                <input
                  type="text"
                  value={newSector}
                  onChange={e => setNewSector(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAddSector()}
                  placeholder="New sector name..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
                />
                <button
                  onClick={handleAddSector}
                  disabled={!newSector.trim()}
                  className="flex items-center gap-2 px-4 py-2 text-white rounded-lg disabled:opacity-40 text-sm font-medium"
                  style={{ backgroundColor: BRAND_COLORS.gold }}
                >
                  <Plus className="w-4 h-4" /> Add
                </button>
              </div>
              {showIconPicker && (
                <div className="mb-5 p-3 border border-gray-200 rounded-lg bg-white">
                  <p className="text-xs text-gray-500 mb-2">Pick an icon for the new sector:</p>
                  <div className="grid grid-cols-10 gap-1">
                    {ICON_OPTIONS.map(({ key, label, component: Ic }) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => { setNewSectorIcon(key); setShowIconPicker(false); }}
                        className={`flex flex-col items-center justify-center p-2 rounded-lg hover:bg-blue-50 transition-colors ${newSectorIcon === key ? 'ring-2' : ''}`}
                        style={newSectorIcon === key ? { ringColor: BRAND_COLORS.navy, backgroundColor: BRAND_COLORS.cream } : {}}
                        title={label}
                      >
                        <Ic className="w-5 h-5" style={{ color: newSectorIcon === key ? BRAND_COLORS.navy : '#6b7280' }} />
                        <span className="text-[10px] text-gray-500 mt-0.5 truncate w-full text-center">{label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {!showIconPicker && <div className="mb-3" />}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {sectors.map(sector => {
                  const SIcon = getSectorIcon(sector);
                  return (
                    <div key={sector}
                      className="flex items-center justify-between px-3 py-2 rounded-lg border group"
                      style={{ borderColor: BRAND_COLORS.navy + '30', backgroundColor: BRAND_COLORS.cream }}
                    >
                      <div className="flex items-center gap-2">
                        {SIcon && <SIcon className="w-4 h-4" style={{ color: BRAND_COLORS.navy }} />}
                        <span className="text-sm font-medium" style={{ color: BRAND_COLORS.navy }}>{sector}</span>
                      </div>
                      <button onClick={() => onRemoveSector(sector)}
                        className="opacity-0 group-hover:opacity-100 p-1 rounded text-red-500 hover:bg-red-50 transition-all">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
                {sectors.length === 0 && (
                  <p className="col-span-3 text-sm text-gray-500 text-center py-6">No sectors yet. Add your first sector above.</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── TAB: Agreement Types ────────────────────────────────────────── */}
        {activeSettingsTab === 'agreements' && (
          <div>
            <div className="mb-6">
              <h3 className="text-xl font-bold" style={{ color: BRAND_COLORS.navy }}>Agreement Types</h3>
              <p className="text-sm text-gray-500 mt-0.5">{agreementTypes.length} types defined · Shown in partner forms and Revenue Deals</p>
            </div>
            <div className="rounded-xl border p-6" style={{ borderColor: BRAND_COLORS.navy + '18' }}>
              <div className="flex gap-2 mb-5">
                <input
                  type="text"
                  value={newAgreementType}
                  onChange={e => setNewAgreementType(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAddAgreementType()}
                  placeholder="e.g. MCA - Revenue Pool"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
                />
                <button
                  onClick={handleAddAgreementType}
                  disabled={!newAgreementType.trim() || agreementTypes.includes(newAgreementType.trim())}
                  className="flex items-center gap-2 px-4 py-2 text-white rounded-lg disabled:opacity-40 text-sm font-medium"
                  style={{ backgroundColor: BRAND_COLORS.gold }}
                >
                  <Plus className="w-4 h-4" /> Add
                </button>
              </div>
              <div className="space-y-2">
                {agreementTypes.map(type => (
                  <div key={type}
                    className="flex items-center justify-between px-4 py-3 rounded-lg border group"
                    style={{ borderColor: BRAND_COLORS.navy + '25', backgroundColor: BRAND_COLORS.cream }}
                  >
                    <div className="flex items-center gap-3">
                      <FileText className="w-4 h-4 flex-shrink-0" style={{ color: BRAND_COLORS.gold }} />
                      <span className="text-sm font-medium" style={{ color: BRAND_COLORS.navy }}>{type}</span>
                    </div>
                    <button
                      onClick={() => onRemoveAgreementType(type)}
                      className="opacity-0 group-hover:opacity-100 p-1.5 rounded text-red-500 hover:bg-red-50 transition-all"
                      title="Remove type"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {agreementTypes.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-6">No agreement types defined.</p>
                )}
              </div>
              <div className="mt-4 p-3 rounded-lg text-xs text-blue-800 flex items-start gap-2" style={{ backgroundColor: '#eff6ff' }}>
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                Removing a type won't affect existing partners already assigned to it. New partners won't be able to select a removed type.
              </div>
            </div>
          </div>
        )}

        {/* ── TAB: Commissions ────────────────────────────────────────────── */}
        {activeSettingsTab === 'commissions' && (
          <div>
            <div className="mb-6">
              <h3 className="text-xl font-bold" style={{ color: BRAND_COLORS.navy }}>Commission Defaults</h3>
              <p className="text-sm text-gray-500 mt-0.5">Default commission % applied when a Revenue Deal is linked to a prospect.</p>
            </div>
            <div className="rounded-xl border p-6" style={{ borderColor: BRAND_COLORS.navy + '18' }}>
              <div className="space-y-3 max-w-2xl">
                {agreementTypes.map(type => {
                  const entry = localDefaults[type] || { percentage: 10, description: '' };
                  return (
                    <div key={type}
                      className="p-4 rounded-xl border-2 transition-all"
                      style={{ borderColor: BRAND_COLORS.navy + '20', backgroundColor: '#fafafa' }}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <FileText className="w-4 h-4 flex-shrink-0" style={{ color: BRAND_COLORS.gold }} />
                        <span className="font-semibold text-sm" style={{ color: BRAND_COLORS.navy }}>{type}</span>
                      </div>
                      <div className="flex gap-3 items-start">
                        <div className="flex-shrink-0">
                          <label className="block text-xs text-gray-500 mb-1">Default %</label>
                          <div className="flex items-center gap-1.5">
                            <input
                              type="number"
                              min="0" max="100" step="0.5"
                              value={entry.percentage}
                              onChange={e => setLocalDefaults(d => ({
                                ...d,
                                [type]: { ...d[type], percentage: parseFloat(e.target.value) || 0 }
                              }))}
                              className="w-20 px-3 py-2 border-2 rounded-lg text-center text-sm font-semibold focus:outline-none"
                              style={{ borderColor: BRAND_COLORS.navy + '40', color: BRAND_COLORS.navy }}
                            />
                            <span className="text-sm text-gray-500 font-medium">%</span>
                          </div>
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs text-gray-500 mb-1">Description (shown in deal)</label>
                          <input
                            type="text"
                            value={entry.description}
                            onChange={e => setLocalDefaults(d => ({
                              ...d,
                              [type]: { ...d[type], description: e.target.value }
                            }))}
                            placeholder="e.g. Referral commission"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
                {agreementTypes.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-8">No agreement types defined. Add types in the Agreement Types tab first.</p>
                )}
              </div>
              {agreementTypes.length > 0 && (
                <div className="flex items-center gap-3 mt-6">
                  <button
                    onClick={handleSaveCommissions}
                    className="flex items-center gap-2 px-6 py-2.5 text-white rounded-lg font-medium text-sm transition-all"
                    style={{ backgroundColor: commissionSaved ? '#16a34a' : BRAND_COLORS.gold }}
                  >
                    {commissionSaved
                      ? <><CheckCircle className="w-4 h-4" /> Saved!</>
                      : <><Save className="w-4 h-4" /> Save Defaults</>}
                  </button>
                  <button
                    onClick={() => {
                      const reset = { ...DEFAULT_COMMISSION_DEFAULTS, ...commissionDefaults };
                      agreementTypes.forEach(t => { if (!reset[t]) reset[t] = { percentage: 10, description: '' }; });
                      setLocalDefaults(reset);
                    }}
                    className="px-4 py-2.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Reset
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── TAB: Business Rules ─────────────────────────────────────────── */}
        {activeSettingsTab === 'rules' && (
          <div>
            <div className="mb-6">
              <h3 className="text-xl font-bold" style={{ color: BRAND_COLORS.navy }}>Business Rules</h3>
              <p className="text-sm text-gray-500 mt-0.5">Core timing rules that govern prospect lifecycle. Changes apply to new prospects only.</p>
            </div>
            <div className="rounded-xl border p-6" style={{ borderColor: BRAND_COLORS.navy + '18' }}>
              <div className="space-y-4 max-w-lg">
                <div className="p-4 rounded-xl border-2" style={{ borderColor: BRAND_COLORS.navy + '20', backgroundColor: '#fafafa' }}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Clock className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} />
                        <span className="font-semibold text-sm" style={{ color: BRAND_COLORS.navy }}>Auto-Reject Window</span>
                      </div>
                      <p className="text-xs text-gray-500">Pending prospects are automatically rejected if not reviewed within this many days.</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <input
                        type="number" min="1" max="365"
                        value={rules.autoRejectDays}
                        onChange={e => setRules(r => ({ ...r, autoRejectDays: parseInt(e.target.value) || 1 }))}
                        className="w-20 px-3 py-2 border-2 rounded-lg text-center text-sm font-semibold focus:outline-none"
                        style={{ borderColor: BRAND_COLORS.navy + '40', color: BRAND_COLORS.navy }}
                      />
                      <span className="text-sm text-gray-500">days</span>
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-xl border-2" style={{ borderColor: BRAND_COLORS.navy + '20', backgroundColor: '#fafafa' }}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Users className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} />
                        <span className="font-semibold text-sm" style={{ color: BRAND_COLORS.navy }}>Introduction Window</span>
                      </div>
                      <p className="text-xs text-gray-500">Days after approval during which an introduction must occur to remain eligible.</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <input
                        type="number" min="1" max="365"
                        value={rules.introWindowDays}
                        onChange={e => setRules(r => ({ ...r, introWindowDays: parseInt(e.target.value) || 1 }))}
                        className="w-20 px-3 py-2 border-2 rounded-lg text-center text-sm font-semibold focus:outline-none"
                        style={{ borderColor: BRAND_COLORS.navy + '40', color: BRAND_COLORS.navy }}
                      />
                      <span className="text-sm text-gray-500">days</span>
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-xl border-2" style={{ borderColor: BRAND_COLORS.navy + '20', backgroundColor: '#fafafa' }}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <DollarSign className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} />
                        <span className="font-semibold text-sm" style={{ color: BRAND_COLORS.navy }}>Tail Period</span>
                      </div>
                      <p className="text-xs text-gray-500">Months after the agreement end date during which the partner remains eligible for commission.</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <input
                        type="number" min="1" max="60"
                        value={rules.tailMonths}
                        onChange={e => setRules(r => ({ ...r, tailMonths: parseInt(e.target.value) || 1 }))}
                        className="w-20 px-3 py-2 border-2 rounded-lg text-center text-sm font-semibold focus:outline-none"
                        style={{ borderColor: BRAND_COLORS.navy + '40', color: BRAND_COLORS.navy }}
                      />
                      <span className="text-sm text-gray-500">months</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <button
                    onClick={handleSaveRules}
                    className="flex items-center gap-2 px-6 py-2.5 text-white rounded-lg font-medium text-sm transition-all"
                    style={{ backgroundColor: rulesSaved ? '#16a34a' : BRAND_COLORS.gold }}
                  >
                    {rulesSaved ? <><CheckCircle className="w-4 h-4" /> Saved!</> : <><Save className="w-4 h-4" /> Save Rules</>}
                  </button>
                  <button
                    onClick={() => setRules(businessRules)}
                    className="px-4 py-2.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Reset
                  </button>
                </div>

                <div className="p-3 rounded-lg text-xs text-amber-800 flex items-start gap-2" style={{ backgroundColor: '#fef3c7' }}>
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  Changes apply to newly created prospects only. Existing prospects retain their original deadlines.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Notification settings live on the Workflows tab as "Quick Notifications"
            (Admin / Super Admin only). Each checkbox there maps to a workflow row
            named `[NotifSettings] {role} · {event}`. There is intentionally no
            Notifications tab in this Settings panel — the Workflows tab is the
            single source of truth so the same data isn't editable from two places. */}

        {/* ── TAB: Security ───────────────────────────────────────────────── */}
        {activeSettingsTab === 'security' && (
          <div>
            <div className="mb-6">
              <h3 className="text-xl font-bold" style={{ color: BRAND_COLORS.navy }}>Security</h3>
              <p className="text-sm text-gray-500 mt-0.5">Authentication and session security settings.</p>
            </div>
            <div className="rounded-xl border p-6 space-y-5 max-w-lg" style={{ borderColor: BRAND_COLORS.navy + '18' }}>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Session Timeout</label>
                <select
                  value={secSettings.sessionTimeout}
                  onChange={e => setSecSettings(s => ({ ...s, sessionTimeout: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 bg-white"
                >
                  <option value="15min">15 minutes</option>
                  <option value="30min">30 minutes</option>
                  <option value="1hr">1 hour</option>
                  <option value="4hr">4 hours</option>
                  <option value="8hr">8 hours</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Password Minimum Length</label>
                <input
                  type="number" min="8" max="32"
                  value={secSettings.passwordMinLength}
                  onChange={e => setSecSettings(s => ({ ...s, passwordMinLength: Math.min(32, Math.max(8, parseInt(e.target.value) || 8)) }))}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
                />
                <p className="text-xs text-gray-400 mt-1">Between 8 and 32 characters</p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Require Two-Factor Authentication</label>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setSecSettings(s => ({ ...s, require2FA: !s.require2FA }))}
                    className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none"
                    style={{ backgroundColor: secSettings.require2FA ? BRAND_COLORS.gold : '#d1d5db' }}
                  >
                    <span
                      className="inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform"
                      style={{ transform: secSettings.require2FA ? 'translateX(22px)' : 'translateX(2px)' }}
                    />
                  </button>
                  <span className="text-sm text-gray-600">{secSettings.require2FA ? 'Enabled' : 'Disabled'}</span>
                  <span className="text-xs text-gray-400">(UI only)</span>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Failed Login Attempts Before Lockout</label>
                <input
                  type="number" min="3" max="10"
                  value={secSettings.failedLoginLockout}
                  onChange={e => setSecSettings(s => ({ ...s, failedLoginLockout: Math.min(10, Math.max(3, parseInt(e.target.value) || 3)) }))}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
                />
                <p className="text-xs text-gray-400 mt-1">Between 3 and 10 attempts</p>
              </div>
              <div className="pt-2">
                <button
                  onClick={handleSaveSecurity}
                  className="flex items-center gap-2 px-6 py-2.5 text-white rounded-lg font-medium text-sm transition-all"
                  style={{ backgroundColor: secSaved ? '#16a34a' : BRAND_COLORS.gold }}
                >
                  {secSaved ? <><CheckCircle className="w-4 h-4" /> Saved!</> : <><Save className="w-4 h-4" /> Save Security</>}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── TAB: Permissions ────────────────────────────────────────────── */}
        {activeSettingsTab === 'permissions' && (
          <div>
            <div className="mb-6">
              <h3 className="text-xl font-bold" style={{ color: BRAND_COLORS.navy }}>Permissions</h3>
              <p className="text-sm text-gray-500 mt-0.5">Role-based access control reference. Read-only — contact your system administrator to modify roles.</p>
            </div>
            <div className="rounded-xl border p-6" style={{ borderColor: BRAND_COLORS.navy + '18' }}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2" style={{ borderColor: BRAND_COLORS.navy + '15' }}>
                      <th className="text-left py-3 pr-6 font-semibold text-xs uppercase tracking-wide text-gray-500">Action</th>
                      {['Super Admin', 'Admin', 'Editor', 'Partner'].map(r => (
                        <th key={r} className="text-center py-3 px-8 font-semibold text-xs uppercase tracking-wide" style={{ color: BRAND_COLORS.navy }}>{r}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {PERMISSION_ROWS.map((row, i) => (
                      <tr key={row.action} className={i % 2 !== 0 ? 'bg-gray-50' : ''} style={{ borderBottom: '1px solid ' + BRAND_COLORS.navy + '10' }}>
                        <td className="py-3 pr-6 font-medium text-gray-700">{row.action}</td>
                        {['Super Admin', 'Admin', 'Editor', 'Partner'].map(role => (
                          <td key={role} className="py-3 px-8 text-center">
                            {row[role]
                              ? <span className="inline-flex items-center justify-center w-6 h-6 rounded-full" style={{ backgroundColor: BRAND_COLORS.gold + '25' }}>
                                  <Check className="w-3.5 h-3.5" style={{ color: BRAND_COLORS.gold }} />
                                </span>
                              : <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100">
                                  <X className="w-3.5 h-3.5 text-gray-400" />
                                </span>
                            }
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 p-3 rounded-lg text-xs text-blue-800 flex items-start gap-2" style={{ backgroundColor: '#eff6ff' }}>
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                This is a read-only reference. Role permissions are defined at the system level.
              </div>
            </div>
          </div>
        )}

        {/* ── TAB: Field Manager ──────────────────────────────────────────── */}
        {activeSettingsTab === 'fields' && (
          <FieldManagerTab showConfirm={showConfirm} />
        )}

        {/* ── TAB: Form Configurator ──────────────────────────────────────── */}
        {activeSettingsTab === 'formconfig' && (
          <FormConfiguratorTab />
        )}

        {/* ── TAB: Data ───────────────────────────────────────────────────── */}
        {activeSettingsTab === 'data' && (
          <div>
            <div className="mb-6">
              <h3 className="text-xl font-bold" style={{ color: BRAND_COLORS.navy }}>Data Management</h3>
              <p className="text-sm text-gray-500 mt-0.5">Manage system data and demo content.</p>
            </div>
            {(currentUserRole === 'Admin' || currentUserRole === 'Super Admin') && (
              <div className="rounded-xl border-2 border-red-200 bg-red-50 p-5 max-w-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-semibold text-red-800 text-sm mb-1">Reset to Demo Data</p>
                    <p className="text-xs text-red-600 mb-4">
                      This will clear <strong>all current data</strong> and reload the system with fresh demo partners, prospects, deals, teasers (including the 4 uploaded PDFs), and users. This action cannot be undone.
                    </p>
                    <button
                      onClick={() => {
                        showConfirm('Are you sure? All current data will be permanently replaced with demo data.', () => {

                        }, 'Reset');
                      }}
                      className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Reset to Demo Data
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};
// ============================================================================
// SectorSelector – search input, chip tags, scrollable checkbox list with icons
// ============================================================================
// WIZARD: Save & Close
// ============================================================================
// Sits in every multi-step wizard footer except the last step, where the primary
// action already saves and closes. On a NEW-entity wizard it parks the work as a
// draft (is_draft = true + the step to resume at); on an EDIT wizard it commits
// the edits so far to the already-live record.
//
// `disabledReason` is non-empty while the record has nothing to be identified by
// (no name yet) — the button greys out and the reason becomes its tooltip rather
// than failing on click with an alert.
const WizardSaveClose = ({ onClick, disabledReason = '', label = 'Save & Close' }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={!!disabledReason}
    title={disabledReason || 'Save what you have filled in so far and close — you can pick up where you left off'}
    className="px-4 py-2 rounded-lg border text-sm font-medium transition-colors hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent inline-flex items-center gap-1.5"
    style={{ borderColor: BRAND_COLORS.navy, color: BRAND_COLORS.navy }}
  >
    <Save className="w-4 h-4" />
    {label}
  </button>
);

// ============================================================================
// DRAFTS PANEL
// ============================================================================
// Backs the "Drafts" filter chip in every list view. Deliberately NOT the normal
// table: a draft has most of its fields still empty, and the usual row actions
// (Approve / Reject / Record Payment / Archive) are meaningless until the wizard
// has actually been finished. So a draft gets exactly two actions — carry on, or
// bin it.
const DraftsPanel = ({ drafts = [], labelOf, stepLabels = [], onResume, onDiscard, emptyText = 'No unfinished drafts.' }) => {
  if (!drafts.length) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center text-sm" style={{ color: BRAND_COLORS.textSecondary }}>
        {emptyText}
      </div>
    );
  }
  return (
    <div className="bg-white rounded-lg shadow divide-y" style={{ borderColor: BRAND_COLORS.border }}>
      {drafts.map(d => {
        const step  = d.draftStep || 1;
        const saved = d.updatedAt || d.createdAt;
        return (
          <div key={d.id} className="p-4 flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-semibold truncate" style={{ color: BRAND_COLORS.navy }}>
                  {labelOf?.(d) || 'Untitled'}
                </span>
                <StatusChip variant="neutral">Draft</StatusChip>
              </div>
              <div className="text-xs mt-0.5" style={{ color: BRAND_COLORS.textSecondary }}>
                Left off on step {step}{stepLabels[step - 1] ? ` — ${stepLabels[step - 1]}` : ''}
                {stepLabels.length ? ` of ${stepLabels.length}` : ''}
                {saved ? ` · saved ${new Date(saved).toLocaleDateString()}` : ''}
              </div>
            </div>
            <button
              type="button"
              onClick={() => onResume?.(d)}
              className="px-4 py-2 rounded-lg text-sm font-bold transition-all hover:opacity-90"
              style={{ background: BRAND_COLORS.gold, color: BRAND_COLORS.textOnGold }}
            >
              ▸ Continue
            </button>
            {onDiscard && (
              <button
                type="button"
                onClick={() => onDiscard(d)}
                title="Discard this draft"
                className="p-2 rounded-lg hover:bg-red-50 transition-colors"
                style={{ color: BRAND_COLORS.statusError }}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

const SectorSelector = ({ sectors, selectedSectors, onSectorsChange }) => {
  const [search, setSearch] = useState('');

  const toggle = (s) =>
    onSectorsChange(selectedSectors.includes(s) ? selectedSectors.filter(x => x !== s) : [...selectedSectors, s]);

  const filtered = search.trim()
    ? sectors.filter(s => s.toLowerCase().includes(search.toLowerCase()))
    : sectors;

  return (
    <div className="space-y-2">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search sectors..."
          className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1"
          style={{ focusRingColor: BRAND_COLORS.navy }}
        />
        {search && (
          <button type="button" onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Selected chips */}
      {selectedSectors.length > 0 && (
        <div className="flex flex-wrap gap-1.5 p-2 rounded-lg" style={{ backgroundColor: BRAND_COLORS.cream }}>
          {selectedSectors.map(s => (
            <span key={s} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full text-white" style={{ backgroundColor: BRAND_COLORS.navy }}>
              {s}
              <button type="button" onClick={() => toggle(s)} className="hover:opacity-70">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          <button type="button" onClick={() => onSectorsChange([])} className="text-xs text-gray-500 hover:text-red-500 ml-1 underline">
            Clear all
          </button>
        </div>
      )}

      {/* Scrollable checkbox list */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" style={{ maxHeight: 200, overflowY: 'auto' }}>
        {filtered.length === 0 ? (
          <p className="text-xs text-gray-500 p-3 text-center">No sectors match "{search}"</p>
        ) : (
          filtered.map((s, i) => {
            const Icon = getSectorIcon(s);
            return (
              <label
                key={s}
                className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-blue-50 transition-colors ${i > 0 ? 'border-t border-gray-100' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={selectedSectors.includes(s)}
                  onChange={() => toggle(s)}
                  className="w-4 h-4 rounded flex-shrink-0"
                  style={{ accentColor: BRAND_COLORS.navy }}
                />
                {Icon && <Icon className="w-4 h-4 flex-shrink-0" style={{ color: selectedSectors.includes(s) ? BRAND_COLORS.navy : '#9ca3af' }} />}
                <span className={`text-sm ${selectedSectors.includes(s) ? 'font-medium' : 'text-gray-700'}`} style={selectedSectors.includes(s) ? { color: BRAND_COLORS.navy } : {}}>
                  {s}
                </span>
              </label>
            );
          })
        )}
      </div>
    </div>
  );
};

// ============================================================================
// GeographySelector – collapsible continents, 2-col country grid, flag chips
const GeographySelector = ({ selectedGeo, onGeoChange }) => {
  const [expandedContinents, setExpandedContinents] = useState({});
  const [geoSearch, setGeoSearch] = useState('');

  const continentEmoji = {
    'North America': '🌎', 'South America': '🌎',
    'Europe': '🌍', 'Middle East': '🌍', 'Africa': '🌍', 'Central Asia': '🌍',
    'Asia Pacific': '🌏',
  };

  const toggleCountry = (continent, country) => {
    const wasSelected = selectedGeo.countries.includes(country);
    const newCountries = wasSelected
      ? selectedGeo.countries.filter(c => c !== country)
      : [...selectedGeo.countries, country];
    const continentCountries = WORLD_GEOGRAPHY[continent];
    const anySelected = continentCountries.some(c => newCountries.includes(c));
    const newContinents = anySelected
      ? [...new Set([...selectedGeo.continents, continent])]
      : selectedGeo.continents.filter(c => c !== continent);
    onGeoChange({ continents: newContinents, countries: newCountries });
  };

  const toggleContinent = (continent) => {
    const countries = WORLD_GEOGRAPHY[continent];
    const allSelected = countries.every(c => selectedGeo.countries.includes(c));
    if (allSelected) {
      onGeoChange({
        countries: selectedGeo.countries.filter(c => !countries.includes(c)),
        continents: selectedGeo.continents.filter(c => c !== continent),
      });
    } else {
      onGeoChange({
        countries: [...new Set([...selectedGeo.countries, ...countries])],
        continents: [...new Set([...selectedGeo.continents, continent])],
      });
    }
  };

  const continentCount = (continent) =>
    WORLD_GEOGRAPHY[continent].filter(c => selectedGeo.countries.includes(c)).length;

  const allCountries = Object.entries(WORLD_GEOGRAPHY).flatMap(([continent, countries]) =>
    countries.map(country => ({ continent, country }))
  );
  const geoQuery = geoSearch.toLowerCase().trim();
  const searchMode = geoQuery.length > 0;
  const filteredCountries = searchMode
    ? allCountries.filter(({ country, continent }) =>
        country.toLowerCase().includes(geoQuery) || continent.toLowerCase().includes(geoQuery)
      )
    : [];

  return (
    <div className="space-y-2">
      {/* Selected country chips */}
      {selectedGeo.countries.length > 0 && (
        <div className="flex flex-wrap gap-1.5 p-2 rounded-lg" style={{ backgroundColor: BRAND_COLORS.cream }}>
          {selectedGeo.countries.map(country => {
            const continent = Object.keys(WORLD_GEOGRAPHY).find(c => WORLD_GEOGRAPHY[c].includes(country));
            const iso = COUNTRY_FLAGS[country];
            return (
              <span key={country} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full text-white" style={{ backgroundColor: BRAND_COLORS.navy }}>
                {iso && <img src={`https://flagcdn.com/16x12/${iso.toLowerCase()}.png`} alt={iso} className="inline-block flex-shrink-0 rounded-sm" style={{ width: 16, height: 12 }} />}
                {country}
                <button type="button" onClick={() => toggleCountry(continent, country)} className="hover:opacity-70">
                  <X className="w-3 h-3" />
                </button>
              </span>
            );
          })}
          <button type="button" onClick={() => onGeoChange({ continents: [], countries: [] })} className="text-xs text-gray-500 hover:text-red-500 ml-1 underline">
            Clear all
          </button>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={geoSearch}
          onChange={e => setGeoSearch(e.target.value)}
          placeholder="Search countries or regions..."
          className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1"
        />
        {geoSearch && (
          <button type="button" onClick={() => setGeoSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Continent groups / search results */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" style={{ maxHeight: 320, overflowY: 'auto' }}>
        {searchMode ? (
          filteredCountries.length === 0 ? (
            <p className="text-xs text-gray-500 p-3 text-center">No results for "{geoSearch}"</p>
          ) : (
            filteredCountries.map(({ continent, country }, i) => {
              const iso = COUNTRY_FLAGS[country];
              return (
                <label
                  key={country}
                  className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-blue-50 transition-colors ${i > 0 ? 'border-t border-gray-100' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={selectedGeo.countries.includes(country)}
                    onChange={() => toggleCountry(continent, country)}
                    className="w-4 h-4 rounded flex-shrink-0"
                    style={{ accentColor: BRAND_COLORS.gold }}
                  />
                  {iso && <img src={`https://flagcdn.com/16x12/${iso.toLowerCase()}.png`} alt={iso} className="flex-shrink-0 rounded-sm" style={{ width: 16, height: 12 }} />}
                  <div className="flex-1 min-w-0">
                    <span className={`text-sm ${selectedGeo.countries.includes(country) ? 'font-medium' : 'text-gray-700'}`} style={selectedGeo.countries.includes(country) ? { color: BRAND_COLORS.navy } : {}}>
                      {country}
                    </span>
                    <span className="text-xs text-gray-400 ml-2">{continentEmoji[continent] || '🌐'} {continent}</span>
                  </div>
                </label>
              );
            })
          )
        ) : (
          Object.keys(WORLD_GEOGRAPHY).map((continent, ci) => {
            const count = continentCount(continent);
            const total = WORLD_GEOGRAPHY[continent].length;
            const allSel = count === total;
            const isOpen = !!expandedContinents[continent];
            return (
              <div key={continent} className={ci > 0 ? 'border-t border-gray-200' : ''}>
                <div className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 sticky top-0 z-10">
                  <button
                    type="button"
                    onClick={() => setExpandedContinents(prev => ({ ...prev, [continent]: !prev[continent] }))}
                    className="flex items-center gap-2 flex-1 text-left"
                  >
                    <span className="text-base">{continentEmoji[continent] || '🌐'}</span>
                    <span className="text-sm font-semibold text-gray-700">{continent}</span>
                    {count > 0 && (
                      <span className="text-xs px-1.5 py-0.5 rounded-full text-white" style={{ backgroundColor: BRAND_COLORS.gold }}>
                        {count}/{total}
                      </span>
                    )}
                    <span className="text-gray-400 text-xs ml-auto mr-2">{isOpen ? '▲' : '▼'}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleContinent(continent)}
                    className="text-xs px-2 py-0.5 rounded border font-medium transition-colors flex-shrink-0"
                    style={allSel
                      ? { backgroundColor: BRAND_COLORS.navy, color: 'white', borderColor: BRAND_COLORS.navy }
                      : { backgroundColor: 'white', color: BRAND_COLORS.navy, borderColor: BRAND_COLORS.navy + '60' }
                    }
                  >
                    {allSel ? 'Deselect' : 'Select All'}
                  </button>
                </div>
                {isOpen && (
                  <div className="grid grid-cols-2 gap-0 bg-white border-t border-gray-100">
                    {WORLD_GEOGRAPHY[continent].map((country, i) => {
                      const iso = COUNTRY_FLAGS[country];
                      return (
                        <label
                          key={country}
                          className={`flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-blue-50 transition-colors text-xs ${
                            i % 2 === 0 ? 'border-r border-gray-100' : ''
                          } ${i > 1 ? 'border-t border-gray-100' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedGeo.countries.includes(country)}
                            onChange={() => toggleCountry(continent, country)}
                            className="w-3.5 h-3.5 rounded flex-shrink-0"
                            style={{ accentColor: BRAND_COLORS.gold }}
                          />
                          {iso && <img src={`https://flagcdn.com/16x12/${iso.toLowerCase()}.png`} alt={iso} className="flex-shrink-0 rounded-sm" style={{ width: 16, height: 12 }} />}
                          <span className={selectedGeo.countries.includes(country) ? 'font-medium' : 'text-gray-600'} style={selectedGeo.countries.includes(country) ? { color: BRAND_COLORS.navy } : {}}>
                            {country}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

// ============================================================================
// MODAL COMPONENTS
// ============================================================================

// `draft` — a partner row parked mid-wizard via Save & Close. When present the
// wizard seeds itself from that row and opens on the step it was left on.
const NewPartnerModal = ({ onClose, onSubmit, onSaveDraft, draft = null, sectors, agreementTypes = DEFAULT_AGREEMENT_TYPES, currentUserRole = 'Admin' }) => {
  const [wizardStep, setWizardStep] = useState(draft?.draftStep || 1);
  const [formData, setFormData] = useState({
    name: draft?.name || '',
    contactPerson: draft?.contactPerson || '',
    email: draft?.email || '',
    phone: draft?.phone || '',
    location: draft?.location || '',
    agreementType: draft?.agreementType || 'Basic MCA Referral Fee',
    startDate: draft?.startDate ? draft.startDate.slice(0, 10) : '',
    endDate: draft?.endDate ? draft.endDate.slice(0, 10) : '',
    tailMonths: draft?.tailMonths ?? 18,
    ndaSigned: draft?.ndaSigned || false,
    ndaType: draft?.ndaType || null,
    ndaSignedDate: draft?.ndaSignedDate ? draft.ndaSignedDate.slice(0, 10) : '',
  });

  const [selectedSectors, setSelectedSectors] = useState(draft?.sectors || []);
  const [selectedGeo, setSelectedGeo] = useState(draft?.geography?.continents ? draft.geography : { continents: [], countries: [] });
  const [commTerms, setCommTerms] = useState(draft?.commissionTerms || DEFAULT_COMMISSION_TERMS[formData.agreementType] || DEFAULT_COMMISSION_TERMS['One-time Referral Fee']);
  const [showCommissionSection, setShowCommissionSection] = useState(false);
  const [formError, setFormError] = useState('');

  // ── Form config ────────────────────────────────────────────────────────────
  const [fieldCfg, setFieldCfg] = useState({});
  const HARDCODED_REQUIRED = { name: true, contact_person: true, email: true, start_date: true, end_date: true };

  useEffect(() => {
    (async () => {
      try {
        const { supabase } = await import('./supabaseClient');
        const { data } = await supabase
          .from('form_field_config')
          .select('is_visible, is_required, role_permissions, custom_field_definitions!inner(field_name, entity_type)')
          .eq('form_type', 'add')
          .eq('custom_field_definitions.entity_type', 'partner');
        if (data?.length) {
          const map = {};
          data.forEach(row => {
            map[row.custom_field_definitions.field_name] = {
              is_visible:       row.is_visible,
              is_required:      row.is_required,
              role_permissions: row.role_permissions || {},
            };
          });
          setFieldCfg(map);
        }
      } catch (_) {}
    })();
  }, []);

  const isVisible  = (fn) => {
    if (fieldCfg[fn]?.is_visible === false) return false;
    if (fieldCfg[fn]?.role_permissions?.[currentUserRole] === false) return false;
    return true;
  };
  const isRequired = (fn) => fieldCfg[fn]?.is_required  ?? HARDCODED_REQUIRED[fn] ?? false;
  const fieldLabel = (fn, defaultLabel) => isRequired(fn) ? `${defaultLabel} *` : defaultLabel;

  // ── Validation ─────────────────────────────────────────────────────────────
  const validateStep2 = () => {
    const missing = [];
    if (isVisible('name')           && isRequired('name')           && !formData.name.trim())          missing.push('Partner Name');
    if (isVisible('contact_person') && isRequired('contact_person') && !formData.contactPerson.trim()) missing.push('Contact Person');
    if (isVisible('email')          && isRequired('email')          && !formData.email.trim())          missing.push('Email');
    if (isVisible('phone')          && isRequired('phone')          && !formData.phone.trim())          missing.push('Phone');
    if (missing.length) { setFormError(`Required: ${missing.join(', ')}`); return false; }
    setFormError('');
    return true;
  };

  const updateCommTerms = (field, value) => setCommTerms(t => ({ ...t, [field]: value }));
  const updateBracket = (idx, field, value) => setCommTerms(t => ({
    ...t,
    brackets: (t.brackets || []).map((b, i) => i === idx ? { ...b, [field]: parseFloat(value) || value } : b)
  }));

  // Shared by the final submit and by Save & Close so a draft and a completed
  // partner are built from exactly the same shape.
  const buildPayload = () => ({
    ...formData,
    ...(draft?.id ? { id: draft.id } : {}),
    sectors: selectedSectors,
    geography: selectedGeo,
    commissionTerms: commTerms,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const missing = [];
    if (isVisible('start_date') && isRequired('start_date') && !formData.startDate) missing.push('Start Date');
    if (isVisible('end_date')   && isRequired('end_date')   && !formData.endDate)   missing.push('End Date');
    if (missing.length) { setFormError(`Required: ${missing.join(', ')}`); return; }
    setFormError('');
    onSubmit(buildPayload());
  };

  // ── Save & Close ───────────────────────────────────────────────────────────
  // Only the name is enforced: it is what identifies the draft in the Drafts
  // list. Every other required field is still enforced on the final submit.
  const draftBlockedReason = formData.name.trim() ? '' : 'Enter the partner name first — it identifies the draft.';
  const handleSaveDraft = async () => {
    if (draftBlockedReason) return;
    setFormError('');
    const ok = await onSaveDraft?.(buildPayload(), wizardStep);
    if (ok) onClose();   // stay open if the write failed, so nothing is lost
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={onClose}>
    <div className="bg-white rounded-lg shadow-xl border-2 w-full flex flex-col" style={{ borderColor: BRAND_COLORS.gold, maxWidth: 560, maxHeight: '90vh' }} onClick={e => e.stopPropagation()}>
      {/* Wizard Header */}
      <div className="px-6 pt-4 pb-2 flex items-center justify-between border-b border-gray-100 flex-shrink-0">
        <h2 className="text-lg font-bold" style={{ color: BRAND_COLORS.navy }}>+ Add New Partner</h2>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">✕</button>
      </div>

      {/* Step Indicators */}
      <div className="px-6 pt-5 pb-4 border-b border-gray-100 flex-shrink-0">
        <div className="flex items-center justify-between">
          {[
            { n: 1, label: 'Agreement Type' },
            { n: 2, label: 'Partner Info' },
            { n: 3, label: 'Sector' },
            { n: 4, label: 'Geography' },
            { n: 5, label: 'Contract' },
          ].map(({ n, label }, idx, arr) => (
            <React.Fragment key={n}>
              <button onClick={() => setWizardStep(n)} className="flex flex-col items-center gap-1">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all"
                  style={{
                    backgroundColor: wizardStep === n ? BRAND_COLORS.navy : wizardStep > n ? BRAND_COLORS.gold : '#E5E7EB',
                    color: wizardStep >= n ? 'white' : '#9CA3AF'
                  }}>
                  {wizardStep > n ? '✓' : n}
                </div>
                <span className={`text-xs font-medium whitespace-nowrap ${wizardStep === n ? 'inline' : 'hidden'} sm:inline`} style={{ color: wizardStep === n ? BRAND_COLORS.navy : '#9CA3AF' }}>{label}</span>
              </button>
              {idx < arr.length - 1 && (
                <div className="flex-1 h-0.5 mx-2 mb-4 rounded" style={{ backgroundColor: wizardStep > n ? BRAND_COLORS.gold : '#E5E7EB' }} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="overflow-y-auto flex-1">
      {/* Step 1: Agreement Type */}
      {wizardStep === 1 && (<>
      <div className="p-6 border-b border-gray-200">
        <h3 className="text-lg font-semibold mb-1" style={{ color: BRAND_COLORS.navy }}>Step 1: Agreement Type</h3>
        <p className="text-sm text-gray-500 mb-6">Select the type of agreement with this partner</p>
        <div className="grid grid-cols-1 gap-3">
          {agreementTypes.map((type) => {
            const isSelected = formData.agreementType === type;
            const Icon = { 'One-time MCA Referral Fee': Target, 'Basic MCA Referral Fee': FileText, 'Table MCA Referral Fee': Activity, 'Revenue Pool MCA': PieChart }[type] || FileText;
            return (
              <button
                key={type}
                type="button"
                onClick={() => {
                  setFormData(f => ({ ...f, agreementType: type }));
                  setCommTerms(DEFAULT_COMMISSION_TERMS[type] || DEFAULT_COMMISSION_TERMS['One-time MCA Referral Fee']);
                }}
                className="py-4 px-5 rounded-xl border-2 transition-all text-left"
                style={{
                  borderColor: isSelected ? BRAND_COLORS.navy : '#e5e7eb',
                  backgroundColor: isSelected ? `${BRAND_COLORS.navy}0d` : 'white',
                  color: isSelected ? BRAND_COLORS.navy : '#374151'
                }}
              >
                <div className="flex items-center gap-2">
                  <Icon className="w-5 h-5 flex-shrink-0" style={{ color: isSelected ? BRAND_COLORS.navy : '#9CA3AF' }} />
                  <span className="font-bold text-sm">{type}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
      <div className="px-6 pb-5 pt-4 flex justify-between sticky bottom-0 bg-white border-t border-gray-100 z-10">
        <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">Cancel</button>
        <WizardSaveClose onClick={handleSaveDraft} disabledReason={draftBlockedReason} />
        <button onClick={() => setWizardStep(2)} className="px-6 py-2 rounded-lg font-medium text-white" style={{ backgroundColor: BRAND_COLORS.gold }}>Next →</button>
      </div>
      </>)}

      {/* Step 2: Partner Info */}
      {wizardStep === 2 && (<>
      <div className="p-6 border-b border-gray-200 space-y-4">
        <h3 className="text-lg font-semibold mb-1" style={{ color: BRAND_COLORS.navy }}>Step 2: Partner Info</h3>
        {isVisible('name') && (
          <Input
            label={fieldLabel('name', 'Partner Name')}
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
        )}
        {isVisible('contact_person') && (
          <Input
            label={fieldLabel('contact_person', 'Contact Person')}
            value={formData.contactPerson}
            onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
          />
        )}
        {isVisible('email') && (
          <Input
            label={fieldLabel('email', 'Email')}
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          />
        )}
        {isVisible('phone') && (
          <Input
            label={fieldLabel('phone', 'Phone')}
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          />
        )}
        {isVisible('location') && (
          <CountryAutocomplete
            label={fieldLabel('location', 'Partner Location')}
            value={formData.location}
            onChange={val => setFormData({ ...formData, location: val })}
          />
        )}
        {/* NDA / MNDA */}
        {isVisible('nda_signed') && (
          <div className="p-3 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">{fieldLabel('nda_signed', 'NDA / MNDA Signed')}</label>
              <input type="checkbox" checked={formData.ndaSigned}
                onChange={e => setFormData(f => ({ ...f, ndaSigned: e.target.checked, ndaType: e.target.checked ? (f.ndaType || 'NDA') : null, ndaSignedDate: e.target.checked ? f.ndaSignedDate : '' }))}
                className="w-4 h-4 accent-emerald-600" />
            </div>
            {formData.ndaSigned && (
              <div className="mt-3 space-y-3">
                <div className="flex gap-2">
                  {['NDA', 'MNDA'].map(t => (
                    <button key={t} type="button" onClick={() => setFormData(f => ({ ...f, ndaType: t }))}
                      className="flex-1 py-1.5 px-3 rounded-lg border-2 text-sm font-medium transition-all"
                      style={{ borderColor: formData.ndaType === t ? BRAND_COLORS.navy : '#e5e7eb', backgroundColor: formData.ndaType === t ? `${BRAND_COLORS.navy}0d` : 'white', color: formData.ndaType === t ? BRAND_COLORS.navy : '#6b7280' }}>
                      {t}
                    </button>
                  ))}
                </div>
                <Input label="Date Signed" type="date" value={formData.ndaSignedDate || ''}
                  onChange={e => setFormData(f => ({ ...f, ndaSignedDate: e.target.value }))} />
              </div>
            )}
          </div>
        )}
      </div>
      <div className="px-6 pb-5 pt-4 flex flex-col gap-2">
        {formError && <p className="text-xs text-red-500 text-right">{formError}</p>}
        <div className="flex justify-between">
          <button onClick={() => { setFormError(''); setWizardStep(1); }} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">← Back</button>
          <WizardSaveClose onClick={handleSaveDraft} disabledReason={draftBlockedReason} />
          <button onClick={() => { if (validateStep2()) setWizardStep(3); }} className="px-6 py-2 rounded-lg font-medium text-white" style={{ backgroundColor: BRAND_COLORS.gold }}>Next →</button>
        </div>
      </div>
      </>)}

      {/* Step 3: Sector */}
      {wizardStep === 3 && (<>
      <div className="p-6 border-b border-gray-200 space-y-4">
        <h3 className="text-lg font-semibold mb-1" style={{ color: BRAND_COLORS.navy }}>Step 3: Sector</h3>
        {isVisible('sectors') && (
          <SectorSelector
            sectors={sectors}
            selectedSectors={selectedSectors}
            onSectorsChange={setSelectedSectors}
                     />
        )}
      </div>
      <div className="px-6 pb-5 pt-4 flex justify-between sticky bottom-0 bg-white border-t border-gray-100 z-10">
        <button type="button" onClick={() => setWizardStep(2)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">← Back</button>
        <WizardSaveClose onClick={handleSaveDraft} disabledReason={draftBlockedReason} />
        <button onClick={() => setWizardStep(4)} className="px-6 py-2 rounded-lg font-medium text-white" style={{ backgroundColor: BRAND_COLORS.gold }}>Next →</button>
      </div>
      </>)}

      {/* Step 4: Geography */}
      {wizardStep === 4 && (<>
      <div className="p-6 border-b border-gray-200 space-y-4">
        <h3 className="text-lg font-semibold mb-1" style={{ color: BRAND_COLORS.navy }}>Step 4: Geography</h3>
        {isVisible('geography') && (
          <GeographySelector
            selectedGeo={selectedGeo}
            onGeoChange={setSelectedGeo}
          />
        )}
      </div>
      <div className="px-6 pb-5 pt-4 flex justify-between sticky bottom-0 bg-white border-t border-gray-100 z-10">
        <button type="button" onClick={() => setWizardStep(3)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">← Back</button>
        <WizardSaveClose onClick={handleSaveDraft} disabledReason={draftBlockedReason} />
        <button onClick={() => setWizardStep(5)} className="px-6 py-2 rounded-lg font-medium text-white" style={{ backgroundColor: BRAND_COLORS.gold }}>Next →</button>
      </div>
      </>)}

      {/* Step 5: Contract */}
      {wizardStep === 5 && (<>
      <form onSubmit={handleSubmit}>
      <div className="p-6 border-b border-gray-200 space-y-4">
        <h3 className="text-lg font-semibold mb-1" style={{ color: BRAND_COLORS.navy }}>Step 5: Contract</h3>
        {isVisible('start_date') && (
          <Input
            label={fieldLabel('start_date', 'Start Date')}
            type="date"
            value={formData.startDate}
            onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
          />
        )}
        {isVisible('end_date') && (
          <Input
            label={fieldLabel('end_date', 'End Date')}
            type="date"
            value={formData.endDate}
            onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
          />
        )}
        {isVisible('tail_months') && (
          <div className="p-3 rounded-lg text-sm" style={{ backgroundColor: BRAND_COLORS.cream, borderLeft: `3px solid ${BRAND_COLORS.gold}` }}>
            <p className="font-medium mb-2" style={{ color: BRAND_COLORS.navy }}>⏳ Tail End Period</p>
            <div className="flex items-center gap-3">
              <input
                type="number" min="1" max="60"
                value={formData.tailMonths}
                onChange={(e) => setFormData({ ...formData, tailMonths: parseInt(e.target.value) || 18 })}
                className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
              />
              <span className="text-xs text-gray-600">months after contract end</span>
            </div>
            {formData.endDate && (
              <p className="text-xs font-medium mt-2" style={{ color: BRAND_COLORS.gold }}>
                Tail End: {formatDate(addMonths(new Date(formData.endDate), formData.tailMonths || 18).toISOString())}
              </p>
            )}
          </div>
        )}
        {/* Commission Terms */}
        {isVisible('commission_terms') && <div className="border border-gray-200 rounded-lg overflow-hidden">
          <button type="button" onClick={() => setShowCommissionSection(!showCommissionSection)}
            className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-left transition-colors hover:bg-gray-50"
            style={{ color: BRAND_COLORS.navy }}>
            <span className="flex items-center gap-2">
              <DollarSign className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} />
              Commission Terms
              <span className="text-xs font-normal text-gray-400 ml-1">({formData.agreementType || 'No agreement type'})</span>
            </span>
            <span className="text-gray-400">{showCommissionSection ? '▲' : '▼'}</span>
          </button>
          {showCommissionSection && (
            <div className="px-4 pb-4 space-y-4 border-t border-gray-100">
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Terms (days)</label>
                <input type="number" value={commTerms.paymentTermsDays ?? 10}
                  onChange={e => updateCommTerms('paymentTermsDays', parseInt(e.target.value) || 10)}
                  className="w-32 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              {commTerms.type === 'fixed' && (
                <div className="max-w-xs">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Agreed Commission %</label>
                  <p className="text-xs text-gray-400 mb-2">Single rate negotiated for this specific referral</p>
                  <div className="relative w-32">
                    <input type="number" value={commTerms.percentage ?? 10}
                      onChange={e => updateCommTerms('percentage', parseFloat(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm pr-7" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">%</span>
                  </div>
                </div>
              )}
              {commTerms.type === 'basic' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Client %</label>
                    <div className="relative">
                      <input type="number" value={commTerms.clientPercentage ?? 10}
                        onChange={e => updateCommTerms('clientPercentage', parseFloat(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm pr-7" />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">%</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Investor %</label>
                    <div className="relative">
                      <input type="number" value={commTerms.investorPercentage ?? 20}
                        onChange={e => updateCommTerms('investorPercentage', parseFloat(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm pr-7" />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">%</span>
                    </div>
                  </div>
                </div>
              )}
              {commTerms.type === 'table' && (
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-2">Sliding Scale Brackets</p>
                  <div className="space-y-2">
                    {(commTerms.brackets || []).map((bracket, idx) => (
                      <div key={idx} className="grid grid-cols-3 gap-2 items-center p-2 bg-gray-50 rounded-lg">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">{idx < (commTerms.brackets.length - 1) ? 'Up to $' : 'Above'}</label>
                          {idx < (commTerms.brackets.length - 1) ? (
                            <input type="number" value={bracket.upTo ?? ''}
                              onChange={e => updateBracket(idx, 'upTo', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-xs" />
                          ) : (
                            <span className="text-xs text-gray-400 italic">No limit</span>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Non-active %</label>
                          <div className="relative">
                            <input type="number" value={bracket.nonActive ?? ''}
                              onChange={e => updateBracket(idx, 'nonActive', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-xs pr-5" />
                            <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">%</span>
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Active %</label>
                          <div className="relative">
                            <input type="number" value={bracket.active ?? ''}
                              onChange={e => updateBracket(idx, 'active', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-xs pr-5" />
                            <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">%</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {commTerms.type === 'pool' && (
                <div className="max-w-xs">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Pool Split %</label>
                  <p className="text-xs text-gray-400 mb-2">This partner's agreed share of the pool on any Revenue Pool MCA deal</p>
                  <div className="relative w-32">
                    <input type="number" min="0" max="100" step="0.1"
                      value={commTerms.poolSplitPercentage ?? 50}
                      onChange={e => updateCommTerms('poolSplitPercentage', parseFloat(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm pr-7" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">%</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>}
      </div>
      <div className="px-6 pb-5 pt-4 flex flex-col gap-2">
        {formError && <p className="text-xs text-red-500 text-right">{formError}</p>}
        <div className="flex justify-between">
          <button type="button" onClick={() => { setFormError(''); setWizardStep(4); }} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">← Back</button>
          <button type="submit" className="px-6 py-2 rounded-lg font-medium text-white" style={{ backgroundColor: BRAND_COLORS.navy }}>Add Partner</button>
        </div>
      </div>
      </form>
      </>)}
      </div>
    </div>
    </div>
  );
};

const EditPartnerModal = ({ onClose, onSubmit, partner, sectors, agreementTypes = DEFAULT_AGREEMENT_TYPES, currentUserRole = 'Admin' }) => {
  const [wizardStep, setWizardStep] = useState(1);
  const [formData, setFormData] = useState({
    name: partner.name || '',
    contactPerson: partner.contactPerson || '',
    email: partner.email || '',
    phone: partner.phone || '',
    location: partner.location || '',
    agreementType: partner.agreementType || 'Basic MCA Referral Fee',
    startDate: partner.startDate ? partner.startDate.split('T')[0] : '',
    endDate: partner.endDate ? partner.endDate.split('T')[0] : '',
    lastLoginDate: partner.lastLoginDate ? partner.lastLoginDate.split('T')[0] : '',
    lastActivityDate: partner.lastActivityDate ? partner.lastActivityDate.split('T')[0] : '',
    tailMonths: partner.tailMonths || 18,
    ndaSigned: partner.ndaSigned || false,
    ndaType: partner.ndaType || null,
    ndaSignedDate: partner.ndaSignedDate ? partner.ndaSignedDate.split('T')[0] : '',
  });

  const [selectedSectors, setSelectedSectors] = useState(() => parseSectorsField(partner.sectors));
  const [selectedGeo, setSelectedGeo] = useState(partner.geography || { continents: [], countries: [] });
  const [showActivitySection, setShowActivitySection] = useState(false);
  const [showCommissionSection, setShowCommissionSection] = useState(false);

  const defaultTerms = DEFAULT_COMMISSION_TERMS[partner.agreementType] || DEFAULT_COMMISSION_TERMS['One-time MCA Referral Fee'];
  const [commTerms, setCommTerms] = useState(partner.commissionTerms || defaultTerms);
  const updateCommTerms = (field, value) => setCommTerms(t => ({ ...t, [field]: value }));
  const updateBracket = (idx, field, value) => setCommTerms(t => ({
    ...t,
    brackets: (t.brackets || []).map((b, i) => i === idx ? { ...b, [field]: parseFloat(value) || value } : b)
  }));

  const [formError, setFormError] = useState('');

  // ── Form config ────────────────────────────────────────────────────────────
  const [fieldCfg, setFieldCfg] = useState({});
  const HARDCODED_REQUIRED = { name: true, contact_person: true, email: true, start_date: true, end_date: true };

  useEffect(() => {
    (async () => {
      try {
        const { supabase } = await import('./supabaseClient');
        const { data } = await supabase
          .from('form_field_config')
          .select('is_visible, is_required, role_permissions, custom_field_definitions!inner(field_name, entity_type)')
          .eq('form_type', 'edit')
          .eq('custom_field_definitions.entity_type', 'partner');
        if (data?.length) {
          const map = {};
          data.forEach(row => {
            map[row.custom_field_definitions.field_name] = {
              is_visible:       row.is_visible,
              is_required:      row.is_required,
              role_permissions: row.role_permissions || {},
            };
          });
          setFieldCfg(map);
        }
      } catch (_) {}
    })();
  }, []);

  const isVisible  = (fn) => {
    if (fieldCfg[fn]?.is_visible === false) return false;
    if (fieldCfg[fn]?.role_permissions?.[currentUserRole] === false) return false;
    return true;
  };
  const isRequired = (fn) => fieldCfg[fn]?.is_required  ?? HARDCODED_REQUIRED[fn] ?? false;
  const fieldLabel = (fn, defaultLabel) => isRequired(fn) ? `${defaultLabel} *` : defaultLabel;

  const validateStep2 = () => {
    const missing = [];
    if (isVisible('name')           && isRequired('name')           && !formData.name.trim())          missing.push('Partner Name');
    if (isVisible('contact_person') && isRequired('contact_person') && !formData.contactPerson.trim()) missing.push('Contact Person');
    if (isVisible('email')          && isRequired('email')          && !formData.email.trim())          missing.push('Email');
    if (isVisible('phone')          && isRequired('phone')          && !formData.phone.trim())          missing.push('Phone');
    if (missing.length) { setFormError(`Required: ${missing.join(', ')}`); return false; }
    setFormError('');
    return true;
  };

  // Shared by the final submit and by Save & Close.
  const buildPayload = () => ({
    ...formData, sectors: selectedSectors, geography: selectedGeo, commissionTerms: commTerms,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const missing = [];
    if (isVisible('start_date') && isRequired('start_date') && !formData.startDate) missing.push('Start Date');
    if (isVisible('end_date')   && isRequired('end_date')   && !formData.endDate)   missing.push('End Date');
    if (missing.length) { setFormError(`Required: ${missing.join(', ')}`); return; }
    setFormError('');
    onSubmit(partner.id, buildPayload());
  };

  // ── Save & Close ───────────────────────────────────────────────────────────
  // This partner already exists, so there is no draft to flag: commit whatever
  // has been edited so far straight to the live record and close. Only the name
  // is enforced — blanking it would leave the partner unidentifiable in lists.
  const draftBlockedReason = formData.name.trim() ? '' : 'The partner name cannot be blank.';
  const handleSaveDraft = () => {
    if (draftBlockedReason) return;
    setFormError('');
    onSubmit(partner.id, buildPayload());
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={onClose}>
    <div className="bg-white rounded-lg shadow-xl border-2 w-full flex flex-col" style={{ borderColor: BRAND_COLORS.gold, maxWidth: 560, maxHeight: '90vh' }} onClick={e => e.stopPropagation()}>
      {/* Wizard Header */}
      <div className="px-6 pt-4 pb-2 flex items-center justify-between border-b border-gray-100 flex-shrink-0">
        <h2 className="text-lg font-bold" style={{ color: BRAND_COLORS.navy }}>Edit Partner</h2>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">✕</button>
      </div>

      {/* Step Indicators */}
      <div className="px-6 pt-5 pb-4 border-b border-gray-100 flex-shrink-0">
        <div className="flex items-center justify-between">
          {[
            { n: 1, label: 'Agreement Type' },
            { n: 2, label: 'Partner Info' },
            { n: 3, label: 'Sector' },
            { n: 4, label: 'Geography' },
            { n: 5, label: 'Contract' },
          ].map(({ n, label }, idx, arr) => (
            <React.Fragment key={n}>
              <button onClick={() => setWizardStep(n)} className="flex flex-col items-center gap-1">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all"
                  style={{
                    backgroundColor: wizardStep === n ? BRAND_COLORS.navy : wizardStep > n ? BRAND_COLORS.gold : '#E5E7EB',
                    color: wizardStep >= n ? 'white' : '#9CA3AF'
                  }}>
                  {wizardStep > n ? '✓' : n}
                </div>
                <span className={`text-xs font-medium whitespace-nowrap ${wizardStep === n ? 'inline' : 'hidden'} sm:inline`} style={{ color: wizardStep === n ? BRAND_COLORS.navy : '#9CA3AF' }}>{label}</span>
              </button>
              {idx < arr.length - 1 && (
                <div className="flex-1 h-0.5 mx-2 mb-4 rounded" style={{ backgroundColor: wizardStep > n ? BRAND_COLORS.gold : '#E5E7EB' }} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="overflow-y-auto flex-1">
      {/* Step 1: Agreement Type */}
      {wizardStep === 1 && (<>
      <div className="p-6 border-b border-gray-200">
        <h3 className="text-lg font-semibold mb-1" style={{ color: BRAND_COLORS.navy }}>Step 1: Agreement Type</h3>
        <p className="text-sm text-gray-500 mb-6">Select the type of agreement with this partner</p>
        <div className="grid grid-cols-1 gap-3">
          {agreementTypes.map((type) => {
            const isSelected = formData.agreementType === type;
            const Icon = { 'One-time MCA Referral Fee': Target, 'Basic MCA Referral Fee': FileText, 'Table MCA Referral Fee': Activity, 'Revenue Pool MCA': PieChart }[type] || FileText;
            return (
              <button
                key={type}
                type="button"
                onClick={() => {
                  setFormData(f => ({ ...f, agreementType: type }));
                  setCommTerms(DEFAULT_COMMISSION_TERMS[type] || DEFAULT_COMMISSION_TERMS['One-time MCA Referral Fee']);
                }}
                className="py-4 px-5 rounded-xl border-2 transition-all text-left"
                style={{
                  borderColor: isSelected ? BRAND_COLORS.navy : '#e5e7eb',
                  backgroundColor: isSelected ? `${BRAND_COLORS.navy}0d` : 'white',
                  color: isSelected ? BRAND_COLORS.navy : '#374151'
                }}
              >
                <div className="flex items-center gap-2">
                  <Icon className="w-5 h-5 flex-shrink-0" style={{ color: isSelected ? BRAND_COLORS.navy : '#9CA3AF' }} />
                  <span className="font-bold text-sm">{type}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
      <div className="px-6 pb-5 pt-4 flex justify-between sticky bottom-0 bg-white border-t border-gray-100 z-10">
        <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">Cancel</button>
        <WizardSaveClose onClick={handleSaveDraft} disabledReason={draftBlockedReason} />
        <button onClick={() => setWizardStep(2)} className="px-6 py-2 rounded-lg font-medium text-white" style={{ backgroundColor: BRAND_COLORS.gold }}>Next →</button>
      </div>
      </>)}

      {/* Step 2: Partner Info */}
      {wizardStep === 2 && (<>
      <div className="p-6 border-b border-gray-200 space-y-4">
        <h3 className="text-lg font-semibold mb-1" style={{ color: BRAND_COLORS.navy }}>Step 2: Partner Info</h3>
        {isVisible('name') && (
          <Input label={fieldLabel('name', 'Partner Name')} value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
        )}
        {isVisible('contact_person') && (
          <Input label={fieldLabel('contact_person', 'Contact Person')} value={formData.contactPerson} onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })} />
        )}
        {isVisible('email') && (
          <Input label={fieldLabel('email', 'Email')} type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
        )}
        {isVisible('phone') && (
          <Input label={fieldLabel('phone', 'Phone')} value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
        )}
        {isVisible('location') && (
          <CountryAutocomplete
            label={fieldLabel('location', 'Partner Location')}
            value={formData.location}
            onChange={val => setFormData({ ...formData, location: val })}
          />
        )}
        {/* NDA / MNDA */}
        {isVisible('nda_signed') && (
          <div className="p-3 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">{fieldLabel('nda_signed', 'NDA / MNDA Signed')}</label>
              <input type="checkbox" checked={formData.ndaSigned}
                onChange={e => setFormData(f => ({ ...f, ndaSigned: e.target.checked, ndaType: e.target.checked ? (f.ndaType || 'NDA') : null, ndaSignedDate: e.target.checked ? f.ndaSignedDate : '' }))}
                className="w-4 h-4 accent-emerald-600" />
            </div>
            {formData.ndaSigned && (
              <div className="mt-3 space-y-3">
                <div className="flex gap-2">
                  {['NDA', 'MNDA'].map(t => (
                    <button key={t} type="button" onClick={() => setFormData(f => ({ ...f, ndaType: t }))}
                      className="flex-1 py-1.5 px-3 rounded-lg border-2 text-sm font-medium transition-all"
                      style={{ borderColor: formData.ndaType === t ? BRAND_COLORS.navy : '#e5e7eb', backgroundColor: formData.ndaType === t ? `${BRAND_COLORS.navy}0d` : 'white', color: formData.ndaType === t ? BRAND_COLORS.navy : '#6b7280' }}>
                      {t}
                    </button>
                  ))}
                </div>
                <Input label="Date Signed" type="date" value={formData.ndaSignedDate || ''}
                  onChange={e => setFormData(f => ({ ...f, ndaSignedDate: e.target.value }))} />
              </div>
            )}
          </div>
        )}
      </div>
      <div className="px-6 pb-5 pt-4 flex flex-col gap-2">
        {formError && <p className="text-xs text-red-500 text-right">{formError}</p>}
        <div className="flex justify-between">
          <button onClick={() => { setFormError(''); setWizardStep(1); }} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">← Back</button>
          <WizardSaveClose onClick={handleSaveDraft} disabledReason={draftBlockedReason} />
          <button onClick={() => { if (validateStep2()) setWizardStep(3); }} className="px-6 py-2 rounded-lg font-medium text-white" style={{ backgroundColor: BRAND_COLORS.gold }}>Next →</button>
        </div>
      </div>
      </>)}

      {/* Step 3: Sector */}
      {wizardStep === 3 && (<>
      <div className="p-6 border-b border-gray-200 space-y-4">
        <h3 className="text-lg font-semibold mb-1" style={{ color: BRAND_COLORS.navy }}>Step 3: Sector</h3>
        {isVisible('sectors') && (
          <SectorSelector
            sectors={sectors}
            selectedSectors={selectedSectors}
            onSectorsChange={setSelectedSectors}
                     />
        )}
      </div>
      <div className="px-6 pb-5 pt-4 flex justify-between sticky bottom-0 bg-white border-t border-gray-100 z-10">
        <button type="button" onClick={() => setWizardStep(2)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">← Back</button>
        <WizardSaveClose onClick={handleSaveDraft} disabledReason={draftBlockedReason} />
        <button onClick={() => setWizardStep(4)} className="px-6 py-2 rounded-lg font-medium text-white" style={{ backgroundColor: BRAND_COLORS.gold }}>Next →</button>
      </div>
      </>)}

      {/* Step 4: Geography */}
      {wizardStep === 4 && (<>
      <div className="p-6 border-b border-gray-200 space-y-4">
        <h3 className="text-lg font-semibold mb-1" style={{ color: BRAND_COLORS.navy }}>Step 4: Geography</h3>
        {isVisible('geography') && (
          <GeographySelector
            selectedGeo={selectedGeo}
            onGeoChange={setSelectedGeo}
          />
        )}
      </div>
      <div className="px-6 pb-5 pt-4 flex justify-between sticky bottom-0 bg-white border-t border-gray-100 z-10">
        <button type="button" onClick={() => setWizardStep(3)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">← Back</button>
        <WizardSaveClose onClick={handleSaveDraft} disabledReason={draftBlockedReason} />
        <button onClick={() => setWizardStep(5)} className="px-6 py-2 rounded-lg font-medium text-white" style={{ backgroundColor: BRAND_COLORS.gold }}>Next →</button>
      </div>
      </>)}

      {/* Step 5: Contract */}
      {wizardStep === 5 && (<>
      <form onSubmit={handleSubmit}>
      <div className="p-6 border-b border-gray-200 space-y-4">
        <h3 className="text-lg font-semibold mb-1" style={{ color: BRAND_COLORS.navy }}>Step 5: Contract</h3>
        {isVisible('start_date') && (
          <Input label={fieldLabel('start_date', 'Start Date')} type="date" value={formData.startDate} onChange={(e) => setFormData({ ...formData, startDate: e.target.value })} />
        )}
        {isVisible('end_date') && (
          <Input label={fieldLabel('end_date', 'End Date')} type="date" value={formData.endDate} onChange={(e) => setFormData({ ...formData, endDate: e.target.value })} />
        )}
        {isVisible('tail_months') && (
          <div className="p-3 rounded-lg text-sm" style={{ backgroundColor: BRAND_COLORS.cream, borderLeft: `3px solid ${BRAND_COLORS.gold}` }}>
            <p className="font-medium mb-2" style={{ color: BRAND_COLORS.navy }}>⏳ Tail End Period</p>
            <div className="flex items-center gap-3">
              <input type="number" min="1" max="60" value={formData.tailMonths}
                onChange={(e) => setFormData({ ...formData, tailMonths: parseInt(e.target.value) || 18 })}
                className="w-20 px-2 py-1 border border-gray-300 rounded text-sm" />
              <span className="text-xs text-gray-600">months after contract end</span>
            </div>
            {formData.endDate && (
              <p className="text-xs font-medium mt-2" style={{ color: BRAND_COLORS.gold }}>
                Tail End: {formatDate(addMonths(new Date(formData.endDate), formData.tailMonths || 18).toISOString())}
              </p>
            )}
          </div>
        )}

        {/* Activity Tracking (Collapsible) */}
        <div className="pt-2 border-t border-gray-200">
          <button type="button" onClick={() => setShowActivitySection(!showActivitySection)} className="flex items-center justify-between w-full text-left">
            <h3 className="text-sm font-semibold uppercase tracking-wide" style={{ color: BRAND_COLORS.navy }}>Activity Tracking</h3>
            <span className="text-gray-400">{showActivitySection ? '▲' : '▼'}</span>
          </button>
          {showActivitySection && (
            <div className="mt-4 space-y-4">
              <Input label="Last Login Date" type="date" value={formData.lastLoginDate} onChange={(e) => setFormData({ ...formData, lastLoginDate: e.target.value })} />
              <Input label="Last Activity Date" type="date" value={formData.lastActivityDate} onChange={(e) => setFormData({ ...formData, lastActivityDate: e.target.value })} />
            </div>
          )}
        </div>

        {/* Commission Terms */}
        {isVisible('commission_terms') && <div className="border border-gray-200 rounded-lg overflow-hidden">
          <button type="button" onClick={() => setShowCommissionSection(!showCommissionSection)}
            className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-left transition-colors hover:bg-gray-50"
            style={{ color: BRAND_COLORS.navy }}>
            <span className="flex items-center gap-2">
              <DollarSign className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} />
              Commission Terms
              <span className="text-xs font-normal text-gray-400 ml-1">({formData.agreementType || 'No agreement type'})</span>
            </span>
            <span className="text-gray-400">{showCommissionSection ? '▲' : '▼'}</span>
          </button>
          {showCommissionSection && (
            <div className="px-4 pb-4 space-y-4 border-t border-gray-100">
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Terms (days)</label>
                <input type="number" value={commTerms.paymentTermsDays ?? 10}
                  onChange={e => updateCommTerms('paymentTermsDays', parseInt(e.target.value) || 10)}
                  className="w-32 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              {commTerms.type === 'fixed' && (
                <div className="max-w-xs">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Agreed Commission %</label>
                  <p className="text-xs text-gray-400 mb-2">Single rate negotiated for this specific referral</p>
                  <div className="relative w-32">
                    <input type="number" value={commTerms.percentage ?? 10}
                      onChange={e => updateCommTerms('percentage', parseFloat(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm pr-7" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">%</span>
                  </div>
                </div>
              )}
              {commTerms.type === 'basic' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Client %</label>
                    <div className="relative">
                      <input type="number" value={commTerms.clientPercentage ?? 10}
                        onChange={e => updateCommTerms('clientPercentage', parseFloat(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm pr-7" />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">%</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Investor %</label>
                    <div className="relative">
                      <input type="number" value={commTerms.investorPercentage ?? 20}
                        onChange={e => updateCommTerms('investorPercentage', parseFloat(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm pr-7" />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">%</span>
                    </div>
                  </div>
                </div>
              )}
              {commTerms.type === 'table' && (
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-2">Sliding Scale Brackets</p>
                  <div className="space-y-2">
                    {(commTerms.brackets || []).map((bracket, idx) => (
                      <div key={idx} className="grid grid-cols-3 gap-2 items-center p-2 bg-gray-50 rounded-lg">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">{idx < (commTerms.brackets.length - 1) ? 'Up to $' : 'Above'}</label>
                          {idx < (commTerms.brackets.length - 1) ? (
                            <input type="number" value={bracket.upTo ?? ''}
                              onChange={e => updateBracket(idx, 'upTo', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-xs" />
                          ) : (
                            <span className="text-xs text-gray-400 italic">No limit</span>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Non-active %</label>
                          <div className="relative">
                            <input type="number" value={bracket.nonActive ?? ''}
                              onChange={e => updateBracket(idx, 'nonActive', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-xs pr-5" />
                            <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">%</span>
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Active %</label>
                          <div className="relative">
                            <input type="number" value={bracket.active ?? ''}
                              onChange={e => updateBracket(idx, 'active', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-xs pr-5" />
                            <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">%</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {commTerms.type === 'pool' && (
                <div className="max-w-xs">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Pool Split %</label>
                  <p className="text-xs text-gray-400 mb-2">This partner's agreed share of the pool on any Revenue Pool MCA deal</p>
                  <div className="relative w-32">
                    <input type="number" min="0" max="100" step="0.1"
                      value={commTerms.poolSplitPercentage ?? 50}
                      onChange={e => updateCommTerms('poolSplitPercentage', parseFloat(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm pr-7" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">%</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>}
      </div>
      <div className="px-6 pb-5 pt-4 flex flex-col gap-2">
        {formError && <p className="text-xs text-red-500 text-right">{formError}</p>}
        <div className="flex justify-between">
          <button type="button" onClick={() => { setFormError(''); setWizardStep(4); }} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">← Back</button>
          <button type="submit" className="px-6 py-2 rounded-lg font-medium text-white" style={{ backgroundColor: BRAND_COLORS.navy }}>Save Changes</button>
        </div>
      </div>
      </form>
      </>)}
      </div>
    </div>
    </div>
  );
};

const MultiSelect = ({ label, field, options, color = BRAND_COLORS.navy, formData, setFormData }) => {
  const [search, setSearch] = React.useState('');
  const filtered = options.filter(opt => opt.toLowerCase().includes(search.toLowerCase()));
  const selected = formData?.[field] || [];
  const toggle = (opt) => {
    const next = selected.includes(opt) ? selected.filter(s => s !== opt) : [...selected, opt];
    setFormData(prev => ({ ...prev, [field]: next }));
  };
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      <div className="border border-gray-200 rounded-lg bg-gray-50 overflow-hidden">
        <div className="flex items-center px-2 py-1.5 border-b border-gray-200 bg-white">
          <Search className="w-3.5 h-3.5 text-gray-400 mr-1.5 flex-shrink-0" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search..." className="w-full text-xs outline-none bg-transparent" />
          {search && <button type="button" onClick={() => setSearch('')}><X className="w-3 h-3 text-gray-400" /></button>}
        </div>
        <div className="max-h-32 overflow-y-auto p-1.5 flex flex-wrap gap-1">
          {filtered.map(opt => (
            <button key={opt} type="button" onClick={() => toggle(opt)}
              className="px-2 py-0.5 rounded text-xs font-medium transition-all"
              style={selected.includes(opt)
                ? { backgroundColor: color, color: 'white' }
                : { backgroundColor: 'white', color: '#374151', border: '1px solid #e5e7eb' }}>
              {opt}
            </button>
          ))}
        </div>
      </div>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {selected.map(s => (
            <span key={s} className="px-2 py-0.5 rounded-full text-xs font-medium text-white flex items-center gap-1"
              style={{ backgroundColor: color }}>
              {s}
              <button type="button" onClick={() => toggle(s)}><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const EditProspectModal = ({ onClose, onSubmit, prospect, sectors = DEFAULT_SECTORS, partners = [], currentUserRole = 'Admin' }) => {
  const { isMobile } = useIsMobile();
  const [wizardStep, setWizardStep] = useState(1);

  // ── Form config ────────────────────────────────────────────────────────────
  const [fieldCfg, setFieldCfg] = useState({});
  useEffect(() => {
    (async () => {
      try {
        const { supabase } = await import('./supabaseClient');
        const { data } = await supabase
          .from('form_field_config')
          .select('is_visible, is_required, role_permissions, custom_field_definitions!inner(field_name, entity_type)')
          .eq('form_type', 'edit')
          .eq('custom_field_definitions.entity_type', 'prospect');
        if (data?.length) {
          const map = {};
          data.forEach(row => {
            map[row.custom_field_definitions.field_name] = {
              is_visible:       row.is_visible,
              is_required:      row.is_required,
              role_permissions: row.role_permissions || {},
            };
          });
          setFieldCfg(map);
        }
      } catch (_) {}
    })();
  }, []);
  const isVisible  = (fn) => {
    if (fieldCfg[fn]?.is_visible === false) return false;
    if (fieldCfg[fn]?.role_permissions?.[currentUserRole] === false) return false;
    return true;
  };
  const isRequired = (fn) => fieldCfg[fn]?.is_required  ?? (fn === 'name');
  const fieldLabel = (fn, defaultLabel) => isRequired(fn) ? `${defaultLabel} *` : defaultLabel;

  const [formData, setFormData] = useState({
    name: prospect.name || prospect.companyName || '',
    description: prospect.description || '',
    notes: prospect.notes || '',
    contractType: prospect.contractType || 'Direct',
    introducingPartnerId: prospect.introducingPartnerId || '',
    investmentFocus: prospect.investmentFocus || [],
    investmentMin: prospect.investmentMin || '',
    investmentMax: prospect.investmentMax || '',
    investmentCurrency: prospect.investmentCurrency || 'USD',
    investmentBodyType: prospect.investmentBodyType || '',
    preferredStage: prospect.preferredStage || '',
    serviceTypes: prospect.serviceTypes || [],
    fundraisingMin: prospect.fundraisingMin || '',
    fundraisingMax: prospect.fundraisingMax || '',
    potentialDescription: prospect.potentialDescription || '',
    websiteUrl: prospect.websiteUrl || '',
    linkedinPerson: prospect.linkedinPerson || '',
    linkedinCompany: prospect.linkedinCompany || '',
    investorsDeckUrl: prospect.investorsDeckUrl || '',
    investorDeckFileUrl:  prospect.investorDeckFileUrl  || '',
    investorDeckFileName: prospect.investorDeckFileName || '',
    investorDeckFileSize: prospect.investorDeckFileSize || 0,
    _investorDeckFile: null, // pending upload (transient)
    stage: prospect.stage || '',
    targetRaise: prospect.targetRaise || '',
    currentValuation: prospect.currentValuation || '',
    revenue: prospect.revenue || ''
  });

  const prospectType = prospect.prospectType || 'Client';
  const update = (field, value) => setFormData(f => ({ ...f, [field]: value }));
  const [dealOpen, setDealOpen] = useState(false);
  const [deckUploadError, setDeckUploadError] = useState('');

  const [editSectors, setEditSectors] = useState(() => {
    const parsed = parseSectorsField(prospect.sectors);
    return parsed.length ? parsed : (prospect.sector ? [prospect.sector] : []);
  });
  const [editGeo, setEditGeo] = useState({
    continents: prospect.geography?.continents || [],
    countries: prospect.countries || (prospect.country ? [prospect.country] : [])
  });
  const toggleItem = (field, item) => setFormData(f => ({
    ...f,
    [field]: f[field].includes(item) ? f[field].filter(t => t !== item) : [...f[field], item]
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { window.alert('Please enter a name'); return; }

    let deckUrl  = formData.investorDeckFileUrl  || '';
    let deckName = formData.investorDeckFileName || '';
    let deckSize = formData.investorDeckFileSize || 0;

    if (formData._investorDeckFile) {
      try {
        const { uploadInvestorDeck } = await import('./supabaseClient');
        deckUrl  = await uploadInvestorDeck(prospect.id, formData._investorDeckFile);
        deckName = formData._investorDeckFile.name;
        deckSize = formData._investorDeckFile.size;
      } catch (err) {
        setDeckUploadError(err.message || 'Failed to upload investor deck');
        return;
      }
    }

    const { _investorDeckFile, ...rest } = formData;
    onSubmit(prospect.id, {
      ...rest,
      investorDeckFileUrl:  deckUrl,
      investorDeckFileName: deckName,
      investorDeckFileSize: deckSize,
      prospectType,
      companyName: formData.name,
      sectors: editSectors,
      geography: editGeo,
      sector: editSectors.join(', '),
      country: editGeo.countries.join(', '),
    });
  };

  // ── Save & Close ───────────────────────────────────────────────────────────
  // This prospect is already live, so there is nothing to flag as a draft:
  // commit the edits made so far and close. A pending deck upload is deliberately
  // not carried over — it belongs to the final submit, and re-opening the wizard
  // lets the user attach it again.
  const draftBlockedReason = formData.name.trim() ? '' : 'The prospect name cannot be blank.';
  const handleSaveDraft = () => {
    if (draftBlockedReason) return;
    const { _investorDeckFile, ...rest } = formData;
    onSubmit(prospect.id, {
      ...rest,
      prospectType,
      companyName: formData.name,
      sectors: editSectors,
      geography: editGeo,
      sector: editSectors.join(', '),
      country: editGeo.countries.join(', '),
    });
    onClose();
  };

  const PROSPECT_TYPE_CARDS = [
    { type: 'Client', label: 'Client', desc: 'Company or individual seeking EvE services', Icon: Building2 },
    { type: 'Angel', label: 'Angel Investor', desc: 'Individual investing personal capital', Icon: Users },
    { type: 'InvestmentBody', label: 'Investment Body', desc: 'VC, PE, Family Office or fund', Icon: Landmark },
    { type: 'Connection', label: 'Interesting Connection', desc: 'Relationship with future potential', Icon: Link },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={onClose}>
    <div className="bg-white rounded-lg shadow-xl border-2 w-full flex flex-col" style={{ borderColor: BRAND_COLORS.gold, maxWidth: isMobile ? '95vw' : 600, maxHeight: '90vh' }} onClick={e => e.stopPropagation()}>
      {/* Wizard Header */}
      <div className="px-6 pt-4 pb-2 flex items-center justify-between border-b border-gray-100 flex-shrink-0">
        <h2 className="text-lg font-bold" style={{ color: BRAND_COLORS.navy }}>Edit Prospect</h2>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">✕</button>
      </div>

      {/* Step Indicators */}
      <div className="px-6 pt-5 pb-4 border-b border-gray-100 flex-shrink-0">
        <div className="flex items-center justify-between">
          {[
            { n: 1, label: 'Prospect Type' },
            { n: 2, label: 'Basic Info' },
            { n: 3, label: 'Sector' },
            { n: 4, label: 'Geography' },
            { n: 5, label: 'Details' },
          ].map(({ n, label }, idx, arr) => (
            <React.Fragment key={n}>
              <button onClick={() => setWizardStep(n)} className="flex flex-col items-center gap-1">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all"
                  style={{
                    backgroundColor: wizardStep === n ? BRAND_COLORS.navy : wizardStep > n ? BRAND_COLORS.gold : '#E5E7EB',
                    color: wizardStep >= n ? 'white' : '#9CA3AF'
                  }}>
                  {wizardStep > n ? '✓' : n}
                </div>
                <span className={`text-xs font-medium whitespace-nowrap ${wizardStep === n ? 'inline' : 'hidden'} sm:inline`} style={{ color: wizardStep === n ? BRAND_COLORS.navy : '#9CA3AF' }}>{label}</span>
              </button>
              {idx < arr.length - 1 && (
                <div className="flex-1 h-0.5 mx-2 mb-4 rounded" style={{ backgroundColor: wizardStep > n ? BRAND_COLORS.gold : '#E5E7EB' }} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="overflow-y-auto flex-1">
      {/* Step 1: Prospect Type (read-only) */}
      {wizardStep === 1 && (<>
      <div className="p-6 border-b border-gray-200">
        <h3 className="text-lg font-semibold mb-1" style={{ color: BRAND_COLORS.navy }}>Step 1: Prospect Type</h3>
        <p className="text-sm text-gray-500 mb-6">The prospect type cannot be changed after creation</p>
        <div className="grid grid-cols-2 gap-3">
          {PROSPECT_TYPE_CARDS.map(({ type, label, desc, Icon }) => {
            const isSelected = prospectType === type;
            return (
              <div
                key={type}
                className="py-4 px-5 rounded-xl border-2 text-left"
                style={{
                  borderColor: isSelected ? BRAND_COLORS.navy : '#e5e7eb',
                  backgroundColor: isSelected ? `${BRAND_COLORS.navy}0d` : '#f9fafb',
                  color: isSelected ? BRAND_COLORS.navy : '#9CA3AF',
                  opacity: isSelected ? 1 : 0.5,
                }}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Icon className="w-5 h-5 flex-shrink-0" style={{ color: isSelected ? BRAND_COLORS.navy : '#9CA3AF' }} />
                  <span className="font-bold text-sm">{label}</span>
                  {isSelected && <span className="ml-auto text-xs font-normal" style={{ color: BRAND_COLORS.gold }}>Current</span>}
                </div>
                <div className="text-xs" style={{ color: isSelected ? `${BRAND_COLORS.navy}99` : '#D1D5DB' }}>{desc}</div>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-gray-400 mt-4 text-center">Type cannot be changed after creation</p>
      </div>
      <div className="px-6 pb-5 pt-4 flex justify-between sticky bottom-0 bg-white border-t border-gray-100 z-10">
        <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">Cancel</button>
        <WizardSaveClose onClick={handleSaveDraft} disabledReason={draftBlockedReason} />
        <button onClick={() => setWizardStep(2)} className="px-6 py-2 rounded-lg font-medium text-white" style={{ backgroundColor: BRAND_COLORS.gold }}>Next →</button>
      </div>
      </>)}

      {/* Step 2: Basic Info */}
      {wizardStep === 2 && (<>
      <div className="p-6 border-b border-gray-200 space-y-4">
        <h3 className="text-lg font-semibold mb-1" style={{ color: BRAND_COLORS.navy }}>Step 2: Basic Info</h3>
        {isVisible('name') && (
          <Input
            label={fieldLabel('name', prospectType === 'Angel' ? 'Full Name' : prospectType === 'Connection' ? 'Name' : 'Company Name')}
            value={formData.name} onChange={e => update('name', e.target.value)} />
        )}
        {isVisible('website_url') && (
          <Input label="Website" value={formData.websiteUrl} onChange={e => update('websiteUrl', e.target.value)} />
        )}

        {/* About the client / description */}
        {isVisible('description') && (
          <Textarea
            label={prospectType === 'Client' ? 'About the client' : 'About'}
            value={formData.description}
            onChange={e => update('description', e.target.value)}
            placeholder={
              prospectType === 'Client'   ? 'Short description of the client, what they do, why they are interesting…' :
              prospectType === 'Angel'    ? 'Background, sectors of interest, check size, anything relevant…' :
              prospectType === 'Connection' ? 'Who they are and the relationship context…' :
              'What the firm does, focus, why they are relevant…'
            }
            rows={3}
          />
        )}

        {/* LinkedIn (Person + Company) — moved out of accordion */}
        {isVisible('linkedin_person') && (
          <Input label="LinkedIn (Person)" value={formData.linkedinPerson} onChange={e => update('linkedinPerson', e.target.value)} placeholder="https://linkedin.com/in/…" />
        )}
        {isVisible('linkedin_company') && (
          <Input label="LinkedIn (Company)" value={formData.linkedinCompany} onChange={e => update('linkedinCompany', e.target.value)} placeholder="https://linkedin.com/company/…" />
        )}

        {/* Deal Intelligence — Client only, financial details */}
        {prospectType === 'Client' && (isVisible('stage') || isVisible('target_raise') || isVisible('current_valuation') || isVisible('revenue')) && (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <button type="button" onClick={() => setDealOpen(o => !o)}
              className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-sm font-medium text-gray-700">
              <span>Deal Intelligence</span>
              <span className="text-gray-400 text-xs">{dealOpen ? '▲' : '▼'}</span>
            </button>
            {dealOpen && (
              <div className="p-4 space-y-3 border-t border-gray-200">
                {isVisible('stage') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Stage</label>
                    <select value={formData.stage} onChange={e => update('stage', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                      <option value="">— Select Stage —</option>
                      {['Seed', 'Series A', 'Series B', 'Growth', 'Pre-IPO'].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                )}
                {isVisible('target_raise')      && <MoneyInput label="Target Raise"      value={formData.targetRaise}      onChange={v => update('targetRaise', v)} />}
                {isVisible('current_valuation') && <MoneyInput label="Current Valuation" value={formData.currentValuation} onChange={v => update('currentValuation', v)} />}
                {isVisible('revenue')           && <MoneyInput label="Revenue"           value={formData.revenue}           onChange={v => update('revenue', v)} />}
              </div>
            )}
          </div>
        )}

        {isVisible('contract_type') && currentUserRole !== 'Partner' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Contract Type</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'Direct', label: '📝 Direct', desc: 'EvE ↔ Prospect' },
                { value: 'ThroughPartner', label: '🔗 Through Partner', desc: 'EvE ↔ Partner' }
              ].map(opt => (
                <button key={opt.value} type="button" onClick={() => update('contractType', opt.value)}
                  className="py-2 px-3 text-sm rounded-lg border-2 text-left transition-all"
                  style={{
                    borderColor: formData.contractType === opt.value ? BRAND_COLORS.navy : '#e5e7eb',
                    backgroundColor: formData.contractType === opt.value ? `${BRAND_COLORS.navy}10` : 'white',
                    color: formData.contractType === opt.value ? BRAND_COLORS.navy : '#6b7280'
                  }}>
                  <div className="font-medium">{opt.label}</div>
                  <div className="text-xs opacity-70 mt-0.5">{opt.desc}</div>
                </button>
              ))}
            </div>
          </div>
        )}
        {formData.contractType === 'ThroughPartner' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Introducing Partner</label>
            <select value={formData.introducingPartnerId || ''} onChange={e => update('introducingPartnerId', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
              <option value="">— Select Partner —</option>
              {partners.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        )}
      </div>
      <div className="px-6 pb-5 pt-4 flex justify-between sticky bottom-0 bg-white border-t border-gray-100 z-10">
        <button onClick={() => setWizardStep(1)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">← Back</button>
        <WizardSaveClose onClick={handleSaveDraft} disabledReason={draftBlockedReason} />
        <button onClick={() => setWizardStep(3)} className="px-6 py-2 rounded-lg font-medium text-white" style={{ backgroundColor: BRAND_COLORS.gold }}>Next →</button>
      </div>
      </>)}

      {/* Step 3: Sector */}
      {wizardStep === 3 && (<>
      <div className="p-6 border-b border-gray-200 space-y-4">
        <h3 className="text-lg font-semibold mb-1" style={{ color: BRAND_COLORS.navy }}>Step 3: Sector</h3>
        {isVisible('sectors') && (
          <SectorSelector sectors={sectors} selectedSectors={editSectors}
            onSectorsChange={(s) => { setEditSectors(s); update('sectors', s); }} />
        )}
      </div>
      <div className="px-6 pb-5 pt-4 flex justify-between sticky bottom-0 bg-white border-t border-gray-100 z-10">
        <button type="button" onClick={() => setWizardStep(2)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">← Back</button>
        <WizardSaveClose onClick={handleSaveDraft} disabledReason={draftBlockedReason} />
        <button onClick={() => setWizardStep(4)} className="px-6 py-2 rounded-lg font-medium text-white" style={{ backgroundColor: BRAND_COLORS.gold }}>Next →</button>
      </div>
      </>)}

      {/* Step 4: Geography */}
      {wizardStep === 4 && (<>
      <div className="p-6 border-b border-gray-200 space-y-4">
        <h3 className="text-lg font-semibold mb-1" style={{ color: BRAND_COLORS.navy }}>Step 4: Geography</h3>
        {isVisible('geography') && (
          <GeographySelector selectedGeo={editGeo}
            onGeoChange={(g) => { setEditGeo(g); update('countries', g.countries); update('geography', g); }} />
        )}
      </div>
      <div className="px-6 pb-5 pt-4 flex justify-between sticky bottom-0 bg-white border-t border-gray-100 z-10">
        <button type="button" onClick={() => setWizardStep(3)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">← Back</button>
        <WizardSaveClose onClick={handleSaveDraft} disabledReason={draftBlockedReason} />
        <button onClick={() => setWizardStep(5)} className="px-6 py-2 rounded-lg font-medium text-white" style={{ backgroundColor: BRAND_COLORS.gold }}>Next →</button>
      </div>
      </>)}

      {/* Step 5: Details */}
      {wizardStep === 5 && (<>
      <form onSubmit={handleSubmit}>
      <div className="p-6 border-b border-gray-200 space-y-4">
        <h3 className="text-lg font-semibold mb-1" style={{ color: BRAND_COLORS.navy }}>Step 5: Details</h3>

        {/* InvestmentBody only */}
        {isVisible('investment_body_type') && prospectType === 'InvestmentBody' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Investment Body Type</label>
            <div className="flex flex-wrap gap-2">
              {['VC', 'Family Office', 'Private Equity', 'Debt Provider', 'Other'].map(t => (
                <button key={t} type="button" onClick={() => update('investmentBodyType', t)}
                  className="px-3 py-1 text-xs rounded-full border font-medium transition-all"
                  style={{
                    borderColor: formData.investmentBodyType === t ? '#0369a1' : '#e5e7eb',
                    backgroundColor: formData.investmentBodyType === t ? '#e0f2fe' : 'white',
                    color: formData.investmentBodyType === t ? '#0369a1' : '#6b7280'
                  }}>{t}</button>
              ))}
            </div>
          </div>
        )}

        {/* Angel & InvestmentBody */}
        {(prospectType === 'Angel' || prospectType === 'InvestmentBody') && (
          <>
            {isVisible('investment_focus') && (
              <MultiSelect label="Investment Focus" field="investmentFocus" options={sectors} color="#7c3aed" formData={formData} setFormData={setFormData} />
            )}
            {(isVisible('investment_min') || isVisible('investment_max')) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Typical Investment Size</label>
                <div className="flex gap-2 items-center">
                  <select value={formData.investmentCurrency} onChange={e => update('investmentCurrency', e.target.value)}
                    className="px-2 py-2 border border-gray-300 rounded-lg text-sm w-24">
                    <option value="USD">$ USD</option>
                    <option value="EUR">€ EUR</option>
                    <option value="GBP">£ GBP</option>
                    <option value="ILS">₪ ILS</option>
                  </select>
                  <MoneyInput value={formData.investmentMin} onChange={v => update('investmentMin', v)} placeholder="Minimum" symbol="" className="flex-1 text-sm" />
                  <span className="text-gray-400">—</span>
                  <MoneyInput value={formData.investmentMax} onChange={v => update('investmentMax', v)} placeholder="Maximum" symbol="" className="flex-1 text-sm" />
                </div>
              </div>
            )}
            {isVisible('preferred_stage') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Investment Stage</label>
                <select value={formData.preferredStage} onChange={e => update('preferredStage', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                  <option value="">— Select Stage —</option>
                  {['Seed', 'Series A', 'Series B', 'Growth', 'Pre-IPO'].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            )}
          </>
        )}

        {/* Client only */}
        {prospectType === 'Client' && (
          <>
            {/* Investor Deck — file upload (PDF / PPT / PPTX, up to 50 MB) */}
            {isVisible('investors_deck_url') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Investor Deck</label>
                {formData._investorDeckFile ? (
                  <div className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <FileText className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} />
                    <span className="flex-1 truncate" style={{ color: BRAND_COLORS.navy }}>{formData._investorDeckFile.name}</span>
                    <span className="text-xs text-gray-500">{(formData._investorDeckFile.size / 1024 / 1024).toFixed(2)} MB</span>
                    <button type="button" onClick={() => { update('_investorDeckFile', null); setDeckUploadError(''); }}
                      className="text-xs text-gray-500 hover:text-gray-900">Cancel</button>
                  </div>
                ) : formData.investorDeckFileUrl ? (
                  <div className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <FileText className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} />
                    <button type="button" onClick={() => openSignedFile(formData.investorDeckFileUrl)}
                      className="flex-1 truncate hover:underline text-left" style={{ color: BRAND_COLORS.navy, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                      {formData.investorDeckFileName || 'Current deck'}
                    </button>
                    {formData.investorDeckFileSize > 0 && (
                      <span className="text-xs text-gray-500">{(formData.investorDeckFileSize / 1024 / 1024).toFixed(2)} MB</span>
                    )}
                    <label className="text-xs cursor-pointer hover:underline" style={{ color: BRAND_COLORS.gold }}>
                      Replace
                      <input type="file"
                        accept=".pdf,.ppt,.pptx,application/pdf,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0]; if (!f) return;
                          setDeckUploadError('');
                          const ext = f.name.split('.').pop().toLowerCase();
                          if (!['pdf','ppt','pptx'].includes(ext)) { setDeckUploadError('Only PDF, PPT, or PPTX files are allowed.'); e.target.value=''; return; }
                          if (f.size > 50*1024*1024) { setDeckUploadError('File exceeds 50 MB limit.'); e.target.value=''; return; }
                          update('_investorDeckFile', f);
                        }}
                      />
                    </label>
                    <button type="button" onClick={() => { update('investorDeckFileUrl',''); update('investorDeckFileName',''); update('investorDeckFileSize',0); }}
                      className="text-xs text-gray-500 hover:text-gray-900">Remove</button>
                  </div>
                ) : (
                  <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-lg text-sm cursor-pointer hover:bg-gray-50">
                    <Upload className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} />
                    <span className="text-gray-600">Choose a PDF or PowerPoint file</span>
                    <input type="file"
                      accept=".pdf,.ppt,.pptx,application/pdf,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0]; if (!f) return;
                        setDeckUploadError('');
                        const ext = f.name.split('.').pop().toLowerCase();
                        if (!['pdf','ppt','pptx'].includes(ext)) { setDeckUploadError('Only PDF, PPT, or PPTX files are allowed.'); e.target.value=''; return; }
                        if (f.size > 50*1024*1024) { setDeckUploadError('File exceeds 50 MB limit.'); e.target.value=''; return; }
                        update('_investorDeckFile', f);
                      }}
                    />
                  </label>
                )}
                <p className="text-xs text-gray-500 mt-1">PDF, PPT or PPTX. Max 50 MB.</p>
                {deckUploadError && <p className="text-xs mt-1" style={{ color: BRAND_COLORS.statusError }}>{deckUploadError}</p>}
              </div>
            )}

            {isVisible('service_types') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Service Type</label>
                <div className="flex gap-2">
                  {[
                    { value: 'Retainer',      Icon: ClipboardList },
                    { value: 'Success-based', Icon: Target },
                    { value: 'Equity-based',  Icon: PieChart },
                  ].map(({ value, Icon }) => (
                    <button key={value} type="button" onClick={() => toggleItem('serviceTypes', value)}
                      className="flex-1 py-2 px-3 text-sm rounded-lg border-2 font-medium transition-all flex items-center justify-center gap-2"
                      style={{
                        borderColor: formData.serviceTypes.includes(value) ? BRAND_COLORS.navy : '#e5e7eb',
                        backgroundColor: formData.serviceTypes.includes(value) ? `${BRAND_COLORS.navy}15` : 'white',
                        color: formData.serviceTypes.includes(value) ? BRAND_COLORS.navy : '#6b7280'
                      }}>
                      <Icon className="w-4 h-4" /> {value}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {(isVisible('fundraising_min') || isVisible('fundraising_max')) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Fundraising Range</label>
                <div className="flex gap-2 items-center">
                  <select value={formData.investmentCurrency} onChange={e => update('investmentCurrency', e.target.value)}
                    className="px-2 py-2 border border-gray-300 rounded-lg text-sm w-24">
                    <option value="USD">$ USD</option>
                    <option value="EUR">€ EUR</option>
                    <option value="GBP">£ GBP</option>
                    <option value="ILS">₪ ILS</option>
                  </select>
                  <input type="number" value={formData.fundraisingMin} onChange={e => update('fundraisingMin', e.target.value)}
                    placeholder="Minimum" className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  <span className="text-gray-400">—</span>
                  <input type="number" value={formData.fundraisingMax} onChange={e => update('fundraisingMax', e.target.value)}
                    placeholder="Maximum" className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              </div>
            )}
          </>
        )}

        {/* Connection only */}
        {isVisible('potential_description') && prospectType === 'Connection' && (
          <Textarea label="What is the potential?" value={formData.potentialDescription}
            onChange={e => update('potentialDescription', e.target.value)} rows={3} />
        )}

        {isVisible('notes') && (
          <Textarea label="Notes" value={formData.notes} onChange={e => update('notes', e.target.value)} rows={3} />
        )}
      </div>
      <div className="px-6 pb-5 pt-4 flex justify-between sticky bottom-0 bg-white border-t border-gray-100 z-10">
        <button type="button" onClick={() => setWizardStep(4)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">← Back</button>
        <button type="submit" className="px-6 py-2 text-white rounded-lg transition-colors font-medium" style={{ backgroundColor: BRAND_COLORS.navy }}>Save Changes</button>
      </div>
      </form>
      </>)}
      </div>
    </div>
    </div>
  );
};

// ============================================================================
// VIEW: Partner Profile
// ============================================================================

const PartnerProfileView = ({ currentUser, partner, prospects = [], revenueDeals = [], onEditProfile }) => {
  const formatDate = (d) => {
    if (!d) return 'N/A';
    const date = new Date(d);
    return isNaN(date) ? 'N/A' : date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const pid = partner?.id;
  const myDeals = revenueDeals.filter(deal => {
    const lp = deal.prospectId ? prospects.find(p => p.id === deal.prospectId) : null;
    const ivp = deal.investorProspectId ? prospects.find(p => p.id === deal.investorProspectId) : null;
    return lp?.introducingPartnerId === pid || ivp?.introducingPartnerId === pid ||
      deal.strategicPartners?.some(sp => sp.partnerId === pid);
  });

  const approvedProspects = prospects.filter(p => p.approvalStatus === 'Approved').length;
  const waitingProspects = prospects.filter(p =>
    p.approvalStatus === 'Waiting for Approval' || p.approvalStatus === 'Pending'
  ).length;

  const statusColor = partner?.status === 'Active' ? '#10B981' :
    partner?.status === 'In Tail' ? '#F59E0B' : '#EF4444';

  return (
    <div className="p-6 space-y-6 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold" style={{ color: BRAND_COLORS.navy }}>My Profile</h2>

      {/* Identity Card */}
      <div className="p-6 rounded-xl" style={{ backgroundColor: BRAND_COLORS.surface, border: `2px solid ${BRAND_COLORS.gold}` }}>
        <div className="flex items-start gap-6 relative">
          <div className="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold text-white flex-shrink-0"
            style={{ backgroundColor: BRAND_COLORS.navy }}>
            {(partner?.name || currentUser.name || '?')[0].toUpperCase()}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-1">
              <h3 className="text-xl font-bold" style={{ color: BRAND_COLORS.navy }}>
                {partner?.name || currentUser.name}
              </h3>
              {partner?.status && (
                <span className="px-2 py-0.5 rounded-full text-xs font-semibold text-white"
                  style={{ backgroundColor: statusColor }}>
                  {partner.status}
                </span>
              )}
            </div>
            <p className="text-sm" style={{ color: BRAND_COLORS.textSecondary }}>
              {partner?.contactEmail || currentUser.email}
            </p>
            {partner?.contactPhone && (
              <p className="text-sm mt-0.5" style={{ color: BRAND_COLORS.textSecondary }}>
                {partner.contactPhone}
              </p>
            )}
          </div>
          {onEditProfile && (
            <button
              onClick={onEditProfile}
              className="absolute top-0 right-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-all hover:opacity-80"
              style={{ borderColor: BRAND_COLORS.gold, color: BRAND_COLORS.gold, backgroundColor: 'white' }}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit Profile
            </button>
          )}
        </div>
      </div>

      {/* Agreement Details */}
      <div className="p-6 rounded-xl" style={{ backgroundColor: BRAND_COLORS.surface, border: `1px solid ${BRAND_COLORS.border}` }}>
        <h3 className="text-lg font-bold mb-4" style={{ color: BRAND_COLORS.navy }}>
          🤝 Partnership Agreement
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <p className="text-xs font-medium uppercase tracking-wider mb-1" style={{ color: BRAND_COLORS.textTertiary }}>
              Agreement Type
            </p>
            <p className="text-base font-semibold" style={{ color: BRAND_COLORS.navy }}>
              {partner?.agreementType || partner?.mcaType || '—'}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wider mb-1" style={{ color: BRAND_COLORS.textTertiary }}>
              Start Date
            </p>
            <p className="text-base font-semibold" style={{ color: BRAND_COLORS.textPrimary }}>
              {formatDate(partner?.startDate || partner?.mcaStartDate || partner?.mcaDate)}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wider mb-1" style={{ color: BRAND_COLORS.textTertiary }}>
              End Date
            </p>
            <p className="text-base font-semibold" style={{ color: BRAND_COLORS.textPrimary }}>
              {formatDate(partner?.endDate || partner?.mcaEndDate)}
            </p>
          </div>
          {(partner?.sector || partner?.sectors?.length > 0) && (
            <div>
              <p className="text-xs font-medium uppercase tracking-wider mb-1" style={{ color: BRAND_COLORS.textTertiary }}>
                Sector
              </p>
              <p className="text-base font-semibold" style={{ color: BRAND_COLORS.textPrimary }}>
                {partner.sectors?.length > 0 ? partner.sectors.join(', ') : partner.sector}
              </p>
            </div>
          )}
          {partner?.geography && (partner.geography.continents?.length > 0 || partner.geography.countries?.length > 0) && (
            <div>
              <p className="text-xs font-medium uppercase tracking-wider mb-1" style={{ color: BRAND_COLORS.textTertiary }}>
                Geography
              </p>
              <p className="text-base font-semibold" style={{ color: BRAND_COLORS.textPrimary }}>
                {[
                  ...(partner.geography.continents || []),
                  ...(partner.geography.countries || [])
                ].join(', ') || '—'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* My Preferences — editable via Edit Profile */}
      {partner && (
        <div className="p-6 rounded-xl" style={{ backgroundColor: BRAND_COLORS.surface, border: `1px solid ${BRAND_COLORS.border}` }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold" style={{ color: BRAND_COLORS.navy }}>🎯 My Preferences</h3>
            {onEditProfile && (
              <button
                onClick={onEditProfile}
                className="text-xs font-medium px-3 py-1 rounded-lg border transition-all hover:opacity-80"
                style={{ borderColor: BRAND_COLORS.gold, color: BRAND_COLORS.gold }}
              >
                Edit
              </button>
            )}
          </div>
          <div className="space-y-4">
            <div>
              <p className="text-xs font-medium uppercase tracking-wider mb-2" style={{ color: BRAND_COLORS.textTertiary }}>
                Sectors of Interest
              </p>
              {partner.sectors?.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {partner.sectors.map(s => (
                    <span key={s} className="px-2.5 py-1 text-xs font-medium rounded-full text-white"
                      style={{ backgroundColor: BRAND_COLORS.navy }}>
                      {s}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm italic" style={{ color: BRAND_COLORS.textTertiary }}>No sectors selected — click Edit to add</p>
              )}
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wider mb-2" style={{ color: BRAND_COLORS.textTertiary }}>
                Geographic Focus
              </p>
              {(partner.geography?.continents?.length > 0 || partner.geography?.countries?.length > 0) ? (
                <div className="flex flex-wrap gap-1.5">
                  {(partner.geography.continents || []).map(c => (
                    <span key={c} className="px-2.5 py-1 text-xs font-medium rounded-full text-white"
                      style={{ backgroundColor: BRAND_COLORS.gold }}>
                      🌍 {c}
                    </span>
                  ))}
                  {(partner.geography.countries || []).map(c => (
                    <span key={c} className="px-2 py-0.5 text-xs rounded-full border"
                      style={{ borderColor: BRAND_COLORS.border, color: BRAND_COLORS.textSecondary }}>
                      {c}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm italic" style={{ color: BRAND_COLORS.textTertiary }}>No geography selected — click Edit to add</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Activity Summary */}
      <div className="p-6 rounded-xl" style={{ backgroundColor: BRAND_COLORS.surface, border: `1px solid ${BRAND_COLORS.border}` }}>
        <h3 className="text-lg font-bold mb-4" style={{ color: BRAND_COLORS.navy }}>
          📊 My Activity
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total Prospects', value: prospects.length, color: BRAND_COLORS.navy },
            { label: 'Approved', value: approvedProspects, color: '#10B981' },
            { label: 'Waiting', value: waitingProspects, color: '#F59E0B' },
            { label: 'Deals', value: myDeals.length, color: BRAND_COLORS.gold },
          ].map(({ label, value, color }) => (
            <div key={label} className="text-center p-3 rounded-lg" style={{ backgroundColor: BRAND_COLORS.background }}>
              <p className="text-2xl font-bold" style={{ color }}>{value}</p>
              <p className="text-xs mt-1" style={{ color: BRAND_COLORS.textSecondary }}>{label}</p>
            </div>
          ))}
        </div>
      </div>

      {!partner && (
        <div className="p-6 rounded-xl text-center" style={{ backgroundColor: '#FEF3C7', border: '1px solid #F59E0B' }}>
          <AlertCircle className="w-8 h-8 mx-auto mb-2" style={{ color: '#92400E' }} />
          <p className="font-medium" style={{ color: '#92400E' }}>
            Your account is not yet linked to a partner profile. Please contact your administrator.
          </p>
        </div>
      )}
    </div>
  );
};

// `draft` — a prospect row parked mid-wizard via Save & Close. When present the
// wizard seeds itself from that row and opens on the step it was left on.
const NewProspectModal = ({ onClose, onSubmit, onSaveDraft, draft = null, sectors = DEFAULT_SECTORS, partners = [], currentUserRole = 'Admin' }) => {
  const { isMobile } = useIsMobile();
  const [wizardStep, setWizardStep] = useState(draft?.draftStep || 1);
  const [prospectType, setProspectType] = useState(draft?.prospectType || 'Client');

  // ── Form config ────────────────────────────────────────────────────────────
  const [fieldCfg, setFieldCfg] = useState({});
  useEffect(() => {
    (async () => {
      try {
        const { supabase } = await import('./supabaseClient');
        const { data } = await supabase
          .from('form_field_config')
          .select('is_visible, is_required, role_permissions, custom_field_definitions!inner(field_name, entity_type)')
          .eq('form_type', 'add')
          .eq('custom_field_definitions.entity_type', 'prospect');
        if (data?.length) {
          const map = {};
          data.forEach(row => {
            map[row.custom_field_definitions.field_name] = {
              is_visible:       row.is_visible,
              is_required:      row.is_required,
              role_permissions: row.role_permissions || {},
            };
          });
          setFieldCfg(map);
        }
      } catch (_) {}
    })();
  }, []);
  const isVisible  = (fn) => {
    if (fieldCfg[fn]?.is_visible === false) return false;
    if (fieldCfg[fn]?.role_permissions?.[currentUserRole] === false) return false;
    return true;
  };
  const isRequired = (fn) => fieldCfg[fn]?.is_required  ?? (fn === 'name');
  const fieldLabel = (fn, defaultLabel) => isRequired(fn) ? `${defaultLabel} *` : defaultLabel;

  // Seeded from `draft` when resuming a parked wizard, otherwise blank.
  const [formData, setFormData] = useState({
    name: draft?.name || '',
    description: draft?.description || '',
    notes: draft?.notes || '',
    contractType: draft?.contractType || 'Direct',
    introducingPartnerId: draft?.introducingPartnerId || '',
    investmentFocus: draft?.investmentFocus || [],
    investmentMin: draft?.investmentMin || '',
    investmentMax: draft?.investmentMax || '',
    investmentCurrency: draft?.investmentCurrency || 'USD',
    investmentBodyType: draft?.investmentBodyType || '',
    preferredStage: draft?.preferredStage || '',
    serviceTypes: draft?.serviceTypes || [],
    fundraisingMin: draft?.fundraisingMin || '',
    fundraisingMax: draft?.fundraisingMax || '',
    potentialDescription: draft?.potentialDescription || '',
    websiteUrl: draft?.websiteUrl || '',
    linkedinPerson: draft?.linkedinPerson || '',
    linkedinCompany: draft?.linkedinCompany || '',
    investorsDeckUrl: draft?.investorsDeckUrl || '',
    investorDeckFileUrl: draft?.investorDeckFileUrl || '',
    investorDeckFileName: draft?.investorDeckFileName || '',
    investorDeckFileSize: draft?.investorDeckFileSize || 0,
    _investorDeckFile: null, // pending upload (transient — never persisted on a draft)
    stage: draft?.stage || '',
    targetRaise: draft?.targetRaise || '',
    currentValuation: draft?.currentValuation || '',
    revenue: draft?.revenue || '',
    sectors: draft?.sectors || [],
    countries: draft?.geography?.countries || [],
    geography: draft?.geography || { continents: [], countries: [] },
  });
  const [deckUploadError, setDeckUploadError] = useState('');

  const update = (field, value) => setFormData(f => ({ ...f, [field]: value }));

  const [newSectors, setNewSectors] = useState(draft?.sectors || []);
  const [newGeo, setNewGeo] = useState(draft?.geography?.continents ? draft.geography : { continents: [], countries: [] });

  const toggleItem = (field, item) => {
    setFormData(f => ({
      ...f,
      [field]: f[field].includes(item)
        ? f[field].filter(t => t !== item)
        : [...f[field], item]
    }));
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { window.alert('Please enter a name'); return; }
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      // Pre-generate the prospect id so the deck upload can use it as its folder.
      // Resuming a draft reuses its id so the row is completed, not duplicated.
      const prospectId = draft?.id || `prospect_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      let deckUrl  = formData.investorDeckFileUrl  || '';
      let deckName = formData.investorDeckFileName || '';
      let deckSize = formData.investorDeckFileSize || 0;

      if (formData._investorDeckFile) {
        try {
          const { uploadInvestorDeck } = await import('./supabaseClient');
          deckUrl  = await uploadInvestorDeck(prospectId, formData._investorDeckFile);
          deckName = formData._investorDeckFile.name;
          deckSize = formData._investorDeckFile.size;
        } catch (err) {
          setDeckUploadError(err.message || 'Failed to upload investor deck');
          setIsSubmitting(false);
          return;
        }
      }

      const { _investorDeckFile, ...rest } = formData;
      await onSubmit({
        ...rest,
        id: prospectId,
        investorDeckFileUrl:  deckUrl,
        investorDeckFileName: deckName,
        investorDeckFileSize: deckSize,
        prospectType,
        companyName: formData.name,
        // `sectors`/`countries` only land in formData once the user touches the
        // Sector/Geography selectors, so both may legitimately be absent.
        sector: (formData.sectors || []).join(', '),
        country: (formData.countries || []).join(', '),
        typicalInvestmentSize: formData.investmentMin || formData.investmentMax
          ? `${formData.investmentCurrency} ${formData.investmentMin ? Number(formData.investmentMin).toLocaleString('en-US') : ''}–${formData.investmentMax ? Number(formData.investmentMax).toLocaleString('en-US') : ''}`
          : ''
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Save & Close ───────────────────────────────────────────────────────────
  // Parks the wizard as a draft row. Only the name is enforced — it is what
  // identifies the draft in the Drafts list; every other required field is still
  // enforced when the wizard is finally submitted.
  // A pending deck upload is not pushed here: the upload needs the final id and
  // belongs to submit. The chosen file is simply re-attached on resume.
  const draftBlockedReason = formData.name.trim() ? '' : 'Enter the name first — it identifies the draft.';
  const handleSaveDraft = async () => {
    if (draftBlockedReason || isSubmitting) return;
    const { _investorDeckFile, ...rest } = formData;
    const ok = await onSaveDraft?.({
      ...rest,
      id: draft?.id || `prospect_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      prospectType,
      companyName: formData.name,
      sector: (formData.sectors || []).join(', '),
      country: (formData.countries || []).join(', '),
    }, wizardStep);
    if (ok) onClose();   // stay open if the write failed, so nothing is lost
  };

  const PROSPECT_TYPE_CARDS = [
    { type: 'Client',         label: 'Client',                desc: 'Company or individual seeking EvE services', Icon: Building2 },
    { type: 'Angel',          label: 'Angel Investor',         desc: 'Individual investing personal capital',       Icon: Users },
    { type: 'InvestmentBody', label: 'Investment Body',        desc: 'VC, PE, Family Office or fund',              Icon: Landmark },
    { type: 'Connection',     label: 'Interesting Connection', desc: 'Relationship with future potential',         Icon: Link },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={onClose}>
    <div className="bg-white rounded-lg shadow-xl border-2 w-full flex flex-col" style={{ borderColor: BRAND_COLORS.gold, maxWidth: isMobile ? '95vw' : 600, maxHeight: '90vh' }} onClick={e => e.stopPropagation()}>

      {/* Wizard Header */}
      <div className="px-6 pt-4 pb-2 flex items-center justify-between border-b border-gray-100 flex-shrink-0">
        <h2 className="text-lg font-bold" style={{ color: BRAND_COLORS.navy }}>+ New Prospect</h2>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">✕</button>
      </div>

      {/* Step Indicators */}
      <div className="px-6 pt-5 pb-4 border-b border-gray-100 flex-shrink-0" data-tour="prospect-steps">
        <div className="flex items-center justify-between">
          {[
            { n: 1, label: 'Prospect Type' },
            { n: 2, label: 'Basic Info' },
            { n: 3, label: 'Sector' },
            { n: 4, label: 'Geography' },
            { n: 5, label: 'Details' },
          ].map(({ n, label }, idx, arr) => (
            <React.Fragment key={n}>
              <button onClick={() => setWizardStep(n)} className="flex flex-col items-center gap-1">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all"
                  style={{
                    backgroundColor: wizardStep === n ? BRAND_COLORS.navy : wizardStep > n ? BRAND_COLORS.gold : '#E5E7EB',
                    color: wizardStep >= n ? 'white' : '#9CA3AF'
                  }}>
                  {wizardStep > n ? '✓' : n}
                </div>
                <span className={`text-xs font-medium whitespace-nowrap ${wizardStep === n ? 'inline' : 'hidden'} sm:inline`} style={{ color: wizardStep === n ? BRAND_COLORS.navy : '#9CA3AF' }}>{label}</span>
              </button>
              {idx < arr.length - 1 && (
                <div className="flex-1 h-0.5 mx-2 mb-4 rounded" style={{ backgroundColor: wizardStep > n ? BRAND_COLORS.gold : '#E5E7EB' }} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="overflow-y-auto flex-1">
      {/* Step 1: Prospect Type */}
      {wizardStep === 1 && (<>
      <div className="p-6 border-b border-gray-200">
        <h3 className="text-lg font-semibold mb-1" style={{ color: BRAND_COLORS.navy }}>Step 1: Prospect Type</h3>
        <p className="text-sm text-gray-500 mb-6">Select the type of prospect you are adding</p>
        <div className="grid grid-cols-2 gap-3" data-tour="prospect-type">
          {PROSPECT_TYPE_CARDS.map(({ type, label, desc, Icon }) => {
            const isSelected = prospectType === type;
            return (
              <button
                key={type}
                type="button"
                onClick={() => setProspectType(type)}
                className="py-4 px-5 rounded-xl border-2 transition-all text-left"
                style={{
                  borderColor: isSelected ? BRAND_COLORS.navy : '#e5e7eb',
                  backgroundColor: isSelected ? `${BRAND_COLORS.navy}0d` : 'white',
                  color: isSelected ? BRAND_COLORS.navy : '#374151'
                }}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Icon className="w-5 h-5 flex-shrink-0" style={{ color: isSelected ? BRAND_COLORS.navy : '#9CA3AF' }} />
                  <span className="font-bold text-sm">{label}</span>
                </div>
                <div className="text-xs" style={{ color: isSelected ? `${BRAND_COLORS.navy}99` : '#9CA3AF' }}>{desc}</div>
              </button>
            );
          })}
        </div>
      </div>
      <div className="px-6 pb-5 pt-4 flex justify-between sticky bottom-0 bg-white border-t border-gray-100 z-10">
        <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">Cancel</button>
        <WizardSaveClose onClick={handleSaveDraft} disabledReason={draftBlockedReason} />
        <button onClick={() => setWizardStep(2)} data-tour="prospect-next" className="px-6 py-2 rounded-lg font-medium text-white" style={{ backgroundColor: BRAND_COLORS.gold }}>Next →</button>
      </div>
      </>)}

      {/* Step 2: Basic Info */}
      {wizardStep === 2 && (<>
      <div className="p-6 border-b border-gray-200 space-y-4">
        <h3 className="text-lg font-semibold mb-1" style={{ color: BRAND_COLORS.navy }}>Step 2: Basic Info</h3>

        {isVisible('name') && (
          <Input
            label={fieldLabel('name', prospectType === 'Angel' ? 'Full Name' : prospectType === 'Connection' ? 'Name' : 'Company Name')}
            value={formData.name}
            onChange={e => update('name', e.target.value)}
          />
        )}

        {isVisible('website_url') && (
          <Input
            label="Website"
            value={formData.websiteUrl}
            onChange={e => update('websiteUrl', e.target.value)}
          />
        )}

        {/* About the client / description — all prospect types */}
        {isVisible('description') && (
          <Textarea
            label={prospectType === 'Client' ? 'About the client' : 'About'}
            value={formData.description}
            onChange={e => update('description', e.target.value)}
            placeholder={
              prospectType === 'Client'   ? 'Short description of the client, what they do, why they are interesting…' :
              prospectType === 'Angel'    ? 'Background, sectors of interest, check size, anything relevant…' :
              prospectType === 'Connection' ? 'Who they are and the relationship context…' :
              'What the firm does, focus, why they are relevant…'
            }
            rows={3}
          />
        )}

        {/* LinkedIn (Person) + LinkedIn (Company) — moved here from Step 5 */}
        {isVisible('linkedin_person') && (
          <Input
            label="LinkedIn (Person)"
            value={formData.linkedinPerson}
            onChange={e => update('linkedinPerson', e.target.value)}
            placeholder="https://linkedin.com/in/…"
          />
        )}
        {isVisible('linkedin_company') && (
          <Input
            label="LinkedIn (Company)"
            value={formData.linkedinCompany}
            onChange={e => update('linkedinCompany', e.target.value)}
            placeholder="https://linkedin.com/company/…"
          />
        )}

        {/* Contract Type */}
        {isVisible('contract_type') && currentUserRole !== 'Partner' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Contract Type</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'Direct', label: '📝 Direct', desc: 'EvE ↔ Prospect' },
                { value: 'ThroughPartner', label: '🔗 Through Partner', desc: 'EvE ↔ Partner' }
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => update('contractType', opt.value)}
                  className="py-2 px-3 text-sm rounded-lg border-2 text-left transition-all"
                  style={{
                    borderColor: formData.contractType === opt.value ? BRAND_COLORS.navy : '#e5e7eb',
                    backgroundColor: formData.contractType === opt.value ? `${BRAND_COLORS.navy}10` : 'white',
                    color: formData.contractType === opt.value ? BRAND_COLORS.navy : '#6b7280'
                  }}
                >
                  <div className="font-medium">{opt.label}</div>
                  <div className="text-xs opacity-70 mt-0.5">{opt.desc}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Introducing Partner */}
        {formData.contractType === 'ThroughPartner' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Introducing Partner</label>
            <select
              value={formData.introducingPartnerId || ''}
              onChange={e => update('introducingPartnerId', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">— Select Partner —</option>
              {partners.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>
      <div className="px-6 pb-5 pt-4 flex justify-between sticky bottom-0 bg-white border-t border-gray-100 z-10">
        <button onClick={() => setWizardStep(1)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">← Back</button>
        <WizardSaveClose onClick={handleSaveDraft} disabledReason={draftBlockedReason} />
        <button onClick={() => setWizardStep(3)} className="px-6 py-2 rounded-lg font-medium text-white" style={{ backgroundColor: BRAND_COLORS.gold }}>Next →</button>
      </div>
      </>)}

      {/* Step 3: Sector */}
      {wizardStep === 3 && (<>
      <div className="p-6 border-b border-gray-200 space-y-4">
        <h3 className="text-lg font-semibold mb-1" style={{ color: BRAND_COLORS.navy }}>Step 3: Sector</h3>
        {isVisible('sectors') && (
          <SectorSelector
            sectors={sectors}
            selectedSectors={newSectors}
            onSectorsChange={(s) => { setNewSectors(s); update('sectors', s); }}
                     />
        )}
      </div>
      <div className="px-6 pb-5 pt-4 flex justify-between sticky bottom-0 bg-white border-t border-gray-100 z-10">
        <button type="button" onClick={() => setWizardStep(2)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">← Back</button>
        <WizardSaveClose onClick={handleSaveDraft} disabledReason={draftBlockedReason} />
        <button onClick={() => setWizardStep(4)} className="px-6 py-2 rounded-lg font-medium text-white" style={{ backgroundColor: BRAND_COLORS.gold }}>Next →</button>
      </div>
      </>)}

      {/* Step 4: Geography */}
      {wizardStep === 4 && (<>
      <div className="p-6 border-b border-gray-200 space-y-4">
        <h3 className="text-lg font-semibold mb-1" style={{ color: BRAND_COLORS.navy }}>Step 4: Geography</h3>
        {isVisible('geography') && (
          <GeographySelector
            selectedGeo={newGeo}
            onGeoChange={(g) => { setNewGeo(g); update('countries', g.countries); update('geography', g); }}
          />
        )}
      </div>
      <div className="px-6 pb-5 pt-4 flex justify-between sticky bottom-0 bg-white border-t border-gray-100 z-10">
        <button type="button" onClick={() => setWizardStep(3)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">← Back</button>
        <WizardSaveClose onClick={handleSaveDraft} disabledReason={draftBlockedReason} />
        <button onClick={() => setWizardStep(5)} className="px-6 py-2 rounded-lg font-medium text-white" style={{ backgroundColor: BRAND_COLORS.gold }}>Next →</button>
      </div>
      </>)}

      {/* Step 5: Details */}
      {wizardStep === 5 && (<>
      <form onSubmit={handleSubmit}>
      <div className="p-6 border-b border-gray-200 space-y-4">
        <h3 className="text-lg font-semibold mb-1" style={{ color: BRAND_COLORS.navy }}>Step 5: Details</h3>

        {/* InvestmentBody only */}
        {isVisible('investment_body_type') && prospectType === 'InvestmentBody' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Investment Body Type</label>
            <div className="flex flex-wrap gap-2">
              {['VC', 'Family Office', 'Private Equity', 'Debt Provider', 'Other'].map(t => (
                <button
                  key={t}
                  type="button"
                  onClick={() => update('investmentBodyType', t)}
                  className="px-3 py-1 text-xs rounded-full border font-medium transition-all"
                  style={{
                    borderColor: formData.investmentBodyType === t ? '#0369a1' : '#e5e7eb',
                    backgroundColor: formData.investmentBodyType === t ? '#e0f2fe' : 'white',
                    color: formData.investmentBodyType === t ? '#0369a1' : '#6b7280'
                  }}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Angel & InvestmentBody */}
        {(prospectType === 'Angel' || prospectType === 'InvestmentBody') && (
          <>
            {isVisible('investment_focus') && (
              <MultiSelect
                label="Investment Focus"
                field="investmentFocus"
                options={sectors}
                color="#7c3aed"
                formData={formData}
                setFormData={setFormData}
              />
            )}
            {(isVisible('investment_min') || isVisible('investment_max')) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Typical Investment Size</label>
                <div className="flex gap-2 items-center">
                  <select
                    value={formData.investmentCurrency}
                    onChange={e => update('investmentCurrency', e.target.value)}
                    className="px-2 py-2 border border-gray-300 rounded-lg text-sm w-24"
                  >
                    <option value="USD">$ USD</option>
                    <option value="EUR">€ EUR</option>
                    <option value="GBP">£ GBP</option>
                    <option value="ILS">₪ ILS</option>
                  </select>
                  <MoneyInput value={formData.investmentMin} onChange={v => update('investmentMin', v)} placeholder="Minimum" symbol="" className="flex-1 text-sm" />
                  <span className="text-gray-400">—</span>
                  <MoneyInput value={formData.investmentMax} onChange={v => update('investmentMax', v)} placeholder="Maximum" symbol="" className="flex-1 text-sm" />
                </div>
              </div>
            )}
            {isVisible('preferred_stage') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Investment Stage</label>
                <select value={formData.preferredStage} onChange={e => update('preferredStage', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                  <option value="">— Select Stage —</option>
                  {['Seed', 'Series A', 'Series B', 'Growth', 'Pre-IPO'].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            )}
          </>
        )}

        {/* Client only */}
        {prospectType === 'Client' && (
          <>
            {/* Investor Deck — file upload (PDF / PPT / PPTX, up to 50 MB) */}
            {isVisible('investors_deck_url') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Investor Deck</label>
                {formData._investorDeckFile ? (
                  <div className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <FileText className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} />
                    <span className="flex-1 truncate" style={{ color: BRAND_COLORS.navy }}>{formData._investorDeckFile.name}</span>
                    <span className="text-xs text-gray-500">{(formData._investorDeckFile.size / 1024 / 1024).toFixed(2)} MB</span>
                    <button type="button" onClick={() => { update('_investorDeckFile', null); setDeckUploadError(''); }}
                      className="text-xs text-gray-500 hover:text-gray-900">Remove</button>
                  </div>
                ) : (
                  <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-lg text-sm cursor-pointer hover:bg-gray-50">
                    <Upload className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} />
                    <span className="text-gray-600">Choose a PDF or PowerPoint file</span>
                    <input
                      type="file"
                      accept=".pdf,.ppt,.pptx,application/pdf,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        setDeckUploadError('');
                        const ext = f.name.split('.').pop().toLowerCase();
                        if (!['pdf', 'ppt', 'pptx'].includes(ext)) {
                          setDeckUploadError('Only PDF, PPT, or PPTX files are allowed.');
                          e.target.value = '';
                          return;
                        }
                        if (f.size > 50 * 1024 * 1024) {
                          setDeckUploadError('File exceeds 50 MB limit.');
                          e.target.value = '';
                          return;
                        }
                        update('_investorDeckFile', f);
                      }}
                    />
                  </label>
                )}
                <p className="text-xs text-gray-500 mt-1">PDF, PPT or PPTX. Max 50 MB.</p>
                {deckUploadError && <p className="text-xs mt-1" style={{ color: BRAND_COLORS.statusError }}>{deckUploadError}</p>}
              </div>
            )}

            {isVisible('stage') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Stage</label>
                <select value={formData.stage} onChange={e => update('stage', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                  <option value="">— Select Stage —</option>
                  {['Seed', 'Series A', 'Series B', 'Growth', 'Pre-IPO'].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            )}
            {(isVisible('target_raise') || isVisible('current_valuation') || isVisible('revenue') || isVisible('fundraising_min') || isVisible('fundraising_max')) && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <label className="block text-sm font-medium text-gray-700">Currency</label>
                  <select value={formData.investmentCurrency} onChange={e => update('investmentCurrency', e.target.value)} className="px-2 py-1 border border-gray-300 rounded-lg text-sm w-24">
                    <option value="USD">$ USD</option>
                    <option value="EUR">€ EUR</option>
                    <option value="GBP">£ GBP</option>
                    <option value="ILS">₪ ILS</option>
                  </select>
                </div>
                {isVisible('target_raise') && <MoneyInput label="Target Raise" value={formData.targetRaise} onChange={v => update('targetRaise', v)} symbol={{ USD: '$', EUR: '€', ILS: '₪' }[formData.investmentCurrency] || '$'} />}
                {(isVisible('fundraising_min') || isVisible('fundraising_max')) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Fundraising Range</label>
                    <div className="flex gap-2 items-center">
                      <input
                        type="number"
                        value={formData.fundraisingMin}
                        onChange={e => update('fundraisingMin', e.target.value)}
                        placeholder="Minimum"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                      <span className="text-gray-400">—</span>
                      <input
                        type="number"
                        value={formData.fundraisingMax}
                        onChange={e => update('fundraisingMax', e.target.value)}
                        placeholder="Maximum"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                )}
                {isVisible('current_valuation') && <MoneyInput label="Current Valuation" value={formData.currentValuation} onChange={v => update('currentValuation', v)} symbol={{ USD: '$', EUR: '€', ILS: '₪' }[formData.investmentCurrency] || '$'} />}
                {isVisible('revenue') && <MoneyInput label="Revenue" value={formData.revenue} onChange={v => update('revenue', v)} symbol={{ USD: '$', EUR: '€', ILS: '₪' }[formData.investmentCurrency] || '$'} />}
              </div>
            )}
            {isVisible('service_types') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Service Type <span className="text-gray-400 font-normal">(multiple allowed)</span>
                </label>
                <div className="flex gap-2">
                  {[
                    { value: 'Retainer',      Icon: ClipboardList },
                    { value: 'Success-based', Icon: Target },
                    { value: 'Equity-based',  Icon: PieChart },
                  ].map(({ value, Icon }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => toggleItem('serviceTypes', value)}
                      className="flex-1 py-2 px-3 text-sm rounded-lg border-2 font-medium transition-all flex items-center justify-center gap-2"
                      style={{
                        borderColor: formData.serviceTypes.includes(value) ? BRAND_COLORS.navy : '#e5e7eb',
                        backgroundColor: formData.serviceTypes.includes(value) ? `${BRAND_COLORS.navy}15` : 'white',
                        color: formData.serviceTypes.includes(value) ? BRAND_COLORS.navy : '#6b7280'
                      }}
                    >
                      <Icon className="w-4 h-4" /> {value}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Connection only */}
        {isVisible('potential_description') && prospectType === 'Connection' && (
          <Textarea
            label="What is the potential of this connection?"
            value={formData.potentialDescription}
            onChange={e => update('potentialDescription', e.target.value)}
            placeholder="Describe what could come from this connection..."
            rows={3}
          />
        )}

        {/* LinkedIn fields moved to Step 2 (Basic Info). */}

        {isVisible('notes') && (
          <Textarea
            label="Additional Notes"
            value={formData.notes}
            onChange={e => update('notes', e.target.value)}
            rows={3}
          />
        )}
      </div>
      <div className="px-6 pb-5 pt-4 flex justify-between sticky bottom-0 bg-white border-t border-gray-100 z-10">
        <button type="button" onClick={() => setWizardStep(4)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">← Back</button>
        <button type="submit" disabled={isSubmitting} className="px-6 py-2 text-white rounded-lg transition-colors font-medium disabled:opacity-50" style={{ backgroundColor: BRAND_COLORS.navy }}>{isSubmitting ? 'Submitting…' : 'Submit Prospect'}</button>
      </div>
      </form>
      </>)}
      </div>

    </div>
    </div>
  );
};

const EditTeaserModal = ({ onClose, onSubmit, teaser, partners, prospects = [], sectors = DEFAULT_SECTORS, currentUserRole = 'Admin' }) => {
  const [wizardStep, setWizardStep] = useState(1);
  const [formData, setFormData] = useState({
    name: teaser.name || teaser.companyName || teaser.opportunityName || '',
    alias: teaser.alias || '',
    description: teaser.description || teaser.summary || '',
    prospectId: teaser.prospectId || '',
    targetRaise: teaser.targetRaise || '',
    raiseCurrency: teaser.raiseCurrency || 'USD',
    stage: teaser.stage || '',
  });

  // ── Form config ────────────────────────────────────────────────────────────
  const [fieldCfg, setFieldCfg] = useState({});
  useEffect(() => {
    (async () => {
      try {
        const { supabase } = await import('./supabaseClient');
        const { data } = await supabase
          .from('form_field_config')
          .select('is_visible, is_required, role_permissions, custom_field_definitions!inner(field_name, entity_type)')
          .eq('form_type', 'edit')
          .eq('custom_field_definitions.entity_type', 'teaser');
        if (data?.length) {
          const map = {};
          data.forEach(row => {
            map[row.custom_field_definitions.field_name] = {
              is_visible:       row.is_visible,
              is_required:      row.is_required,
              role_permissions: row.role_permissions || {},
            };
          });
          setFieldCfg(map);
        }
      } catch (_) {}
    })();
  }, []);
  const isVisible  = (fn) => {
    if (fieldCfg[fn]?.is_visible === false) return false;
    if (fieldCfg[fn]?.role_permissions?.[currentUserRole] === false) return false;
    return true;
  };
  const isRequired = (fn) => fieldCfg[fn]?.is_required ?? (fn === 'name');

  // Normalise visibility coming from DB — column may be 'all', a jsonb array,
  // a JSON string (if column is text type), null, or undefined.
  const _initVis = (() => {
    const v = teaser.visibility;
    if (!v || v === 'all') return 'all';
    if (Array.isArray(v)) return v;
    try { const p = JSON.parse(v); if (Array.isArray(p)) return p; } catch (_) {}
    return 'all';
  })();

  const [visibility, setVisibility] = useState(Array.isArray(_initVis) ? 'specific' : _initVis);
  const [selectedPartners, setSelectedPartners] = useState(Array.isArray(_initVis) ? _initVis : []);

  const [editSectors, setEditSectors] = useState(() => parseSectorsField(teaser.sectors));
  const [editGeo, setEditGeo] = useState(teaser.geography || { continents: [], countries: [] });
  const [editFilterSectors, setEditFilterSectors] = useState(() => parseSectorsField(teaser.sectors));
  const [sectorSource, setSectorSource] = useState('manual');
  const [geoSource, setGeoSource] = useState('manual');
  const [newFile, setNewFile] = useState(null);

  const linkedProspect = prospects.find(p => p.id === formData.prospectId) || null;

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== 'application/pdf') { window.alert('Please select a PDF file'); return; }
    if (file.size > 10 * 1024 * 1024) { window.alert('File size must be less than 10MB'); return; }
    setNewFile({ rawFile: file, name: file.name, size: file.size });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    let visibilityValue;
    if (visibility === 'all') {
      visibilityValue = 'all';
    } else if (visibility === 'sectors') {
      visibilityValue = partners
        .filter(p => p.status === 'Active' && (p.sectors || []).some(s => editFilterSectors.includes(s)))
        .map(p => p.id);
    } else {
      visibilityValue = selectedPartners;
    }
    onSubmit(teaser.id, {
      ...formData,
      sectors: editSectors,
      geography: editGeo,
      ...(newFile ? { _newRawFile: newFile.rawFile, _newFileName: newFile.name, _newFileSize: newFile.size } : {})
    }, visibilityValue);
  };

  // ── Save & Close ───────────────────────────────────────────────────────────
  // The teaser is already live, so there is no draft to flag: commit the edits
  // made so far and close. Visibility is only recomputed on the final step
  // (Distribution), so the existing value is preserved here rather than being
  // narrowed by half-made selections. A newly picked PDF still uploads.
  const draftBlockedReason = formData.name?.trim() ? '' : 'The teaser name cannot be blank.';
  const handleSaveDraft = () => {
    if (draftBlockedReason) return;
    onSubmit(teaser.id, {
      ...formData,
      sectors: editSectors,
      geography: editGeo,
      ...(newFile ? { _newRawFile: newFile.rawFile, _newFileName: newFile.name, _newFileSize: newFile.size } : {})
    }, teaser.visibility);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={onClose}>
    <div className="bg-white rounded-lg shadow-xl border-2 w-full flex flex-col" style={{ borderColor: BRAND_COLORS.gold, maxWidth: 560, maxHeight: '90vh' }} onClick={e => e.stopPropagation()}>
      {/* Wizard Header */}
      <div className="px-6 pt-4 pb-2 flex items-center justify-between border-b border-gray-100 flex-shrink-0">
        <h2 className="text-lg font-bold" style={{ color: BRAND_COLORS.navy }}>Edit Teaser</h2>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">✕</button>
      </div>

      {/* Step Indicators */}
      <div className="px-6 pt-5 pb-4 border-b border-gray-100 flex-shrink-0">
        <div className="flex items-center justify-between">
          {[
            { n: 1, label: 'Teaser Info' },
            { n: 2, label: 'Sector' },
            { n: 3, label: 'Geography' },
            { n: 4, label: 'Distribution' },
          ].map(({ n, label }, idx, arr) => (
            <React.Fragment key={n}>
              <button onClick={() => setWizardStep(n)} className="flex flex-col items-center gap-1">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all"
                  style={{
                    backgroundColor: wizardStep === n ? BRAND_COLORS.navy : wizardStep > n ? BRAND_COLORS.gold : '#E5E7EB',
                    color: wizardStep >= n ? 'white' : '#9CA3AF'
                  }}>
                  {wizardStep > n ? '✓' : n}
                </div>
                <span className={`text-xs font-medium whitespace-nowrap ${wizardStep === n ? 'inline' : 'hidden'} sm:inline`} style={{ color: wizardStep === n ? BRAND_COLORS.navy : '#9CA3AF' }}>{label}</span>
              </button>
              {idx < arr.length - 1 && (
                <div className="flex-1 h-0.5 mx-2 mb-4 rounded" style={{ backgroundColor: wizardStep > n ? BRAND_COLORS.gold : '#E5E7EB' }} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="overflow-y-auto flex-1">
      {/* Step 1: Teaser Info */}
      {wizardStep === 1 && (<>
      <div className="p-6 border-b border-gray-200 space-y-4">
        <h3 className="text-lg font-semibold mb-1" style={{ color: BRAND_COLORS.navy }}>Step 1: Teaser Info</h3>
        <div className="p-3 rounded-lg text-sm" style={{ backgroundColor: BRAND_COLORS.cream, borderLeft: `3px solid ${BRAND_COLORS.gold}` }}>
          <p className="font-medium" style={{ color: BRAND_COLORS.navy }}>Current Version: v{teaser.version}</p>
          <p className="text-xs text-gray-600 mt-1">Saving changes will increment version to v{(parseFloat(teaser.version) + 0.1).toFixed(1)}</p>
        </div>
        {isVisible('prospect_id') && prospects.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Linked Prospect <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <select
              value={formData.prospectId}
              onChange={e => setFormData({ ...formData, prospectId: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
            >
              <option value="">— Not linked to a prospect —</option>
              {prospects.filter(p => p.approvalStatus === 'Approved').map(p => (
                <option key={p.id} value={p.id}>{p.companyName}</option>
              ))}
            </select>
            {formData.prospectId && (
              <p className="text-xs text-green-700 mt-1 flex items-center gap-1">
                <CheckCircle className="w-3 h-3" /> Teaser linked to this prospect
              </p>
            )}
          </div>
        )}
        {isVisible('name') && (
          <Input
            label={isRequired('name') ? 'Teaser Name *' : 'Teaser Name'}
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required={isRequired('name')}
          />
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Client Alias <span className="text-xs font-normal text-gray-400">(optional — shown to partners instead of the real client name)</span>
          </label>
          <input
            type="text"
            value={formData.alias}
            onChange={(e) => setFormData({ ...formData, alias: e.target.value })}
            placeholder="e.g., Israeli B2B SaaS — Series A"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Stage <span className="text-xs font-normal text-gray-400">(optional)</span>
            </label>
            <select
              value={formData.stage}
              onChange={(e) => setFormData({ ...formData, stage: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none"
            >
              <option value="">— Select stage —</option>
              {['Pre-Seed','Seed','Series A','Series B','Series C+','Growth','Late Stage','IPO-Ready','Other'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Target Raise <span className="text-xs font-normal text-gray-400">(optional)</span>
            </label>
            <div className="flex gap-1.5">
              <select
                value={formData.raiseCurrency}
                onChange={(e) => setFormData({ ...formData, raiseCurrency: e.target.value })}
                className="px-2 py-2 border border-gray-300 rounded-lg text-sm w-20 flex-shrink-0"
              >
                {['USD','EUR','GBP','ILS'].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <input
                type="number"
                min="0"
                value={formData.targetRaise}
                onChange={(e) => setFormData({ ...formData, targetRaise: e.target.value })}
                placeholder="e.g. 2500000"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none"
              />
            </div>
          </div>
        </div>
        {isVisible('description') && (
          <Textarea label="Description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} />
        )}
        {isVisible('file') && <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Replace PDF <span className="text-xs font-normal text-gray-400">(optional — leave blank to keep current)</span>
          </label>
          <label className="flex items-center gap-3 px-4 py-3 rounded-lg border-2 border-dashed cursor-pointer transition-all"
            style={{ borderColor: newFile ? BRAND_COLORS.gold : '#e5e7eb' }}>
            <input type="file" accept=".pdf" className="hidden" onChange={handleFileChange} />
            {newFile ? (
              <span className="text-sm font-medium" style={{ color: BRAND_COLORS.navy }}>✅ {newFile.name}</span>
            ) : (
              <span className="text-sm text-gray-500">📄 Click to select new PDF...</span>
            )}
          </label>
        </div>}
      </div>
      <div className="px-6 pb-5 pt-4 flex justify-between sticky bottom-0 bg-white border-t border-gray-100 z-10">
        <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">Cancel</button>
        <WizardSaveClose onClick={handleSaveDraft} disabledReason={draftBlockedReason} />
        <button onClick={() => setWizardStep(2)} className="px-6 py-2 rounded-lg font-medium text-white" style={{ backgroundColor: BRAND_COLORS.gold }}>Next →</button>
      </div>
      </>)}

      {/* Step 2: Sector */}
      {wizardStep === 2 && (<>
      <div className="p-6 border-b border-gray-200 space-y-4">
        <h3 className="text-lg font-semibold mb-1" style={{ color: BRAND_COLORS.navy }}>Step 2: Sector</h3>
        {linkedProspect && (
          <div className="flex gap-2">
            <button type="button"
              onClick={() => { setSectorSource('prospect'); setEditSectors(parseSectorsField(linkedProspect.sectors)); }}
              className="flex-1 py-2 px-3 text-sm rounded-lg border-2 font-medium transition-all"
              style={{ borderColor: sectorSource === 'prospect' ? BRAND_COLORS.navy : '#e5e7eb', backgroundColor: sectorSource === 'prospect' ? `${BRAND_COLORS.navy}15` : 'white', color: sectorSource === 'prospect' ? BRAND_COLORS.navy : '#6b7280' }}>
              📎 Pull from prospect
            </button>
            <button type="button"
              onClick={() => setSectorSource('manual')}
              className="flex-1 py-2 px-3 text-sm rounded-lg border-2 font-medium transition-all"
              style={{ borderColor: sectorSource === 'manual' ? BRAND_COLORS.navy : '#e5e7eb', backgroundColor: sectorSource === 'manual' ? `${BRAND_COLORS.navy}15` : 'white', color: sectorSource === 'manual' ? BRAND_COLORS.navy : '#6b7280' }}>
              ✏️ Enter manually
            </button>
          </div>
        )}
        {isVisible('sectors') && (sectorSource === 'prospect' ? (
          <div className="rounded-lg p-3 bg-gray-50 border border-gray-200">
            {editSectors.length > 0
              ? <div className="flex flex-wrap gap-2">{editSectors.map(s => <span key={s} className="px-3 py-1 rounded-full text-sm font-medium" style={{ backgroundColor: `${BRAND_COLORS.gold}25`, color: BRAND_COLORS.navy }}>{s}</span>)}</div>
              : <p className="text-sm text-gray-400 italic">Prospect has no sectors defined — switch to "Enter manually" to add them.</p>}
          </div>
        ) : (
          <SectorSelector sectors={sectors} selectedSectors={editSectors} onSectorsChange={setEditSectors} />
        ))}
      </div>
      <div className="px-6 pb-5 pt-4 flex justify-between sticky bottom-0 bg-white border-t border-gray-100 z-10">
        <button type="button" onClick={() => setWizardStep(1)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">← Back</button>
        <WizardSaveClose onClick={handleSaveDraft} disabledReason={draftBlockedReason} />
        <button onClick={() => setWizardStep(3)} className="px-6 py-2 rounded-lg font-medium text-white" style={{ backgroundColor: BRAND_COLORS.gold }}>Next →</button>
      </div>
      </>)}

      {/* Step 3: Geography */}
      {wizardStep === 3 && (<>
      <div className="p-6 border-b border-gray-200 space-y-4">
        <h3 className="text-lg font-semibold mb-1" style={{ color: BRAND_COLORS.navy }}>Step 3: Geography</h3>
        {linkedProspect && (
          <div className="flex gap-2">
            <button type="button"
              onClick={() => { setGeoSource('prospect'); setEditGeo(parseGeographyField(linkedProspect.geography)); }}
              className="flex-1 py-2 px-3 text-sm rounded-lg border-2 font-medium transition-all"
              style={{ borderColor: geoSource === 'prospect' ? BRAND_COLORS.navy : '#e5e7eb', backgroundColor: geoSource === 'prospect' ? `${BRAND_COLORS.navy}15` : 'white', color: geoSource === 'prospect' ? BRAND_COLORS.navy : '#6b7280' }}>
              📎 Pull from prospect
            </button>
            <button type="button"
              onClick={() => setGeoSource('manual')}
              className="flex-1 py-2 px-3 text-sm rounded-lg border-2 font-medium transition-all"
              style={{ borderColor: geoSource === 'manual' ? BRAND_COLORS.navy : '#e5e7eb', backgroundColor: geoSource === 'manual' ? `${BRAND_COLORS.navy}15` : 'white', color: geoSource === 'manual' ? BRAND_COLORS.navy : '#6b7280' }}>
              ✏️ Enter manually
            </button>
          </div>
        )}
        {isVisible('geography') && (geoSource === 'prospect' ? (
          <div className="rounded-lg p-3 bg-gray-50 border border-gray-200">
            {(editGeo.continents?.length > 0 || editGeo.countries?.length > 0) ? (
              <div className="text-sm text-gray-700 space-y-1">
                {editGeo.continents?.length > 0 && <p><span className="font-medium">Continents:</span> {editGeo.continents.join(', ')}</p>}
                {editGeo.countries?.length > 0 && <p><span className="font-medium">Countries:</span> {editGeo.countries.join(', ')}</p>}
              </div>
            ) : <p className="text-sm text-gray-400 italic">Prospect has no geography defined — go back to Step 2 and switch to "Enter manually".</p>}
          </div>
        ) : (
          <GeographySelector selectedGeo={editGeo} onGeoChange={setEditGeo} />
        ))}
      </div>
      <div className="px-6 pb-5 pt-4 flex justify-between sticky bottom-0 bg-white border-t border-gray-100 z-10">
        <button type="button" onClick={() => setWizardStep(2)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">← Back</button>
        <WizardSaveClose onClick={handleSaveDraft} disabledReason={draftBlockedReason} />
        <button onClick={() => setWizardStep(4)} className="px-6 py-2 rounded-lg font-medium text-white" style={{ backgroundColor: BRAND_COLORS.gold }}>Next →</button>
      </div>
      </>)}

      {/* Step 4: Distribution */}
      {wizardStep === 4 && (<>
      <form onSubmit={handleSubmit}>
      <div className="p-6 border-b border-gray-200 space-y-4">
        <h3 className="text-lg font-semibold mb-1" style={{ color: BRAND_COLORS.navy }}>Step 4: Distribution</h3>
        <p className="text-sm text-gray-500">Choose who can see this teaser</p>
        {isVisible('visibility') && <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Visibility</label>
          <div className="space-y-2">
            <label className="flex items-center">
              <input type="radio" checked={visibility === 'all'} onChange={() => setVisibility('all')} className="mr-2" />
              <span className="text-sm text-gray-700">All MCA Partners</span>
            </label>
            <label className="flex items-center">
              <input type="radio" checked={visibility === 'sectors'} onChange={() => {
                setVisibility('sectors');
                if (editFilterSectors.length === 0 && editSectors.length > 0) setEditFilterSectors(editSectors);
              }} className="mr-2" />
              <span className="text-sm text-gray-700">MCA Partners interested in specific sectors</span>
            </label>
            <label className="flex items-center">
              <input type="radio" checked={visibility === 'specific'} onChange={() => setVisibility('specific')} className="mr-2" />
              <span className="text-sm text-gray-700">Specific Partners</span>
            </label>
          </div>

          {visibility === 'sectors' && (() => {
            const matchingPartners = partners.filter(p =>
              p.status === 'Active' && (p.sectors || []).some(s => editFilterSectors.includes(s))
            );
            return (
              <div className="mt-3 space-y-3">
                <div className="border rounded-lg p-3" style={{ borderColor: BRAND_COLORS.border, backgroundColor: BRAND_COLORS.background }}>
                  <p className="text-xs font-medium text-gray-700 mb-2">Pick sectors — partners with matching focus will receive this teaser:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {sectors.map(s => {
                      const active = editFilterSectors.includes(s);
                      return (
                        <button key={s} type="button"
                          onClick={() => active ? setEditFilterSectors(editFilterSectors.filter(x => x !== s)) : setEditFilterSectors([...editFilterSectors, s])}
                          className="px-2.5 py-1 rounded-full text-xs font-medium border transition-colors"
                          style={{ borderColor: active ? BRAND_COLORS.gold : BRAND_COLORS.border, backgroundColor: active ? `${BRAND_COLORS.gold}20` : 'white', color: active ? BRAND_COLORS.navy : '#6b7280' }}>
                          {s}
                        </button>
                      );
                    })}
                  </div>
                  {editSectors.length > 0 && editFilterSectors.join('|') !== editSectors.join('|') && (
                    <button type="button" onClick={() => setEditFilterSectors(editSectors)} className="mt-2 text-xs underline" style={{ color: BRAND_COLORS.gold }}>
                      Reset to teaser's sectors ({editSectors.join(', ')})
                    </button>
                  )}
                </div>
                <div className="border rounded-lg p-3" style={{ borderColor: BRAND_COLORS.border, backgroundColor: BRAND_COLORS.background }}>
                  <p className="text-xs font-medium text-gray-700 mb-2">
                    {editFilterSectors.length === 0 ? 'No sectors picked yet.' : `${matchingPartners.length} active partner${matchingPartners.length === 1 ? '' : 's'} match`}
                  </p>
                  {matchingPartners.length === 0 && editFilterSectors.length > 0 && (
                    <p className="text-xs text-amber-600">⚠️ No active partners are interested in these sectors — this teaser will not be visible to anyone.</p>
                  )}
                  {matchingPartners.length > 0 && (
                    <ul className="text-sm text-gray-700 space-y-0.5 max-h-40 overflow-y-auto">
                      {matchingPartners.map(p => (
                        <li key={p.id}>• {p.name} <span className="text-xs text-gray-400">— {(p.sectors || []).filter(s => editFilterSectors.includes(s)).join(', ')}</span></li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            );
          })()}

          {visibility === 'specific' && (
            <div className="mt-3 space-y-2 max-h-60 overflow-y-auto border rounded-lg p-3" style={{ borderColor: BRAND_COLORS.border, backgroundColor: BRAND_COLORS.background }}>
              {partners.filter(p => p.status === 'Active' || selectedPartners.includes(p.id)).length === 0 ? (
                <p className="text-xs text-gray-500">No active partners available.</p>
              ) : (
                partners.filter(p => p.status === 'Active' || selectedPartners.includes(p.id)).map(partner => (
                  <label key={partner.id} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedPartners.includes(partner.id)}
                      onChange={(e) => {
                        if (e.target.checked) setSelectedPartners([...selectedPartners, partner.id]);
                        else setSelectedPartners(selectedPartners.filter(id => id !== partner.id));
                      }}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">{partner.name}</span>
                  </label>
                ))
              )}
              {selectedPartners.length === 0 && (
                <p className="text-xs text-amber-600 mt-2">⚠️ No partners selected — this teaser will not be visible to anyone.</p>
              )}
            </div>
          )}
        </div>}
      </div>
      <div className="px-6 pb-5 pt-4 flex justify-between sticky bottom-0 bg-white border-t border-gray-100 z-10">
        <button type="button" onClick={() => setWizardStep(3)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">← Back</button>
        <button type="submit" className="px-6 py-2 rounded-lg font-medium text-white" style={{ backgroundColor: BRAND_COLORS.navy }}>Save Changes</button>
      </div>
      </form>
      </>)}
      </div>
    </div>
    </div>
  );
};

const IntroModal = ({ prospect, onClose, onSubmit }) => {
  const [introDate, setIntroDate] = useState(new Date().toISOString().split('T')[0]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(introDate);
  };

  return (
    <Modal title="Log Introduction" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <p className="text-sm text-gray-600 mb-4">
            Logging intro for: <span className="font-semibold text-gray-900">{prospect.companyName}</span>
          </p>
          {prospect.introDeadline && (
            <p className="text-sm text-gray-600 mb-4">
              Intro Deadline: <span className="font-semibold">{formatDate(prospect.introDeadline)}</span>
            </p>
          )}
        </div>
        
        <Input
          label="Introduction Date"
          type="date"
          value={introDate}
          onChange={(e) => setIntroDate(e.target.value)}
          required
        />
        
        <div className="flex space-x-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="flex-1 px-4 py-2 bg-[#1A2C5C] text-white rounded-lg hover:bg-[#0F1E42] transition-colors"
          >
            Log Intro
          </button>
        </div>
      </form>
    </Modal>
  );
};

// ============================================================================
// UTILITY COMPONENTS
// ============================================================================

// ─── COMPONENT: HotTopicModal ────────────────────────────────────────────────
// Announcement popup shown to targeted partners on login. Body renders as
// PLAIN TEXT (pre-wrap) — never HTML — so a compromised editor account can't
// inject markup/scripts into partners' browsers. The CTA renders only when the
// linked teaser is in the partner's OWN visible teaser list (defense-in-depth:
// no link leakage to teasers outside their RLS scope).
const HotTopicModal = ({ topic, teasers = [], onClose, onDismissForever, onViewTeaser }) => {
  if (!topic) return null;
  const linkedTeaser = topic.refTeaserId ? teasers.find(t => t.id === topic.refTeaserId) : null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full overflow-hidden"
        style={{ maxWidth: 520, borderLeft: `4px solid ${BRAND_COLORS.gold}`, border: `1px solid ${BRAND_COLORS.borderNavy12}` }}
        onClick={e => e.stopPropagation()}>
        {/* Navy header */}
        <div style={{ background: BRAND_COLORS.navy, padding: '18px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
            <span style={{ fontSize: '20px' }}>📣</span>
            <h2 style={{ margin: 0, fontSize: '17px', fontWeight: 700, color: 'white', lineHeight: 1.3 }}>{topic.title}</h2>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white text-xl font-bold flex-shrink-0 ml-3">✕</button>
        </div>
        {/* Body — plain text only */}
        <div style={{ padding: '20px 24px' }}>
          {topic.body && (
            <p style={{ fontSize: '14px', color: BRAND_COLORS.textPrimary, lineHeight: 1.55, margin: 0, whiteSpace: 'pre-wrap' }}>
              {topic.body}
            </p>
          )}
          {linkedTeaser && (
            <button
              onClick={() => onViewTeaser(linkedTeaser)}
              className="mt-4 w-full py-2.5 rounded-lg font-semibold text-sm transition-colors"
              style={{ backgroundColor: BRAND_COLORS.gold, color: 'white' }}>
              {topic.ctaLabel || `View Teaser: ${linkedTeaser.alias || linkedTeaser.name} →`}
            </button>
          )}
        </div>
        {/* Footer */}
        <div style={{ padding: '12px 24px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button onClick={() => onDismissForever(topic.id)}
            className="text-xs hover:underline" style={{ color: BRAND_COLORS.textTertiary }}>
            Don't show this again
          </button>
          <button onClick={onClose}
            className="px-5 py-2 rounded-lg text-sm font-semibold"
            style={{ backgroundColor: BRAND_COLORS.navy, color: 'white' }}>
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

const PasswordResetModal = ({ onClose }) => {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setSubmitting(true);
    try {
      const { supabase } = await import('./supabaseClient');
      const { error: err } = await supabase.auth.updateUser({ password });
      if (err) { setError(err.message); setSubmitting(false); return; }
      setDone(true);
      setTimeout(onClose, 1500);
    } catch (ex) {
      setError(ex.message || 'Password update failed');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md p-6">
        <h2 className="text-xl font-bold mb-2" style={{ color: BRAND_COLORS.navy }}>Set a New Password</h2>
        <p className="text-sm text-gray-600 mb-5">Enter a new password for your EvE Partners Portal account. Once submitted, you'll be logged in automatically.</p>
        {done ? (
          <div className="flex items-center gap-2 text-green-700 py-4">
            <CheckCircle className="w-5 h-5" /> Password updated. Redirecting…
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
                autoFocus
                required
                minLength={8}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
                required
                minLength={8}
              />
            </div>
            {error && <div className="text-sm text-red-600">{error}</div>}
            <button
              type="submit"
              disabled={submitting}
              className="w-full px-4 py-2 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
              style={{ backgroundColor: BRAND_COLORS.gold }}
            >
              {submitting ? 'Setting…' : 'Set Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

const Modal = ({ title, children, onClose }) => {
  // Handle ESC key to close
  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Prevent body scroll when modal is open
  React.useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg shadow-xl w-full max-w-lg md:max-w-xl lg:max-w-2xl my-auto flex flex-col"
        style={{ maxHeight: 'calc(100vh - 2rem)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - Fixed at top */}
        <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex justify-between items-center rounded-t-lg">
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900 pr-4">{title}</h3>
          <button
            onClick={onClose}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors p-1"
            title="Close (ESC)"
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>
        
        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
          {children}
        </div>
      </div>
    </div>
  );
};

const Input = ({ label, ...props }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <input
      {...props}
      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A2C5C] focus:border-transparent"
    />
  </div>
);

const MoneyInput = ({ label, value, onChange, placeholder, symbol = '$', className = '' }) => {
  const [focused, setFocused] = React.useState(false);
  const raw = String(value ?? '').replace(/,/g, '');
  const displayValue = focused ? raw : (raw ? Number(raw).toLocaleString('en-US') : '');
  const inner = (
    <div className={`relative ${!label ? className : ''}`}>
      {symbol && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium pointer-events-none select-none">
          {symbol}
        </span>
      )}
      <input
        type="text"
        inputMode="numeric"
        className={`w-full ${symbol ? 'pl-6' : 'px-3'} pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A2C5C] focus:border-transparent`}
        value={displayValue}
        placeholder={placeholder || '0'}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={e => onChange(e.target.value.replace(/[^0-9]/g, ''))}
      />
    </div>
  );
  if (!label) return inner;
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {inner}
    </div>
  );
};

// WYSIWYG Editor Component
const Textarea = ({ label, ...props }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <textarea
      {...props}
      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A2C5C] focus:border-transparent"
    />
  </div>
);

const Select = ({ label, options, ...props }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <select
      {...props}
      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A2C5C] focus:border-transparent"
    >
      {options.map(option => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </div>
);

const FilterButton = ({ active, onClick, label }) => (
  <button
    onClick={onClick}
    className="px-4 py-2 rounded-lg font-medium transition-all shadow-sm"
    style={{
      backgroundColor: active ? BRAND_COLORS.navy : '#F3F4F6',
      color: active ? 'white' : '#374151'
    }}
    onMouseEnter={(e) => {
      if (!active) e.currentTarget.style.backgroundColor = BRAND_COLORS.cream;
    }}
    onMouseLeave={(e) => {
      if (!active) e.currentTarget.style.backgroundColor = '#F3F4F6';
    }}
  >
    {label}
  </button>
);

// ============================================================================
// COMPONENT: StatusChip — canonical chip for all status / pill / tag uses
// ============================================================================
// Five flat tone variants (no rgba on navy/gold per palette spec):
//   success  — Active / Approved / Closed-won
//   warning  — Pending / countdown / In Tail (legacy alias maps here)
//   error    — Rejected / Terminated / Closed-lost
//   info     — Draft / In Review / Informational
//   neutral  — Default / sector tags / generic labels (cream + navy)
// Use this everywhere instead of inline bg/colour combos.

const STATUS_CHIP_VARIANTS = {
  success: { bg: '#D1FAE5', color: '#065F46' },
  warning: { bg: '#FEF3C7', color: '#92400E' },
  error:   { bg: '#FEE2E2', color: '#991B1B' },
  info:    { bg: '#DBEAFE', color: '#1E40AF' },
  neutral: { bg: '#FFF8DC', color: '#0A1428' },
};

const StatusChip = ({ variant = 'neutral', children, className = '', style = {} }) => {
  const tone = STATUS_CHIP_VARIANTS[variant] || STATUS_CHIP_VARIANTS.neutral;
  return (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${className}`}
      style={{ backgroundColor: tone.bg, color: tone.color, ...style }}
    >
      {children}
    </span>
  );
};

// ── Status → variant maps (single source of truth for status label → chip tone) ──
const PARTNER_STATUS_VARIANT = {
  'Active':     'success',
  'In Tail':    'info',      // per spec table: In Tail = Info tone (blue)
  'Terminated': 'error',
};

const PROSPECT_STATUS_VARIANT = {
  'Pending':              'warning',
  'Waiting for Approval': 'warning',
  'Approved':             'success',
  'Rejected':             'error',
};

const StatusBadge = ({ status }) => (
  <StatusChip variant={PARTNER_STATUS_VARIANT[status] || 'neutral'}>{status}</StatusChip>
);

const ProspectStatusBadge = ({ status }) => (
  <StatusChip variant={PROSPECT_STATUS_VARIANT[status] || 'neutral'}>{status}</StatusChip>
);

// ============================================================================
// COMPONENT: Global Search Bar
// ============================================================================

const GlobalSearchBar = ({ searchTerm, onSearchChange, filters, onFilterChange, onClearFilters, activeFilterCount, partners, entityType, sectors = DEFAULT_SECTORS }) => {
  const [showFilters, setShowFilters] = useState(false);

  // Sectors come from the canonical config (passed prop). Used for prospects + teasers.
  const sectorOptions = (sectors || []).map(s => typeof s === 'string' ? s : s?.name).filter(Boolean);
  const continentOptions = Object.keys(WORLD_GEOGRAPHY);

  return (
    <div className="bg-white rounded-lg shadow p-4 space-y-4">
      {/* Search Input */}
      <div className="flex space-x-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search across all data..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A2C5C] focus:border-transparent"
          />
        </div>
        
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center space-x-2 px-4 py-2 border rounded-lg transition-colors ${
            showFilters ? 'bg-[#FFEEEE] border-[#1A2C5C] text-[#1A2C5C]' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
          }`}
        >
          <Filter className="w-5 h-5" />
          <span>Filters</span>
          {activeFilterCount > 0 && (
            <span className="ml-1 bg-[#1A2C5C] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {activeFilterCount}
            </span>
          )}
        </button>
        
        {activeFilterCount > 0 && (
          <button
            onClick={onClearFilters}
            className="px-4 py-2 text-gray-600 hover:text-gray-900"
          >
            Clear
          </button>
        )}
      </div>

      {/* Advanced Filters */}
      {showFilters && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t">
          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
            <div className="space-y-2">
              <input
                type="date"
                value={filters.dateRange.start}
                onChange={(e) => onFilterChange({
                  ...filters,
                  dateRange: { ...filters.dateRange, start: e.target.value }
                })}
                className="w-full px-3 py-1 text-sm border border-gray-300 rounded"
                placeholder="Start date"
              />
              <input
                type="date"
                value={filters.dateRange.end}
                onChange={(e) => onFilterChange({
                  ...filters,
                  dateRange: { ...filters.dateRange, end: e.target.value }
                })}
                className="w-full px-3 py-1 text-sm border border-gray-300 rounded"
                placeholder="End date"
              />
            </div>
          </div>

          {/* Partner Filter */}
          {entityType === 'prospects' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Partners</label>
              <div className="max-h-32 overflow-y-auto space-y-1 border border-gray-300 rounded p-2">
                {partners.map(partner => (
                  <label key={partner.id} className="flex items-center text-sm">
                    <input
                      type="checkbox"
                      checked={filters.partners.includes(partner.id)}
                      onChange={(e) => {
                        const newPartners = e.target.checked
                          ? [...filters.partners, partner.id]
                          : filters.partners.filter(id => id !== partner.id);
                        onFilterChange({ ...filters, partners: newPartners });
                      }}
                      className="mr-2"
                    />
                    <span className="text-gray-700">{partner.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Status Filter — partners + prospects only (teasers have no status) */}
          {entityType !== 'teasers' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <div className="space-y-1">
                {(entityType === 'prospects'
                  ? ['Pending', 'Approved', 'Rejected']
                  : ['Active', 'In Tail', 'Terminated']
                ).map(status => (
                  <label key={status} className="flex items-center text-sm">
                    <input
                      type="checkbox"
                      checked={filters.statuses.includes(status)}
                      onChange={(e) => {
                        const newStatuses = e.target.checked
                          ? [...filters.statuses, status]
                          : filters.statuses.filter(s => s !== status);
                        onFilterChange({ ...filters, statuses: newStatuses });
                      }}
                      className="mr-2"
                    />
                    <span className="text-gray-700">{status}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Sector Filter — prospects + teasers */}
          {(entityType === 'prospects' || entityType === 'teasers') && sectorOptions.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sector</label>
              <div className="max-h-32 overflow-y-auto space-y-1 border border-gray-300 rounded p-2">
                {sectorOptions.map(sector => (
                  <label key={sector} className="flex items-center text-sm">
                    <input
                      type="checkbox"
                      checked={filters.sectors.includes(sector)}
                      onChange={(e) => {
                        const newSectors = e.target.checked
                          ? [...filters.sectors, sector]
                          : filters.sectors.filter(s => s !== sector);
                        onFilterChange({ ...filters, sectors: newSectors });
                      }}
                      className="mr-2"
                    />
                    <span className="text-gray-700">{sector}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Geography (continent) Filter — teasers only */}
          {entityType === 'teasers' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Geography</label>
              <div className="max-h-32 overflow-y-auto space-y-1 border border-gray-300 rounded p-2">
                {continentOptions.map(c => (
                  <label key={c} className="flex items-center text-sm">
                    <input
                      type="checkbox"
                      checked={(filters.continents || []).includes(c)}
                      onChange={(e) => {
                        const cur = filters.continents || [];
                        const next = e.target.checked ? [...cur, c] : cur.filter(x => x !== c);
                        onFilterChange({ ...filters, continents: next });
                      }}
                      className="mr-2"
                    />
                    <span className="text-gray-700">🌍 {c}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Raise Amount Range — teasers only */}
          {entityType === 'teasers' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Raise Amount</label>
              <div className="space-y-2">
                <input
                  type="number"
                  min="0"
                  value={filters.raiseRange?.min || ''}
                  onChange={(e) => onFilterChange({
                    ...filters,
                    raiseRange: { ...(filters.raiseRange || {}), min: e.target.value }
                  })}
                  placeholder="Min ($)"
                  className="w-full px-3 py-1 text-sm border border-gray-300 rounded"
                />
                <input
                  type="number"
                  min="0"
                  value={filters.raiseRange?.max || ''}
                  onChange={(e) => onFilterChange({
                    ...filters,
                    raiseRange: { ...(filters.raiseRange || {}), max: e.target.value }
                  })}
                  placeholder="Max ($)"
                  className="w-full px-3 py-1 text-sm border border-gray-300 rounded"
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// ============================================================================
// ANALYTICS HELPERS
// ============================================================================

const useAnimatedCounter = (target, duration = 1200) => {
  const [value, setValue] = React.useState(0);
  React.useEffect(() => {
    if (target === 0) { setValue(0); return; }
    let startTime = null;
    let rafId;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.floor(eased * target));
      if (progress < 1) rafId = requestAnimationFrame(animate);
    };
    rafId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafId);
  }, [target, duration]);
  return value;
};

const formatSmartNumber = (n) => {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toLocaleString();
};

const NavyTooltip = ({ active, payload, label, formatter }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div style={{ background: '#1A1C5C', border: '1px solid #D4AF37', borderRadius: '8px', padding: '10px 14px', boxShadow: '0 4px 16px rgba(0,0,0,0.4)' }}>
      {label && <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', marginBottom: '6px', fontWeight: 600, margin: '0 0 6px 0' }}>{label}</p>}
      {payload.map((entry, i) => (
        <p key={i} style={{ fontSize: '13px', color: '#ffffff', fontWeight: 700, margin: 0 }}>
          <span style={{ color: entry.color || '#D4AF37', marginRight: '6px' }}>●</span>
          {entry.name}: {formatter ? formatter(entry.value) : entry.value}
        </p>
      ))}
    </div>
  );
};

const AnimatedKPICard = ({ label, value, subtitle, icon: Icon, trend, accent, onClick }) => {
  // Flat canonical KPI card with optional left accent stripe.
  // Legacy props (iconColor / iconBg / gradient / shadow) still accepted at
  // the call site but ignored. `accent` defaults to gold.
  const stripe = accent || BRAND_COLORS.gold;
  const numericTarget = typeof value === 'number' ? value : 0;
  const animatedNum = useAnimatedCounter(numericTarget);
  const displayValue = typeof value === 'string' ? value : formatSmartNumber(animatedNum);
  return (
    <div
      style={{
        background: BRAND_COLORS.bgSurface,
        border: `1px solid ${BRAND_COLORS.borderNavy12}`,
        borderLeft: `4px solid ${stripe}`,
        borderRadius: '12px',
        padding: '16px 18px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'box-shadow 0.15s, border-color 0.15s',
      }}
      onClick={onClick}
      onMouseEnter={(e) => { if (onClick) { e.currentTarget.style.boxShadow = '0 4px 12px rgba(10,20,40,0.08)'; } }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)'; }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '8px' }}>
        <p style={{ fontSize: '11px', fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase', color: BRAND_COLORS.textSecondary, margin: 0 }}>{label}</p>
        <div style={{ width: '30px', height: '30px', borderRadius: '7px', backgroundColor: `${stripe}1A`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon style={{ width: '16px', height: '16px', color: stripe }} />
        </div>
      </div>
      <p style={{ fontSize: '28px', fontWeight: 700, lineHeight: 1, color: BRAND_COLORS.navy, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums', margin: 0 }}>{displayValue}</p>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px' }}>
        <p style={{ fontSize: '11px', color: BRAND_COLORS.textTertiary, margin: 0 }}>{subtitle}</p>
        {trend !== undefined && (
          <span style={{ fontSize: '11px', fontWeight: 600, color: trend >= 0 ? BRAND_COLORS.statusSuccess : BRAND_COLORS.statusError }}>
            {trend >= 0 ? '▲' : '▼'} {Math.abs(trend)}%
          </span>
        )}
      </div>
    </div>
  );
};

// VIEW: Analytics
// ============================================================================

// Right-side slide-in panel that shows the rows behind a clicked chart segment.
// Shared across all Analytics drill-downs. Pure presentational — caller owns
// the data and the row-render function.
const DrillDownPanel = ({ title, subtitle, rows, renderRow, emptyLabel = 'No matching rows.', onClose }) => {
  if (!title) return null;
  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        zIndex: 40, animation: 'analyticsFadeIn 0.18s ease-out',
      }} />
      <div style={{
        position: 'fixed', right: 0, top: 0, bottom: 0,
        width: '480px', maxWidth: '95vw',
        background: BRAND_COLORS.bgSurface,
        boxShadow: '-20px 0 50px rgba(0,0,0,0.18)',
        borderLeft: `4px solid ${BRAND_COLORS.gold}`,
        zIndex: 50,
        display: 'flex', flexDirection: 'column',
        animation: 'analyticsSlideInRight 0.22s ease-out',
      }}>
        <div style={{
          background: BRAND_COLORS.navy, color: 'white', padding: '18px 22px',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>{title}</h3>
            {subtitle && <p style={{ margin: '4px 0 0 0', fontSize: '12px', opacity: 0.75 }}>{subtitle}</p>}
          </div>
          <button onClick={onClose} style={{ background: 'transparent', color: 'white', border: 'none', cursor: 'pointer', fontSize: '20px', lineHeight: 1, padding: '0 4px' }}>×</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 22px' }}>
          {(!rows || rows.length === 0) ? (
            <p style={{ textAlign: 'center', color: BRAND_COLORS.textTertiary, fontSize: '13px', padding: '32px 0' }}>{emptyLabel}</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {rows.map((row, i) => renderRow(row, i))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// Wrapper around an analytics report card. Adds stagger-in animation
// (per-card delay) + consistent hover lift. Stays presentational — the
// children supply the chart/JSX.
const AnalyticsCard = ({ children, index = 0, onClick, style = {} }) => (
  <div
    onClick={onClick}
    style={{
      background: '#fff', borderRadius: '16px',
      border: `1.5px solid ${BRAND_COLORS.gold}30`,
      padding: '20px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      transition: 'box-shadow 0.2s, border-color 0.2s, transform 0.2s',
      opacity: 0, animation: `analyticsFadeUp 0.4s ease-out forwards`,
      animationDelay: `${index * 60}ms`,
      cursor: onClick ? 'pointer' : 'default',
      ...style,
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.boxShadow = '0 8px 24px rgba(10,20,40,0.10)';
      e.currentTarget.style.borderColor = `${BRAND_COLORS.gold}80`;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)';
      e.currentTarget.style.borderColor = `${BRAND_COLORS.gold}30`;
    }}
  >
    {children}
  </div>
);

// Compact multi-select for the Analytics filter bar. Shows "Label" + selected
// count; clicking opens a popover with a search-free checkbox list. Closes
// on outside click. Uses module-scope refs only; one instance per filter row.
const FilterMultiSelect = ({ label, options, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const toggle = (val) => {
    if (selected.includes(val)) onChange(selected.filter(v => v !== val));
    else onChange([...selected, val]);
  };
  const count = selected.length;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          padding: '5px 11px', fontSize: '12px', fontWeight: 600,
          borderRadius: '999px',
          border: count > 0 ? `1px solid ${BRAND_COLORS.navy}` : `1px solid ${BRAND_COLORS.borderDefault}`,
          background: count > 0 ? `${BRAND_COLORS.navy}0D` : 'white',
          color: count > 0 ? BRAND_COLORS.navy : BRAND_COLORS.textSecondary,
          cursor: 'pointer',
          transition: 'all 0.15s',
        }}
      >
        {label}
        {count > 0 && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            minWidth: '18px', height: '18px', borderRadius: '9px', padding: '0 5px',
            background: BRAND_COLORS.navy, color: 'white', fontSize: '10px', fontWeight: 700,
          }}>{count}</span>
        )}
        <span style={{ fontSize: '9px', opacity: 0.6 }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 100,
          background: 'white',
          border: `1px solid ${BRAND_COLORS.borderDefault}`,
          borderRadius: '10px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          padding: '6px 0',
          minWidth: '200px', maxHeight: '260px', overflowY: 'auto',
        }}>
          {options.length === 0 && (
            <p style={{ padding: '8px 14px', fontSize: '12px', color: BRAND_COLORS.textTertiary, margin: 0 }}>No options</p>
          )}
          {options.map(opt => (
            <label key={opt.value}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '6px 14px', cursor: 'pointer', fontSize: '13px',
                color: BRAND_COLORS.textPrimary,
              }}
              onMouseEnter={e => e.currentTarget.style.background = BRAND_COLORS.bgApp}
              onMouseLeave={e => e.currentTarget.style.background = ''}
            >
              <input type="checkbox" checked={selected.includes(opt.value)} onChange={() => toggle(opt.value)} />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

const AnalyticsView = ({ analyticsData, stats, prospects: allProspects, partners, teasers: allTeasers, pools, transactions, revenueDeals: allRevenueDeals }) => {
  const [selectedDot, setSelectedDot] = useState(null);

  // ─── Global Filter State (Phase 1) ──────────────────────────────────────
  // Every chart and KPI on this page consumes the filtered datasets below.
  // Empty arrays = no filter on that dimension.
  const [dateRange, setDateRange] = useState('all'); // 7d | 30d | 90d | ytd | all | custom
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [filterPartnerIds, setFilterPartnerIds] = useState([]);
  const [filterSectors, setFilterSectors] = useState([]);
  const [filterContinents, setFilterContinents] = useState([]);
  const [filterDealStates, setFilterDealStates] = useState([]);

  // Compute current period [fromMs, toMs] and the previous equivalent period
  // (same span, immediately before) for delta-vs-previous computations.
  const { fromMs, toMs, prevFromMs, prevToMs, hasDateFilter } = React.useMemo(() => {
    const now = Date.now();
    let from = 0, to = now, hasFilter = true;
    if (dateRange === '7d')        from = now - 7  * 86400e3;
    else if (dateRange === '30d')  from = now - 30 * 86400e3;
    else if (dateRange === '90d')  from = now - 90 * 86400e3;
    else if (dateRange === 'ytd')  from = new Date(new Date().getFullYear(), 0, 1).getTime();
    else if (dateRange === 'custom') {
      from = customStart ? new Date(customStart).getTime() : 0;
      to   = customEnd   ? new Date(customEnd).getTime()   : now;
      hasFilter = !!(customStart || customEnd);
    } else {
      // 'all' — no date filter; previous period falls back to first half of the data.
      hasFilter = false;
    }
    const span = to - from;
    return { fromMs: from, toMs: to, prevFromMs: from - span, prevToMs: from, hasDateFilter: hasFilter };
  }, [dateRange, customStart, customEnd]);

  // Date extractors per entity type.
  const prospectDateMs = (p) => new Date(p.approvalRequestDate || p.createdAt || 0).getTime();
  const dealDateMs     = (d) => new Date(d.dealStartDate || d.createdAt || 0).getTime();
  const teaserDateMs   = (t) => new Date(t.lastUpdated || t.createdAt || 0).getTime();

  // Match helpers — return true if the row passes ALL active filters.
  const matchProspect = (p, [from, to]) => {
    if (hasDateFilter) {
      const t = prospectDateMs(p);
      if (!t || t < from || t > to) return false;
    }
    if (filterPartnerIds.length > 0 && !filterPartnerIds.includes(p.introducingPartnerId)) return false;
    if (filterSectors.length > 0) {
      const ps = Array.isArray(p.sectors) ? p.sectors : (p.sector ? [p.sector] : []);
      if (!ps.some(s => filterSectors.includes(s))) return false;
    }
    if (filterContinents.length > 0) {
      const cs = p.geography?.continents || [];
      if (!cs.some(c => filterContinents.includes(c))) return false;
    }
    return true;
  };
  const matchDeal = (d, [from, to]) => {
    if (hasDateFilter) {
      const t = dealDateMs(d);
      if (!t || t < from || t > to) return false;
    }
    if (filterDealStates.length > 0 && !filterDealStates.includes(d.dealState)) return false;
    if (filterPartnerIds.length > 0) {
      const linkedProspect = d.prospectId ? allProspects.find(p => p.id === d.prospectId) : null;
      const partnerIdFromProspect = linkedProspect?.introducingPartnerId;
      const inStrategic = (d.strategicPartners || []).some(sp => filterPartnerIds.includes(sp.partnerId));
      const inDirect = filterPartnerIds.includes(d.partnerId);
      if (!inStrategic && !inDirect && !filterPartnerIds.includes(partnerIdFromProspect)) return false;
    }
    if ((filterSectors.length > 0 || filterContinents.length > 0) && d.prospectId) {
      const linkedProspect = allProspects.find(p => p.id === d.prospectId);
      if (!linkedProspect) return false;
      if (filterSectors.length > 0) {
        const ps = Array.isArray(linkedProspect.sectors) ? linkedProspect.sectors : (linkedProspect.sector ? [linkedProspect.sector] : []);
        if (!ps.some(s => filterSectors.includes(s))) return false;
      }
      if (filterContinents.length > 0) {
        const cs = linkedProspect.geography?.continents || [];
        if (!cs.some(c => filterContinents.includes(c))) return false;
      }
    } else if ((filterSectors.length > 0 || filterContinents.length > 0) && !d.prospectId) {
      // No linked prospect → can't match sector / geography filter.
      return false;
    }
    return true;
  };
  const matchTeaser = (t, [from, to]) => {
    if (hasDateFilter) {
      const ts = teaserDateMs(t);
      if (!ts || ts < from || ts > to) return false;
    }
    if (filterSectors.length > 0) {
      const ts2 = Array.isArray(t.sectors) ? t.sectors : (t.sector ? [t.sector] : []);
      if (!ts2.some(s => filterSectors.includes(s))) return false;
    }
    if (filterContinents.length > 0) {
      const cs = t.geography?.continents || [];
      if (!cs.some(c => filterContinents.includes(c))) return false;
    }
    return true;
  };

  // Filtered datasets (current period) — shadow the original props so the rest
  // of the function uses these without touching every reference.
  const prospects    = React.useMemo(() => allProspects.filter(p => matchProspect(p, [fromMs, toMs])), [allProspects, fromMs, toMs, filterPartnerIds, filterSectors, filterContinents, hasDateFilter]);
  const revenueDeals = React.useMemo(() => allRevenueDeals.filter(d => matchDeal(d, [fromMs, toMs])), [allRevenueDeals, fromMs, toMs, filterPartnerIds, filterSectors, filterContinents, filterDealStates, hasDateFilter, allProspects]);
  const teasers      = React.useMemo(() => allTeasers.filter(t => matchTeaser(t, [fromMs, toMs])), [allTeasers, fromMs, toMs, filterSectors, filterContinents, hasDateFilter]);

  // Previous-equivalent-period datasets (for KPI deltas). Only meaningful when
  // there IS a date filter; for 'all' we'd be comparing all-vs-nothing.
  const prevProspects    = React.useMemo(() => hasDateFilter ? allProspects.filter(p => matchProspect(p, [prevFromMs, prevToMs])) : null, [allProspects, prevFromMs, prevToMs, hasDateFilter, filterPartnerIds, filterSectors, filterContinents]);
  const prevRevenueDeals = React.useMemo(() => hasDateFilter ? allRevenueDeals.filter(d => matchDeal(d, [prevFromMs, prevToMs])) : null, [allRevenueDeals, prevFromMs, prevToMs, hasDateFilter, filterPartnerIds, filterSectors, filterContinents, filterDealStates, allProspects]);

  // Delta helper: returns rounded % change, or undefined when comparison
  // is not meaningful (no date filter, or previous period had zero rows).
  const pctDelta = (cur, prev) => {
    if (prev === null || prev === undefined) return undefined;
    if (prev === 0) return cur > 0 ? 100 : undefined;
    return Math.round(((cur - prev) / prev) * 100);
  };
  const prospectsDelta = pctDelta(prospects.length, prevProspects?.length);
  const approvedDelta  = pctDelta(prospects.filter(p => p.approvalStatus === 'Approved').length, prevProspects?.filter(p => p.approvalStatus === 'Approved').length);
  const partnersDelta  = pctDelta(partners.length, null); // partners list isn't date-filtered; no meaningful delta
  const dealsDelta     = pctDelta(revenueDeals.length, prevRevenueDeals?.length);
  const revenueDelta   = pctDelta(
    revenueDeals.reduce((s, d) => s + (d.totalReceived || 0), 0),
    prevRevenueDeals ? prevRevenueDeals.reduce((s, d) => s + (d.totalReceived || 0), 0) : null
  );

  // Active filter count for the Clear button.
  const activeFilterCount =
    (hasDateFilter ? 1 : 0)
    + (filterPartnerIds.length > 0 ? 1 : 0)
    + (filterSectors.length > 0 ? 1 : 0)
    + (filterContinents.length > 0 ? 1 : 0)
    + (filterDealStates.length > 0 ? 1 : 0);

  // Drill-down panel state — { title, subtitle, rows, renderRow, emptyLabel }.
  const [drillDown, setDrillDown] = useState(null);
  const closeDrillDown = () => setDrillDown(null);

  // Shared row renderers for the drill-down panel — keep visual style
  // consistent across all 5 drill-down sources.
  const drillRowProspect = (p, i) => {
    const partner = partners.find(x => x.id === p.introducingPartnerId);
    return (
      <div key={p.id || i} style={{ padding: '10px 12px', border: `1px solid ${BRAND_COLORS.borderNavy12}`, borderRadius: '8px', background: BRAND_COLORS.bgSurface }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px' }}>
          <span style={{ fontWeight: 600, fontSize: '13px', color: BRAND_COLORS.navy }}>{p.companyName || p.name}</span>
          <span style={{ fontSize: '11px', color: BRAND_COLORS.textTertiary }}>{p.approvalStatus}</span>
        </div>
        <div style={{ fontSize: '11px', color: BRAND_COLORS.textSecondary, marginTop: '3px' }}>
          {p.prospectType} {partner ? `· ${partner.name}` : ''}
        </div>
      </div>
    );
  };
  const drillRowDeal = (d, i) => {
    const lp = d.prospectId ? allProspects.find(p => p.id === d.prospectId) : null;
    return (
      <div key={d.id || i} style={{ padding: '10px 12px', border: `1px solid ${BRAND_COLORS.borderNavy12}`, borderRadius: '8px', background: BRAND_COLORS.bgSurface }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px' }}>
          <span style={{ fontWeight: 600, fontSize: '13px', color: BRAND_COLORS.navy }}>{d.dealName || 'Untitled deal'}</span>
          <span style={{ fontSize: '11px', color: BRAND_COLORS.textTertiary }}>{d.dealState}</span>
        </div>
        <div style={{ fontSize: '11px', color: BRAND_COLORS.textSecondary, marginTop: '3px' }}>
          {lp?.companyName || 'No prospect'} · ${(d.totalReceived || 0).toLocaleString()} received / ${(d.totalAgreed || 0).toLocaleString()} agreed
        </div>
      </div>
    );
  };
  const drillRowPartner = (p, i) => (
    <div key={p.id || i} style={{ padding: '10px 12px', border: `1px solid ${BRAND_COLORS.borderNavy12}`, borderRadius: '8px', background: BRAND_COLORS.bgSurface }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px' }}>
        <span style={{ fontWeight: 600, fontSize: '13px', color: BRAND_COLORS.navy }}>{p.name}</span>
        <span style={{ fontSize: '11px', color: BRAND_COLORS.textTertiary }}>{p.status}</span>
      </div>
      <div style={{ fontSize: '11px', color: BRAND_COLORS.textSecondary, marginTop: '3px' }}>
        {p.agreementType || 'No agreement'} · joined {p.startDate ? new Date(p.startDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '—'}
      </div>
    </div>
  );

  const clearAllFilters = () => {
    setDateRange('all'); setCustomStart(''); setCustomEnd('');
    setFilterPartnerIds([]); setFilterSectors([]); setFilterContinents([]); setFilterDealStates([]);
  };

  // === Derived data ===
  const totalProspects = prospects.length;
  const approvedProspects = prospects.filter(p => p.approvalStatus === 'Approved').length;
  const totalRevenue = revenueDeals.reduce((sum, d) => sum + (d.totalReceived || 0), 0);
  const activeDeals = revenueDeals.filter(d => d.dealState === 'Active').length;
  const closedDeals = revenueDeals.filter(d => d.dealState === 'Closed').length;
  const approvalRate = (approvedProspects + stats.rejectedProspects) > 0
    ? Math.round((approvedProspects / (approvedProspects + stats.rejectedProspects)) * 100) : 0;
  const introSuccessRate = (() => {
    const a = prospects.filter(p => p.approvalStatus === 'Approved' && p.introOccurred).length;
    const t = prospects.filter(p => p.approvalStatus === 'Approved').length;
    return t > 0 ? Math.round((a / t) * 100) : 0;
  })();
  const actionRequired = prospects.filter(p => {
    if (p.approvalStatus === 'Pending' || p.approvalStatus === 'Waiting for Approval') return true;
    if (p.approvalStatus === 'Approved' && !p.introOccurred && p.introDeadline) {
      const days = Math.floor((new Date(p.introDeadline).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
      return days <= 7 && days >= 0;
    }
    return false;
  }).length;
  const recentTeasers = teasers.filter(t => Math.floor((Date.now() - new Date(t.lastUpdated).getTime()) / (1000 * 60 * 60 * 24)) <= 30).length;

  // NDA / MNDA
  const ndaSigned = partners.filter(p => p.ndaSigned);
  const ndaCount = ndaSigned.filter(p => p.ndaType === 'NDA').length;
  const mndaCount = ndaSigned.filter(p => p.ndaType === 'MNDA').length;
  const ndaUnsigned = partners.length - ndaSigned.length;
  const ndaCoverage = partners.length > 0 ? Math.round((ndaSigned.length / partners.length) * 100) : 0;

  // Partner status breakdown
  const partnersByStatus = {
    Active: partners.filter(p => p.status === 'Active').length,
    'In Tail': partners.filter(p => p.status === 'In Tail').length,
    Terminated: partners.filter(p => p.status === 'Terminated').length,
  };

  // Revenue over time (12 months)
  const revenueOverTime = (() => {
    const now = new Date();
    return Array.from({ length: 12 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (11 - i), 1);
      const nextD = new Date(d.getFullYear(), d.getMonth() + 1, 1);
      const rev = revenueDeals.reduce((sum, deal) => {
        const pays = (deal.payments || []).filter(pay => { const pd = new Date(pay.date); return pd >= d && pd < nextD; });
        return sum + pays.reduce((s, p) => s + (p.amount || 0), 0);
      }, 0);
      return { month: d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }), revenue: rev };
    });
  })();

  // Deal pipeline by partner agreement type
  const dealsByType = (() => {
    const counts = {};
    revenueDeals.forEach(deal => {
      const partner = partners.find(p => p.id === deal.partnerId);
      const type = (partner?.agreementType || 'Other').replace('MCA - ', '').replace('Management Consulting Agreement - ', '');
      counts[type] = (counts[type] || 0) + 1;
    });
    return Object.entries(counts).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count).slice(0, 6);
  })();

  // Partner performance ranking by revenue
  const partnerRanking = partners.map(partner => {
    const partnerDeals = revenueDeals.filter(d => d.partnerId === partner.id);
    const revenue = partnerDeals.reduce((sum, d) => sum + (d.totalReceived || 0), 0);
    return { id: partner.id, name: partner.name, revenue, dealCount: partnerDeals.length, prospectCount: prospects.filter(p => p.introducingPartnerId === partner.id).length };
  }).filter(p => p.dealCount > 0 || p.prospectCount > 0).sort((a, b) => b.revenue - a.revenue || b.dealCount - a.dealCount).slice(0, 10);
  const topRevenue = partnerRanking[0]?.revenue || 1;

  // Funnel stages
  const funnelStages = [
    { label: 'Total Prospects', count: totalProspects, color: '#0a0f3d' },
    { label: 'Approved', count: approvedProspects, color: '#1A1C5C' },
    { label: 'Active Deals', count: activeDeals, color: '#2a3a8a' },
    { label: 'Closed Deals', count: closedDeals, color: '#8a6a18' },
    { label: 'Total Revenue', count: totalRevenue, color: '#D4AF37', isCurrency: true },
  ];

  // Deal timeline scatter
  const dealTimelineData = revenueDeals
    .filter(d => d.dealStartDate && (d.totalReceived || d.dealSize))
    .map(d => ({ x: new Date(d.dealStartDate).getTime(), y: d.totalReceived || d.dealSize || 0, name: d.dealName || 'Deal', partner: partners.find(p => p.id === d.partnerId)?.name || 'Unknown', state: d.dealState, id: d.id }))
    .sort((a, b) => a.x - b.x);
  const maxDealValue = dealTimelineData.reduce((m, d) => Math.max(m, d.y), 1);

  // ─── Phase 2 — Advanced Report Data ──────────────────────────────────────

  // 1. Partner performance bubble — X = deals, Y = revenue, Z = avg deal size.
  const partnerBubbleData = React.useMemo(() => {
    return partners.map(partner => {
      const partnerDeals = revenueDeals.filter(d => {
        if (d.partnerId === partner.id) return true;
        const lp = d.prospectId ? allProspects.find(p => p.id === d.prospectId) : null;
        if (lp?.introducingPartnerId === partner.id) return true;
        return (d.strategicPartners || []).some(sp => sp.partnerId === partner.id);
      });
      const revenue = partnerDeals.reduce((s, d) => s + (d.totalReceived || 0), 0);
      const dealCount = partnerDeals.length;
      const avg = dealCount > 0 ? revenue / dealCount : 0;
      return { id: partner.id, name: partner.name, x: dealCount, y: revenue, z: avg, type: partner.agreementType || 'Other' };
    }).filter(p => p.x > 0);
  }, [partners, revenueDeals, allProspects]);

  // 2. Sector × Geography heatmap — rows = sectors, cols = continents, value = deal count.
  const sectorGeoMatrix = React.useMemo(() => {
    const sectors = new Set();
    const continents = new Set();
    const grid = {};
    revenueDeals.forEach(d => {
      const lp = d.prospectId ? allProspects.find(p => p.id === d.prospectId) : null;
      if (!lp) return;
      const lpSectors = Array.isArray(lp.sectors) ? lp.sectors : (lp.sector ? [lp.sector] : []);
      const lpContinents = lp.geography?.continents || [];
      lpSectors.forEach(s => sectors.add(s));
      lpContinents.forEach(c => continents.add(c));
      lpSectors.forEach(s => {
        lpContinents.forEach(c => {
          grid[s] = grid[s] || {};
          grid[s][c] = (grid[s][c] || 0) + 1;
        });
      });
    });
    return {
      sectors: [...sectors].sort(),
      continents: [...continents].sort(),
      grid,
      max: Math.max(1, ...Object.values(grid).flatMap(row => Object.values(row))),
    };
  }, [revenueDeals, allProspects]);

  // 3. Activity heatmap — last 12 weeks of daily prospect submissions.
  const activityHeatmap = React.useMemo(() => {
    const days = 84; // 12 weeks
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startMs = today.getTime() - (days - 1) * 86400e3;
    const counts = new Array(days).fill(0);
    prospects.forEach(p => {
      const t = prospectDateMs(p);
      if (!t || t < startMs) return;
      const idx = Math.floor((t - startMs) / 86400e3);
      if (idx >= 0 && idx < days) counts[idx]++;
    });
    const max = Math.max(1, ...counts);
    return Array.from({ length: days }, (_, i) => ({
      dayMs: startMs + i * 86400e3,
      count: counts[i],
      intensity: counts[i] / max,
    }));
  }, [prospects]);

  // 4. Cohort retention — partners grouped by signup month; cells show how many
  // were still active (status === 'Active') in each subsequent month after join.
  const cohortMatrix = React.useMemo(() => {
    if (partners.length === 0) return { cohorts: [], maxMonthsSince: 0 };
    const cohortMap = {};
    partners.forEach(p => {
      const start = new Date(p.startDate || p.createdAt || Date.now());
      const key = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}`;
      cohortMap[key] = cohortMap[key] || { key, label: start.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }), partners: [], startMs: new Date(start.getFullYear(), start.getMonth(), 1).getTime() };
      cohortMap[key].partners.push(p);
    });
    const now = new Date();
    const cohorts = Object.values(cohortMap)
      .sort((a, b) => b.startMs - a.startMs)
      .slice(0, 8); // latest 8 cohorts
    const maxMonthsSince = Math.max(0, ...cohorts.map(c => {
      const start = new Date(c.startMs);
      return (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
    }));
    cohorts.forEach(c => {
      const start = new Date(c.startMs);
      const monthsSince = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
      c.cells = Array.from({ length: monthsSince + 1 }, (_, i) => {
        // Number of cohort partners still 'Active' at month i after joining.
        // Approximation: today's status (we don't track history). For the
        // most-recent column it's exact; older columns are reverse-projected
        // using start/end dates.
        const monthMs = new Date(start.getFullYear(), start.getMonth() + i, 1).getTime();
        const monthEndMs = new Date(start.getFullYear(), start.getMonth() + i + 1, 1).getTime();
        const active = c.partners.filter(p => {
          const partnerStart = new Date(p.startDate || p.createdAt || 0).getTime();
          const partnerEnd = p.endDate ? new Date(p.endDate).getTime() : Infinity;
          return partnerStart <= monthEndMs && partnerEnd > monthMs;
        }).length;
        return { active, total: c.partners.length, pct: c.partners.length > 0 ? Math.round((active / c.partners.length) * 100) : 0 };
      });
    });
    return { cohorts, maxMonthsSince };
  }, [partners]);

  // 5. Pipeline forecast — next 6 months of weighted expected revenue from
  // open deals (Draft 20%, Active 70%; Closed/Lost excluded).
  const pipelineForecast = React.useMemo(() => {
    const STATE_WEIGHT = { Draft: 0.2, Active: 0.7 };
    const now = new Date();
    now.setDate(1); now.setHours(0, 0, 0, 0);
    const months = Array.from({ length: 6 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      return { key: i, label: d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }), draft: 0, active: 0 };
    });
    revenueDeals.forEach(d => {
      const w = STATE_WEIGHT[d.dealState];
      if (!w) return;
      const remaining = (d.totalAgreed || d.dealSize || 0) - (d.totalReceived || 0);
      if (remaining <= 0) return;
      // Spread the weighted remaining across the 6 months evenly (placeholder
      // — without explicit close dates we can't be more precise).
      const perMonth = (remaining * w) / 6;
      months.forEach(m => {
        if (d.dealState === 'Draft')  m.draft  += perMonth;
        if (d.dealState === 'Active') m.active += perMonth;
      });
    });
    return months;
  }, [revenueDeals]);

  // 6. Time-to-approval histogram — distribution of (approvalDate − approvalRequestDate)
  // in days, bucketed.
  const approvalDistribution = React.useMemo(() => {
    const buckets = [
      { label: '0–1d',  min: 0,   max: 1,   count: 0 },
      { label: '2–3d',  min: 2,   max: 3,   count: 0 },
      { label: '4–7d',  min: 4,   max: 7,   count: 0 },
      { label: '8–14d', min: 8,   max: 14,  count: 0 },
      { label: '15–30d',min: 15,  max: 30,  count: 0 },
      { label: '30d+',  min: 31,  max: Infinity, count: 0 },
    ];
    prospects.forEach(p => {
      if (p.approvalStatus !== 'Approved' || !p.approvalDate || !p.approvalRequestDate) return;
      const days = (new Date(p.approvalDate).getTime() - new Date(p.approvalRequestDate).getTime()) / 86400e3;
      if (days < 0) return;
      const b = buckets.find(b => days >= b.min && days <= b.max);
      if (b) b.count++;
    });
    return buckets;
  }, [prospects]);

  // ── CSV export ───────────────────────────────────────────────────────────
  // Multi-section CSV (Excel-friendly): KPIs, funnel, top partners, monthly
  // revenue, deal pipeline by type. Blank rows separate sections.
  const handleExportReport = () => {
    // RFC 4180 escape: wrap in quotes and double internal quotes if the field
    // contains comma, quote, newline, or starts/ends with whitespace.
    const esc = (v) => {
      if (v === null || v === undefined) return '';
      const s = String(v);
      return /[",\n\r]|^\s|\s$/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const row = (...cells) => cells.map(esc).join(',');
    const lines = [];
    const generatedAt = new Date().toISOString();

    lines.push(row('Partner Portal — Analytics Report'));
    lines.push(row('Generated', generatedAt));
    lines.push('');

    lines.push(row('Key Metrics'));
    lines.push(row('Metric', 'Value'));
    lines.push(row('Total Prospects', totalProspects));
    lines.push(row('Approved Prospects', approvedProspects));
    lines.push(row('Pending Review', stats.pendingProspects));
    lines.push(row('Approval Rate (%)', approvalRate));
    lines.push(row('Avg. Days to Approval', analyticsData.avgTimeToApproval));
    lines.push(row('Intro Success Rate (%)', introSuccessRate));
    lines.push(row('Action Required', actionRequired));
    lines.push(row('Total Partners', partners.length));
    lines.push(row('Active Partners', stats.activePartners));
    lines.push(row('Revenue Deals', revenueDeals.length));
    lines.push(row('Active Deals', activeDeals));
    lines.push(row('Closed Deals', closedDeals));
    lines.push(row('Total Revenue (USD)', totalRevenue));
    lines.push(row('Investment Teasers', teasers.length));
    lines.push(row('Recent Updates (30d)', recentTeasers));
    lines.push(row('NDA/MNDA Signed', ndaSigned.length));
    lines.push(row('NDA Coverage (%)', ndaCoverage));
    lines.push('');

    lines.push(row('Funnel Stages'));
    lines.push(row('Stage', 'Count'));
    funnelStages.forEach(s => lines.push(row(s.label, s.count)));
    lines.push('');

    lines.push(row('Top 10 Partners by Revenue'));
    lines.push(row('Rank', 'Partner', 'Revenue (USD)', 'Deals', 'Prospects'));
    partnerRanking.forEach((p, i) => lines.push(row(i + 1, p.name, p.revenue, p.dealCount, p.prospectCount)));
    if (partnerRanking.length === 0) lines.push(row('(none)', '', '', '', ''));
    lines.push('');

    lines.push(row('Revenue Over Time (last 12 months)'));
    lines.push(row('Month', 'Revenue (USD)'));
    revenueOverTime.forEach(m => lines.push(row(m.month, m.revenue)));
    lines.push('');

    lines.push(row('Deal Pipeline by Type'));
    lines.push(row('Type', 'Deal Count'));
    dealsByType.forEach(t => lines.push(row(t.name, t.count)));
    lines.push('');

    lines.push(row('Partner Status Breakdown'));
    lines.push(row('Status', 'Count'));
    Object.entries(partnersByStatus).forEach(([k, v]) => lines.push(row(k, v)));

    // ﻿ = UTF-8 BOM. Without it, Excel mis-detects as ANSI and mangles
    // any non-ASCII characters (e.g. partner names with accents, em-dashes).
    const csv = '﻿' + lines.join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const fname = `analytics-report-${generatedAt.slice(0, 10)}.csv`;
    const a = document.createElement('a');
    a.href = url;
    a.download = fname;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Defer revoke so Safari has time to start the download.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div className="space-y-6">
      {/* Phase 3 — keyframes for analytics cards / drill-down panel */}
      <style>{`
        @keyframes analyticsFadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes analyticsFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes analyticsSlideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
      `}</style>
      {drillDown && (
        <DrillDownPanel
          title={drillDown.title}
          subtitle={drillDown.subtitle}
          rows={drillDown.rows}
          renderRow={drillDown.renderRow}
          emptyLabel={drillDown.emptyLabel}
          onClose={closeDrillDown}
        />
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: BRAND_COLORS.navy }}>Analytics Dashboard</h2>
          <p className="text-sm mt-1" style={{ color: '#9ca3af' }}>Live insights across your partner pipeline</p>
        </div>
        <button
          onClick={handleExportReport}
          title="Download a CSV report of current analytics"
          className="flex items-center space-x-2 px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity"
          style={{ background: BRAND_COLORS.navy, border: `1px solid ${BRAND_COLORS.gold}40` }}
        >
          <Download className="w-4 h-4" />
          <span className="text-sm font-medium">Export Report</span>
        </button>
      </div>

      {/* ─── Global Filter Bar (Phase 1) ───────────────────────────────── */}
      <div style={{
        background: BRAND_COLORS.bgSurface,
        border: `1px solid ${BRAND_COLORS.borderNavy12}`,
        borderRadius: '12px',
        padding: '12px 16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
          {/* Date range chip group */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ fontSize: '11px', fontWeight: 600, color: BRAND_COLORS.textSecondary, marginRight: '6px', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Range</span>
            {[
              { key: '7d',  label: '7d'  },
              { key: '30d', label: '30d' },
              { key: '90d', label: '90d' },
              { key: 'ytd', label: 'YTD' },
              { key: 'all', label: 'All' },
            ].map(opt => (
              <button key={opt.key} type="button" onClick={() => setDateRange(opt.key)}
                style={{
                  padding: '5px 11px', fontSize: '12px', fontWeight: 600,
                  borderRadius: '999px',
                  border: dateRange === opt.key ? `1px solid ${BRAND_COLORS.navy}` : `1px solid ${BRAND_COLORS.borderDefault}`,
                  background: dateRange === opt.key ? BRAND_COLORS.navy : 'white',
                  color:      dateRange === opt.key ? 'white' : BRAND_COLORS.textSecondary,
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
              >{opt.label}</button>
            ))}
          </div>

          {/* Multi-select dropdowns (native <select multiple> kept compact via single-select; chip-style display follows) */}
          <FilterMultiSelect label="Partner"
            options={partners.map(p => ({ value: p.id, label: p.name }))}
            selected={filterPartnerIds} onChange={setFilterPartnerIds} />
          <FilterMultiSelect label="Sector"
            options={DEFAULT_SECTORS.map(s => ({ value: typeof s === 'string' ? s : s.name, label: typeof s === 'string' ? s : s.name }))}
            selected={filterSectors} onChange={setFilterSectors} />
          <FilterMultiSelect label="Geography"
            options={Object.keys(WORLD_GEOGRAPHY).map(c => ({ value: c, label: c }))}
            selected={filterContinents} onChange={setFilterContinents} />
          <FilterMultiSelect label="Deal State"
            options={['Draft', 'Active', 'Closed', 'Lost'].map(s => ({ value: s, label: s }))}
            selected={filterDealStates} onChange={setFilterDealStates} />

          {activeFilterCount > 0 && (
            <button type="button" onClick={clearAllFilters}
              style={{
                marginLeft: 'auto', fontSize: '12px', fontWeight: 600,
                color: BRAND_COLORS.statusError, background: 'transparent',
                border: 'none', cursor: 'pointer', padding: '4px 8px',
              }}>
              Clear filters ({activeFilterCount})
            </button>
          )}
        </div>
      </div>

      {/* KPI CARDS - 4 columns */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        <AnimatedKPICard label="Total Prospects" value={totalProspects} subtitle={hasDateFilter ? 'vs previous period' : 'All opportunities'} icon={FileText} trend={prospectsDelta} onClick={() => window.dispatchEvent(new CustomEvent('navigate-to-tab', { detail: 'prospects' }))} />
        <AnimatedKPICard label="Approved" value={approvedProspects} subtitle={`${approvalRate}% approval rate`} icon={Check} trend={approvedDelta} onClick={() => window.dispatchEvent(new CustomEvent('navigate-to-tab', { detail: 'prospects' }))} />
        <AnimatedKPICard label="Pending Review" value={stats.pendingProspects} subtitle="Awaiting approval" icon={Clock} iconColor="rgba(240,100,60,0.95)" iconBg="rgba(240,100,60,0.2)" gradient="linear-gradient(135deg,#4a1200 0%,#8a3018 100%)" shadow="0 4px 12px rgba(138,48,24,0.35),inset 0 1px 0 rgba(255,255,255,0.1)" onClick={() => window.dispatchEvent(new CustomEvent('navigate-to-tab', { detail: 'prospects' }))} />
        <AnimatedKPICard label="Avg. Approval Time" value={`${analyticsData.avgTimeToApproval}d`} subtitle="Days to approve" icon={Activity} iconColor="rgba(220,160,40,0.95)" iconBg="rgba(220,160,40,0.2)" gradient="linear-gradient(135deg,#3d2800 0%,#7a5018 100%)" shadow="0 4px 12px rgba(122,80,24,0.4),inset 0 1px 0 rgba(255,255,255,0.1)" />
        <AnimatedKPICard label="Total Partners" value={partners.length} subtitle={`${stats.activePartners} active`} icon={Users} onClick={() => window.dispatchEvent(new CustomEvent('navigate-to-tab', { detail: 'partners' }))} />
        <AnimatedKPICard label="Active Partners" value={stats.activePartners} subtitle={`${partners.length > 0 ? Math.round((stats.activePartners/partners.length)*100) : 0}% active rate`} icon={Users} iconColor="rgba(13,220,160,0.95)" iconBg="rgba(13,220,160,0.2)" gradient="linear-gradient(135deg,#0d4a3a 0%,#0d8a6a 100%)" shadow="0 4px 12px rgba(13,138,106,0.3),inset 0 1px 0 rgba(255,255,255,0.1)" onClick={() => window.dispatchEvent(new CustomEvent('navigate-to-tab', { detail: 'partners' }))} />
        <AnimatedKPICard label="Revenue Deals" value={revenueDeals.length} subtitle={hasDateFilter ? 'vs previous period' : 'Total deals'} icon={DollarSign} trend={dealsDelta} onClick={() => window.dispatchEvent(new CustomEvent('navigate-to-tab', { detail: 'revenue' }))} />
        <AnimatedKPICard label="Total Revenue" value={totalRevenue} subtitle={hasDateFilter ? 'vs previous period' : 'Total received'} icon={TrendingUp} trend={revenueDelta} onClick={() => window.dispatchEvent(new CustomEvent('navigate-to-tab', { detail: 'revenue' }))} />
        <AnimatedKPICard label="Investment Teasers" value={teasers.length} subtitle="Active documents" icon={FileText} iconColor="rgba(220,160,40,0.95)" iconBg="rgba(220,160,40,0.2)" gradient="linear-gradient(135deg,#3d2800 0%,#7a5018 100%)" shadow="0 4px 12px rgba(122,80,24,0.4),inset 0 1px 0 rgba(255,255,255,0.1)" onClick={() => window.dispatchEvent(new CustomEvent('navigate-to-tab', { detail: 'teasers' }))} />
        <AnimatedKPICard label="Recent Updates" value={recentTeasers} subtitle="Last 30 days" icon={Zap} iconColor="rgba(160,120,255,0.95)" iconBg="rgba(160,120,255,0.2)" gradient="linear-gradient(135deg,#2a1a4a 0%,#4a3488 100%)" shadow="0 4px 12px rgba(74,52,136,0.4),inset 0 1px 0 rgba(255,255,255,0.1)" />
        <AnimatedKPICard label="Intro Success" value={`${introSuccessRate}%`} subtitle="Intro completion rate" icon={TrendingUp} iconColor="rgba(80,130,255,0.95)" iconBg="rgba(80,130,255,0.2)" gradient="linear-gradient(135deg,#0d1a4a 0%,#1e3896 100%)" shadow="0 4px 12px rgba(30,56,150,0.35),inset 0 1px 0 rgba(255,255,255,0.1)" />
        <AnimatedKPICard label="Action Required" value={actionRequired} subtitle="Items need attention" icon={AlertCircle} iconColor="rgba(240,80,80,0.95)" iconBg="rgba(240,80,80,0.2)" gradient="linear-gradient(135deg,#4a0000 0%,#8a1818 100%)" shadow="0 4px 12px rgba(138,24,24,0.4),inset 0 1px 0 rgba(255,255,255,0.1)" />
        <AnimatedKPICard label="NDA/MNDA Signed" value={ndaSigned.length} subtitle={`${ndaCount} NDA · ${mndaCount} MNDA · ${ndaCoverage}% coverage`} icon={Shield} iconColor="rgba(13,220,160,0.95)" iconBg="rgba(13,220,160,0.2)" gradient="linear-gradient(135deg,#0d4a3a 0%,#0d8a6a 100%)" shadow="0 4px 12px rgba(13,138,106,0.3),inset 0 1px 0 rgba(255,255,255,0.1)" onClick={() => window.dispatchEvent(new CustomEvent('navigate-to-tab', { detail: 'partners' }))} />
        <AnimatedKPICard label="NDA Unsigned" value={ndaUnsigned} subtitle={`${partners.length - ndaUnsigned} of ${partners.length} covered`} icon={ShieldAlert} iconColor="rgba(240,100,60,0.95)" iconBg="rgba(240,100,60,0.2)" gradient="linear-gradient(135deg,#4a1200 0%,#8a3018 100%)" shadow="0 4px 12px rgba(138,48,24,0.35),inset 0 1px 0 rgba(255,255,255,0.1)" onClick={() => window.dispatchEvent(new CustomEvent('navigate-to-tab', { detail: 'partners' }))} />
      </div>

      {/* CHARTS ROW: Revenue Over Time + Deal Pipeline by Type */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Revenue Over Time - AreaChart navy/gold gradient */}
        <div style={{ background: '#fff', borderRadius: '16px', border: `1.5px solid ${BRAND_COLORS.gold}30`, padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <h3 style={{ fontSize: '15px', fontWeight: 700, color: BRAND_COLORS.navy, margin: '0 0 2px 0' }}>Revenue Over Time</h3>
          <p style={{ fontSize: '12px', color: '#9ca3af', margin: '0 0 16px 0' }}>Monthly revenue received (12 months)</p>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={revenueOverTime} margin={{ top: 5, right: 10, bottom: 5, left: 10 }}>
              <defs>
                <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#1A1C5C" stopOpacity={0.45} />
                  <stop offset="95%" stopColor="#D4AF37" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#9ca3af' }} />
              <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} tickFormatter={v => v >= 1000000 ? `$${(v/1000000).toFixed(1)}M` : v >= 1000 ? `$${(v/1000).toFixed(0)}K` : `$${v}`} />
              <Tooltip content={<NavyTooltip formatter={v => `$${v.toLocaleString()}`} />} />
              <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#1A1C5C" strokeWidth={2.5} fill="url(#revenueGradient)" dot={{ fill: '#D4AF37', stroke: '#1A1C5C', strokeWidth: 2, r: 4 }} activeDot={{ r: 6, fill: '#D4AF37' }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Deal Pipeline by Type - Navy/Gold alternating BarChart */}
        <div style={{ background: '#fff', borderRadius: '16px', border: `1.5px solid ${BRAND_COLORS.gold}30`, padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <h3 style={{ fontSize: '15px', fontWeight: 700, color: BRAND_COLORS.navy, margin: '0 0 2px 0' }}>Deal Pipeline by Type</h3>
          <p style={{ fontSize: '12px', color: '#9ca3af', margin: '0 0 16px 0' }}>Revenue deals by partner agreement type</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={dealsByType} margin={{ top: 5, right: 10, bottom: 30, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#9ca3af' }} angle={-25} textAnchor="end" interval={0} />
              <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} allowDecimals={false} />
              <Tooltip content={<NavyTooltip />} />
              <Bar dataKey="count" name="Deals" radius={[6, 6, 0, 0]}>
                {dealsByType.map((_, i) => <Cell key={i} fill={i % 2 === 0 ? '#1A1C5C' : '#D4AF37'} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* PARTNER STATUS + NDA COMPLIANCE */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Partner Status Breakdown */}
        <div style={{ background: '#fff', borderRadius: '16px', border: `1.5px solid ${BRAND_COLORS.gold}30`, padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <h3 style={{ fontSize: '15px', fontWeight: 700, color: BRAND_COLORS.navy, margin: '0 0 2px 0' }}>Partner Status Breakdown</h3>
          <p style={{ fontSize: '12px', color: '#9ca3af', margin: '0 0 20px 0' }}>Distribution of partners by contract status</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {[
              { label: 'Active', count: partnersByStatus.Active, color: '#16a34a', bg: '#dcfce7' },
              { label: 'In Tail', count: partnersByStatus['In Tail'], color: '#d97706', bg: '#fef3c7' },
              { label: 'Terminated', count: partnersByStatus.Terminated, color: '#dc2626', bg: '#fee2e2' },
            ].map(({ label, count, color, bg }) => {
              const pct = partners.length > 0 ? Math.round((count / partners.length) * 100) : 0;
              return (
                <div key={label}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                    <span style={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>{label}</span>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <span style={{ fontSize: '11px', padding: '1px 8px', borderRadius: '20px', background: bg, color, fontWeight: 700 }}>{count}</span>
                      <span style={{ fontSize: '11px', color: '#9ca3af' }}>{pct}%</span>
                    </div>
                  </div>
                  <div style={{ height: '8px', borderRadius: '4px', background: '#f0f0f0', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: '4px', transition: 'width 1s ease' }} />
                  </div>
                </div>
              );
            })}
          </div>
          {/* Stacked mini-bar */}
          {partners.length > 0 && (
            <div style={{ marginTop: '20px' }}>
              <p style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '6px' }}>Overall split</p>
              <div style={{ display: 'flex', height: '12px', borderRadius: '6px', overflow: 'hidden', gap: '2px' }}>
                {partnersByStatus.Active > 0 && <div style={{ flex: partnersByStatus.Active, background: '#16a34a', transition: 'flex 1s ease' }} title={`Active: ${partnersByStatus.Active}`} />}
                {partnersByStatus['In Tail'] > 0 && <div style={{ flex: partnersByStatus['In Tail'], background: '#d97706', transition: 'flex 1s ease' }} title={`In Tail: ${partnersByStatus['In Tail']}`} />}
                {partnersByStatus.Terminated > 0 && <div style={{ flex: partnersByStatus.Terminated, background: '#dc2626', transition: 'flex 1s ease' }} title={`Terminated: ${partnersByStatus.Terminated}`} />}
              </div>
            </div>
          )}
        </div>

        {/* NDA/MNDA Compliance */}
        <div style={{ background: '#fff', borderRadius: '16px', border: `1.5px solid ${BRAND_COLORS.gold}30`, padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <h3 style={{ fontSize: '15px', fontWeight: 700, color: BRAND_COLORS.navy, margin: '0 0 2px 0' }}>NDA / MNDA Coverage</h3>
          <p style={{ fontSize: '12px', color: '#9ca3af', margin: '0 0 20px 0' }}>Agreement signing status across all partners</p>
          {/* Coverage ring / progress */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '20px' }}>
            <div style={{ position: 'relative', width: '80px', height: '80px', flexShrink: 0 }}>
              <svg viewBox="0 0 36 36" style={{ width: '80px', height: '80px', transform: 'rotate(-90deg)' }}>
                <circle cx="18" cy="18" r="15.9" fill="none" stroke="#f0f0f0" strokeWidth="3.5" />
                <circle cx="18" cy="18" r="15.9" fill="none" stroke="#16a34a" strokeWidth="3.5"
                  strokeDasharray={`${ndaCoverage} ${100 - ndaCoverage}`} strokeLinecap="round" />
              </svg>
              <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: '16px', fontWeight: 800, color: BRAND_COLORS.navy, lineHeight: 1 }}>{ndaCoverage}%</span>
                <span style={{ fontSize: '9px', color: '#9ca3af' }}>covered</span>
              </div>
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {[
                { label: 'NDA', count: ndaCount, color: '#1A1C5C', bg: '#e8eaf6' },
                { label: 'MNDA', count: mndaCount, color: '#D4AF37', bg: '#fef9e7' },
                { label: 'Unsigned', count: ndaUnsigned, color: '#dc2626', bg: '#fee2e2' },
              ].map(({ label, count, color, bg }) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: color, flexShrink: 0 }} />
                    <span style={{ fontSize: '12px', color: '#374151' }}>{label}</span>
                  </div>
                  <span style={{ fontSize: '13px', fontWeight: 700, padding: '1px 10px', borderRadius: '20px', background: bg, color }}>{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* REVENUE FUNNEL + PARTNER PERFORMANCE RANKING */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Revenue Funnel */}
        <div style={{ background: '#fff', borderRadius: '16px', border: `1.5px solid ${BRAND_COLORS.gold}30`, padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <h3 style={{ fontSize: '15px', fontWeight: 700, color: BRAND_COLORS.navy, margin: '0 0 2px 0' }}>Revenue Funnel</h3>
          <p style={{ fontSize: '12px', color: '#9ca3af', margin: '0 0 20px 0' }}>Conversion through pipeline stages</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {funnelStages.map((stage, i) => {
              const prev = funnelStages[i - 1];
              const convRate = prev && !prev.isCurrency && !stage.isCurrency && prev.count > 0 ? Math.round((stage.count / prev.count) * 100) : null;
              const maxCount = funnelStages[0].count || 1;
              const barWidth = stage.isCurrency ? Math.max(10, Math.min(100, totalRevenue > 0 ? 100 : 0)) : Math.max(10, Math.min(100, (stage.count / maxCount) * 100));
              const stageColors = ['#0a0f3d', '#1A1C5C', '#2a3a8a', '#8a6a18', '#D4AF37'];
              return (
                <div key={i}>
                  {convRate !== null && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '3px 0', marginLeft: '16px' }}>
                      <div style={{ width: '1px', height: '14px', background: `${BRAND_COLORS.gold}40` }} />
                      <span style={{ fontSize: '11px', color: BRAND_COLORS.gold, fontWeight: 600 }}>▼ {convRate}% conversion</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 0' }}>
                    <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: stageColors[i], display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span style={{ fontSize: '11px', fontWeight: 700, color: '#fff' }}>{i + 1}</span>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span style={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>{stage.label}</span>
                        <span style={{ fontSize: '13px', fontWeight: 700, color: stageColors[i] }}>
                          {stage.isCurrency ? `$${formatSmartNumber(stage.count)}` : stage.count.toLocaleString()}
                        </span>
                      </div>
                      <div style={{ height: '8px', borderRadius: '4px', background: '#f0f0f0', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${barWidth}%`, background: stageColors[i], borderRadius: '4px', transition: 'width 1s ease' }} />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Partner Performance Ranking */}
        <div style={{ background: '#fff', borderRadius: '16px', border: `1.5px solid ${BRAND_COLORS.gold}30`, padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <h3 style={{ fontSize: '15px', fontWeight: 700, color: BRAND_COLORS.navy, margin: '0 0 2px 0' }}>Partner Leaderboard</h3>
          <p style={{ fontSize: '12px', color: '#9ca3af', margin: '0 0 16px 0' }}>Ranked by revenue generated</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '340px', overflowY: 'auto' }}>
            {partnerRanking.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#9ca3af', fontSize: '13px', padding: '32px 0' }}>No partner revenue data yet</p>
            ) : partnerRanking.map((partner, i) => {
              const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : null;
              const barPct = topRevenue > 0 ? Math.max(5, (partner.revenue / topRevenue) * 100) : 5;
              return (
                <div key={partner.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', borderRadius: '10px', background: i < 3 ? `${BRAND_COLORS.gold}08` : '#fafafa', border: `1px solid ${i < 3 ? BRAND_COLORS.gold + '30' : '#f0f0f0'}` }}>
                  <div style={{ width: '26px', textAlign: 'center', flexShrink: 0 }}>
                    {medal ? <span style={{ fontSize: '16px' }}>{medal}</span> : <span style={{ fontSize: '12px', fontWeight: 700, color: '#9ca3af' }}>#{i + 1}</span>}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 600, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '140px' }}>{partner.name}</span>
                      <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                        <span style={{ fontSize: '11px', color: '#9ca3af' }}>{partner.dealCount} deals</span>
                        <span style={{ fontSize: '12px', fontWeight: 700, color: BRAND_COLORS.navy }}>${formatSmartNumber(partner.revenue)}</span>
                      </div>
                    </div>
                    <div style={{ height: '5px', borderRadius: '3px', background: '#f0f0f0', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${barPct}%`, background: BRAND_COLORS.gold, borderRadius: '3px', transition: 'width 1s ease' }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* PARTNER HORIZONTAL BARCHART */}
      <div style={{ background: '#fff', borderRadius: '16px', border: `1.5px solid ${BRAND_COLORS.gold}30`, padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <h3 style={{ fontSize: '15px', fontWeight: 700, color: BRAND_COLORS.navy, margin: '0 0 2px 0' }}>Top Partner Prospects & Approvals</h3>
        <p style={{ fontSize: '12px', color: '#9ca3af', margin: '0 0 16px 0' }}>Top 5 partners by prospects submitted</p>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={analyticsData.partnerPerformance.slice(0, 5)} layout="vertical" margin={{ top: 5, right: 40, bottom: 5, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 10, fill: '#9ca3af' }} allowDecimals={false} />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: '#374151' }} width={100} />
            <Tooltip content={<NavyTooltip />} />
            <Legend wrapperStyle={{ fontSize: '11px' }} />
            <Bar dataKey="prospects" name="Prospects" fill="#1A1C5C" radius={[0, 4, 4, 0]} barSize={10} />
            <Bar dataKey="approved" name="Approved" fill="#D4AF37" radius={[0, 4, 4, 0]} barSize={10} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* DEAL TIMELINE - ScatterChart */}
      <div style={{ background: '#fff', borderRadius: '16px', border: `1.5px solid ${BRAND_COLORS.gold}30`, padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
          <div>
            <h3 style={{ fontSize: '15px', fontWeight: 700, color: BRAND_COLORS.navy, margin: '0 0 2px 0' }}>Deal Timeline</h3>
            <p style={{ fontSize: '12px', color: '#9ca3af', margin: 0 }}>Deal value over time — dot size reflects deal value. Click a dot for details.</p>
          </div>
          {selectedDot && (
            <div style={{ background: '#1A1C5C', borderRadius: '10px', padding: '10px 14px', border: `1px solid ${BRAND_COLORS.gold}`, maxWidth: '220px', flexShrink: 0 }}>
              <p style={{ fontSize: '12px', fontWeight: 700, color: '#fff', margin: '0 0 2px 0' }}>{selectedDot.name}</p>
              <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', margin: '0 0 2px 0' }}>Partner: {selectedDot.partner}</p>
              <p style={{ fontSize: '11px', color: '#D4AF37', fontWeight: 600, margin: '0 0 2px 0' }}>Value: ${selectedDot.y.toLocaleString()}</p>
              <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', margin: '0 0 4px 0' }}>State: {selectedDot.state}</p>
              <button onClick={() => setSelectedDot(null)} style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>✕ Close</button>
            </div>
          )}
        </div>
        {dealTimelineData.length === 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '220px', color: '#9ca3af', fontSize: '13px' }}>No deal timeline data available</div>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <ScatterChart margin={{ top: 10, right: 30, bottom: 10, left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis type="number" dataKey="x" domain={['auto', 'auto']} name="Date" tickFormatter={v => new Date(v).toLocaleDateString('en-US', { month: 'short', year: '2-digit' })} tick={{ fontSize: 10, fill: '#9ca3af' }} />
              <YAxis type="number" dataKey="y" name="Value" tickFormatter={v => v >= 1000000 ? `$${(v/1000000).toFixed(1)}M` : v >= 1000 ? `$${(v/1000).toFixed(0)}K` : `$${v}`} tick={{ fontSize: 10, fill: '#9ca3af' }} />
              <ZAxis type="number" dataKey="y" range={[40, 400]} />
              <Tooltip cursor={{ strokeDasharray: '3 3' }} content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const d = payload[0]?.payload;
                if (!d) return null;
                return (
                  <div style={{ background: '#1A1C5C', border: '1px solid #D4AF37', borderRadius: '8px', padding: '10px 14px' }}>
                    <p style={{ fontSize: '12px', fontWeight: 700, color: '#fff', margin: '0 0 2px 0' }}>{d.name}</p>
                    <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', margin: '0 0 2px 0' }}>Partner: {d.partner}</p>
                    <p style={{ fontSize: '11px', color: '#D4AF37', fontWeight: 600, margin: '0 0 2px 0' }}>Value: ${d.y.toLocaleString()}</p>
                    <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', margin: 0 }}>{new Date(d.x).toLocaleDateString()}</p>
                  </div>
                );
              }} />
              <Scatter data={dealTimelineData} shape={(props) => {
                const { cx, cy, payload } = props;
                const isSelected = selectedDot?.id === payload.id;
                const size = Math.max(6, Math.min(20, 6 + (payload.y / maxDealValue) * 14));
                return (
                  <circle cx={cx} cy={cy} r={size} fill={isSelected ? '#D4AF37' : '#1A1C5C'} stroke={isSelected ? '#ffffff' : '#D4AF37'} strokeWidth={isSelected ? 2 : 1} style={{ cursor: 'pointer', transition: 'all 0.2s' }} onClick={() => setSelectedDot(isSelected ? null : payload)} />
                );
              }} />
            </ScatterChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* ──────────────────────────────────────────────────────────────────
          PHASE 2 — ADVANCED REPORTS
          ────────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginTop: '8px' }}>
        <div>
          <h3 style={{ fontSize: '20px', fontWeight: 700, color: BRAND_COLORS.navy, margin: 0 }}>Advanced Reports</h3>
          <p style={{ fontSize: '12px', color: BRAND_COLORS.textTertiary, margin: '4px 0 0 0' }}>Deeper cuts across partners, sectors, geography and time</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* 1. Partner performance bubble */}
        <AnalyticsCard index={0}>
          <h3 style={{ fontSize: '15px', fontWeight: 700, color: BRAND_COLORS.navy, margin: '0 0 2px 0' }}>Partner Performance</h3>
          <p style={{ fontSize: '12px', color: '#9ca3af', margin: '0 0 16px 0' }}>Click a bubble to see that partner's deals · X = deals, Y = revenue, size = avg</p>
          {partnerBubbleData.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#9ca3af', fontSize: '13px', padding: '40px 0' }}>No partner deal data in this period</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <ScatterChart margin={{ top: 10, right: 20, bottom: 20, left: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" dataKey="x" name="Deals" tick={{ fontSize: 10, fill: '#9ca3af' }} label={{ value: 'Deals', position: 'insideBottom', offset: -8, style: { fontSize: 11, fill: '#9ca3af' } }} />
                <YAxis type="number" dataKey="y" name="Revenue" tick={{ fontSize: 10, fill: '#9ca3af' }} tickFormatter={v => v >= 1000000 ? `$${(v/1e6).toFixed(1)}M` : v >= 1000 ? `$${(v/1e3).toFixed(0)}K` : `$${v}`} />
                <ZAxis type="number" dataKey="z" range={[60, 400]} />
                <Tooltip content={({ active, payload }) => {
                  if (!active || !payload?.[0]) return null;
                  const d = payload[0].payload;
                  return (
                    <div style={{ background: BRAND_COLORS.navy, color: 'white', padding: '8px 12px', borderRadius: '8px', fontSize: '12px' }}>
                      <div style={{ fontWeight: 700, marginBottom: '2px' }}>{d.name}</div>
                      <div>{d.x} deal{d.x !== 1 ? 's' : ''} · ${d.y.toLocaleString()}</div>
                      <div style={{ opacity: 0.7 }}>avg ${Math.round(d.z).toLocaleString()}</div>
                    </div>
                  );
                }} cursor={{ strokeDasharray: '3 3' }} />
                <Scatter
                  name="Partners"
                  data={partnerBubbleData}
                  fill={BRAND_COLORS.gold}
                  fillOpacity={0.75}
                  stroke={BRAND_COLORS.navy}
                  strokeWidth={1.5}
                  onClick={(d) => {
                    if (!d?.id) return;
                    const partnerDeals = revenueDeals.filter(rd => {
                      if (rd.partnerId === d.id) return true;
                      const lp = rd.prospectId ? allProspects.find(p => p.id === rd.prospectId) : null;
                      if (lp?.introducingPartnerId === d.id) return true;
                      return (rd.strategicPartners || []).some(sp => sp.partnerId === d.id);
                    });
                    setDrillDown({
                      title: `${d.name} — Deals`,
                      subtitle: `${partnerDeals.length} deal${partnerDeals.length !== 1 ? 's' : ''} · $${Math.round(d.y).toLocaleString()} revenue`,
                      rows: partnerDeals,
                      renderRow: drillRowDeal,
                      emptyLabel: 'No deals attributed to this partner.',
                    });
                  }}
                  style={{ cursor: 'pointer' }}
                />
              </ScatterChart>
            </ResponsiveContainer>
          )}
        </AnalyticsCard>

        {/* 2. Sector × Geography heatmap */}
        <AnalyticsCard index={1}>
          <h3 style={{ fontSize: '15px', fontWeight: 700, color: BRAND_COLORS.navy, margin: '0 0 2px 0' }}>Sector × Geography</h3>
          <p style={{ fontSize: '12px', color: '#9ca3af', margin: '0 0 16px 0' }}>Click any cell to see those deals · count by sector and continent</p>
          {sectorGeoMatrix.sectors.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#9ca3af', fontSize: '13px', padding: '40px 0' }}>No deals tagged with sector + geography</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ fontSize: '12px', borderCollapse: 'collapse', width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '6px 8px', color: BRAND_COLORS.textTertiary, fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Sector</th>
                    {sectorGeoMatrix.continents.map(c => (
                      <th key={c} style={{ textAlign: 'center', padding: '6px 8px', color: BRAND_COLORS.textTertiary, fontWeight: 600, fontSize: '11px' }}>{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sectorGeoMatrix.sectors.map(s => (
                    <tr key={s}>
                      <td style={{ padding: '4px 8px', fontWeight: 600, color: BRAND_COLORS.navy }}>{s}</td>
                      {sectorGeoMatrix.continents.map(c => {
                        const v = sectorGeoMatrix.grid[s]?.[c] || 0;
                        const intensity = v / sectorGeoMatrix.max;
                        return (
                          <td key={c} style={{ padding: '2px' }}>
                            <div
                              onClick={() => {
                                if (v === 0) return;
                                const matching = revenueDeals.filter(d => {
                                  const lp = d.prospectId ? allProspects.find(p => p.id === d.prospectId) : null;
                                  if (!lp) return false;
                                  const lpSectors = Array.isArray(lp.sectors) ? lp.sectors : (lp.sector ? [lp.sector] : []);
                                  const lpContinents = lp.geography?.continents || [];
                                  return lpSectors.includes(s) && lpContinents.includes(c);
                                });
                                setDrillDown({
                                  title: `${s} · ${c}`,
                                  subtitle: `${matching.length} deal${matching.length !== 1 ? 's' : ''}`,
                                  rows: matching,
                                  renderRow: drillRowDeal,
                                  emptyLabel: 'No deals in this cell.',
                                });
                              }}
                              style={{
                                width: '100%', minWidth: '40px', height: '32px',
                                borderRadius: '4px',
                                background: v > 0 ? `rgba(212,175,55,${0.15 + intensity * 0.85})` : '#f9fafb',
                                color: intensity > 0.5 ? '#fff' : BRAND_COLORS.navy,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontWeight: 700, fontSize: '12px',
                                transition: 'background 0.3s, transform 0.15s',
                                cursor: v > 0 ? 'pointer' : 'default',
                              }}
                              onMouseEnter={(e) => { if (v > 0) e.currentTarget.style.transform = 'scale(1.08)'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                            >{v > 0 ? v : ''}</div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </AnalyticsCard>

        {/* 3. Activity heatmap — last 12 weeks of daily prospect submissions */}
        <AnalyticsCard index={2}>
          <h3 style={{ fontSize: '15px', fontWeight: 700, color: BRAND_COLORS.navy, margin: '0 0 2px 0' }}>Activity Heatmap</h3>
          <p style={{ fontSize: '12px', color: '#9ca3af', margin: '0 0 16px 0' }}>Click a day to see prospects submitted · last 12 weeks</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '3px' }}>
            {Array.from({ length: 12 }, (_, weekIdx) => (
              <div key={weekIdx} style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                {Array.from({ length: 7 }, (_, dayIdx) => {
                  const cellIdx = weekIdx * 7 + dayIdx;
                  const cell = activityHeatmap[cellIdx];
                  if (!cell) return <div key={dayIdx} style={{ aspectRatio: '1', borderRadius: '3px', background: '#f9fafb' }} />;
                  const d = new Date(cell.dayMs);
                  return (
                    <div key={dayIdx}
                      title={`${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}: ${cell.count} prospect${cell.count !== 1 ? 's' : ''}`}
                      onClick={() => {
                        if (cell.count === 0) return;
                        const dayStart = cell.dayMs;
                        const dayEnd = dayStart + 86400e3;
                        const matching = prospects.filter(p => {
                          const t = prospectDateMs(p);
                          return t >= dayStart && t < dayEnd;
                        });
                        setDrillDown({
                          title: d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }),
                          subtitle: `${matching.length} prospect${matching.length !== 1 ? 's' : ''} submitted`,
                          rows: matching,
                          renderRow: drillRowProspect,
                          emptyLabel: 'No prospects on this day.',
                        });
                      }}
                      style={{
                        aspectRatio: '1',
                        borderRadius: '3px',
                        background: cell.count === 0 ? '#f3f4f6' : `rgba(10,20,40,${0.2 + cell.intensity * 0.8})`,
                        cursor: cell.count > 0 ? 'pointer' : 'default',
                        transition: 'background 0.2s, transform 0.15s',
                      }}
                      onMouseEnter={e => { if (cell.count > 0) e.currentTarget.style.transform = 'scale(1.2)'; }}
                      onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                    />
                  );
                })}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '6px', marginTop: '10px', fontSize: '10px', color: BRAND_COLORS.textTertiary }}>
            <span>Less</span>
            {[0.2, 0.4, 0.6, 0.8, 1].map(i => (
              <div key={i} style={{ width: '12px', height: '12px', borderRadius: '3px', background: `rgba(10,20,40,${i})` }} />
            ))}
            <span>More</span>
          </div>
        </AnalyticsCard>

        {/* 4. Cohort retention */}
        <AnalyticsCard index={3}>
          <h3 style={{ fontSize: '15px', fontWeight: 700, color: BRAND_COLORS.navy, margin: '0 0 2px 0' }}>Cohort Retention</h3>
          <p style={{ fontSize: '12px', color: '#9ca3af', margin: '0 0 16px 0' }}>Click any cohort label to see its partners · % still active per month since join</p>
          {cohortMatrix.cohorts.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#9ca3af', fontSize: '13px', padding: '40px 0' }}>No partner cohort data</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ fontSize: '11px', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '4px 8px', color: BRAND_COLORS.textTertiary, fontWeight: 600 }}>Cohort</th>
                    <th style={{ textAlign: 'center', padding: '4px 8px', color: BRAND_COLORS.textTertiary, fontWeight: 600 }}>n</th>
                    {Array.from({ length: Math.min(12, cohortMatrix.maxMonthsSince + 1) }, (_, i) => (
                      <th key={i} style={{ textAlign: 'center', padding: '4px 6px', color: BRAND_COLORS.textTertiary, fontWeight: 600, minWidth: '34px' }}>M{i}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cohortMatrix.cohorts.map(c => (
                    <tr key={c.key}>
                      <td
                        onClick={() => setDrillDown({
                          title: `${c.label} cohort`,
                          subtitle: `${c.partners.length} partner${c.partners.length !== 1 ? 's' : ''}`,
                          rows: c.partners,
                          renderRow: drillRowPartner,
                          emptyLabel: 'No partners in this cohort.',
                        })}
                        style={{ padding: '4px 8px', fontWeight: 600, color: BRAND_COLORS.navy, whiteSpace: 'nowrap', cursor: 'pointer', textDecoration: 'underline', textDecorationColor: `${BRAND_COLORS.gold}80` }}
                      >{c.label}</td>
                      <td style={{ padding: '4px 8px', textAlign: 'center', color: BRAND_COLORS.textSecondary }}>{c.partners.length}</td>
                      {Array.from({ length: Math.min(12, cohortMatrix.maxMonthsSince + 1) }, (_, i) => {
                        const cell = c.cells?.[i];
                        if (!cell) return <td key={i} style={{ padding: '2px' }}><div style={{ height: '22px', background: '#f9fafb', borderRadius: '3px' }} /></td>;
                        const intensity = cell.pct / 100;
                        return (
                          <td key={i} style={{ padding: '2px' }}>
                            <div title={`${cell.active}/${cell.total} active (${cell.pct}%)`} style={{
                              height: '22px', minWidth: '34px',
                              borderRadius: '3px',
                              background: `rgba(16,185,129,${0.15 + intensity * 0.85})`,
                              color: intensity > 0.5 ? '#fff' : BRAND_COLORS.navy,
                              fontSize: '10px', fontWeight: 700,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>{cell.pct}%</div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </AnalyticsCard>

        {/* 5. Pipeline forecast — stacked area */}
        <AnalyticsCard index={4}>
          <h3 style={{ fontSize: '15px', fontWeight: 700, color: BRAND_COLORS.navy, margin: '0 0 2px 0' }}>Pipeline Forecast</h3>
          <p style={{ fontSize: '12px', color: '#9ca3af', margin: '0 0 16px 0' }}>Weighted expected revenue (Draft × 20%, Active × 70%) — next 6 months</p>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={pipelineForecast} margin={{ top: 5, right: 10, bottom: 5, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#9ca3af' }} />
              <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} tickFormatter={v => v >= 1000000 ? `$${(v/1e6).toFixed(1)}M` : v >= 1000 ? `$${(v/1e3).toFixed(0)}K` : `$${v}`} />
              <Tooltip content={<NavyTooltip formatter={v => `$${Math.round(v).toLocaleString()}`} />} />
              <Legend wrapperStyle={{ fontSize: '11px' }} />
              <Area type="monotone" dataKey="active" name="Active (×70%)" stackId="1" stroke={BRAND_COLORS.gold} fill={BRAND_COLORS.gold} fillOpacity={0.7} animationDuration={800} />
              <Area type="monotone" dataKey="draft"  name="Draft (×20%)"  stackId="1" stroke={BRAND_COLORS.navy} fill={BRAND_COLORS.navy} fillOpacity={0.5} animationDuration={800} />
            </AreaChart>
          </ResponsiveContainer>
        </AnalyticsCard>

        {/* 6. Time-to-approval histogram */}
        <AnalyticsCard index={5}>
          <h3 style={{ fontSize: '15px', fontWeight: 700, color: BRAND_COLORS.navy, margin: '0 0 2px 0' }}>Time to Approval</h3>
          <p style={{ fontSize: '12px', color: '#9ca3af', margin: '0 0 16px 0' }}>Click a bar to see prospects · days from submission → approval</p>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={approvalDistribution} margin={{ top: 5, right: 10, bottom: 5, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#9ca3af' }} />
              <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} allowDecimals={false} />
              <Tooltip content={<NavyTooltip />} />
              <Bar
                dataKey="count"
                name="Prospects"
                radius={[6, 6, 0, 0]}
                animationDuration={700}
                onClick={(bucket) => {
                  if (!bucket || bucket.count === 0) return;
                  const { min, max, label } = bucket;
                  const matching = prospects.filter(p => {
                    if (p.approvalStatus !== 'Approved' || !p.approvalDate || !p.approvalRequestDate) return false;
                    const days = (new Date(p.approvalDate).getTime() - new Date(p.approvalRequestDate).getTime()) / 86400e3;
                    return days >= min && days <= max;
                  });
                  setDrillDown({
                    title: `Approved in ${label}`,
                    subtitle: `${matching.length} prospect${matching.length !== 1 ? 's' : ''}`,
                    rows: matching,
                    renderRow: drillRowProspect,
                    emptyLabel: 'No prospects in this bucket.',
                  });
                }}
                style={{ cursor: 'pointer' }}
              >
                {approvalDistribution.map((_, i) => <Cell key={i} fill={i < 2 ? BRAND_COLORS.statusSuccess : i < 4 ? BRAND_COLORS.gold : BRAND_COLORS.statusError} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </AnalyticsCard>

      </div>
    </div>
  );
};

// ─── COMPONENT: HotTopicFormModal (local to WorkflowsView) ──────────────────
const HotTopicFormModal = ({ topic, partners = [], teasers = [], onClose, onSave }) => {
  const [form, setForm] = useState({
    id: topic?.id || null,
    title: topic?.title || '',
    body: topic?.body || '',
    ctaLabel: topic?.ctaLabel || '',
    refTeaserId: topic?.refTeaserId || '',
    audienceMode: topic ? (topic.visibility === 'all' ? 'all' : 'specific') : 'all',
    selectedPartners: Array.isArray(topic?.visibility) ? topic.visibility : [],
    startsAt: topic?.startsAt ? topic.startsAt.slice(0, 10) : '',
    endsAt: topic?.endsAt ? topic.endsAt.slice(0, 10) : '',
    active: topic?.active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const u = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.title.trim()) { window.alert('Title is required'); return; }
    if (form.audienceMode === 'specific' && form.selectedPartners.length === 0) {
      window.alert('Select at least one partner, or switch audience to All'); return;
    }
    setSaving(true);
    const ok = await onSave({
      id: form.id || undefined,
      title: form.title.trim(),
      body: form.body,
      ctaLabel: form.ctaLabel.trim(),
      refTeaserId: form.refTeaserId || null,
      visibility: form.audienceMode === 'all' ? 'all' : form.selectedPartners,
      startsAt: form.startsAt ? new Date(form.startsAt).toISOString() : null,
      endsAt: form.endsAt ? new Date(`${form.endsAt}T23:59:59`).toISOString() : null,
      active: form.active,
    });
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full flex flex-col" style={{ maxWidth: 560, maxHeight: '90vh', border: `2px solid ${BRAND_COLORS.gold}` }} onClick={e => e.stopPropagation()}>
        <div style={{ background: BRAND_COLORS.navy, padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: 'white' }}>📣 {form.id ? 'Edit' : 'New'} Hot Topic</h2>
          <button onClick={onClose} className="text-white/70 hover:text-white text-xl font-bold">✕</button>
        </div>
        <div className="p-6 space-y-4 overflow-y-auto">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title * <span className="text-gray-400 font-normal">(max 150)</span></label>
            <input value={form.title} maxLength={150} onChange={e => u('title', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="e.g. New hot deal in MedTech 🔥" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Message <span className="text-gray-400 font-normal">(plain text, max 2000)</span></label>
            <textarea value={form.body} maxLength={2000} rows={4} onChange={e => u('body', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="What do you want partners to know?" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Link a teaser <span className="text-gray-400 font-normal">(optional)</span></label>
            <select value={form.refTeaserId} onChange={e => u('refTeaserId', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
              <option value="">— No teaser link —</option>
              {teasers.map(t => <option key={t.id} value={t.id}>{t.alias || t.name}</option>)}
            </select>
            {form.refTeaserId && (
              <input value={form.ctaLabel} maxLength={40} onChange={e => u('ctaLabel', e.target.value)}
                className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Button label (default: View Teaser →)" />
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Audience</label>
            <div className="flex gap-2 mb-2">
              {[{ v: 'all', l: 'All Partners' }, { v: 'specific', l: 'Specific Partners' }].map(opt => (
                <button key={opt.v} type="button" onClick={() => u('audienceMode', opt.v)}
                  className="flex-1 py-2 px-3 text-sm rounded-lg border-2 font-medium transition-all"
                  style={{
                    borderColor: form.audienceMode === opt.v ? BRAND_COLORS.navy : '#e5e7eb',
                    backgroundColor: form.audienceMode === opt.v ? `${BRAND_COLORS.navy}10` : 'white',
                    color: form.audienceMode === opt.v ? BRAND_COLORS.navy : '#6b7280',
                  }}>{opt.l}</button>
              ))}
            </div>
            {form.audienceMode === 'specific' && (
              <div className="max-h-36 overflow-y-auto border border-gray-300 rounded-lg p-2 space-y-1">
                {partners.filter(p => p.status === 'Active').map(p => (
                  <label key={p.id} className="flex items-center text-sm cursor-pointer">
                    <input type="checkbox" className="mr-2"
                      checked={form.selectedPartners.includes(p.id)}
                      onChange={e => u('selectedPartners', e.target.checked
                        ? [...form.selectedPartners, p.id]
                        : form.selectedPartners.filter(id => id !== p.id))} />
                    <span className="text-gray-700">{p.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Starts <span className="text-gray-400 font-normal">(optional)</span></label>
              <input type="date" value={form.startsAt} onChange={e => u('startsAt', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ends <span className="text-gray-400 font-normal">(optional)</span></label>
              <input type="date" value={form.endsAt} onChange={e => u('endsAt', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
          </div>
          <label className="flex items-center text-sm cursor-pointer">
            <input type="checkbox" className="mr-2" checked={form.active} onChange={e => u('active', e.target.checked)} />
            <span className="text-gray-700 font-medium">Active</span>
            <span className="text-gray-400 ml-2 text-xs">(partners see it on next login while active)</span>
          </label>
        </div>
        <div className="px-6 pb-5 pt-3 flex justify-end gap-2 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
          <button onClick={submit} disabled={saving}
            className="px-6 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
            style={{ backgroundColor: BRAND_COLORS.gold }}>
            {saving ? 'Saving…' : 'Publish'}
          </button>
        </div>
      </div>
    </div>
  );
};

const WorkflowsView = ({ workflows, onAdd, onToggle, onDelete, onRequestDelete, onEdit, onSaveNotificationSettings, role, showConfirm, hotTopics = [], partners = [], teasers = [], onSaveHotTopic, onDeleteHotTopic, draftWorkflows = [], onResumeDraft, onDiscardDraft }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [hotTopicForm, setHotTopicForm] = useState(null); // null | {} (new) | topic (edit)

  // ── Quick Notifications matrix (mirrors workflows where name starts with
  // "[NotifSettings] {role} · {event}"). Only shown to Admin / Super Admin.
  // Checking/unchecking a cell then clicking Save creates or deletes the
  // matching workflow row, which is immediately visible in the list below —
  // keeping the two representations in sync by construction.
  const NOTIFICATION_TYPES = ['Prospect Submitted', 'Prospect Approved', 'Prospect Rejected', 'Deal Created', 'Intro Deadline'];
  const NOTIFICATION_ROLES = ['Admin', 'Editor', 'Partner'];
  const buildNotifMatrix = (wfList) => {
    const m = {};
    NOTIFICATION_TYPES.forEach(t => { m[t] = {}; NOTIFICATION_ROLES.forEach(r => { m[t][r] = false; }); });
    (wfList || []).forEach(w => {
      const match = (w.name || '').match(/^\[NotifSettings\] (.+?) · (.+)$/);
      if (match && m[match[2]] && NOTIFICATION_ROLES.includes(match[1])) {
        m[match[2]][match[1]] = true;
      }
    });
    return m;
  };
  const [notifMatrix, setNotifMatrix] = useState(() => buildNotifMatrix(workflows));
  const [notifSaved, setNotifSaved] = useState(false);
  const [notifSaving, setNotifSaving] = useState(false);
  React.useEffect(() => {
    setNotifMatrix(buildNotifMatrix(workflows));
  }, [workflows]);

  const handleSaveNotif = async () => {
    if (!onSaveNotificationSettings || notifSaving) return;
    setNotifSaving(true);
    try {
      const result = await onSaveNotificationSettings(notifMatrix);
      // saveNotificationSettings returns { ok, saved, failed, noop? }.
      // Older callers returned undefined — treat that as success.
      // Failures already trigger setAppAlert in the parent; here we
      // just decide whether to flash the green "Saved!" confirmation.
      if (!result || result.ok !== false) {
        setNotifSaved(true);
        setTimeout(() => setNotifSaved(false), 2500);
      }
    } finally {
      setNotifSaving(false);
    }
  };

  const canEditNotifMatrix = role === 'Admin' || role === 'Super Admin';
  
  const triggerCategories = {
    'Prospects': [
      { value: 'prospect_submitted', label: 'Prospect Submitted', icon: '📄', description: 'When a partner submits a new prospect for review' },
      { value: 'prospect_approved', label: 'Prospect Approved', icon: '✅', description: 'When admin approves a submitted prospect' },
      { value: 'prospect_rejected', label: 'Prospect Rejected', icon: '❌', description: 'When admin rejects a submitted prospect' },
      { value: 'prospect_auto_rejected', label: 'Auto-Rejected (7 days)', icon: '⏰', description: 'When a prospect is auto-rejected after 7 days without review' },
      { value: 'intro_completed', label: 'Introduction Completed', icon: '🤝', description: 'When an introduction is logged for a prospect' },
      { value: 'intro_deadline_approaching', label: 'Intro Deadline Approaching', icon: ' ', description: 'When intro deadline is within 7 days' },
      { value: 'intro_overdue', label: 'Introduction Overdue', icon: '🚨', description: 'When intro deadline has passed' },
      { value: 'validity_expiring_soon', label: 'Validity Expiring Soon', icon: '', description: 'When 18-month validity period is expiring in 30 days' }
    ],
    'Partners': [
      { value: 'partner_created', label: 'New Partner Created', icon: '➕', description: 'When a new partner is added to the system' },
      { value: 'partner_status_changed', label: 'Partner Status Changed', icon: '🔄', description: 'When partner status changes (Active/Tail/Terminated)' },
      { value: 'partner_entering_tail', label: 'Entering Tail Period', icon: '⏳', description: 'When partner\'s agreement ends and tail period begins' },
      { value: 'agreement_expiring_soon', label: 'Agreement Expiring', icon: '', description: 'When partner agreement is expiring in 30 days' },
      { value: 'tail_period_ending', label: 'Tail Period Ending', icon: '', description: 'When tail period is ending in 30 days' }
    ],
    'Revenue': [
      { value: 'revenue_deal_created', label: 'Revenue Deal Created', icon: '💰', description: 'When a new revenue deal is recorded' },
      { value: 'revenue_deal_threshold', label: 'Deal Exceeds Threshold', icon: '', description: 'When a revenue deal exceeds a specified amount' },
      { value: 'pool_created', label: 'Pool Created', icon: '', description: 'When a new commission pool is created' },
      { value: 'pool_distributed', label: 'Pool Distributed', icon: '', description: 'When a pool distribution is completed' },
      { value: 'transaction_recorded', label: 'Transaction Recorded', icon: '📊', description: 'When a new transaction is logged' }
    ],
    'Content': [
      { value: 'teaser_published', label: 'Teaser Published', icon: '📢', description: 'When a new investment teaser is published' },
      { value: 'teaser_updated', label: 'Teaser Updated', icon: '✏️', description: 'When an existing teaser is modified' }
    ],
    'Users': [
      { value: 'user_created', label: 'New User Created', icon: '👤', description: 'When a new user account is created' },
      { value: 'user_login_first_time', label: 'User First Login', icon: '🎉', description: 'When a user logs in for the first time' },
      { value: 'user_inactive_30_days', label: 'User Inactive 30 Days', icon: '', description: 'When a user hasn\'t logged in for 30 days' },
      { value: 'user_status_changed', label: 'User Status Changed', icon: '🔒', description: 'When user status changes (Active/Inactive)' }
    ],
    'System': [
      { value: 'audit_log_critical', label: 'Critical Audit Event', icon: '🔍', description: 'When a critical system event is logged' },
      { value: 'data_export_requested', label: 'Data Export Requested', icon: '📤', description: 'When a user requests data export' }
    ],
    'Scheduled': [
      { value: 'daily_summary', label: 'Daily Summary', icon: '', description: 'Runs every day at 9 AM' },
      { value: 'weekly_report', label: 'Weekly Report', icon: '', description: 'Runs every Monday at 9 AM' },
      { value: 'monthly_metrics', label: 'Monthly Metrics', icon: '', description: 'Runs on the 1st of each month' }
    ]
  };
  
  const allCategories = Object.keys(triggerCategories);
  
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: BRAND_COLORS.navy }}>Workflow Automation</h2>
          <p className="text-gray-600 mt-1">Create automated workflows triggered by system events</p>
        </div>
        <button
          onClick={onAdd}
          className="flex items-center space-x-2 px-4 py-2 rounded-lg text-white font-medium transition-colors shadow-lg"
          style={{ background: BRAND_COLORS.navy }}
        >
          <Plus className="w-5 h-5" />
          <span>Create Workflow</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        {[
          { label: 'Active',   value: workflows.filter(w => w.enabled).length,        icon: Check,       accent: BRAND_COLORS.statusSuccess },
          { label: 'Disabled', value: workflows.filter(w => !w.enabled).length,       icon: X,           accent: BRAND_COLORS.textTertiary },
          { label: 'Total',    value: workflows.length,                                icon: Zap,         accent: BRAND_COLORS.gold },
          { label: 'Triggers', value: Object.values(triggerCategories).flat().length, icon: AlertCircle, accent: BRAND_COLORS.statusInfo },
        ].map(c => <KPICard key={c.label} {...c} />)}
      </div>

      {/* ── Hot Topics — announcement popups for partners ── */}
      {onSaveHotTopic && (
        <div style={{ background: BRAND_COLORS.bgSurface, border: `1px solid ${BRAND_COLORS.borderNavy12}`, borderLeft: `4px solid ${BRAND_COLORS.gold}`, borderRadius: '12px', padding: '20px 24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-lg font-bold" style={{ color: BRAND_COLORS.navy }}>📣 Hot Topics</h3>
              <p className="text-xs mt-0.5" style={{ color: BRAND_COLORS.textTertiary }}>
                Popup announcements shown to targeted partners on every login, until they opt out or you deactivate.
              </p>
            </div>
            <button onClick={() => setHotTopicForm({})}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-white"
              style={{ backgroundColor: BRAND_COLORS.gold }}>
              <Plus className="w-4 h-4" /> New Hot Topic
            </button>
          </div>
          {hotTopics.length === 0 ? (
            <p className="text-sm text-center py-4" style={{ color: BRAND_COLORS.textTertiary }}>No hot topics yet. Create one to broadcast to partners.</p>
          ) : (
            <div className="space-y-2">
              {hotTopics.map(t => {
                const now = new Date();
                const inWindow = (!t.startsAt || new Date(t.startsAt) <= now) && (!t.endsAt || new Date(t.endsAt) >= now);
                const live = t.active !== false && inWindow;
                const audience = t.visibility === 'all' ? 'All partners' : `${(t.visibility || []).length} partner${(t.visibility || []).length !== 1 ? 's' : ''}`;
                const optOuts = (t.dismissedBy || []).length;
                return (
                  <div key={t.id} className="p-3 rounded-lg flex items-center justify-between gap-3" style={{ backgroundColor: BRAND_COLORS.bgApp, border: `1px solid ${BRAND_COLORS.borderNavy12}` }}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-sm truncate" style={{ color: BRAND_COLORS.navy }}>{t.title}</p>
                        <span style={{ padding: '2px 8px', borderRadius: '999px', fontSize: '10px', fontWeight: 700, letterSpacing: '0.04em',
                          background: live ? `${BRAND_COLORS.statusSuccess}18` : `${BRAND_COLORS.textTertiary}18`,
                          color: live ? BRAND_COLORS.statusSuccess : BRAND_COLORS.textTertiary }}>
                          {live ? 'LIVE' : t.active === false ? 'OFF' : 'SCHEDULED'}
                        </span>
                      </div>
                      <p className="text-xs mt-0.5" style={{ color: BRAND_COLORS.textTertiary }}>
                        {audience}{t.refTeaserId ? ' · linked teaser' : ''}{optOuts > 0 ? ` · ${optOuts} opted out` : ''}
                        {t.endsAt ? ` · ends ${new Date(t.endsAt).toLocaleDateString()}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={() => onSaveHotTopic({ ...t, active: t.active === false })}
                        title={t.active === false ? 'Activate' : 'Deactivate'}
                        className="px-2 py-1 text-xs rounded border hover:bg-gray-50"
                        style={{ borderColor: BRAND_COLORS.borderNavy12, color: BRAND_COLORS.navy }}>
                        {t.active === false ? 'Activate' : 'Deactivate'}
                      </button>
                      <button onClick={() => setHotTopicForm(t)} title="Edit" className="p-1.5 rounded hover:bg-gray-100">
                        <Edit2 className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} />
                      </button>
                      <button onClick={() => showConfirm(`Delete hot topic "${t.title}"? Partners will stop seeing it immediately.`, () => onDeleteHotTopic(t.id), 'Delete')}
                        title="Delete" className="p-1.5 rounded hover:bg-red-50">
                        <Trash2 className="w-4 h-4" style={{ color: BRAND_COLORS.statusError }} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {hotTopicForm !== null && (
            <HotTopicFormModal
              topic={hotTopicForm.id ? hotTopicForm : null}
              partners={partners}
              teasers={teasers}
              onClose={() => setHotTopicForm(null)}
              onSave={onSaveHotTopic}
            />
          )}
        </div>
      )}

      {/* Quick Notifications Matrix — simplified UI for the most common
          workflow case (role receives notification+email on event).
          Reads from and writes to the workflows list below. */}
      {canEditNotifMatrix && (
        <div className="bg-white rounded-xl shadow-md border border-gray-100 p-6">
          <div className="mb-4">
            <h3 className="text-xl font-bold" style={{ color: BRAND_COLORS.navy }}>Quick Notifications</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Turn on a cell to create a workflow that sends an in-app notification and email to that role when the event fires.
              Each checked cell maps to one <span className="font-mono text-xs">[NotifSettings]</span> workflow in the list below.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2" style={{ borderColor: BRAND_COLORS.navy + '15' }}>
                  <th className="text-left py-3 pr-6 font-semibold text-xs uppercase tracking-wide text-gray-500">Event</th>
                  {NOTIFICATION_ROLES.map(r => (
                    <th key={r} className="text-center py-3 px-6 font-semibold text-xs uppercase tracking-wide" style={{ color: BRAND_COLORS.navy }}>{r}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {NOTIFICATION_TYPES.map((type, i) => (
                  <tr key={type} className={i % 2 !== 0 ? 'bg-gray-50' : ''} style={{ borderBottom: '1px solid ' + BRAND_COLORS.navy + '10' }}>
                    <td className="py-3 pr-6 font-medium" style={{ color: BRAND_COLORS.navy }}>{type}</td>
                    {NOTIFICATION_ROLES.map(r => (
                      <td key={r} className="py-3 px-6 text-center">
                        <input
                          type="checkbox"
                          checked={notifMatrix[type]?.[r] || false}
                          onChange={e => setNotifMatrix(m => ({ ...m, [type]: { ...m[type], [r]: e.target.checked } }))}
                          className="w-4 h-4 rounded cursor-pointer"
                          style={{ accentColor: BRAND_COLORS.gold }}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-6">
            <button
              onClick={handleSaveNotif}
              disabled={notifSaving}
              className="flex items-center gap-2 px-6 py-2.5 text-white rounded-lg font-medium text-sm transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ backgroundColor: notifSaved ? '#16a34a' : BRAND_COLORS.gold }}
            >
              {notifSaving
                ? <><Clock className="w-4 h-4 animate-spin" /> Saving…</>
                : notifSaved
                  ? <><CheckCircle className="w-4 h-4" /> Saved!</>
                  : <><Save className="w-4 h-4" /> Save Notifications</>}
            </button>
          </div>
        </div>
      )}

      {/* Unfinished New Workflow wizards parked with Save & Close. Workflows has
          no filter-chip row, so drafts sit in their own section above the list
          rather than behind a chip. */}
      {draftWorkflows.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: BRAND_COLORS.textSecondary }}>
            Drafts ({draftWorkflows.length})
          </h3>
          <DraftsPanel
            drafts={draftWorkflows}
            labelOf={d => d.name}
            stepLabels={['Trigger', 'Conditions', 'Actions']}
            onResume={onResumeDraft}
            onDiscard={onDiscardDraft}
          />
        </div>
      )}

      {/* Workflow Cards */}
      <div className="space-y-4">
        {workflows.map(workflow => (
          <div 
            key={workflow.id} 
            className="bg-white rounded-lg shadow-md p-6 border-l-4 transition-all hover:shadow-lg"
            style={{ borderColor: workflow.enabled ? BRAND_COLORS.gold : '#9CA3AF' }}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4 flex-1">
                <div className={`p-3 rounded-lg ${workflow.enabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                  <Zap className={`w-6 h-6 ${workflow.enabled ? 'text-green-600' : 'text-gray-400'}`} />
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold" style={{ color: BRAND_COLORS.navy }}>
                      {workflow.name}
                    </h3>
                    <StatusChip variant={workflow.enabled ? 'success' : 'neutral'}>
                      {workflow.enabled ? 'Active' : 'Disabled'}
                    </StatusChip>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-3">{workflow.description}</p>
                  
                  <div className="flex flex-wrap gap-4 text-sm">
                    <div className="flex items-center text-gray-700">
                      <span className="font-medium mr-2">Trigger:</span>
                      <span className="px-2 py-1 rounded" style={{ backgroundColor: `${BRAND_COLORS.gold}20`, color: BRAND_COLORS.navy }}>
                        {workflow.trigger.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                    </div>
                    
                    {workflow.conditions && workflow.conditions.length > 0 && (
                      <div className="flex items-center text-gray-700">
                        <span className="font-medium mr-2">Conditions:</span>
                        <span className="text-gray-600">{workflow.conditions.length} rule(s)</span>
                      </div>
                    )}
                    
                    <div className="flex items-center text-gray-700">
                      <span className="font-medium mr-2">Actions:</span>
                      <span className="text-gray-600">{workflow.actions.length} action(s)</span>
                    </div>
                  </div>
                  
                  {/* Action Summary */}
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-xs text-gray-500 font-medium mb-2">ACTIONS:</p>
                    <div className="flex flex-wrap gap-2">
                      {workflow.actions.map((action, idx) => (
                        <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                          {action.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                {/* Toggle Switch */}
                <button
                  onClick={() => {
                    const action = workflow.enabled ? 'disable' : 'enable';
                    const msg = workflow.enabled
                      ? `Are you sure you want to disable "${workflow.name}"?\nThis workflow will stop running until re-enabled.`
                      : `Are you sure you want to enable "${workflow.name}"?\nThis workflow will start running automatically.`;
                    showConfirm(msg, () => onToggle(workflow.id));
                  }}
                  title={workflow.enabled ? 'Click to disable' : 'Click to enable'}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    padding: '6px 12px', borderRadius: '999px', border: 'none',
                    cursor: 'pointer', transition: 'all 0.2s',
                    backgroundColor: workflow.enabled ? '#DCFCE7' : '#F3F4F6',
                    color: workflow.enabled ? '#065F46' : '#6B7280',
                  }}
                >
                  {/* Track */}
                  <div style={{
                    width: '36px', height: '20px', borderRadius: '999px',
                    backgroundColor: workflow.enabled ? '#10B981' : '#D1D5DB',
                    position: 'relative', transition: 'background 0.2s', flexShrink: 0
                  }}>
                    {/* Knob */}
                    <div style={{
                      position: 'absolute', top: '2px',
                      left: workflow.enabled ? '18px' : '2px',
                      width: '16px', height: '16px', borderRadius: '50%',
                      backgroundColor: 'white',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      transition: 'left 0.2s'
                    }} />
                  </div>
                  <span style={{ fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {workflow.enabled ? 'Active' : 'Disabled'}
                  </span>
                </button>

                {/* Edit */}
                {onEdit && (
                  <button
                    onClick={() => onEdit(workflow)}
                    className="p-2 text-blue-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Edit workflow"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                )}

                {/* Delete — Admin only via prop */}
                {onDelete && (
                  <button
                    onClick={() => {
                      showConfirm(`Delete "${workflow.name}"?\nThis cannot be undone.`, () => onDelete(workflow.id), 'Delete');
                    }}
                    className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete workflow"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                {onRequestDelete && !onDelete && (
                  <button
                    onClick={() => {
                      showConfirm(`Request deletion of "${workflow.name}"? Admin will be notified.`, () => {
                        onRequestDelete('workflow', workflow.id, workflow.name);
                      });
                    }}
                    className="p-2 hover:bg-yellow-50 rounded-lg transition-colors"
                    title="Request delete"
                    style={{ color: '#92400E' }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {workflows.length === 0 && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <div style={{ color: BRAND_COLORS.navy, opacity: 0.55, marginBottom: 16, display: 'flex', justifyContent: 'center' }}>
            <NauticalMark name="lighthouse" size={80} />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Workflows Yet</h3>
          <p className="text-gray-600 mb-4">Create your first automated workflow to streamline your processes</p>
          <button
            onClick={onAdd}
            className="inline-flex items-center space-x-2 px-4 py-2 rounded-lg text-white transition-colors"
            style={{ background: BRAND_COLORS.navy }}
          >
            <Plus className="w-5 h-5" />
            <span>Create Workflow</span>
          </button>
        </div>
      )}

      {/* Categorized Trigger Events Reference */}
      <div className="rounded-lg p-6 border-2" style={{ backgroundColor: `${BRAND_COLORS.cream}40`, borderColor: `${BRAND_COLORS.gold}60` }}>
        <h3 className="text-lg font-semibold mb-4 flex items-center" style={{ color: BRAND_COLORS.navy }}>
          <AlertCircle className="w-5 h-5 mr-2" style={{ color: BRAND_COLORS.gold }} />
          Available Trigger Events ({Object.values(triggerCategories).flat().length} triggers)
        </h3>
        
        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
              selectedCategory === 'all'
                ? 'text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
            style={selectedCategory === 'all' ? { backgroundColor: BRAND_COLORS.gold } : {}}
          >
            All ({Object.values(triggerCategories).flat().length})
          </button>
          {allCategories.map(category => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                selectedCategory === category
                  ? 'text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
              style={selectedCategory === category ? { backgroundColor: BRAND_COLORS.royalBlue } : {}}
            >
              {category} ({triggerCategories[category].length})
            </button>
          ))}
        </div>
        
        {/* Triggers Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
          {(selectedCategory === 'all' 
            ? Object.entries(triggerCategories).flatMap(([cat, triggers]) => 
                triggers.map(t => ({ ...t, category: cat }))
              )
            : triggerCategories[selectedCategory] || []
          ).map((trigger, idx) => (
            <div key={idx} className="bg-white p-3 rounded-lg border hover:shadow-md transition-shadow" style={{ borderColor: `${BRAND_COLORS.gold}30` }}>
              <div className="flex items-start space-x-2 mb-1">
                <span className="text-lg">{trigger.icon}</span>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-medium" style={{ color: BRAND_COLORS.navy }}>
                      {trigger.label}
                    </span>
                    {selectedCategory === 'all' && (
                      <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: `${BRAND_COLORS.royalBlue}20`, color: BRAND_COLORS.royalBlue }}>
                        {trigger.category}
                      </span>
                    )}
                  </div>
                  <p className="text-gray-600 text-xs mt-1">{trigger.description}</p>
                  <code className="text-xs text-gray-500 mt-1 block">{trigger.value}</code>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MODAL: New Workflow
// ============================================================================

const EMAIL_TEMPLATES = [
  { slug: 'prospect-submitted',          label: 'New Prospect Submitted' },
  { slug: 'prospect-approved',           label: 'Prospect Approved' },
  { slug: 'prospect-rejected',           label: 'Prospect Rejected' },
  { slug: 'prospect-auto-rejected',      label: 'Prospect Auto Rejected' },
  { slug: 'intro-completed',             label: 'Intro Completed' },
  { slug: 'intro-deadline-approaching',  label: 'Intro Deadline Approaching' },
  { slug: 'intro-overdue',               label: 'Intro Overdue' },
  { slug: 'validity-expiring-soon',      label: 'Validity Expiring Soon' },
  { slug: 'partner-created',             label: 'Partner Created' },
  { slug: 'partner-status-changed',      label: 'Partner Status Changed' },
  { slug: 'partner-entering-tail',       label: 'Partner Entering Tail Period' },
  { slug: 'agreement-expiring-soon',     label: 'Agreement Expiring Soon' },
  { slug: 'tail-period-ending',          label: 'Tail Period Ending' },
  { slug: 'teaser-published',            label: 'Teaser Published' },
  { slug: 'user-created',               label: 'User Created' },
  { slug: 'user-status-changed',        label: 'User Status Changed' },
  { slug: 'revenue-deal-created',       label: 'New Revenue Deal Created' },
];

// `draft` — a workflow row parked mid-wizard via Save & Close. When present the
// wizard seeds itself from that row and opens on the step it was left on.
const NewWorkflowModal = ({ show, onClose, onSubmit, onSaveDraft, draft = null, partners }) => {
  const [step, setStep] = useState(1);
  const [stepError, setStepError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    trigger: '',
    conditions: [],
    actions: [{ type: 'send_notification', message: '' }]
  });
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  React.useEffect(() => {
    if (show) {
      setStep(draft?.draftStep || 1);
      setStepError('');
      setFormData(draft ? {
        name:        draft.name || '',
        description: draft.description || '',
        trigger:     draft.trigger || '',
        conditions:  draft.conditions || [],
        actions:     draft.actions?.length ? draft.actions : [{ type: 'send_notification', message: '' }],
      } : { name: '', description: '', trigger: '', conditions: [], actions: [{ type: 'send_notification', message: '' }] });
      setTemplatesLoading(true);
      import('./supabaseClient')
        .then(({ invokeFunction }) => invokeFunction('send-email', { body: { action: 'list_templates' } }))
        .then(({ data }) => setAvailableTemplates(data?.templates || EMAIL_TEMPLATES))
        .catch(() => setAvailableTemplates(EMAIL_TEMPLATES))
        .finally(() => setTemplatesLoading(false));
    }
    // draft?.id is in the deps so opening a *different* draft reseeds the form.
  }, [show, draft?.id]);

  if (!show) return null;

  const triggerOptions = [
    // Prospects (8)
    { value: 'prospect_submitted',          label: '📄 Prospect Submitted',               category: 'Prospects' },
    { value: 'prospect_approved',           label: '✅ Prospect Approved',                category: 'Prospects' },
    { value: 'prospect_rejected',           label: '❌ Prospect Rejected',                category: 'Prospects' },
    { value: 'prospect_auto_rejected',      label: '⏰ Prospect Auto-Rejected (7 days)',   category: 'Prospects' },
    { value: 'intro_completed',             label: '🤝 Introduction Completed',           category: 'Prospects' },
    { value: 'intro_deadline_approaching',  label: '⏱️ Intro Deadline Approaching (7d)',  category: 'Prospects' },
    { value: 'intro_overdue',               label: '🚨 Introduction Overdue',             category: 'Prospects' },
    { value: 'validity_expiring_soon',      label: '📅 Validity Period Expiring (30d)',   category: 'Prospects' },
    // Partners (5)
    { value: 'partner_created',             label: '➕ New Partner Created',              category: 'Partners' },
    { value: 'partner_status_changed',      label: '🔄 Partner Status Changed',           category: 'Partners' },
    { value: 'partner_entering_tail',       label: '⏳ Partner Entering Tail Period',     category: 'Partners' },
    { value: 'agreement_expiring_soon',     label: '📋 Agreement Expiring (30 days)',     category: 'Partners' },
    { value: 'tail_period_ending',          label: '🏁 Tail Period Ending (30 days)',     category: 'Partners' },
    // Revenue (5)
    { value: 'revenue_deal_created',        label: '💰 Revenue Deal Created',             category: 'Revenue' },
    { value: 'revenue_deal_threshold',      label: '💎 Revenue Deal Exceeds Threshold',   category: 'Revenue' },
    { value: 'pool_created',                label: '🏦 Commission Pool Created',          category: 'Revenue' },
    { value: 'pool_distributed',            label: '💸 Pool Distribution Completed',      category: 'Revenue' },
    { value: 'transaction_recorded',        label: '📊 Transaction Recorded',             category: 'Revenue' },
    // Content (2)
    { value: 'teaser_published',            label: '📢 Teaser Published',                 category: 'Content' },
    { value: 'teaser_updated',              label: '✏️ Teaser Updated',                  category: 'Content' },
    // Users (5)
    { value: 'user_created',                label: '👤 New User Created',                 category: 'Users' },
    { value: 'user_login_first_time',       label: '🎉 User First Login',                 category: 'Users' },
    { value: 'user_inactive_30_days',       label: '😴 User Inactive for 30 Days',        category: 'Users' },
    { value: 'user_status_changed',         label: '🔒 User Status Changed',              category: 'Users' },
    { value: 'user_password_reset',         label: '🔑 User Password Reset',              category: 'Users' },
    // System (3)
    { value: 'audit_log_critical',          label: '🔍 Critical Audit Event',             category: 'System' },
    { value: 'data_export_requested',       label: '📤 Data Export Requested',            category: 'System' },
    { value: 'system_maintenance',          label: '🛠️ System Maintenance Scheduled',    category: 'System' },
    // Scheduled (3)
    { value: 'daily_summary',               label: '📆 Daily Summary (9 AM)',             category: 'Scheduled' },
    { value: 'weekly_report',               label: '📊 Weekly Report (Monday 9 AM)',      category: 'Scheduled' },
    { value: 'monthly_metrics',             label: '📈 Monthly Metrics (1st of month)',   category: 'Scheduled' },
  ];

  const TRIGGER_FIELDS = {
    // Prospects
    prospect_submitted:          ['status', 'sector', 'country', 'estimated_value', 'company_name', 'contact_name'],
    prospect_approved:           ['status', 'sector', 'country', 'estimated_value', 'company_name', 'contact_name'],
    prospect_rejected:           ['status', 'sector', 'country', 'estimated_value', 'company_name', 'contact_name'],
    prospect_auto_rejected:      ['status', 'sector', 'country', 'estimated_value', 'company_name', 'contact_name'],
    intro_completed:             ['status', 'sector', 'country', 'estimated_value', 'company_name', 'contact_name'],
    intro_deadline_approaching:  ['status', 'sector', 'country', 'estimated_value', 'company_name', 'contact_name'],
    intro_overdue:               ['status', 'sector', 'country', 'estimated_value', 'company_name', 'contact_name'],
    validity_expiring_soon:      ['status', 'sector', 'country', 'estimated_value', 'company_name', 'contact_name'],
    // Partners
    partner_created:             ['status', 'tier', 'sector', 'country', 'name'],
    partner_status_changed:      ['status', 'tier', 'sector', 'country', 'name'],
    partner_entering_tail:       ['status', 'tier', 'sector', 'country', 'name'],
    agreement_expiring_soon:     ['status', 'tier', 'sector', 'country', 'name'],
    tail_period_ending:          ['status', 'tier', 'sector', 'country', 'name'],
    // Revenue
    revenue_deal_created:        ['status', 'type', 'value', 'partner_name', 'prospect_name'],
    revenue_deal_threshold:      ['status', 'type', 'value', 'partner_name', 'prospect_name'],
    pool_created:                ['type', 'value', 'partner_name'],
    pool_distributed:            ['type', 'value', 'partner_name'],
    transaction_recorded:        ['status', 'type', 'value', 'partner_name'],
    // Content
    teaser_published:            ['name', 'sector', 'geography'],
    teaser_updated:              ['name', 'sector', 'geography'],
    // Users
    user_created:                ['role', 'email', 'name', 'status'],
    user_login_first_time:       ['role', 'email', 'name', 'status'],
    user_inactive_30_days:       ['role', 'email', 'name', 'status'],
    user_status_changed:         ['role', 'email', 'name', 'status'],
    user_password_reset:         ['role', 'email', 'name'],
    // System
    audit_log_critical:          ['type', 'severity'],
    data_export_requested:       ['type', 'role', 'email'],
    system_maintenance:          ['type'],
    // Scheduled
    daily_summary:               ['day', 'time'],
    weekly_report:               ['day', 'time'],
    monthly_metrics:             ['day', 'time'],
  };

  const operatorOptions = [
    { value: 'equals',       label: 'equals' },
    { value: 'not_equals',   label: 'not equals' },
    { value: 'contains',     label: 'contains' },
    { value: 'greater_than', label: 'greater than' },
    { value: 'less_than',    label: 'less than' },
  ];

  const actionTypes = [
    { value: 'send_notification', label: 'Send In-App Notification' },
    { value: 'send_email',        label: 'Send Email' },
    { value: 'update_field',      label: 'Update Field' },
    { value: 'create_task',       label: 'Create Task' },
  ];

  const addCondition = () => setFormData(f => ({ ...f, conditions: [...f.conditions, { field: '', operator: 'equals', value: '' }] }));
  const updateCondition = (i, key, val) => setFormData(f => { const c = [...f.conditions]; c[i] = { ...c[i], [key]: val }; return { ...f, conditions: c }; });
  const removeCondition = (i) => setFormData(f => ({ ...f, conditions: f.conditions.filter((_, idx) => idx !== i) }));

  const addAction = () => setFormData(f => ({ ...f, actions: [...f.actions, { type: 'send_notification', message: '' }] }));
  const updateAction = (i, key, val) => setFormData(f => { const a = [...f.actions]; a[i] = { ...a[i], [key]: val }; return { ...f, actions: a }; });
  const removeAction = (i) => setFormData(f => ({ ...f, actions: f.actions.filter((_, idx) => idx !== i) }));

  const handleNext = () => {
    if (step === 1) {
      if (!formData.name.trim() || !formData.trigger) {
        setStepError('Workflow name and trigger event are required.');
        return;
      }
    }
    setStepError('');
    setStep(s => s + 1);
  };

  const handleBack = () => { setStepError(''); setStep(s => s - 1); };

  const handleSubmit = () => {
    for (const a of formData.actions) {
      if (a.type === 'send_notification' && !a.message?.trim()) { setStepError('Send Notification actions require a message.'); return; }
      if (a.type === 'send_email' && (!a.to?.trim() || !a.subject?.trim() || !a.template_name?.trim())) {
        setStepError('Send Email actions require a To address, Subject, and Template.'); return;
      }
    }
    // Resuming a draft passes its id through so the parked row is completed
    // rather than duplicated.
    onSubmit(draft?.id ? { ...formData, id: draft.id } : formData);
    onClose();
  };

  // ── Save & Close ───────────────────────────────────────────────────────────
  // Only the name is enforced; the trigger and actions are still required to
  // finish the wizard. A drafted workflow is stored disabled by definition — it
  // has no complete trigger/action set to run against yet.
  const draftBlockedReason = formData.name.trim() ? '' : 'Enter the workflow name first — it identifies the draft.';
  const handleSaveDraft = async () => {
    if (draftBlockedReason) return;
    setStepError('');
    const ok = await onSaveDraft?.({ ...formData, id: draft?.id, enabled: false }, step);
    if (ok) onClose();   // stay open if the write failed, so nothing is lost
  };

  const selectedTrigger = triggerOptions.find(t => t.value === formData.trigger);

  const wizardSteps = [
    { n: 1, label: 'Trigger' },
    { n: 2, label: 'Conditions' },
    { n: 3, label: 'Actions' },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl my-auto flex flex-col" style={{ maxHeight: 'calc(100vh - 2rem)' }} onClick={e => e.stopPropagation()}>

        {/* Navy Header */}
        <div className="flex-shrink-0 px-6 py-4 flex items-center justify-between rounded-t-xl" style={{ background: BRAND_COLORS.navy, borderBottom: `3px solid ${BRAND_COLORS.gold}` }}>
          <div>
            <h2 className="text-lg font-bold text-white">Create Workflow Automation</h2>
            <p className="text-xs mt-0.5" style={{ color: BRAND_COLORS.cream }}>Step {step} of 3</p>
          </div>
          <button onClick={onClose} className="text-white hover:text-gray-300 transition-colors p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step Indicator */}
        <div className="flex-shrink-0 px-6 py-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            {wizardSteps.map(({ n, label }, idx, arr) => (
              <React.Fragment key={n}>
                <div className="flex flex-col items-center gap-1">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all"
                    style={{
                      backgroundColor: step === n ? BRAND_COLORS.gold : step > n ? '#10B981' : '#E5E7EB',
                      color: step >= n ? 'white' : '#9CA3AF',
                    }}>
                    {step > n ? '✓' : n}
                  </div>
                  <span className="text-xs font-medium" style={{ color: step === n ? BRAND_COLORS.navy : '#9CA3AF' }}>{label}</span>
                </div>
                {idx < arr.length - 1 && (
                  <div className="flex-1 h-0.5 mx-2 mb-4 rounded" style={{ backgroundColor: step > n ? '#10B981' : '#E5E7EB' }} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5">

          {/* Step 1: Trigger */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <h3 className="text-base font-semibold mb-1" style={{ color: BRAND_COLORS.navy }}>Step 1: Trigger</h3>
                <p className="text-sm text-gray-500">Name your workflow and choose what event triggers it.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Workflow Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g., Notify on Prospect Approval"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A1C5C] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={e => setFormData(f => ({ ...f, description: e.target.value }))}
                  placeholder="What does this workflow do?"
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A1C5C] focus:border-transparent resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Trigger Event <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.trigger}
                  onChange={e => setFormData(f => ({ ...f, trigger: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1A1C5C] focus:border-transparent"
                >
                  <option value="">— Select a trigger —</option>
                  {['Prospects', 'Partners', 'Revenue', 'Content', 'Users', 'System', 'Scheduled'].map(cat => (
                    <optgroup key={cat} label={cat}>
                      {triggerOptions.filter(t => t.category === cat).map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Step 2: Conditions */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-base font-semibold" style={{ color: BRAND_COLORS.navy }}>Step 2: Conditions</h3>
                  <p className="text-sm text-gray-500 mt-0.5">Add optional filters — leave empty to run on every trigger.</p>
                </div>
                <button
                  type="button"
                  onClick={addCondition}
                  className="flex-shrink-0 text-sm font-medium px-3 py-1.5 rounded-lg border transition-colors hover:bg-yellow-50"
                  style={{ borderColor: BRAND_COLORS.gold, color: BRAND_COLORS.navy }}
                >
                  + Add Condition
                </button>
              </div>
              {formData.conditions.length === 0 ? (
                <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
                  <p className="text-sm text-gray-400">No conditions — workflow runs for every trigger event.</p>
                  <button type="button" onClick={addCondition} className="mt-2 text-sm font-medium hover:underline" style={{ color: BRAND_COLORS.navy }}>
                    + Add a condition
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {formData.conditions.map((cond, i) => (
                    <div key={i} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      {(() => {
                        const availableFields = TRIGGER_FIELDS[formData.trigger] || [];
                        return availableFields.length === 0 ? (
                          <select disabled className="flex-1 min-w-0 px-3 py-1.5 border border-gray-300 rounded text-sm bg-gray-100 text-gray-400">
                            <option>Select trigger first</option>
                          </select>
                        ) : (
                          <select
                            value={cond.field}
                            onChange={e => updateCondition(i, 'field', e.target.value)}
                            className="flex-1 min-w-0 px-3 py-1.5 border border-gray-300 rounded text-sm"
                          >
                            <option value="">— Select field —</option>
                            {availableFields.map(f => (
                              <option key={f} value={f}>{f}</option>
                            ))}
                          </select>
                        );
                      })()}
                      <select
                        value={cond.operator}
                        onChange={e => updateCondition(i, 'operator', e.target.value)}
                        className="px-2 py-1.5 border border-gray-300 rounded text-sm"
                      >
                        {operatorOptions.map(op => (
                          <option key={op.value} value={op.value}>{op.label}</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        value={cond.value}
                        onChange={e => updateCondition(i, 'value', e.target.value)}
                        placeholder="Value"
                        className="flex-1 min-w-0 px-3 py-1.5 border border-gray-300 rounded text-sm"
                      />
                      <button type="button" onClick={() => removeCondition(i)} className="flex-shrink-0 text-red-400 hover:text-red-600 transition-colors">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Actions & Review */}
          {step === 3 && (
            <div className="space-y-5">
              {/* Actions */}
              <div className="space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-base font-semibold" style={{ color: BRAND_COLORS.navy }}>Step 3: Actions &amp; Review</h3>
                    <p className="text-sm text-gray-500 mt-0.5">Define what happens when the workflow fires.</p>
                  </div>
                  <button
                    type="button"
                    onClick={addAction}
                    className="flex-shrink-0 text-sm font-medium px-3 py-1.5 rounded-lg border transition-colors hover:bg-yellow-50"
                    style={{ borderColor: BRAND_COLORS.gold, color: BRAND_COLORS.navy }}
                  >
                    + Add Action
                  </button>
                </div>
                <div className="space-y-3">
                  {formData.actions.map((action, i) => (
                    <div key={i} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Action {i + 1}</span>
                        {formData.actions.length > 1 && (
                          <button type="button" onClick={() => removeAction(i)} className="text-red-400 hover:text-red-600 transition-colors">
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <select
                        value={action.type}
                        onChange={e => updateAction(i, 'type', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm mb-2"
                      >
                        {actionTypes.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                      {action.type === 'send_email' ? (
                        <div className="space-y-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">To <span className="text-red-500">*</span> <span className="text-gray-400 font-normal">— use {'{admin.emails}'} or hardcoded address</span></label>
                            <input
                              type="text"
                              value={action.to || ''}
                              onChange={e => updateAction(i, 'to', e.target.value)}
                              placeholder="{admin.emails} or name@example.com"
                              className={`w-full px-3 py-2 border rounded text-sm ${stepError && !action.to?.trim() ? 'border-red-400' : 'border-gray-300'}`}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Subject <span className="text-red-500">*</span></label>
                            <input
                              type="text"
                              value={action.subject || ''}
                              onChange={e => updateAction(i, 'subject', e.target.value)}
                              placeholder="e.g., New prospect submitted"
                              className={`w-full px-3 py-2 border rounded text-sm ${stepError && !action.subject?.trim() ? 'border-red-400' : 'border-gray-300'}`}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Template <span className="text-red-500">*</span></label>
                            <select
                              value={action.template_name || ''}
                              onChange={e => updateAction(i, 'template_name', e.target.value)}
                              className={`w-full px-3 py-2 border rounded text-sm ${stepError && !action.template_name?.trim() ? 'border-red-400' : 'border-gray-300'}`}
                            >
                              <option value="">{templatesLoading ? 'Loading templates…' : '— Select template —'}</option>
                              {availableTemplates.map(t => {
                                const friendly = EMAIL_TEMPLATES.find(e => e.slug === t.slug);
                                return <option key={t.slug} value={t.slug}>{friendly ? friendly.label : t.name || t.slug}</option>;
                              })}
                            </select>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Message / Value <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            required
                            value={action.message || ''}
                            onChange={e => updateAction(i, 'message', e.target.value)}
                            placeholder={action.type === 'update_field' ? 'field_name=new_value' : 'Message or value...'}
                            className={`w-full px-3 py-2 border rounded text-sm ${stepError && !action.message?.trim() ? 'border-red-400 focus:ring-red-400' : 'border-gray-300'}`}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Read-only Review Summary */}
              <div className="rounded-xl border-2 p-4" style={{ borderColor: BRAND_COLORS.gold, backgroundColor: `${BRAND_COLORS.navy}08` }}>
                <h4 className="text-sm font-bold mb-3 uppercase tracking-wide" style={{ color: BRAND_COLORS.navy }}>Workflow Summary</h4>
                <dl className="space-y-2 text-sm">
                  <div className="flex gap-2">
                    <dt className="font-medium text-gray-500 w-24 flex-shrink-0">Name:</dt>
                    <dd className="text-gray-800 font-medium">{formData.name || '—'}</dd>
                  </div>
                  {formData.description && (
                    <div className="flex gap-2">
                      <dt className="font-medium text-gray-500 w-24 flex-shrink-0">Description:</dt>
                      <dd className="text-gray-700">{formData.description}</dd>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <dt className="font-medium text-gray-500 w-24 flex-shrink-0">Trigger:</dt>
                    <dd className="text-gray-800">{selectedTrigger ? selectedTrigger.label : '—'}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="font-medium text-gray-500 w-24 flex-shrink-0">Conditions:</dt>
                    <dd className="text-gray-700">
                      {formData.conditions.length === 0
                        ? <span className="italic text-gray-400">None — always runs</span>
                        : formData.conditions.map((c, idx) => (
                            <div key={idx}>{c.field} <span className="text-gray-400">{c.operator.replace('_', ' ')}</span> &ldquo;{c.value}&rdquo;</div>
                          ))
                      }
                    </dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="font-medium text-gray-500 w-24 flex-shrink-0">Actions:</dt>
                    <dd className="text-gray-700 space-y-0.5">
                      {formData.actions.map((a, idx) => (
                        <div key={idx}>
                          <span className="font-medium">{actionTypes.find(t => t.value === a.type)?.label}</span>
                          {a.message ? <span className="text-gray-500">: &ldquo;{a.message}&rdquo;</span> : null}
                        </div>
                      ))}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          )}

          {stepError && (
            <p className="mt-3 text-sm text-red-600 font-medium">{stepError}</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100 flex justify-between items-center">
          <button
            type="button"
            onClick={step === 1 ? onClose : handleBack}
            className="px-5 py-2 border rounded-lg text-sm font-medium transition-colors hover:bg-gray-50"
            style={{ borderColor: BRAND_COLORS.border, color: BRAND_COLORS.textPrimary }}
          >
            {step === 1 ? 'Cancel' : '← Back'}
          </button>
          {/* Save & Close is offered on every step but the last, where the
              primary action already saves and closes. */}
          {step < 3 && <WizardSaveClose onClick={handleSaveDraft} disabledReason={draftBlockedReason} />}
          {step < 3 ? (
            <button
              type="button"
              onClick={handleNext}
              className="px-6 py-2 rounded-lg text-sm font-bold text-white transition-all hover:opacity-90"
              style={{ background: BRAND_COLORS.gold }}
            >
              Next →
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              className="px-6 py-2 rounded-lg text-sm font-bold transition-all hover:opacity-90"
              style={{ background: BRAND_COLORS.gold, color: BRAND_COLORS.textOnGold }}
            >
              Create Workflow
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MODAL: Edit Workflow
// ============================================================================

const EditWorkflowModal = ({ show, onClose, workflow, updateWorkflow, partners }) => {
  const [step, setStep] = useState(1);
  const [stepError, setStepError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    trigger: '',
    conditions: [],
    actions: [{ type: 'send_notification', message: '' }]
  });
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  React.useEffect(() => {
    if (show && workflow) {
      setStep(1);
      setStepError('');
      setFormData({
        name: workflow.name || '',
        description: workflow.description || '',
        trigger: workflow.trigger || '',
        conditions: workflow.conditions ? [...workflow.conditions] : [],
        actions: workflow.actions && workflow.actions.length > 0 ? [...workflow.actions] : [{ type: 'send_notification', message: '' }]
      });
      setTemplatesLoading(true);
      import('./supabaseClient')
        .then(({ invokeFunction }) => invokeFunction('send-email', { body: { action: 'list_templates' } }))
        .then(({ data }) => setAvailableTemplates(data?.templates || EMAIL_TEMPLATES))
        .catch(() => setAvailableTemplates(EMAIL_TEMPLATES))
        .finally(() => setTemplatesLoading(false));
    }
  }, [show, workflow]);

  if (!show) return null;

  const triggerOptions = [
    { value: 'prospect_submitted',          label: '📄 Prospect Submitted',               category: 'Prospects' },
    { value: 'prospect_approved',           label: '✅ Prospect Approved',                category: 'Prospects' },
    { value: 'prospect_rejected',           label: '❌ Prospect Rejected',                category: 'Prospects' },
    { value: 'prospect_auto_rejected',      label: '⏰ Prospect Auto-Rejected (7 days)',   category: 'Prospects' },
    { value: 'intro_completed',             label: '🤝 Introduction Completed',           category: 'Prospects' },
    { value: 'intro_deadline_approaching',  label: '⏱️ Intro Deadline Approaching (7d)',  category: 'Prospects' },
    { value: 'intro_overdue',               label: '🚨 Introduction Overdue',             category: 'Prospects' },
    { value: 'validity_expiring_soon',      label: '📅 Validity Period Expiring (30d)',   category: 'Prospects' },
    { value: 'partner_created',             label: '➕ New Partner Created',              category: 'Partners' },
    { value: 'partner_status_changed',      label: '🔄 Partner Status Changed',           category: 'Partners' },
    { value: 'partner_entering_tail',       label: '⏳ Partner Entering Tail Period',     category: 'Partners' },
    { value: 'agreement_expiring_soon',     label: '📋 Agreement Expiring (30 days)',     category: 'Partners' },
    { value: 'tail_period_ending',          label: '🏁 Tail Period Ending (30 days)',     category: 'Partners' },
    { value: 'revenue_deal_created',        label: '💰 Revenue Deal Created',             category: 'Revenue' },
    { value: 'revenue_deal_threshold',      label: '💎 Revenue Deal Exceeds Threshold',   category: 'Revenue' },
    { value: 'pool_created',                label: '🏦 Commission Pool Created',          category: 'Revenue' },
    { value: 'pool_distributed',            label: '💸 Pool Distribution Completed',      category: 'Revenue' },
    { value: 'transaction_recorded',        label: '📊 Transaction Recorded',             category: 'Revenue' },
    { value: 'teaser_published',            label: '📢 Teaser Published',                 category: 'Content' },
    { value: 'teaser_updated',              label: '✏️ Teaser Updated',                  category: 'Content' },
    { value: 'user_created',                label: '👤 New User Created',                 category: 'Users' },
    { value: 'user_login_first_time',       label: '🎉 User First Login',                 category: 'Users' },
    { value: 'user_inactive_30_days',       label: '😴 User Inactive for 30 Days',        category: 'Users' },
    { value: 'user_status_changed',         label: '🔒 User Status Changed',              category: 'Users' },
    { value: 'user_password_reset',         label: '🔑 User Password Reset',              category: 'Users' },
    { value: 'audit_log_critical',          label: '🔍 Critical Audit Event',             category: 'System' },
    { value: 'data_export_requested',       label: '📤 Data Export Requested',            category: 'System' },
    { value: 'system_maintenance',          label: '🛠️ System Maintenance Scheduled',    category: 'System' },
    { value: 'daily_summary',               label: '📆 Daily Summary (9 AM)',             category: 'Scheduled' },
    { value: 'weekly_report',               label: '📊 Weekly Report (Monday 9 AM)',      category: 'Scheduled' },
    { value: 'monthly_metrics',             label: '📈 Monthly Metrics (1st of month)',   category: 'Scheduled' },
  ];

  const TRIGGER_FIELDS = {
    // Prospects
    prospect_submitted:          ['status', 'sector', 'country', 'estimated_value', 'company_name', 'contact_name'],
    prospect_approved:           ['status', 'sector', 'country', 'estimated_value', 'company_name', 'contact_name'],
    prospect_rejected:           ['status', 'sector', 'country', 'estimated_value', 'company_name', 'contact_name'],
    prospect_auto_rejected:      ['status', 'sector', 'country', 'estimated_value', 'company_name', 'contact_name'],
    intro_completed:             ['status', 'sector', 'country', 'estimated_value', 'company_name', 'contact_name'],
    intro_deadline_approaching:  ['status', 'sector', 'country', 'estimated_value', 'company_name', 'contact_name'],
    intro_overdue:               ['status', 'sector', 'country', 'estimated_value', 'company_name', 'contact_name'],
    validity_expiring_soon:      ['status', 'sector', 'country', 'estimated_value', 'company_name', 'contact_name'],
    // Partners
    partner_created:             ['status', 'tier', 'sector', 'country', 'name'],
    partner_status_changed:      ['status', 'tier', 'sector', 'country', 'name'],
    partner_entering_tail:       ['status', 'tier', 'sector', 'country', 'name'],
    agreement_expiring_soon:     ['status', 'tier', 'sector', 'country', 'name'],
    tail_period_ending:          ['status', 'tier', 'sector', 'country', 'name'],
    // Revenue
    revenue_deal_created:        ['status', 'type', 'value', 'partner_name', 'prospect_name'],
    revenue_deal_threshold:      ['status', 'type', 'value', 'partner_name', 'prospect_name'],
    pool_created:                ['type', 'value', 'partner_name'],
    pool_distributed:            ['type', 'value', 'partner_name'],
    transaction_recorded:        ['status', 'type', 'value', 'partner_name'],
    // Content
    teaser_published:            ['name', 'sector', 'geography'],
    teaser_updated:              ['name', 'sector', 'geography'],
    // Users
    user_created:                ['role', 'email', 'name', 'status'],
    user_login_first_time:       ['role', 'email', 'name', 'status'],
    user_inactive_30_days:       ['role', 'email', 'name', 'status'],
    user_status_changed:         ['role', 'email', 'name', 'status'],
    user_password_reset:         ['role', 'email', 'name'],
    // System
    audit_log_critical:          ['type', 'severity'],
    data_export_requested:       ['type', 'role', 'email'],
    system_maintenance:          ['type'],
    // Scheduled
    daily_summary:               ['day', 'time'],
    weekly_report:               ['day', 'time'],
    monthly_metrics:             ['day', 'time'],
  };

  const operatorOptions = [
    { value: 'equals',       label: 'equals' },
    { value: 'not_equals',   label: 'not equals' },
    { value: 'contains',     label: 'contains' },
    { value: 'greater_than', label: 'greater than' },
    { value: 'less_than',    label: 'less than' },
  ];

  const actionTypes = [
    { value: 'send_notification', label: 'Send In-App Notification' },
    { value: 'send_email',        label: 'Send Email' },
    { value: 'update_field',      label: 'Update Field' },
    { value: 'create_task',       label: 'Create Task' },
  ];

  const addCondition = () => setFormData(f => ({ ...f, conditions: [...f.conditions, { field: '', operator: 'equals', value: '' }] }));
  const updateCondition = (i, key, val) => setFormData(f => { const c = [...f.conditions]; c[i] = { ...c[i], [key]: val }; return { ...f, conditions: c }; });
  const removeCondition = (i) => setFormData(f => ({ ...f, conditions: f.conditions.filter((_, idx) => idx !== i) }));

  const addAction = () => setFormData(f => ({ ...f, actions: [...f.actions, { type: 'send_notification', message: '' }] }));
  const updateAction = (i, key, val) => setFormData(f => { const a = [...f.actions]; a[i] = { ...a[i], [key]: val }; return { ...f, actions: a }; });
  const removeAction = (i) => setFormData(f => ({ ...f, actions: f.actions.filter((_, idx) => idx !== i) }));

  const handleNext = () => {
    if (step === 1) {
      if (!formData.name.trim() || !formData.trigger) {
        setStepError('Workflow name and trigger event are required.');
        return;
      }
    }
    setStepError('');
    setStep(s => s + 1);
  };

  const handleBack = () => { setStepError(''); setStep(s => s - 1); };

  const handleSubmit = () => {
    for (const a of formData.actions) {
      if (a.type === 'send_notification' && !a.message?.trim()) { setStepError('Send Notification actions require a message.'); return; }
      if (a.type === 'send_email' && (!a.to?.trim() || !a.subject?.trim() || !a.template_name?.trim())) {
        setStepError('Send Email actions require a To address, Subject, and Template.'); return;
      }
    }
    updateWorkflow(workflow.id, formData);
  };

  // ── Save & Close ───────────────────────────────────────────────────────────
  // This workflow already exists, so there is no draft to flag: commit the edits
  // made so far to the live record and close. Only the name is enforced.
  const draftBlockedReason = formData.name.trim() ? '' : 'The workflow name cannot be blank.';
  const handleSaveDraft = () => {
    if (draftBlockedReason) return;
    setStepError('');
    updateWorkflow(workflow.id, formData);
    onClose();
  };

  const selectedTrigger = triggerOptions.find(t => t.value === formData.trigger);

  const wizardSteps = [
    { n: 1, label: 'Trigger' },
    { n: 2, label: 'Conditions' },
    { n: 3, label: 'Actions' },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl my-auto flex flex-col" style={{ maxHeight: 'calc(100vh - 2rem)' }} onClick={e => e.stopPropagation()}>

        {/* Navy Header */}
        <div className="flex-shrink-0 px-6 py-4 flex items-center justify-between rounded-t-xl" style={{ background: BRAND_COLORS.navy, borderBottom: `3px solid ${BRAND_COLORS.gold}` }}>
          <div>
            <h2 className="text-lg font-bold text-white">Edit Workflow Automation</h2>
            <p className="text-xs mt-0.5" style={{ color: BRAND_COLORS.cream }}>Step {step} of 3</p>
          </div>
          <button onClick={onClose} className="text-white hover:text-gray-300 transition-colors p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step Indicator */}
        <div className="flex-shrink-0 px-6 py-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            {wizardSteps.map(({ n, label }, idx, arr) => (
              <React.Fragment key={n}>
                <div className="flex flex-col items-center gap-1">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all"
                    style={{
                      backgroundColor: step === n ? BRAND_COLORS.gold : step > n ? '#10B981' : '#E5E7EB',
                      color: step >= n ? 'white' : '#9CA3AF',
                    }}>
                    {step > n ? '✓' : n}
                  </div>
                  <span className="text-xs font-medium" style={{ color: step === n ? BRAND_COLORS.navy : '#9CA3AF' }}>{label}</span>
                </div>
                {idx < arr.length - 1 && (
                  <div className="flex-1 h-0.5 mx-2 mb-4 rounded" style={{ backgroundColor: step > n ? '#10B981' : '#E5E7EB' }} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5">

          {/* Step 1: Trigger */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <h3 className="text-base font-semibold mb-1" style={{ color: BRAND_COLORS.navy }}>Step 1: Trigger</h3>
                <p className="text-sm text-gray-500">Name your workflow and choose what event triggers it.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Workflow Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g., Notify on Prospect Approval"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A1C5C] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={e => setFormData(f => ({ ...f, description: e.target.value }))}
                  placeholder="What does this workflow do?"
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A1C5C] focus:border-transparent resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Trigger Event <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.trigger}
                  onChange={e => setFormData(f => ({ ...f, trigger: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1A1C5C] focus:border-transparent"
                >
                  <option value="">— Select a trigger —</option>
                  {['Prospects', 'Partners', 'Revenue', 'Content', 'Users', 'System', 'Scheduled'].map(cat => (
                    <optgroup key={cat} label={cat}>
                      {triggerOptions.filter(t => t.category === cat).map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Step 2: Conditions */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-base font-semibold" style={{ color: BRAND_COLORS.navy }}>Step 2: Conditions</h3>
                  <p className="text-sm text-gray-500 mt-0.5">Add optional filters — leave empty to run on every trigger.</p>
                </div>
                <button
                  type="button"
                  onClick={addCondition}
                  className="flex-shrink-0 text-sm font-medium px-3 py-1.5 rounded-lg border transition-colors hover:bg-yellow-50"
                  style={{ borderColor: BRAND_COLORS.gold, color: BRAND_COLORS.navy }}
                >
                  + Add Condition
                </button>
              </div>
              {formData.conditions.length === 0 ? (
                <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
                  <p className="text-sm text-gray-400">No conditions — workflow runs for every trigger event.</p>
                  <button type="button" onClick={addCondition} className="mt-2 text-sm font-medium hover:underline" style={{ color: BRAND_COLORS.navy }}>
                    + Add a condition
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {formData.conditions.map((cond, i) => (
                    <div key={i} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      {(() => {
                        const availableFields = TRIGGER_FIELDS[formData.trigger] || [];
                        return availableFields.length === 0 ? (
                          <select disabled className="flex-1 min-w-0 px-3 py-1.5 border border-gray-300 rounded text-sm bg-gray-100 text-gray-400">
                            <option>Select trigger first</option>
                          </select>
                        ) : (
                          <select
                            value={cond.field}
                            onChange={e => updateCondition(i, 'field', e.target.value)}
                            className="flex-1 min-w-0 px-3 py-1.5 border border-gray-300 rounded text-sm"
                          >
                            <option value="">— Select field —</option>
                            {availableFields.map(f => (
                              <option key={f} value={f}>{f}</option>
                            ))}
                          </select>
                        );
                      })()}
                      <select
                        value={cond.operator}
                        onChange={e => updateCondition(i, 'operator', e.target.value)}
                        className="px-2 py-1.5 border border-gray-300 rounded text-sm"
                      >
                        {operatorOptions.map(op => (
                          <option key={op.value} value={op.value}>{op.label}</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        value={cond.value}
                        onChange={e => updateCondition(i, 'value', e.target.value)}
                        placeholder="Value"
                        className="flex-1 min-w-0 px-3 py-1.5 border border-gray-300 rounded text-sm"
                      />
                      <button type="button" onClick={() => removeCondition(i)} className="flex-shrink-0 text-red-400 hover:text-red-600 transition-colors">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Actions & Review */}
          {step === 3 && (
            <div className="space-y-5">
              {/* Actions */}
              <div className="space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-base font-semibold" style={{ color: BRAND_COLORS.navy }}>Step 3: Actions &amp; Review</h3>
                    <p className="text-sm text-gray-500 mt-0.5">Define what happens when the workflow fires.</p>
                  </div>
                  <button
                    type="button"
                    onClick={addAction}
                    className="flex-shrink-0 text-sm font-medium px-3 py-1.5 rounded-lg border transition-colors hover:bg-yellow-50"
                    style={{ borderColor: BRAND_COLORS.gold, color: BRAND_COLORS.navy }}
                  >
                    + Add Action
                  </button>
                </div>
                <div className="space-y-3">
                  {formData.actions.map((action, i) => (
                    <div key={i} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Action {i + 1}</span>
                        {formData.actions.length > 1 && (
                          <button type="button" onClick={() => removeAction(i)} className="text-red-400 hover:text-red-600 transition-colors">
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <select
                        value={action.type}
                        onChange={e => updateAction(i, 'type', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm mb-2"
                      >
                        {actionTypes.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                      {action.type === 'send_email' ? (
                        <div className="space-y-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">To <span className="text-red-500">*</span> <span className="text-gray-400 font-normal">— use {'{admin.emails}'} or hardcoded address</span></label>
                            <input
                              type="text"
                              value={action.to || ''}
                              onChange={e => updateAction(i, 'to', e.target.value)}
                              placeholder="{admin.emails} or name@example.com"
                              className={`w-full px-3 py-2 border rounded text-sm ${stepError && !action.to?.trim() ? 'border-red-400' : 'border-gray-300'}`}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Subject <span className="text-red-500">*</span></label>
                            <input
                              type="text"
                              value={action.subject || ''}
                              onChange={e => updateAction(i, 'subject', e.target.value)}
                              placeholder="e.g., New prospect submitted"
                              className={`w-full px-3 py-2 border rounded text-sm ${stepError && !action.subject?.trim() ? 'border-red-400' : 'border-gray-300'}`}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Template <span className="text-red-500">*</span></label>
                            <select
                              value={action.template_name || ''}
                              onChange={e => updateAction(i, 'template_name', e.target.value)}
                              className={`w-full px-3 py-2 border rounded text-sm ${stepError && !action.template_name?.trim() ? 'border-red-400' : 'border-gray-300'}`}
                            >
                              <option value="">{templatesLoading ? 'Loading templates…' : '— Select template —'}</option>
                              {availableTemplates.map(t => {
                                const friendly = EMAIL_TEMPLATES.find(e => e.slug === t.slug);
                                return <option key={t.slug} value={t.slug}>{friendly ? friendly.label : t.name || t.slug}</option>;
                              })}
                            </select>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Message / Value <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            required
                            value={action.message || ''}
                            onChange={e => updateAction(i, 'message', e.target.value)}
                            placeholder={action.type === 'update_field' ? 'field_name=new_value' : 'Message or value...'}
                            className={`w-full px-3 py-2 border rounded text-sm ${stepError && !action.message?.trim() ? 'border-red-400 focus:ring-red-400' : 'border-gray-300'}`}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Read-only Review Summary */}
              <div className="rounded-xl border-2 p-4" style={{ borderColor: BRAND_COLORS.gold, backgroundColor: `${BRAND_COLORS.navy}08` }}>
                <h4 className="text-sm font-bold mb-3 uppercase tracking-wide" style={{ color: BRAND_COLORS.navy }}>Workflow Summary</h4>
                <dl className="space-y-2 text-sm">
                  <div className="flex gap-2">
                    <dt className="font-medium text-gray-500 w-24 flex-shrink-0">Name:</dt>
                    <dd className="text-gray-800 font-medium">{formData.name || '—'}</dd>
                  </div>
                  {formData.description && (
                    <div className="flex gap-2">
                      <dt className="font-medium text-gray-500 w-24 flex-shrink-0">Description:</dt>
                      <dd className="text-gray-700">{formData.description}</dd>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <dt className="font-medium text-gray-500 w-24 flex-shrink-0">Trigger:</dt>
                    <dd className="text-gray-800">{selectedTrigger ? selectedTrigger.label : '—'}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="font-medium text-gray-500 w-24 flex-shrink-0">Conditions:</dt>
                    <dd className="text-gray-700">
                      {formData.conditions.length === 0
                        ? <span className="italic text-gray-400">None — always runs</span>
                        : formData.conditions.map((c, idx) => (
                            <div key={idx}>{c.field} <span className="text-gray-400">{c.operator.replace('_', ' ')}</span> &ldquo;{c.value}&rdquo;</div>
                          ))
                      }
                    </dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="font-medium text-gray-500 w-24 flex-shrink-0">Actions:</dt>
                    <dd className="text-gray-700 space-y-0.5">
                      {formData.actions.map((a, idx) => (
                        <div key={idx}>
                          <span className="font-medium">{actionTypes.find(t => t.value === a.type)?.label}</span>
                          {a.message ? <span className="text-gray-500">: &ldquo;{a.message}&rdquo;</span> : null}
                        </div>
                      ))}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          )}

          {stepError && (
            <p className="mt-3 text-sm text-red-600 font-medium">{stepError}</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100 flex justify-between items-center">
          <button
            type="button"
            onClick={step === 1 ? onClose : handleBack}
            className="px-5 py-2 border rounded-lg text-sm font-medium transition-colors hover:bg-gray-50"
            style={{ borderColor: BRAND_COLORS.border, color: BRAND_COLORS.textPrimary }}
          >
            {step === 1 ? 'Cancel' : '← Back'}
          </button>
          {/* Save & Close is offered on every step but the last, where the
              primary action already saves and closes. */}
          {step < 3 && <WizardSaveClose onClick={handleSaveDraft} disabledReason={draftBlockedReason} />}
          {step < 3 ? (
            <button
              type="button"
              onClick={handleNext}
              className="px-6 py-2 rounded-lg text-sm font-bold text-white transition-all hover:opacity-90"
              style={{ background: BRAND_COLORS.gold }}
            >
              Next →
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              className="px-6 py-2 rounded-lg text-sm font-bold transition-all hover:opacity-90"
              style={{ background: BRAND_COLORS.gold, color: BRAND_COLORS.textOnGold }}
            >
              Save Changes
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MODAL: Alert Dialog
// ============================================================================

const AlertDialog = ({ title = 'Notice', message, onClose }) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center"
    style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
  >
    <div className="rounded-xl overflow-hidden shadow-2xl w-full max-w-sm mx-4" style={{ background: '#fff' }}>
      <div className="px-6 py-4" style={{ backgroundColor: BRAND_COLORS.navy }}>
        <h2 className="text-base font-bold text-white tracking-wide">{title}</h2>
      </div>
      <div className="px-6 py-5">
        <p className="text-sm text-gray-700 leading-relaxed">{message}</p>
      </div>
      <div className="px-6 pb-5 flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="px-5 py-2 rounded-lg text-sm font-bold transition-all hover:opacity-90"
          style={{ background: BRAND_COLORS.gold, color: BRAND_COLORS.textOnGold }}
        >
          OK
        </button>
      </div>
    </div>
  </div>
);

// ============================================================================
// MODAL: Confirm Dialog
// ============================================================================

const ConfirmDialog = ({ message, confirmLabel = 'Confirm', onConfirm, onClose }) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center"
    style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
  >
    <div className="rounded-xl overflow-hidden shadow-2xl w-full max-w-sm mx-4" style={{ background: '#fff' }}>
      {/* Header */}
      <div className="px-6 py-4" style={{ backgroundColor: BRAND_COLORS.navy }}>
        <h2 className="text-base font-bold text-white tracking-wide">Confirm</h2>
      </div>

      {/* Body */}
      <div className="px-6 py-5">
        <p className="text-sm text-gray-700 leading-relaxed">{message}</p>
      </div>

      {/* Footer */}
      <div className="px-6 pb-5 flex justify-end gap-3">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          className="px-5 py-2 rounded-lg text-sm font-bold transition-all hover:opacity-90"
          style={{ background: BRAND_COLORS.gold, color: BRAND_COLORS.textOnGold }}
        >
          {confirmLabel}
        </button>
      </div>
    </div>
  </div>
);

// ============================================================================
// MODAL: New User
// ============================================================================

const NewUserModal = ({ onClose, onSubmit, partners, users = [], sectors = DEFAULT_SECTORS, agreementTypes = DEFAULT_AGREEMENT_TYPES }) => {
  const [role, setRole] = useState('Admin');
  // Password input was removed (April 2026, S-9 fix). Account creation now
  // routes through manage-user → auth.admin.inviteUserByEmail, which emails
  // the user a magic link to set their own password. Sending a password from
  // the client was always a no-op — it was dropped before reaching the EF.

  // For Admin / Editor
  const [baseForm, setBaseForm] = useState({ name: '', email: '' });

  // For Partner — mode toggle: create new record vs link to existing one
  const [partnerMode, setPartnerMode] = useState('new'); // 'new' | 'existing'

  // For Partner — new record form
  const [partnerForm, setPartnerForm] = useState({
    name: '', contactPerson: '', email: '', phone: '',
    agreementType: 'Basic MCA Referral Fee', startDate: '', endDate: '', tailMonths: 18,
  });
  const [partnerSectors, setPartnerSectors] = useState([]);
  const [partnerGeo, setPartnerGeo] = useState({ continents: [], countries: [] });

  // For Partner — link to existing partner
  const [existingForm, setExistingForm] = useState({ partnerId: '', name: '', email: '' });

  // Partners that don't yet have a linked user account
  const unlinkedPartners = (partners || [])
    .filter(p => !(users || []).some(u => u.partnerId === p.id))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (role === 'Partner') {
      if (partnerMode === 'existing') {
        if (!existingForm.partnerId) {
          window.alert('Please select a partner');
          return;
        }
        if (!existingForm.name || !existingForm.email) {
          window.alert('Name and email are required');
          return;
        }
        onSubmit({
          name: existingForm.name,
          email: existingForm.email,
          role: 'Partner',
          partnerId: existingForm.partnerId,
          // no newPartnerData — addUser will skip partner creation and use partnerId directly
        });
      } else {
        if (!partnerForm.name || !partnerForm.email) {
          window.alert('Name and email are required');
          return;
        }
        onSubmit({
          name: partnerForm.name,
          email: partnerForm.email,
          role: 'Partner',
          newPartnerData: {
            ...partnerForm,
            sectors: partnerSectors,
            geography: partnerGeo,
          }
        });
      }
    } else {
      if (!baseForm.name || !baseForm.email) {
        window.alert('Name and email are required');
        return;
      }
      onSubmit({ ...baseForm, role, partnerId: null });
    }
  };

  return (
    <Modal onClose={onClose} title={`Add New ${role}`}>
      <form onSubmit={handleSubmit} className="space-y-4">

        {/* Role selector — always on top */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Role <span className="text-red-500">*</span></label>
          <div className="flex gap-2 flex-wrap">
            {['Super Admin', 'Admin', 'Editor', 'Partner'].map(r => (
              <button
                key={r} type="button"
                onClick={() => setRole(r)}
                className="flex-1 py-2 rounded-lg text-sm font-medium border transition-all"
                style={role === r
                  ? { backgroundColor: BRAND_COLORS.navy, color: 'white', borderColor: BRAND_COLORS.navy }
                  : { backgroundColor: 'white', color: BRAND_COLORS.navy, borderColor: BRAND_COLORS.navy + '40' }
                }
              >
                {r}
              </button>
            ))}
          </div>
        </div>

        {/* Non-Partner — simple form */}
        {role !== 'Partner' && (
          <>
            <div className="p-3 rounded-lg text-xs" style={{ backgroundColor: BRAND_COLORS.cream }}>
              <strong>{role}</strong> — {
                role === 'Super Admin' ? 'Full system access including Settings, Field Manager, and Form Configurator.' :
                role === 'Admin'       ? 'Full access to partners, prospects, deals, users and workflows. Cannot access Settings.' :
                                         'Can view and edit partners, teasers, prospects and workflows. Cannot delete or manage users.'
              }
            </div>
            <Input label="Full Name" value={baseForm.name} onChange={e => setBaseForm(f => ({ ...f, name: e.target.value }))} required />
            <Input label="Email" type="email" value={baseForm.email} onChange={e => setBaseForm(f => ({ ...f, email: e.target.value }))} required />
            <div className="text-xs px-3 py-2 rounded-lg" style={{ backgroundColor: BRAND_COLORS.cream, color: '#6b5028' }}>
              An invite email with a magic link will be sent on Save. The user clicks the link, sets their own password, and signs in.
            </div>
          </>
        )}

        {/* Partner — mode toggle + conditional form */}
        {role === 'Partner' && (
          <>
            {/* Mode toggle */}
            <div className="flex gap-2">
              {[
                { key: 'new',      label: 'New partner' },
                { key: 'existing', label: 'Existing partner' },
              ].map(({ key, label }) => (
                <button
                  key={key} type="button"
                  onClick={() => setPartnerMode(key)}
                  className="flex-1 py-2 rounded-lg text-sm font-medium border transition-all"
                  style={partnerMode === key
                    ? { backgroundColor: BRAND_COLORS.gold, color: 'white', borderColor: BRAND_COLORS.gold }
                    : { backgroundColor: 'white', color: '#6b5028', borderColor: BRAND_COLORS.gold + '60' }
                  }
                >
                  {label}
                </button>
              ))}
            </div>

            {/* ── NEW PARTNER ─────────────────────────────────── */}
            {partnerMode === 'new' && (
              <>
                <div className="p-3 rounded-lg text-xs" style={{ backgroundColor: BRAND_COLORS.cream }}>
                  Creates both a login account and a new Partner record simultaneously.
                </div>

                <div className="space-y-3">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500">Partner Details</h4>
                  <Input label="Partner / Company Name" value={partnerForm.name} onChange={e => setPartnerForm(f => ({ ...f, name: e.target.value }))} required />
                  <Input label="Contact Person" value={partnerForm.contactPerson} onChange={e => setPartnerForm(f => ({ ...f, contactPerson: e.target.value }))} />
                  <Input label="Email" type="email" value={partnerForm.email} onChange={e => setPartnerForm(f => ({ ...f, email: e.target.value }))} required />
                  <Input label="Phone" value={partnerForm.phone} onChange={e => setPartnerForm(f => ({ ...f, phone: e.target.value }))} />
                  <div className="text-xs px-3 py-2 rounded-lg" style={{ backgroundColor: BRAND_COLORS.cream, color: '#6b5028' }}>
                    An invite email with a magic link will be sent on Save.
                  </div>
                </div>

                <div className="space-y-3 pt-3 border-t border-gray-200">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500">Agreement</h4>
                  <Select
                    label="Agreement Type"
                    value={partnerForm.agreementType}
                    onChange={e => setPartnerForm(f => ({ ...f, agreementType: e.target.value }))}
                    options={agreementTypes.map(t => ({ value: t, label: t }))}
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <Input label="Start Date" type="date" value={partnerForm.startDate} onChange={e => setPartnerForm(f => ({ ...f, startDate: e.target.value }))} />
                    <Input label="End Date" type="date" value={partnerForm.endDate} onChange={e => setPartnerForm(f => ({ ...f, endDate: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tail End Period (months)</label>
                    <div className="flex items-center gap-3">
                      <input
                        type="number" min="1" max="60"
                        value={partnerForm.tailMonths}
                        onChange={e => setPartnerForm(f => ({ ...f, tailMonths: parseInt(e.target.value) || 18 }))}
                        className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                      <span className="text-sm text-gray-500">months after contract end (default: 18)</span>
                    </div>
                  </div>
                </div>

                <SectorSelector sectors={sectors} selectedSectors={partnerSectors} onSectorsChange={setPartnerSectors} />
                <GeographySelector selectedGeo={partnerGeo} onGeoChange={setPartnerGeo} />
              </>
            )}

            {/* ── EXISTING PARTNER ────────────────────────────── */}
            {partnerMode === 'existing' && (
              <>
                <div className="p-3 rounded-lg text-xs" style={{ backgroundColor: BRAND_COLORS.cream }}>
                  Links a login account to an existing partner record. Only partners without a user account are shown.
                </div>

                {unlinkedPartners.length === 0 ? (
                  <div className="text-sm text-gray-500 text-center py-4">
                    All partner records already have a linked user account.
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Partner Record <span className="text-red-500">*</span></label>
                      <select
                        value={existingForm.partnerId}
                        onChange={e => {
                          const pid = e.target.value;
                          const p = unlinkedPartners.find(x => x.id === pid);
                          setExistingForm(f => ({
                            ...f,
                            partnerId: pid,
                            // Pre-fill name/email from partner record if not yet typed
                            name: f.name || (p?.contactPerson || p?.name || ''),
                            email: f.email || (p?.email || ''),
                          }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                        required
                      >
                        <option value="">— Select partner —</option>
                        {unlinkedPartners.map(p => (
                          <option key={p.id} value={p.id}>{p.name}{p.email ? ` (${p.email})` : ''}</option>
                        ))}
                      </select>
                    </div>
                    <Input
                      label="Contact Person Full Name"
                      value={existingForm.name}
                      onChange={e => setExistingForm(f => ({ ...f, name: e.target.value }))}
                      required
                    />
                    <Input
                      label="Login Email (invite will be sent here)"
                      type="email"
                      value={existingForm.email}
                      onChange={e => setExistingForm(f => ({ ...f, email: e.target.value }))}
                      required
                    />
                    <div className="text-xs px-3 py-2 rounded-lg" style={{ backgroundColor: BRAND_COLORS.cream, color: '#6b5028' }}>
                      An invite email with a magic link will be sent to this address. The partner clicks it, sets their password, and gains access.
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}

        <div className="flex justify-end space-x-3 pt-4 border-t">
          <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">
            Cancel
          </button>
          <button type="submit" className="px-5 py-2 text-white rounded-lg font-medium" style={{ backgroundColor: BRAND_COLORS.gold }}>
            Create {role}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// ============================================================================
// MODAL: Edit User
// ============================================================================

const EditUserModal = ({ onClose, onSubmit, user, partners }) => {
  const [formData, setFormData] = useState({
    name: user.name,
    email: user.email,
    role: user.role,
    partnerId: user.partnerId || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email) {
      window.alert('Please fill in all required fields');
      return;
    }
    
    if (formData.role === 'Partner' && !formData.partnerId) {
      window.alert('Please select a partner for this user');
      return;
    }
    
    onSubmit(user.id, {
      name: formData.name,
      email: formData.email,
      role: formData.role,
      partnerId: formData.role === 'Partner' ? formData.partnerId : null
    });
  };

  return (
    <Modal onClose={onClose} title="Edit User">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Role <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.role}
            onChange={(e) => setFormData({ ...formData, role: e.target.value, partnerId: '' })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            required
          >
            <option value="Super Admin">Super Admin</option>
            <option value="Admin">Admin</option>
            <option value="Editor">Editor</option>
            <option value="Partner">Partner</option>
          </select>
        </div>

        {formData.role === 'Partner' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Associated Partner <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.partnerId}
              onChange={(e) => setFormData({ ...formData, partnerId: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              required
            >
              <option value="">Select a partner...</option>
              {partners.map(partner => (
                <option key={partner.id} value={partner.id}>
                  {partner.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="flex justify-end space-x-3 pt-4 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-white rounded-lg"
            style={{ backgroundColor: BRAND_COLORS.gold }}
          >
            Save Changes
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default PartnersPortal;

